<template>
  <div v-if="caseBuddyHeadless" class="tool88-page tool88-page--case-buddy-embedded" aria-hidden="true">
    <Tool88HeadlessSpreadsheetStore ref="inputSpreadsheet" />
    <Tool88HeadlessSpreadsheetStore ref="outputSpreadsheet" />
  </div>
  <div v-else :class="['tool88-page', { 'simple-view-active': simpleViewOpen, 'tool88-page--case-buddy-embedded': caseBuddyEmbedded }]">
    <button
      v-if="!simpleViewOpen"
      class="simple-view-launcher"
      type="button"
      @click="openSimpleView"
      aria-label="Open Tool 88 simple view"
    >
      <span class="simple-view-launcher-dot"></span>
      <span>Simple View</span>
    </button>

    <div v-if="directAutomationActive && !caseBuddyEmbedded" class="direct-auto-stop-overlay" role="status" aria-live="polite">
      <button class="direct-auto-stop-button" type="button" @click="requestDirectAutomationStop('User stopped direct Tool 88 automation.')">
        Stop Auto
      </button>
      <div class="direct-auto-stop-copy">
        <strong>Tool 88 Auto is running</strong>
        <span>{{ directAutomationRunState.detail || 'Finishing the current step before checking stop/guardrails.' }}</span>
      </div>
    </div>

    <div v-if="simpleViewOpen" class="simple-view-overlay" role="dialog" aria-modal="true" aria-labelledby="tool88-simple-view-title">
      <div class="simple-view-backdrop" @click="closeSimpleView"></div>
      <section ref="simpleViewModal" class="simple-view-modal">
        <button class="simple-view-close" type="button" aria-label="Close simple view" @click="closeSimpleView"><span aria-hidden="true">×</span></button>
        <div class="simple-view-layout">
          <div class="simple-view-left">
            <div class="simple-compact-header">
              <div class="simple-compact-title">
                <h2 id="tool88-simple-view-title">Paste a transaction, address list, or Case Buddy TSV</h2>
              </div>
              <div class="simple-actions-row simple-actions-row-top">
                <button class="primary-button" type="button" @click="simpleProcessData" :disabled="isProcessing || simpleDecisionResultReady">
                  {{ simpleDecisionResultReady ? 'Result ready' : countLookupResult ? 'Refresh checks' : 'Load + run checks' }}
                </button>
                <button class="ghost-button" type="button" @click="clearSimpleInput" :disabled="isProcessing">Clear text</button>
              </div>
            </div>

            <div class="simple-view-visual" aria-label="Compact flow map preview">
              <div class="simple-orbit simple-orbit-one"></div>
              <div class="simple-orbit simple-orbit-two"></div>
              <div class="simple-flow-map-head">
                <div>
                  <span class="mini-label">Flow map</span>
                  <strong>{{ simpleFlowMiniMap.summary }}</strong>
                </div>
                <div class="simple-flow-map-pills">
                  <span>{{ formatInteger(simpleFlowMiniMap.entryCount) }} entries</span>
                  <span>{{ formatInteger(simpleFlowMiniMap.linkCount) }} links</span>
                  <span>{{ simpleFlowMiniMap.openLabel }}</span>
                </div>
              </div>

              <svg
                v-if="simpleFlowMiniMap.nodes.length"
                class="simple-flow-map-svg"
                viewBox="0 0 520 116"
                preserveAspectRatio="none"
                role="img"
                :aria-label="simpleFlowMiniMap.ariaLabel"
              >
                <defs>
                  <linearGradient id="tool88SimpleFlowLine" x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0%" stop-color="#6366f1" stop-opacity="0.34" />
                    <stop offset="100%" stop-color="#14b8a6" stop-opacity="0.58" />
                  </linearGradient>
                  <radialGradient id="tool88SimpleFlowNode" cx="50%" cy="45%" r="70%">
                    <stop offset="0%" stop-color="#ffffff" stop-opacity="1" />
                    <stop offset="100%" stop-color="#dbeafe" stop-opacity="0.98" />
                  </radialGradient>
                </defs>
                <path
                  v-for="link in simpleFlowMiniMap.links"
                  :key="link.key"
                  class="simple-flow-map-link"
                  :d="link.path"
                />
                <g
                  v-for="node in simpleFlowMiniMap.nodes"
                  :key="node.key"
                  :class="['simple-flow-map-node', `simple-flow-map-node-${node.status}`]"
                >
                  <circle v-if="node.active" class="simple-flow-map-node-pulse" :cx="node.x" :cy="node.y" :r="node.r + 7" />
                  <circle :cx="node.x" :cy="node.y" :r="node.r" />
                  <text :x="node.x" :y="node.y + 3">{{ node.label }}</text>
                  <title>{{ node.title }}</title>
                </g>
                <text v-if="simpleFlowMiniMap.hiddenCount" class="simple-flow-map-hidden" x="504" y="105" text-anchor="end">+{{ simpleFlowMiniMap.hiddenCount }} more</text>
              </svg>

              <div v-else class="simple-flow-empty-map">
                <span class="simple-flow-empty-dot"></span>
                <span>Paste or load data to see the flow shape here.</span>
              </div>
            </div>

            <div class="simple-mode-bar">
              <span class="simple-mode-label">Mode</span>
              <button
                class="simple-mode-button"
                type="button"
                @click="simpleSettingsExpanded = !simpleSettingsExpanded"
                :aria-expanded="simpleSettingsExpanded ? 'true' : 'false'"
              >
                <span>{{ directionLabel }}</span>
                <span class="simple-mode-divider" aria-hidden="true">·</span>
                <span>{{ hopLogicTypeLabel }}</span>
                <span class="simple-mode-hint">{{ simpleSettingsExpanded ? 'Hide settings' : 'Change' }}</span>
              </button>
            </div>

            <div v-if="simpleSettingsExpanded" class="simple-settings-card">
              <label class="setting-field">
                <span>Direction</span>
                <select v-model="traceDirection" :disabled="isProcessing">
                  <option value="forward">Forward: follow Column D / To Address</option>
                  <option value="reverse">Reverse: follow Column C / From Address</option>
                </select>
              </label>
              <label class="setting-field">
                <span>Hop logic type</span>
                <select v-model="hopLogicType" :disabled="isProcessing">
                  <option value="NEXT_CHRONOLOGICAL">Next chronological exit</option>
                  <option value="FIFO">FIFO</option>
                  <option value="LIFO">LIFO / Post Transaction Deposit Queue</option>
                </select>
              </label>
            </div>

            <label class="simple-input-block">
              <span>Input data</span>
              <textarea
                v-model="simpleInputText"
                :disabled="isProcessing"
                autocomplete="off"
                autocapitalize="off"
                autocorrect="off"
                spellcheck="false"
                data-lpignore="true"
                data-1p-ignore="true"
                placeholder="Paste Case Buddy A–W rows, a transaction hash, or a comma/line-separated list of hashes. Optional To Addresses can be added in the staged rows below."
              ></textarea>
            </label>

            <div v-if="simpleInputShowsStagedRows" class="simple-staged-card">
              <div class="simple-staged-head">
                <div>
                  <span class="mini-label">Staged for Tool 88</span>
                  <strong>{{ simpleInputStagedRows.length }} transaction{{ simpleInputStagedRows.length === 1 ? '' : 's' }}</strong>
                </div>
                <span class="simple-staged-hint">Blank targets are auto-inferred when the hash has one clear recipient.</span>
              </div>
              <div class="simple-staged-table-wrap">
                <table class="simple-staged-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Transaction hash</th>
                      <th>{{ traceDirection === 'reverse' ? 'From Address' : 'To Address' }} override</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(stageRow, stageIndex) in simpleInputStagedRows" :key="stageRow.key">
                      <td>{{ stageIndex + 1 }}</td>
                      <td><code>{{ shortenMiddle(stageRow.txHash, 14, 10) }}</code></td>
                      <td>
                        <input
                          v-model="simpleStagedAddressOverrides[stageRow.key]"
                          :disabled="isProcessing"
                          autocomplete="off"
                          autocapitalize="off"
                          autocorrect="off"
                          spellcheck="false"
                          placeholder="Optional target address"
                        />
                      </td>
                      <td>{{ getSimpleStagedRowStatus(stageRow) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="targetCandidates.length > 1" class="simple-target-card">
              <strong>Choose the address to research</strong>
              <select v-model="selectedTargetAddress" :disabled="isProcessing">
                <option value="">Select an address...</option>
                <option v-for="candidate in targetCandidates" :key="candidate.key" :value="candidate.address">
                  {{ shortenMiddle(candidate.address, 16, 12) }} — rows {{ candidate.rowNumbers.join(', ') }}
                </option>
              </select>
              <button class="secondary-button small-button" type="button" @click="simpleProcessData" :disabled="!selectedTargetAddress || isProcessing">Run checks for selected address</button>
            </div>

          </div>

          <div class="simple-view-right">
            <div class="simple-next-actions-card">
              <button
                class="primary-button simple-main-action"
                type="button"
                @click="simpleRunGuidedAction"
                :disabled="simplePrimaryActionDisabled"
              >
                {{ simplePrimaryActionText }}
              </button>
              <div class="simple-next-action-heading simple-next-action-heading-after">
                <span class="mini-label">Guided next step</span>
                <strong>{{ simplePrimaryActionText }}</strong>
              </div>
              <p class="simple-action-hint">{{ simpleGuidedActionHint }}</p>
              <div v-if="simpleResultReadyForContinue" class="simple-decision-locked">
                The decision was applied for this step. Continue with the result, or open advanced view to inspect/override.
              </div>
              <div v-if="!caseBuddyEmbedded" class="direct-auto-card">
                <div class="direct-auto-head">
                  <div>
                    <span class="mini-label">Standalone Auto</span>
                    <strong>{{ directAutomationActive ? 'Auto running' : 'Run until Flow Complete' }}</strong>
                  </div>
                  <button
                    class="direct-auto-run-button"
                    type="button"
                    @click="startDirectAutomationLoop"
                    :disabled="!directAutomationCanStart"
                  >
                    {{ directAutomationButtonText }}
                  </button>
                </div>
                <label class="direct-auto-speed">
                  <span>Speed multiplier <strong>{{ directAutomationSpeedLabel }}</strong></span>
                  <input
                    v-model.number="directAutomationSpeedMultiplier"
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.25"
                  />
                </label>
                <p class="direct-auto-hint">Patient auto uses the guided decision flow, absorbs each verified result, selects the next unresolved ending, and pauses on review/guardrails.</p>
                <p v-if="directAutomationRunState.status !== 'idle'" class="direct-auto-status">{{ directAutomationStatusMessage }}</p>
              </div>
              <div class="simple-export-actions">
                <button class="ghost-button" type="button" @click="copySimpleOutputToClipboard" :disabled="isProcessing || !simpleHasOutputRows">Copy raw output</button>
                <button class="ghost-button" type="button" @click="downloadSimpleOutputCsv" :disabled="isProcessing || !simpleHasOutputRows">Download CSV</button>
                <button class="ghost-button simple-advanced-button" type="button" @click="closeSimpleView">Advanced view</button>
              </div>
            </div>

            <div :class="['simple-status-card', `simple-status-${simpleViewSummary.status}`]">
              <span class="mini-label">Current result</span>
              <h3>{{ simpleViewSummary.title }}</h3>
              <p>{{ simpleViewSummary.body }}</p>
              <div class="simple-summary-grid">
                <div><span>Entries</span><strong>{{ formatInteger(parsedEntries.length) }}</strong></div>
                <div><span>Checks</span><strong>{{ countLookupResult ? 'Done' : 'Needed' }}</strong></div>
                <div><span>Decision</span><strong>{{ simpleDecisionRecorded ? 'Recorded' : countLookupResult ? 'Ready' : 'Needed' }}</strong></div>
                <div><span>Flow</span><strong>{{ flowCompletionResult?.done ? 'Complete' : `${flowCompletionResult?.incompleteCount || 0} open` }}</strong></div>
              </div>
            </div>

            <div v-if="autoDecisionPreview" :class="['simple-recommendation-card', `simple-recommendation-${autoDecisionPreview.status}`]">
              <div class="simple-card-heading">
                <span class="mini-label">Recommended next action</span>
                <strong>{{ autoDecisionPreview.title }}</strong>
              </div>
              <p>{{ autoDecisionPreview.summary }}</p>
            </div>

            <div v-if="traceResult" class="simple-result-card">
              <strong v-if="getTool88CurrentDormantResult() && !traceResult.preventPathEnding">Dormant funds detected</strong>
              <strong v-else>{{ traceResult.exitRows.length }} {{ traceResult.analysisDirection === 'reverse' ? 'source' : 'hop' }}{{ traceResult.exitRows.length === 1 ? '' : 's' }} found</strong>
              <p v-if="traceResult.exitRows.length && traceResult.outputMutationVerified !== true">Tool 88 found solved row{{ traceResult.exitRows.length === 1 ? '' : 's' }}, but output insertion is not verified. Open advanced view before continuing.</p>
              <p v-else-if="traceResult.exitRows.length">The solved row{{ traceResult.exitRows.length === 1 ? '' : 's' }} are ready in the output data and insertion was verified.</p>
              <p v-else-if="getTool88CurrentDormantResult() && !traceResult.preventPathEnding">{{ getTool88CurrentDormantResult().note }}</p>
              <p v-else>No qualifying hop was found. Tool 88 treated this as dormant/path-ending unless a safety guard prevented finalization.</p>
            </div>

            <div v-if="pathEndingResult" class="simple-result-card"><strong>Path ending recorded</strong><p>{{ pathEndingResult.note }}</p></div>
            <div v-if="swapApplyResult" class="simple-result-card"><strong>Swap result ready</strong><p>{{ swapApplyResult.note }}</p></div>
            <div v-if="bridgeApplyResult" class="simple-result-card"><strong>Bridge result ready</strong><p>{{ bridgeApplyResult.note }}</p></div>
            <div v-if="flowCompletionMessage" class="simple-flow-message">{{ flowCompletionMessage }}</div>
            <div v-if="simpleViewMessage" class="simple-flow-message">{{ simpleViewMessage }}</div>
          </div>
        </div>
      </section>
    </div>
    <section class="hero-card">
      <div class="hero-copy">
        <div class="eyebrow">Case Buddy / Tool 88</div>
        <h1>Tool 88 - Hop Logic Solver</h1>
        <p>
          Load a Case Buddy-style transaction entry, choose the tracing direction and hop logic,
          run a lightweight server count/check pass, then either intentionally end the path or
          trace through to the next hop.
        </p>
      </div>

      <div class="hero-stats">
        <div class="hero-stat">
          <span>Mode</span>
          <strong>{{ directionLabel }} / {{ hopLogicTypeLabel }}</strong>
        </div>
        <div class="hero-stat">
          <span>Current step</span>
          <strong>{{ finalDecisionMade ? 'Review output' : countLookupResult ? 'Choose next action' : 'Process + checks' }}</strong>
        </div>
      </div>
    </section>

    <section class="workflow-shell">
      <div class="workflow-steps" aria-label="Tool 88 workflow progress">
        <div
          v-for="step in workflowSteps"
          :key="step.number"
          :class="['workflow-step', `workflow-step-${step.status}`]"
        >
          <div class="step-number">{{ step.number }}</div>
          <div class="step-body">
            <strong>{{ step.title }}</strong>
            <span>{{ step.text }}</span>
          </div>
          <span class="step-status">{{ step.statusLabel }}</span>
        </div>
      </div>
    </section>

    <section class="stage-card stage-card-primary">
      <div class="stage-header">
        <div>
          <div class="stage-kicker">Step 1</div>
          <h2>Load the entry and choose settings</h2>
        </div>
        <span class="stage-badge">Settings saved locally</span>
      </div>

      <p class="stage-copy">
        Paste the complete entry you want to trace into the input spreadsheet. Column B starts each
        transaction entry; continuation rows with blank Column B stay attached to that entry. Your
        direction and hop-logic choices are remembered on this browser.
      </p>

      <div class="settings-grid settings-grid-user">
        <label class="setting-field">
          <span>Direction</span>
          <select v-model="traceDirection" :disabled="isProcessing">
            <option value="forward">Forward: follow Column D / To Address</option>
            <option value="reverse">Reverse: follow Column C / From Address</option>
          </select>
        </label>

        <label class="setting-field">
          <span>Hop logic type</span>
          <select v-model="hopLogicType" :disabled="isProcessing">
            <option value="NEXT_CHRONOLOGICAL">Next chronological exit</option>
            <option value="FIFO">FIFO</option>
            <option value="LIFO">LIFO / Post Transaction Deposit Queue</option>
          </select>
        </label>

        <label class="setting-field">
          <span>Tool 88 notes</span>
          <select v-model="tool88NoteColumn" :disabled="isProcessing">
            <option value="J">Add to Column J / Notes</option>
            <option value="W">Put in Column W / Tool 88 Notes</option>
          </select>
        </label>

        <label class="setting-field setting-field-checkbox">
          <span>Additional service checks</span>
          <label class="toggle-row">
            <input
              type="checkbox"
              v-model="enableAdditionalServiceChecks"
              :disabled="isProcessing"
            />
            <span>Stop early if fetched activity looks like an unattributed exchange hot wallet or service address</span>
          </label>
        </label>

        <label class="setting-field setting-field-checkbox">
          <span>Native amount/date match shortcut</span>
          <label class="toggle-row">
            <input
              type="checkbox"
              v-model="enableNativeDirectSpendMatchGuard"
              :disabled="isProcessing || caseBuddyNativeDirectSpendGuardForcedOff"
            />
            <span>Opt in to amount/date matching for native-asset spends.</span>
          </label>
        </label>

        <label class="setting-field setting-field-checkbox">
          <span>Session cache</span>
          <label class="toggle-row">
            <input
              type="checkbox"
              v-model="enableSessionDataCache"
              :disabled="isProcessing"
            />
            <span>Reuse count and transaction-history fetches already pulled in this tab</span>
          </label>
        </label>
      </div>

      <p class="settings-note settings-note-actions">
        <span>Tool 88 remembers these settings on this browser. Generated notes can stay in Column J or be separated into Column W. Additional service checks are on by default. Session cache keeps fetched count/history data only in this tab; refreshing the page clears it.</span>
        <span class="settings-note-button-row">
          <button class="ghost-button text-button" @click="clearSessionDataCache" :disabled="isProcessing || !hasSessionDataCacheEntries">
            Clear session cache
          </button>
          <button class="ghost-button text-button" @click="resetUserSettings" :disabled="isProcessing">
            Reset settings
          </button>
        </span>
      </p>
      <p v-if="enableSessionDataCache" class="settings-note cache-note">
        <span>{{ sessionDataCacheSummary }}</span>
      </p>

      <details class="flow-completion-settings">
        <summary>
          <span>Advanced hop options</span>
          <small>Optional WayFinder-style deposit handling</small>
        </summary>
        <div class="flow-settings-grid">
          <label class="setting-field setting-field-checkbox flow-setting-wide">
            <span>Deposit to same-entity wallet probe</span>
            <label class="toggle-row">
              <input type="checkbox" v-model="enableDepositEntityHopExtension" :disabled="isProcessing" />
              <span>When a deposit address is found, trace one hop further and keep the hop only if the next address resolves to the same entity.</span>
            </label>
            <small>Off by default. Example: Binance Deposit → Binance / Binance Hot Wallet is kept; an unknown or unrelated next hop is dropped and the original deposit remains Path ends.</small>
          </label>
        </div>
      </details>

      <details class="flow-completion-settings">
        <summary>
          <span>Flow completion options</span>
          <small>Used by the unresolved-ending loop</small>
        </summary>
        <div class="flow-settings-grid">
          <label class="setting-field setting-field-checkbox">
            <span>Low-value ending rule</span>
            <label class="toggle-row">
              <input type="checkbox" v-model="flowCompletionValueTooLowEnabled" :disabled="isProcessing" />
              <span>Mark tiny unresolved endings instead of tracing through</span>
            </label>
          </label>

          <label class="setting-field">
            <span>Max manual depth</span>
            <input v-model.number="flowCompletionMaxDepth" type="number" min="1" max="500" :disabled="isProcessing" />
          </label>

          <label class="setting-field">
            <span>BTC / WBTC below</span>
            <input v-model.number="lowValueThresholdBtc" type="number" min="0" step="0.00000001" :disabled="isProcessing || !flowCompletionValueTooLowEnabled" />
          </label>

          <label class="setting-field">
            <span>ETH / WETH / BNB below</span>
            <input v-model.number="lowValueThresholdNative" type="number" min="0" step="0.0001" :disabled="isProcessing || !flowCompletionValueTooLowEnabled" />
          </label>

          <label class="setting-field">
            <span>USDT / USDC / DAI / USDD below</span>
            <input v-model.number="lowValueThresholdStable" type="number" min="0" step="1" :disabled="isProcessing || !flowCompletionValueTooLowEnabled" />
          </label>

          <label class="setting-field">
            <span>Other tokens below</span>
            <input v-model.number="lowValueThresholdOther" type="number" min="0" step="1" :disabled="isProcessing || !flowCompletionValueTooLowEnabled" />
          </label>
          <label class="setting-field flow-setting-wide">
            <span>Quick extra trace-through labels</span>
            <textarea
              v-model="flowCompletionTraceThroughEntitiesText"
              rows="2"
              :disabled="isProcessing"
              placeholder="Personal Wallet, Self-Custody Wallet"
            ></textarea>
            <small>Optional exact labels. Use the Trace-through entity manager below for aliases, addresses, restore/hide, and shipped data.</small>
          </label>

        </div>
      </details>

      <details class="trace-through-entity-manager-panel token-manager-panel">
        <summary>
          <span>Trace-through entity manager</span>
          <small>{{ traceThroughEntitySummary }}</small>
        </summary>

        <div class="token-manager-grid trace-through-entity-manager-grid">
          <div class="token-manager-card">
            <h3>Add or update a trace-through entity</h3>
            <p>
              These names, aliases, and addresses override attribution/service checks. If a selected address or attribution matches here,
              Tool 88 treats it as trace-through-friendly instead of a path ending.
            </p>
            <div class="token-form-grid trace-through-entity-form-grid">
              <label class="setting-field">
                <span>Entity name</span>
                <input v-model="traceThroughEntityDraft.canonicalName" placeholder="Example Entity Name" :disabled="isProcessing" />
              </label>
              <label class="setting-field">
                <span>Category / note</span>
                <input v-model="traceThroughEntityDraft.note" placeholder="Personal wallet, investigator-controlled, etc." :disabled="isProcessing" />
              </label>
              <label class="setting-field trace-through-entity-wide-field">
                <span>Aliases</span>
                <textarea
                  v-model="traceThroughEntityDraft.aliasesText"
                  rows="3"
                  placeholder="ExampleEntityName&#10;Example_Entity_Name&#10;The Example Entity Name Thing"
                  :disabled="isProcessing"
                ></textarea>
              </label>
              <label class="setting-field trace-through-entity-wide-field">
                <span>Addresses</span>
                <textarea
                  v-model="traceThroughEntityDraft.addressesText"
                  rows="3"
                  placeholder="0x91023...&#10;0x123444..."
                  :disabled="isProcessing"
                ></textarea>
              </label>
            </div>
            <div class="token-manager-actions">
              <button class="secondary-button small-button" @click="addOrUpdateTraceThroughEntityOverride" :disabled="isProcessing">
                Add / Update Entity
              </button>
              <button class="ghost-button small-button" @click="resetTraceThroughEntityDraft" :disabled="isProcessing">
                Clear Fields
              </button>
            </div>
          </div>

          <div class="token-manager-card">
            <h3>Import / export local entity overrides</h3>
            <p>
              User-added entities and hidden shipped rows are stored in this browser. The shipped list is maintained separately in
              <code>src/data/tool88TraceThroughEntityCatalog.generated.js</code>.
            </p>
            <textarea
              v-model="traceThroughEntityImportText"
              class="token-import-textarea"
              placeholder='{ "entities": [ { "canonicalName": "Example Entity Name", "aliases": [], "addresses": [] } ] }'
              :disabled="isProcessing"
            ></textarea>
            <div class="token-manager-actions">
              <button class="secondary-button small-button" @click="importTraceThroughEntityRegistryFromText" :disabled="isProcessing">
                Import User Entities
              </button>
              <button class="ghost-button small-button" @click="downloadTraceThroughEntityOverrides" :disabled="isProcessing">
                Export User Entities
              </button>
              <button class="ghost-button small-button" @click="restoreAllHiddenTraceThroughEntities" :disabled="isProcessing || !removedTraceThroughEntityCount">
                Restore Hidden Shipped
              </button>
              <button class="ghost-button small-button danger-text" @click="resetTraceThroughEntityOverrides" :disabled="isProcessing">
                Reset User Entities
              </button>
            </div>
          </div>
        </div>

        <div v-if="traceThroughEntityMessage" class="token-manager-message">{{ traceThroughEntityMessage }}</div>

        <div class="token-registry-preview trace-through-entity-registry-preview">
          <div class="token-preview-header">
            <div>
              <strong>Effective trace-through entity registry</strong>
              <span>Matches selected target addresses and attribution labels before Tool 88 applies service/swap/bridge/path-ending rules.</span>
            </div>
            <label class="token-search-label">
              <span>Search</span>
              <input v-model="traceThroughEntitySearch" placeholder="Entity, alias, or address" />
            </label>
          </div>
          <div v-if="!effectiveTraceThroughEntityRegistry.length" class="empty-token-registry">
            No shipped or user-added trace-through entities are active. Tool 88 will use only address-like and domain/personal-wallet fallback patterns.
          </div>
          <table v-else class="token-registry-table trace-through-entity-registry-table">
            <thead>
              <tr>
                <th>Entity</th>
                <th>Aliases</th>
                <th>Addresses</th>
                <th>Source</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entity in filteredTraceThroughEntityPreview" :key="entity.key">
                <td>
                  <strong>{{ entity.canonicalName }}</strong>
                  <small v-if="entity.note">{{ entity.note }}</small>
                </td>
                <td>{{ formatTraceThroughEntityAliases(entity) || '—' }}</td>
                <td><code>{{ formatTraceThroughEntityAddresses(entity) || '—' }}</code></td>
                <td>{{ entity.source === 'custom' ? 'User' : 'Shipped' }}</td>
                <td>
                  <button class="ghost-button text-button danger-text" @click="removeTraceThroughEntityEntry(entity)" :disabled="isProcessing">
                    {{ entity.source === 'custom' ? 'Delete' : 'Hide' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="effectiveTraceThroughEntityRegistry.length > filteredTraceThroughEntityPreview.length" class="token-preview-note">
            Showing {{ filteredTraceThroughEntityPreview.length }} of {{ effectiveTraceThroughEntityRegistry.length }} active trace-through entities. Use search to narrow the list.
          </p>
        </div>
      </details>

      <details class="token-manager-panel">
        <summary>
          <span>Token manager</span>
          <small>{{ tokenRegistrySummary }}</small>
        </summary>

        <div class="token-manager-grid">
          <div class="token-manager-card">
            <h3>Add or update a trusted token</h3>
            <p>
              User-added rows are stored in this browser and override the shipped WayFinder token list.
              This prepares Tool 88 to validate ERC20/TRC20-style transfers by contract address.
            </p>
            <div class="token-form-grid">
              <label class="setting-field">
                <span>Symbol</span>
                <input v-model="tokenManagerDraft.symbol" placeholder="USDT" :disabled="isProcessing" />
              </label>
              <label class="setting-field">
                <span>Chain / token type</span>
                <select v-model="tokenManagerDraft.chainKey" :disabled="isProcessing">
                  <option v-for="chain in tokenChainOptions" :key="chain.value" :value="chain.value">
                    {{ chain.label }}
                  </option>
                </select>
              </label>
              <label class="setting-field">
                <span>Contract address / mint</span>
                <input v-model="tokenManagerDraft.contractAddress" placeholder="0x... or TRON/Solana contract" :disabled="isProcessing" />
              </label>
              <label class="setting-field">
                <span>Decimals</span>
                <input v-model.number="tokenManagerDraft.decimals" type="number" min="0" max="36" :disabled="isProcessing" />
              </label>
            </div>
            <div class="token-manager-actions">
              <button class="secondary-button small-button" @click="addOrUpdateTokenOverride" :disabled="isProcessing">
                Add / Update Token
              </button>
              <button class="ghost-button small-button" @click="resetTokenManagerDraft" :disabled="isProcessing">
                Clear Fields
              </button>
            </div>
          </div>

          <div class="token-manager-card">
            <h3>Import WayFinder token export</h3>
            <p>
              Paste the JSON output from the Unity editor exporter. Importing here is useful for testing;
              for production, copy the generated JS module to src/data/tool88TokenCatalog.generated.js.
            </p>
            <textarea
              v-model="tokenManagerImportText"
              class="token-import-textarea"
              placeholder='{ "tokens": [ ... ] }'
              :disabled="isProcessing"
            ></textarea>
            <div class="token-manager-actions">
              <button class="secondary-button small-button" @click="importTokenRegistryFromText" :disabled="isProcessing">
                Import as User Tokens
              </button>
              <button class="ghost-button small-button" @click="downloadTokenManagerOverrides" :disabled="isProcessing">
                Export User Tokens
              </button>
              <button class="ghost-button small-button danger-text" @click="resetTokenManagerOverrides" :disabled="isProcessing">
                Reset User Tokens
              </button>
            </div>
          </div>
        </div>

        <div v-if="tokenManagerMessage" class="token-manager-message">{{ tokenManagerMessage }}</div>

        <div class="token-registry-preview">
          <div class="token-preview-header">
            <div>
              <strong>Effective token registry</strong>
              <span>Used for token contract lookup and fake-token warnings in the trace inspector.</span>
            </div>
            <label class="token-search-label">
              <span>Search</span>
              <input v-model="tokenManagerSearch" placeholder="USDT, 0x..., TRON" />
            </label>
          </div>
          <div v-if="!effectiveTokenRegistry.length" class="empty-token-registry">
            No shipped or user-added tokens are active yet. Tool 88 will still run, but token-contract
            validation will stay unavailable until a seed or override is added.
          </div>
          <table v-else class="token-registry-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Chain</th>
                <th>Decimals</th>
                <th>Contract</th>
                <th>Source</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="token in filteredTokenRegistryPreview" :key="token.key">
                <td>{{ token.symbol }}</td>
                <td>{{ formatTokenChainLabel(token.chainKey) }}</td>
                <td>{{ token.decimals }}</td>
                <td><code>{{ shortenMiddle(token.contractAddress, 12, 10) }}</code></td>
                <td>{{ token.source === 'custom' ? 'User' : 'Shipped' }}</td>
                <td>
                  <button class="ghost-button text-button danger-text" @click="removeTokenRegistryEntry(token)" :disabled="isProcessing">
                    {{ token.source === 'custom' ? 'Delete' : 'Hide' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="effectiveTokenRegistry.length > filteredTokenRegistryPreview.length" class="token-preview-note">
            Showing {{ filteredTokenRegistryPreview.length }} of {{ effectiveTokenRegistry.length }} active token rows. Use search to narrow the list.
          </p>
        </div>
      </details>

      <div v-if="selectedTokenResolution" :class="['token-resolution-panel', selectedTokenResolution.status]">
        <div>
          <span class="mini-label">Followed token contract check</span>
          <strong>{{ selectedTokenResolution.title }}</strong>
        </div>
        <p>{{ selectedTokenResolution.summary }}</p>
      </div>

      <div class="action-toolbar">
        <button class="primary-button small-button" @click="processData" :disabled="isProcessing">
          {{ countLookupResult ? 'Refresh Checks' : 'Process Entry + Checks' }}
        </button>

        <button class="ghost-button small-button" @click="clearAll" :disabled="isProcessing">Clear Data</button>

        <details class="sample-data-panel">
          <summary>Sample data</summary>
          <div class="example-loader">
            <label for="tool88-example-select">Example</label>
            <select id="tool88-example-select" v-model="selectedExampleKey" :disabled="isProcessing">
              <option
                v-for="example in exampleOptions"
                :key="example.value"
                :value="example.value"
              >
                {{ example.label }}
              </option>
            </select>
            <button class="small-button" @click="loadSelectedExample" :disabled="isProcessing">Load</button>
          </div>
        </details>
      </div>

      <div v-if="parsedEntries.length > 1" class="decision-panel">
        <label class="setting-field wide-field">
          <span>Entry to solve</span>
          <select v-model="selectedEntryKey" :disabled="isProcessing">
            <option
              v-for="entry in parsedEntries"
              :key="entry.key"
              :value="entry.key"
            >
              {{ formatEntryOption(entry) }}
            </option>
          </select>
        </label>
        <button class="secondary-button small-button" @click="processData" :disabled="isProcessing">Use This Entry</button>
      </div>

      <div v-if="targetCandidates.length > 1" class="decision-panel decision-panel-attention">
        <div class="decision-copy">
          <strong>Ambiguous {{ targetColumnLabel }}</strong>
          <span>
            This entry has multiple possible target addresses. Choose the address you actually want
            Tool 88 to research, then rerun the checks.
          </span>
        </div>
        <label class="setting-field wide-field">
          <span>{{ targetColumnLabel }} to research</span>
          <select v-model="selectedTargetAddress" :disabled="isProcessing">
            <option value="">Select an address...</option>
            <option
              v-for="candidate in targetCandidates"
              :key="candidate.key"
              :value="candidate.address"
            >
              {{ candidate.address }} — rows {{ candidate.rowNumbers.join(', ') }}
            </option>
          </select>
        </label>
        <button class="secondary-button small-button" @click="processData" :disabled="!selectedTargetAddress || isProcessing">
          Lookup Count For Selected Address
        </button>
      </div>
    </section>



    <section class="stage-card flow-completion-card">
      <div class="stage-header">
        <div>
          <div class="stage-kicker">Flow completion helper</div>
          <h2>Find and solve unresolved path endings</h2>
        </div>
        <span :class="['stage-badge', flowCompletionResult?.done ? 'stage-badge-ready' : '']">
          {{ flowCompletionResult ? (flowCompletionResult.done ? 'Flow complete' : `${flowCompletionResult.incompleteCount} unresolved`) : 'Manual loop' }}
        </span>
      </div>

      <p class="stage-copy">
        Scan the whole input sheet for entries that look like unresolved endings. After a trace/path-ending decision,
        use the output as the new input so Tool 88 can continue with the next branch without unattended recursion.
      </p>

      <div class="flow-completion-actions">
        <button class="secondary-button small-button" @click="reviewFlowCompletionStatus" :disabled="isProcessing">
          Review Flow Status
        </button>
        <button class="primary-button small-button" @click="selectNextUnresolvedEnding" :disabled="isProcessing">
          Select Next Unresolved Ending
        </button>
        <button class="secondary-button small-button" @click="applyFlowCompletionMarkers" :disabled="isProcessing">
          Mark Resolved Endings in Column W
        </button>
        <button class="ghost-button small-button" @click="absorbOutputIntoInput" :disabled="isProcessing || !finalDecisionMade">
          Use Output as New Input
        </button>
        <span class="flow-depth-pill">Depth {{ flowCompletionDepth }} / {{ normalizedFlowCompletionMaxDepth }}</span>
      </div>

      <div v-if="!caseBuddyEmbedded" class="direct-auto-card direct-auto-card-advanced">
        <div class="direct-auto-head">
          <div>
            <span class="mini-label">Standalone Auto</span>
            <strong>{{ directAutomationActive ? 'Auto running' : 'Run recommended actions until Flow Complete' }}</strong>
          </div>
          <button
            class="direct-auto-run-button"
            type="button"
            @click="startDirectAutomationLoop({ source: 'advanced-flow-card' })"
            :disabled="!directAutomationCanStart"
          >
            {{ directAutomationButtonText }}
          </button>
        </div>
        <label class="direct-auto-speed">
          <span>Speed multiplier <strong>{{ directAutomationSpeedLabel }}</strong></span>
          <input
            v-model.number="directAutomationSpeedMultiplier"
            type="range"
            min="0.5"
            max="3"
            step="0.25"
          />
        </label>
        <p class="direct-auto-hint">Uses the standalone Tool 88 run state, not the Case Buddy embedded automation state. Stop Auto pauses after the current provider request finishes.</p>
        <p v-if="directAutomationRunState.status !== 'idle'" class="direct-auto-status">{{ directAutomationStatusMessage }}</p>
      </div>

      <div v-if="flowCompletionMessage" class="flow-completion-message">{{ flowCompletionMessage }}</div>

      <div v-if="flowCompletionResult" :class="['flow-completion-summary', flowCompletionResult.done ? 'flow-completion-complete' : 'flow-completion-incomplete']">
        <div class="flow-summary-head">
          <strong>{{ flowCompletionResult.summary }}</strong>
          <span>{{ flowCompletionResult.done ? 'No unresolved path endings were found.' : 'Select or solve the next ending, then absorb the output to continue.' }}</span>
        </div>
        <div class="flow-summary-grid">
          <div><span>Entries</span><strong>{{ formatInteger(flowCompletionResult.totalEntries) }}</strong></div>
          <div><span>Path endings</span><strong>{{ formatInteger(flowCompletionResult.endingCount) }}</strong></div>
          <div><span>Complete endings</span><strong>{{ formatInteger(flowCompletionResult.completeCount) }}</strong></div>
          <div><span>Unresolved endings</span><strong>{{ formatInteger(flowCompletionResult.incompleteCount) }}</strong></div>
          <div><span>Skipped by depth</span><strong>{{ formatInteger(flowCompletionResult.depthSkippedCount) }}</strong></div>
        </div>

        <div v-if="flowCompletionResult.incomplete.length" class="flow-next-panel">
          <strong>Next unresolved ending</strong>
          <span>{{ flowCompletionResult.incomplete[0].label }} — {{ flowCompletionResult.incomplete[0].reason }}</span>
        </div>

        <details v-if="flowCompletionResult.endings.length" class="flow-ending-list">
          <summary>Review path endings</summary>
          <table>
            <thead>
              <tr>
                <th>Row</th>
                <th>Hash</th>
                <th>Target</th>
                <th>Status</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="ending in flowCompletionResult.endings"
                :key="ending.entryKey"
                :class="ending.complete ? 'flow-ending-complete' : 'flow-ending-incomplete'"
              >
                <td>{{ ending.rowNumber }}</td>
                <td :title="ending.txHash">{{ shortenMiddle(ending.txHash, 12, 10) }}</td>
                <td :title="ending.targetAddress">{{ ending.targetAddress ? shortenMiddle(ending.targetAddress, 12, 10) : ending.targetStatus }}</td>
                <td>{{ ending.complete ? 'Complete' : 'Unresolved' }}</td>
                <td>{{ ending.reason }}</td>
              </tr>
            </tbody>
          </table>
        </details>
      </div>
    </section>

    <section v-if="hasProcessedInput || countLookupResult" class="stage-card">
      <div class="stage-header">
        <div>
          <div class="stage-kicker">Step 2</div>
          <h2>Process data and run pre-trace lookups</h2>
        </div>
        <span :class="['stage-badge', countLookupResult ? 'stage-badge-ready' : '']">
          {{ countLookupResult ? 'Count + checks complete' : 'Waiting for count' }}
        </span>
      </div>

      <p v-if="!countLookupResult" class="stage-copy">
        Tool 88 has parsed the input and is ready for the transaction-count, attribution, and transaction-signal checks once a target
        address is selected.
      </p>

      <div v-if="countLookupResult" :class="['count-panel', countSeverityClass]">
        <div class="count-headline">
          <div>
            <span class="mini-label">Selected address</span>
            <strong>{{ countLookupResult.address }}</strong>
          </div>
          <div>
            <span class="mini-label">Transaction count</span>
            <strong>{{ formatTransactionCount(countLookupResult.transactionCount, countLookupResult.countAvailable) }}</strong>
          </div>
          <div>
            <span class="mini-label">Recommendation</span>
            <strong>{{ countLookupResult.recommendation }}</strong>
          </div>
        </div>
        <div class="meta-grid compact-meta-grid">
          <div><b>Direction</b>: {{ directionLabel }}</div>
          <div><b>Hop logic</b>: {{ hopLogicTypeLabel }}</div>
          <div><b>Lookup source</b>: {{ countLookupResult.source }}</div>
          <div><b>Provider</b>: {{ countLookupResult.providerLabel || countLookupResult.provider || 'Unavailable' }}</div>
          <div><b>Count route</b>: {{ countLookupResult.routeLabel || countLookupResult.route || 'Unavailable' }}</div>
          <div><b>Network</b>: {{ countLookupResult.chainLabel || countLookupResult.chainName || 'Inferred' }}</div>
          <div><b>Count type</b>: {{ formatCountKind(countLookupResult.countKind) }}</div>
          <div><b>Target column</b>: {{ targetColumnLabel }}</div>
          <div v-if="countLookupResult.countUnavailableReason"><b>Count note</b>: {{ countLookupResult.countUnavailableReason }}</div>
        </div>
      </div>
    </section>

    <section v-if="countLookupResult" class="stage-card">
      <div class="stage-header">
        <div>
          <div class="stage-kicker">Step 3</div>
          <h2>Review pre-trace checks</h2>
        </div>
        <span class="stage-badge">Expandable check pass</span>
      </div>

      <p class="stage-copy">
        This is where Tool 88 can grow chain-specific checks before fetching full transaction
        history: transaction volume, attribution, EVM/TRON event-log markers, and BTC OP_RETURN/memo
        checks.
      </p>

      <div
        v-if="attributionLookupResult"
        :class="['attribution-panel', `attribution-panel-${attributionLookupResult.adviceStatus || 'neutral'}`]"
      >
        <div class="attribution-headline">
          <div>
            <span class="mini-label">Attribution</span>
            <strong>{{ attributionLookupResult.displayLabel || 'No server/vendor label found' }}</strong>
          </div>
          <div>
            <span class="mini-label">Classification</span>
            <strong>{{ formatAttributionClassification(attributionLookupResult.classification || attributionLookupResult.entityType) }}</strong>
          </div>
          <div>
            <span class="mini-label">Source</span>
            <strong>{{ attributionLookupResult.sourceLabel }}</strong>
          </div>
        </div>
        <p>{{ attributionLookupResult.recommendation }}</p>
        <small v-if="attributionLookupResult.signalsText">Signals: {{ attributionLookupResult.signalsText }}</small>
      </div>

      <div
        v-if="transactionSignalResult"
        :class="['signal-panel', `signal-panel-${transactionSignalResult.adviceStatus || 'neutral'}`]"
      >
        <div class="signal-headline">
          <div>
            <span class="mini-label">Chain signal</span>
            <strong>{{ transactionSignalResult.summaryLabel || transactionSignalResult.classificationLabel }}</strong>
          </div>
          <div>
            <span class="mini-label">Simple transfer confidence</span>
            <strong>{{ transactionSignalResult.simpleTransferConfidenceLabel }}</strong>
          </div>
          <div>
            <span class="mini-label">Evidence</span>
            <strong>{{ transactionSignalResult.evidenceLabel }}</strong>
          </div>
        </div>
        <p>{{ transactionSignalResult.recommendation }}</p>
        <small v-if="transactionSignalResult.detailText">{{ transactionSignalResult.detailText }}</small>
      </div>

      <div class="checks-grid">
        <div
          v-for="check in preTraceChecks"
          :key="check.key"
          :class="['check-card', `check-card-${check.status}`]"
        >
          <div class="check-title-row">
            <strong>{{ check.title }}</strong>
            <span>{{ check.statusLabel }}</span>
          </div>
          <p>{{ check.summary }}</p>
          <small v-if="check.detail">{{ check.detail }}</small>
        </div>
      </div>
    </section>

    <section v-if="countLookupResult" class="stage-card decision-stage">
      <div class="stage-header">
        <div>
          <div class="stage-kicker">Step 4</div>
          <h2>Decide whether the path ends or traces through</h2>
        </div>
        <span :class="['stage-badge', finalDecisionMade ? 'stage-badge-ready' : '']">
          {{ finalDecisionMade ? 'Decision recorded' : 'Decision needed' }}
        </span>
      </div>

      <p class="stage-copy">
        Use the automatic recommendation when the checks are decisive, or keep using the manual
        controls when analyst judgement is still needed. Tool 88 will only auto-trace when the
        address has no meaningful service attribution, the count is within the automatic limit,
        and the transaction-level signal looks like a simple transfer.
      </p>

      <div
        v-if="autoDecisionPreview"
        :class="['auto-decision-panel', `auto-decision-panel-${autoDecisionPreview.status}`]"
      >
        <div class="auto-decision-head">
          <div>
            <span class="mini-label">Auto recommendation</span>
            <strong>{{ autoDecisionPreview.title }}</strong>
          </div>
          <span>{{ autoDecisionPreview.confidenceLabel }}</span>
        </div>
        <p>{{ autoDecisionPreview.summary }}</p>
        <ul v-if="autoDecisionPreview.reasons.length" class="auto-decision-reasons">
          <li v-for="reason in autoDecisionPreview.reasons" :key="reason">{{ reason }}</li>
        </ul>
      </div>

      <div class="decision-actions">
        <button
          class="primary-button auto-decision-button"
          @click="applyAutomaticDecision"
          :disabled="!autoDecisionPreview || autoDecisionPreview.action === 'review' || isProcessing || (autoDecisionPreview.action === 'trace' && !canTraceThrough)"
        >
          {{ autoDecisionPreview ? autoDecisionPreview.buttonLabel : 'Auto Decide' }}
        </button>
        <button class="secondary-button" @click="markPathEnding" :disabled="isProcessing">
          Mark as Path Ending
        </button>
        <button class="review-button" @click="openSwapExplorer" :disabled="isProcessing || !selectedEntry">
          Treat as Swap / Open Explorer
        </button>
        <button class="review-button" @click="openBridgeSolver()" :disabled="isProcessing || !selectedEntry">
          Treat as Bridge / Open Solver
        </button>
        <button
          class="primary-button"
          @click="traceThrough"
          :disabled="!canTraceThrough || isProcessing"
        >
          Trace Through Selected Address
        </button>
      </div>

      <div v-if="!canTraceThrough && countLookupResult" class="decision-hint">
        Trace-through is unavailable when the selected address is blocked by Tool 88's trace gate or no valid
        target address is selected. You can still mark the path as ending.
      </div>

      <div v-if="pathEndingResult" class="result-callout result-callout-ending">
        <strong>Path ending recorded.</strong>
        <span>{{ pathEndingResult.note }}</span>
      </div>

      <div v-if="traceResult" class="result-callout result-callout-trace">
        <strong v-if="getTool88CurrentDormantResult() && !traceResult.preventPathEnding">Dormant funds detected.</strong>
        <strong v-else>
          {{ traceResult.exitRows.length }}
          {{ traceResult.analysisDirection === 'reverse' ? 'prior source' : 'exit' }}{{ traceResult.exitRows.length === 1 ? '' : 's' }} found.
        </strong>
        <span v-if="traceResult.exitRows.length && traceResult.outputMutationVerified !== true">
          Tool 88 found solved row(s), but output insertion has not been verified. The simple workflow will not advance until the output change is confirmed.
        </span>
        <span v-else-if="traceResult.exitRows.length">
          {{ traceResult.analysisDirection === 'reverse'
            ? 'The solved prior-source row(s) were inserted below the selected input entry in the output spreadsheet and the output change was verified.'
            : 'The solved next-hop row(s) were inserted below the selected input entry in the output spreadsheet and the output change was verified.' }}
        </span>
        <span v-else-if="traceResult.preventPathEnding">
          Tool 88 could not resolve the reverse source/output confidently enough, so the output spreadsheet was left unchanged.
        </span>
        <span v-else-if="getTool88CurrentDormantResult() && !traceResult.preventPathEnding">
          {{ getTool88CurrentDormantResult().note }}
        </span>
        <span v-else>
          No qualifying exit was found, so the selected entry was finalized as a path ending.
        </span>
      </div>

      <div v-if="swapApplyResult" class="result-callout result-callout-swap">
        <strong>Swap hop recorded.</strong>
        <span>{{ swapApplyResult.note }}</span>
      </div>

      <div v-if="swapExplorer" class="swap-explorer-panel">
        <div class="swap-explorer-header">
          <div>
            <span class="mini-label">Transaction explorer</span>
            <strong>{{ swapExplorerStatusLabel }}</strong>
          </div>
          <div class="swap-explorer-meta">
            <span>{{ shortenMiddle(swapExplorer.txHash, 12, 10) }}</span>
            <span>Initiator: {{ shortenMiddle(swapExplorer.initiatingAddress, 8, 6) }}</span>
          </div>
        </div>

        <div v-if="swapExplorer.summary" class="swap-explorer-summary">{{ swapExplorer.summary }}</div>

        <div
          v-if="swapAutomaticCandidate"
          :class="['swap-candidate-card', swapAutomaticCandidate.canApply ? 'swap-candidate-ok' : 'swap-candidate-warn']"
        >
          <div>
            <span class="mini-label">Automatic swap solver</span>
            <strong>{{ swapAutomaticCandidate.title }}</strong>
          </div>
          <p>{{ swapAutomaticCandidate.summary }}</p>
          <button
            class="primary-button small-button"
            @click="applySwapCandidate(swapAutomaticCandidate)"
            :disabled="isProcessing || !swapAutomaticCandidate.canApply"
          >
            Apply Swap Result
          </button>
        </div>

        <div class="swap-tabs">
          <button
            v-for="tab in swapExplorerTabs"
            :key="tab.key"
            :class="['swap-tab-button', { active: swapExplorerActiveTab === tab.key }]"
            @click="swapExplorerActiveTab = tab.key"
          >
            {{ tab.label }} <span>{{ tab.rows.length }}</span>
          </button>
        </div>

        <div class="swap-table-wrap">
          <table class="swap-explorer-table">
            <thead>
              <tr>
                <th>Use</th>
                <th>Direction</th>
                <th>Kind</th>
                <th>Date UTC</th>
                <th>From</th>
                <th>To</th>
                <th>Amount</th>
                <th>Token</th>
                <th>Contract / details</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in activeSwapExplorerRows"
                :key="row.key"
                :class="{ 'swap-row-selected': row.key === swapSelectedTransferKey, 'swap-row-incoming': row.direction === 'incoming', 'swap-row-outgoing': row.direction === 'outgoing' }"
              >
                <td>
                  <button
                    v-if="row.canUseAsSwapResult"
                    class="ghost-button text-button"
                    @click="swapSelectedTransferKey = row.key"
                  >
                    Select
                  </button>
                </td>
                <td>{{ formatTraceInspectorDirection(row.direction) }}</td>
                <td>{{ row.kindLabel }}</td>
                <td>{{ row.dateText || 'Unknown' }}</td>
                <td :title="row.fromAddress">{{ shortenMiddle(row.fromAddress, 8, 6) }}</td>
                <td :title="row.toAddress">{{ shortenMiddle(row.toAddress, 8, 6) }}</td>
                <td>{{ formatAmount(row.amount) }}</td>
                <td>{{ row.token || row.tokenSymbol || '' }}</td>
                <td class="swap-detail-cell">{{ row.detailText }}</td>
              </tr>
              <tr v-if="!activeSwapExplorerRows.length">
                <td colspan="9">No rows in this transaction-explorer tab.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="selectedSwapCandidate" class="manual-swap-card">
          <div>
            <span class="mini-label">Manual swap result</span>
            <strong>{{ selectedSwapCandidate.title }}</strong>
          </div>
          <p>{{ selectedSwapCandidate.summary }}</p>
          <button class="primary-button small-button" @click="applySwapCandidate(selectedSwapCandidate)" :disabled="isProcessing || !selectedSwapCandidate.canApply">
            Apply Selected Transfer as Swap Result
          </button>
        </div>

        <div class="swap-explorer-actions">
          <button class="secondary-button small-button" @click="openSwapExplorer" :disabled="isProcessing">Refresh Explorer</button>
          <button class="ghost-button small-button" @click="treatAsSpecialPathEnding('swap')" :disabled="isProcessing">Record as Swap Path Ending Instead</button>
        </div>
      </div>

      <div v-if="bridgeApplyResult" class="result-callout result-callout-bridge">
        <strong>Bridge hop recorded.</strong>
        <span>{{ bridgeApplyResult.note }}</span>
      </div>

      <div v-if="bridgeExplorer" class="bridge-explorer-panel">
        <div class="bridge-explorer-header">
          <div>
            <span class="mini-label">Bridge solver</span>
            <strong>{{ bridgeExplorerStatusLabel }}</strong>
          </div>
          <div class="swap-explorer-meta">
            <span>{{ shortenMiddle(bridgeExplorer.txHash, 12, 10) }}</span>
            <span>Destination hint: {{ bridgeExplorer.destinationNetworkHint || 'TRON-first' }}</span>
            <span v-if="bridgeExplorer.provider">Provider: {{ bridgeExplorer.provider }}</span>
          </div>
        </div>

        <div v-if="bridgeExplorer.summary" class="swap-explorer-summary">{{ bridgeExplorer.summary }}</div>

        <div v-if="bridgeStages.length" class="bridge-stage-grid">
          <div
            v-for="stage in bridgeStages"
            :key="stage.key"
            :class="['bridge-stage-card', `bridge-stage-${stage.status || 'neutral'}`]"
          >
            <span class="mini-label">{{ stage.label }}</span>
            <strong>{{ stage.title }}</strong>
            <p>{{ stage.summary }}</p>
          </div>
        </div>

        <div
          v-if="bridgeBestCandidate"
          :class="['swap-candidate-card', bridgeBestCandidate.canApply ? 'swap-candidate-ok' : 'swap-candidate-warn']"
        >
          <div>
            <span class="mini-label">Best bridge candidate</span>
            <strong class="bridge-candidate-title">{{ bridgeBestCandidate.title }}</strong>
          </div>
          <div v-if="bridgeBestCandidate.destinationAddress" class="bridge-full-value-row">
            <span class="mini-label">Destination</span>
            <code class="bridge-full-value">{{ bridgeBestCandidate.destinationAddress }}</code>
            <button class="ghost-button tiny-button" type="button" @click="copyBridgeValue(bridgeBestCandidate.destinationAddress)">Copy</button>
          </div>
          <p>{{ bridgeBestCandidate.summary }}</p>
          <button
            class="primary-button small-button"
            @click="applyBridgeCandidate(bridgeBestCandidate)"
            :disabled="isProcessing || !bridgeBestCandidate.canApply"
          >
            Apply Bridge Result
          </button>
        </div>

        <div class="swap-explorer-actions">
          <button class="secondary-button small-button" @click="openBridgeSolver()" :disabled="isProcessing">Refresh Bridge Solver</button>
          <button class="secondary-button small-button" @click="openBridgeSolver({ bruteForce: true })" :disabled="isProcessing">Run Brute-force Candidate Scan</button>
          <button class="ghost-button small-button" @click="treatAsSpecialPathEnding('bridge')" :disabled="isProcessing">Record as Bridge Path Ending Instead</button>
          <span v-if="bridgeCopiedValue" class="copy-feedback bridge-copy-feedback">Copied {{ bridgeCopiedValue }}</span>
        </div>

        <div class="swap-table-wrap">
          <table class="swap-explorer-table">
            <thead>
              <tr>
                <th>Use</th>
                <th>Method</th>
                <th>Confidence</th>
                <th>Destination</th>
                <th>Network</th>
                <th>Amount</th>
                <th>Token</th>
                <th>Resolved receiving transfer</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="candidate in bridgeCandidates"
                :key="candidate.key"
                :class="{ 'swap-row-selected': candidate.key === bridgeSelectedCandidateKey, 'bridge-row-high': candidate.confidence === 'high', 'bridge-row-review': candidate.confidence !== 'high' }"
              >
                <td>
                  <button
                    v-if="candidate.canApply"
                    class="ghost-button text-button"
                    @click="bridgeSelectedCandidateKey = candidate.key"
                  >
                    Select
                  </button>
                </td>
                <td>{{ candidate.methodLabel }}</td>
                <td>{{ candidate.confidenceLabel }}</td>
                <td class="bridge-address-cell">
                  <div class="bridge-address-stack" v-if="candidate.destinationAddress">
                    <code class="bridge-full-value">{{ candidate.destinationAddress }}</code>
                    <button class="ghost-button tiny-button" type="button" @click="copyBridgeValue(candidate.destinationAddress)">Copy</button>
                  </div>
                  <span v-else>Unknown</span>
                </td>
                <td>{{ candidate.destinationNetwork || 'Unknown' }}</td>
                <td>{{ candidate.amountDisplay || 'Unknown' }}</td>
                <td>{{ candidate.tokenSymbol || '' }}</td>
                <td class="bridge-resolved-cell">
                  <div v-if="candidate.resolvedTransactionHash" class="bridge-address-stack">
                    <code class="bridge-full-value">{{ candidate.resolvedTransferLabel }}</code>
                    <button class="ghost-button tiny-button" type="button" @click="copyBridgeValue(candidate.resolvedTransactionHash)">Copy hash</button>
                  </div>
                  <span v-else>{{ candidate.resolvedTransferLabel }}</span>
                </td>
                <td class="swap-detail-cell">{{ candidate.reason || candidate.summary }}</td>
              </tr>
              <tr v-if="!bridgeCandidates.length">
                <td colspan="9">No bridge destination candidates were found. Try the brute-force scan or keep this as a bridge path-ending for manual review.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="selectedBridgeCandidate" class="manual-swap-card">
          <div>
            <span class="mini-label">Manual bridge result</span>
            <strong class="bridge-candidate-title">{{ selectedBridgeCandidate.title }}</strong>
          </div>
          <div v-if="selectedBridgeCandidate.destinationAddress" class="bridge-full-value-row">
            <span class="mini-label">Destination</span>
            <code class="bridge-full-value">{{ selectedBridgeCandidate.destinationAddress }}</code>
            <button class="ghost-button tiny-button" type="button" @click="copyBridgeValue(selectedBridgeCandidate.destinationAddress)">Copy</button>
          </div>
          <p>{{ selectedBridgeCandidate.summary }}</p>
          <button class="primary-button small-button" @click="applyBridgeCandidate(selectedBridgeCandidate)" :disabled="isProcessing || !selectedBridgeCandidate.canApply">
            Apply Selected Bridge Candidate
          </button>
        </div>

        <details v-if="bridgeLogRows.length" class="bridge-log-details">
          <summary>Review bridge log rows ({{ bridgeLogRows.length }})</summary>
          <div class="swap-table-wrap">
            <table class="swap-explorer-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Address / contract</th>
                  <th>Topic 0</th>
                  <th>Data preview</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in bridgeLogRows" :key="log.key">
                  <td>{{ log.rowNumber }}</td>
                  <td :title="log.address">{{ shortenMiddle(log.address, 10, 8) }}</td>
                  <td :title="log.topic0">{{ shortenMiddle(log.topic0, 12, 10) }}</td>
                  <td class="swap-detail-cell">{{ log.dataPreview }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </details>
      </div>

      <div v-if="traceFetchProgress || traceInspectorRows.length" class="trace-fetch-panel">
        <div class="trace-fetch-header">
          <div>
            <span class="mini-label">Transaction history fetch</span>
            <strong>{{ traceFetchStatusLabel }}</strong>
          </div>
          <span>{{ traceFetchProgressPercentText }}</span>
        </div>

        <div class="trace-progress-track" aria-label="Transaction fetch progress">
          <div class="trace-progress-fill" :style="{ width: `${traceFetchProgressBarValue}%` }"></div>
        </div>

        <div class="trace-fetch-metrics">
          <span>Fetched {{ formatInteger(traceFetchFetchedCount) }}</span>
          <span>Expected {{ traceFetchExpectedCount >= 0 ? formatInteger(traceFetchExpectedCount) : 'Unknown' }}</span>
          <span v-if="traceFetchProgressPercent !== null && traceFetchProgressPercent > 100">Exceeds prior count</span>
          <span v-if="traceFetchProgress?.trace_fetch?.provider || traceFetchProgress?.provider">Provider: {{ traceFetchProgress?.trace_fetch?.provider || traceFetchProgress.provider }}</span>
          <span v-if="traceFetchProgress?.trace_fetch?.route_label || traceFetchProgress?.trace_route_label">Route: {{ traceFetchProgress?.trace_fetch?.route_label || traceFetchProgress.trace_route_label }}</span>
          <span v-if="traceFetchProgress?.trace_fetch?.requests_made">Requests {{ formatInteger(traceFetchProgress.trace_fetch.requests_made) }}</span>
          <span v-if="traceFetchProgress?.trace_fetch?.pages_completed">Pages {{ formatInteger(traceFetchProgress.trace_fetch.pages_completed) }}</span>
          <span v-if="traceFetchProgress?.trace_fetch?.last_http_code">HTTP {{ traceFetchProgress.trace_fetch.last_http_code }}</span>
        </div>

        <div
          v-if="traceFetchProgress?.trace_fetch?.last_error || traceFetchProgress?.trace_fetch?.message || traceFetchProgress?.trace_fetch?.last_provider_message"
          class="trace-fetch-warning"
        >
          {{ traceFetchProgress.trace_fetch.last_error || traceFetchProgress.trace_fetch.message || traceFetchProgress.trace_fetch.last_provider_message }}
        </div>

        <div
          v-if="traceServiceAssessment"
          :class="['trace-service-assessment', traceServiceAssessment.severity]"
        >
          <div>
            <span class="mini-label">Additional service check</span>
            <strong>{{ traceServiceAssessment.title }}</strong>
          </div>
          <p>{{ traceServiceAssessment.summary }}</p>
          <ul v-if="traceServiceAssessment.reasons.length">
            <li v-for="reason in traceServiceAssessment.reasons" :key="reason">{{ reason }}</li>
          </ul>
        </div>

        <div v-if="traceInspectorRows.length" class="trace-inspector">
          <div class="trace-inspector-header">
            <div>
              <span class="mini-label">Address inspector</span>
              <strong>{{ formatInteger(traceInspectorSummary.total) }} {{ traceInspectorTransferLabel }} row{{ traceInspectorSummary.total === 1 ? '' : 's' }}</strong>
            </div>
            <button
              class="ghost-button small-button"
              @click="focusTraceTransactionOfInterest"
              :disabled="!traceInputTransactionFound"
            >
              Jump to input transaction
            </button>
          </div>

          <div class="trace-summary-grid">
            <div><span>Incoming</span><strong>{{ formatInteger(traceInspectorSummary.incoming) }}</strong></div>
            <div><span>Outgoing</span><strong>{{ formatInteger(traceInspectorSummary.outgoing) }}</strong></div>
            <div><span>Self</span><strong>{{ formatInteger(traceInspectorSummary.self) }}</strong></div>
            <div><span>Internal</span><strong>{{ formatInteger(traceInspectorSummary.internal) }}</strong></div>
            <div><span>Failed / reverted</span><strong>{{ formatInteger(traceInspectorSummary.failed) }}</strong></div>
            <div><span>Non-balance</span><strong>{{ formatInteger(traceInspectorSummary.nonBalance) }}</strong></div>
            <div><span>Trusted tokens</span><strong>{{ formatInteger(traceInspectorSummary.trustedTokens) }}</strong></div>
            <div><span>Token issues</span><strong>{{ formatInteger(traceInspectorSummary.tokenIssues) }}</strong></div>
            <div v-if="traceInspectorSummary.exits"><span>Tool 88 exits</span><strong>{{ formatInteger(traceInspectorSummary.exits) }}</strong></div>
            <div v-if="traceInspectorSummary.balanceRows"><span>Selected asset balance</span><strong>{{ formatTraceInspectorBalanceAfter(traceInspectorSummary) }}</strong></div>
            <div v-if="traceInspectorSummary.tokenIssues"><span>Token warnings</span><strong>{{ formatInteger(traceInspectorSummary.tokenIssues) }}</strong></div>
          </div>

          <div :class="['trace-interest-note', traceInputTransactionFound ? 'trace-interest-note-ok' : 'trace-interest-note-warn']">
            <strong>{{ traceInputTransactionFound ? 'Input transaction located in fetched history.' : 'Input transaction not found in fetched history yet.' }}</strong>
            <span>Tool 88 highlights the original transaction hash when the provider history includes it.</span>
          </div>

          <div v-if="traceAssessorPlaceholder" class="trace-assessor-note">
            <strong>Service-pattern fetch summary</strong>
            <span>{{ traceAssessorPlaceholder }}</span>
          </div>

          <div class="trace-inspector-toolbar">
            <label>Show</label>
            <select v-model="traceInspectorFilter">
              <option value="all">All rows</option>
              <option value="incoming">Incoming</option>
              <option value="outgoing">Outgoing</option>
              <option value="self">Self</option>
              <option value="internal">Internal</option>
              <option value="nonbalance">Non-balance</option>
              <option value="failed">Failed / reverted</option>
              <option value="token-issues">Token warnings</option>
              <option value="interest">Input transaction</option>
              <option value="exits">Tool 88 exits</option>
            </select>
            <span v-if="traceCopiedHash" class="copy-feedback">Copied {{ traceCopiedHash }}</span>
          </div>

          <div class="trace-table-wrap">
            <table class="trace-inspector-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Direction</th>
                  <th>Kind</th>
                  <th>Date UTC</th>
                  <th>Hash</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Amount</th>
                  <th>Balance Δ</th>
                  <th>Balance after</th>
                  <th>Token check</th>
                  <th>Tool 88</th>
                  <th>Details</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in visibleTraceInspectorRows"
                  :key="row.uniqueKey || `${row.hash}-${row.rowNumber}`"
                  :id="row.isTransactionOfInterest ? 'tool88-trace-interest-row' : null"
                  :class="{
                    'trace-row-interest': row.isTransactionOfInterest,
                    'trace-row-exit': row.isExit,
                    'trace-row-failed': row.failedOrReverted,
                    'trace-row-token-issue': row.hasTokenIssue,
                    'trace-row-balance-impact': row.assetMatchedForBalance,
                    'trace-row-balance-negative': row.assetMatchedForBalance && Number(row.assetBalanceAfter) < -0.000000001,
                  }"
                >
                  <td>{{ row.rowNumber }}</td>
                  <td><span :class="['direction-pill', `direction-pill-${row.direction}`]">{{ formatTraceInspectorDirection(row.direction) }}</span></td>
                  <td>{{ row.kindLabel }}</td>
                  <td>{{ row.dateText || 'Unknown' }}</td>
                  <td>
                    <button class="hash-copy-button" @click="copyTraceHash(row.hash)" :title="row.hash">
                      {{ row.shortHash }}
                    </button>
                  </td>
                  <td :title="row.fromTitle || row.fromAddress">{{ row.fromDisplay || row.shortFrom }}</td>
                  <td :title="row.toTitle || row.toAddress">{{ row.toDisplay || row.shortTo }}</td>
                  <td>{{ formatTraceInspectorAmount(row) }}</td>
                  <td>{{ formatTraceInspectorBalanceDelta(row) }}</td>
                  <td>{{ formatTraceInspectorBalanceAfter(row) }}</td>
                  <td class="trace-token-check-cell">
                    <span :class="['token-check-pill', row.tokenCheckStatusClass]">{{ row.tokenCheckLabel }}</span>
                    <small v-if="row.tokenCheckDetail">{{ row.tokenCheckDetail }}</small>
                  </td>
                  <td>
                    <span v-if="row.analysisRole" class="fifo-role-pill fifo-role-selected">{{ row.analysisRole }}</span>
                    <span v-else-if="row.assetMatchedForBalance" class="fifo-role-pill">{{ row.balanceRoleLabel }}</span>
                    <span v-else class="muted-cell">—</span>
                  </td>
                  <td class="trace-cell-note">{{ formatTraceInspectorDetails(row) }}</td>
                  <td>{{ row.statusDisplay || (row.failedOrReverted ? 'Failed / reverted' : (row.nonBalanceAffecting ? 'Non-balance' : 'Success')) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <button
            v-if="hiddenTraceInspectorRowCount > 0"
            class="ghost-button small-button trace-show-more"
            @click="showMoreTraceRows"
          >
            Show {{ Math.min(250, hiddenTraceInspectorRowCount) }} more
          </button>
        </div>
      </div>
    </section>

    <section v-if="finalDecisionMade" class="stage-card output-ready-stage">
      <div class="stage-header">
        <div>
          <div class="stage-kicker">Step 5</div>
          <h2>Review the output spreadsheet and analysis report</h2>
        </div>
        <span class="stage-badge stage-badge-ready">Output ready</span>
      </div>

      <p class="stage-copy">
        The output spreadsheet now reflects the decision: a trace-through inserts the solved hop
        below the selected entry, while a path-ending decision finalizes the selected entry in place.
        The report underneath the spreadsheets keeps the audit trail for the pass.
      </p>
      <div class="flow-completion-actions output-absorb-actions">
        <button class="primary-button small-button" @click="absorbOutputIntoInput" :disabled="isProcessing">
          Use Output as New Input
        </button>
        <span>Replace the input with this output so you can continue to the next unresolved path ending.</span>
      </div>
      <div v-if="flowAbsorbResult" class="result-callout result-callout-flow">
        <strong>Input refreshed from output.</strong>
        <span>{{ flowAbsorbResult.summary || flowCompletionMessage }}</span>
      </div>
    </section>

    <section v-if="warnings.length || isProcessing || lastRunMeta" class="status-area">
      <div v-if="isProcessing" class="progress-panel">
        <div class="panel-title">Processing</div>
        <div>{{ statusMessage || 'Working through the current pass...' }}</div>
      </div>

      <div v-if="warnings.length" class="warnings-box">
        <strong>Warnings</strong>
        <ul>
          <li v-for="(warning, index) in warnings" :key="`warning-${index}`">
            {{ warning }}
          </li>
        </ul>
      </div>

      <div v-if="lastRunMeta" class="meta-panel">
        <div class="panel-title">Last Run Summary</div>
        <div class="meta-grid">
          <div><b>Rows loaded</b>: {{ lastRunMeta.rowsLoaded }}</div>
          <div><b>Header detected</b>: {{ lastRunMeta.headerDetected ? 'Yes' : 'No' }}</div>
          <div><b>Entries found</b>: {{ lastRunMeta.entriesFound }}</div>
          <div><b>Candidate addresses</b>: {{ lastRunMeta.candidateCount }}</div>
          <div><b>Selected address</b>: {{ lastRunMeta.selectedAddress || 'None' }}</div>
          <div><b>Exit count</b>: {{ lastRunMeta.exitCount }}</div>
          <div><b>Dormant amount</b>: {{ lastRunMeta.dormantAmountText || 'None' }}</div>
          <div v-if="lastRunMeta.transactionCount !== undefined">
            <b>Transaction count</b>: {{ formatCountValue(lastRunMeta) }}
          </div>
          <div v-if="lastRunMeta.countProvider"><b>Count provider</b>: {{ lastRunMeta.countProvider }}</div>
          <div v-if="lastRunMeta.countRoute"><b>Count route</b>: {{ lastRunMeta.countRoute }}</div>
          <div v-if="lastRunMeta.attributionLabel"><b>Attribution</b>: {{ lastRunMeta.attributionLabel }}</div>
          <div v-if="lastRunMeta.attributionClassification"><b>Attribution detail</b>: {{ formatAttributionClassification(lastRunMeta.attributionClassification) }}</div>
          <div v-if="lastRunMeta.jobId"><b>Server job</b>: {{ lastRunMeta.jobId }}</div>
        </div>
      </div>
    </section>

    <section class="spreadsheet-section">
      <div class="section-heading">
        <div>
          <h2>Input Spreadsheet</h2>
          <p>Paste the raw/main Case Buddy entry here. Multiple entries are okay if you choose one.</p>
        </div>
      </div>
      <SpreadsheetComponent
        ref="inputSpreadsheet"
        buttonLabel="Add 100 Rows to Input"
        width="1260px"
        height="340px"
      />
    </section>

    <section class="spreadsheet-section">
      <div class="section-heading">
        <div>
          <h2>Output Spreadsheet</h2>
          <p>
            Output starts as the original data. Trace-through inserts the solved hop below the
            selected entry; path-ending decisions finalize the selected entry in place.
          </p>
        </div>
      </div>
      <SpreadsheetComponent
        ref="outputSpreadsheet"
        buttonLabel="Add 100 Rows to Output"
        width="1260px"
        height="340px"
      />
    </section>

    <section class="report-panel">
      <div class="section-heading">
        <div>
          <h2>Analysis Report</h2>
          <p>Readable audit trail for the parsing, count/check pass, and final decision.</p>
        </div>
      </div>
      <pre class="report-pre">{{ reportText }}</pre>
    </section>
  </div>
</template>

<script>
import SpreadsheetComponent from "@/components/SpreadsheetComponent.vue";
import AuthService from "@/services/AuthService";
import { TOOL88_SHIPPED_TOKEN_REGISTRY } from "@/data/tool88TokenCatalog.generated.js";
import { TOOL88_SHIPPED_TRACE_THROUGH_ENTITIES } from "@/data/tool88TraceThroughEntityCatalog.generated.js";

const COLUMN_COUNT = 24;
const DEFAULT_ROW_COUNT = 100;

const COL = Object.freeze({
  PATH_LABEL: 0,
  TX_HASH: 1,
  FROM_ADDRESS: 2,
  TO_ADDRESS: 3,
  DATE: 4,
  AMOUNT: 5,
  TOKEN: 6,
  SYSTEM_NOTES: 9,
  ATTRIBUTION: 10,
  USER_NOTES: 11,
  FUNDS_FOLLOWED: 12,
  PRIOR_BALANCE: 13,
  FROM_HASH: 14,
  FROM_LABEL: 15,
  CONTINUES_AS: 16,
  LEFT_BOUNDS_DISPLAY: 17,
  LEFT_BOUNDS_NUMERIC: 18,
  CHAIN_ID: 19,
  CHAIN_NAME: 20,
  HOP_LOGIC: 21,
  TOOL88_NOTES: 22,
  FROM_ATTRIBUTION: 23,
});

const HEADER_ROW = [
  "Path label",
  "Transaction Hash",
  "From Address",
  "To Address",
  "Date",
  "Amount",
  "TokenSymbol",
  "",
  "",
  "System-generated notes",
  "Attribution data",
  "User-created additional notes",
  "Funds followed",
  "Prior balance if known",
  "From hash",
  "From label",
  "Continues-as hashes",
  "Left-bounds display data",
  "Left-bounds numeric value only",
  "Chain id",
  "Chain name",
  "Hop logic used",
  "Tool 88 notes",
  "From Address Attribution",
];

// Keep the Tool 88 endpoint relative so the app uses the current host/protocol
// and does not accidentally cross host aliases or trigger CORS issues.
const TOOL88_ENDPOINT_URL = "/api/casebuddy/tool88_hop_logic_solver_casebuddy.php";
const TOOL88_SESSION_CACHE_MS = 2 * 60 * 1000;
const TOOL88_SETTINGS_STORAGE_KEY = "casebuddy_tool88_hop_logic_solver_settings_v1";
const TOOL88_SETTINGS_SCHEMA_VERSION = 2;
const TOOL88_TOKEN_MANAGER_STORAGE_KEY = "casebuddy_tool88_token_manager_overrides_v1";
const TOOL88_TRACE_THROUGH_ENTITY_STORAGE_KEY = "casebuddy_tool88_trace_through_entity_overrides_v1";
const TOOL88_SESSION_CACHE_MAX_COUNT_ENTRIES = 300;
const TOOL88_SESSION_CACHE_MAX_TRACE_ENTRIES = 35;
const TOOL88_SESSION_DATA_CACHE = {
  counts: new Map(),
  traces: new Map(),
  stats: {
    countHits: 0,
    countStores: 0,
    traceHits: 0,
    traceStores: 0,
  },
  createdAt: Date.now(),
};

// The WayFinder Unity editor exporter included with this pass generates the
// imported shipped registry. Keep it flat: one token contract per row/chain, so
// Tool 88 can resolve symbols to contracts without carrying Unity ScriptableObject
// shape into the browser.
const SHIPPED_TOKEN_REGISTRY = Object.freeze(TOOL88_SHIPPED_TOKEN_REGISTRY || []);
const SHIPPED_TRACE_THROUGH_ENTITY_REGISTRY = Object.freeze(TOOL88_SHIPPED_TRACE_THROUGH_ENTITIES || []);


// Contract-level decimal overrides protect transaction-receipt based swap solving from
// bad or chain-generic TokenManager rows. EVM receipts expose raw Transfer log values,
// but not token decimals, so these trusted contracts are authoritative when provider
// row decimals are absent.
const TOOL88_TOKEN_DECIMAL_OVERRIDES = Object.freeze({
  "0xdac17f958d2ee523a2206206994597c13d831ec7": { symbol: "USDT", decimals: 6, chainKey: "evm:1", label: "Ethereum USDT" },
  "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48": { symbol: "USDC", decimals: 6, chainKey: "evm:1", label: "Ethereum USDC" },
  "0x6b175474e89094c44da98b954eedeac495271d0f": { symbol: "DAI", decimals: 18, chainKey: "evm:1", label: "Ethereum DAI" },
  "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2": { symbol: "WETH", decimals: 18, chainKey: "evm:1", label: "Ethereum WETH" },
  "0x2260fac5e5542a773aa44fbcfedf7c193bc2c599": { symbol: "WBTC", decimals: 8, chainKey: "evm:1", label: "Ethereum WBTC" },
  "0x55d398326f99059ff775485246999027b3197955": { symbol: "USDT", decimals: 18, chainKey: "evm:56", label: "BSC USDT" },
  "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d": { symbol: "USDC", decimals: 18, chainKey: "evm:56", label: "BSC USDC" },
  "0x2791bca1f2de4661ed88a30c99a7a9449aa84174": { symbol: "USDC", decimals: 6, chainKey: "evm:137", label: "Polygon USDC.e" },
  "0x3c499c542cef5e3811e1192ce70d8cc03d5c3359": { symbol: "USDC", decimals: 6, chainKey: "evm:137", label: "Polygon USDC" },
  "0x9702230a8ea53601f5cd2dc00fdbc13d4df4a8c7": { symbol: "USDT", decimals: 6, chainKey: "evm:43114", label: "Avalanche USDT" },
  "0xb97ef9ef8734c71904d8002f8b6bc66dd9c48a6e": { symbol: "USDC", decimals: 6, chainKey: "evm:43114", label: "Avalanche USDC" },
  "0xa7d7079b0fead91f3e65f86e8915cb59c1a4c664": { symbol: "USDC", decimals: 6, chainKey: "evm:43114", label: "Avalanche USDC.e" },
  "0xc2132d05d31c914a87c6611c10748aeb04b58e8f": { symbol: "USDT", decimals: 6, chainKey: "evm:137", label: "Polygon USDT" },
  "0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9": { symbol: "USDT", decimals: 6, chainKey: "evm:42161", label: "Arbitrum USDT" },
  "0xff970a61a04b1ca14834a43f5de4533ebddb5cc8": { symbol: "USDC", decimals: 6, chainKey: "evm:42161", label: "Arbitrum USDC.e" },
  "0xaf88d065e77c8cc2239327c5edb3a432268e5831": { symbol: "USDC", decimals: 6, chainKey: "evm:42161", label: "Arbitrum USDC" },
  "0x94b008aa00579c1307b0ef2c499ad98a8ce58e58": { symbol: "USDT", decimals: 6, chainKey: "evm:10", label: "Optimism USDT" },
  "0x7f5c764cbc14f9669b88837ca1490cca17c31607": { symbol: "USDC", decimals: 6, chainKey: "evm:10", label: "Optimism USDC.e" },
  "0x0b2c639c533813f4aa9d7837caf62653d097ff85": { symbol: "USDC", decimals: 6, chainKey: "evm:10", label: "Optimism USDC" },
  "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913": { symbol: "USDC", decimals: 6, chainKey: "evm:8453", label: "Base USDC" },
  "0xa614f803b6fd780986a42c78ec9c7f77e6ded13c": { symbol: "USDT", decimals: 6, chainKey: "tron", label: "TRON USDT hex" },
  "tr7nhqjekqxgtci8q8zy4pl8otszgjlj6t": { symbol: "USDT", decimals: 6, chainKey: "tron", label: "TRON USDT" },
  "tekxitehnzsmse2xqrbj4w32run966rdz8": { symbol: "USDC", decimals: 6, chainKey: "tron", label: "TRON USDC" },
  "0:b113a994b5024a16719f69139328eb759596c38a25f59028b146fecdc3621dfe": { symbol: "USDT", decimals: 6, chainKey: "ton", label: "TON USDT" }
});

const TOKEN_CHAIN_OPTIONS = Object.freeze([
  { value: "evm:1", label: "Ethereum / ERC20" },
  { value: "evm:56", label: "BSC / BEP20" },
  { value: "evm:137", label: "Polygon" },
  { value: "evm:42161", label: "Arbitrum" },
  { value: "evm:10", label: "Optimism" },
  { value: "evm:8453", label: "Base" },
  { value: "evm:250", label: "Fantom" },
  { value: "evm:59144", label: "Linea" },
  { value: "evm:43114", label: "Avalanche / AVAX C-Chain" },
  { value: "tron", label: "TRON / TRC20" },
  { value: "pulsechain", label: "PulseChain" },
  { value: "ton", label: "TON" },
  { value: "solana", label: "Solana" },
]);

const UNKNOWN_TRANSACTION_COUNT = -1;
const INTERNAL_WARNING_THRESHOLD = 10000;
const INTERNAL_HARD_LIMIT_THRESHOLD = 250000;
const AUTO_EVM_TRACE_COUNT_LIMIT = 10000;
const AUTO_BTC_TRACE_COUNT_LIMIT = 1000;
const BTC_WITHDRAWAL_RECIPIENT_TRACE_COUNT_LIMIT = 150;
const AUTO_TRON_TRACE_COUNT_LIMIT = 10000;
const ERC20_TRANSFER_TOPIC = "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef";
const TOOL88_ATTRIBUTION_TTL_HOURS = 168;
const SIMPLE_ATTRIBUTION_TRACE_THROUGH_PATTERNS = Object.freeze([
  /\.eth\b/i,
  /\.crypto\b/i,
  /\.nft\b/i,
  /\.wallet\b/i,
  /\.xyz\b/i,
  /\.bit\b/i,
  /unstoppable\s+domains?/i,
  /\bens\b/i,
  /personal\s+wallet/i,
  /self[-\s]?custod/i,
  /self[-\s]?hosted/i,
  /individual\s+wallet/i,
  /private\s+wallet/i,
]);

const TOOL88_HANDLE_ATTRIBUTION_SERVICE_EXCEPTIONS = Object.freeze([
  "cryptobot",
  "crypto_bot",
  "binance",
  "binanceofficial",
  "coinbase",
  "coinbasewallet",
  "kraken",
  "okx",
  "okxweb3",
  "crypto.com",
  "cryptocom",
  "kucoin",
  "bybit",
  "bitget",
  "lbank",
  "mexc",
  "gateio",
  "gate_io",
  "huobi",
  "htx",
  "upbit",
  "bithumb",
]);

const TOOL88_HANDLE_ATTRIBUTION_SERVICE_PATTERN = /(?:^|[^a-z0-9])(@?(?:cryptobot|crypto[_-]?bot|binance|coinbase|kraken|okx|kucoin|bybit|bitget|lbank|mexc|gate[_-]?io|huobi|htx|upbit|bithumb|crypto\.?com))(?:[^a-z0-9]|$)/i;

const TOOL88_BRIDGE_SERVICE_DEFINITIONS = Object.freeze([
  { canonicalName: "BKBridge", aliases: ["BKBridge", "BK Bridge", "Bitget Bridge"] },
  { canonicalName: "OKX Router Web3", aliases: ["OKX Router Web 3", "OKX Router Web3", "OKX Web3 Router", "OKX Web 3 Router", "OKX Dex Router", "OKX DEX Router", "OKX Web 3", "OKX Web3"] },
  { canonicalName: "Rango", aliases: ["Rango", "Rango Exchange", "Rango Explorer", "Rango (DEX)", "Rango DEX"] },
  { canonicalName: "Bridgers", aliases: ["Bridgers", "Bridgers.xyz", "Bridgers Explorer"] },
  { canonicalName: "Allchainbridge", aliases: ["Allchainbridge", "Allchainbridge.com", "Allchain bridge", "Allchain Bridge", "All Chain Bridge"] },
  { canonicalName: "CrossChainX", aliases: ["CrossChainX", "Cross Chain X", "CrossChain X"] },
  { canonicalName: "Teleport", aliases: ["Teleport", "Teleswap", "TeleSwap"] },
  { canonicalName: "TransitSwap", aliases: ["TransitSwap", "Transit Swap", "TransitFinance", "Transit Finance"] },
  { canonicalName: "SWFT Blockchain", aliases: ["SWFT Blockchain", "SWFT", "SWFT Bridge", "SWFT Swap"] },
  { canonicalName: "Wormhole", aliases: ["Wormhole", "Wormholescan", "Wormhole Scan", "Portal Bridge"] },
  { canonicalName: "Near-Intents", aliases: ["Near-Intents", "Near Intents", "NEAR Intents", "Near Intents Explorer"] },
  { canonicalName: "Chainflip", aliases: ["Chainflip", "Chainflip Scan", "Chainflip (Bridge)"] },
  { canonicalName: "Butterswap", aliases: ["Butterswap", "Butter Swap", "Butterswap Explorer"] },
  { canonicalName: "Allbridge", aliases: ["Allbridge", "All Bridge", "Allbridge Core"] },
  { canonicalName: "Hifiswap", aliases: ["Hifiswap", "Hifi Swap", "HiFiSwap", "Hifiswap Search"] },
  { canonicalName: "THORChain", aliases: ["THORChain", "Thorchain", "Thor Chain", "THORSwap"] },
  { canonicalName: "Across", aliases: ["Across", "Across Protocol"] },
  { canonicalName: "Stargate", aliases: ["Stargate", "Stargate Finance"] },
  { canonicalName: "Orbiter", aliases: ["Orbiter", "Orbiter Finance"] },
  { canonicalName: "Hop Protocol", aliases: ["Hop Protocol", "HopProtocol"] },
  { canonicalName: "LI.FI", aliases: ["LI.FI", "LiFi", "Li Fi", "LIFI"] },
]);


const TOOL88_SWAP_EVENT_NAME_PATTERN = /\b(swap|swapped|swop|swapin|swapout|trade|traded|fill|filled|orderfilled|cow|uniswap|sushiswap|curve|1inch|tokenlon|universal\s*router|universalrouter|exactinput|exactoutput|metamask|dodo|pendle|aave|bkswap|mimic|arrakis|router|dex|amm)\b/i;
const TOOL88_BRIDGE_EVENT_NAME_PATTERN = /\b(bridge|bridged|bridgein|bridgeout|cross[-\s]?chain|crosschain|xchain|teleport|teleswap|wormhole|portal|stargate|allbridge|allchain|chainflip|rango|bridgers|bkbridge|transit|swft|thorchain|near[-\s]?intents|destination|dst\s*chain|to\s*chain|fromamount|minreturnamount|totoken)\b/i;
const DEFAULT_FLOW_COMPLETION_TRACE_THROUGH_ENTITIES = Object.freeze([
  "ENS",
  "personal wallet",
  "private wallet",
  "individual wallet",
  "self custody",
  "self-custody",
  "self-hosted",
]);
const DEFAULT_FLOW_COMPLETION_MAX_DEPTH = 50;
const DEFAULT_LOW_VALUE_THRESHOLDS = Object.freeze({
  btc: 0.001,
  native: 0.1,
  stable: 100,
  other: 10,
});
const TOOL88_DEPOSIT_ENTITY_GENERIC_TERMS = Object.freeze([
  "address",
  "admin",
  "arkham",
  "attribution",
  "batch",
  "bridge",
  "broker",
  "cold",
  "contract",
  "custody",
  "customer",
  "deposit",
  "deposits",
  "dex",
  "entity",
  "exchange",
  "finance",
  "funds",
  "gateway",
  "global",
  "hot",
  "known",
  "label",
  "main",
  "memo",
  "official",
  "note",
  "notes",
  "path",
  "payout",
  "receive",
  "receiver",
  "router",
  "service",
  "smart",
  "swap",
  "tag",
  "tags",
  "tool",
  "transfer",
  "transfers",
  "user",
  "wallet",
  "withdraw",
  "withdrawal",
  "withdrawals",
]);
const TOOL88_DEPOSIT_ENTITY_TOKEN_TERMS = Object.freeze([
  "btc",
  "bnb",
  "busd",
  "dai",
  "eth",
  "fdusd",
  "pyusd",
  "trx",
  "tusd",
  "usdc",
  "usdd",
  "usdt",
  "wbtc",
  "weth",
]);
const DEFAULT_USER_SETTINGS = Object.freeze({
  traceDirection: "forward",
  hopLogicType: "NEXT_CHRONOLOGICAL",
  selectedExampleKey: "basic",
  tool88NoteColumn: "W",
  enableAdditionalServiceChecks: true,
  enableDepositEntityHopExtension: false,
  enableNativeDirectSpendMatchGuard: false,
  enableSessionDataCache: true,
  flowCompletionMaxDepth: DEFAULT_FLOW_COMPLETION_MAX_DEPTH,
  flowCompletionValueTooLowEnabled: true,
  lowValueThresholdBtc: DEFAULT_LOW_VALUE_THRESHOLDS.btc,
  lowValueThresholdNative: DEFAULT_LOW_VALUE_THRESHOLDS.native,
  lowValueThresholdStable: DEFAULT_LOW_VALUE_THRESHOLDS.stable,
  lowValueThresholdOther: DEFAULT_LOW_VALUE_THRESHOLDS.other,
  flowCompletionTraceThroughEntitiesText: DEFAULT_FLOW_COMPLETION_TRACE_THROUGH_ENTITIES.join(", "),
  directAutomationSpeedMultiplier: 1,
});
const TOOL88_CASE_BUDDY_INTEGRATION_VERSION = "tool88-case-buddy-integration-v17-progressive-root-completion-2026-06-20";
const TOOL88_TRACE_POLL_MAX_ATTEMPTS = 720;
const TOOL88_TRACE_POLL_DELAY_MS = 1200;
const TOOL88_TRACE_BROWSER_FALLBACK_AFTER_MS = 45 * 1000;
const TOOL88_TRACE_EMBEDDED_HARD_LIMIT_MS = 3 * 60 * 1000;
const TOOL88_TRACE_MANUAL_HARD_LIMIT_MS = 10 * 60 * 1000;
const TOOL88_TRACE_INFER_COMPLETE_POLLS = 3;
const TOOL88_MEMPOOL_BROWSER_AUTO_MAX_COUNT = 25;
const TOOL88_MEMPOOL_BROWSER_MANUAL_MAX_COUNT = 100;
const TOOL88_MEMPOOL_BROWSER_REQUEST_TIMEOUT_MS = 15 * 1000;
const TOOL88_MEMPOOL_API_BASE_URL = "https://mempool.space/api";
// PathTracer CSP intentionally permits only same-origin API traffic. Keep the
// old browser-provider fallback dormant so it cannot create CSP errors, leak
// investigated addresses to a third-party browser request, or bypass the
// isolated authenticated Case Buddy gateway.
const TOOL88_ALLOW_DIRECT_MEMPOOL_BROWSER_FALLBACK = false;
const TOOL88_DIRECT_AUTOMATION_VERSION = "tool88-direct-automation-v2-standalone-auto-stale-input-safe-2026-05-16";
const TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS = Object.freeze({
  enabled: true,
  maxElapsedMs: 60 * 60 * 1000,
  maxEntriesResearched: 250,
  maxLoopSteps: 500,
  maxConsecutiveSameAction: 10,
  maxNoProgressSteps: 4,
  maxStateVisits: 3,
  recentStateWindow: 18,
  maxAutoLowValue: 25,
  speedMultiplier: 1,
  delayMs: 750,
  pauseOnManualReview: true,
  pauseOnTargetAmbiguity: true,
  pauseOnUnverifiedOutput: true,
  flowCompleteSettleMs: 6500,
  flowCompleteConfirmations: 3,
});
const TOOL88_DIRECT_AUTOMATION_DEFAULT_GUARDRAILS = Object.freeze({
  ...TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS,
  delayMs: 900,
});

function createTool88CaseBuddyIntegrationRunState() {
  return {
    active: false,
    stopRequested: false,
    startedAtMs: 0,
    finishedAtMs: 0,
    stepCount: 0,
    researchedEntryKeys: [],
    lastActionKey: "",
    repeatedActionCount: 0,
    lastGuardrail: null,
    lastResult: null,
    lastResultSummary: null,
    recentSteps: [],
    lastStateKey: "",
    recentStateKeys: [],
    currentStateVisitCount: 0,
    lastProgressKey: "",
    noProgressCount: 0,
    flowCompleteFirstSeenAtMs: 0,
    flowCompleteConfirmations: 0,
    status: "idle",
    detail: "",
  };
}

function createTool88DirectAutomationRunState() {
  return {
    ...createTool88CaseBuddyIntegrationRunState(),
    mode: "direct-tool88-auto",
  };
}

const VALID_EXAMPLE_KEYS = Object.freeze([
  "basic",
  "eth-native",
  "bsc-native",
  "ambiguous",
  "evm-event-log",
  "btc-memo",
  "tron-trc20",
  "tron-trx",
  "unsupported-sol",
  "dormant",
  "warning",
  "hard-limit",
]);

function safeReadTool88Settings() {
  const settings = { ...DEFAULT_USER_SETTINGS };

  try {
    if (typeof localStorage === "undefined") {
      return settings;
    }

    const raw = localStorage.getItem(TOOL88_SETTINGS_STORAGE_KEY);
    if (!raw) {
      return settings;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return settings;
    }

    if (["forward", "reverse"].includes(parsed.traceDirection)) {
      settings.traceDirection = parsed.traceDirection;
    }

    if (["NEXT_CHRONOLOGICAL", "FIFO", "LIFO"].includes(parsed.hopLogicType)) {
      settings.hopLogicType = parsed.hopLogicType;
    }

    if (VALID_EXAMPLE_KEYS.includes(parsed.selectedExampleKey)) {
      settings.selectedExampleKey = parsed.selectedExampleKey;
    }

    if (["J", "W", "P"].includes(parsed.tool88NoteColumn) && Number(parsed.settingsVersion || 1) >= TOOL88_SETTINGS_SCHEMA_VERSION) {
      settings.tool88NoteColumn = parsed.tool88NoteColumn === "P" ? "W" : parsed.tool88NoteColumn;
    }

    if (typeof parsed.enableAdditionalServiceChecks === "boolean") {
      settings.enableAdditionalServiceChecks = parsed.enableAdditionalServiceChecks;
    }

    if (typeof parsed.enableDepositEntityHopExtension === "boolean") {
      settings.enableDepositEntityHopExtension = parsed.enableDepositEntityHopExtension;
    }

    if (typeof parsed.enableNativeDirectSpendMatchGuard === "boolean") {
      settings.enableNativeDirectSpendMatchGuard = parsed.enableNativeDirectSpendMatchGuard;
    }

    if (typeof parsed.enableSessionDataCache === "boolean") {
      settings.enableSessionDataCache = parsed.enableSessionDataCache;
    }

    const flowDepth = Number(parsed.flowCompletionMaxDepth);
    if (Number.isFinite(flowDepth) && flowDepth > 0) {
      settings.flowCompletionMaxDepth = Math.min(500, Math.trunc(flowDepth));
    }

    if (typeof parsed.flowCompletionValueTooLowEnabled === "boolean") {
      settings.flowCompletionValueTooLowEnabled = parsed.flowCompletionValueTooLowEnabled;
    }

    if (typeof parsed.flowCompletionTraceThroughEntitiesText === "string") {
      settings.flowCompletionTraceThroughEntitiesText = parsed.flowCompletionTraceThroughEntitiesText;
    }

    ["lowValueThresholdBtc", "lowValueThresholdNative", "lowValueThresholdStable", "lowValueThresholdOther"].forEach((key) => {
      const value = Number(parsed[key]);
      if (Number.isFinite(value) && value >= 0) {
        settings[key] = value;
      }
    });

    const directAutomationSpeedMultiplier = Number(parsed.directAutomationSpeedMultiplier);
    if (Number.isFinite(directAutomationSpeedMultiplier)) {
      settings.directAutomationSpeedMultiplier = Math.max(0.5, Math.min(3, directAutomationSpeedMultiplier));
    }
  } catch (error) {
    console.warn("Could not read Tool 88 saved settings.", error);
  }

  return settings;
}

function safeWriteTool88Settings(settings) {
  try {
    if (typeof localStorage === "undefined") {
      return;
    }

    localStorage.setItem(
      TOOL88_SETTINGS_STORAGE_KEY,
      JSON.stringify({
        settingsVersion: TOOL88_SETTINGS_SCHEMA_VERSION,
        traceDirection: settings.traceDirection,
        hopLogicType: settings.hopLogicType,
        selectedExampleKey: settings.selectedExampleKey,
        tool88NoteColumn: settings.tool88NoteColumn,
        enableAdditionalServiceChecks: settings.enableAdditionalServiceChecks !== false,
        enableDepositEntityHopExtension: settings.enableDepositEntityHopExtension === true,
        enableSessionDataCache: settings.enableSessionDataCache !== false,
        flowCompletionMaxDepth: settings.flowCompletionMaxDepth,
        flowCompletionValueTooLowEnabled: settings.flowCompletionValueTooLowEnabled !== false,
        lowValueThresholdBtc: settings.lowValueThresholdBtc,
        lowValueThresholdNative: settings.lowValueThresholdNative,
        lowValueThresholdStable: settings.lowValueThresholdStable,
        lowValueThresholdOther: settings.lowValueThresholdOther,
        flowCompletionTraceThroughEntitiesText: settings.flowCompletionTraceThroughEntitiesText || "",
        directAutomationSpeedMultiplier: settings.directAutomationSpeedMultiplier,
      })
    );
  } catch (error) {
    console.warn("Could not save Tool 88 settings.", error);
  }
}

function normalizeTokenText(value) {
  return String(value ?? "").trim();
}

function normalizeTokenSymbolText(value) {
  return normalizeTokenText(value).toUpperCase().replace(/\s+/g, "");
}

function normalizeTokenContractText(value) {
  const text = normalizeTokenText(value);
  if (!text) return "";
  if (/^0x[a-fA-F0-9]{40}$/.test(text)) return text.toLowerCase();
  return text;
}

function normalizeTokenChainKey(value) {
  const text = normalizeTokenText(value).toLowerCase();
  if (!text) return "";
  if (/^0x[0-9a-f]+$/.test(text)) return `evm:${parseInt(text.slice(2), 16)}`;
  if (/^\d+$/.test(text)) return `evm:${text}`;
  if (text === "eth" || text === "ethereum" || text === "mainnet") return "evm:1";
  if (text === "bsc" || text === "bnb" || text === "binance") return "evm:56";
  if (text === "polygon" || text === "matic" || text === "pol") return "evm:137";
  if (text === "arbitrum") return "evm:42161";
  if (text === "optimism" || text === "op") return "evm:10";
  if (text === "base") return "evm:8453";
  if (text === "fantom" || text === "ftm") return "evm:250";
  if (text === "linea") return "evm:59144";
  if (text === "avax" || text === "avalanche") return "evm:43114";
  if (text === "tron" || text === "trx" || text === "trc20") return "tron";
  if (text === "pulse" || text === "pulsechain") return "pulsechain";
  if (text === "ton") return "ton";
  if (text === "sol" || text === "solana") return "solana";
  return text;
}

const TOOL88_CANONICAL_TOKEN_METADATA_BY_CHAIN_CONTRACT = Object.freeze({
  "evm:1|0xdac17f958d2ee523a2206206994597c13d831ec7": { symbol: "USDT", decimals: 6, reason: "Ethereum USDT canonical decimals" },
  "evm:1|0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48": { symbol: "USDC", decimals: 6, reason: "Ethereum USDC canonical decimals" },
  "evm:1|0x6b175474e89094c44da98b954eedeac495271d0f": { symbol: "DAI", decimals: 18, reason: "Ethereum DAI canonical decimals" },
  "evm:1|0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2": { symbol: "WETH", decimals: 18, reason: "Ethereum WETH canonical decimals" },
  "evm:1|0x2260fac5e5542a773aa44fbcfedf7c193bc2c599": { symbol: "WBTC", decimals: 8, reason: "Ethereum WBTC canonical decimals" },
  "evm:56|0x55d398326f99059ff775485246999027b3197955": { symbol: "USDT", decimals: 18, reason: "BSC USDT canonical decimals" },
  "evm:56|0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d": { symbol: "USDC", decimals: 18, reason: "BSC USDC canonical decimals" },
  "evm:137|0xc2132d05d31c914a87c6611c10748aeb04b58e8f": { symbol: "USDT", decimals: 6, reason: "Polygon USDT canonical decimals" },
  "evm:137|0x2791bca1f2de4661ed88a30c99a7a9449aa84174": { symbol: "USDC", decimals: 6, reason: "Polygon bridged USDC canonical decimals" },
  "evm:137|0x3c499c542cef5e3811e1192ce70d8cc03d5c3359": { symbol: "USDC", decimals: 6, reason: "Polygon native USDC canonical decimals" },
  "evm:43114|0x9702230a8ea53601f5cd2dc00fdbc13d4df4a8c7": { symbol: "USDT", decimals: 6, reason: "Avalanche USDT canonical decimals" },
  "evm:43114|0xb97ef9ef8734c71904d8002f8b6bc66dd9c48a6e": { symbol: "USDC", decimals: 6, reason: "Avalanche USDC canonical decimals" },
  "tron|tr7nhqjekqxgtci8q8zy4pl8otszgjlj6t": { symbol: "USDT", decimals: 6, reason: "TRON USDT canonical decimals" },
  "tron|teknc9w4y6q3ywxdnynlcc7elk6vfza8um": { symbol: "USDC", decimals: 6, reason: "TRON USDC canonical decimals" },
  "evm:42161|0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9": { symbol: "USDT", decimals: 6, reason: "Arbitrum USDT canonical decimals" },
  "evm:42161|0xff970a61a04b1ca14834a43f5de4533ebddb5cc8": { symbol: "USDC", decimals: 6, reason: "Arbitrum bridged USDC canonical decimals" },
  "evm:42161|0xaf88d065e77c8cc2239327c5edb3a432268e5831": { symbol: "USDC", decimals: 6, reason: "Arbitrum native USDC canonical decimals" },
  "evm:10|0x94b008aa00579c1307b0ef2c499ad98a8ce58e58": { symbol: "USDT", decimals: 6, reason: "Optimism USDT canonical decimals" },
  "evm:10|0x7f5c764cbc14f9669b88837ca1490cca17c31607": { symbol: "USDC", decimals: 6, reason: "Optimism bridged USDC canonical decimals" },
  "evm:10|0x0b2c639c533813f4aa9d7837caf62653d097ff85": { symbol: "USDC", decimals: 6, reason: "Optimism native USDC canonical decimals" },
  "evm:8453|0x833589fcd6edb6e08f4c7c32d4f71b54bda02913": { symbol: "USDC", decimals: 6, reason: "Base USDC canonical decimals" },
  "evm:43114|0xa7d7079b0fead91f3e65f86e8915cb59c1a4c664": { symbol: "USDC", decimals: 6, reason: "Avalanche bridged USDC canonical decimals" },
  "tron|tekxitehnzsmse2xqrbj4w32run966rdz8": { symbol: "USDC", decimals: 6, reason: "TRON USDC canonical decimals" },
  "ton|0:b113a994b5024a16719f69139328eb759596c38a25f59028b146fecdc3621dfe": { symbol: "USDT", decimals: 6, reason: "TON USDT canonical decimals" },
});

const TOOL88_CANONICAL_TOKEN_METADATA_BY_CONTRACT = Object.freeze(
  Object.entries(TOOL88_CANONICAL_TOKEN_METADATA_BY_CHAIN_CONTRACT).reduce((acc, [key, metadata]) => {
    const [, contract] = key.split("|");
    if (contract && !acc[contract]) acc[contract] = metadata;
    return acc;
  }, {})
);

function getTool88CanonicalTokenMetadata(contractAddress, chainKey = "", symbol = "") {
  const contract = normalizeTokenContractText(contractAddress).toLowerCase();
  const chain = normalizeTokenChainKey(chainKey);
  const normalizedSymbol = normalizeTokenSymbolText(symbol);
  if (!contract) return null;
  const directOverride = TOOL88_TOKEN_DECIMAL_OVERRIDES[contract] || null;
  const exact = chain ? TOOL88_CANONICAL_TOKEN_METADATA_BY_CHAIN_CONTRACT[`${chain}|${contract}`] : null;
  const fallback = TOOL88_CANONICAL_TOKEN_METADATA_BY_CONTRACT[contract] || null;
  const metadata = directOverride || exact || fallback;
  if (!metadata) return null;
  if (normalizedSymbol && metadata.symbol && normalizedSymbol !== normalizeTokenSymbolText(metadata.symbol)) {
    return { ...metadata, symbolMismatch: true };
  }
  return metadata;
}

function getTokenEntryKey(entry = {}) {
  const symbol = normalizeTokenSymbolText(entry.symbol);
  const chainKey = normalizeTokenChainKey(entry.chainKey || entry.chain_key || entry.chain);
  const contract = normalizeTokenContractText(entry.contractAddress || entry.contract_address || entry.address);
  return [chainKey, symbol, contract].filter(Boolean).join("|");
}

function makeFlatTokenEntry(symbol, decimals, chainKey, contractAddress, source = "shipped", sourceId = "") {
  const normalizedSymbol = normalizeTokenSymbolText(symbol);
  const normalizedChain = normalizeTokenChainKey(chainKey);
  const normalizedContract = normalizeTokenContractText(contractAddress);
  if (!normalizedSymbol || !normalizedChain || !normalizedContract) {
    return null;
  }
  const decimalNumber = Number(decimals);
  const canonicalMetadata = getTool88CanonicalTokenMetadata(normalizedContract, normalizedChain, normalizedSymbol);
  const finalDecimals = Number.isFinite(Number(canonicalMetadata?.decimals))
    ? Math.trunc(Number(canonicalMetadata.decimals))
    : (Number.isFinite(decimalNumber) ? Math.trunc(decimalNumber) : 6);
  const finalSymbol = canonicalMetadata?.symbol && !canonicalMetadata.symbolMismatch
    ? normalizeTokenSymbolText(canonicalMetadata.symbol)
    : normalizedSymbol;
  return {
    key: getTokenEntryKey({ symbol: finalSymbol, chainKey: normalizedChain, contractAddress: normalizedContract }),
    symbol: finalSymbol,
    decimals: finalDecimals,
    chainKey: normalizedChain,
    contractAddress: normalizedContract,
    source,
    sourceId: normalizeTokenText(sourceId),
    decimalSource: canonicalMetadata ? "canonical_override" : "catalog",
  };
}

function expandTool88TokenSeedEntry(entry, source = "shipped") {
  if (!entry || typeof entry !== "object") {
    return [];
  }

  const symbol = entry.symbol || entry.tokenSymbol || entry.token_symbol || entry.name || "";
  const sourceId = entry.sourceId || entry.source_id || entry.guid || entry.assetGuid || entry.sourceAssetName || entry.assetName || "";

  // Already-flat Tool 88 export row.
  const flatContract = entry.contractAddress || entry.contract_address || entry.address;
  const flatChain = entry.chainKey || entry.chain_key || entry.chain || entry.network;
  if (flatContract && flatChain) {
    const flat = makeFlatTokenEntry(symbol, entry.decimals, flatChain, flatContract, source, sourceId);
    return flat ? [flat] : [];
  }

  const rows = [];
  const maybeAdd = (field, chainKey, decimalsField = "decimals") => {
    const token = makeFlatTokenEntry(symbol, entry[decimalsField] ?? entry.decimals, chainKey, entry[field], source, sourceId);
    if (token) rows.push(token);
  };

  maybeAdd("contractAddress", "evm:1");
  maybeAdd("tronContractAddress", "tron");
  maybeAdd("pulseChainContractAddress", "pulsechain");
  maybeAdd("tonContractAddress", "ton");
  maybeAdd("avaxContractAddress", "evm:43114", "avaxdecimals");
  maybeAdd("solanaContractAddress", "solana", "soldecimals");

  return rows;
}

function expandTool88TokenSeedEntries(entries, source = "shipped") {
  const output = [];
  const seen = new Set();
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    expandTool88TokenSeedEntry(entry, source).forEach((token) => {
      if (!token || seen.has(token.key)) return;
      seen.add(token.key);
      output.push(token);
    });
  });
  return output;
}

function safeReadTool88TokenOverrides() {
  const empty = { customTokens: [], removedTokenKeys: [] };
  try {
    if (typeof localStorage === "undefined") {
      return empty;
    }
    const raw = localStorage.getItem(TOOL88_TOKEN_MANAGER_STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return empty;
    const customTokens = expandTool88TokenSeedEntries(parsed.customTokens || [], "custom");
    const removedTokenKeys = Array.isArray(parsed.removedTokenKeys)
      ? parsed.removedTokenKeys.map((key) => normalizeTokenText(key)).filter(Boolean)
      : [];
    return { customTokens, removedTokenKeys };
  } catch (error) {
    console.warn("Could not read Tool 88 token manager overrides.", error);
    return empty;
  }
}

function safeWriteTool88TokenOverrides(overrides) {
  try {
    if (typeof localStorage === "undefined") {
      return;
    }
    localStorage.setItem(
      TOOL88_TOKEN_MANAGER_STORAGE_KEY,
      JSON.stringify({
        customTokens: Array.isArray(overrides?.customTokens) ? overrides.customTokens : [],
        removedTokenKeys: Array.isArray(overrides?.removedTokenKeys) ? overrides.removedTokenKeys : [],
      })
    );
  } catch (error) {
    console.warn("Could not save Tool 88 token manager overrides.", error);
  }
}

const SHIPPED_TOKEN_REGISTRY_EXPANDED = Object.freeze(expandTool88TokenSeedEntries(SHIPPED_TOKEN_REGISTRY, "shipped"));

function normalizeTraceThroughEntityText(value) {
  return String(value ?? "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function normalizeTraceThroughEntityComparable(value) {
  return normalizeTraceThroughEntityText(value)
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeTraceThroughEntityAddress(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";
  if (/^0x[a-fA-F0-9]{40}$/.test(text)) return text.toLowerCase();
  return text;
}

function splitTraceThroughEntityList(value) {
  return String(value ?? "")
    .split(/[\n,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function getTraceThroughEntityKey(entry = {}) {
  const sourceId = normalizeTraceThroughEntityText(entry.id || entry.sourceId || entry.source_id || "");
  if (sourceId) {
    return sourceId.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }
  const name = normalizeTraceThroughEntityComparable(entry.canonicalName || entry.name || entry.label || "");
  if (name) return name;
  const addressKey = (Array.isArray(entry.addresses) ? entry.addresses : [])
    .map((address) => normalizeTraceThroughEntityAddress(address).toLowerCase())
    .filter(Boolean)
    .sort()
    .join("|");
  return addressKey || "trace-through-entity";
}

function normalizeTraceThroughEntityEntry(entry = {}, source = "shipped") {
  if (!entry || typeof entry !== "object") return null;
  const canonicalName = normalizeTraceThroughEntityText(entry.canonicalName || entry.name || entry.label || "");
  const aliases = Array.from(new Set((Array.isArray(entry.aliases) ? entry.aliases : splitTraceThroughEntityList(entry.aliases || entry.aliasesText))
    .map((alias) => normalizeTraceThroughEntityText(alias))
    .filter(Boolean)));
  const addresses = Array.from(new Set((Array.isArray(entry.addresses) ? entry.addresses : splitTraceThroughEntityList(entry.addresses || entry.addressesText))
    .map((address) => normalizeTraceThroughEntityAddress(address))
    .filter(Boolean)));
  if (!canonicalName && !aliases.length && !addresses.length) return null;
  const normalized = {
    key: "",
    canonicalName: canonicalName || aliases[0] || addresses[0],
    aliases,
    addresses,
    source,
    sourceId: normalizeTraceThroughEntityText(entry.id || entry.sourceId || entry.source_id || ""),
    note: normalizeTraceThroughEntityText(entry.note || entry.notes || ""),
  };
  normalized.key = getTraceThroughEntityKey(normalized);
  return normalized;
}

function normalizeTraceThroughEntityEntries(entries, source = "shipped") {
  const output = [];
  const seen = new Set();
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const normalized = normalizeTraceThroughEntityEntry(entry, source);
    if (!normalized || seen.has(normalized.key)) return;
    seen.add(normalized.key);
    output.push(normalized);
  });
  return output;
}

function safeReadTool88TraceThroughEntityOverrides() {
  const empty = { customEntities: [], removedEntityKeys: [] };
  try {
    if (typeof localStorage === "undefined") return empty;
    const raw = localStorage.getItem(TOOL88_TRACE_THROUGH_ENTITY_STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return empty;
    const customEntities = normalizeTraceThroughEntityEntries(parsed.customEntities || parsed.entities || [], "custom");
    const removedEntityKeys = Array.isArray(parsed.removedEntityKeys)
      ? parsed.removedEntityKeys.map((key) => normalizeTraceThroughEntityText(key)).filter(Boolean)
      : [];
    return { customEntities, removedEntityKeys };
  } catch (error) {
    console.warn("Could not read Tool 88 trace-through entity overrides.", error);
    return empty;
  }
}

function safeWriteTool88TraceThroughEntityOverrides(overrides) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(
      TOOL88_TRACE_THROUGH_ENTITY_STORAGE_KEY,
      JSON.stringify({
        customEntities: Array.isArray(overrides?.customEntities) ? overrides.customEntities : [],
        removedEntityKeys: Array.isArray(overrides?.removedEntityKeys) ? overrides.removedEntityKeys : [],
      })
    );
  } catch (error) {
    console.warn("Could not save Tool 88 trace-through entity overrides.", error);
  }
}

const SHIPPED_TRACE_THROUGH_ENTITY_REGISTRY_EXPANDED = Object.freeze(
  normalizeTraceThroughEntityEntries(SHIPPED_TRACE_THROUGH_ENTITY_REGISTRY, "shipped")
);

// Embedded Case Buddy solver instances are driven through public methods and
// do not need Tool88's large standalone interface or Handsontable DOM. This
// tiny adapter preserves the exact getData/loadData contract used internally.
const Tool88HeadlessSpreadsheetStore = {
  name: "Tool88HeadlessSpreadsheetStore",
  data() {
    return { rows: [] };
  },
  methods: {
    cloneRows(rows = []) {
      return (Array.isArray(rows) ? rows : []).map((row) => (Array.isArray(row) ? row.slice() : []));
    },
    getData() {
      return this.cloneRows(this.rows);
    },
    loadData(rows = []) {
      this.rows = this.cloneRows(rows);
    },
  },
  render() {
    return null;
  },
};

export default {
  name: "Tool88Page_CaseBuddy",
  components: {
    SpreadsheetComponent,
    Tool88HeadlessSpreadsheetStore,
  },
  props: {
    caseBuddyEmbedded: {
      type: Boolean,
      default: false,
    },
    caseBuddyHeadless: {
      type: Boolean,
      default: false,
    },
    caseBuddyStartInAdvancedView: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "case-buddy-status",
    "case-buddy-progress",
    "case-buddy-result",
    "case-buddy-guardrail",
    "case-buddy-flow-complete",
  ],
  data() {
    const savedSettings = safeReadTool88Settings();
    const tokenOverrides = safeReadTool88TokenOverrides();
    const traceThroughEntityOverrides = safeReadTool88TraceThroughEntityOverrides();

    return {
      simpleViewOpen: !this.caseBuddyHeadless,
      simpleSettingsExpanded: false,
      simpleInputText: "",
      simpleStagedAddressOverrides: {},
      simpleViewMessage: "",
      simpleViewPreviousBodyOverflow: "",
      simpleRecommendationApplied: false,
      simpleFlowActionReady: false,
      simpleLastLoadedInputText: "",
      caseBuddyIntegrationRunState: createTool88CaseBuddyIntegrationRunState(),
      caseBuddyIntegrationLastSnapshot: null,
      caseBuddyActiveRootContext: null,
      caseBuddyLowValueRuleEnabledOverride: null,
      caseBuddyLowValueRulePolicy: null,
      caseBuddySelectedOutputInferenceBlock: null,
      caseBuddyLowValuePolicyOverride: null,
      directAutomationRunState: createTool88DirectAutomationRunState(),
      directAutomationSpeedMultiplier: savedSettings.directAutomationSpeedMultiplier,
      traceDirection: savedSettings.traceDirection,
      hopLogicType: savedSettings.hopLogicType,
      tool88NoteColumn: this.caseBuddyEmbedded ? "W" : savedSettings.tool88NoteColumn,
      enableAdditionalServiceChecks: savedSettings.enableAdditionalServiceChecks !== false,
      enableDepositEntityHopExtension: savedSettings.enableDepositEntityHopExtension === true,
      enableNativeDirectSpendMatchGuard: savedSettings.enableNativeDirectSpendMatchGuard === true,
      caseBuddyNativeDirectSpendGuardForcedOff: false,
      enableSessionDataCache: savedSettings.enableSessionDataCache !== false,
      sessionCacheRevision: 0,
      flowCompletionMaxDepth: savedSettings.flowCompletionMaxDepth,
      flowCompletionValueTooLowEnabled: savedSettings.flowCompletionValueTooLowEnabled !== false,
      lowValueThresholdBtc: savedSettings.lowValueThresholdBtc,
      lowValueThresholdNative: savedSettings.lowValueThresholdNative,
      lowValueThresholdStable: savedSettings.lowValueThresholdStable,
      lowValueThresholdOther: savedSettings.lowValueThresholdOther,
      flowCompletionTraceThroughEntitiesText: savedSettings.flowCompletionTraceThroughEntitiesText,
      flowCompletionScanResult: null,
      flowCompletionDepth: 0,
      lastFlowSelectionResult: null,
      flowAbsorbResult: null,
      warningThreshold: INTERNAL_WARNING_THRESHOLD,
      hardLimitThreshold: INTERNAL_HARD_LIMIT_THRESHOLD,
      useMockBackend: false,

      tokenManagerOverrides: tokenOverrides,
      tokenManagerDraft: {
        symbol: "",
        chainKey: "evm:1",
        contractAddress: "",
        decimals: 6,
      },
      tokenManagerImportText: "",
      tokenManagerSearch: "",
      tokenManagerMessage: "",

      traceThroughEntityOverrides,
      traceThroughEntityDraft: {
        canonicalName: "",
        aliasesText: "",
        addressesText: "",
        note: "",
      },
      traceThroughEntityImportText: "",
      traceThroughEntitySearch: "",
      traceThroughEntityMessage: "",

      endpointUrl: TOOL88_ENDPOINT_URL,
      selectedExampleKey: savedSettings.selectedExampleKey,

      isProcessing: false,
      statusMessage: "",
      warnings: [],
      reportText: "",
      lastRunMeta: null,

      inputSnapshotRows: [],
      outputBaseRows: [],
      outputSnapshotRows: [],
      headerDetected: false,
      dataStartIndex: 0,
      parsedEntries: [],
      selectedEntryKey: "",
      targetCandidates: [],
      selectedTargetAddress: "",
      countLookupResult: null,
      attributionLookupResult: null,
      transactionSignalResult: null,
      traceResult: null,
      dormantResult: null,
      traceTransactionPayload: null,
      pathEndingResult: null,
      flowCompletionMessage: "",
      swapExplorer: null,
      swapExplorerActiveTab: "main",
      swapSelectedTransferKey: "",
      swapApplyResult: null,
      bridgeExplorer: null,
      bridgeSelectedCandidateKey: "",
      bridgeApplyResult: null,
      bridgeCopiedValue: "",
      backendJobMeta: null,
      countJobMeta: null,
      attributionJobMeta: null,
      signalJobMeta: null,
      traceFetchProgress: null,
      traceFetchStopRequested: false,
      traceFetchStopMessage: "",
      traceInspectorRows: [],
      traceInspectorFilter: "all",
      traceInspectorDisplayLimit: 250,
      traceCopiedHash: "",
      traceTransactionOfInterestHash: "",
      lastTraceBatchSequenceReceived: 0,
      lastTraceProgressEventKey: "",
      lastTraceProgressEventAtMs: 0,
      isSampleDataLoaded: false,
      authSessionCache: null,
      monitoringExecutionContext: null,
      monitoringAttestation: null,
      isRefreshingSelections: false,
    };
  },
  computed: {
    simpleFlowMiniMap() {
      return this.buildSimpleFlowMiniMap();
    },

    simpleInputStagedRows() {
      return this.buildSimpleInputStagedRows(this.simpleInputText);
    },

    simpleInputShowsStagedRows() {
      return Boolean(this.simpleInputStagedRows.length && this.simpleInputStagedRows.length <= 250);
    },

    directionLabel() {
      return this.traceDirection === "reverse" ? "Reverse" : "Forward";
    },

    targetColumnLabel() {
      return this.traceDirection === "reverse"
        ? "Column C / From Address"
        : "Column D / To Address";
    },

    hopLogicTypeLabel() {
      if (this.hopLogicType === "FIFO") {
        return "FIFO";
      }
      if (this.hopLogicType === "LIFO") {
        return "LIFO / PTDQ";
      }
      return "Next chronological";
    },

    tool88NoteColumnLabel() {
      return this.tool88NoteColumn === "W" ? "Column W / Tool 88 Notes" : "Column J / Notes";
    },

    hasSessionDataCacheEntries() {
      // sessionCacheRevision intentionally makes this computed value reactive while
      // the actual large transaction arrays stay in a module-level Map instead of Vue state.
      const revision = this.sessionCacheRevision;
      const hasEntries = TOOL88_SESSION_DATA_CACHE.counts.size > 0 || TOOL88_SESSION_DATA_CACHE.traces.size > 0;
      return revision >= 0 && hasEntries;
    },

    sessionDataCacheSummary() {
      const revision = this.sessionCacheRevision;
      const counts = TOOL88_SESSION_DATA_CACHE.counts.size;
      const traces = TOOL88_SESSION_DATA_CACHE.traces.size;
      const stats = TOOL88_SESSION_DATA_CACHE.stats;
      const parts = [
        `${this.formatInteger(counts)} count result${counts === 1 ? "" : "s"} cached`,
        `${this.formatInteger(traces)} transaction histor${traces === 1 ? "y" : "ies"} cached`,
      ];
      if (stats.countHits || stats.traceHits) {
        parts.push(`${this.formatInteger(stats.countHits + stats.traceHits)} reused this session`);
      }
      return `Session cache: ${parts.join(" · ")}. Refreshing or closing this tab clears the cached data.${revision < 0 ? "" : ""}`;
    },

    shippedTraceThroughEntityCount() {
      return SHIPPED_TRACE_THROUGH_ENTITY_REGISTRY_EXPANDED.length;
    },

    customTraceThroughEntityCount() {
      return Array.isArray(this.traceThroughEntityOverrides?.customEntities)
        ? this.traceThroughEntityOverrides.customEntities.length
        : 0;
    },

    removedTraceThroughEntityCount() {
      return Array.isArray(this.traceThroughEntityOverrides?.removedEntityKeys)
        ? this.traceThroughEntityOverrides.removedEntityKeys.length
        : 0;
    },

    effectiveTraceThroughEntityRegistry() {
      const removed = new Set(this.traceThroughEntityOverrides?.removedEntityKeys || []);
      const rowsByKey = new Map();

      SHIPPED_TRACE_THROUGH_ENTITY_REGISTRY_EXPANDED.forEach((entity) => {
        if (!entity || removed.has(entity.key)) return;
        rowsByKey.set(entity.key, { ...entity, source: entity.source || "shipped" });
      });

      (this.traceThroughEntityOverrides?.customEntities || []).forEach((entity) => {
        const normalized = normalizeTraceThroughEntityEntry(entity, "custom");
        if (normalized) rowsByKey.set(normalized.key, { ...normalized, source: "custom" });
      });

      return Array.from(rowsByKey.values()).sort((left, right) =>
        left.canonicalName.localeCompare(right.canonicalName)
      );
    },

    traceThroughEntitySummary() {
      const effective = this.effectiveTraceThroughEntityRegistry.length;
      const parts = [`${this.formatInteger(effective)} active`];
      parts.push(`${this.formatInteger(this.shippedTraceThroughEntityCount)} shipped`);
      if (this.customTraceThroughEntityCount) parts.push(`${this.formatInteger(this.customTraceThroughEntityCount)} user-added`);
      if (this.removedTraceThroughEntityCount) parts.push(`${this.formatInteger(this.removedTraceThroughEntityCount)} hidden`);
      return parts.join(" · ");
    },

    filteredTraceThroughEntityPreview() {
      const query = this.toText(this.traceThroughEntitySearch).toLowerCase();
      const rows = this.effectiveTraceThroughEntityRegistry.filter((entity) => {
        if (!query) return true;
        const haystack = [
          entity.canonicalName,
          ...(entity.aliases || []),
          ...(entity.addresses || []),
          entity.note,
          entity.source,
        ].join(" ").toLowerCase();
        return haystack.includes(query);
      });
      return rows.slice(0, 60);
    },


    exampleOptions() {
      return [
        { value: "basic", label: "ETH/ERC20 one-output" },
        { value: "eth-native", label: "ETH native + internals" },
        { value: "bsc-native", label: "BSC native BNB" },
        { value: "ambiguous", label: "Ambiguous EVM outputs" },
        { value: "evm-event-log", label: "EVM event-log check" },
        { value: "btc-memo", label: "BTC OP_RETURN / memo" },
        { value: "tron-trc20", label: "TRON TRC20 fetch" },
        { value: "tron-trx", label: "TRON TRX count" },
        { value: "unsupported-sol", label: "Unsupported count example" },
        { value: "dormant", label: "Dormant/no-exit" },
        { value: "warning", label: "High-count caution" },
        { value: "hard-limit", label: "Blocked-count" },
      ];
    },

    tokenChainOptions() {
      return TOKEN_CHAIN_OPTIONS;
    },

    shippedTokenCount() {
      return SHIPPED_TOKEN_REGISTRY_EXPANDED.length;
    },

    customTokenCount() {
      return Array.isArray(this.tokenManagerOverrides?.customTokens)
        ? this.tokenManagerOverrides.customTokens.length
        : 0;
    },

    removedTokenCount() {
      return Array.isArray(this.tokenManagerOverrides?.removedTokenKeys)
        ? this.tokenManagerOverrides.removedTokenKeys.length
        : 0;
    },

    effectiveTokenRegistry() {
      const removed = new Set(this.tokenManagerOverrides?.removedTokenKeys || []);
      const rowsByKey = new Map();

      SHIPPED_TOKEN_REGISTRY_EXPANDED.forEach((token) => {
        if (!token || removed.has(token.key)) return;
        rowsByKey.set(token.key, { ...token, source: token.source || "shipped" });
      });

      (this.tokenManagerOverrides?.customTokens || []).forEach((token) => {
        const expanded = expandTool88TokenSeedEntry(token, "custom");
        expanded.forEach((row) => {
          if (row) rowsByKey.set(row.key, { ...row, source: "custom" });
        });
      });

      return Array.from(rowsByKey.values()).sort((left, right) => {
        const symbolSort = left.symbol.localeCompare(right.symbol);
        if (symbolSort !== 0) return symbolSort;
        return this.formatTokenChainLabel(left.chainKey).localeCompare(this.formatTokenChainLabel(right.chainKey));
      });
    },

    tokenRegistrySummary() {
      const effective = this.effectiveTokenRegistry.length;
      const custom = this.customTokenCount;
      const removed = this.removedTokenCount;
      const parts = [`${this.formatInteger(effective)} active`];
      parts.push(`${this.formatInteger(this.shippedTokenCount)} shipped`);
      if (custom) parts.push(`${this.formatInteger(custom)} user-added`);
      if (removed) parts.push(`${this.formatInteger(removed)} hidden`);
      return parts.join(" · ");
    },

    filteredTokenRegistryPreview() {
      const query = this.toText(this.tokenManagerSearch).toLowerCase();
      const rows = query
        ? this.effectiveTokenRegistry.filter((token) => [
            token.symbol,
            token.contractAddress,
            token.chainKey,
            this.formatTokenChainLabel(token.chainKey),
            token.source,
          ].join(" ").toLowerCase().includes(query))
        : this.effectiveTokenRegistry;
      return rows.slice(0, 24);
    },

    selectedTokenResolution() {
      return this.getSelectedTokenResolution();
    },

    swapExplorerTabs() {
      const explorer = this.swapExplorer || {};
      const tabs = explorer.tabs || {};
      return [
        { key: "main", label: "Main transaction", rows: tabs.main || [] },
        { key: "internal", label: "Internal transfers", rows: tabs.internal || [] },
        { key: "erc20", label: "ERC20 / TRC20 transfers", rows: tabs.erc20 || [] },
        { key: "nft", label: "NFT transfers", rows: tabs.nft || [] },
      ];
    },

    activeSwapExplorerRows() {
      const active = this.swapExplorerTabs.find((tab) => tab.key === this.swapExplorerActiveTab);
      return active ? active.rows : [];
    },

    swapAutomaticCandidate() {
      return this.swapExplorer?.automaticCandidate || null;
    },

    selectedSwapCandidate() {
      if (!this.swapExplorer || !this.swapSelectedTransferKey) {
        return null;
      }
      return this.buildSwapCandidateFromSelectedTransfer(this.swapSelectedTransferKey);
    },

    swapExplorerStatusLabel() {
      if (!this.swapExplorer) {
        return "Not loaded";
      }
      if (this.swapAutomaticCandidate?.canApply) {
        return "Solvable candidate found";
      }
      if (this.swapExplorer?.nftWarning) {
        return "NFT/manual review";
      }
      return "Manual review";
    },

    bridgeCandidates() {
      return Array.isArray(this.bridgeExplorer?.candidates) ? this.bridgeExplorer.candidates : [];
    },

    bridgeBestCandidate() {
      if (!this.bridgeCandidates.length) return null;
      const explicit = this.bridgeCandidates.find((candidate) => candidate.isBestCandidate && candidate.canApply);
      if (explicit) return explicit;
      return this.bridgeCandidates.find((candidate) => candidate.canApply && candidate.confidence === "high") || this.bridgeCandidates.find((candidate) => candidate.canApply) || this.bridgeCandidates[0];
    },

    selectedBridgeCandidate() {
      if (!this.bridgeSelectedCandidateKey) return null;
      return this.bridgeCandidates.find((candidate) => candidate.key === this.bridgeSelectedCandidateKey) || null;
    },

    bridgeLogRows() {
      return Array.isArray(this.bridgeExplorer?.logs) ? this.bridgeExplorer.logs : [];
    },

    bridgeStages() {
      return Array.isArray(this.bridgeExplorer?.stages) ? this.bridgeExplorer.stages : [];
    },

    bridgeExplorerStatusLabel() {
      if (!this.bridgeExplorer) return "Not loaded";
      if (this.bridgeBestCandidate?.canApply && this.bridgeBestCandidate?.confidence === "high") return "High-confidence bridge candidate found";
      if (this.bridgeCandidates.some((candidate) => candidate.canApply)) return "Candidate destination found";
      return "Manual bridge review";
    },


    hasProcessedInput() {
      return Boolean(this.inputSnapshotRows.length || this.parsedEntries.length);
    },

    flowCompletionResult() {
      return this.flowCompletionScanResult;
    },


    flowCompletionHasAutoMarkableEndings() {
      return Boolean((this.flowCompletionResult?.complete || []).some((ending) => ending && (ending.autoMarkable || ending.canAutoMark)));
    },

    normalizedFlowCompletionMaxDepth() {
      const depth = Number(this.flowCompletionMaxDepth);
      if (!Number.isFinite(depth) || depth < 1) {
        return DEFAULT_FLOW_COMPLETION_MAX_DEPTH;
      }
      return Math.max(1, Math.min(500, Math.trunc(depth)));
    },


    finalDecisionMade() {
      return Boolean(
        this.isTraceResultReadyForContinue(this.traceResult) ||
        this.pathEndingResult ||
        this.swapApplyResult ||
        this.bridgeApplyResult ||
        this.flowAbsorbResult
      );
    },

    simpleHasOutputRows() {
      void this.traceResult;
      void this.pathEndingResult;
      void this.swapApplyResult;
      void this.bridgeApplyResult;
      void this.flowAbsorbResult;
      void this.lastRunMeta;
      void this.outputSnapshotRows;
      const rows = this.getCurrentOutputRowsForExport();
      return rows.some((row) => this.rowHasAnyData(row));
    },

    simpleDecisionResultReady() {
      return Boolean(
        this.isTraceResultReadyForContinue(this.traceResult) ||
        this.pathEndingResult ||
        this.swapApplyResult ||
        this.bridgeApplyResult
      );
    },

    simpleResultReadyForContinue() {
      return Boolean(
        this.isTraceResultReadyForContinue(this.traceResult) ||
        this.pathEndingResult ||
        this.swapApplyResult ||
        this.bridgeApplyResult ||
        this.simpleFlowActionReady
      );
    },

    simpleDecisionRecorded() {
      return Boolean(this.simpleDecisionResultReady || this.simpleFlowActionReady);
    },

    simpleHasTargetAmbiguity() {
      return Boolean(this.targetCandidates.length > 1 && !this.countLookupResult);
    },

    simpleCanAutoDecide() {
      return Boolean(
        !this.isProcessing &&
        !this.simpleRecommendationApplied &&
        !this.simpleResultReadyForContinue &&
        !this.simpleHasTargetAmbiguity &&
        !this.flowCompletionResult?.done &&
        this.countLookupResult &&
        this.autoDecisionPreview &&
        this.autoDecisionPreview.action !== "review"
      );
    },

    directAutomationActive() {
      return this.directAutomationRunState?.active === true;
    },

    directAutomationSpeedLabel() {
      const speed = this.normalizeDirectAutomationSpeed(this.directAutomationSpeedMultiplier);
      return `${speed.toFixed(2).replace(/\.?0+$/, "")}x`;
    },

    directAutomationCanStart() {
      return !this.caseBuddyEmbedded && !this.directAutomationActive && !this.isProcessing;
    },

    directAutomationButtonText() {
      if (this.directAutomationActive) return "Auto running";
      if (this.flowCompletionResult?.done && !this.toText(this.simpleInputText)) return "Flow complete";
      return "Run Auto";
    },

    directAutomationStatusMessage() {
      const state = this.directAutomationRunState || {};
      if (state.active) return state.detail || "Tool 88 direct auto is running.";
      if (state.lastGuardrail?.message) return state.lastGuardrail.message;
      if (state.status && state.status !== "idle") return state.detail || state.status;
      return "Runs the same guided next-step logic with pauses, loop detection, and flow-complete checks.";
    },

    simpleRecommendationButtonText() {
      if (this.simpleHasTargetAmbiguity) return "Choose target address";
      if (this.flowCompletionResult?.done) return "Flow complete";
      if (!this.countLookupResult) return "Run checks";
      if (!this.autoDecisionPreview) return "Auto decide";
      if (this.autoDecisionPreview.action === "review") return "Manual review needed";
      const label = this.toText(this.autoDecisionPreview.buttonLabel).replace(/^Apply\s+/i, "").trim();
      return label ? `Auto decide: ${label}` : "Auto decide";
    },

    simplePrimaryActionText() {
      if (this.isProcessing) return this.statusMessage || "Working...";
      if (this.simpleResultReadyForContinue) return "Use result + choose next unresolved";
      if (this.flowCompletionResult?.done) return "Flow complete";
      if (this.simpleHasTargetAmbiguity) return this.selectedTargetAddress ? "Run checks for selected address" : "Choose target address";
      if (!this.countLookupResult) return (this.selectedEntryKey || this.parsedEntries.length || this.toText(this.simpleInputText)) ? "Run checks" : "Load + run checks";
      return this.simpleRecommendationButtonText;
    },

    simplePrimaryActionDisabled() {
      if (this.isProcessing) return true;
      if (this.simpleResultReadyForContinue) return false;
      if (this.flowCompletionResult?.done) return true;
      if (this.simpleHasTargetAmbiguity) return !this.selectedTargetAddress;
      if (!this.countLookupResult) {
        return !(
          this.selectedEntryKey ||
          this.parsedEntries.length ||
          this.toText(this.simpleInputText) ||
          (this.$refs.inputSpreadsheet?.getData?.() || []).some((row) => this.rowHasAnyData(row))
        );
      }
      if (!this.autoDecisionPreview || this.autoDecisionPreview.action === "review") return true;
      return Boolean(this.simpleRecommendationApplied);
    },

    simpleGuidedActionHint() {
      if (this.isProcessing) return this.statusMessage || "Tool 88 is working on the current step.";
      if (this.simpleHasTargetAmbiguity) return "Tool 88 found more than one possible target address. Pick the address to research, then run checks for that address.";
      if (this.flowCompletionResult?.done) return "The flow appears complete. Copy or download the raw output when you are ready.";
      if (this.simpleResultReadyForContinue) return "This step has a result. Use it as the next input and Tool 88 will choose the next unresolved ending for you.";
      if (!this.countLookupResult) return this.selectedEntryKey ? "The next unresolved ending is selected. Run checks to get count, attribution, signal checks, and the next recommendation." : "Load data and run checks first. Tool 88 will then recommend the safest next action.";
      if (!this.autoDecisionPreview || this.autoDecisionPreview.action === "review") return "Tool 88 recommends manual review. Open advanced view for details before applying an action.";
      return "Checks are complete. Apply the recommended action to produce the next row or finalize this ending.";
    },

    simpleViewSummary() {
      if (this.isProcessing) return { status: "active", title: "Tool 88 is working", body: this.statusMessage || "Fetching and analyzing the selected hop." };
      if (this.simpleHasTargetAmbiguity) return { status: "review", title: "Choose a target address", body: "This entry has multiple possible addresses. Select the address Tool 88 should research, then run checks again." };
      if (this.simpleFlowActionReady && !this.simpleDecisionResultReady) return { status: "complete", title: "Path ending recorded", body: "Tool 88 finalized a low-value or flow-helper ending. Use the result as new input to keep solving." };
      if (this.traceResult) {
        const count = this.traceResult.exitRows?.length || 0;
        if (count > 0) return { status: "complete", title: `${count} ${this.traceResult.analysisDirection === "reverse" ? "source" : "hop"}${count === 1 ? "" : "s"} found`, body: "Review or use the output as the next input to continue the flow." };
        const dormant = this.getTool88CurrentDormantResult();
        if (dormant && !this.traceResult.preventPathEnding) {
          return {
            status: "complete",
            title: "Dormant funds detected",
            body: dormant.note || `A portion of the funds remains at the receiving address (${dormant.dormantAmountText || "followed funds"}). Path ends.`
          };
        }
        return {
          status: this.traceResult.preventPathEnding ? "review" : "complete",
          title: this.traceResult.preventPathEnding ? "Manual review needed" : "No hop found",
          body: this.traceResult.preventPathEnding ? "Tool 88 avoided changing the output because the hop/source could not be resolved safely." : "No qualifying exit/source was found, so the entry was finalized as a path ending.",
        };
      }
      if (this.pathEndingResult) return { status: "complete", title: "Path ending recorded", body: "The selected entry was finalized. Use the result as new input to continue the flow loop." };
      if (this.swapApplyResult) return { status: "complete", title: "Swap result ready", body: "The swap result was inserted into the output raw data." };
      if (this.bridgeApplyResult) return { status: "complete", title: "Bridge result ready", body: "The bridge result was inserted into the output raw data." };
      if (this.flowCompletionResult?.done) return { status: "complete", title: "Flow complete", body: "Tool 88 does not see another unresolved path ending in the current input." };
      if (this.countLookupResult) {
        const decision = this.autoDecisionPreview;
        return { status: decision?.status || "review", title: decision?.title || "Checks complete", body: decision?.summary || "Use the recommendation or switch to advanced view for detailed inspection." };
      }
      if (this.parsedEntries.length) return { status: "active", title: "Input loaded", body: "Run checks to get count, attribution, signal checks, and a recommendation." };
      return { status: "idle", title: "Paste data to begin", body: "Tool 88 can accept full Case Buddy TSV or smaller transaction/address lists and load the hidden spreadsheet for you." };
    },

    selectedEntry() {
      return (
        this.parsedEntries.find((entry) => entry.key === this.selectedEntryKey) ||
        null
      );
    },

    selectedCandidate() {
      const selectedKey = this.normalizeAddressKey(this.selectedTargetAddress);
      if (!selectedKey) {
        return null;
      }

      return (
        this.targetCandidates.find(
          (candidate) => this.normalizeAddressKey(candidate.address) === selectedKey
        ) || null
      );
    },

    countSeverityClass() {
      if (!this.countLookupResult) {
        return "count-panel-neutral";
      }
      if (this.countLookupResult.countAvailable === false) {
        return "count-panel-neutral";
      }
      if (this.countLookupResult.countUnavailable || this.countLookupResult.countAvailable === false) {
        return "count-panel-neutral";
      }
      if (!this.countLookupResult.canTrace) {
        return "count-panel-block";
      }
      if (this.countLookupResult.exceedsWarning) {
        return "count-panel-warning";
      }
      return "count-panel-ok";
    },


    traceFetchExpectedCount() {
      const candidates = [
        this.traceFetchProgress?.trace_fetch?.expected_count,
        this.traceFetchProgress?.expected_count,
        this.traceFetchProgress?.expected_transaction_count,
        this.countLookupResult?.transactionCount,
        this.countLookupResult?.transaction_count,
      ];
      for (const candidate of candidates) {
        const number = Number(candidate);
        if (Number.isFinite(number) && number >= 0) {
          return Math.trunc(number);
        }
      }
      return -1;
    },

    traceFetchFetchedCount() {
      const serverFetched = Number(this.traceFetchProgress?.trace_fetch?.fetched_count ?? this.traceFetchProgress?.fetched_count);
      if (Number.isFinite(serverFetched) && serverFetched >= 0) {
        return Math.max(serverFetched, this.traceInspectorRows.length);
      }
      return this.traceInspectorRows.length;
    },

    traceFetchProgressPercent() {
      const expected = this.traceFetchExpectedCount;
      if (expected <= 0) {
        return null;
      }
      return (this.traceFetchFetchedCount / expected) * 100;
    },

    traceFetchProgressPercentText() {
      const percent = this.traceFetchProgressPercent;
      if (percent === null) {
        return ["complete", "success"].includes(this.toText(this.traceFetchProgress?.status).toLowerCase()) ? "Complete" : "Fetching";
      }
      return `${percent.toFixed(percent >= 100 ? 0 : 1)}%`;
    },

    traceFetchProgressBarValue() {
      const percent = this.traceFetchProgressPercent;
      if (percent === null) {
        const status = this.toText(
          this.traceFetchProgress?.trace_fetch?.status ||
            this.traceFetchProgress?.trace_status ||
            this.traceFetchProgress?.status ||
            ""
        ).toLowerCase();
        return ["complete", "success"].includes(status) ? 100 : 0;
      }
      return Math.max(0, Math.min(100, percent));
    },

    traceFetchStatusLabel() {
      const status = this.toText(
        this.traceFetchProgress?.trace_fetch?.status ||
          this.traceFetchProgress?.trace_status ||
          this.traceFetchProgress?.status ||
          ""
      ).toLowerCase();
      if (status === "service_stop" || status === "stopped_for_service" || status === "potential_service_stop") return "Potential service detected";
      if (status === "complete" || status === "success") return "Fetch complete";
      if (status === "failed") return "Fetch failed";
      if (status === "queued") return "Queued";
      if (status === "running") return "Fetching";
      return this.traceFetchProgress ? "Preparing" : "Not started";
    },

    traceInspectorTransferLabel() {
      const provider = this.toText(
        this.traceFetchProgress?.trace_fetch?.provider ||
          this.traceFetchProgress?.provider ||
          this.countLookupResult?.provider ||
          ""
      ).toLowerCase();
      const route = this.toText(
        this.traceFetchProgress?.trace_fetch?.route ||
          this.traceFetchProgress?.trace_route ||
          this.countLookupResult?.route ||
          ""
      ).toLowerCase();
      const chainName = this.toText(
        this.traceFetchProgress?.trace_fetch?.chain_name ||
          this.traceFetchProgress?.chain_name ||
          this.countLookupResult?.chainName ||
          this.countLookupResult?.chain_name ||
          ""
      ).toLowerCase();
      const family = this.toText(this.countLookupResult?.networkFamily || this.countLookupResult?.network_family || "").toLowerCase();
      const countKind = this.toText(this.countLookupResult?.countKind || this.countLookupResult?.count_kind || "").toLowerCase();

      if (route.includes("trc20") || provider.includes("trongrid")) {
        return "TRC20 transfer";
      }
      if (route.includes("tatum") || route.includes("btc") || provider.includes("tatum") || family === "btc") {
        return "BTC transaction";
      }
      if (route.includes("tronscan_trx") || (family === "tron" && countKind === "native_transaction")) {
        return "TRX transaction";
      }
      if (route.includes("native") || route.includes("txlist") || countKind === "native_transaction") {
        return family === "evm" || provider.includes("etherscan") || provider.includes("moralis") ? "native EVM transaction" : "native transaction";
      }
      if (route.includes("erc20") || provider.includes("etherscan") || provider.includes("moralis") || family === "evm") {
        return "ERC20 transfer";
      }
      if (chainName.includes("tron") || family === "tron") {
        return "TRON transaction";
      }
      return "transaction";
    },

    traceInspectorSummary() {
      const rows = this.traceInspectorRows || [];
      const incoming = rows.filter((row) => row.direction === "incoming").length;
      const outgoing = rows.filter((row) => row.direction === "outgoing").length;
      const self = rows.filter((row) => row.direction === "self").length;
      const failed = rows.filter((row) => row.failedOrReverted).length;
      const internal = rows.filter((row) => row.isInternal).length;
      const nonBalance = rows.filter((row) => row.nonBalanceAffecting).length;
      const interest = rows.filter((row) => row.isTransactionOfInterest).length;
      const exits = rows.filter((row) => row.isExit).length;
      const btcInputCount = rows.reduce((sum, row) => sum + (Number(row.btcInputCount) || 0), 0);
      const btcOutputCount = rows.reduce((sum, row) => sum + (Number(row.btcOutputCount) || 0), 0);
      const btcOpReturnCount = rows.reduce((sum, row) => sum + (Number(row.btcOpReturnCount || row.opReturnCount) || 0), 0);
      const trustedTokens = rows.filter((row) => row.tokenAuthenticity?.status === "trusted").length;
      const tokenIssues = rows.filter((row) => row.hasTokenIssue).length;
      const balanceRows = rows.filter((row) => row.assetMatchedForBalance);
      const lastBalanceRow = balanceRows.length ? balanceRows[balanceRows.length - 1] : null;
      return {
        total: rows.length,
        incoming,
        outgoing,
        self,
        failed,
        internal,
        nonBalance,
        interest,
        exits,
        btcInputCount,
        btcOutputCount,
        btcOpReturnCount,
        trustedTokens,
        tokenIssues,
        balanceRows: balanceRows.length,
        assetMatchedForBalance: Boolean(lastBalanceRow),
        assetBalanceAfter: lastBalanceRow ? lastBalanceRow.assetBalanceAfter : null,
        assetBalanceDelta: lastBalanceRow ? lastBalanceRow.assetBalanceDelta : null,
        balanceToken: lastBalanceRow ? lastBalanceRow.balanceToken : "",
      };
    },

    traceInputTransactionFound() {
      return this.traceInspectorSummary.interest > 0;
    },

    traceServiceAssessment() {
      return this.getTraceServiceAssessment();
    },

    traceAssessorPlaceholder() {
      const traceFetch = this.traceFetchProgress?.trace_fetch || {};
      const assessor = traceFetch.service_assessment || traceFetch.hot_wallet_assessor || traceFetch.hot_wallet_assessor_placeholder || traceFetch.service_pattern_placeholder || null;
      if (!assessor || typeof assessor !== "object") {
        return "";
      }
      const note = this.toText(assessor.short_summary || assessor.summary || assessor.note || assessor.message || "");
      const parts = [];
      if (note) parts.push(note);
      const score = Number(assessor.score01 ?? assessor.score);
      if (Number.isFinite(score)) {
        parts.push(`Service-pattern score: ${score.toFixed(2)}.`);
      }
      if (traceFetch.unique_counterparty_sample_count !== undefined) {
        parts.push(`Counterparty sample size so far: ${this.formatInteger(traceFetch.unique_counterparty_sample_count)}.`);
      }
      if (traceFetch.internal_transaction_count) {
        parts.push(`Internal/native balance rows so far: ${this.formatInteger(traceFetch.internal_transaction_count)}.`);
      }
      if (traceFetch.btc_input_count || traceFetch.btc_output_count) {
        parts.push(`BTC inputs/outputs observed so far: ${this.formatInteger(traceFetch.btc_input_count || 0)} inputs / ${this.formatInteger(traceFetch.btc_output_count || 0)} outputs.`);
      }
      const btcOpReturnCount = traceFetch.btc_op_return_output_count || traceFetch.btc_op_return_count || 0;
      if (btcOpReturnCount) {
        parts.push(`BTC OP_RETURN outputs observed so far: ${this.formatInteger(btcOpReturnCount)}.`);
      }
      if (traceFetch.non_balance_affecting_count) {
        parts.push(`Non-balance rows visible for review: ${this.formatInteger(traceFetch.non_balance_affecting_count)}.`);
      }
      if (traceFetch.filtered_non_transfer) {
        parts.push(`Filtered non-transfer rows: ${this.formatInteger(traceFetch.filtered_non_transfer)}.`);
      }
      return parts.join(" ");
    },

    visibleTraceInspectorRows() {
      const rows = this.traceInspectorRows || [];
      const filter = this.traceInspectorFilter;
      let filtered = rows;
      if (filter === "incoming") {
        filtered = rows.filter((row) => row.direction === "incoming");
      } else if (filter === "outgoing") {
        filtered = rows.filter((row) => row.direction === "outgoing");
      } else if (filter === "self") {
        filtered = rows.filter((row) => row.direction === "self");
      } else if (filter === "internal") {
        filtered = rows.filter((row) => row.isInternal);
      } else if (filter === "nonbalance") {
        filtered = rows.filter((row) => row.nonBalanceAffecting);
      } else if (filter === "failed") {
        filtered = rows.filter((row) => row.failedOrReverted);
      } else if (filter === "token-issues") {
        filtered = rows.filter((row) => row.hasTokenIssue);
      } else if (filter === "interest") {
        filtered = rows.filter((row) => row.isTransactionOfInterest);
      } else if (filter === "exits") {
        filtered = rows.filter((row) => row.isExit);
      }
      return filtered.slice(0, this.traceInspectorDisplayLimit);
    },

    hiddenTraceInspectorRowCount() {
      const rows = this.traceInspectorRows || [];
      let count = rows.length;
      if (this.traceInspectorFilter === "incoming") count = rows.filter((row) => row.direction === "incoming").length;
      if (this.traceInspectorFilter === "outgoing") count = rows.filter((row) => row.direction === "outgoing").length;
      if (this.traceInspectorFilter === "self") count = rows.filter((row) => row.direction === "self").length;
      if (this.traceInspectorFilter === "internal") count = rows.filter((row) => row.isInternal).length;
      if (this.traceInspectorFilter === "nonbalance") count = rows.filter((row) => row.nonBalanceAffecting).length;
      if (this.traceInspectorFilter === "failed") count = rows.filter((row) => row.failedOrReverted).length;
      if (this.traceInspectorFilter === "token-issues") count = rows.filter((row) => row.hasTokenIssue).length;
      if (this.traceInspectorFilter === "interest") count = rows.filter((row) => row.isTransactionOfInterest).length;
      if (this.traceInspectorFilter === "exits") count = rows.filter((row) => row.isExit).length;
      return Math.max(0, count - this.visibleTraceInspectorRows.length);
    },
    preTraceChecks() {
      if (!this.countLookupResult) {
        return [];
      }

      const checks = [];
      const count = Number(this.countLookupResult.transactionCount);
      const countUnavailable = this.countLookupResult.countUnavailable || this.countLookupResult.countAvailable === false || !Number.isFinite(count) || count < 0;
      const warnThreshold = Number(this.warningThreshold || 0);
      const hardLimit = Number(this.hardLimitThreshold || 0);

      if (countUnavailable) {
        checks.push({
          key: "txn-count",
          title: "Transaction count",
          status: "neutral",
          statusLabel: "Unavailable",
          summary: "Tool 88 could not obtain a reliable transaction count for this network/provider route.",
          detail: this.countLookupResult.countUnavailableReason || this.countLookupResult.message || "The user can still continue to the review/decision steps; the count gate will not block trace-through by itself.",
        });
      } else if (count >= hardLimit) {
        checks.push({
          key: "txn-count",
          title: "Transaction count",
          status: "block",
          statusLabel: "Blocked",
          summary: `Count is ${this.formatTransactionCount(count)}. Tool 88 is blocking trace-through for this pass.`,
          detail: "Mark the path ending or narrow the scope before continuing.",
        });
      } else if (count >= warnThreshold) {
        checks.push({
          key: "txn-count",
          title: "Transaction count",
          status: "warn",
          statusLabel: "Caution",
          summary: `Count is ${this.formatTransactionCount(count)}. Tool 88 recommends caution before tracing through.`,
          detail: "Trace-through is allowed, but this address may be slow or analytically noisy.",
        });
      } else {
        checks.push({
          key: "txn-count",
          title: "Transaction count",
          status: "ok",
          statusLabel: "OK",
          summary: `Count is ${this.formatTransactionCount(count)}. Tool 88 allows trace-through for this pass.`,
          detail: "This passes the initial transaction-volume review.",
        });
      }

      const tokenRegistryCheck = this.buildTokenRegistryCheck();
      if (tokenRegistryCheck) {
        checks.push(tokenRegistryCheck);
      }

      const attributionValues = this.getSelectedAttributionValues();
      const meaningfulAttributionValues = attributionValues.filter((value) => !this.isUnknownAttributionPlaceholder(value));
      const attributionColumnLabel = this.traceDirection === "reverse"
        ? "Column X / From Address Attribution"
        : "Column K / To Address Attribution";
      const attributionResult = this.attributionLookupResult;
      const resultHasMeaningfulLabel = Boolean(
        attributionResult?.displayLabel &&
        !attributionResult?.unknownPlaceholderOnly &&
        !this.isUnknownAttributionPlaceholder(attributionResult.displayLabel)
      );
      if (attributionResult && resultHasMeaningfulLabel) {
        const traceThroughFriendly = this.hasSimpleTraceThroughAttributionSignal(attributionResult.displayLabel);
        checks.push({
          key: "attribution",
          title: "Attribution data",
          status: traceThroughFriendly ? "ok" : (attributionResult.adviceStatus === "block" ? "block" : "warn"),
          statusLabel: traceThroughFriendly ? "Trace-through label" : (attributionResult.statusLabel || "Review"),
          summary: traceThroughFriendly
            ? `Attribution looks like an address-owned label/domain: ${attributionResult.displayLabel}.`
            : `Server/vendor attribution found: ${attributionResult.displayLabel}.`,
          detail: traceThroughFriendly
            ? "This is treated as a trace-through-friendly attribution override instead of a service/path-ending label. The pattern list can be extended later."
            : `${attributionResult.recommendation}${attributionResult.signalsText ? ` Signals: ${attributionResult.signalsText}.` : ""}`,
        });
      } else if (meaningfulAttributionValues.length) {
        const traceThroughFriendlyLocal = meaningfulAttributionValues.some((value) =>
          this.isTraceThroughFriendlyAttributionLabel(value, this.selectedTargetAddress)
        );
        checks.push({
          key: "attribution",
          title: "Attribution data",
          status: traceThroughFriendlyLocal ? "ok" : "warn",
          statusLabel: traceThroughFriendlyLocal ? "Trace-through label" : "Review",
          summary: traceThroughFriendlyLocal
            ? `${attributionColumnLabel} looks address-owned or address-like: ${meaningfulAttributionValues.join("; ")}.`
            : `${attributionColumnLabel} has attribution for the selected target: ${meaningfulAttributionValues.join("; ")}.`,
          detail: traceThroughFriendlyLocal
            ? "Tool 88 treats this as a trace-through-friendly attribution override rather than a service/path-ending label."
            : "Review whether this is a known service, exchange, bridge, or other endpoint where the path should end.",
        });
      } else {
        checks.push({
          key: "attribution",
          title: "Attribution data",
          status: "neutral",
          statusLabel: "No visible hit",
          summary: attributionValues.some((value) => this.isUnknownAttributionPlaceholder(value)) || attributionResult?.unknownPlaceholderOnly
            ? "Attribution is marked Unknown; no service/entity attribution was found for this selected address."
            : `No ${attributionColumnLabel}, address_notes, or Arkham attribution was found for the selected target rows.`,
          detail: "Unknown is a no-attribution placeholder, not evidence of a service or hot wallet. Tool 88 continues to the count and transaction-signal checks.",
        });
      }

      const chainCheck = this.buildTransactionSignalCheck();
      checks.push(chainCheck);

      return checks;
    },

    hasBlockingChecks() {
      const blockingChecks = this.preTraceChecks.filter((check) => check.status === "block");
      if (!blockingChecks.length) return false;

      // In reverse analysis, a deposit classification belongs to the address
      // we are tracing backward through. It is not a source-of-funds ending.
      // Preserve hard transaction-count, token, and signal safeguards while
      // allowing the attribution-only deposit stop to be bypassed.
      if (this.reverseDepositShouldTraceThrough()) {
        return blockingChecks.some((check) => check.key !== "attribution");
      }

      return true;
    },

    canTraceThrough() {
      const explicitReverseBtcInput = this.traceDirection === 'reverse'
        ? this.getBtcReverseInputSelectionMetadata(this.selectedEntry, this.selectedTargetAddress)
        : null;
      if (explicitReverseBtcInput?.explicit) {
        // A selected Bitcoin input identifies an exact previous UTXO. Count,
        // attribution, and signal checks still run for context, but an address-
        // history volume gate must not redirect this deterministic input trace
        // into swap/service logic.
        return Boolean(
          this.countLookupResult
            && this.selectedEntry
            && this.selectedTargetAddress
            && !this.selectedReverseEntryHasZeroFundsAllocation()
        );
      }
      return Boolean(
        this.countLookupResult &&
          this.countLookupResult.canTrace &&
          this.selectedEntry &&
          this.selectedTargetAddress &&
          !this.hasBlockingChecks &&
          !this.selectedReverseEntryHasZeroFundsAllocation()
      );
    },

    autoDecisionPreview() {
      return this.buildAutomaticDecisionPreview();
    },

    workflowSteps() {
      const inputReady = this.parsedEntries.length > 0;
      const countReady = Boolean(this.countLookupResult);
      const checksReady = countReady && this.preTraceChecks.length > 0;
      const decisionReady = this.finalDecisionMade;
      const outputReady = decisionReady;

      return [
        {
          number: 1,
          title: "Load entry",
          text: inputReady ? `${this.parsedEntries.length} entr${this.parsedEntries.length === 1 ? "y" : "ies"} parsed` : "Paste Case Buddy data",
          status: inputReady ? "complete" : "active",
          statusLabel: inputReady ? "Ready" : "Start here",
        },
        {
          number: 2,
          title: "Process + checks",
          text: countReady ? (this.countLookupResult.countAvailable === false ? "Count unavailable" : `${this.formatTransactionCount(this.countLookupResult.transactionCount, this.countLookupResult.countAvailable)} transactions + checks`) : "Run pre-trace lookups",
          status: countReady ? "complete" : inputReady ? "active" : "locked",
          statusLabel: countReady ? "Done" : inputReady ? "Next" : "Locked",
        },
        {
          number: 3,
          title: "Pre-trace checks",
          text: checksReady ? "Review gate complete" : "Txn count + attribution + event/memo checks",
          status: checksReady ? "complete" : countReady ? "active" : "locked",
          statusLabel: checksReady ? "Ready" : countReady ? "Review" : "Locked",
        },
        {
          number: 4,
          title: "Decision",
          text: decisionReady ? "Decision recorded" : "End path or trace through",
          status: decisionReady ? "complete" : checksReady ? "active" : "locked",
          statusLabel: decisionReady ? "Recorded" : checksReady ? "Choose" : "Locked",
        },
        {
          number: 5,
          title: "Output + report",
          text: outputReady ? "Review spreadsheet and report" : "Updates after decision",
          status: outputReady ? "complete" : decisionReady ? "active" : "locked",
          statusLabel: outputReady ? "Ready" : decisionReady ? "Review" : "Locked",
        },
      ];
    },
  },
  watch: {
    simpleViewOpen(value) {
      this.setSimpleViewBodyLock(value);
      if (value) this.scrollCaseBuddyEmbeddedSimpleViewToTop();
    },

    traceDirection() {
      this.saveUserSettings();
      this.resetDecisionState();
    },

    hopLogicType() {
      this.saveUserSettings();
      this.countLookupResult = null;
      this.attributionLookupResult = null;
      this.transactionSignalResult = null;
      this.traceResult = null;
      this.dormantResult = null;
      this.traceTransactionPayload = null;
      this.pathEndingResult = null;
      this.swapExplorer = null;
      this.swapSelectedTransferKey = "";
      this.swapApplyResult = null;
      this.resetBridgeUi();
      this.resetTraceFetchUi(null);
    },

    selectedExampleKey() {
      this.saveUserSettings();
    },

    tool88NoteColumn() {
      if (this.caseBuddyEmbedded && this.tool88NoteColumn !== "W") {
        this.tool88NoteColumn = "W";
        return;
      }
      if (!this.caseBuddyEmbedded) this.saveUserSettings();
    },

    enableAdditionalServiceChecks() {
      this.saveUserSettings();
    },

    enableDepositEntityHopExtension() {
      this.saveUserSettings();
    },

    enableNativeDirectSpendMatchGuard() {
      this.saveUserSettings();
      this.traceResult = null;
      this.flowCompletionScanResult = null;
    },

    enableSessionDataCache(value) {
      this.saveUserSettings();
      if (value === false) {
        this.clearSessionDataCache({ silent: true });
      }
    },

    flowCompletionMaxDepth() {
      this.saveUserSettings();
      this.flowCompletionScanResult = null;
    },

    flowCompletionValueTooLowEnabled() {
      this.saveUserSettings();
      this.flowCompletionScanResult = null;
    },

    lowValueThresholdBtc() {
      this.saveUserSettings();
      this.flowCompletionScanResult = null;
    },

    lowValueThresholdNative() {
      this.saveUserSettings();
      this.flowCompletionScanResult = null;
    },

    lowValueThresholdStable() {
      this.saveUserSettings();
      this.flowCompletionScanResult = null;
    },

    lowValueThresholdOther() {
      this.saveUserSettings();
      this.flowCompletionScanResult = null;
    },

    flowCompletionTraceThroughEntitiesText() {
      this.saveUserSettings();
      this.flowCompletionScanResult = null;
    },

    directAutomationSpeedMultiplier(value) {
      const normalized = this.normalizeDirectAutomationSpeed(value);
      if (normalized !== value) {
        this.directAutomationSpeedMultiplier = normalized;
        return;
      }
      this.saveUserSettings();
    },

    selectedEntryKey() {
      if (this.isRefreshingSelections) {
        return;
      }
      this.targetCandidates = [];
      this.selectedTargetAddress = "";
      this.outputBaseRows = [];
      this.countLookupResult = null;
      this.attributionLookupResult = null;
      this.transactionSignalResult = null;
      this.traceResult = null;
      this.dormantResult = null;
      this.traceTransactionPayload = null;
      this.pathEndingResult = null;
      this.swapExplorer = null;
      this.swapSelectedTransferKey = "";
      this.swapApplyResult = null;
      this.resetBridgeUi();
      this.resetTraceFetchUi(null);
    },

    selectedTargetAddress() {
      if (this.isRefreshingSelections) {
        return;
      }
      this.countLookupResult = null;
      this.attributionLookupResult = null;
      this.transactionSignalResult = null;
      this.traceResult = null;
      this.dormantResult = null;
      this.traceTransactionPayload = null;
      this.pathEndingResult = null;
      this.swapExplorer = null;
      this.swapSelectedTransferKey = "";
      this.swapApplyResult = null;
      this.resetBridgeUi();
      this.resetTraceFetchUi(null);
    },
  },
  mounted() {
    this.clearAll();
    if (this.caseBuddyHeadless) {
      this.simpleViewOpen = false;
    } else if (this.caseBuddyEmbedded && this.caseBuddyStartInAdvancedView) {
      this.simpleViewOpen = false;
    }
    if (this.caseBuddyEmbedded && this.tool88NoteColumn !== "W") {
      this.tool88NoteColumn = "W";
    }
    this.setSimpleViewBodyLock(this.simpleViewOpen);
    if (this.caseBuddyEmbedded && this.simpleViewOpen) {
      this.scrollCaseBuddyEmbeddedSimpleViewToTop();
    }
  },
  beforeUnmount() {
    if (this.directAutomationRunState?.active) {
      this.requestDirectAutomationStop("Tool 88 component is closing.");
    }
    this.setSimpleViewBodyLock(false);
  },
  methods: {
    getSimpleFlowMiniRows() {
      if (this.simpleResultReadyForContinue && Array.isArray(this.outputSnapshotRows) && this.outputSnapshotRows.some((row) => this.rowHasAnyData(row))) {
        return this.trimTrailingBlankRows(this.outputSnapshotRows);
      }

      const simpleText = this.toText(this.simpleInputText);
      const simpleTextChanged = Boolean(simpleText && simpleText !== this.simpleLastLoadedInputText);
      if (simpleTextChanged) {
        try {
          const parsed = this.parseSimpleInputTextToRows(simpleText);
          if (parsed && Array.isArray(parsed.rows) && parsed.rows.length) {
            return this.trimTrailingBlankRows(parsed.rows);
          }
        } catch (error) {
          // Fall through to the current spreadsheet snapshot. The visual should never block Tool 88.
        }
      }

      if (Array.isArray(this.inputSnapshotRows) && this.inputSnapshotRows.some((row) => this.rowHasAnyData(row))) {
        return this.trimTrailingBlankRows(this.inputSnapshotRows);
      }

      const liveRows = this.$refs.inputSpreadsheet?.getData?.() || [];
      if (Array.isArray(liveRows) && liveRows.some((row) => this.rowHasAnyData(row))) {
        return this.trimTrailingBlankRows(liveRows);
      }

      if (simpleText) {
        try {
          const parsed = this.parseSimpleInputTextToRows(simpleText);
          if (parsed && Array.isArray(parsed.rows)) {
            return this.trimTrailingBlankRows(parsed.rows);
          }
        } catch (error) {
          return [];
        }
      }

      return [];
    },

    extractSimpleFlowHashReferences(value) {
      const text = this.toText(value);
      if (!text) return [];
      const found = new Set();
      const patterns = [
        /0x[a-fA-F0-9][a-fA-F0-9.…]{5,}/g,
        /\b[a-fA-F0-9]{24,64}\b/g,
      ];
      patterns.forEach((pattern) => {
        let match = pattern.exec(text);
        while (match) {
          const cleaned = this.toText(match[0]).replace(/[),;:"'\]]+$/g, "").trim();
          if (cleaned) found.add(cleaned);
          match = pattern.exec(text);
        }
      });
      return Array.from(found);
    },

    simpleFlowHashReferenceMatches(reference, candidateHash) {
      const ref = this.toText(reference).toLowerCase().replace(/\s+/g, "");
      const candidate = this.toText(candidateHash).toLowerCase().replace(/\s+/g, "");
      if (!ref || !candidate) return false;
      if (ref === candidate) return true;
      const normalizedRef = ref.replace(/…/g, "...");
      if (normalizedRef.includes("...")) {
        const [prefix, suffix] = normalizedRef.split("...");
        const prefixOk = !prefix || candidate.startsWith(prefix);
        const suffixOk = !suffix || candidate.endsWith(suffix);
        return prefixOk && suffixOk;
      }
      return candidate.startsWith(ref) || candidate.endsWith(ref);
    },

    findSimpleFlowEntryByHashReference(reference, entryInfos = []) {
      return (entryInfos || []).find((entry) => this.simpleFlowHashReferenceMatches(reference, entry.txHash)) || null;
    },

    inferSimpleFlowEntryStatus(entryInfo, endingByHash) {
      if (!entryInfo) return "idle";
      if (entryInfo.key === this.selectedEntryKey) return "active";
      const ending = endingByHash.get(this.normalizeHashKey(entryInfo.txHash));
      if (ending) return ending.complete ? "complete" : "open";
      const combined = `${entryInfo.continuesText}\n${entryInfo.notesText}`.toLowerCase();
      if (/tool\s*88\s*flow:\s*assessed|action=traced-through|action=path-ending|action=address-poisoning-ending|action=swap|action=bridge/.test(combined)) return "complete";
      if (/continues\s+as|branches\s+as/.test(combined)) return "branch";
      if (/path\s+ends\.?/.test(combined)) return "open";
      return "idle";
    },

    buildSimpleFlowMiniMap() {
      const rows = this.getSimpleFlowMiniRows();
      if (!rows.length) {
        return {
          summary: "No flow loaded yet",
          ariaLabel: "No flow data loaded yet",
          entryCount: 0,
          linkCount: 0,
          openLabel: "0 open",
          hiddenCount: 0,
          nodes: [],
          links: [],
        };
      }

      const headerDetected = this.isLikelyHeaderRow(rows[0]);
      const dataStartIndex = headerDetected ? 1 : 0;
      const { entries } = this.collectEntries(rows, dataStartIndex);
      const endingByHash = new Map();
      (this.flowCompletionResult?.endings || []).forEach((ending) => {
        const hashKey = this.normalizeHashKey(ending?.txHash);
        if (hashKey) endingByHash.set(hashKey, ending);
      });

      const entryInfos = entries.map((entry, index) => {
        const rowTexts = entry.rows.map(({ row }) => row || []);
        const continuesText = rowTexts.map((row) => this.toText(row[COL.CONTINUES_AS])).filter(Boolean).join("\n");
        const attributionColumnIndex = this.getAttributionColumnIndex();
        const notesText = rowTexts.map((row) => [row[COL.SYSTEM_NOTES], row[attributionColumnIndex], row[COL.TOOL88_NOTES]].map((cell) => this.toText(cell)).filter(Boolean).join(" ")).filter(Boolean).join("\n");
        const parentRefs = rowTexts.flatMap((row) => this.extractSimpleFlowHashReferences(row[COL.FROM_HASH]));
        const continuesRefs = this.extractSimpleFlowHashReferences(continuesText);
        return {
          key: entry.key,
          index,
          txHash: entry.txHash,
          pathLabel: entry.pathLabel,
          targetAddress: this.toText(rowTexts[0]?.[this.getTargetColumnIndex()]),
          parentRefs,
          continuesRefs,
          continuesText,
          notesText,
        };
      }).filter((entry) => entry.txHash);

      if (!entryInfos.length) {
        return {
          summary: "Input ready",
          ariaLabel: "Input rows are present, but no transaction entries were found yet",
          entryCount: 0,
          linkCount: 0,
          openLabel: "0 open",
          hiddenCount: 0,
          nodes: [],
          links: [],
        };
      }

      const parentByKey = new Map();
      const linkPairs = [];
      const addLink = (fromEntry, toEntry, reason) => {
        if (!fromEntry || !toEntry || fromEntry.key === toEntry.key) return;
        const key = `${fromEntry.key}->${toEntry.key}:${reason}`;
        if (linkPairs.some((link) => link.key === key)) return;
        linkPairs.push({ key, fromKey: fromEntry.key, toKey: toEntry.key, reason });
      };

      entryInfos.forEach((entry) => {
        for (const ref of entry.parentRefs) {
          const parent = this.findSimpleFlowEntryByHashReference(ref, entryInfos);
          if (parent && parent.key !== entry.key) {
            parentByKey.set(entry.key, parent.key);
            addLink(parent, entry, "from-hash");
            break;
          }
        }
      });

      entryInfos.forEach((entry) => {
        entry.continuesRefs.forEach((ref) => {
          const child = this.findSimpleFlowEntryByHashReference(ref, entryInfos);
          if (child && child.key !== entry.key) {
            if (!parentByKey.has(child.key)) parentByKey.set(child.key, entry.key);
            addLink(entry, child, "continues-as");
          }
        });
      });

      const depthByKey = new Map();
      const depthFor = (entry, seen = new Set()) => {
        if (!entry) return 0;
        if (depthByKey.has(entry.key)) return depthByKey.get(entry.key);
        if (seen.has(entry.key)) return 0;
        seen.add(entry.key);
        const parentKey = parentByKey.get(entry.key);
        const parent = entryInfos.find((candidate) => candidate.key === parentKey);
        const depth = parent ? depthFor(parent, seen) + 1 : 0;
        depthByKey.set(entry.key, depth);
        return depth;
      };
      entryInfos.forEach((entry) => depthFor(entry));

      const visibleLimit = 24;
      let visibleEntries = entryInfos.slice(0, visibleLimit);
      const selected = entryInfos.find((entry) => entry.key === this.selectedEntryKey);
      if (selected && !visibleEntries.some((entry) => entry.key === selected.key)) {
        visibleEntries = visibleEntries.slice(0, Math.max(0, visibleLimit - 1)).concat(selected);
      }
      const visibleKeys = new Set(visibleEntries.map((entry) => entry.key));
      const maxDepth = Math.max(0, ...visibleEntries.map((entry) => depthByKey.get(entry.key) || 0));
      const groupedByDepth = new Map();
      visibleEntries.forEach((entry) => {
        const depth = depthByKey.get(entry.key) || 0;
        if (!groupedByDepth.has(depth)) groupedByDepth.set(depth, []);
        groupedByDepth.get(depth).push(entry);
      });

      const flowIsComplete = Boolean(this.flowCompletionResult?.done);
      const nodes = visibleEntries.map((entry, visibleIndex) => {
        const depth = depthByKey.get(entry.key) || 0;
        const depthGroup = groupedByDepth.get(depth) || [entry];
        const withinDepthIndex = depthGroup.findIndex((candidate) => candidate.key === entry.key);
        const x = maxDepth > 0
          ? 30 + (depth * (460 / Math.max(1, maxDepth)))
          : 30 + (visibleIndex * (460 / Math.max(1, visibleEntries.length - 1)));
        const laneCount = Math.min(5, Math.max(1, depthGroup.length));
        const lane = depthGroup.length > 5 ? (withinDepthIndex % 5) : withinDepthIndex;
        const y = 25 + ((lane + 0.5) * (66 / laneCount));
        const status = flowIsComplete ? "complete" : this.inferSimpleFlowEntryStatus(entry, endingByHash);
        const label = entry.pathLabel ? String(entry.pathLabel).slice(0, 3) : String(entry.index + 1);
        return {
          key: entry.key,
          txHash: entry.txHash,
          x: Number(x.toFixed(2)),
          y: Number(y.toFixed(2)),
          r: Math.max(7, Math.min(12, 12 - Math.floor(visibleEntries.length / 10))),
          label,
          status,
          active: !flowIsComplete && entry.key === this.selectedEntryKey,
          title: `${entry.pathLabel ? `Path ${entry.pathLabel} / ` : ""}${this.shortenMiddle(entry.txHash, 12, 10)}${entry.targetAddress ? ` → ${this.shortenMiddle(entry.targetAddress, 8, 6)}` : ""}`,
        };
      });
      const nodeByKey = new Map(nodes.map((node) => [node.key, node]));
      const links = linkPairs
        .filter((link) => visibleKeys.has(link.fromKey) && visibleKeys.has(link.toKey))
        .slice(0, 42)
        .map((link) => {
          const from = nodeByKey.get(link.fromKey);
          const to = nodeByKey.get(link.toKey);
          const curve = Math.max(12, Math.abs(to.y - from.y) * 0.32);
          const midX = (from.x + to.x) / 2;
          return {
            ...link,
            path: `M ${from.x} ${from.y} C ${midX} ${from.y - curve}, ${midX} ${to.y + curve}, ${to.x} ${to.y}`,
          };
        });

      const branchCount = entryInfos.filter((entry) => entry.continuesRefs.length > 1).length;
      const incompleteCount = Number(this.flowCompletionResult?.incompleteCount);
      const estimatedOpen = Number.isFinite(incompleteCount)
        ? incompleteCount
        : entryInfos.filter((entry) => this.inferSimpleFlowEntryStatus(entry, endingByHash) === "open").length;
      const componentCount = entryInfos.filter((entry) => !parentByKey.has(entry.key)).length;
      const summaryParts = [`${this.formatInteger(entryInfos.length)} entr${entryInfos.length === 1 ? "y" : "ies"}`];
      if (branchCount) summaryParts.push(`${this.formatInteger(branchCount)} branch${branchCount === 1 ? "" : "es"}`);
      if (componentCount > 1) summaryParts.push(`${this.formatInteger(componentCount)} clusters`);
      if (this.flowCompletionResult?.done) summaryParts.push("complete");

      return {
        summary: summaryParts.join(" · "),
        ariaLabel: `Compact flow map with ${entryInfos.length} entries, ${links.length} visible links, and ${estimatedOpen} open endings`,
        entryCount: entryInfos.length,
        linkCount: linkPairs.length,
        openLabel: this.flowCompletionResult?.done ? "complete" : `${this.formatInteger(Math.max(0, estimatedOpen))} open`,
        hiddenCount: Math.max(0, entryInfos.length - visibleEntries.length),
        nodes,
        links,
      };
    },

    setSimpleViewBodyLock(value) {
      if (this.caseBuddyEmbedded) {
        this.simpleViewPreviousBodyOverflow = "";
        return;
      }
      if (typeof document === "undefined" || !document.body) {
        return;
      }
      if (value) {
        if (!document.body.classList.contains("tool88-simple-view-open")) {
          this.simpleViewPreviousBodyOverflow = document.body.style.overflow || "";
        }
        document.body.classList.add("tool88-simple-view-open");
        document.body.style.overflow = "hidden";
      } else {
        document.body.classList.remove("tool88-simple-view-open");
        document.body.style.overflow = this.simpleViewPreviousBodyOverflow || "";
      }
    },

    openSimpleView() {
      this.simpleViewOpen = true;
      this.setSimpleViewBodyLock(true);
      this.scrollCaseBuddyEmbeddedSimpleViewToTop();
    },

    closeSimpleView() {
      this.simpleViewOpen = false;
      this.setSimpleViewBodyLock(false);
    },

    scrollCaseBuddyEmbeddedSimpleViewToTop() {
      if (!this.caseBuddyEmbedded) return;
      const scrollToTop = () => {
        const root = this.$el;
        const modal = root?.querySelector?.('.simple-view-modal');
        const left = root?.querySelector?.('.simple-view-left');
        const right = root?.querySelector?.('.simple-view-right');
        [modal, left, right].forEach((element) => {
          if (element && typeof element.scrollTo === 'function') element.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          else if (element) element.scrollTop = 0;
        });
      };
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(scrollToTop);
        return;
      }
      Promise.resolve().then(scrollToTop);
    },

    clearSimpleInput() {
      this.simpleInputText = "";
      this.simpleLastLoadedInputText = "";
      this.simpleViewMessage = "Simple input cleared. Existing spreadsheet data was not changed.";
    },

    simpleNormalizeDelimitedCell(value) {
      return String(value ?? "")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .replace(/^\uFEFF/, "")
        .trim();
    },

    simpleDetectInputDelimiter(text) {
      const source = String(text ?? "");
      let tabCount = 0;
      let commaCount = 0;
      let inQuotes = false;

      for (let index = 0; index < source.length; index += 1) {
        const char = source[index];
        if (char === '"') {
          if (inQuotes && source[index + 1] === '"') {
            index += 1;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (!inQuotes && char === "\t") {
          tabCount += 1;
        } else if (!inQuotes && char === ",") {
          commaCount += 1;
        }
      }

      if (tabCount > 0) return "\t";
      if (commaCount > 0) return ",";
      return "";
    },

    simpleParseDelimitedText(text, delimiter = "\t") {
      const source = String(text ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      if (!source.trim()) return [];

      if (!delimiter) {
        return source
          .split(/\n+/)
          .map((line) => [this.simpleNormalizeDelimitedCell(line)])
          .filter((row) => row.some((value) => this.toText(value)));
      }

      const rows = [];
      let row = [];
      let cell = "";
      let inQuotes = false;
      let cellStarted = false;

      const pushCell = () => {
        row.push(this.simpleNormalizeDelimitedCell(cell));
        cell = "";
        cellStarted = false;
      };

      const pushRow = () => {
        pushCell();
        if (row.some((value) => this.toText(value))) {
          rows.push(row);
        }
        row = [];
      };

      for (let index = 0; index < source.length; index += 1) {
        const char = source[index];
        const next = source[index + 1];

        if (char === '"') {
          if (inQuotes && next === '"') {
            cell += '"';
            index += 1;
            cellStarted = true;
          } else if (!inQuotes && !cellStarted && cell.trim() === "") {
            inQuotes = true;
            cellStarted = true;
          } else if (inQuotes) {
            inQuotes = false;
            cellStarted = true;
          } else {
            cell += char;
            cellStarted = true;
          }
          continue;
        }

        if (char === delimiter && !inQuotes) {
          pushCell();
          continue;
        }

        if (char === "\n" && !inQuotes) {
          pushRow();
          continue;
        }

        cell += char;
        if (char !== " " && char !== "\t") {
          cellStarted = true;
        }
      }

      if (cell || row.length || cellStarted) {
        pushRow();
      }

      return rows;
    },

    simpleParseInputRows(text) {
      const source = String(text ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      if (!source.trim()) return [];
      const delimiter = this.simpleDetectInputDelimiter(source);
      if (delimiter) {
        return this.simpleParseDelimitedText(source, delimiter);
      }
      return this.simpleParseDelimitedText(source, "");
    },

    simpleSplitPastedLine(line) {
      const delimiter = this.simpleDetectInputDelimiter(line) || ",";
      return this.simpleParseDelimitedText(line, delimiter)[0] || [];
    },

    isPotentialTransactionHash(value) {
      const text = this.toText(value);
      return /^0x[a-fA-F0-9]{20,80}$/.test(text) || /^[a-fA-F0-9]{64}$/.test(text);
    },

    findFirstTransactionHashInCells(cells = []) {
      return (cells || []).map((cell) => this.toText(cell)).find((cell) => this.isPotentialTransactionHash(cell)) || "";
    },

    findFirstAddressInCells(cells = []) {
      return (cells || []).map((cell) => this.toText(cell)).find((cell) => this.isValidBlockchainAddress(cell)) || "";
    },

    findSimpleDateLikeCell(cells = []) {
      return (cells || []).map((cell) => this.toText(cell)).find((cell) => {
        if (!cell) return false;
        return /\d{4}-\d{1,2}-\d{1,2}/.test(cell) || /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(cell);
      }) || "";
    },

    findSimpleAmountAndToken(cells = []) {
      const ignored = new Set();
      cells.forEach((cell, index) => {
        if (this.isPotentialTransactionHash(cell) || this.isValidBlockchainAddress(cell)) ignored.add(index);
      });
      let amount = "";
      let token = "";
      for (let index = 0; index < cells.length; index += 1) {
        if (ignored.has(index)) continue;
        const cell = this.toText(cells[index]);
        if (!cell) continue;
        const parsedAmount = this.parseNumberLike(cell);
        const parsedToken = this.parseTokenLike(cell).replace(/[,:;]+$/g, "").trim();
        if (!amount && parsedAmount !== null) amount = this.formatAmount(parsedAmount);
        if (!token && parsedToken && /[A-Za-z]/.test(parsedToken) && !/date|time|hash|address/i.test(parsedToken)) token = parsedToken.toUpperCase();
        if (amount && token) break;
      }
      return { amount, token };
    },

    extractTransactionHashesFromText(value) {
      const text = this.toText(value);
      if (!text) return [];
      const regex = /(^|[^a-fA-F0-9])(0x[a-fA-F0-9]{64}|[a-fA-F0-9]{64})(?=$|[^a-fA-F0-9])/g;
      const hashes = [];
      const seen = new Set();
      let match = regex.exec(text);
      while (match) {
        const hash = this.toText(match[2]);
        const key = this.normalizeHashKey(hash);
        if (hash && !seen.has(key)) {
          seen.add(key);
          hashes.push(hash);
        }
        match = regex.exec(text);
      }
      return hashes;
    },

    simpleInputLooksLikeFullCaseBuddyRows(cellRows = []) {
      return (cellRows || []).some((row) => {
        if (!Array.isArray(row)) return false;
        const nonBlankCells = row.map((cell) => this.toText(cell)).filter(Boolean);
        if (!nonBlankCells.length) return false;
        const hashCount = nonBlankCells.filter((cell) => this.isPotentialTransactionHash(cell)).length;
        const addressCount = nonBlankCells.filter((cell) => this.isValidBlockchainAddress(cell)).length;
        const onlyLooseHashesOrAddresses = hashCount > 0 && nonBlankCells.every((cell) => this.isPotentialTransactionHash(cell) || this.isValidBlockchainAddress(cell));
        const hasColumnHash = this.isPotentialTransactionHash(row[COL.TX_HASH]);
        const hasFromOrTo = this.isValidBlockchainAddress(row[COL.FROM_ADDRESS]) || this.isValidBlockchainAddress(row[COL.TO_ADDRESS]);

        if (this.isLikelyHeaderRow(row)) return true;
        if (hasColumnHash && hasFromOrTo) return true;
        if (this.toText(row[COL.FUNDS_FOLLOWED]) || this.toText(row[COL.LEFT_BOUNDS_DISPLAY]) || this.toText(row[COL.CONTINUES_AS])) return true;
        if (onlyLooseHashesOrAddresses) return false;
        if (row.length >= COLUMN_COUNT) return true;
        if (row.length >= 8 && (hasColumnHash || hasFromOrTo || addressCount > 0)) return true;
        return false;
      });
    },

    buildSimpleInputStageKey(txHash, index) {
      const hashKey = this.normalizeHashKey(txHash).replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      return `simple-stage-${index}-${hashKey || "tx"}`;
    },

    buildSimpleInputStagedRowsFromCellRows(cellRows = []) {
      const staged = [];
      (cellRows || []).forEach((cells, lineIndex) => {
        const safeCells = Array.isArray(cells) ? cells : [];
        const lineText = safeCells.map((cell) => this.toText(cell)).filter(Boolean).join(" ");
        const txHashes = this.extractTransactionHashesFromText(lineText);
        if (!txHashes.length) return;

        const preferredAddress = this.isValidBlockchainAddress(safeCells[COL.TO_ADDRESS])
          ? this.toText(safeCells[COL.TO_ADDRESS])
          : (this.isValidBlockchainAddress(safeCells[COL.FROM_ADDRESS]) ? this.toText(safeCells[COL.FROM_ADDRESS]) : "");
        const addresses = safeCells
          .map((cell) => this.toText(cell))
          .filter((cell) => this.isValidBlockchainAddress(cell));
        const orderedAddresses = Array.from(new Set([preferredAddress, ...addresses].filter(Boolean)));
        const { amount, token } = this.findSimpleAmountAndToken(safeCells);
        const date = this.findSimpleDateLikeCell(safeCells);

        txHashes.forEach((txHash, hashIndex) => {
          const key = this.buildSimpleInputStageKey(txHash, staged.length);
          const address = orderedAddresses[hashIndex] || orderedAddresses[0] || "";
          staged.push({
            key,
            txHash,
            address,
            lineIndex,
            hashIndex,
            amount,
            token,
            date,
            cells: safeCells.slice(),
          });
        });
      });
      return staged;
    },

    buildSimpleInputStagedRows(text) {
      const source = this.toText(text);
      if (!source) return [];
      const parsedRows = this.simpleParseInputRows(source);
      if (!parsedRows.length) return [];
      const cellRows = this.simpleRepairCaseBuddyMultilineRows(parsedRows);
      if (this.simpleInputLooksLikeFullCaseBuddyRows(cellRows)) return [];
      return this.buildSimpleInputStagedRowsFromCellRows(cellRows).slice(0, 250);
    },

    getSimpleStagedAddressOverride(stageRow = {}) {
      const direct = this.toText(this.simpleStagedAddressOverrides?.[stageRow.key]);
      if (direct) return direct;
      const hashKey = this.normalizeHashKey(stageRow.txHash || "");
      return this.toText(this.simpleStagedAddressOverrides?.[hashKey]);
    },

    getSimpleStagedRowStatus(stageRow = {}) {
      const override = this.getSimpleStagedAddressOverride(stageRow);
      if (override) {
        return this.isValidBlockchainAddress(override) ? "Target supplied" : "Invalid target override";
      }
      if (stageRow.address) return "Target supplied";
      if (/^0x[a-fA-F0-9]{64}$/.test(this.toText(stageRow.txHash))) return "Auto-infer recipient";
      return "Needs target address";
    },

    simpleRepairCaseBuddyMultilineRows(cellRows = []) {
      const repaired = [];
      const continuationMarkers = /(?:this transaction follows|details:\s*\(|continues as:|branches as:|left bounds after|a portion of the funds remains|tool 88|path ends\.?)/i;

      (cellRows || []).forEach((rawRow) => {
        const row = Array.isArray(rawRow) ? rawRow.slice() : [];
        const previous = repaired[repaired.length - 1];
        const firstCell = this.toText(row[0]);
        const hasTransactionHash = this.isPotentialTransactionHash(row[COL.TX_HASH]);
        const hasNormalAddressColumns = this.isValidBlockchainAddress(row[COL.FROM_ADDRESS]) || this.isValidBlockchainAddress(row[COL.TO_ADDRESS]);
        const previousLooksLikeCaseBuddyRow = previous && (this.isPotentialTransactionHash(previous[COL.TX_HASH]) || previous.length >= COL.LEFT_BOUNDS_DISPLAY + 1);
        const previousLeftBoundsLooksOpen = previousLooksLikeCaseBuddyRow && (
          previous.length <= COL.LEFT_BOUNDS_DISPLAY + 1 ||
          this.toText(previous[COL.LEFT_BOUNDS_DISPLAY]) !== ""
        );
        const looksLikeSplitLeftBoundsContinuation = Boolean(
          previous &&
          !hasTransactionHash &&
          !hasNormalAddressColumns &&
          firstCell &&
          previousLeftBoundsLooksOpen &&
          (continuationMarkers.test(firstCell) || previous.length <= COL.LEFT_BOUNDS_DISPLAY + 1)
        );

        if (looksLikeSplitLeftBoundsContinuation) {
          const existingLeftBounds = this.toText(previous[COL.LEFT_BOUNDS_DISPLAY]);
          previous[COL.LEFT_BOUNDS_DISPLAY] = existingLeftBounds ? `${existingLeftBounds}\n${firstCell}` : firstCell;
          for (let index = 1; index < row.length; index += 1) {
            const destinationIndex = COL.LEFT_BOUNDS_DISPLAY + index;
            if (destinationIndex >= COLUMN_COUNT) break;
            if (!this.toText(previous[destinationIndex]) && this.toText(row[index])) {
              previous[destinationIndex] = row[index];
            }
          }
          return;
        }

        repaired.push(row);
      });

      return repaired;
    },

    shouldTreatSimpleRowsAsCaseBuddyRows(cellRows = []) {
      return this.simpleInputLooksLikeFullCaseBuddyRows(cellRows);
    },

    buildSimpleInputRowFromStagedRow(stageRow = {}, index = 0) {
      const row = this.makeBlankRow();
      const txHash = this.toText(stageRow.txHash);
      const overrideAddress = this.getSimpleStagedAddressOverride(stageRow);
      const address = this.toText(overrideAddress || stageRow.address);
      const amount = this.toText(stageRow.amount);
      const token = this.toText(stageRow.token).toUpperCase();

      row[COL.PATH_LABEL] = String(index + 1);
      row[COL.TX_HASH] = txHash;
      if (address && this.isValidBlockchainAddress(address)) {
        if (this.traceDirection === "reverse") row[COL.FROM_ADDRESS] = address;
        else row[COL.TO_ADDRESS] = address;
      }
      row[COL.DATE] = this.toText(stageRow.date);
      row[COL.AMOUNT] = amount;
      row[COL.TOKEN] = token;
      row[COL.SYSTEM_NOTES] = address
        ? "Loaded from Tool 88 Simple View staged input."
        : "Loaded from Tool 88 Simple View staged input; Tool 88 will try to infer the clear recipient from provider transaction details.";
      if (amount || token) row[COL.FUNDS_FOLLOWED] = `${amount}${token ? ` ${token}` : ""}`.trim();
      row[COL.CONTINUES_AS] = "Path ends.";
      row[COL.LEFT_BOUNDS_DISPLAY] = "0";
      row[COL.LEFT_BOUNDS_NUMERIC] = "0";
      if (/^0x[a-fA-F0-9]{64}$/.test(txHash)) {
        row[COL.CHAIN_ID] = "1";
        row[COL.CHAIN_NAME] = "Ethereum";
      }
      row[COL.HOP_LOGIC] = this.hopLogicType;
      return row;
    },

    parseSimpleInputTextToRows(text) {
      const parsedRows = this.simpleParseInputRows(text);
      if (!parsedRows.length) return { rows: [], mode: "empty", skipped: 0 };
      const cellRows = this.simpleRepairCaseBuddyMultilineRows(parsedRows);
      const repairedCount = Math.max(0, parsedRows.length - cellRows.length);
      if (this.shouldTreatSimpleRowsAsCaseBuddyRows(cellRows)) {
        return {
          rows: cellRows.map((row) => this.cloneRowPreservingValues(row.slice(0, COLUMN_COUNT))),
          mode: "casebuddy-tsv",
          skipped: 0,
          repaired: repairedCount,
        };
      }

      const stagedRows = this.buildSimpleInputStagedRowsFromCellRows(cellRows);
      if (stagedRows.length) {
        const rows = [this.cloneRowPreservingValues(HEADER_ROW)];
        let skipped = 0;
        stagedRows.forEach((stageRow, index) => {
          const overrideAddress = this.getSimpleStagedAddressOverride(stageRow);
          if (overrideAddress && !this.isValidBlockchainAddress(overrideAddress)) {
            skipped += 1;
            return;
          }
          rows.push(this.buildSimpleInputRowFromStagedRow(stageRow, index));
        });
        return { rows, mode: "staged-hash-list", skipped, repaired: repairedCount };
      }

      return { rows: [], mode: "empty", skipped: cellRows.length, repaired: repairedCount };
    },

    normalizeLooseHashOnlyInputRows(rows = []) {
      let changed = false;
      const output = (rows || []).map((sourceRow, rowIndex) => {
        const row = this.cloneRowPreservingValues(sourceRow);
        const firstCell = this.toText(row[COL.PATH_LABEL]);
        const hasTxHash = this.toText(row[COL.TX_HASH]);
        const nonBlankCells = row.filter((cell) => this.toText(cell));
        if (!hasTxHash && firstCell && this.isPotentialTransactionHash(firstCell) && nonBlankCells.length === 1) {
          const txHash = firstCell;
          const normalized = this.makeBlankRow();
          normalized[COL.PATH_LABEL] = String(rowIndex + 1);
          normalized[COL.TX_HASH] = txHash;
          normalized[COL.SYSTEM_NOTES] = "Loaded from a loose transaction-hash-only spreadsheet row; Tool 88 will try to infer the clear recipient from provider transaction details.";
          normalized[COL.CONTINUES_AS] = "Path ends.";
          normalized[COL.LEFT_BOUNDS_DISPLAY] = "0";
          normalized[COL.LEFT_BOUNDS_NUMERIC] = "0";
          if (/^0x[a-fA-F0-9]{64}$/.test(txHash)) {
            normalized[COL.CHAIN_ID] = "1";
            normalized[COL.CHAIN_NAME] = "Ethereum";
          }
          normalized[COL.HOP_LOGIC] = this.hopLogicType;
          changed = true;
          return normalized;
        }
        return row;
      });
      return { rows: output, changed };
    },

    shouldInferTransactionDetailsForRow(row = []) {
      const txHash = this.toText(row[COL.TX_HASH]);
      if (!txHash || !/^0x[a-fA-F0-9]{64}$/.test(txHash)) return false;
      const targetAddress = this.toText(row[this.getTargetColumnIndex()]);
      const hasBothAddresses = Boolean(this.toText(row[COL.FROM_ADDRESS]) && this.toText(row[COL.TO_ADDRESS]));
      const hasAmountAndToken = Boolean(this.toText(row[COL.AMOUNT]) && this.toText(row[COL.TOKEN]));
      const hasDate = Boolean(this.toText(row[COL.DATE]));
      const hasFundsFollowed = Boolean(this.toText(row[COL.FUNDS_FOLLOWED]));
      const hasContinuesAs = Boolean(this.toText(row[COL.CONTINUES_AS]));
      const hasLeftBounds = Boolean(this.toText(row[COL.LEFT_BOUNDS_DISPLAY]) || this.toText(row[COL.LEFT_BOUNDS_NUMERIC]));
      return !targetAddress || !hasBothAddresses || !hasDate || !hasAmountAndToken || !hasFundsFollowed || !hasContinuesAs || !hasLeftBounds;
    },

    async lookupTransactionBundleForInputInference(row = []) {
      const txHash = this.toText(row[COL.TX_HASH]);
      const chainId = this.toText(row[COL.CHAIN_ID]) || "1";
      const chainName = this.toText(row[COL.CHAIN_NAME]) || "Ethereum";
      const response = await this.postTool88({
        action: "transaction_explorer_bundle",
        tx_hash: txHash,
        transaction_hash: txHash,
        chain_id: chainId,
        chain_name: chainName,
        network_family: "evm",
        native_symbol: this.toText(row[COL.TOKEN]) || "ETH",
        sample_mode: this.isSampleDataLoaded ? 1 : 0,
      });
      return response.result || response;
    },

    normalizeInputInferenceCandidate(rawRow = {}, kind = "main", bundle = {}) {
      if (!rawRow || typeof rawRow !== "object") return null;
      const fromAddress = this.toText(rawRow.from_address || rawRow.from || rawRow.sender || rawRow.ownerAddress || "");
      const toAddress = this.toText(rawRow.to_address || rawRow.to || rawRow.receiver || rawRow.toAddress || "");
      if (!fromAddress || !toAddress) return null;
      const amountText = this.toText(rawRow.amount_formatted || rawRow.amountFormatted || rawRow.value_formatted || rawRow.valueFormatted || rawRow.amount || rawRow.value || "");
      const amount = this.parseNumberLike(amountText);
      if (!Number.isFinite(amount) || amount <= 0) return null;
      const tokenInfo = rawRow.tokenInfo || rawRow.token_info || {};
      const tokenSymbol = this.toText(rawRow.token_symbol || rawRow.tokenSymbol || rawRow.token || rawRow.symbol || tokenInfo.tokenAbbr || tokenInfo.symbol || (kind === "main" || kind === "internal" ? "ETH" : "TOKEN")).toUpperCase();
      const bundleMainRows = Array.isArray(bundle.main_transactions)
        ? bundle.main_transactions
        : (Array.isArray(bundle.mainTransactions)
            ? bundle.mainTransactions
            : (Array.isArray(bundle.main) ? bundle.main : []));
      const bundleMainRow = bundleMainRows[0]
        || bundle.main_transaction
        || bundle.mainTransaction
        || bundle.transaction
        || bundle.tx
        || {};
      const rawDate = rawRow.timeStamp
        || rawRow.timestamp
        || rawRow.block_ts
        || rawRow.block_timestamp
        || rawRow.date
        || bundle.timeStamp
        || bundle.timestamp
        || bundle.block_ts
        || bundle.block_timestamp
        || bundle.date
        || bundleMainRow.timeStamp
        || bundleMainRow.timestamp
        || bundleMainRow.block_ts
        || bundleMainRow.block_timestamp
        || bundleMainRow.date
        || "";
      const date = this.parseProviderDate(rawDate);
      return {
        kind,
        txHash: this.toText(rawRow.hash || rawRow.transaction_hash || rawRow.tx_hash || bundle.tx_hash || bundle.transaction_hash || ""),
        fromAddress,
        toAddress,
        amount,
        amountText: this.formatAmount(amount),
        tokenSymbol,
        dateText: date ? this.formatUtcDate(date) : this.toText(rawDate),
        chainId: this.toText(bundle.chain_id || rawRow.chain_id || rawRow.chainId || "1"),
        chainName: this.toText(bundle.chain_name || bundle.chainName || rawRow.chain_name || rawRow.chainName || "Ethereum"),
        sourceLabel: kind === "erc20" ? "single ERC20 Transfer log" : (kind === "internal" ? "single internal native transfer" : "native transaction value"),
        raw: rawRow,
      };
    },

    extractInputInferenceCandidatesFromBundle(bundle = {}) {
      const asArray = (value) => Array.isArray(value) ? value : (value && typeof value === "object" ? [value] : []);
      const mainRows = asArray(bundle.main_transactions || bundle.mainTransactions || bundle.main || bundle.main_transaction);
      const tokenRows = asArray(bundle.token_transfers || bundle.tokenTransfers || bundle.erc20 || bundle.trc20);
      const internalRows = asArray(bundle.internal_transactions || bundle.internalTransactions || bundle.internal);
      const logs = asArray(bundle.logs || bundle.transfer_logs || bundle.transferLogs);

      const mainCandidates = mainRows
        .map((row) => this.normalizeInputInferenceCandidate(row, "main", bundle))
        .filter(Boolean);
      const tokenCandidates = tokenRows
        .map((row) => this.normalizeInputInferenceCandidate(row, "erc20", bundle))
        .filter(Boolean);
      const internalCandidates = internalRows
        .map((row) => this.normalizeInputInferenceCandidate(row, "internal", bundle))
        .filter(Boolean);

      if (tokenCandidates.length === 1 && mainCandidates.length === 0) return tokenCandidates;
      if (tokenCandidates.length === 1 && mainCandidates.every((candidate) => !Number.isFinite(candidate.amount) || candidate.amount <= 0)) return tokenCandidates;
      if (tokenCandidates.length === 1 && logs.length === 1) return tokenCandidates;
      if (mainCandidates.length === 1 && tokenCandidates.length === 0 && internalCandidates.length === 0 && logs.length === 0) return mainCandidates;
      if (internalCandidates.length === 1 && tokenCandidates.length === 0 && mainCandidates.length === 0) return internalCandidates;
      return [...tokenCandidates, ...mainCandidates, ...internalCandidates];
    },

    chooseInputInferenceCandidate(candidates = [], row = []) {
      const viable = (Array.isArray(candidates) ? candidates : []).filter(Boolean);
      if (!viable.length) return null;
      const targetKey = this.normalizeAddressKey(row[this.getTargetColumnIndex()]);
      if (targetKey) {
        const matched = viable.find((candidate) => {
          const candidateTarget = this.traceDirection === "reverse" ? candidate.fromAddress : candidate.toAddress;
          return this.normalizeAddressKey(candidateTarget) === targetKey;
        });
        if (matched) return matched;
      }
      if (viable.length === 1) return viable[0];
      return null;
    },

    caseBuddyLineIsTransferNarrative(line = '') {
      const text = this.toText(line).replace(/\s+/g, ' ').trim();
      return /^Funds\s+move\s+from\b.+?\s+into\b.+?\.?$/i.test(text);
    },

    caseBuddyPathLabelLooksLikeRootBase(value = '') {
      const label = this.toText(value).trim();
      if (!label) return true;
      if (label.includes('->') || label.includes('→') || label.includes('.') || /\s/.test(label)) return false;
      if (/^\d+$/.test(label)) return true;
      if (/^[A-Z]+$/.test(label)) return true;
      if (/^[A-Za-z0-9_:/.-]+-\d+$/.test(label)) return true;
      return false;
    },

    caseBuddyExtractHashTokensFromContinuesAs(value = '') {
      const text = this.toText(value);
      if (!text || /path\s*ends/i.test(text)) return [];
      return text
        .replace(/^\s*(?:continues\s+as|branches\s+as)\s*/i, '')
        .split(/[,;\n]+/)
        .map(part => this.toText(part).replace(/^\s*(?:continues\s+as|branches\s+as)\s*/i, '').replace(/[.。]+$/g, '').trim())
        .filter(part => this.isTool88LikelyTransactionHash(part) || this.isPotentialTransactionHash(part));
    },

    parseCaseBuddyEntriesFromRows(rows = []) {
      const entries = [];
      let current = null;
      (Array.isArray(rows) ? rows : []).forEach((sourceRow, rowIndex) => {
        const row = this.cloneRowPreservingValues(sourceRow || []);
        if (rowIndex === 0 && this.isCaseBuddySnapshotHeaderRow(row)) return;
        if (!row.some(value => this.toText(value))) return;
        if (this.isCaseBuddySnapshotHeaderRow(row)) {
          current = null;
          return;
        }
        const hash = this.toText(row[COL.TX_HASH]);
        if (hash) {
          current = { hash, startRow: rowIndex, rows: [{ rowIndex, row }] };
          entries.push(current);
          return;
        }
        if (current) current.rows.push({ rowIndex, row });
      });
      return entries;
    },

    caseBuddyEntryLeadRow(entry = {}) {
      return Array.isArray(entry?.rows) && entry.rows.length ? (entry.rows[0]?.row || []) : [];
    },

    caseBuddyEntryHash(entry = {}) {
      return this.toText(entry?.hash || this.caseBuddyEntryLeadRow(entry)?.[COL.TX_HASH]);
    },

    caseBuddyBuildNarrativeRelationshipIndex(entries = []) {
      const byHash = new Map();
      const parentsByChildHash = new Map();
      const rememberParent = (childHash, parentHash) => {
        const childKey = this.normalizeHashKey(childHash);
        const parentKey = this.normalizeHashKey(parentHash);
        if (!childKey || !parentKey || childKey === parentKey) return;
        if (!parentsByChildHash.has(childKey)) parentsByChildHash.set(childKey, new Set());
        parentsByChildHash.get(childKey).add(parentKey);
      };
      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        const hashKey = this.normalizeHashKey(this.caseBuddyEntryHash(entry));
        if (hashKey && !byHash.has(hashKey)) byHash.set(hashKey, entry);
      });
      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        const parentHash = this.caseBuddyEntryHash(entry);
        (entry.rows || []).forEach((ref) => {
          const row = ref?.row || [];
          const fromHash = this.toText(row[COL.FROM_HASH]);
          if (fromHash) rememberParent(parentHash, fromHash);
          this.caseBuddyExtractHashTokensFromContinuesAs(row[COL.CONTINUES_AS]).forEach(childHash => rememberParent(childHash, parentHash));
        });
      });
      return { byHash, parentsByChildHash };
    },

    caseBuddyFindEntryForRowIndex(entries = [], rowIndex = -1) {
      if (!Number.isInteger(rowIndex)) return null;
      return (Array.isArray(entries) ? entries : []).find(entry => (entry.rows || []).some(ref => ref?.rowIndex === rowIndex)) || null;
    },

    caseBuddyRowShouldGenerateTransferNarrative(row = [], options = {}) {
      const rows = Array.isArray(options.rows) ? options.rows : [];
      const entries = Array.isArray(options.entries) ? options.entries : this.parseCaseBuddyEntriesFromRows(rows);
      const rowIndex = Number.isInteger(options.rowIndex) ? options.rowIndex : -1;
      const entry = options.entry || this.caseBuddyFindEntryForRowIndex(entries, rowIndex) || (this.toText(row[COL.TX_HASH]) ? { hash: row[COL.TX_HASH], startRow: rowIndex >= 0 ? rowIndex : 1, rows: [{ rowIndex: rowIndex >= 0 ? rowIndex : 1, row }] } : null);
      if (!entry) return false;
      if (Number.isInteger(rowIndex) && Number(entry.startRow) !== rowIndex) return false;
      const index = this.caseBuddyBuildNarrativeRelationshipIndex(entries.length ? entries : [entry]);
      const hashKey = this.normalizeHashKey(this.caseBuddyEntryHash(entry));
      if (hashKey && index.parentsByChildHash.has(hashKey)) return false;
      if ((entry.rows || []).some(ref => this.toText(ref?.row?.[COL.FROM_HASH]))) return false;
      return this.caseBuddyPathLabelLooksLikeRootBase(this.caseBuddyEntryLeadRow(entry)?.[COL.PATH_LABEL]);
    },

    stripCaseBuddyTransferNarrativeFromDescendantNotes(notes = '', row = [], options = {}) {
      const allow = this.caseBuddyRowShouldGenerateTransferNarrative(row, options);
      return this.appendUniqueNote('', notes)
        .split(/\r?\n/u)
        .map(line => line.trim())
        .filter(Boolean)
        .filter(line => allow || !this.caseBuddyLineIsTransferNarrative(line))
        .join('\n');
    },

    buildCaseBuddyRootTransferNarrativeNote(row = [], candidate = {}, options = {}) {
      const normalized = this.cloneRowPreservingValues(row || []);
      if (!this.caseBuddyRowShouldGenerateTransferNarrative(normalized, options)) return '';
      const fromAddress = this.toText(candidate.fromAddress || normalized[COL.FROM_ADDRESS]);
      const toAddress = this.toText(candidate.toAddress || normalized[COL.TO_ADDRESS]);
      if (!fromAddress && !toAddress) return '';
      return `Funds move from ${this.shortenMiddle(fromAddress, 8, 6) || 'an address'} into ${this.shortenMiddle(toAddress, 8, 6) || 'an address'}.`;
    },

    applyInputInferenceCandidateToRow(row = [], candidate = null, options = {}) {
      const output = this.cloneRowPreservingValues(row);
      if (!candidate) return output;
      output[COL.FROM_ADDRESS] = candidate.fromAddress || output[COL.FROM_ADDRESS];
      output[COL.TO_ADDRESS] = candidate.toAddress || output[COL.TO_ADDRESS];
      output[COL.DATE] = output[COL.DATE] || candidate.dateText;
      output[COL.AMOUNT] = output[COL.AMOUNT] || candidate.amountText;
      output[COL.TOKEN] = output[COL.TOKEN] || candidate.tokenSymbol;
      output[COL.SYSTEM_NOTES] = this.stripCaseBuddyTransferNarrativeFromDescendantNotes(output[COL.SYSTEM_NOTES], output, options);
      if (!this.toText(output[COL.SYSTEM_NOTES])) {
        const narrative = this.buildCaseBuddyRootTransferNarrativeNote(output, candidate, options);
        if (narrative) output[COL.SYSTEM_NOTES] = narrative;
      }
      if (!this.toText(output[COL.FUNDS_FOLLOWED]) && candidate.amountText) {
        output[COL.FUNDS_FOLLOWED] = `${candidate.amountText}${candidate.tokenSymbol ? ` ${candidate.tokenSymbol}` : ""}`.trim();
      }
      output[COL.CONTINUES_AS] = output[COL.CONTINUES_AS] || "Path ends.";
      output[COL.LEFT_BOUNDS_DISPLAY] = output[COL.LEFT_BOUNDS_DISPLAY] || "0";
      output[COL.LEFT_BOUNDS_NUMERIC] = output[COL.LEFT_BOUNDS_NUMERIC] || "0";
      output[COL.CHAIN_ID] = output[COL.CHAIN_ID] || candidate.chainId || "1";
      output[COL.CHAIN_NAME] = output[COL.CHAIN_NAME] || candidate.chainName || "Ethereum";
      output[COL.HOP_LOGIC] = output[COL.HOP_LOGIC] || this.hopLogicType;
      this.appendTool88NoteToRow(output, `Tool 88: inferred missing ${candidate.tokenSymbol || "asset"} transfer details from ${candidate.sourceLabel} before hop solving.`);
      return output;
    },

    async inferMissingTransactionDetailsForRows(rows = [], dataStartIndex = 0, options = {}) {
      if (options.skipHashInference) return { rows, changed: false, warnings: [] };
      const outputRows = (rows || []).map((row) => this.cloneRowPreservingValues(row));
      const warnings = [];
      let changed = false;
      let inferredCount = 0;
      let attemptedCount = 0;
      const maxRows = Math.max(1, Math.min(50, Number(options.maxHashInferenceRows || 25)));

      for (let rowIndex = dataStartIndex; rowIndex < outputRows.length && attemptedCount < maxRows; rowIndex += 1) {
        const row = outputRows[rowIndex];
        if (!this.rowHasAnyData(row) || !this.shouldInferTransactionDetailsForRow(row)) continue;
        attemptedCount += 1;
        const txHash = this.toText(row[COL.TX_HASH]);
        this.statusMessage = `Inferring transaction details for ${this.shortenMiddle(txHash, 12, 10)}...`;
        try {
          const bundle = await this.lookupTransactionBundleForInputInference(row);
          const candidates = this.extractInputInferenceCandidatesFromBundle(bundle);
          const candidate = this.chooseInputInferenceCandidate(candidates, row);
          if (!candidate) {
            const count = candidates.length;
            warnings.push(count > 1
              ? `Tool 88 found ${count} possible recipients for ${this.shortenMiddle(txHash, 12, 10)}. Add the intended ${this.targetColumnLabel} value before running automation.`
              : `Tool 88 could not infer a clear recipient for ${this.shortenMiddle(txHash, 12, 10)} from provider transaction details.`);
            continue;
          }
          outputRows[rowIndex] = this.applyInputInferenceCandidateToRow(row, candidate, { rowIndex, rows: outputRows });
          inferredCount += 1;
          changed = true;
        } catch (error) {
          warnings.push(`Transaction-detail inference failed for ${this.shortenMiddle(txHash, 12, 10)}: ${error?.message || "unknown provider error"}.`);
        }
      }

      if (inferredCount) {
        warnings.push(`Tool 88 inferred missing transaction detail${inferredCount === 1 ? "" : "s"} for ${inferredCount} hash-only/input row${inferredCount === 1 ? "" : "s"}.`);
      }
      return { rows: outputRows, changed, warnings, inferredCount, attemptedCount };
    },

    loadSimpleInputIntoSpreadsheet() {
      const parsed = this.parseSimpleInputTextToRows(this.simpleInputText);
      if (!parsed.rows.length) {
        this.simpleViewMessage = "Paste data first, or close Simple View and use the spreadsheet directly.";
        return false;
      }
      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(parsed.rows));
      this.simpleLastLoadedInputText = this.toText(this.simpleInputText);
      this.flowCompletionScanResult = null;
      this.flowCompletionMessage = "";
      this.flowAbsorbResult = null;
      this.simpleFlowActionReady = false;
      const skippedText = parsed.skipped ? ` ${parsed.skipped} line${parsed.skipped === 1 ? "" : "s"} could not be interpreted.` : "";
      const repairedText = parsed.repaired ? ` Repaired ${parsed.repaired} multiline spreadsheet cell${parsed.repaired === 1 ? "" : "s"}.` : "";
      this.simpleViewMessage = parsed.mode === "casebuddy-tsv"
        ? `Loaded ${this.formatInteger(parsed.rows.length)} Case Buddy-style row${parsed.rows.length === 1 ? "" : "s"}.${repairedText}${skippedText}`
        : parsed.mode === "staged-hash-list"
          ? `Loaded ${this.formatInteger(Math.max(0, parsed.rows.length - 1))} staged transaction hash row${parsed.rows.length === 2 ? "" : "s"}.${repairedText}${skippedText}`
          : `Loaded ${this.formatInteger(Math.max(0, parsed.rows.length - 1))} simplified transaction row${parsed.rows.length === 2 ? "" : "s"}.${repairedText}${skippedText}`;
      return true;
    },

    async simpleProcessData() {
      if (this.isProcessing) return;
      this.simpleRecommendationApplied = false;
      this.simpleFlowActionReady = false;
      this.simpleViewMessage = "";
      let loadedFromSimpleText = false;
      const simpleText = this.toText(this.simpleInputText);
      const shouldLoadSimpleText = Boolean(simpleText && simpleText !== this.simpleLastLoadedInputText);
      if (shouldLoadSimpleText) {
        const loaded = this.loadSimpleInputIntoSpreadsheet();
        if (!loaded) return;
        loadedFromSimpleText = true;
        await this.$nextTick();
      }

      const rawSpreadsheetRows = this.trimTrailingBlankRows(this.$refs.inputSpreadsheet?.getData?.() || []);
      const hasSpreadsheetData = rawSpreadsheetRows.some((row) => this.rowHasAnyData(row));
      if (loadedFromSimpleText && this.shouldProcessSimpleLoadedRowsDirectly(rawSpreadsheetRows)) {
        await this.processData();
      } else if (loadedFromSimpleText || hasSpreadsheetData) {
        await this.simpleAdvanceToNextActionableEnding({ source: "load" });
      } else {
        await this.processData();
      }

      if (this.simpleHasTargetAmbiguity) this.simpleViewMessage = "Tool 88 found multiple possible target addresses. Choose one and run checks again.";
      else if (this.countLookupResult) this.simpleViewMessage = this.simpleViewMessage || "Checks complete. Use the recommendation or open advanced view to inspect details.";
      else if (this.flowCompletionScanResult?.done) this.simpleViewMessage = this.simpleViewMessage || this.flowCompletionMessage || "Tool 88 sees this flow as complete.";
    },

    shouldProcessSimpleLoadedRowsDirectly(rows = null) {
      const inputRows = Array.isArray(rows)
        ? rows
        : this.trimTrailingBlankRows(this.$refs.inputSpreadsheet?.getData?.() || []);
      if (!inputRows.length || !inputRows.some((row) => this.rowHasAnyData(row))) {
        return false;
      }
      const headerDetected = this.isLikelyHeaderRow(inputRows[0]);
      const dataStartIndex = headerDetected ? 1 : 0;
      const { entries } = this.collectEntries(inputRows, dataStartIndex);
      return entries.length <= 1;
    },

    isSimpleLowValueAutoResultReady() {
      return Boolean(
        this.pathEndingResult &&
        (this.pathEndingResult.lowValueAuto || /low-value/i.test(this.toText(this.reportText)) || /low-value/i.test(this.toText(this.flowCompletionMessage)))
      );
    },

    async simpleEnsureSelectedTargetForCurrentSimpleStep() {
      if (!this.selectedEntryKey) return false;
      if (this.selectedTargetAddress) return true;
      const entry = this.selectedEntry || this.parsedEntries.find((item) => item.key === this.selectedEntryKey) || null;
      if (!entry) return false;
      const candidates = this.collectTargetCandidates(entry);
      this.targetCandidates = candidates;
      if (candidates.length === 1) {
        this.selectedTargetAddress = candidates[0].address;
        await this.$nextTick();
        return true;
      }
      return false;
    },

    async simpleAdvanceToNextActionableEnding(options = {}) {
      const maxAutoLowValue = Math.max(1, Number(options.maxAutoLowValue || 25));
      let lowValueEnded = 0;

      for (let pass = 0; pass < maxAutoLowValue; pass += 1) {
        this.simpleRecommendationApplied = false;
        const selectionResult = await this.selectNextUnresolvedEnding({
          source: options.source || "simple-advance",
          ignoreManualDepthLimit: options.ignoreManualDepthLimit === true,
          caseBuddyAutomation: options.caseBuddyAutomation === true,
          directAutomation: options.directAutomation === true,
        });
        await this.$nextTick();

        if (selectionResult?.blocked) {
          this.simpleRecommendationApplied = false;
          this.simpleViewMessage = selectionResult.message || this.flowCompletionMessage || "Tool 88 stopped before selecting another ending.";
          return {
            done: false,
            lowValueEnded,
            selectionBlocked: true,
            guardrailReason: selectionResult.reason || "selection-blocked",
            selectionResult,
          };
        }

        if (selectionResult?.empty) {
          this.simpleRecommendationApplied = false;
          this.simpleViewMessage = selectionResult.message || this.flowCompletionMessage || "No input data was found to review.";
          return { done: false, lowValueEnded, empty: true, selectionResult };
        }

        if (this.isSimpleLowValueAutoResultReady()) {
          lowValueEnded += 1;
          this.absorbOutputIntoInput();
          await this.$nextTick();
          if (this.flowCompletionScanResult?.done) {
            this.simpleRecommendationApplied = false;
            this.simpleViewMessage = `Auto-finalized ${lowValueEnded} low-value ending${lowValueEnded === 1 ? "" : "s"}. Tool 88 sees this flow as complete.`;
            return { done: true, lowValueEnded };
          }
          continue;
        }

        if (this.flowCompletionScanResult?.done) {
          this.simpleRecommendationApplied = false;
          this.simpleViewMessage = lowValueEnded
            ? `Auto-finalized ${lowValueEnded} low-value ending${lowValueEnded === 1 ? "" : "s"}. Tool 88 sees this flow as complete.`
            : (this.flowCompletionMessage || "Tool 88 sees this flow as complete.");
          return { done: true, lowValueEnded };
        }

        if (this.simpleHasTargetAmbiguity) {
          this.simpleViewMessage = lowValueEnded
            ? `Auto-finalized ${lowValueEnded} low-value ending${lowValueEnded === 1 ? "" : "s"}. Choose the next target address before continuing.`
            : "Tool 88 found multiple possible target addresses. Choose one before continuing.";
          return { done: false, lowValueEnded, needsTarget: true };
        }

        if (this.countLookupResult) {
          this.simpleViewMessage = lowValueEnded
            ? `Auto-finalized ${lowValueEnded} low-value ending${lowValueEnded === 1 ? "" : "s"}. The next unresolved ending is ready for Auto Decide.`
            : "Next unresolved ending is ready for Auto Decide.";
          return { done: false, lowValueEnded, readyForDecision: true };
        }

        await this.simpleEnsureSelectedTargetForCurrentSimpleStep();

        if (this.simpleResultReadyForContinue) {
          this.simpleViewMessage = lowValueEnded
            ? `Auto-finalized ${lowValueEnded} low-value ending${lowValueEnded === 1 ? "" : "s"}. Use the result to continue.`
            : "This step has a result. Use it as the next input to continue.";
          return { done: false, lowValueEnded, resultReady: true };
        }

        await this.simpleEnsureSelectedTargetForCurrentSimpleStep();

        if (
          !this.countLookupResult &&
          this.selectedEntryKey &&
          this.selectedTargetAddress &&
          !this.simpleHasTargetAmbiguity
        ) {
          this.simpleViewMessage = lowValueEnded
            ? `Auto-finalized ${lowValueEnded} low-value ending${lowValueEnded === 1 ? "" : "s"}. Running checks for the next unresolved ending...`
            : "Running checks for the selected unresolved ending...";
          await this.processData();
          await this.$nextTick();

          if (this.isSimpleLowValueAutoResultReady()) {
            lowValueEnded += 1;
            this.absorbOutputIntoInput();
            await this.$nextTick();
            continue;
          }

          if (this.countLookupResult) {
            this.simpleViewMessage = lowValueEnded
              ? `Auto-finalized ${lowValueEnded} low-value ending${lowValueEnded === 1 ? "" : "s"}. The next unresolved ending is ready for Auto Decide.`
              : "Next unresolved ending is ready for Auto Decide.";
            return { done: false, lowValueEnded, readyForDecision: true };
          }

          if (this.simpleResultReadyForContinue) {
            this.simpleViewMessage = lowValueEnded
              ? `Auto-finalized ${lowValueEnded} low-value ending${lowValueEnded === 1 ? "" : "s"}. Use the result to continue.`
              : "This step has a result. Use it as the next input to continue.";
            return { done: false, lowValueEnded, resultReady: true };
          }
        }

        this.simpleViewMessage = lowValueEnded
          ? `Auto-finalized ${lowValueEnded} low-value ending${lowValueEnded === 1 ? "" : "s"}. Run checks for the selected ending to continue.`
          : "Selected the next unresolved ending. Run checks to continue.";
        return { done: false, lowValueEnded };
      }

      this.simpleViewMessage = `Auto-finalized ${lowValueEnded} low-value ending${lowValueEnded === 1 ? "" : "s"}. Stopped to avoid an unintended loop; open advanced view to review the current state.`;
      return { done: false, lowValueEnded, stoppedForSafety: true };
    },

    async simpleRunGuidedAction() {
      if (this.simplePrimaryActionDisabled) return;
      if (this.simpleResultReadyForContinue) {
        await this.simpleAbsorbOutputAndSelectNext();
        return;
      }
      if (!this.countLookupResult) {
        await this.simpleEnsureSelectedTargetForCurrentSimpleStep();
        const simpleText = this.toText(this.simpleInputText);
        const simpleTextChanged = Boolean(simpleText && simpleText !== this.simpleLastLoadedInputText);
        if (!simpleTextChanged && this.parsedEntries.length && this.selectedEntryKey) {
          await this.processData({
            entryKey: this.selectedEntryKey,
            selectedTargetAddress: this.selectedTargetAddress,
          });
        } else {
          await this.simpleProcessData();
        }
        return;
      }
      await this.simpleApplyRecommendation();
    },

    caseBuddyRawDataAutomationContext(options = {}) {
      return Boolean(
        options.caseWorkflowRawData === true ||
        options.caseBuddyRawDataAutomation === true ||
        options.rawDataAutomation === true
      );
    },

    caseBuddySkipResolvableReviewsEnabled(options = {}) {
      return Boolean(
        options.skipResolvableReviews === true ||
        options.skipResolvableReview === true ||
        options.rubberStampResolvableReviews === true ||
        options.autoFinalizeRecognizedBtcBridgeEndings === true
      );
    },

    buildCaseBuddyRecognizedBtcBridgeEndingEvidence(decision = null) {
      const selectedEntry = this.selectedEntry;
      const selectedAddress = this.selectedTargetAddress;
      const signal = this.transactionSignalResult || {};
      const signalRaw = signal.raw || {};
      const attributionText = this.getAttributionOnlyCombinedText();
      const positiveSignalText = this.getPositiveSignalMarkerText();
      const decisionText = [
        decision?.title,
        decision?.summary,
        ...(Array.isArray(decision?.reasons) ? decision.reasons : []),
      ].map((value) => this.toText(value)).filter(Boolean).join(' ');
      const entryText = this.collectEntrySearchText(selectedEntry);
      const evidenceText = `${attributionText} ${positiveSignalText} ${decisionText}`.trim();
      const matchedBridgeServiceName = this.getMatchedBridgeServiceName(evidenceText);
      const positiveMemoEvidence = this.hasPositiveBtcMemoEvidence([
        entryText,
        positiveSignalText,
        decisionText,
        signal.classification,
        signal.classificationLabel,
        signal.memoText,
        signal.evidenceSummary,
        signal.detailText,
        signal.recommendation,
        signalRaw.classification,
        signalRaw.signal_classification,
        signalRaw.result_type,
        Array.isArray(signalRaw.op_return_outputs)
          ? signalRaw.op_return_outputs.map((item) => item?.memo || item?.scriptpubkey_asm || item?.script || '').join(' ')
          : '',
      ].map((value) => this.toText(value)).filter(Boolean).join(' '));
      const signalClass = this.normalizeSignalCode(signal.classification || signalRaw.classification || signalRaw.signal_classification || '');
      const attributionClass = this.normalizeAttributionCode(
        this.attributionLookupResult?.classification ||
        this.attributionLookupResult?.entityType ||
        ''
      );
      const structuredBridgeSignal = Boolean(
        ['bridge_like', 'swap_or_bridge', 'btc_op_return_memo'].includes(signalClass) ||
        ['bridge_like', 'swap_or_bridge'].includes(attributionClass) ||
        this.tool88FlagIsTrue(signal.hasSwapOrBridgeSignal) ||
        this.tool88FlagIsTrue(signal.hasMemo) ||
        (Array.isArray(signalRaw.bridge_field_hits) && signalRaw.bridge_field_hits.length > 0) ||
        (Array.isArray(signalRaw.op_return_outputs) && signalRaw.op_return_outputs.length > 0)
      );
      const family = this.getAutoDecisionNetworkFamily();
      return {
        family,
        selectedAddress: this.toText(selectedAddress),
        matchedBridgeServiceName,
        positiveMemoEvidence,
        structuredBridgeSignal,
        signalClass,
        attributionClass,
        decisionSummary: this.toText(decision?.summary || ''),
        decisionReasons: Array.isArray(decision?.reasons)
          ? decision.reasons.map((value) => this.toText(value)).filter(Boolean).slice(0, 6)
          : [],
      };
    },

    caseBuddyCanAutoFinalizeRecognizedBtcBridgeEnding(options = {}, decision = null, evidence = null) {
      const details = evidence || this.buildCaseBuddyRecognizedBtcBridgeEndingEvidence(decision);
      return Boolean(
        this.caseBuddyRawDataAutomationContext(options) &&
        this.caseBuddySkipResolvableReviewsEnabled(options) &&
        this.toText(decision?.action).toLowerCase() === 'bridge' &&
        details.family === 'btc' &&
        (details.matchedBridgeServiceName || details.positiveMemoEvidence || details.structuredBridgeSignal) &&
        this.selectedEntry &&
        this.selectedTargetAddress
      );
    },

    caseBuddyFinalizeRecognizedBtcBridgeEnding(options = {}, decision = null) {
      const evidence = this.buildCaseBuddyRecognizedBtcBridgeEndingEvidence(decision);
      if (!this.caseBuddyCanAutoFinalizeRecognizedBtcBridgeEnding(options, decision, evidence)) return false;

      const serviceName = this.cleanAttributionLabel(evidence.matchedBridgeServiceName || '').replace(/[.\s]+$/g, '');
      const evidenceParts = [];
      if (serviceName) evidenceParts.push(`recognized bridge service ${serviceName}`);
      if (evidence.positiveMemoEvidence) evidenceParts.push('positive BTC OP_RETURN/memo evidence');
      if (!evidenceParts.length && evidence.structuredBridgeSignal) evidenceParts.push('structured bridge-style transaction evidence');
      const evidenceLabel = evidenceParts.join(' and ') || 'positive bridge evidence';
      const reason = `Tool 88 automatic raw-data decision: ${evidenceLabel} identifies this as a BTC bridge-style endpoint. The current workflow does not have a safe destination row to continue through this bridge, so Skip resolvable reviews recorded it as a reviewed bridge path ending.`;

      this.treatAsSpecialPathEnding('bridge', {
        skipConfirm: true,
        autoReason: reason,
        autoBridgeTerminalFallback: true,
        bridgeEvidence: evidence,
      });

      if (!this.simpleResultReadyForContinue || !this.pathEndingResult) return false;
      this.simpleRecommendationApplied = true;
      this.simpleViewMessage = serviceName
        ? `${serviceName} bridge endpoint recorded as a reviewed path ending. Tool 88 will continue with the remaining unresolved endings.`
        : 'Recognized BTC bridge/memo endpoint recorded as a reviewed path ending. Tool 88 will continue with the remaining unresolved endings.';
      return true;
    },

    async simpleApplyRecommendation(options = {}) {
      if (this.isProcessing || this.simpleRecommendationApplied || this.simpleResultReadyForContinue) return;
      if (!this.countLookupResult) {
        await this.simpleProcessData();
        if (this.simpleResultReadyForContinue) {
          this.simpleViewMessage = "This ending was finalized without lookup. Use the result as new input to keep solving the flow.";
          return;
        }
        if (!this.countLookupResult) {
          this.simpleViewMessage = this.simpleHasTargetAmbiguity
            ? "Choose the target address, then run checks."
            : "Run checks for the selected unresolved ending before Auto Decide can apply a recommendation.";
          return;
        }
      }
      const decision = this.buildAutomaticDecisionPreview();
      if (!decision || decision.action === "review") {
        this.simpleViewMessage = decision?.summary || "Tool 88 recommends manual review. Open advanced view to inspect the details.";
        return;
      }
      if (decision.action === "swap") {
        await this.openSwapExplorer();
        if (this.swapAutomaticCandidate?.canApply) {
          this.applySwapCandidate(this.swapAutomaticCandidate);
          this.simpleRecommendationApplied = true;
          this.simpleViewMessage = "Swap recommendation applied. Use the result as new input to keep solving the flow.";
        } else this.simpleViewMessage = "Swap explorer opened. The swap needs manual selection or advanced review.";
        return;
      }
      if (decision.action === "bridge") {
        // In Case Buddy raw-data Auto with resolvable reviews skipped, a
        // positively identified BTC bridge/OP_RETURN endpoint is an accepted
        // terminal for now. Record it before opening the destination solver so
        // the loop can keep resolving the other branches in the same root.
        if (this.caseBuddyFinalizeRecognizedBtcBridgeEnding(options, decision)) return;

        await this.openBridgeSolver();
        if (this.bridgeBestCandidate?.canApply) {
          this.applyBridgeCandidate(this.bridgeBestCandidate);
          this.simpleRecommendationApplied = true;
          this.simpleViewMessage = "Bridge recommendation applied. Use the result as new input to keep solving the flow.";
        } else this.simpleViewMessage = "Bridge solver opened. The bridge needs manual selection or advanced review.";
        return;
      }
      await this.applyAutomaticDecision(options);
      if (this.simpleResultReadyForContinue) {
        this.simpleRecommendationApplied = true;
      }
      if (!this.simpleResultReadyForContinue) {
        this.simpleRecommendationApplied = false;
      }
      this.simpleViewMessage = this.simpleResultReadyForContinue
        ? "Recommendation applied. Use the result as new input to keep solving the flow."
        : "Recommendation applied. If Tool 88 did not produce output, open advanced view for details.";
    },

    async simpleAbsorbOutputAndSelectNext() {
      if (this.isProcessing || !this.simpleResultReadyForContinue) return;
      if (!this.ensureSimpleTraceOutputCanAdvance()) return;
      this.simpleFlowActionReady = false;
      this.simpleViewMessage = "Using the result and looking for the next unresolved ending...";
      this.absorbOutputIntoInput();
      await this.$nextTick();
      if (this.flowCompletionScanResult?.done) {
        this.simpleRecommendationApplied = false;
        this.simpleViewMessage = "Output absorbed. Tool 88 sees this flow as complete.";
        return;
      }
      await this.simpleAdvanceToNextActionableEnding({ source: "absorb" });
    },

    normalizeDirectAutomationSpeed(value = this.directAutomationSpeedMultiplier) {
      const number = Number(value);
      if (!Number.isFinite(number)) return 1;
      return Math.max(0.5, Math.min(3, Math.round(number * 4) / 4));
    },

    normalizeDirectAutomationGuardrails(guardrails = {}) {
      const suppliedGuardrails = guardrails || {};
      const merged = {
        ...TOOL88_DIRECT_AUTOMATION_DEFAULT_GUARDRAILS,
        ...suppliedGuardrails,
        speedMultiplier: suppliedGuardrails.speedMultiplier ?? this.directAutomationSpeedMultiplier,
      };
      return {
        ...this.normalizeCaseBuddyAutomationGuardrails(merged),
        directAutomationVersion: TOOL88_DIRECT_AUTOMATION_VERSION,
      };
    },

    getDirectAutomationLoopDelayMs(guardrails = {}) {
      const baseDelayMs = Number(guardrails.baseDelayMs ?? guardrails.delayMs ?? TOOL88_DIRECT_AUTOMATION_DEFAULT_GUARDRAILS.delayMs);
      const speed = this.normalizeDirectAutomationSpeed(this.directAutomationSpeedMultiplier);
      if (!Number.isFinite(baseDelayMs) || baseDelayMs <= 0) return 0;
      return Math.max(0, Math.round(baseDelayMs / speed));
    },

    requestDirectAutomationStop(message = "Direct Tool 88 automation stop requested.") {
      if (!this.directAutomationRunState?.active) return;
      this.directAutomationRunState = {
        ...this.directAutomationRunState,
        stopRequested: true,
        status: "stop-requested",
        detail: this.toText(message) || "Stop requested.",
      };
      this.simpleViewMessage = this.directAutomationRunState.detail;
    },

    async sleepDirectAutomationDelay(delayMs = 0) {
      const totalMs = Math.max(0, Number(delayMs) || 0);
      const startedAt = Date.now();
      while (Date.now() - startedAt < totalMs) {
        if (this.directAutomationRunState?.stopRequested) return;
        await this.sleep(Math.min(250, totalMs - (Date.now() - startedAt)));
      }
    },

    buildDirectAutomationSnapshot(options = {}) {
      const snapshot = this.buildCaseBuddyHopSolverSnapshot({
        includeRows: options.includeRows === true,
        status: options.status,
        integrationAction: options.integrationAction,
        guardrail: options.guardrail,
        message: options.message,
      });
      return {
        ...snapshot,
        integrationVersion: TOOL88_DIRECT_AUTOMATION_VERSION,
        automationMode: "direct-tool88-auto",
        directAutomationVersion: TOOL88_DIRECT_AUTOMATION_VERSION,
        runState: this.buildCaseBuddyAutomationRunStateSnapshot(this.directAutomationRunState),
      };
    },

    buildDirectAutomationActionKey(action, snapshot = {}) {
      return this.buildCaseBuddyAutomationActionKey(action, snapshot);
    },

    evaluateDirectAutomationGuardrail(state, guardrails, snapshot = {}) {
      return this.evaluateCaseBuddyAutomationGuardrail(state, guardrails, snapshot);
    },

    async runDirectAutomationStep(options = {}) {
      if (this.isProcessing) {
        return this.buildDirectAutomationSnapshot({
          includeRows: false,
          status: "busy",
          integrationAction: "busy",
          message: "Tool 88 is already processing a hop.",
        });
      }

      const simpleText = this.toText(this.simpleInputText);
      const simpleTextChanged = Boolean(simpleText && simpleText !== this.simpleLastLoadedInputText);
      const spreadsheetRows = this.trimTrailingBlankRows(this.$refs.inputSpreadsheet?.getData?.() || []);
      const hasSpreadsheetRows = spreadsheetRows.some((row) => this.rowHasAnyData(row));
      const hasLoadedState = Boolean(this.parsedEntries.length || this.inputSnapshotRows.length || this.outputSnapshotRows.length || this.countLookupResult || this.flowCompletionResult);
      let integrationAction = "review";

      if (simpleTextChanged || (!hasLoadedState && !hasSpreadsheetRows && simpleText)) {
        integrationAction = "load-run-checks";
        await this.simpleProcessData();
      } else if (this.simpleResultReadyForContinue) {
        integrationAction = "absorb-output-select-next";
        await this.simpleAbsorbOutputAndSelectNext();
      } else if (this.flowCompletionResult?.done) {
        integrationAction = "flow-complete";
      } else if (this.simpleHasTargetAmbiguity && !this.selectedTargetAddress) {
        integrationAction = "target-ambiguity";
      } else if (this.countLookupResult && this.autoDecisionPreview && this.autoDecisionPreview.action !== "review") {
        integrationAction = `apply-${this.autoDecisionPreview.action}`;
        await this.simpleApplyRecommendation();
      } else if (this.countLookupResult && (!this.autoDecisionPreview || this.autoDecisionPreview.action === "review")) {
        integrationAction = "manual-review";
      } else if (this.selectedEntryKey || this.parsedEntries.length || hasSpreadsheetRows || hasLoadedState) {
        if (!this.selectedTargetAddress) {
          await this.simpleEnsureSelectedTargetForCurrentSimpleStep();
        }
        if (this.selectedEntryKey && this.selectedTargetAddress) {
          integrationAction = "run-checks";
          await this.processData({
            entryKey: this.selectedEntryKey,
            selectedTargetAddress: this.selectedTargetAddress,
          });
        } else if (this.targetCandidates.length > 1) {
          integrationAction = "target-ambiguity";
        } else {
          integrationAction = "select-next-unresolved";
          await this.simpleAdvanceToNextActionableEnding({
            source: "direct-auto-next",
            directAutomation: true,
            ignoreManualDepthLimit: true,
            maxAutoLowValue: options.maxAutoLowValue || TOOL88_DIRECT_AUTOMATION_DEFAULT_GUARDRAILS.maxAutoLowValue,
          });
        }
      } else {
        integrationAction = "no-input";
        this.simpleViewMessage = "Paste or load valid Tool 88 input data before starting Auto.";
      }

      return this.buildDirectAutomationSnapshot({
        includeRows: options.includeRows === true,
        status: this.deriveCaseBuddySnapshotStatus(integrationAction),
        integrationAction,
        message: this.simpleViewMessage || this.flowCompletionMessage || this.statusMessage || "Tool 88 direct auto step completed.",
      });
    },

    async startDirectAutomationLoop(options = {}) {
      if (this.caseBuddyEmbedded) {
        return this.buildDirectAutomationSnapshot({
          includeRows: false,
          status: "not-available",
          message: "Standalone Tool 88 auto is hidden while Tool 88 is embedded in Case Buddy.",
        });
      }
      if (this.directAutomationRunState.active || this.isProcessing) {
        return this.buildDirectAutomationSnapshot({
          includeRows: false,
          status: "busy",
          message: "Tool 88 direct auto is already running or a hop is processing.",
        });
      }

      const guardrails = this.normalizeDirectAutomationGuardrails(options.guardrails || options);
      const runState = {
        ...createTool88DirectAutomationRunState(),
        active: true,
        startedAtMs: Date.now(),
        status: "running",
        detail: "Tool 88 direct auto started.",
      };
      this.directAutomationRunState = runState;
      this.simpleViewMessage = runState.detail;

      let finalSnapshot = null;
      try {
        for (let automationLoopIndex = 0; automationLoopIndex < guardrails.maxLoopSteps; automationLoopIndex += 1) {
          const preGuardrail = this.evaluateDirectAutomationGuardrail(this.directAutomationRunState, guardrails, finalSnapshot || {});
          if (preGuardrail) {
            finalSnapshot = this.buildDirectAutomationSnapshot({
              includeRows: false,
              status: "guardrail-paused",
              guardrail: preGuardrail,
              message: preGuardrail.message,
            });
            break;
          }

          const nextStepNumber = Number(this.directAutomationRunState.stepCount || 0) + 1;
          this.directAutomationRunState = {
            ...this.directAutomationRunState,
            status: "running",
            detail: `Tool 88 direct auto is solving unresolved path ending ${this.formatInteger(nextStepNumber)}.`,
          };
          this.simpleViewMessage = this.directAutomationRunState.detail;

          const stepResult = await this.runDirectAutomationStep({
            includeRows: true,
            maxAutoLowValue: guardrails.maxAutoLowValue,
          });
          const selectedKey = this.toText(stepResult.selectedEntry?.key || stepResult.selectedEntry?.txHash || "");
          const researched = new Set(this.directAutomationRunState.researchedEntryKeys || []);
          if (selectedKey) researched.add(selectedKey);

          const actionKey = this.buildDirectAutomationActionKey(stepResult.integrationAction || "step", stepResult);
          const repeatedActionCount = actionKey === this.directAutomationRunState.lastActionKey
            ? Number(this.directAutomationRunState.repeatedActionCount || 0) + 1
            : 1;

          const directProgressState = this.buildCaseBuddyAutomationRunProgressState(
            this.directAutomationRunState,
            stepResult,
            stepResult.integrationAction || "step",
            guardrails
          );
          this.directAutomationRunState = {
            ...this.directAutomationRunState,
            ...directProgressState,
            stepCount: Number(this.directAutomationRunState.stepCount || 0) + 1,
            researchedEntryKeys: Array.from(researched),
            lastActionKey: actionKey,
            repeatedActionCount,
            status: "running",
            detail: stepResult.message || stepResult.status || "Tool 88 direct auto step completed.",
          };

          finalSnapshot = this.buildDirectAutomationSnapshot({
            includeRows: false,
            status: stepResult.status,
            integrationAction: stepResult.integrationAction,
            message: stepResult.message || this.directAutomationRunState.detail,
          });

          const postGuardrail = this.evaluateDirectAutomationGuardrail(this.directAutomationRunState, guardrails, stepResult);
          if (postGuardrail) {
            finalSnapshot = this.buildDirectAutomationSnapshot({
              includeRows: false,
              status: "guardrail-paused",
              guardrail: postGuardrail,
              message: postGuardrail.message,
            });
            break;
          }

          if (stepResult.flowComplete) {
            const nowMs = Date.now();
            const firstSeenAtMs = Number(this.directAutomationRunState.flowCompleteFirstSeenAtMs) || nowMs;
            const confirmations = Number(this.directAutomationRunState.flowCompleteConfirmations || 0) + 1;
            const settled = confirmations >= guardrails.flowCompleteConfirmations && nowMs - firstSeenAtMs >= guardrails.flowCompleteSettleMs;
            this.directAutomationRunState = {
              ...this.directAutomationRunState,
              flowCompleteFirstSeenAtMs: firstSeenAtMs,
              flowCompleteConfirmations: confirmations,
              detail: settled
                ? `Tool 88 confirmed flow completion ${this.formatInteger(confirmations)} time${confirmations === 1 ? "" : "s"}.`
                : `Tool 88 saw flow completion ${this.formatInteger(confirmations)}/${this.formatInteger(guardrails.flowCompleteConfirmations)}; waiting briefly to confirm no late unresolved endings remain.`,
            };
            finalSnapshot = this.buildDirectAutomationSnapshot({
              includeRows: false,
              status: settled ? stepResult.status : "flow-complete-confirming",
              integrationAction: settled ? stepResult.integrationAction : "confirm-flow-complete",
              message: this.directAutomationRunState.detail,
            });
            this.simpleViewMessage = this.directAutomationRunState.detail;
            if (settled) break;
            const settleDelayMs = Math.max(350, this.getDirectAutomationLoopDelayMs(guardrails));
            if (settleDelayMs > 0) await this.sleepDirectAutomationDelay(settleDelayMs);
            continue;
          }

          this.directAutomationRunState = {
            ...this.directAutomationRunState,
            flowCompleteFirstSeenAtMs: 0,
            flowCompleteConfirmations: 0,
          };

          if (stepResult.needsManualReview || stepResult.needsTargetSelection || (guardrails.pauseOnUnverifiedOutput && stepResult.unverifiedOutput) || stepResult.status === "no-input" || stepResult.integrationAction === "no-input") {
            break;
          }

          const delayMs = this.getDirectAutomationLoopDelayMs(guardrails);
          if (delayMs > 0) {
            await this.sleepDirectAutomationDelay(delayMs);
          }
        }

        if (!finalSnapshot?.guardrail && !finalSnapshot?.flowComplete && !finalSnapshot?.needsManualReview && !finalSnapshot?.needsTargetSelection && !finalSnapshot?.unverifiedOutput && Number(this.directAutomationRunState.stepCount || 0) >= guardrails.maxLoopSteps) {
          const maxStepGuardrail = {
            reason: "max-steps",
            message: `Tool 88 direct auto paused after ${this.formatInteger(this.directAutomationRunState.stepCount)} automation step${this.directAutomationRunState.stepCount === 1 ? "" : "s"}.`,
          };
          finalSnapshot = this.buildDirectAutomationSnapshot({
            includeRows: false,
            status: "guardrail-paused",
            guardrail: maxStepGuardrail,
            message: maxStepGuardrail.message,
          });
        }
      } finally {
        this.directAutomationRunState = {
          ...this.directAutomationRunState,
          active: false,
          finishedAtMs: Date.now(),
          status: finalSnapshot?.status || "finished",
          detail: finalSnapshot?.message || "Tool 88 direct auto finished.",
          lastGuardrail: finalSnapshot?.guardrail || null,
        };
      }

      this.simpleViewMessage = finalSnapshot?.message || this.directAutomationRunState.detail || "Tool 88 direct auto finished.";
      return finalSnapshot || this.buildDirectAutomationSnapshot({ includeRows: false });
    },


    getTool88CaseBuddyIntegrationContract() {
      return {
        version: TOOL88_CASE_BUDDY_INTEGRATION_VERSION,
        componentName: "Tool88Page_CaseBuddy",
        purpose: "Embed or ref Tool 88 from Case Buddy to solve one selected path ending, select the next unresolved path ending, or run guarded step-by-step automation until flow completion or review pause.",
        expectedRows: "Case Buddy A-X matrix or TSV using the standard Flow Solver / Case Buddy raw-data layout.",
        columns: {
          pathLabel: "A",
          transactionHash: "B",
          fromAddress: "C",
          toAddress: "D",
          dateTime: "E",
          amount: "F",
          token: "G",
          notes: "J",
          attribution: "K",
          fundsFollowed: "M",
          fromHash: "O",
          continuesAs: "Q",
          leftBoundsDisplay: "R",
          leftBoundsNumeric: "S",
          chainId: "T",
          chainName: "U",
          hopLogic: "V",
        },
        caseBuddyPlaceholderMapping: {
          currentPlaceholderButton: "openCaseHopSolverPlaceholderFromInvestigationVisualizer",
          currentPlaceholderCandidate: "caseInvestigationVisualizerSelectedCandidateView",
          currentLockFlag: "caseInvestigationVisualizerInteractionLocked",
          expectedMount: "Mount Tool88Page_CaseBuddy with caseBuddyEmbedded=true inside the Solve Hop modal, or keep a hidden ref and drive it through these public methods.",
        },
        candidateShape: {
          spreadsheetRowNumber: "1-based Case Buddy row number; preferred when available because Tool 88 entries can repeat hashes across path labels.",
          rowIndex: "0-based row index alternative.",
          pathLabel: "Column A path/story label, used to disambiguate repeated transaction hashes.",
          hash: "Column B transaction hash for the selected path-ending entry.",
          toAddress: "Column D target for forward solving; fromAddress can be supplied for reverse solving.",
          fundsFollowed: "Column M funds-followed value, including token text when available.",
          leftBoundsDisplay: "Column R left-bounds display text; leftBoundsNumeric can be supplied from Column S.",
          chainId: "Column T chain identifier, if Case Buddy has it.",
          chainName: "Column U chain name, if Case Buddy has it.",
          hopLogic: "Column V hop logic, usually FIFO, LIFO, or NEXT_CHRONOLOGICAL.",
        },
        responseShape: {
          status: "selected-hop-ready, decision-ready, next-unresolved-selected, flow-complete, manual-review, target-ambiguity, guardrail-paused, busy, empty, or not-found.",
          outputRows: "A-X raw-data-style rows when includeRows is true. Case Buddy should stage these for review before applying to Main Data.",
          outputTsv: "Tab-delimited outputRows for copy/import fallback.",
          selectedEntry: "Summary of the currently selected Tool 88 entry and row bounds.",
          selectedTargetAddress: "The active address Tool 88 solved or is ready to solve.",
          decision: "Automatic recommendation summary for Simple View / guided mode.",
          result: "Trace/path-ending/swap/bridge application summary.",
          runState: "Guarded automation counters and stop state.",
          guardrail: "Pause reason when automation stops for safety or review.",
        },
        applyModel: {
          singleHopManual: "caseBuddyOpenSelectedHop(candidate, { rows }) -> let user use Simple or Advanced view -> caseBuddyRunNextAction() / caseBuddyApplyCurrentRecommendation().",
          selectNext: "caseBuddySelectNextUnresolvedHop({ rows }) or caseBuddyRunNextAction({ rows }) when nothing is selected.",
          guidedNext: "Case Buddy's Next button can call caseBuddyRunNextAction({ includeRows: true, speedMultiplier }) once per click.",
          patientAuto: "Either call caseBuddyRunAutomationLoop({ speedMultiplier, guardrails }) or keep the Case Buddy timer in control and repeatedly call caseBuddyRunNextAction().",
          activeRootContext: "For workflow raw-data passes, supply caseBuddyActiveRoot/workflowRawDataRoot so snapshots and progress identify the one main transaction currently being solved.",
          applyResult: "Do not mutate Main Data blindly. Stage outputRows/outputTsv first, then graft one-hop rows or apply zero-hop note/continues-as updates after review/acceptance.",
        },
        reverseTracingNotes: [
          "Tool 88 supports traceDirection='reverse'; Case Buddy visualizer code should not assume Column D is always the active solve address.",
          "For reverse solving, supply fromAddress/targetAddress explicitly when a visual node makes the intended target clear.",
          "Keep the entry-model row bounds because blank Column B rows can belong to the selected transaction entry.",
        ],
        publicMethods: [
          "getTool88CaseBuddyIntegrationContract()",
          "getCaseBuddyHopSolverIntegrationContract()",
          "getCaseBuddyAutomationGuardrails(options)",
          "caseBuddyLoadRowsForHopSolver(rowsOrTsv, options)",
          "caseBuddyOpenSelectedHop(candidate, options)",
          "caseBuddySelectNextUnresolvedHop(options)",
          "caseBuddySettleImmediateAutoEndings(options)",
          "caseBuddyRunNextAction(options)",
          "caseBuddyRunAutomationLoop(options)",
          "caseBuddyRequestAutomationStop(message)",
          "caseBuddyRequestTraceStop(message)",
          "requestTraceFetchStop(message)",
          "buildCaseBuddyHopSolverSnapshot(options)",
        ],
        emittedEvents: [
          "case-buddy-status",
          "case-buddy-progress",
          "case-buddy-result",
          "case-buddy-guardrail",
          "case-buddy-flow-complete",
        ],
        defaultGuardrails: { ...TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS },
        automationNotes: [
          "Call caseBuddyRunNextAction for the Case Buddy Next button.",
          "Call caseBuddyRunAutomationLoop for patient Auto runs, or let Case Buddy own the timer and repeatedly call caseBuddyRunNextAction using its speed slider.",
          "Pass Case Buddy's speedMultiplier (0.5 to 3) into Tool 88 guardrails; Tool 88 scales its patient-auto delay from baseDelayMs / speedMultiplier.",
          "Default guardrails are on: one-hour maximum, 250 unique researched endings, 500 loop steps, and a repeated-action pause after the same action fingerprint exceeds 10 consecutive steps.",
          "Automation pauses on manual review, target ambiguity, unverified trace output, repeated-action loop detection, elapsed-time limit, researched-entry limit, or explicit stop request.",
          "Reverse tracing is supported by setting traceDirection to reverse before selecting or processing the candidate; the visualizer integration should not assume Column D is always the active target.",
        ],
      };
    },

    getCaseBuddyHopSolverIntegrationContract() {
      return this.getTool88CaseBuddyIntegrationContract();
    },

    getCaseBuddyAutomationGuardrails(options = {}) {
      return this.normalizeCaseBuddyAutomationGuardrails(options.guardrails || options);
    },

    emitCaseBuddyIntegrationEvent(eventName, payload = {}) {
      const event = this.toText(eventName);
      if (!event) return;
      const enriched = {
        integrationVersion: TOOL88_CASE_BUDDY_INTEGRATION_VERSION,
        emittedAtUtc: new Date().toISOString(),
        ...payload,
      };
      if (this.caseBuddyEmbedded && typeof this.$emit === "function") {
        this.$emit(event, enriched);
      }
    },

    normalizeCaseBuddyRowsForTool88(rowsOrText) {
      if (Array.isArray(rowsOrText)) {
        const normalizedRows = this.trimTrailingBlankRows(rowsOrText.map((row) => {
          if (Array.isArray(row)) return this.cloneRowPreservingValues(row);
          if (row && typeof row === "object") {
            const mapped = this.makeBlankRow();
            mapped[COL.PATH_LABEL] = row.pathLabel ?? row.label ?? row.path_label ?? "";
            mapped[COL.TX_HASH] = row.hash ?? row.txHash ?? row.transactionHash ?? row.transaction_hash ?? "";
            mapped[COL.FROM_ADDRESS] = row.fromAddress ?? row.from_address ?? row.from ?? "";
            mapped[COL.TO_ADDRESS] = row.toAddress ?? row.to_address ?? row.to ?? row.targetAddress ?? "";
            mapped[COL.DATE] = row.date ?? row.dateTime ?? row.datetime ?? row.utcDate ?? "";
            mapped[COL.AMOUNT] = row.amount ?? row.amountOfTxn ?? row.amount_of_txn ?? "";
            mapped[COL.TOKEN] = row.token ?? row.tokenType ?? row.token_type ?? row.symbol ?? "";
            mapped[COL.SYSTEM_NOTES] = row.notes ?? row.systemNotes ?? row.system_notes ?? "";
            mapped[COL.ATTRIBUTION] = row.attribution ?? row.attributionData ?? row.entity ?? row.toAttribution ?? row.to_attribution ?? "";
            mapped[COL.FROM_ATTRIBUTION] = row.fromAttribution ?? row.from_attribution ?? row.fromAttributionLabel ?? row.from_attribution_label ?? "";
            mapped[COL.USER_NOTES] = row.analystNotes ?? row.userNotes ?? row.user_notes ?? row.analyst_notes ?? "";
            mapped[COL.FUNDS_FOLLOWED] = row.fundsFollowed ?? row.funds_followed ?? row.amountFollowedText ?? row.followedAmountText ?? row.followed_amount_text ?? "";
            mapped[COL.PRIOR_BALANCE] = row.caseBuddyPriorBalance
              ?? row.priorBalanceBeforeTransaction
              ?? row.prior_balance_before_transaction
              ?? row.actualPriorBalanceAmount
              ?? row.accountPriorBalance
              ?? row.addressBalanceBeforeTransaction
              ?? row.balanceBeforeTransaction
              ?? row.balance_before_transaction
              ?? row.assetBalanceBefore
              ?? row.asset_balance_before
              ?? row.balanceBefore
              ?? row.balance_before
              ?? "";
            mapped[COL.FROM_HASH] = row.fromHash ?? row.from_hash ?? row.parentHash ?? row.parent_hash ?? row.previousHash ?? "";
            mapped[COL.FROM_LABEL] = row.fromLabel ?? row.from_label ?? row.parentLabel ?? row.parent_label ?? "";
            mapped[COL.TOOL88_NOTES] = row.tool88Notes ?? row.tool88_notes ?? row.caseNotes ?? row.case_notes ?? row.internalNotes ?? "";
            mapped[COL.CONTINUES_AS] = row.continuesAs ?? row.continues_as ?? row.continues ?? row.continuesAsText ?? "";
            mapped[COL.LEFT_BOUNDS_DISPLAY] = row.leftBoundsDisplay
              ?? row.leftBounds
              ?? row.left_bounds
              ?? row.leftBoundAmount
              ?? row.leftBoundsAmount
              ?? row.leftBound
              ?? row.leftBefore
              ?? row.fundsFollowedInfo?.leftBounds
              ?? "";
            mapped[COL.LEFT_BOUNDS_NUMERIC] = row.leftBoundsNumeric
              ?? row.leftBoundsValue
              ?? row.left_bounds_numeric
              ?? row.leftBoundAmount
              ?? row.leftBoundsAmount
              ?? row.leftBound
              ?? row.fundsFollowedInfo?.leftBounds
              ?? "";
            mapped[COL.CHAIN_ID] = row.chainId ?? row.chain_id ?? "";
            mapped[COL.CHAIN_NAME] = row.chainName ?? row.chain_name ?? "";
            mapped[COL.HOP_LOGIC] = row.hopLogic ?? row.hop_logic ?? "";
            return mapped;
          }
          return this.makeBlankRow();
        }));
        return this.caseBuddyEmbedded ? this.sanitizeCaseBuddySnapshotRows(normalizedRows) : normalizedRows;
      }

      const text = this.toText(rowsOrText);
      if (!text) return [];
      const parsed = this.parseSimpleInputTextToRows(text);
      return this.trimTrailingBlankRows(parsed.rows || []);
    },

    normalizeCaseBuddyActiveRootContext(root = null) {
      if (!root || typeof root !== "object" || Array.isArray(root)) return null;
      const pathLabel = this.toText(root.pathLabel || root.label || root.path_label).trim();
      const hash = this.toText(root.hash || root.txHash || root.transactionHash || root.transaction_hash).trim();
      const toAddress = this.toText(root.toAddress || root.targetAddress || root.to_address || root.target_address).trim();
      const rootInstanceKey = this.toText(root.rootInstanceKey || root.root_instance_key || root.id).trim();
      if (!pathLabel && !hash && !toAddress && !rootInstanceKey) return null;
      return {
        rootInstanceKey,
        pathLabel,
        hash,
        toAddress,
        rowIndex: Number.isFinite(Number(root.rowIndex)) ? Number(root.rowIndex) : null,
        spreadsheetRowNumber: Number.isFinite(Number(root.spreadsheetRowNumber)) ? Number(root.spreadsheetRowNumber) : null,
        duplicateHash: root.duplicateHash === true,
        exclusive: root.exclusive !== false,
      };
    },

    caseBuddyActiveRootLabel() {
      const root = this.caseBuddyActiveRootContext || null;
      if (!root) return "";
      if (this.toText(root.pathLabel)) return `Path ${this.toText(root.pathLabel)}`;
      if (this.toText(root.hash)) return this.shortenMiddle(this.toText(root.hash), 10, 8);
      return this.toText(root.rootInstanceKey || "");
    },

    caseBuddyHasActiveRootContextOption(options = {}) {
      if (!options || typeof options !== "object" || Array.isArray(options)) return false;
      return [
        "caseBuddyActiveRoot",
        "workflowRawDataRoot",
        "activeRoot",
        "rootContext",
      ].some((key) => Object.prototype.hasOwnProperty.call(options, key));
    },

    applyCaseBuddyPreferredSettings(options = {}) {
      // The automation loop calls this method for every hop. Preserve the root
      // identity unless a caller explicitly supplies a replacement (including
      // null to clear it); otherwise progress would lose Path N after step one.
      if (this.caseBuddyHasActiveRootContextOption(options)) {
        const suppliedRoot = options.caseBuddyActiveRoot
          ?? options.workflowRawDataRoot
          ?? options.activeRoot
          ?? options.rootContext
          ?? null;
        this.caseBuddyActiveRootContext = this.normalizeCaseBuddyActiveRootContext(suppliedRoot);
        if (this.caseBuddyActiveRootContext && options.caseBuddyActiveRootExclusive !== undefined) {
          this.caseBuddyActiveRootContext = {
            ...this.caseBuddyActiveRootContext,
            exclusive: options.caseBuddyActiveRootExclusive !== false,
          };
        }
      }

      const direction = this.toText(options.traceDirection || options.direction).toLowerCase();
      if (direction === "forward" || direction === "reverse") {
        this.traceDirection = direction;
      }

      const hopLogic = this.toText(options.hopLogicType || options.hopLogic || options.hop_logic).toUpperCase();
      if (["FIFO", "LIFO", "NEXT_CHRONOLOGICAL"].includes(hopLogic)) {
        this.hopLogicType = hopLogic;
      }

      if (options.enableDepositEntityHopExtension !== undefined) {
        this.enableDepositEntityHopExtension = options.enableDepositEntityHopExtension === true;
      }

      if (options.enableAdditionalServiceChecks !== undefined) {
        this.enableAdditionalServiceChecks = options.enableAdditionalServiceChecks !== false;
      }

      const forceStrictNativeFifo = options.caseBuddyForceStrictNativeFifo === true
        || options.strictFifoNativeSpend === true
        || options.disableNativeDirectSpendMatchGuard === true;
      if (forceStrictNativeFifo) {
        this.caseBuddyNativeDirectSpendGuardForcedOff = true;
        this.enableNativeDirectSpendMatchGuard = false;
      } else if (Object.prototype.hasOwnProperty.call(options, 'enableNativeDirectSpendMatchGuard')) {
        this.caseBuddyNativeDirectSpendGuardForcedOff = false;
        this.enableNativeDirectSpendMatchGuard = options.enableNativeDirectSpendMatchGuard === true;
      } else if (options.clearCaseBuddyNativeDirectSpendGuardOverride === true) {
        this.caseBuddyNativeDirectSpendGuardForcedOff = false;
      }

      if (Object.prototype.hasOwnProperty.call(options, 'caseBuddyLowValueRuleEnabled')) {
        this.caseBuddyLowValueRuleEnabledOverride = typeof options.caseBuddyLowValueRuleEnabled === 'boolean'
          ? options.caseBuddyLowValueRuleEnabled
          : null;
        this.caseBuddyLowValueRulePolicy = options.caseBuddyLowValuePolicy || options.caseBuddyLiteLowValuePolicy || null;
        this.flowCompletionScanResult = null;
      } else if (Object.prototype.hasOwnProperty.call(options, 'flowCompletionValueTooLowEnabled')) {
        this.caseBuddyLowValueRuleEnabledOverride = typeof options.flowCompletionValueTooLowEnabled === 'boolean'
          ? options.flowCompletionValueTooLowEnabled
          : null;
        this.caseBuddyLowValueRulePolicy = options.caseBuddyLowValuePolicy || options.caseBuddyLiteLowValuePolicy || null;
        this.flowCompletionScanResult = null;
      }

      if (options.clearCaseBuddyLowValueRuleOverride === true) {
        this.caseBuddyLowValueRuleEnabledOverride = null;
        this.caseBuddyLowValueRulePolicy = null;
        this.flowCompletionScanResult = null;
      }

      if (Object.prototype.hasOwnProperty.call(options, "caseBuddyLowValuePolicy")) {
        const policy = options.caseBuddyLowValuePolicy;
        this.caseBuddyLowValuePolicyOverride = policy && typeof policy === "object" && !Array.isArray(policy)
          ? { ...policy }
          : null;
      } else if (options.clearCaseBuddyLowValuePolicy === true) {
        this.caseBuddyLowValuePolicyOverride = null;
      }
    },

    async caseBuddyLoadRowsForHopSolver(rowsOrText, options = {}) {
      if (this.isProcessing) {
        return this.buildCaseBuddyHopSolverSnapshot({
          includeRows: options.includeRows,
          status: "busy",
          message: "Tool 88 is already processing a hop.",
        });
      }

      if (options.resetCaseBuddyStopState === true || !this.caseBuddyIntegrationRunState?.active) {
        this.caseBuddyClearStaleStopState(options.resetCaseBuddyStopReason || 'Case Buddy loaded fresh rows for Tool 88.');
      }
      // A fresh manual load without workflow-root metadata starts a context-free
      // run. Loads from Case Buddy include an explicit root and keep it stable.
      if (!this.caseBuddyHasActiveRootContextOption(options)) {
        this.caseBuddyActiveRootContext = null;
      }
      this.applyCaseBuddyPreferredSettings(options);
      const rows = this.normalizeCaseBuddyRowsForTool88(rowsOrText);
      if (!rows.length || !rows.some((row) => this.rowHasAnyData(row))) {
        const snapshot = this.buildCaseBuddyHopSolverSnapshot({
          includeRows: false,
          status: "empty",
          message: "No Case Buddy rows were provided to Tool 88.",
        });
        this.emitCaseBuddyIntegrationEvent("case-buddy-status", snapshot);
        return snapshot;
      }

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.simpleInputText = "";
      this.simpleLastLoadedInputText = "";
      this.simpleRecommendationApplied = false;
      this.simpleFlowActionReady = false;
      this.flowCompletionDepth = 0;
      this.lastFlowSelectionResult = null;
      this.flowCompletionScanResult = null;
      this.flowCompletionMessage = "";
      this.resetDecisionState();
      await this.$nextTick();

      if (options.selectedHop || options.candidate) {
        return this.caseBuddyOpenSelectedHop(options.selectedHop || options.candidate, {
          ...options,
          rowsAlreadyLoaded: true,
        });
      }

      if (options.selectNextUnresolved !== false) {
        return this.caseBuddySelectNextUnresolvedHop({ ...options, rowsAlreadyLoaded: true });
      }

      await this.processData();
      const snapshot = this.buildCaseBuddyHopSolverSnapshot({
        includeRows: options.includeRows,
        status: "loaded",
        message: "Rows loaded and Tool 88 checks were run.",
      });
      this.emitCaseBuddyIntegrationEvent("case-buddy-status", snapshot);
      return snapshot;
    },

    getCaseBuddyCandidateDirectTargetAddress(candidate = {}) {
      const directionalAddress = this.toText(
        this.traceDirection === "reverse"
          ? (candidate.fromAddress || candidate.from_address)
          : (candidate.toAddress || candidate.to_address)
      );
      const explicitlySelected = this.toText(candidate.selectedTargetAddress || candidate.selected_target_address || "");
      if (directionalAddress) return directionalAddress;
      if (explicitlySelected) return explicitlySelected;
      return this.toText(candidate.targetAddress || candidate.target_address || candidate.address || "");
    },

    caseBuddyEntryMatchesHopCandidateIdentity(entry = null, candidate = {}, options = {}) {
      if (!entry) return false;
      const hashKey = this.normalizeHashKey(candidate.hash || candidate.txHash || candidate.transactionHash || candidate.transaction_hash);
      const pathLabel = this.toText(candidate.pathLabel || candidate.label || candidate.path_label).trim().toLowerCase();
      const targetAddressKey = this.normalizeAddressKey(this.getCaseBuddyCandidateDirectTargetAddress(candidate));
      const requireAnyIdentity = options.requireAnyIdentity === true;
      let checked = 0;

      if (hashKey) {
        checked += 1;
        if (this.normalizeHashKey(entry.txHash) !== hashKey) return false;
      }

      if (pathLabel) {
        checked += 1;
        const entryLabel = this.toText(entry.pathLabel).trim().toLowerCase();
        if (entryLabel !== pathLabel) return false;
      }

      if (targetAddressKey) {
        checked += 1;
        const entryTargetKeys = new Set(
          this.collectTargetCandidates(entry)
            .map((target) => this.normalizeAddressKey(target?.address || ''))
            .filter(Boolean)
        );
        entry?.rows?.forEach(({ row }) => {
          const directTarget = this.normalizeAddressKey(row?.[this.getTargetColumnIndex?.() ?? COL.TO_ADDRESS]);
          if (directTarget) entryTargetKeys.add(directTarget);
        });
        if (entryTargetKeys.size && !entryTargetKeys.has(targetAddressKey)) return false;
      }

      return checked > 0 || !requireAnyIdentity;
    },

    findCaseBuddyEntryForHopCandidate(candidate = {}, entries = []) {
      const rowIndexRaw = candidate.rowIndex ?? candidate.startRowIndex ?? candidate.sourceRowIndex;
      const rowNumberRaw = candidate.spreadsheetRowNumber ?? candidate.rowNumber ?? candidate.sourceRowNumber;
      const zeroBasedRowIndex = Number.isInteger(rowIndexRaw)
        ? rowIndexRaw
        : (Number.isFinite(Number(rowIndexRaw)) ? Math.trunc(Number(rowIndexRaw)) : null);
      const oneBasedRowIndex = Number.isInteger(rowNumberRaw)
        ? rowNumberRaw - 1
        : (Number.isFinite(Number(rowNumberRaw)) ? Math.trunc(Number(rowNumberRaw)) - 1 : null);
      const candidateRowIndex = Number.isInteger(zeroBasedRowIndex) ? zeroBasedRowIndex : oneBasedRowIndex;
      const pathLabel = this.toText(candidate.pathLabel || candidate.label || candidate.path_label).trim();
      const pathLabelKey = pathLabel.toLowerCase();
      const hashKey = this.normalizeHashKey(candidate.hash || candidate.txHash || candidate.transactionHash || candidate.transaction_hash);

      if (Number.isInteger(candidateRowIndex) && candidateRowIndex >= 0) {
        const rowMatched = entries.find((entry) => {
          const start = Number(entry.startRowIndex);
          const end = Number(entry.endRowIndex);
          return Number.isFinite(start) && Number.isFinite(end) && candidateRowIndex >= start && candidateRowIndex <= end;
        });
        if (rowMatched && this.caseBuddyEntryMatchesHopCandidateIdentity(rowMatched, candidate, { requireAnyIdentity: true })) return rowMatched;
        if (rowMatched && !hashKey && !pathLabelKey && !this.getCaseBuddyCandidateDirectTargetAddress(candidate)) return rowMatched;
      }

      if (hashKey && pathLabelKey) {
        const exact = entries.find((entry) =>
          this.normalizeHashKey(entry.txHash) === hashKey && this.toText(entry.pathLabel).trim().toLowerCase() === pathLabelKey
        );
        if (exact) return exact;
      }

      if (hashKey) {
        const hashMatches = entries.filter((entry) => this.normalizeHashKey(entry.txHash) === hashKey);
        if (hashMatches.length === 1) return hashMatches[0];
        if (hashMatches.length > 1) {
          const directTargetKey = this.normalizeAddressKey(this.getCaseBuddyCandidateDirectTargetAddress(candidate));
          if (directTargetKey) {
            const explicitTargetMatches = hashMatches.filter((entry) => {
              const entryTargetKeys = new Set(
                this.collectTargetCandidates(entry)
                  .map((target) => this.normalizeAddressKey(target?.address || ''))
                  .filter(Boolean)
              );
              entry?.rows?.forEach(({ row }) => {
                const directTarget = this.normalizeAddressKey(row?.[this.getTargetColumnIndex?.() ?? COL.TO_ADDRESS]);
                if (directTarget) entryTargetKeys.add(directTarget);
              });
              return entryTargetKeys.has(directTargetKey);
            });
            if (explicitTargetMatches.length === 1) return explicitTargetMatches[0];
            if (pathLabelKey && explicitTargetMatches.length > 1) {
              const targetAndPath = explicitTargetMatches.filter((entry) =>
                this.toText(entry.pathLabel).trim().toLowerCase() === pathLabelKey
              );
              if (targetAndPath.length === 1) return targetAndPath[0];
            }
          }
          const exactIdentityMatches = hashMatches.filter((entry) =>
            this.caseBuddyEntryMatchesHopCandidateIdentity(entry, candidate, { requireAnyIdentity: true })
          );
          if (exactIdentityMatches.length === 1) return exactIdentityMatches[0];
          if (pathLabelKey) {
            const exactPathMatches = exactIdentityMatches.filter((entry) =>
              this.toText(entry.pathLabel).trim().toLowerCase() === pathLabelKey
            );
            if (exactPathMatches.length === 1) return exactPathMatches[0];
          }
          // A path label or selected target is an explicit root-instance
          // discriminator. Never silently use the first same-hash entry when it
          // disagrees; that merges two investigator-defined BTC output flows.
          if (pathLabelKey || directTargetKey) return null;
          return hashMatches[0];
        }
      }

      if (pathLabelKey) {
        const labelMatches = entries.filter((entry) => this.toText(entry.pathLabel).trim().toLowerCase() === pathLabelKey);
        if (labelMatches.length === 1) return labelMatches[0];
        const identityMatch = labelMatches.find((entry) => this.caseBuddyEntryMatchesHopCandidateIdentity(entry, candidate, { requireAnyIdentity: true }));
        if (identityMatch) return identityMatch;
        if (labelMatches.length > 1) return labelMatches[0];
      }

      return null;
    },

    getCaseBuddyCandidateTargetAddress(candidate = {}, entry = null) {
      const preferred = this.getCaseBuddyCandidateDirectTargetAddress(candidate);
      if (preferred) return preferred;
      const candidates = entry ? this.collectTargetCandidates(entry) : [];
      return candidates.length === 1 ? candidates[0].address : "";
    },

    async caseBuddyOpenSelectedHop(candidate = {}, options = {}) {
      if (this.isProcessing) {
        return this.buildCaseBuddyHopSolverSnapshot({
          includeRows: options.includeRows,
          status: "busy",
          message: "Tool 88 is already processing a hop.",
        });
      }

      if (options.resetCaseBuddyStopState === true || !this.caseBuddyIntegrationRunState?.active) {
        this.caseBuddyClearStaleStopState(options.resetCaseBuddyStopReason || 'Case Buddy opened a fresh selected hop.');
      }

      if (!options.rowsAlreadyLoaded && (options.rows || options.tsv || options.text)) {
        return this.caseBuddyLoadRowsForHopSolver(options.rows || options.tsv || options.text, {
          ...options,
          selectedHop: candidate,
        });
      }

      this.applyCaseBuddyPreferredSettings({ ...options, ...candidate });
      const parsed = this.parseInputRowsForFlowCompletion();
      if (!parsed.rows.length || !parsed.rows.some((row) => this.rowHasAnyData(row))) {
        const snapshot = this.buildCaseBuddyHopSolverSnapshot({
          includeRows: false,
          status: "empty",
          message: "Tool 88 has no loaded Case Buddy rows to select from.",
        });
        this.emitCaseBuddyIntegrationEvent("case-buddy-status", snapshot);
        return snapshot;
      }

      const entry = this.findCaseBuddyEntryForHopCandidate(candidate, parsed.entries || []);
      if (!entry) {
        const snapshot = this.buildCaseBuddyHopSolverSnapshot({
          includeRows: false,
          status: "not-found",
          message: "The selected Case Buddy path ending could not be matched to a Tool 88 entry.",
        });
        this.emitCaseBuddyIntegrationEvent("case-buddy-status", snapshot);
        return snapshot;
      }

      const selectedTargetAddress = this.getCaseBuddyCandidateTargetAddress(candidate, entry);
      this.isRefreshingSelections = true;
      this.inputSnapshotRows = parsed.rows.map((row) => this.cloneRowPreservingValues(row));
      this.outputBaseRows = this.inputSnapshotRows.map((row) => this.cloneRowPreservingValues(row));
      this.headerDetected = parsed.headerDetected;
      this.dataStartIndex = parsed.dataStartIndex;
      this.parsedEntries = parsed.entries;
      this.selectedEntryKey = entry.key;
      this.targetCandidates = this.collectTargetCandidates(entry);
      this.selectedTargetAddress = selectedTargetAddress;
      this.isRefreshingSelections = false;
      this.simpleRecommendationApplied = false;
      this.simpleFlowActionReady = false;
      this.traceResult = null;
      this.dormantResult = null;
      this.pathEndingResult = null;
      this.traceTransactionPayload = null;
      this.caseBuddySelectedOutputInferenceBlock = null;
      await this.$nextTick();

      if (!selectedTargetAddress && this.targetCandidates.length > 1) {
        const snapshot = this.buildCaseBuddyHopSolverSnapshot({
          includeRows: options.includeRows,
          status: "target-ambiguity",
          message: "The selected path ending has multiple candidate target addresses. Case Buddy should ask the user which address to solve.",
        });
        this.emitCaseBuddyIntegrationEvent("case-buddy-status", snapshot);
        return snapshot;
      }

      await this.processData({
        entryKey: entry.key,
        selectedTargetAddress,
      });

      const snapshot = this.buildCaseBuddyHopSolverSnapshot({
        includeRows: options.includeRows,
        status: "selected-hop-ready",
        message: "Selected path ending loaded and Tool 88 checks are ready.",
      });
      this.emitCaseBuddyIntegrationEvent("case-buddy-status", snapshot);
      this.scrollCaseBuddyEmbeddedSimpleViewToTop();
      return snapshot;
    },

    async caseBuddySelectNextUnresolvedHop(options = {}) {
      if (this.isProcessing) {
        return this.buildCaseBuddyHopSolverSnapshot({
          includeRows: options.includeRows,
          status: "busy",
          message: "Tool 88 is already processing a hop.",
        });
      }

      if (!options.rowsAlreadyLoaded && (options.rows || options.tsv || options.text)) {
        return this.caseBuddyLoadRowsForHopSolver(options.rows || options.tsv || options.text, {
          ...options,
          selectNextUnresolved: true,
        });
      }

      this.applyCaseBuddyPreferredSettings(options);
      const selectionResult = await this.selectNextUnresolvedEnding({
        source: "case-buddy-select-next",
        caseBuddyAutomation: true,
        ignoreManualDepthLimit: true,
      });
      const snapshot = this.buildCaseBuddyHopSolverSnapshot({
        includeRows: options.includeRows,
        status: this.flowCompletionResult?.done
          ? "flow-complete"
          : (selectionResult?.selected ? "next-unresolved-selected" : (selectionResult?.blocked ? "guardrail-paused" : "no-selection")),
        guardrail: selectionResult?.blocked ? {
          reason: selectionResult.reason || "selection-blocked",
          message: selectionResult.message || this.flowCompletionMessage || "Tool 88 could not select the next unresolved ending.",
        } : null,
        message: selectionResult?.message || this.flowCompletionMessage || "Next unresolved path ending selected.",
      });
      if (snapshot.flowComplete) this.emitCaseBuddyIntegrationEvent("case-buddy-flow-complete", snapshot);
      else this.emitCaseBuddyIntegrationEvent("case-buddy-status", snapshot);
      return snapshot;
    },

    async caseBuddySettleImmediateAutoEndings(options = {}) {
      if (this.isProcessing) {
        return this.buildCaseBuddyHopSolverSnapshot({
          includeRows: options.includeRows,
          status: "busy",
          message: "Tool 88 is already processing a hop.",
        });
      }

      this.applyCaseBuddyPreferredSettings(options);
      const maxAutoLowValue = Math.max(1, Math.min(250, Number(options.maxAutoLowValue || 50) || 50));
      let settledCount = 0;
      let lastSelectionResult = null;

      // This method is intentionally network-free. It only commits endings that
      // the flow scanner has already classified as safe, deterministic low-value
      // endings. Case Buddy calls it when a timed automation segment stops, so a
      // leaf that needs no vendor lookup cannot consume another five-minute
      // continuation segment or be left behind as a false unresolved ending.
      for (let pass = 0; pass < maxAutoLowValue; pass += 1) {
        const parsed = this.parseInputRowsForFlowCompletion();
        const completion = this.evaluateFlowCompletion(parsed);
        this.flowCompletionScanResult = completion;
        if (completion.done || !completion.incomplete.length) break;

        const next = completion.incomplete[0];
        if (!next?.autoMarkable || !next?.lowValue) break;

        lastSelectionResult = await this.selectNextUnresolvedEnding({
          source: "case-buddy-budget-boundary-settle",
          caseBuddyAutomation: true,
          ignoreManualDepthLimit: true,
        });
        await this.$nextTick();

        if (!lastSelectionResult?.selected || !this.isSimpleLowValueAutoResultReady()) break;
        this.absorbOutputIntoInput();
        await this.$nextTick();
        settledCount += 1;
      }

      const finalParsed = this.parseInputRowsForFlowCompletion();
      const finalCompletion = this.evaluateFlowCompletion(finalParsed);
      this.flowCompletionScanResult = finalCompletion;
      const nextIncomplete = finalCompletion.incomplete?.[0] || null;
      const message = settledCount
        ? `Tool 88 finalized ${this.formatInteger(settledCount)} deterministic low-value ending${settledCount === 1 ? "" : "s"} at the automation-segment boundary. ${finalCompletion.summary}`
        : `No deterministic low-value ending was ready at the automation-segment boundary. ${finalCompletion.summary}`;
      this.flowCompletionMessage = message;
      this.simpleViewMessage = message;
      this.reportText = finalCompletion.report;

      const snapshot = this.buildCaseBuddyHopSolverSnapshot({
        includeRows: options.includeRows,
        status: finalCompletion.done
          ? "flow-complete"
          : (settledCount ? "budget-boundary-auto-settled" : "budget-boundary-no-auto-ending"),
        integrationAction: "settle-immediate-auto-endings",
        message,
      });
      snapshot.boundarySettlement = {
        settledCount,
        maxAutoLowValue,
        flowComplete: finalCompletion.done === true,
        unresolvedCount: Number(finalCompletion.incompleteCount || 0),
        nextEntryKey: this.toText(nextIncomplete?.entryKey || nextIncomplete?.entry?.key || ""),
        nextTxHash: this.toText(nextIncomplete?.txHash || nextIncomplete?.entry?.txHash || ""),
        nextTargetAddress: this.toText(nextIncomplete?.targetAddress || ""),
        nextAutoMarkable: nextIncomplete?.autoMarkable === true,
        lastSelectionResult,
      };

      this.caseBuddyIntegrationLastSnapshot = snapshot;
      this.emitCaseBuddyIntegrationEvent(
        snapshot.flowComplete ? "case-buddy-flow-complete" : "case-buddy-progress",
        snapshot
      );
      return snapshot;
    },

    async caseBuddyApplyCurrentRecommendation(options = {}) {
      if (this.isProcessing) {
        return this.buildCaseBuddyHopSolverSnapshot({
          includeRows: options.includeRows,
          status: "busy",
          message: "Tool 88 is already processing a hop.",
        });
      }
      if (!this.countLookupResult) {
        await this.processData({
          entryKey: this.selectedEntryKey,
          selectedTargetAddress: this.selectedTargetAddress,
        });
      }
      await this.simpleApplyRecommendation(options);
      const snapshot = this.buildCaseBuddyHopSolverSnapshot({
        includeRows: options.includeRows,
        status: this.simpleResultReadyForContinue ? "decision-ready" : "manual-review",
        message: this.simpleViewMessage || "Tool 88 recommendation pass completed.",
      });
      this.emitCaseBuddyIntegrationEvent(snapshot.needsManualReview ? "case-buddy-guardrail" : "case-buddy-result", snapshot);
      return snapshot;
    },

    caseBuddyAllowsUnverifiedTraceContinuation(options = {}) {
      return Boolean(
        options.acceptUnverifiedTraceOutput === true ||
        options.allowUnverifiedTraceOutput === true ||
        options.caseWorkflowRawData === true ||
        options.caseBuddyRawDataAutomation === true ||
        options.rawDataAutomation === true
      );
    },

    normalizeCaseBuddyTraceRowsForSelectedParent(rows = [], entry = this.selectedEntry) {
      const parentHash = this.toText(entry?.txHash || entry?.row?.[COL.TX_HASH] || '').trim();
      const parentKey = this.normalizeHashKey(parentHash);
      const parentPathLabel = this.toText(entry?.pathLabel || entry?.row?.[COL.PATH_LABEL] || '').trim();
      const selectedTargetKey = this.normalizeAddressKey(this.selectedTargetAddress);
      const reverse = this.traceDirection === 'reverse';
      const traceRows = this.getCaseBuddyExportableTraceRows(rows);
      if (!parentKey) return traceRows;
      return traceRows.map((row) => {
        const next = this.cloneRowPreservingValues(row);
        const childHashKey = this.normalizeHashKey(next?.[COL.TX_HASH]);
        if (!childHashKey || childHashKey === parentKey) return next;
        const childConnectionKey = this.normalizeAddressKey(
          next?.[reverse ? COL.TO_ADDRESS : COL.FROM_ADDRESS]
        );
        // In a selected-hop trace-through, direct child rows belong under the
        // selected transaction occurrence even when the same on-chain child
        // already exists under a sibling path. Forward connects through the
        // child's From Address; Reverse connects through the child's To Address.
        if (selectedTargetKey && childConnectionKey === selectedTargetKey) {
          next[COL.FROM_HASH] = parentHash;
          if (parentPathLabel) next[COL.FROM_LABEL] = parentPathLabel;
        }
        return next;
      });
    },

    caseBuddyHasUnverifiedTraceOutputForContinuation() {
      if (!this.traceResult || this.traceResult.preventPathEnding) return false;
      const traceRows = this.normalizeCaseBuddyTraceRowsForSelectedParent(this.traceResult.exitRows || []);
      if (!traceRows.length) return false;

      // Embedded Case Buddy runs frequently receive a correct trace result before
      // the visible output spreadsheet reports a verified mutation. Rebuild the
      // complete transition idempotently: retain each solved child row once and,
      // critically, move the selected parent from "Path ends" to "Continues as".
      // Merely seeing a child row is not enough because the old behavior could
      // absorb that row while leaving the parent unresolved, causing the same hop
      // to be selected again and appended indefinitely.
      const recoveredRows = this.buildCaseBuddySnapshotOutputRows();
      if (!recoveredRows.some((row) => this.rowHasAnyData(row))) return false;
      const continuation = this.caseBuddyTraceContinuationState(recoveredRows, traceRows);
      if (!continuation.ok) return false;

      this.loadOutputRows(recoveredRows);
      this.traceResult = {
        ...this.traceResult,
        outputMutationVerified: true,
        outputMutationVerification: {
          ...continuation.rowVerification,
          ok: true,
          recoveredForCaseBuddyAutomation: true,
          parentContinuationVerified: true,
          selectedParentRowIndex: continuation.parentRowIndex,
          expectedChildHashes: continuation.expectedChildHashes,
          referencedChildHashes: continuation.referencedChildHashes,
        },
      };
      this.pushUniqueWarnings([
        "Case Buddy recovered Tool 88's solved trace row(s) and verified the selected parent's continuation before absorbing the output.",
      ]);
      return true;
    },

    caseBuddyCanUseOutputAndSelectNext(options = {}) {
      return Boolean(
        this.simpleResultReadyForContinue ||
        (this.caseBuddyAllowsUnverifiedTraceContinuation(options) && this.caseBuddyHasUnverifiedTraceOutputForContinuation())
      );
    },

    async caseBuddyUseOutputAndSelectNext(options = {}) {
      if (this.isProcessing) {
        return this.buildCaseBuddyHopSolverSnapshot({
          includeRows: options.includeRows,
          status: "busy",
          message: "Tool 88 is already processing a hop.",
        });
      }
      const usingVerifiedOutput = this.simpleResultReadyForContinue === true;
      const usingAcceptedUnverifiedOutput = !usingVerifiedOutput && this.caseBuddyCanUseOutputAndSelectNext(options);
      if (!usingVerifiedOutput && !usingAcceptedUnverifiedOutput) {
        return this.buildCaseBuddyHopSolverSnapshot({
          includeRows: options.includeRows,
          status: "not-ready",
          message: this.caseBuddyAllowsUnverifiedTraceContinuation(options)
            ? "No exportable Tool 88 output is ready to absorb into Case Buddy input."
            : "No verified Tool 88 output is ready to absorb into Case Buddy input.",
        });
      }
      if (usingVerifiedOutput) {
        if (!this.ensureSimpleTraceOutputCanAdvance()) {
          return this.buildCaseBuddyHopSolverSnapshot({
            includeRows: options.includeRows,
            status: "unverified-output",
            message: this.simpleViewMessage || "Tool 88 output insertion was not verified.",
          });
        }
      } else {
        this.pushUniqueWarnings([
          "Case Buddy raw-data automation accepted Tool 88 snapshot output rows even though visual output insertion verification did not pass.",
        ]);
        this.simpleViewMessage = "Using Tool 88 snapshot output rows and looking for the next unresolved ending...";
      }
      this.simpleFlowActionReady = false;
      this.absorbOutputIntoInput();
      await this.$nextTick();
      let advanceResult = null;
      if (this.flowCompletionScanResult?.done) {
        this.simpleRecommendationApplied = false;
        this.simpleViewMessage = this.simpleViewMessage || "Output absorbed. Tool 88 sees this flow as complete.";
      } else {
        advanceResult = await this.simpleAdvanceToNextActionableEnding({
          source: usingAcceptedUnverifiedOutput ? "accepted-unverified-output" : "absorb",
          caseBuddyAutomation: true,
          ignoreManualDepthLimit: true,
          maxAutoLowValue: options.maxAutoLowValue || TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.maxAutoLowValue,
        });
      }
      const selectionGuardrail = advanceResult?.selectionBlocked ? {
        reason: advanceResult.guardrailReason || advanceResult.selectionResult?.reason || "selection-blocked",
        message: advanceResult.selectionResult?.message || this.simpleViewMessage || this.flowCompletionMessage || "Tool 88 could not select the next unresolved ending.",
      } : null;
      const snapshotStatus = this.flowCompletionResult?.done
        ? "flow-complete"
        : (selectionGuardrail
          ? "guardrail-paused"
          : (advanceResult?.needsTarget
            ? "target-ambiguity"
            : (advanceResult?.empty ? "empty" : "next-unresolved-selected")));
      const snapshot = this.buildCaseBuddyHopSolverSnapshot({
        includeRows: options.includeRows,
        status: snapshotStatus,
        integrationAction: usingAcceptedUnverifiedOutput ? "absorb-unverified-output-select-next" : "absorb-output-select-next",
        guardrail: selectionGuardrail,
        message: this.simpleViewMessage || this.flowCompletionMessage || "Output absorbed and next unresolved ending selected.",
      });
      if (snapshot.flowComplete) this.emitCaseBuddyIntegrationEvent("case-buddy-flow-complete", snapshot);
      else this.emitCaseBuddyIntegrationEvent("case-buddy-status", snapshot);
      return snapshot;
    },

    async caseBuddyRunNextAction(options = {}) {
      if (this.isProcessing) {
        return this.buildCaseBuddyHopSolverSnapshot({
          includeRows: options.includeRows,
          status: "busy",
          integrationAction: "busy",
          message: "Tool 88 is already processing a hop.",
        });
      }

      if (options.rows || options.tsv || options.text) {
        return this.caseBuddyLoadRowsForHopSolver(options.rows || options.tsv || options.text, {
          ...options,
          selectNextUnresolved: options.selectNextUnresolved !== false,
        });
      }

      this.applyCaseBuddyPreferredSettings(options);
      let integrationAction = "review";
      let actionGuardrail = null;
      let forceManualReview = false;

      if (this.caseBuddyCanUseOutputAndSelectNext(options)) {
        integrationAction = this.simpleResultReadyForContinue ? "absorb-output-select-next" : "absorb-unverified-output-select-next";
        const continuationSnapshot = await this.caseBuddyUseOutputAndSelectNext({ ...options, includeRows: false });
        if (continuationSnapshot?.guardrail) {
          actionGuardrail = { ...continuationSnapshot.guardrail };
          integrationAction = continuationSnapshot.integrationAction || integrationAction;
        }
        forceManualReview = continuationSnapshot?.needsManualReview === true;
      } else if (this.flowCompletionResult?.done) {
        integrationAction = "flow-complete";
      } else if (this.simpleHasTargetAmbiguity) {
        integrationAction = "target-ambiguity";
      } else if (this.countLookupResult && this.autoDecisionPreview && this.autoDecisionPreview.action !== "review") {
        const recommendedAction = this.toText(this.autoDecisionPreview.action || "recommendation");
        integrationAction = `apply-${recommendedAction}`;
        await this.simpleApplyRecommendation(options);
        if (this.pathEndingResult?.autoBridgeTerminalFallback === true) {
          integrationAction = "apply-bridge-terminal-fallback";
        } else if (this.caseBuddyCurrentResultIsPotentialServicePathEnding()) {
          integrationAction = "apply-path_end";
        }
        const canAdvance = this.flowCompletionResult?.done === true || this.caseBuddyCanUseOutputAndSelectNext(options);
        if (!canAdvance) {
          const selected = this.buildCaseBuddySelectedEntrySummary();
          const traceExitRows = this.normalizeCaseBuddyTraceRowsForSelectedParent(this.traceResult?.exitRows || [], this.selectedEntry);
          const verification = this.traceResult?.outputMutationVerification || null;
          const selectedLabel = this.toText(selected?.pathLabel || selected?.shortHash || selected?.txHash || "the selected ending");
          const missing = Array.isArray(verification?.missing) && verification.missing.length
            ? ` Missing verification: ${verification.missing.slice(0, 4).join(", ")}.`
            : "";
          const resultDetail = traceExitRows.length
            ? `Tool 88 found ${this.formatInteger(traceExitRows.length)} trace row${traceExitRows.length === 1 ? "" : "s"}, but could not retain a branch-specific exportable output row.`
            : `The ${recommendedAction.replace(/[_-]+/g, " ")} recommendation did not produce an exportable output row.`;
          const message = `${resultDetail} Auto paused at ${selectedLabel} instead of retrying the same ending indefinitely.${missing}`;
          integrationAction = `apply-${recommendedAction}-no-output`;
          actionGuardrail = {
            reason: traceExitRows.length ? "trace-output-not-exportable" : "recommendation-produced-no-output",
            message,
          };
          forceManualReview = true;
          this.simpleViewMessage = message;
        }
      } else if (this.countLookupResult && this.autoDecisionPreview?.action === "review") {
        integrationAction = "manual-review";
      } else if (this.selectedEntryKey && this.selectedTargetAddress) {
        integrationAction = "run-checks";
        await this.processData({
          entryKey: this.selectedEntryKey,
          selectedTargetAddress: this.selectedTargetAddress,
        });
      } else {
        integrationAction = "select-next-unresolved";
        const advanceResult = await this.simpleAdvanceToNextActionableEnding({
          source: "case-buddy-next",
          caseBuddyAutomation: true,
          ignoreManualDepthLimit: true,
          maxAutoLowValue: options.maxAutoLowValue || TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.maxAutoLowValue,
        });
        if (advanceResult?.selectionBlocked) {
          integrationAction = "selection-blocked";
          actionGuardrail = {
            reason: advanceResult.guardrailReason || advanceResult.selectionResult?.reason || "selection-blocked",
            message: advanceResult.selectionResult?.message || this.simpleViewMessage || this.flowCompletionMessage || "Tool 88 could not select the next unresolved ending.",
          };
        } else if (advanceResult?.needsTarget) {
          integrationAction = "target-ambiguity";
        } else if (advanceResult?.empty) {
          integrationAction = "empty";
        }
      }

      const snapshotStatus = actionGuardrail
        ? (/no-output$/i.test(integrationAction) ? "recommendation-no-output" : "guardrail-paused")
        : this.deriveCaseBuddySnapshotStatus(integrationAction);
      const snapshot = this.buildCaseBuddyHopSolverSnapshot({
        includeRows: options.includeRows,
        status: snapshotStatus,
        integrationAction,
        guardrail: actionGuardrail,
        needsManualReview: forceManualReview,
        message: this.simpleViewMessage || this.flowCompletionMessage || this.statusMessage || "Tool 88 Case Buddy action completed.",
      });
      this.caseBuddyIntegrationLastSnapshot = snapshot;
      this.emitCaseBuddyIntegrationEvent(snapshot.flowComplete ? "case-buddy-flow-complete" : (snapshot.needsManualReview || snapshot.guardrail ? "case-buddy-guardrail" : "case-buddy-result"), snapshot);
      this.scrollCaseBuddyEmbeddedSimpleViewToTop();
      return snapshot;
    },

    normalizeCaseBuddyAutomationGuardrails(guardrails = {}) {
      const merged = {
        ...TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS,
        ...(guardrails || {}),
      };
      const numeric = (key, fallback, min, max) => {
        const number = Number(merged[key]);
        if (!Number.isFinite(number)) return fallback;
        return Math.max(min, Math.min(max, Math.trunc(number)));
      };
      const decimal = (key, fallback, min, max) => {
        const number = Number(merged[key]);
        if (!Number.isFinite(number)) return fallback;
        return Math.max(min, Math.min(max, number));
      };
      const speedMultiplier = decimal("speedMultiplier", TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.speedMultiplier, 0.5, 3);
      const baseDelayMs = numeric("delayMs", TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.delayMs, 0, 60000);
      return {
        enabled: merged.enabled !== false,
        maxElapsedMs: numeric("maxElapsedMs", TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.maxElapsedMs, 1000, 24 * 60 * 60 * 1000),
        maxEntriesResearched: numeric("maxEntriesResearched", TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.maxEntriesResearched, 1, 100000),
        maxLoopSteps: numeric("maxLoopSteps", TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.maxLoopSteps, 1, 100000),
        maxConsecutiveSameAction: numeric("maxConsecutiveSameAction", TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.maxConsecutiveSameAction, 1, 1000),
        maxNoProgressSteps: numeric("maxNoProgressSteps", TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.maxNoProgressSteps, 2, 100),
        maxStateVisits: numeric("maxStateVisits", TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.maxStateVisits, 2, 100),
        recentStateWindow: numeric("recentStateWindow", TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.recentStateWindow, 6, 100),
        maxAutoLowValue: numeric("maxAutoLowValue", TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.maxAutoLowValue, 1, 1000),
        flowCompleteSettleMs: numeric("flowCompleteSettleMs", TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.flowCompleteSettleMs, 0, 60000),
        flowCompleteConfirmations: numeric("flowCompleteConfirmations", TOOL88_CASE_BUDDY_DEFAULT_AUTOMATION_GUARDRAILS.flowCompleteConfirmations, 1, 20),
        speedMultiplier,
        baseDelayMs,
        delayMs: Math.max(0, Math.round(baseDelayMs / speedMultiplier)),
        pauseOnManualReview: merged.pauseOnManualReview !== false,
        pauseOnTargetAmbiguity: merged.pauseOnTargetAmbiguity !== false,
        pauseOnUnverifiedOutput: merged.pauseOnUnverifiedOutput !== false,
      };
    },

    caseBuddyClearStaleStopState(reason = "") {
      const detail = this.toText(reason || "Case Buddy opened a fresh Tool 88 action.");
      this.traceFetchStopRequested = false;
      this.traceFetchStopMessage = "";
      const progress = this.traceFetchProgress && typeof this.traceFetchProgress === "object" ? this.traceFetchProgress : null;
      if (progress && /^(?:cancelled|canceled|stop-requested|trace-stop-requested)$/i.test(this.toText(progress.status || progress.trace_status || progress.trace_fetch?.status))) {
        this.traceFetchProgress = null;
      }
      if (this.caseBuddyIntegrationRunState?.stopRequested) {
        this.caseBuddyIntegrationRunState = {
          ...this.caseBuddyIntegrationRunState,
          stopRequested: false,
          status: this.caseBuddyIntegrationRunState.active ? (this.caseBuddyIntegrationRunState.status || "active") : (this.caseBuddyIntegrationRunState.status === "stop-requested" ? "idle" : this.caseBuddyIntegrationRunState.status),
          detail: this.caseBuddyIntegrationRunState.active ? this.caseBuddyIntegrationRunState.detail : detail,
          staleStopClearedAtUtc: new Date().toISOString(),
          staleStopClearReason: detail,
        };
      }
      return this.caseBuddyIntegrationRunState;
    },

    caseBuddyRequestAutomationStop(message = "Case Buddy requested Tool 88 automation stop.") {
      this.caseBuddyIntegrationRunState = {
        ...this.caseBuddyIntegrationRunState,
        stopRequested: true,
        status: "stop-requested",
        detail: this.toText(message) || "Stop requested.",
      };
      this.emitCaseBuddyIntegrationEvent("case-buddy-status", this.buildCaseBuddyHopSolverSnapshot({
        includeRows: false,
        status: "stop-requested",
        message: this.caseBuddyIntegrationRunState.detail,
      }));
    },

    caseBuddyRequestTraceStop(message = "Case Buddy requested Tool 88 trace preview stop.") {
      const cleanMessage = this.toText(message) || "Case Buddy requested Tool 88 trace preview stop.";
      const progress = this.requestTraceFetchStop(cleanMessage);
      this.emitCaseBuddyIntegrationEvent("case-buddy-progress", {
        ...this.buildCaseBuddyHopSolverSnapshot({
          includeRows: false,
          status: "trace-stop-requested",
          integrationAction: "trace-through",
          message: cleanMessage,
        }),
        traceFetchProgress: progress,
        trace_fetch: progress?.trace_fetch || {},
      });
      return progress;
    },

    emitCaseBuddyAutomationProgress(status = "automation-progress", message = "", options = {}) {
      const snapshot = this.buildCaseBuddyHopSolverSnapshot({
        includeRows: options.includeRows === true,
        status,
        integrationAction: options.integrationAction || status,
        message: this.toText(message) || this.caseBuddyIntegrationRunState?.detail || this.statusMessage || "Tool 88 is working.",
      });
      this.caseBuddyIntegrationLastSnapshot = snapshot;
      this.emitCaseBuddyIntegrationEvent(options.eventName || "case-buddy-progress", snapshot);
      return snapshot;
    },

    resetCaseBuddyIntegrationRunState(options = {}) {
      this.caseBuddyIntegrationRunState = createTool88CaseBuddyIntegrationRunState();
      this.caseBuddyIntegrationLastSnapshot = null;
      if (options.keepActiveRootContext !== true) {
        this.caseBuddyActiveRootContext = null;
      }
    },

    buildCaseBuddyAutomationActionKey(action, snapshot = {}) {
      const entry = snapshot.selectedEntry || {};
      const outputKey = this.hashCaseBuddyRowsForGuardrail(snapshot.outputRows || []);
      return [
        this.toText(action),
        this.toText(entry.key || entry.txHash || ""),
        this.normalizeAddressKey(snapshot.selectedTargetAddress || ""),
        this.toText(snapshot.decision?.action || ""),
        outputKey,
      ].join("|");
    },

    hashCaseBuddyRowsForGuardrail(rows = []) {
      const normalized = this.trimTrailingBlankRows(Array.isArray(rows) ? rows : [])
        .map((row) => this.normalizeRowForMaterialComparison(row).join("\u001f"))
        .join("\u001e");
      let hash = 0;
      for (let index = 0; index < normalized.length; index += 1) {
        hash = ((hash << 5) - hash + normalized.charCodeAt(index)) | 0;
      }
      return String(hash >>> 0);
    },

    caseBuddyClipAutomationText(value, limit = 1200) {
      const text = this.toText(value).replace(/\s+/g, " ").trim();
      const maxLength = Math.max(80, Number(limit) || 1200);
      return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
    },

    caseBuddyCompactSelectedEntrySummary(entry = null) {
      if (!entry || typeof entry !== "object") return null;
      return {
        key: this.toText(entry.key || ""),
        pathLabel: this.toText(entry.pathLabel || entry.label || ""),
        txHash: this.toText(entry.txHash || entry.hash || entry.transactionHash || ""),
        shortHash: this.toText(entry.shortHash || ""),
        spreadsheetRowNumber: Number.isFinite(Number(entry.spreadsheetRowNumber)) ? Number(entry.spreadsheetRowNumber) : null,
        fromAddress: this.toText(entry.fromAddress || ""),
        toAddress: this.toText(entry.toAddress || ""),
      };
    },

    buildCaseBuddyAutomationStepSummary(snapshot = {}, action = "", stepNumber = 0) {
      const outputRows = Array.isArray(snapshot.outputRows) ? snapshot.outputRows : this.getCurrentOutputRowsForExport();
      const exitRows = Array.isArray(snapshot.exitRows)
        ? snapshot.exitRows
        : (Array.isArray(snapshot.result?.trace?.exitRows) ? snapshot.result.trace.exitRows : []);
      const inputRows = Array.isArray(this.inputSnapshotRows) ? this.inputSnapshotRows : [];
      const flow = snapshot.flowCompletion || {};
      const trace = snapshot.result?.trace || {};
      const selectedEntry = this.caseBuddyCompactSelectedEntrySummary(snapshot.selectedEntry || this.buildCaseBuddySelectedEntrySummary());
      return {
        atUtc: new Date().toISOString(),
        stepNumber: Math.max(0, Number(stepNumber) || 0),
        action: this.toText(action || snapshot.integrationAction || ""),
        status: this.toText(snapshot.status || ""),
        message: this.caseBuddyClipAutomationText(snapshot.message || "", 900),
        selectedEntry,
        selectedTargetAddress: this.toText(snapshot.selectedTargetAddress || this.selectedTargetAddress || ""),
        decisionAction: this.toText(snapshot.decision?.action || this.autoDecisionPreview?.action || ""),
        flowComplete: snapshot.flowComplete === true,
        flowCompletion: {
          totalEntries: Number(flow.totalEntries) || 0,
          endingCount: Number(flow.endingCount) || 0,
          completeCount: Number(flow.completeCount) || 0,
          incompleteCount: Number(flow.incompleteCount) || 0,
        },
        outputRowCount: outputRows.filter((row) => row && this.rowHasAnyData(row)).length,
        exitRowCount: exitRows.filter((row) => row && this.rowHasAnyData(row)).length,
        inputRowsHash: this.hashCaseBuddyRowsForGuardrail(inputRows),
        outputRowsHash: this.hashCaseBuddyRowsForGuardrail(outputRows),
        exitRowsHash: this.hashCaseBuddyRowsForGuardrail(exitRows),
        simpleResultReadyForContinue: this.simpleResultReadyForContinue === true,
        simpleRecommendationApplied: this.simpleRecommendationApplied === true,
        countLookupReady: Boolean(this.countLookupResult),
        traceResultPresent: Boolean(this.traceResult),
        traceOutputMutationVerified: trace.outputMutationVerified === true || this.traceResult?.outputMutationVerified === true,
        tracePreventPathEnding: trace.preventPathEnding === true || this.traceResult?.preventPathEnding === true,
        needsManualReview: snapshot.needsManualReview === true,
        needsTargetSelection: snapshot.needsTargetSelection === true,
        unverifiedOutput: snapshot.unverifiedOutput === true,
        guardrailReason: this.toText(snapshot.guardrail?.reason || ""),
      };
    },

    buildCaseBuddyAutomationProgressKey(snapshot = {}) {
      const summary = this.buildCaseBuddyAutomationStepSummary(snapshot, snapshot.integrationAction || "", 0);
      return [
        summary.selectedEntry?.key || summary.selectedEntry?.txHash || "",
        this.normalizeAddressKey(summary.selectedTargetAddress || ""),
        summary.decisionAction,
        summary.flowComplete ? "complete" : "open",
        summary.flowCompletion.totalEntries,
        summary.flowCompletion.completeCount,
        summary.flowCompletion.incompleteCount,
        summary.inputRowsHash,
        summary.outputRowsHash,
        summary.exitRowsHash,
        summary.simpleResultReadyForContinue ? "ready" : "not-ready",
        summary.traceResultPresent ? "trace" : "no-trace",
        summary.traceOutputMutationVerified ? "verified" : "unverified",
        summary.tracePreventPathEnding ? "prevent-end" : "normal",
      ].join("|");
    },

    buildCaseBuddyAutomationStateKey(snapshot = {}, action = "") {
      return [
        this.toText(action || snapshot.integrationAction || ""),
        this.toText(snapshot.status || ""),
        this.buildCaseBuddyAutomationProgressKey(snapshot),
      ].join("|");
    },

    buildCaseBuddyAutomationRunProgressState(state = {}, snapshot = {}, action = "", guardrails = {}) {
      const stepNumber = Number(state.stepCount || 0) + 1;
      const stateKey = this.buildCaseBuddyAutomationStateKey(snapshot, action);
      const progressKey = this.buildCaseBuddyAutomationProgressKey(snapshot);
      const recentWindow = Math.max(6, Math.min(60, Number(guardrails.recentStateWindow) || 18));
      const recentStateKeys = [...(Array.isArray(state.recentStateKeys) ? state.recentStateKeys : []), stateKey].slice(-recentWindow);
      const currentStateVisitCount = recentStateKeys.filter((key) => key === stateKey).length;
      const noProgressCount = state.lastProgressKey && state.lastProgressKey === progressKey
        ? Number(state.noProgressCount || 0) + 1
        : 0;
      const stepSummary = {
        ...this.buildCaseBuddyAutomationStepSummary(snapshot, action, stepNumber),
        stateVisitCount: currentStateVisitCount,
        noProgressCount,
      };
      return {
        lastResult: stepSummary,
        lastResultSummary: stepSummary,
        recentSteps: [stepSummary, ...(Array.isArray(state.recentSteps) ? state.recentSteps : [])].slice(0, 24),
        lastStateKey: stateKey,
        recentStateKeys,
        currentStateVisitCount,
        lastProgressKey: progressKey,
        noProgressCount,
      };
    },

    buildCaseBuddyAutomationRunStateSnapshot(state = this.caseBuddyIntegrationRunState) {
      const source = state && typeof state === "object" ? state : {};
      return {
        active: source.active === true,
        stopRequested: source.stopRequested === true,
        startedAtMs: Number(source.startedAtMs) || 0,
        finishedAtMs: Number(source.finishedAtMs) || 0,
        stepCount: Number(source.stepCount) || 0,
        researchedEntryCount: Array.isArray(source.researchedEntryKeys) ? source.researchedEntryKeys.length : 0,
        researchedEntryKeys: Array.isArray(source.researchedEntryKeys) ? source.researchedEntryKeys.slice(-40) : [],
        lastActionKey: this.caseBuddyClipAutomationText(source.lastActionKey || "", 500),
        repeatedActionCount: Number(source.repeatedActionCount) || 0,
        noProgressCount: Number(source.noProgressCount) || 0,
        currentStateVisitCount: Number(source.currentStateVisitCount) || 0,
        recentStateKeys: Array.isArray(source.recentStateKeys) ? source.recentStateKeys.slice(-18) : [],
        lastGuardrail: source.lastGuardrail ? {
          reason: this.toText(source.lastGuardrail.reason || ""),
          message: this.caseBuddyClipAutomationText(source.lastGuardrail.message || "", 1200),
        } : null,
        lastResultSummary: source.lastResultSummary || source.lastResult || null,
        recentSteps: Array.isArray(source.recentSteps) ? source.recentSteps.slice(0, 16) : [],
        flowCompleteFirstSeenAtMs: Number(source.flowCompleteFirstSeenAtMs) || 0,
        flowCompleteConfirmations: Number(source.flowCompleteConfirmations) || 0,
        status: this.toText(source.status || ""),
        detail: this.caseBuddyClipAutomationText(source.detail || "", 1200),
      };
    },

    buildCaseBuddyAutomationStallDiagnostics(snapshot = {}, options = {}) {
      const outputRows = Array.isArray(snapshot.outputRows) ? snapshot.outputRows : this.getCurrentOutputRowsForExport();
      const traceExitRows = this.normalizeCaseBuddyTraceRowsForSelectedParent(this.traceResult?.exitRows || [], this.selectedEntry);
      const verification = this.traceResult?.outputMutationVerification || null;
      return {
        selectedEntry: this.caseBuddyCompactSelectedEntrySummary(snapshot.selectedEntry || this.buildCaseBuddySelectedEntrySummary()),
        selectedTargetAddress: this.toText(snapshot.selectedTargetAddress || this.selectedTargetAddress || ""),
        decisionAction: this.toText(snapshot.decision?.action || this.autoDecisionPreview?.action || ""),
        simpleResultReadyForContinue: this.simpleResultReadyForContinue === true,
        simpleRecommendationApplied: this.simpleRecommendationApplied === true,
        countLookupReady: Boolean(this.countLookupResult),
        traceResultPresent: Boolean(this.traceResult),
        traceStatus: this.toText(this.traceResult?.status || ""),
        traceExitRowCount: traceExitRows.length,
        traceOutputMutationVerified: this.traceResult?.outputMutationVerified === true,
        tracePreventPathEnding: this.traceResult?.preventPathEnding === true,
        traceVerification: verification ? {
          ok: verification.ok === true,
          changed: verification.changed === true,
          rowCountDelta: Number(verification.rowCountDelta) || 0,
          newlyPresent: verification.newlyPresent === true,
          repaired: verification.repaired === true,
          missing: Array.isArray(verification.missing) ? verification.missing.slice(0, 12) : [],
          expected: Array.isArray(verification.expected) ? verification.expected.slice(0, 12) : [],
        } : null,
        inputRowCount: Array.isArray(this.inputSnapshotRows) ? this.inputSnapshotRows.filter((row) => row && this.rowHasAnyData(row)).length : 0,
        outputRowCount: outputRows.filter((row) => row && this.rowHasAnyData(row)).length,
        runState: this.buildCaseBuddyAutomationRunStateSnapshot(options.runState || this.caseBuddyIntegrationRunState),
      };
    },

    evaluateCaseBuddyAutomationGuardrail(state, guardrails, snapshot = {}) {
      if (!guardrails.enabled) return null;
      const now = Date.now();
      if (state.stopRequested) {
        return { reason: "stop-requested", message: state.detail || "Automation stop requested." };
      }
      if (guardrails.maxElapsedMs && state.startedAtMs && now - state.startedAtMs >= guardrails.maxElapsedMs) {
        return { reason: "max-elapsed", message: `Tool 88 automation paused after ${this.formatInteger(Math.round((now - state.startedAtMs) / 1000))} seconds.` };
      }
      if (guardrails.maxEntriesResearched && state.researchedEntryKeys.length >= guardrails.maxEntriesResearched) {
        return { reason: "max-entries", message: `Tool 88 automation paused after researching ${this.formatInteger(state.researchedEntryKeys.length)} unique path ending entr${state.researchedEntryKeys.length === 1 ? "y" : "ies"}.` };
      }
      if (guardrails.maxLoopSteps && state.stepCount >= guardrails.maxLoopSteps) {
        return { reason: "max-steps", message: `Tool 88 automation paused after ${this.formatInteger(state.stepCount)} automation step${state.stepCount === 1 ? "" : "s"}.` };
      }
      if (guardrails.maxConsecutiveSameAction && state.repeatedActionCount > guardrails.maxConsecutiveSameAction) {
        return { reason: "repeated-action", message: `Tool 88 automation saw the same action fingerprint ${this.formatInteger(state.repeatedActionCount)} times in a row. Review this ending before continuing.` };
      }
      if (!snapshot.flowComplete && guardrails.maxNoProgressSteps && Number(state.noProgressCount || 0) >= guardrails.maxNoProgressSteps) {
        const selected = snapshot.selectedEntry || state.lastResultSummary?.selectedEntry || {};
        const ending = this.toText(selected.pathLabel || selected.shortHash || selected.txHash || snapshot.selectedTargetAddress || "the selected ending");
        return {
          reason: "no-material-progress",
          message: `Tool 88 paused because ${ending} produced no material input/output or flow-progress change for ${this.formatInteger(state.noProgressCount)} consecutive steps. It will not keep retrying the same ending automatically.`,
        };
      }
      if (!snapshot.flowComplete && guardrails.maxStateVisits && Number(state.currentStateVisitCount || 0) > guardrails.maxStateVisits) {
        const selected = snapshot.selectedEntry || state.lastResultSummary?.selectedEntry || {};
        const ending = this.toText(selected.pathLabel || selected.shortHash || selected.txHash || snapshot.selectedTargetAddress || "the selected ending");
        return {
          reason: "automation-state-cycle",
          message: `Tool 88 paused after returning to the same state for ${ending} ${this.formatInteger(state.currentStateVisitCount)} times. This prevents two unresolved endings from cycling indefinitely.`,
        };
      }
      if (guardrails.pauseOnManualReview && snapshot.needsManualReview) {
        return { reason: "manual-review", message: snapshot.message || "Tool 88 recommends manual review before continuing automation." };
      }
      if (guardrails.pauseOnTargetAmbiguity && snapshot.needsTargetSelection) {
        return { reason: "target-ambiguity", message: "Tool 88 found more than one possible target address. Choose the target before continuing automation." };
      }
      if (guardrails.pauseOnUnverifiedOutput && snapshot.unverifiedOutput) {
        return { reason: "unverified-output", message: "Tool 88 found a trace result but output insertion was not verified. Review before continuing automation." };
      }
      return null;
    },

    async caseBuddyRunAutomationLoop(options = {}) {
      if (this.caseBuddyIntegrationRunState.active || this.isProcessing) {
        return this.buildCaseBuddyHopSolverSnapshot({
          includeRows: options.includeRows,
          status: "busy",
          message: "Tool 88 automation is already running or a hop is processing.",
        });
      }

      const guardrails = this.normalizeCaseBuddyAutomationGuardrails(options.guardrails || options);
      const runState = {
        ...createTool88CaseBuddyIntegrationRunState(),
        active: true,
        startedAtMs: Date.now(),
        status: "running",
        detail: "Tool 88 Case Buddy automation loop started.",
      };
      this.caseBuddyIntegrationRunState = runState;
      this.emitCaseBuddyIntegrationEvent("case-buddy-status", this.buildCaseBuddyHopSolverSnapshot({
        includeRows: false,
        status: "automation-started",
        message: runState.detail,
      }));

      let finalSnapshot = null;
      try {
        for (let automationLoopIndex = 0; automationLoopIndex < guardrails.maxLoopSteps; automationLoopIndex += 1) {
          const preGuardrail = this.evaluateCaseBuddyAutomationGuardrail(this.caseBuddyIntegrationRunState, guardrails, finalSnapshot || {});
          if (preGuardrail) {
            finalSnapshot = this.buildCaseBuddyHopSolverSnapshot({
              includeRows: options.includeRows,
              status: "guardrail-paused",
              guardrail: preGuardrail,
              message: preGuardrail.message,
            });
            break;
          }

          const nextStepNumber = Number(this.caseBuddyIntegrationRunState.stepCount || 0) + 1;
          const activeRootLabel = this.caseBuddyActiveRootLabel();
          this.caseBuddyIntegrationRunState = {
            ...this.caseBuddyIntegrationRunState,
            status: "running",
            detail: activeRootLabel
              ? `Tool 88 is solving ${activeRootLabel}, unresolved path ending ${this.formatInteger(nextStepNumber)}.`
              : `Tool 88 is solving unresolved path ending ${this.formatInteger(nextStepNumber)}.`,
          };
          this.emitCaseBuddyAutomationProgress(
            "automation-step-running",
            this.caseBuddyIntegrationRunState.detail,
            { integrationAction: "run-next-action", includeRows: false }
          );

          const stepResult = await this.caseBuddyRunNextAction({
            includeRows: true,
            maxAutoLowValue: guardrails.maxAutoLowValue,
            acceptUnverifiedTraceOutput: options.acceptUnverifiedTraceOutput === true || options.allowUnverifiedTraceOutput === true || guardrails.pauseOnUnverifiedOutput === false,
            allowUnverifiedTraceOutput: options.allowUnverifiedTraceOutput === true || options.acceptUnverifiedTraceOutput === true || guardrails.pauseOnUnverifiedOutput === false,
            caseWorkflowRawData: options.caseWorkflowRawData === true,
            caseBuddyRawDataAutomation: options.caseBuddyRawDataAutomation === true,
            rawDataAutomation: options.rawDataAutomation === true,
            skipResolvableReviews: options.skipResolvableReviews === true || options.skipResolvableReview === true,
            rubberStampResolvableReviews: options.rubberStampResolvableReviews === true,
            autoFinalizeRecognizedBtcBridgeEndings: options.autoFinalizeRecognizedBtcBridgeEndings === true,
          });
          const selectedKey = this.toText(stepResult.selectedEntry?.key || stepResult.selectedEntry?.txHash || "");
          const researched = new Set(this.caseBuddyIntegrationRunState.researchedEntryKeys || []);
          if (selectedKey) researched.add(selectedKey);

          const actionKey = this.buildCaseBuddyAutomationActionKey(stepResult.integrationAction || "step", stepResult);
          const repeatedActionCount = actionKey === this.caseBuddyIntegrationRunState.lastActionKey
            ? Number(this.caseBuddyIntegrationRunState.repeatedActionCount || 0) + 1
            : 1;

          const caseBuddyProgressState = this.buildCaseBuddyAutomationRunProgressState(
            this.caseBuddyIntegrationRunState,
            stepResult,
            stepResult.integrationAction || "step",
            guardrails
          );
          this.caseBuddyIntegrationRunState = {
            ...this.caseBuddyIntegrationRunState,
            ...caseBuddyProgressState,
            stepCount: Number(this.caseBuddyIntegrationRunState.stepCount || 0) + 1,
            researchedEntryKeys: Array.from(researched),
            lastActionKey: actionKey,
            repeatedActionCount,
            status: "running",
            detail: stepResult.message || stepResult.status || "Tool 88 automation step completed.",
          };

          finalSnapshot = this.buildCaseBuddyHopSolverSnapshot({
            includeRows: options.includeRows,
            status: stepResult.status,
            integrationAction: stepResult.integrationAction,
            message: stepResult.message,
          });
          this.caseBuddyIntegrationLastSnapshot = finalSnapshot;
          this.emitCaseBuddyIntegrationEvent("case-buddy-progress", this.buildCaseBuddyHopSolverSnapshot({
            includeRows: false,
            status: stepResult.status || "automation-step-complete",
            integrationAction: stepResult.integrationAction || "run-next-action",
            message: stepResult.message || this.caseBuddyIntegrationRunState.detail,
          }));

          const postGuardrail = this.evaluateCaseBuddyAutomationGuardrail(this.caseBuddyIntegrationRunState, guardrails, stepResult);
          if (postGuardrail) {
            finalSnapshot = this.buildCaseBuddyHopSolverSnapshot({
              includeRows: options.includeRows,
              status: "guardrail-paused",
              guardrail: postGuardrail,
              message: postGuardrail.message,
            });
            break;
          }

          if (stepResult.flowComplete) {
            const nowMs = Date.now();
            const firstSeenAtMs = Number(this.caseBuddyIntegrationRunState.flowCompleteFirstSeenAtMs) || nowMs;
            const confirmations = Number(this.caseBuddyIntegrationRunState.flowCompleteConfirmations || 0) + 1;
            const settled = confirmations >= guardrails.flowCompleteConfirmations && nowMs - firstSeenAtMs >= guardrails.flowCompleteSettleMs;
            this.caseBuddyIntegrationRunState = {
              ...this.caseBuddyIntegrationRunState,
              flowCompleteFirstSeenAtMs: firstSeenAtMs,
              flowCompleteConfirmations: confirmations,
              detail: settled
                ? `Tool 88 confirmed flow completion ${this.formatInteger(confirmations)} time${confirmations === 1 ? "" : "s"}.`
                : `Tool 88 saw flow completion ${this.formatInteger(confirmations)}/${this.formatInteger(guardrails.flowCompleteConfirmations)}; waiting briefly to confirm no late unresolved endings remain.`,
            };
            finalSnapshot = this.buildCaseBuddyHopSolverSnapshot({
              includeRows: options.includeRows,
              status: settled ? stepResult.status : "flow-complete-confirming",
              integrationAction: settled ? stepResult.integrationAction : "confirm-flow-complete",
              message: this.caseBuddyIntegrationRunState.detail,
            });
            this.caseBuddyIntegrationLastSnapshot = finalSnapshot;
            this.emitCaseBuddyIntegrationEvent("case-buddy-progress", this.buildCaseBuddyHopSolverSnapshot({
              includeRows: false,
              status: settled ? stepResult.status : "flow-complete-confirming",
              integrationAction: settled ? stepResult.integrationAction : "confirm-flow-complete",
              message: this.caseBuddyIntegrationRunState.detail,
            }));
            if (settled) break;
            if (guardrails.delayMs > 0 || guardrails.flowCompleteSettleMs > 0) {
              await this.sleep(Math.max(350, guardrails.delayMs));
            }
            continue;
          }

          this.caseBuddyIntegrationRunState = {
            ...this.caseBuddyIntegrationRunState,
            flowCompleteFirstSeenAtMs: 0,
            flowCompleteConfirmations: 0,
          };

          if (stepResult.needsManualReview || stepResult.needsTargetSelection || (guardrails.pauseOnUnverifiedOutput && stepResult.unverifiedOutput)) {
            break;
          }

          if (guardrails.delayMs > 0) {
            await this.sleep(guardrails.delayMs);
          }
        }

        if (!finalSnapshot?.guardrail && !finalSnapshot?.flowComplete && !finalSnapshot?.needsManualReview && !finalSnapshot?.needsTargetSelection && !finalSnapshot?.unverifiedOutput && Number(this.caseBuddyIntegrationRunState.stepCount || 0) >= guardrails.maxLoopSteps) {
          const maxStepGuardrail = {
            reason: "max-steps",
            message: `Tool 88 automation paused after ${this.formatInteger(this.caseBuddyIntegrationRunState.stepCount)} automation step${this.caseBuddyIntegrationRunState.stepCount === 1 ? "" : "s"}.`,
          };
          finalSnapshot = this.buildCaseBuddyHopSolverSnapshot({
            includeRows: options.includeRows,
            status: "guardrail-paused",
            guardrail: maxStepGuardrail,
            message: maxStepGuardrail.message,
          });
        }
      } finally {
        this.caseBuddyIntegrationRunState = {
          ...this.caseBuddyIntegrationRunState,
          active: false,
          finishedAtMs: Date.now(),
          status: finalSnapshot?.status || "finished",
          detail: finalSnapshot?.message || "Tool 88 automation loop finished.",
          lastGuardrail: finalSnapshot?.guardrail || null,
        };
      }

      const eventName = finalSnapshot?.flowComplete
        ? "case-buddy-flow-complete"
        : (finalSnapshot?.guardrail || finalSnapshot?.needsManualReview ? "case-buddy-guardrail" : "case-buddy-result");
      this.emitCaseBuddyIntegrationEvent(eventName, finalSnapshot || this.buildCaseBuddyHopSolverSnapshot({ includeRows: options.includeRows }));
      return finalSnapshot || this.buildCaseBuddyHopSolverSnapshot({ includeRows: options.includeRows });
    },

    deriveCaseBuddySnapshotStatus(integrationAction = "") {
      if (this.caseBuddySelectedOutputInferenceBlock) return "selected-output-missing";
      if (this.flowCompletionResult?.done) return "flow-complete";
      if (this.simpleHasTargetAmbiguity) return "target-ambiguity";
      if (this.traceResult && !this.isTraceResultReadyForContinue(this.traceResult) && Array.isArray(this.traceResult.exitRows) && this.traceResult.exitRows.length) return "unverified-output";
      if (this.autoDecisionPreview?.action === "review") return "manual-review";
      if (this.simpleResultReadyForContinue) return "result-ready";
      if (this.countLookupResult) return "decision-ready";
      if (this.selectedEntryKey) return "selected-hop-ready";
      return this.toText(integrationAction) || "idle";
    },

    buildCaseBuddySelectedEntrySummary(entry = this.selectedEntry) {
      if (!entry) return null;
      const firstRow = entry.rows?.find(({ row }) => this.rowHasAnyData(row))?.row || [];
      return {
        key: entry.key,
        pathLabel: entry.pathLabel || this.toText(firstRow[COL.PATH_LABEL]),
        txHash: entry.txHash || this.toText(firstRow[COL.TX_HASH]),
        shortHash: this.shortenMiddle(entry.txHash || firstRow[COL.TX_HASH], 10, 8),
        startRowIndex: entry.startRowIndex,
        endRowIndex: entry.endRowIndex,
        spreadsheetRowNumber: Number.isInteger(entry.startRowIndex) ? entry.startRowIndex + 1 : null,
        fromAddress: this.toText(firstRow[COL.FROM_ADDRESS]),
        toAddress: this.toText(firstRow[COL.TO_ADDRESS]),
        fundsFollowed: this.toText(firstRow[COL.FUNDS_FOLLOWED]),
        priorBalance: this.toText(firstRow[COL.PRIOR_BALANCE]),
        leftBoundsDisplay: this.toText(firstRow[COL.LEFT_BOUNDS_DISPLAY]),
        leftBoundsNumeric: this.toText(firstRow[COL.LEFT_BOUNDS_NUMERIC]),
        chainId: this.toText(firstRow[COL.CHAIN_ID]),
        chainName: this.toText(firstRow[COL.CHAIN_NAME]),
        hopLogic: this.toText(firstRow[COL.HOP_LOGIC] || this.hopLogicType),
      };
    },

    buildCaseBuddyDecisionSummary() {
      if (this.caseBuddyCurrentResultIsPotentialServicePathEnding()) {
        const pathEnding = this.pathEndingResult || {};
        const reason = this.toText(pathEnding.reason || pathEnding.note || "Tool 88 identified Unknown service/hot-wallet-like path-ending evidence.");
        return {
          action: "path_end",
          status: "warn",
          title: "Mark as Unknown path ending",
          summary: reason || "Tool 88 identified this ending as Unknown with service/hot-wallet-like behavior, so trace-through should not be the default recommendation.",
          buttonLabel: "Use + apply path ending",
          reasons: ["Additional service / hot-wallet checks identified this as a likely unattributed service endpoint."],
        };
      }
      const decision = this.autoDecisionPreview;
      if (!decision) return null;
      return {
        action: decision.action,
        status: decision.status,
        title: decision.title,
        summary: decision.summary,
        buttonLabel: decision.buttonLabel,
        reasons: Array.isArray(decision.reasons) ? decision.reasons.slice() : [],
      };
    },

    caseBuddyCurrentResultIsPotentialServicePathEnding() {
      const result = this.pathEndingResult || null;
      if (!result) return false;
      if (result.specialTreatment === "potential_service_address") return true;
      const text = `${this.toText(result.reason || "")} ${this.toText(result.note || "")}`;
      if (!text.trim()) return false;
      const hasUnknown = /\bunknown\b/i.test(text) || /unattributed/i.test(text);
      const serviceShape = /(unattributed\s+exchange\s+hot\s+wallet|features\s+of\s+an\s+unattributed\s+exchange|exchange\s+hot\s+wallet|hot[-\s]?wallet|service\s+address|service[-\s]?pattern|service\s*\/\s*hot|potential\s+service|additional\s+service\s+checks|high\s+stablecoin\s+flow|high\s+erc20\s+transfer\s+count|trace\s+through\s+again\s+only\s+if)/i.test(text);
      const stopShape = /(path\s*ends|manual-path-ending|tool\s*88\s+stopped|action\s*=\s*manual-path-ending)/i.test(text);
      return Boolean(hasUnknown && serviceShape && (stopShape || result.specialTreatment));
    },


    formatDormantFundsAmount(value, token = '') {
      const symbol = this.toText(token).replace(/^(?:null|undefined|nan)$/i, '').trim();
      const amount = Number(value);
      if (!Number.isFinite(amount)) return '';
      const upper = symbol.toUpperCase();
      const minDecimals = upper === 'BTC' ? 0 : 2;
      const maxDecimals = upper === 'BTC' ? 8 : (this.isCaseBuddyStablecoinToken(symbol) ? 2 : 8);
      const amountText = amount.toLocaleString('en-US', {
        minimumFractionDigits: minDecimals,
        maximumFractionDigits: maxDecimals,
        useGrouping: true
      });
      return `${amountText}${symbol ? ` ${symbol}` : ''}`.trim();
    },

    normalizeTool88DormantAmountText(value = '', fallbackToken = '') {
      const text = this.toText(value).replace(/\s+/g, ' ').trim();
      if (!text) return '';
      const amount = this.parseNumberLike(text);
      const token = this.parseTokenLike(text) || this.toText(fallbackToken).replace(/^(?:null|undefined|nan)$/i, '').trim();
      if (!Number.isFinite(amount)) {
        if (!token) return text;
        const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`\\b${escapedToken}\\b`, 'i').test(text) ? text : `${text} ${token}`;
      }
      return this.formatDormantFundsAmount(amount, token);
    },

    buildTool88DormantFundsNote(amountText = '', pathEnds = true) {
      const normalizedAmount = this.normalizeTool88DormantAmountText(amountText);
      if (!normalizedAmount) return '';
      return `A portion of the funds remains at the receiving address (${normalizedAmount}).${pathEnds ? ' Path ends.' : ''}`;
    },

    buildTool88StructuredDormantResult(solution = null, entry = this.selectedEntry, selectedAddress = this.selectedTargetAddress, options = {}) {
      const source = solution && typeof solution === 'object' ? solution : {};
      const analysisDirection = this.toText(
        source.analysisDirection
          || source.btcAnalysis?.analysisDirection
          || source.accountAnalysis?.analysisDirection
          || this.traceDirection
      ).toLowerCase();
      if (analysisDirection === 'reverse' || source.preventPathEnding === true) return null;
      const exitRows = this.getCaseBuddyExportableTraceRows(source.exitRows || []);
      const exitRowHashes = exitRows
        .map(row => this.toText(Array.isArray(row) ? row[1] : (row?.continuesAsLabel || row?.hash || row?.txHash || row?.transactionHash)).trim())
        .filter(hash => hash && this.isTool88LikelyTransactionHash(hash));
      const transactionExitHashes = (Array.isArray(source.exitTransactions) ? source.exitTransactions : [])
        .map(exit => this.getCaseBuddyChildTransactionHash(exit))
        .filter(hash => hash && this.isTool88LikelyTransactionHash(hash));
      // Prefer the structured exit list because it preserves one occurrence per
      // BTC output branch, including legitimate duplicate transaction hashes.
      // The row-derived list is only a fallback so the same exits are not counted
      // twice merely because both representations are present.
      const validExitTransactions = transactionExitHashes.length
        ? transactionExitHashes
        : exitRowHashes;
      const dormantAmountText = this.normalizeTool88DormantAmountText(
        source.dormantAmountText || options.dormantAmountText || '',
        source.fundsIn?.token || entry?.token || ''
      );
      const dormantAmount = this.parseNumberLike(dormantAmountText);
      const fundsIn = source.fundsIn || this.deriveFundsIn(entry, selectedAddress);
      const fundsFollowed = Number.isFinite(Number(fundsIn?.amount)) ? Number(fundsIn.amount) : this.parseNumberLike(entry?.fundsFollowed || entry?.amount || '');
      const token = this.parseTokenLike(dormantAmountText) || this.toText(fundsIn?.token || entry?.token || '').trim();
      if (!dormantAmountText || !Number.isFinite(dormantAmount) || dormantAmount <= 0) return null;
      const isPartial = exitRows.length > 0 || validExitTransactions.length > 0;
      const validHashes = validExitTransactions.slice();
      const continuesAs = isPartial && validHashes.length
        ? `Continues as ${validHashes.join(', ')}`
        : (isPartial ? this.toText(options.continuesAs || '') : 'Path ends.');
      const note = this.buildTool88DormantFundsNote(dormantAmountText, !isPartial);
      const downloadedTransactionCount = Array.isArray(options.transactions)
        ? options.transactions.length
        : (Array.isArray(source.relevantTransactions) ? source.relevantTransactions.length : (Array.isArray(this.traceInspectorRows) ? this.traceInspectorRows.length : 0));
      const candidateOutgoingCount = Array.isArray(source.relevantTransactions) ? source.relevantTransactions.length : 0;
      const runId = this.toText(options.runId || options.transactionPayload?.job_id || this.backendJobMeta?.job_id || this.lastRunMeta?.jobId || '');
      return {
        action: isPartial ? 'partial-dormant-with-exits' : 'dormant-ending',
        status: 'valid',
        addressOfInterest: selectedAddress || entry?.toAddress || '',
        receivingAddress: selectedAddress || entry?.toAddress || '',
        dormantAddress: selectedAddress || entry?.toAddress || '',
        transactionHash: entry?.txHash || entry?.hash || entry?.transactionHash || '',
        utcDate: entry?.date || entry?.utcDate || '',
        token,
        fundsFollowed: Number.isFinite(fundsFollowed) ? fundsFollowed : null,
        fundsFollowedText: Number.isFinite(fundsFollowed) ? this.formatFunds(fundsFollowed, token) : this.toText(entry?.fundsFollowed || ''),
        dormantAmount,
        dormantAmountText,
        exitedAmount: Math.max(0, (Number.isFinite(fundsFollowed) ? fundsFollowed : 0) - dormantAmount),
        validExitCount: validHashes.length,
        validExitTransactions: validHashes,
        noExitReason: isPartial ? 'Some followed funds remain at the receiving address after qualifying exits.' : 'No qualifying exits after the deposit of interest.',
        note,
        continuesAs: continuesAs || 'Path ends.',
        resultId: runId,
        runId,
        evidence: {
          depositOfInterestFound: true,
          downloadedTransactionCount,
          candidateExitCount: candidateOutgoingCount,
          validExitCount: validHashes.length,
          excludedExitCount: Math.max(0, candidateOutgoingCount - validHashes.length),
          dustTolerance: this.toText(options.dustTolerance || ''),
          analysisWindow: this.toText(options.analysisWindow || ''),
          selectedPathLabel: this.toText(entry?.pathLabel || ''),
          selectedTransactionHash: this.toText(entry?.txHash || entry?.hash || ''),
          receivingAddress: selectedAddress || entry?.toAddress || '',
          token,
          fundsFollowed: Number.isFinite(fundsFollowed) ? fundsFollowed : null,
          priorBalance: this.toText(entry?.priorBalance || entry?.balance || ''),
          leftBounds: this.toText(entry?.leftBounds || entry?.leftBoundsDisplay || '')
        },
        warnings: Array.isArray(source.warnings) ? source.warnings.slice() : []
      };
    },

    getTool88CurrentDormantResult() {
      if (this.traceDirection === 'reverse') return null;
      if (this.dormantResult && typeof this.dormantResult === 'object' && this.dormantResult.status === 'valid') return this.dormantResult;
      return this.buildTool88StructuredDormantResult(this.traceResult, this.selectedEntry, this.selectedTargetAddress, {
        transactionPayload: this.traceTransactionPayload,
        runId: this.lastRunMeta?.jobId || ''
      });
    },

    buildCaseBuddyResultSummary() {
      const dormantResult = this.getTool88CurrentDormantResult();
      return {
        dormant: dormantResult ? { ...dormantResult } : null,
        trace: this.traceResult ? {
          exitRowCount: this.normalizeCaseBuddyTraceRowsForSelectedParent(this.traceResult.exitRows, this.selectedEntry).length,
          exitRows: this.normalizeCaseBuddyTraceRowsForSelectedParent(this.traceResult.exitRows, this.selectedEntry),
          outputMutationVerified: this.traceResult.outputMutationVerified === true,
          preventPathEnding: this.traceResult.preventPathEnding === true,
          analysisDirection: this.traceResult.analysisDirection || this.traceDirection,
          serviceStopOverride: this.traceResult.serviceStopOverride === true || this.traceResult.manualServiceOverride === true,
          manualServiceOverride: this.traceResult.manualServiceOverride === true || this.traceResult.serviceStopOverride === true,
          serviceOverrideReason: this.traceResult.serviceOverrideReason || this.lastRunMeta?.serviceOverrideReason || "",
          dormantResult: dormantResult ? { ...dormantResult } : null,
        } : null,
        pathEnding: this.pathEndingResult ? {
          note: this.pathEndingResult.note || "",
          reason: this.pathEndingResult.reason || "",
          specialTreatment: this.pathEndingResult.specialTreatment || "",
          dormantAmountText: this.pathEndingResult.dormantAmountText || dormantResult?.dormantAmountText || "",
          dormantResult: this.pathEndingResult.dormantResult || (dormantResult ? { ...dormantResult } : null),
          lowValueAuto: this.pathEndingResult.lowValueAuto === true,
          autoBridgeTerminalFallback: this.pathEndingResult.autoBridgeTerminalFallback === true,
          bridgeEvidence: this.pathEndingResult.bridgeEvidence ? { ...this.pathEndingResult.bridgeEvidence } : null,
        } : null,
        swap: this.swapApplyResult ? {
          note: this.swapApplyResult.note || "",
          insertedRowCount: Array.isArray(this.swapApplyResult.rows) ? this.swapApplyResult.rows.length : 0,
          rows: Array.isArray(this.swapApplyResult.rows) ? this.swapApplyResult.rows.map((row) => this.cloneRowPreservingValues(row)) : [],
          row: this.swapApplyResult.row ? this.cloneRowPreservingValues(this.swapApplyResult.row) : null,
          txHash: this.swapApplyResult.candidate?.txHash || this.swapExplorer?.txHash || "",
          initiatingAddress: this.swapApplyResult.candidate?.initiatingAddress || this.swapExplorer?.initiatingAddress || "",
          selectedAddress: this.swapApplyResult.candidate?.selectedAddress || this.selectedTargetAddress || "",
          swapContractAddress: this.swapApplyResult.candidate?.swapContractAddress || "",
          receivedTokenContract: this.swapApplyResult.candidate?.receivedTokenContract || "",
          sent: this.swapApplyResult.candidate?.sent || null,
          received: this.swapApplyResult.candidate?.received || null,
          followedBefore: this.swapApplyResult.candidate?.followedBefore ?? null,
          followedAfter: this.swapApplyResult.candidate?.followedAfter ?? null,
          leftBefore: this.swapApplyResult.candidate?.leftBefore ?? null,
          leftAfter: this.swapApplyResult.candidate?.leftAfter ?? null,
          explorerRows: Array.isArray(this.swapExplorer?.allRows) ? this.swapExplorer.allRows.map((row) => ({
            key: row.key || "",
            hash: row.hash || "",
            kind: row.kind || "",
            kindLabel: row.kindLabel || "",
            direction: row.direction || "",
            fromAddress: row.fromAddress || "",
            toAddress: row.toAddress || "",
            token: row.token || row.tokenSymbol || "",
            amount: row.amount ?? null,
            contractAddress: row.contractAddress || "",
            detailText: row.detailText || "",
            dateText: row.dateText || ""
          })) : [],
          title: this.swapApplyResult.candidate?.title || "",
          summary: this.swapApplyResult.candidate?.summary || "",
        } : null,
        bridge: this.bridgeApplyResult ? {
          note: this.bridgeApplyResult.note || "",
          insertedRowCount: Array.isArray(this.bridgeApplyResult.rows) ? this.bridgeApplyResult.rows.length : 0,
          destinationAddress: this.bridgeApplyResult.candidate?.destinationAddress || "",
          destinationNetwork: this.bridgeApplyResult.candidate?.destinationNetwork || "",
          tokenSymbol: this.bridgeApplyResult.candidate?.tokenSymbol || "",
          followedAfter: this.bridgeApplyResult.candidate?.followedAfter ?? null,
          leftAfter: this.bridgeApplyResult.candidate?.leftAfter ?? null,
          title: this.bridgeApplyResult.candidate?.title || "",
          summary: this.bridgeApplyResult.candidate?.summary || "",
        } : null,
      };
    },

    getCaseBuddyChildTransactionHash(exitLike = null) {
      const rawValue = Array.isArray(exitLike)
        ? exitLike?.[COL.TX_HASH]
        : (exitLike && typeof exitLike === 'object'
          ? (exitLike.hash || exitLike.txHash || exitLike.transactionHash || exitLike.continuesAsLabel || '')
          : exitLike);
      const value = this.toText(rawValue).trim();
      if (!value) return '';
      const candidates = [
        value,
        value.replace(/\s+output\s+\d+.*$/iu, '').trim(),
        value.replace(/\s+(?:vin|vout|input|source)\s+\d+.*$/iu, '').trim(),
      ];
      return candidates.find(candidate => candidate && this.isTool88LikelyTransactionHash(candidate)) || '';
    },

    getCaseBuddyOutputIndex(value = null) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const candidates = [
          value.outputIndex,
          value.output_index,
          value.vout,
          value.voutIndex,
          value.vout_index,
          value.logIndex,
          value.log_index,
          value.eventIndex,
          value.event_index,
          value.transferIndex,
          value.transfer_index,
          value.index,
        ];
        for (const candidate of candidates) {
          const parsed = Number(candidate);
          if (Number.isInteger(parsed) && parsed >= 0) return parsed;
        }
      }
      const row = Array.isArray(value) ? value : [];
      const noteText = [row?.[COL.TOOL88_NOTES], row?.[COL.SYSTEM_NOTES]]
        .map(note => this.toText(note))
        .filter(Boolean)
        .join('\n');
      const match = noteText.match(/(?:(?:BTC\s+)?output|log|event|transfer)\s+index\s+(\d+)/iu);
      return match ? Number(match[1]) : null;
    },

    getCaseBuddyInputIndex(value = null) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const candidates = [
          value.inputIndex,
          value.input_index,
          value.vin,
          value.vinIndex,
          value.vin_index,
        ];
        for (const candidate of candidates) {
          const parsed = Number(candidate);
          if (Number.isInteger(parsed) && parsed >= 0) return parsed;
        }
      }
      const row = Array.isArray(value) ? value : [];
      const noteText = [row?.[COL.TOOL88_NOTES], row?.[COL.SYSTEM_NOTES]]
        .map(note => this.toText(note))
        .filter(Boolean)
        .join('\n');
      const markerMatch = noteText.match(/(?:input_index|vin_index)\s*=\s*(\d+)/iu);
      if (markerMatch) return Number(markerMatch[1]);
      const match = noteText.match(/(?:BTC\s+reverse\s+input|selected\s+input|input|vin)\s*(?:index\s*=?\s*|#\s*)?(\d+)/iu);
      return match ? Number(match[1]) : null;
    },

    getCaseBuddyTraceBranchKey(exitLike = null, parentHash = '') {
      const row = Array.isArray(exitLike) ? exitLike : null;
      const objectValue = exitLike && typeof exitLike === 'object' && !Array.isArray(exitLike)
        ? exitLike
        : null;
      const hash = this.getCaseBuddyChildTransactionHash(exitLike);
      if (!hash) return '';
      const fromAddress = row
        ? row?.[COL.FROM_ADDRESS]
        : (objectValue?.fromAddress ?? objectValue?.from_address ?? objectValue?.from ?? '');
      const toAddress = row
        ? row?.[COL.TO_ADDRESS]
        : (objectValue?.toAddress ?? objectValue?.to_address ?? objectValue?.to ?? objectValue?.destinationAddress ?? '');
      const token = row
        ? row?.[COL.TOKEN]
        : (objectValue?.token ?? objectValue?.tokenSymbol ?? objectValue?.symbol ?? '');
      const chain = row
        ? (row?.[COL.CHAIN_ID] || row?.[COL.CHAIN_NAME])
        : (objectValue?.chainId ?? objectValue?.chain_id ?? objectValue?.chainName ?? objectValue?.chain_name ?? '');
      const fromHash = row
        ? row?.[COL.FROM_HASH]
        : (objectValue?.fromHash ?? objectValue?.from_hash ?? parentHash ?? '');
      const outputIndex = this.getCaseBuddyOutputIndex(exitLike);
      const inputIndex = this.getCaseBuddyInputIndex(exitLike);
      const tokenText = this.toText(token).trim().toUpperCase();

      // A branch is the on-chain output/transfer itself. Funds-followed, left
      // bounds, and display-formatted amount are allocation/presentation details.
      // The output spreadsheet may round or comma-format amount cells after a trace
      // preview, so using amount as a fallback branch-key component caused valid
      // trace-through rows to fail verification as "missing". Prefer explicit
      // output/log indexes when available; otherwise rely on the stable chain
      // identity tuple used by Case Buddy Lite.
      return [
        this.normalizeHashKey(hash),
        this.normalizeAddressKey(fromAddress),
        this.normalizeAddressKey(toAddress),
        this.normalizeHashKey(fromHash || parentHash),
        tokenText,
        this.toText(chain).trim().toLowerCase(),
        Number.isInteger(outputIndex) ? `branch:${outputIndex}` : '',
        Number.isInteger(inputIndex) ? `input:${inputIndex}` : '',
      ].join('|');
    },

    getCaseBuddyExitChildHashes(exits = [], parentHash = '') {
      const parentKey = this.normalizeHashKey(parentHash);
      const seenBranches = new Set();
      const hashes = [];
      (Array.isArray(exits) ? exits : []).forEach((exit) => {
        const hash = this.getCaseBuddyChildTransactionHash(exit);
        const hashKey = this.normalizeHashKey(hash);
        if (!hash || !hashKey || (parentKey && hashKey === parentKey)) return;

        // Preserve one Column Q occurrence per distinct output branch. Two BTC
        // outputs can legitimately share one transaction hash, while repeated
        // absorption of the exact same branch must remain idempotent.
        if (exit && typeof exit === 'object') {
          const branchKey = this.getCaseBuddyTraceBranchKey(exit, parentHash);
          if (branchKey && seenBranches.has(branchKey)) return;
          if (branchKey) seenBranches.add(branchKey);
        }
        hashes.push(hash);
      });
      return hashes;
    },

    parseCaseBuddyContinuationHashes(value = '') {
      const text = this.toText(value).trim();
      if (!/^continues\s+as\b/iu.test(text)) return [];
      return text
        .replace(/^continues\s+as\s*/iu, '')
        .split(',')
        .map(part => this.toText(part).trim())
        .filter(Boolean)
        .map(part => part.replace(/\s+output\s+\d+.*$/iu, '').trim())
        .filter(hash => !!this.normalizeHashKey(hash));
    },

    caseBuddyTraceContinuationState(rows = [], expectedRows = []) {
      const outputRows = (Array.isArray(rows) ? rows : [])
        .map(row => this.cloneRowPreservingValues(row));
      const selectedEntry = this.selectedEntry;
      const traceRows = this.normalizeCaseBuddyTraceRowsForSelectedParent(expectedRows, selectedEntry);
      const parentHash = selectedEntry?.txHash || selectedEntry?.row?.[COL.TX_HASH] || '';
      const expectedChildHashes = this.getCaseBuddyExitChildHashes(
        Array.isArray(this.traceResult?.exitTransactions) && this.traceResult.exitTransactions.length
          ? this.traceResult.exitTransactions
          : traceRows,
        parentHash
      );
      const bounds = this.findEntryBoundsInRows(outputRows, selectedEntry);
      const startRowIndex = Number(bounds?.startRowIndex);
      const endRowIndex = Number(bounds?.endRowIndex);
      let parentRowIndex = Number.isInteger(startRowIndex) ? startRowIndex : -1;
      const selectedTargetKey = this.normalizeAddressKey(this.selectedTargetAddress);
      if (parentRowIndex >= 0 && selectedTargetKey) {
        const safeEnd = Number.isInteger(endRowIndex) ? endRowIndex : parentRowIndex;
        for (let index = parentRowIndex; index <= safeEnd; index += 1) {
          if (this.normalizeAddressKey(outputRows[index]?.[this.getTargetColumnIndex()]) === selectedTargetKey) {
            parentRowIndex = index;
            break;
          }
        }
      }
      const referencedChildHashes = parentRowIndex >= 0
        ? this.parseCaseBuddyContinuationHashes(outputRows[parentRowIndex]?.[COL.CONTINUES_AS])
        : [];
      const countHashes = (hashes = []) => {
        const counts = new Map();
        (Array.isArray(hashes) ? hashes : []).forEach((hash) => {
          const key = this.normalizeHashKey(hash);
          if (key) counts.set(key, (counts.get(key) || 0) + 1);
        });
        return counts;
      };
      const expectedCounts = countHashes(expectedChildHashes);
      const referencedCounts = countHashes(referencedChildHashes);
      const parentContinuationVerified = parentRowIndex >= 0
        && expectedChildHashes.length > 0
        && Array.from(expectedCounts.entries()).every(([key, required]) => (referencedCounts.get(key) || 0) >= required);
      const rowVerification = this.verifyTraceOutputMutation(outputRows, traceRows, null);
      const traceRowsPresent = !Array.isArray(rowVerification.missing) || rowVerification.missing.length === 0;
      return {
        ok: parentContinuationVerified && traceRowsPresent,
        parentContinuationVerified,
        traceRowsPresent,
        parentRowIndex,
        expectedChildHashes,
        referencedChildHashes,
        rowVerification,
      };
    },

    collapseCaseBuddyDuplicateTraceEntries(rows = []) {
      const outputRows = (Array.isArray(rows) ? rows : [])
        .map(row => this.cloneRowPreservingValues(row));
      if (!outputRows.length) {
        return { rows: outputRows, removedCount: 0, removed: [] };
      }

      const headerDetected = this.isLikelyHeaderRow?.(outputRows[0]) === true;
      const { entries } = this.collectEntries(outputRows, headerDetected ? 1 : 0);
      const seenBranches = new Set();
      const rangesToRemove = [];
      const removed = [];

      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        const firstRow = entry?.rows?.[0]?.row || [];
        const fromHash = this.normalizeHashKey(firstRow?.[COL.FROM_HASH]);
        const pathLabel = this.toText(firstRow?.[COL.PATH_LABEL]).trim();
        const rowCount = Array.isArray(entry?.rows) ? entry.rows.length : 0;
        if (!fromHash || pathLabel || rowCount !== 1) return;
        const branchKey = this.getCaseBuddyTraceBranchKey(firstRow, fromHash);
        if (!branchKey) return;
        if (!seenBranches.has(branchKey)) {
          seenBranches.add(branchKey);
          return;
        }
        const start = Math.max(0, Number(entry?.startRowIndex || 0));
        const end = Math.max(start, Number(entry?.endRowIndex ?? start));
        rangesToRemove.push({ start, end });
        removed.push({
          rowIndex: start,
          spreadsheetRowNumber: start + 1,
          hash: this.toText(firstRow?.[COL.TX_HASH]),
          fromHash: this.toText(firstRow?.[COL.FROM_HASH]),
          fromAddress: this.toText(firstRow?.[COL.FROM_ADDRESS]),
          toAddress: this.toText(firstRow?.[COL.TO_ADDRESS]),
          outputIndex: this.getCaseBuddyOutputIndex(firstRow),
          branchKey,
        });
      });

      rangesToRemove
        .sort((left, right) => right.start - left.start)
        .forEach(({ start, end }) => outputRows.splice(start, end - start + 1));

      return {
        rows: outputRows,
        removedCount: removed.length,
        removed: removed.slice(0, 24),
      };
    },

    mergeCaseBuddyTraceRowsIdempotently(baseRows = [], entry = null, expectedRows = []) {
      const collapsedBefore = this.collapseCaseBuddyDuplicateTraceEntries(baseRows);
      let outputRows = collapsedBefore.rows;
      const candidates = this.normalizeCaseBuddyTraceRowsForSelectedParent(expectedRows, entry);
      const candidateBlocks = [];
      let currentBlock = null;

      candidates.forEach((row) => {
        const txHash = this.toText(row?.[COL.TX_HASH]).trim();
        if (txHash) {
          const key = this.getCaseBuddyTraceBranchKey(row, row?.[COL.FROM_HASH] || entry?.txHash || '');
          currentBlock = key ? { key, rows: [row] } : null;
          if (currentBlock) candidateBlocks.push(currentBlock);
          return;
        }
        if (currentBlock && this.rowLooksLikeCaseBuddyContinuationData(row)) {
          currentBlock.rows.push(row);
        }
      });

      const uniqueCandidates = [];
      const seenCandidateKeys = new Set();
      candidateBlocks.forEach((block) => {
        if (!block?.key || seenCandidateKeys.has(block.key)) return;
        seenCandidateKeys.add(block.key);
        uniqueCandidates.push(block);
      });

      const existingKeys = new Set();
      outputRows.forEach((row) => {
        if (!this.toText(row?.[COL.TX_HASH]).trim()) return;
        const key = this.getCaseBuddyTraceBranchKey(row, row?.[COL.FROM_HASH] || '');
        if (key) existingKeys.add(key);
      });

      const missingBlocks = uniqueCandidates.filter(({ key }) => !existingKeys.has(key));
      missingBlocks.forEach(({ key }) => existingKeys.add(key));
      const missingRows = missingBlocks.flatMap(({ rows }) => rows);

      if (missingRows.length) {
        outputRows = this.insertRowsAfterEntry(outputRows, entry, missingRows);
      }
      const collapsedAfter = this.collapseCaseBuddyDuplicateTraceEntries(outputRows);
      outputRows = collapsedAfter.rows;

      return {
        rows: outputRows,
        insertedCount: missingRows.length,
        insertedRows: missingRows.map(row => this.cloneRowPreservingValues(row)),
        alreadyPresentCount: Math.max(0, uniqueCandidates.length - missingBlocks.length),
        removedDuplicateCount: Number(collapsedBefore.removedCount || 0) + Number(collapsedAfter.removedCount || 0),
        removedDuplicates: [...(collapsedBefore.removed || []), ...(collapsedAfter.removed || [])].slice(0, 24),
        candidateCount: uniqueCandidates.length,
      };
    },

    repairCaseBuddyTracedContinuationRows(rows = []) {
      const outputRows = (Array.isArray(rows) ? rows : []).map(row => this.cloneRowPreservingValues(row));
      if (!outputRows.length) return outputRows;
      const headerDetected = this.isLikelyHeaderRow?.(outputRows[0]) === true;
      const { entries } = this.collectEntries(outputRows, headerDetected ? 1 : 0);
      if (!entries.length) return outputRows;

      const entryDescriptors = entries.map(entry => {
        const firstRow = entry?.rows?.[0]?.row || [];
        return {
          entry,
          hashKey: this.normalizeHashKey(entry?.txHash || firstRow?.[COL.TX_HASH]),
          fromHashKey: this.normalizeHashKey(firstRow?.[COL.FROM_HASH]),
          fromKey: this.normalizeAddressKey(firstRow?.[COL.FROM_ADDRESS]),
          targetKeys: new Set((entry?.rows || [])
            .map(({ row }) => this.normalizeAddressKey(row?.[COL.TO_ADDRESS]))
            .filter(Boolean)),
        };
      });

      entryDescriptors.forEach(parent => {
        if (!parent.hashKey) return;
        const children = entryDescriptors.filter(child => {
          if (!child.hashKey || child.entry?.key === parent.entry?.key) return false;
          if (child.fromHashKey !== parent.hashKey) return false;
          if (!parent.targetKeys.size || !child.fromKey) return true;
          return parent.targetKeys.has(child.fromKey);
        });
        if (!children.length) return;
        const seenBranches = new Set();
        const childHashes = children
          .filter((child) => {
            const firstRow = child.entry?.rows?.[0]?.row || [];
            const branchKey = this.getCaseBuddyTraceBranchKey(firstRow, parent.entry?.txHash || '');
            if (!branchKey || seenBranches.has(branchKey)) return false;
            seenBranches.add(branchKey);
            return true;
          })
          .map(child => child.entry.txHash)
          .filter(Boolean);
        if (!childHashes.length) return;
        const rowIndex = Math.max(0, Number(parent.entry.startRowIndex || 0));
        const row = outputRows[rowIndex] || this.makeBlankRow();
        const desired = `Continues as ${childHashes.join(', ')}`;
        const current = this.toText(row?.[COL.CONTINUES_AS]).trim();
        if (current === desired) return;
        if (current && !/^path\s+ends\.?$/iu.test(current) && !/^continues\s+as\b/iu.test(current)) return;
        row[COL.CONTINUES_AS] = desired;
        this.removeRawDataPathEndsSentence(row);
        outputRows[rowIndex] = row;
      });
      return outputRows;
    },

    buildCaseBuddyRawGraphAudit(rows = []) {
      const cleanRows = this.sanitizeCaseBuddySnapshotRows(Array.isArray(rows) ? rows : [])
        .filter(row => this.rowLooksLikeCaseBuddyEntryData(row));
      const headerDetected = cleanRows.length && this.isLikelyHeaderRow?.(cleanRows[0]) === true;
      const { entries } = this.collectEntries(cleanRows, headerDetected ? 1 : 0);
      const hashCounts = new Map();
      entries.forEach(entry => {
        const key = this.normalizeHashKey(entry?.txHash);
        if (key) hashCounts.set(key, (hashCounts.get(key) || 0) + 1);
      });
      const examples = [];
      let rootMissingFromAddressCount = 0;
      let tracedButPathEndsCount = 0;
      let linkedChildButPathEndsCount = 0;
      let blankHashContinuationCount = 0;
      cleanRows.forEach((row, index) => {
        if (!this.toText(row?.[COL.TX_HASH]).trim() && this.rowLooksLikeCaseBuddyContinuationData(row)) {
          blankHashContinuationCount += 1;
        }
        const tokenText = [row?.[COL.TOKEN], row?.[COL.CHAIN_NAME], row?.[COL.CHAIN_ID]]
          .map(value => this.toText(value).toLowerCase()).join(' ');
        const root = !this.toText(row?.[COL.FROM_HASH]).trim() && !!this.toText(row?.[COL.TX_HASH]).trim();
        if (root && /\b(?:btc|bitcoin)\b/u.test(tokenText) && !this.toText(row?.[COL.FROM_ADDRESS]).trim()) {
          rootMissingFromAddressCount += 1;
          if (examples.length < 12) examples.push({ rowNumber: index + 1, issue: 'btc-root-missing-from-address', hash: this.toText(row?.[COL.TX_HASH]), toAddress: this.toText(row?.[COL.TO_ADDRESS]) });
        }
        const pathEnds = /^path\s+ends\.?$/iu.test(this.toText(row?.[COL.CONTINUES_AS]).trim());
        const notes = `${this.toText(row?.[COL.SYSTEM_NOTES])}\n${this.toText(row?.[COL.TOOL88_NOTES])}`;
        if (pathEnds && /tool\s*88:.*traced\s+through|action\s*=\s*traced-through/iu.test(notes)) {
          tracedButPathEndsCount += 1;
          if (examples.length < 12) examples.push({ rowNumber: index + 1, issue: 'traced-row-still-path-ends', hash: this.toText(row?.[COL.TX_HASH]), toAddress: this.toText(row?.[COL.TO_ADDRESS]) });
        }
      });
      entries.forEach(parent => {
        const firstRow = parent?.rows?.[0]?.row || [];
        if (!/^path\s+ends\.?$/iu.test(this.toText(firstRow?.[COL.CONTINUES_AS]).trim())) return;
        const parentHash = this.normalizeHashKey(parent?.txHash);
        const targetKeys = new Set((parent?.rows || []).map(({ row }) => this.normalizeAddressKey(row?.[COL.TO_ADDRESS])).filter(Boolean));
        const hasChild = entries.some(child => {
          if (child?.key === parent?.key) return false;
          const childRow = child?.rows?.[0]?.row || [];
          if (this.normalizeHashKey(childRow?.[COL.FROM_HASH]) !== parentHash) return false;
          const fromKey = this.normalizeAddressKey(childRow?.[COL.FROM_ADDRESS]);
          return !targetKeys.size || !fromKey || targetKeys.has(fromKey);
        });
        if (hasChild) linkedChildButPathEndsCount += 1;
      });
      return {
        rowCount: cleanRows.length,
        entryCount: entries.length,
        duplicateHashEntryCount: Array.from(hashCounts.values()).filter(count => count > 1).reduce((sum, count) => sum + count, 0),
        duplicateHashGroupCount: Array.from(hashCounts.values()).filter(count => count > 1).length,
        rootMissingFromAddressCount,
        tracedButPathEndsCount,
        linkedChildButPathEndsCount,
        blankHashContinuationCount,
        blockingIssueCount: rootMissingFromAddressCount + tracedButPathEndsCount + linkedChildButPathEndsCount,
        examples,
      };
    },

    getCaseBuddyExportableTraceRows(rows = []) {
      return this.sanitizeCaseBuddySnapshotRows(Array.isArray(rows) ? rows : [])
        .filter(row => this.rowLooksLikeCaseBuddyEntryData(row));
    },

    buildCaseBuddySnapshotOutputRows() {
      let outputRows = this.sanitizeCaseBuddySnapshotRows(this.getCurrentOutputRowsForExport())
        .filter(row => this.rowLooksLikeCaseBuddyEntryData(row));
      if (!outputRows.length && this.swapApplyResult?.rows?.length) {
        outputRows = this.sanitizeCaseBuddySnapshotRows(this.swapApplyResult.rows)
          .filter(row => this.rowLooksLikeCaseBuddyEntryData(row));
      }
      if (this.traceResult?.btcAnalysis && this.selectedEntry) {
        outputRows = this.updateBtcSelectedEntryTransactionDetailsInRows(
          outputRows,
          this.selectedEntry,
          this.selectedTargetAddress,
          this.traceResult?.relevantTransactions || [],
          this.traceResult
        );
        outputRows = this.updateBtcSelectedEntryPriorBalanceInRows(outputRows, this.selectedEntry, this.traceResult);
      }
      if (this.traceResult?.accountAnalysis && this.selectedEntry) {
        outputRows = this.updateAccountSelectedEntryPriorBalanceInRows(outputRows, this.selectedEntry, this.traceResult);
      }
      outputRows = this.collapseCaseBuddyDuplicateTraceEntries(outputRows).rows;
      outputRows = this.repairCaseBuddyTracedContinuationRows(outputRows);
      const exitRows = this.normalizeCaseBuddyTraceRowsForSelectedParent(this.traceResult?.exitRows, this.selectedEntry);

      if (!exitRows.length) return outputRows;

      const selectedHash = this.selectedEntry?.txHash || this.selectedEntry?.row?.[COL.TX_HASH] || '';
      const childHashes = this.getCaseBuddyExitChildHashes(this.traceResult?.exitTransactions || [], selectedHash);
      const fallbackChildHashes = this.getCaseBuddyExitChildHashes(exitRows, selectedHash);
      const resolvedChildHashes = childHashes.length ? childHashes : fallbackChildHashes;

      // Repair the parent transition on every recovery pass, even when every
      // child row is already present. The former early return left the parent at
      // "Path ends.", so the next flow scan selected the same parent and appended
      // the same child again.
      if (resolvedChildHashes.length && this.selectedEntry) {
        outputRows = this.markEntryContinuesAs(
          outputRows,
          this.selectedEntry,
          resolvedChildHashes
        );
      }
      outputRows = this.repairCaseBuddyTracedContinuationRows(outputRows);

      const merge = this.mergeCaseBuddyTraceRowsIdempotently(
        outputRows,
        this.selectedEntry,
        exitRows
      );
      outputRows = this.repairCaseBuddyTracedContinuationRows(merge.rows);
      return this.sanitizeCaseBuddySnapshotRows(this.trimTrailingBlankRows(outputRows))
        .filter(row => this.rowLooksLikeCaseBuddyEntryData(row));
    },

    caseBuddySnapshotRequiresTraceExitRows(options = {}) {
      const actionText = [
        options.integrationAction,
        options.action,
        options.status
      ]
        .map((value) => this.toText(value))
        .filter(Boolean)
        .join(' ');
      const messageText = this.toText(options.message);
      const tracePattern = /manual[-_\s]*trace|trace[-_\s]*through|traced[-_\s]*through|trace[-_\s]*fetch|candidate\s+exit|next[-_\s]*hop/iu;
      const nonTraceActionPattern = /manual[-_\s]*(?:path[-_\s]*ending|swap|bridge)|path[-_\s]*ending|low[-_\s]*value|swap|bridge|service[-_\s]*stop|selected[-_\s]*output[-_\s]*missing/iu;
      if (nonTraceActionPattern.test(actionText) && !tracePattern.test(actionText)) return false;
      return tracePattern.test(actionText) || (!actionText && tracePattern.test(messageText)) || /trace[-_\s]*through\s+staged|trace[-_\s]*through\s+action\s+is\s+ready/iu.test(messageText);
    },

    buildCaseBuddyHopSolverSnapshot(options = {}) {
      const includeRows = options.includeRows !== false;
      const traceExitRows = includeRows ? this.normalizeCaseBuddyTraceRowsForSelectedParent(this.traceResult?.exitRows, this.selectedEntry) : [];
      const dormantResult = this.getTool88CurrentDormantResult();
      const hasValidDormantResult = !!(dormantResult && dormantResult.status === 'valid');
      const requireTraceExitRows = includeRows && this.caseBuddySnapshotRequiresTraceExitRows(options) && !hasValidDormantResult;
      const outputRows = includeRows
        ? (requireTraceExitRows && !traceExitRows.length ? [] : this.buildCaseBuddySnapshotOutputRows())
        : [];
      const flowComplete = this.flowCompletionResult?.done === true;
      const activeRoot = this.caseBuddyActiveRootContext ? { ...this.caseBuddyActiveRootContext } : null;
      const inferenceBlock = flowComplete ? null : (this.caseBuddySelectedOutputInferenceBlock || null);
      const status = flowComplete ? 'flow-complete' : (inferenceBlock ? "selected-output-missing" : this.toText(options.status || this.deriveCaseBuddySnapshotStatus(options.integrationAction)));
      const guardrail = flowComplete ? null : (options.guardrail || (inferenceBlock ? {
        reason: inferenceBlock.reason || 'selected-output-missing',
        message: inferenceBlock.message || 'Selected output amount/token could not be inferred.',
      } : null));
      const selectedEntry = this.buildCaseBuddySelectedEntrySummary();
      const message = this.toText(options.message || inferenceBlock?.message || this.simpleViewMessage || this.flowCompletionMessage || this.statusMessage || "");
      const swapApplyRows = includeRows && Array.isArray(this.swapApplyResult?.rows)
        ? this.swapApplyResult.rows.map((row) => this.cloneRowPreservingValues(row))
        : [];
      const bridgeApplyRows = includeRows && Array.isArray(this.bridgeApplyResult?.rows)
        ? this.bridgeApplyResult.rows.map((row) => this.cloneRowPreservingValues(row))
        : [];
      const swapApplySnapshot = this.swapApplyResult ? {
        ...this.swapApplyResult,
        rows: swapApplyRows,
        row: this.swapApplyResult.row ? this.cloneRowPreservingValues(this.swapApplyResult.row) : (swapApplyRows[0] || null),
      } : null;
      const bridgeApplySnapshot = this.bridgeApplyResult ? {
        ...this.bridgeApplyResult,
        rows: bridgeApplyRows,
        row: this.bridgeApplyResult.row ? this.cloneRowPreservingValues(this.bridgeApplyResult.row) : (bridgeApplyRows[0] || null),
      } : null;
      return {
        integrationVersion: TOOL88_CASE_BUDDY_INTEGRATION_VERSION,
        status,
        message,
        guardrail,
        integrationAction: this.toText(options.integrationAction || ""),
        traceDirection: this.traceDirection,
        hopLogicType: this.hopLogicType,
        activeRoot,
        activeRootLabel: this.caseBuddyActiveRootLabel(),
        selectedEntry,
        selectedTargetAddress: this.selectedTargetAddress,
        countLookup: this.countLookupResult ? { ...this.countLookupResult } : null,
        transactionCount: this.countLookupResult?.transactionCount ?? this.countLookupResult?.transaction_count ?? null,
        knownTransactionCount: this.countLookupResult?.transactionCount ?? this.countLookupResult?.transaction_count ?? null,
        targetCandidates: (this.targetCandidates || []).map((candidate) => ({
          address: candidate.address,
          key: candidate.key,
          rowNumbers: Array.isArray(candidate.rowNumbers) ? candidate.rowNumbers.slice() : [],
        })),
        decision: this.buildCaseBuddyDecisionSummary(),
        result: this.buildCaseBuddyResultSummary(),
        swapApplyResult: swapApplySnapshot,
        bridgeApplyResult: bridgeApplySnapshot,
        caseBuddyLiteSwapRows: swapApplyRows,
        caseBuddySwapRows: swapApplyRows,
        caseBuddyLiteBridgeRows: bridgeApplyRows,
        caseBuddyBridgeRows: bridgeApplyRows,
        dormantResult: dormantResult ? { ...dormantResult } : null,
        dormant: dormantResult ? { ...dormantResult } : null,
        flowComplete,
        flowCompletion: this.flowCompletionResult ? {
          done: this.flowCompletionResult.done === true,
          totalEntries: this.flowCompletionResult.totalEntries || 0,
          endingCount: this.flowCompletionResult.endingCount || 0,
          incompleteCount: this.flowCompletionResult.incompleteCount || 0,
          completeCount: this.flowCompletionResult.completeCount || 0,
          summary: this.flowCompletionResult.summary || this.flowCompletionMessage || "",
        } : null,
        flowSelection: this.lastFlowSelectionResult ? { ...this.lastFlowSelectionResult } : null,
        canContinue: this.simpleResultReadyForContinue === true || hasValidDormantResult,
        needsManualReview: !flowComplete && (options.needsManualReview === true || status === "manual-review" || status === "selected-output-missing" || status === "recommendation-no-output" || this.autoDecisionPreview?.action === "review"),
        needsTargetSelection: !flowComplete && this.simpleHasTargetAmbiguity === true,
        unverifiedOutput: !flowComplete && (status === "unverified-output" || Boolean(this.traceResult && Array.isArray(this.traceResult.exitRows) && this.traceResult.exitRows.length && this.traceResult.outputMutationVerified !== true)),
        isProcessing: this.isProcessing,
        warnings: (this.warnings || []).slice(),
        reportText: this.reportText || "",
        outputRows,
        rawGraphAudit: includeRows ? this.buildCaseBuddyRawGraphAudit(outputRows) : null,
        exitRows: traceExitRows,
        outputTsv: includeRows ? this.rowsToDelimitedText(outputRows, "\t") : "",
        runState: this.buildCaseBuddyAutomationRunStateSnapshot(this.caseBuddyIntegrationRunState),
        stallDiagnostics: this.buildCaseBuddyAutomationStallDiagnostics({
          status,
          integrationAction: this.toText(options.integrationAction || ""),
          selectedEntry,
          selectedTargetAddress: this.selectedTargetAddress,
          decision: this.buildCaseBuddyDecisionSummary(),
          result: this.buildCaseBuddyResultSummary(),
          flowComplete,
          flowCompletion: this.flowCompletionResult || null,
          outputRows,
        }, { runState: this.caseBuddyIntegrationRunState }),
        generatedAtUtc: new Date().toISOString(),
      };
    },

    isTraceResultReadyForContinue(result = null) {
      if (!result) return false;
      if (result.preventPathEnding) return false;
      if (!this.getCaseBuddyExportableTraceRows(result.exitRows).length) return false;
      return result.outputMutationVerified === true;
    },

    getCurrentOutputRowsForExport() {
      const snapshotRows = Array.isArray(this.outputSnapshotRows)
        ? this.trimTrailingBlankRows(this.outputSnapshotRows)
        : [];
      const visibleRows = this.trimTrailingBlankRows(this.$refs.outputSpreadsheet?.getData?.() || []);
      const snapshotScore = this.scoreCaseBuddyOutputRowsForExport(snapshotRows);
      const visibleScore = this.scoreCaseBuddyOutputRowsForExport(visibleRows);
      const source = visibleScore > snapshotScore ? visibleRows : snapshotRows;
      if (source.some((row) => this.rowHasAnyData(row))) {
        return this.sanitizeCaseBuddySnapshotRows(source)
          .filter((row) => this.rowLooksLikeCaseBuddyEntryData(row));
      }
      return this.sanitizeCaseBuddySnapshotRows(visibleRows)
        .filter((row) => this.rowLooksLikeCaseBuddyEntryData(row));
    },

    rowsToDelimitedText(rows, delimiter = "\t") {
      return (rows || []).map((row) => this.cloneRowPreservingValues(row).slice(0, COLUMN_COUNT).map((cell) => {
        const text = String(cell ?? "");
        const quotePattern = delimiter === "," ? /[",\n\r]/ : /["\t\n\r]/;
        if (quotePattern.test(text)) return `"${text.replace(/"/g, '""')}"`;
        return text;
      }).join(delimiter)).join("\n");
    },

    async copySimpleOutputToClipboard() {
      const rows = this.getCurrentOutputRowsForExport();
      if (!rows.length) {
        this.simpleViewMessage = "No output rows are available to copy yet.";
        return;
      }
      const text = this.rowsToDelimitedText(rows, "\t");
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          this.simpleViewMessage = `Copied ${this.formatInteger(rows.length)} raw output row${rows.length === 1 ? "" : "s"} to the clipboard.`;
          return;
        }
      } catch (error) {
        this.simpleViewMessage = `Clipboard copy failed: ${error?.message || "browser denied clipboard access"}.`;
        return;
      }
      this.simpleViewMessage = "Clipboard is unavailable in this browser.";
    },

    downloadSimpleOutputCsv() {
      const rows = this.getCurrentOutputRowsForExport();
      if (!rows.length) {
        this.simpleViewMessage = "No output rows are available to download yet.";
        return;
      }
      const csv = this.rowsToDelimitedText(rows, ",");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tool88-output-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      this.simpleViewMessage = `Downloaded ${this.formatInteger(rows.length)} raw output row${rows.length === 1 ? "" : "s"}.`;
    },

    saveUserSettings() {
      safeWriteTool88Settings({
        traceDirection: this.traceDirection,
        hopLogicType: this.hopLogicType,
        selectedExampleKey: this.selectedExampleKey,
        tool88NoteColumn: this.tool88NoteColumn,
        enableAdditionalServiceChecks: this.enableAdditionalServiceChecks !== false,
        enableDepositEntityHopExtension: this.enableDepositEntityHopExtension === true,
        enableNativeDirectSpendMatchGuard: this.enableNativeDirectSpendMatchGuard === true,
        enableSessionDataCache: this.enableSessionDataCache !== false,
        flowCompletionMaxDepth: this.normalizedFlowCompletionMaxDepth,
        flowCompletionValueTooLowEnabled: this.flowCompletionValueTooLowEnabled !== false,
        lowValueThresholdBtc: this.lowValueThresholdBtc,
        lowValueThresholdNative: this.lowValueThresholdNative,
        lowValueThresholdStable: this.lowValueThresholdStable,
        lowValueThresholdOther: this.lowValueThresholdOther,
        flowCompletionTraceThroughEntitiesText: this.flowCompletionTraceThroughEntitiesText,
        directAutomationSpeedMultiplier: this.normalizeDirectAutomationSpeed(this.directAutomationSpeedMultiplier),
      });
    },

    resetUserSettings() {
      this.traceDirection = DEFAULT_USER_SETTINGS.traceDirection;
      this.hopLogicType = DEFAULT_USER_SETTINGS.hopLogicType;
      this.selectedExampleKey = DEFAULT_USER_SETTINGS.selectedExampleKey;
      this.tool88NoteColumn = DEFAULT_USER_SETTINGS.tool88NoteColumn;
      this.enableAdditionalServiceChecks = DEFAULT_USER_SETTINGS.enableAdditionalServiceChecks;
      this.enableDepositEntityHopExtension = DEFAULT_USER_SETTINGS.enableDepositEntityHopExtension;
      this.enableNativeDirectSpendMatchGuard = DEFAULT_USER_SETTINGS.enableNativeDirectSpendMatchGuard;
      this.caseBuddyNativeDirectSpendGuardForcedOff = false;
      this.enableSessionDataCache = DEFAULT_USER_SETTINGS.enableSessionDataCache;
      this.flowCompletionMaxDepth = DEFAULT_USER_SETTINGS.flowCompletionMaxDepth;
      this.flowCompletionValueTooLowEnabled = DEFAULT_USER_SETTINGS.flowCompletionValueTooLowEnabled;
      this.lowValueThresholdBtc = DEFAULT_USER_SETTINGS.lowValueThresholdBtc;
      this.lowValueThresholdNative = DEFAULT_USER_SETTINGS.lowValueThresholdNative;
      this.lowValueThresholdStable = DEFAULT_USER_SETTINGS.lowValueThresholdStable;
      this.lowValueThresholdOther = DEFAULT_USER_SETTINGS.lowValueThresholdOther;
      this.flowCompletionTraceThroughEntitiesText = DEFAULT_USER_SETTINGS.flowCompletionTraceThroughEntitiesText;
      this.directAutomationSpeedMultiplier = DEFAULT_USER_SETTINGS.directAutomationSpeedMultiplier;
      this.flowCompletionScanResult = null;
      this.flowCompletionMessage = "";
      this.flowAbsorbResult = null;
      this.saveUserSettings();
      this.resetDecisionState();
    },

    saveTokenManagerOverrides() {
      safeWriteTool88TokenOverrides(this.tokenManagerOverrides);
    },

    resetTokenManagerDraft() {
      this.tokenManagerDraft = {
        symbol: "",
        chainKey: "evm:1",
        contractAddress: "",
        decimals: 6,
      };
    },

    addOrUpdateTokenOverride() {
      const entry = makeFlatTokenEntry(
        this.tokenManagerDraft.symbol,
        this.tokenManagerDraft.decimals,
        this.tokenManagerDraft.chainKey,
        this.tokenManagerDraft.contractAddress,
        "custom"
      );

      if (!entry) {
        this.tokenManagerMessage = "Enter a symbol, chain, contract address, and decimals before adding a token.";
        return;
      }

      const existing = Array.isArray(this.tokenManagerOverrides.customTokens)
        ? this.tokenManagerOverrides.customTokens.filter((token) => token.key !== entry.key)
        : [];

      this.tokenManagerOverrides = {
        customTokens: [...existing, entry],
        removedTokenKeys: (this.tokenManagerOverrides.removedTokenKeys || []).filter((key) => key !== entry.key),
      };
      this.saveTokenManagerOverrides();
      this.tokenManagerMessage = `Saved trusted token ${entry.symbol} on ${this.formatTokenChainLabel(entry.chainKey)}.`;
      this.resetTokenManagerDraft();
    },

    removeTokenRegistryEntry(token) {
      if (!token || !token.key) {
        return;
      }

      if (token.source === "custom") {
        this.tokenManagerOverrides = {
          customTokens: (this.tokenManagerOverrides.customTokens || []).filter((row) => row.key !== token.key),
          removedTokenKeys: this.tokenManagerOverrides.removedTokenKeys || [],
        };
        this.tokenManagerMessage = `Deleted user token ${token.symbol}.`;
      } else {
        const removed = new Set(this.tokenManagerOverrides.removedTokenKeys || []);
        removed.add(token.key);
        this.tokenManagerOverrides = {
          customTokens: this.tokenManagerOverrides.customTokens || [],
          removedTokenKeys: Array.from(removed),
        };
        this.tokenManagerMessage = `Hidden shipped token ${token.symbol}. Use Reset User Tokens to restore shipped rows.`;
      }

      this.saveTokenManagerOverrides();
    },

    resetTokenManagerOverrides() {
      this.tokenManagerOverrides = { customTokens: [], removedTokenKeys: [] };
      this.saveTokenManagerOverrides();
      this.tokenManagerMessage = "User-added tokens and hidden shipped-token choices were reset.";
    },

    extractTokenRowsFromImportPayload(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }
      if (!payload || typeof payload !== "object") {
        return [];
      }
      if (Array.isArray(payload.tokens)) return payload.tokens;
      if (Array.isArray(payload.result)) return payload.result;
      if (Array.isArray(payload.tokenRegistry)) return payload.tokenRegistry;
      if (Array.isArray(payload.default)) return payload.default;
      return [];
    },

    importTokenRegistryFromText() {
      const text = this.toText(this.tokenManagerImportText);
      if (!text) {
        this.tokenManagerMessage = "Paste token JSON from the Unity exporter first.";
        return;
      }

      try {
        const cleaned = text
          .replace(/^\s*\/\/.*$/gm, "")
          .trim()
          .replace(/^export\s+default\s+/i, "")
          .replace(/^export\s+const\s+TOOL88_SHIPPED_TOKEN_REGISTRY\s*=\s*Object\.freeze\(/i, "")
          .replace(/^export\s+const\s+TOOL88_SHIPPED_TOKEN_REGISTRY\s*=\s*/i, "")
          .replace(/^const\s+SHIPPED_TOKEN_REGISTRY\s*=\s*Object\.freeze\(/i, "")
          .replace(/^const\s+SHIPPED_TOKEN_REGISTRY\s*=\s*/i, "")
          .replace(/\)\s*;\s*export\s+default[\s\S]*$/i, "")
          .replace(/\)\s*;\s*$/u, "")
          .replace(/;\s*$/u, "");
        const parsed = JSON.parse(cleaned);
        const rows = expandTool88TokenSeedEntries(this.extractTokenRowsFromImportPayload(parsed), "custom");

        if (!rows.length) {
          this.tokenManagerMessage = "No usable token rows were found in that import.";
          return;
        }

        const byKey = new Map();
        (this.tokenManagerOverrides.customTokens || []).forEach((token) => byKey.set(token.key, token));
        rows.forEach((token) => byKey.set(token.key, token));

        this.tokenManagerOverrides = {
          customTokens: Array.from(byKey.values()),
          removedTokenKeys: this.tokenManagerOverrides.removedTokenKeys || [],
        };
        this.saveTokenManagerOverrides();
        this.tokenManagerImportText = "";
        this.tokenManagerMessage = `Imported ${this.formatInteger(rows.length)} token contract row${rows.length === 1 ? "" : "s"} as user overrides.`;
      } catch (error) {
        this.tokenManagerMessage = `Token import failed: ${error?.message || "invalid JSON"}.`;
      }
    },

    downloadTokenManagerOverrides() {
      const payload = {
        exportedAt: new Date().toISOString(),
        customTokens: this.tokenManagerOverrides.customTokens || [],
        removedTokenKeys: this.tokenManagerOverrides.removedTokenKeys || [],
      };
      const text = JSON.stringify(payload, null, 2);
      const blob = new Blob([text], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "tool88-token-manager-overrides.json";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    saveTraceThroughEntityOverrides() {
      safeWriteTool88TraceThroughEntityOverrides(this.traceThroughEntityOverrides);
      this.flowCompletionScanResult = null;
    },

    resetTraceThroughEntityDraft() {
      this.traceThroughEntityDraft = {
        canonicalName: "",
        aliasesText: "",
        addressesText: "",
        note: "",
      };
    },

    addOrUpdateTraceThroughEntityOverride() {
      const entry = normalizeTraceThroughEntityEntry({
        canonicalName: this.traceThroughEntityDraft.canonicalName,
        aliases: splitTraceThroughEntityList(this.traceThroughEntityDraft.aliasesText),
        addresses: splitTraceThroughEntityList(this.traceThroughEntityDraft.addressesText),
        note: this.traceThroughEntityDraft.note,
      }, "custom");

      if (!entry) {
        this.traceThroughEntityMessage = "Enter at least an entity name before adding a trace-through entity.";
        return;
      }

      const existing = Array.isArray(this.traceThroughEntityOverrides.customEntities)
        ? this.traceThroughEntityOverrides.customEntities.filter((entity) => entity.key !== entry.key)
        : [];

      this.traceThroughEntityOverrides = {
        customEntities: [...existing, entry],
        removedEntityKeys: (this.traceThroughEntityOverrides.removedEntityKeys || []).filter((key) => key !== entry.key),
      };
      this.saveTraceThroughEntityOverrides();
      this.traceThroughEntityMessage = `Saved trace-through entity ${entry.canonicalName}.`;
      this.resetTraceThroughEntityDraft();
    },

    removeTraceThroughEntityEntry(entity) {
      if (!entity || !entity.key) return;

      if (entity.source === "custom") {
        this.traceThroughEntityOverrides = {
          customEntities: (this.traceThroughEntityOverrides.customEntities || []).filter((row) => row.key !== entity.key),
          removedEntityKeys: this.traceThroughEntityOverrides.removedEntityKeys || [],
        };
        this.traceThroughEntityMessage = `Deleted user trace-through entity ${entity.canonicalName}.`;
      } else {
        const removed = new Set(this.traceThroughEntityOverrides.removedEntityKeys || []);
        removed.add(entity.key);
        this.traceThroughEntityOverrides = {
          customEntities: this.traceThroughEntityOverrides.customEntities || [],
          removedEntityKeys: Array.from(removed),
        };
        this.traceThroughEntityMessage = `Hidden shipped trace-through entity ${entity.canonicalName}.`;
      }

      this.saveTraceThroughEntityOverrides();
    },

    restoreAllHiddenTraceThroughEntities() {
      this.traceThroughEntityOverrides = {
        customEntities: this.traceThroughEntityOverrides.customEntities || [],
        removedEntityKeys: [],
      };
      this.saveTraceThroughEntityOverrides();
      this.traceThroughEntityMessage = "Hidden shipped trace-through entities were restored.";
    },

    resetTraceThroughEntityOverrides() {
      this.traceThroughEntityOverrides = { customEntities: [], removedEntityKeys: [] };
      this.saveTraceThroughEntityOverrides();
      this.traceThroughEntityMessage = "User-added trace-through entities and hidden shipped-entity choices were reset.";
    },

    extractTraceThroughEntityRowsFromImportPayload(payload) {
      if (Array.isArray(payload)) return payload;
      if (!payload || typeof payload !== "object") return [];
      if (Array.isArray(payload.entities)) return payload.entities;
      if (Array.isArray(payload.traceThroughEntities)) return payload.traceThroughEntities;
      if (Array.isArray(payload.trace_through_entities)) return payload.trace_through_entities;
      if (Array.isArray(payload.result)) return payload.result;
      if (Array.isArray(payload.default)) return payload.default;
      return [];
    },

    importTraceThroughEntityRegistryFromText() {
      const text = this.toText(this.traceThroughEntityImportText);
      if (!text) {
        this.traceThroughEntityMessage = "Paste trace-through entity JSON first.";
        return;
      }

      try {
        const cleaned = text
          .replace(/^\s*\/\/.*$/gm, "")
          .trim()
          .replace(/^export\s+default\s+/i, "")
          .replace(/^export\s+const\s+TOOL88_SHIPPED_TRACE_THROUGH_ENTITIES\s*=\s*Object\.freeze\(/i, "")
          .replace(/^export\s+const\s+TOOL88_SHIPPED_TRACE_THROUGH_ENTITIES\s*=\s*/i, "")
          .replace(/\)\s*;\s*export\s+default[\s\S]*$/i, "")
          .replace(/\)\s*;\s*$/u, "")
          .replace(/;\s*$/u, "");
        const parsed = JSON.parse(cleaned);
        const rows = normalizeTraceThroughEntityEntries(this.extractTraceThroughEntityRowsFromImportPayload(parsed), "custom");

        if (!rows.length) {
          this.traceThroughEntityMessage = "No usable trace-through entity rows were found in that import.";
          return;
        }

        const byKey = new Map();
        (this.traceThroughEntityOverrides.customEntities || []).forEach((entity) => byKey.set(entity.key, entity));
        rows.forEach((entity) => byKey.set(entity.key, entity));

        this.traceThroughEntityOverrides = {
          customEntities: Array.from(byKey.values()),
          removedEntityKeys: this.traceThroughEntityOverrides.removedEntityKeys || [],
        };
        this.saveTraceThroughEntityOverrides();
        this.traceThroughEntityImportText = "";
        this.traceThroughEntityMessage = `Imported ${this.formatInteger(rows.length)} trace-through entit${rows.length === 1 ? "y" : "ies"}.`;
      } catch (error) {
        this.traceThroughEntityMessage = `Trace-through entity import failed: ${error?.message || "invalid JSON"}.`;
      }
    },

    downloadTraceThroughEntityOverrides() {
      const payload = {
        exportedAt: new Date().toISOString(),
        customEntities: this.traceThroughEntityOverrides.customEntities || [],
        removedEntityKeys: this.traceThroughEntityOverrides.removedEntityKeys || [],
      };
      const text = JSON.stringify(payload, null, 2);
      const blob = new Blob([text], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "tool88-trace-through-entity-overrides.json";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    formatTraceThroughEntityAliases(entity = {}) {
      const aliases = Array.isArray(entity.aliases) ? entity.aliases : [];
      if (!aliases.length) return "";
      const shown = aliases.slice(0, 4).join(", ");
      return aliases.length > 4 ? `${shown} +${aliases.length - 4}` : shown;
    },

    formatTraceThroughEntityAddresses(entity = {}) {
      const addresses = Array.isArray(entity.addresses) ? entity.addresses : [];
      if (!addresses.length) return "";
      const shown = addresses.slice(0, 3).map((address) => this.shortenMiddle(address, 12, 10)).join(", ");
      return addresses.length > 3 ? `${shown} +${addresses.length - 3}` : shown;
    },

    normalizeTraceThroughEntityComparisonValue(value) {
      return normalizeTraceThroughEntityComparable(this.cleanAttributionLabel(value));
    },

    traceThroughEntityLabelMatches(entity, label) {
      const labelComparable = this.normalizeTraceThroughEntityComparisonValue(label);
      if (!labelComparable || !entity) return false;
      const names = [entity.canonicalName, ...(entity.aliases || [])]
        .map((value) => this.normalizeTraceThroughEntityComparisonValue(value))
        .filter(Boolean);
      return names.some((name) => {
        if (labelComparable === name) return true;
        if (name.includes(".") && labelComparable.endsWith(name)) return true;
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return new RegExp(`(^|\\b)${escaped}(\\b|$)`, "i").test(labelComparable);
      });
    },

    findTraceThroughEntityByLabel(label) {
      return this.effectiveTraceThroughEntityRegistry.find((entity) => this.traceThroughEntityLabelMatches(entity, label)) || null;
    },

    findTraceThroughEntityByAddress(address) {
      const key = this.normalizeAddressKey(address);
      if (!key) return null;
      return this.effectiveTraceThroughEntityRegistry.find((entity) =>
        (entity.addresses || []).some((candidate) => this.normalizeAddressKey(candidate) === key)
      ) || null;
    },

    getAutomaticTraceThroughEntityOverrideMatch(selectedAddress = this.selectedTargetAddress, labels = null) {
      const addressMatch = this.findTraceThroughEntityByAddress(selectedAddress);
      if (addressMatch) {
        return { ...addressMatch, matchedAddress: selectedAddress, matchedLabel: "" };
      }
      const labelList = Array.isArray(labels) ? labels : this.getAutomaticDecisionAttributionLabels();
      for (const label of labelList) {
        const labelMatch = this.findTraceThroughEntityByLabel(label);
        if (labelMatch) {
          return { ...labelMatch, matchedAddress: "", matchedLabel: this.cleanAttributionLabel(label) };
        }
      }
      return null;
    },

    appendRawDataTraceThroughEntityAttributionNote(row, entityMatch, selectedAddress = this.selectedTargetAddress) {
      if (!row || !entityMatch) return row;
      const label = this.cleanAttributionLabel(entityMatch.canonicalName || entityMatch.matchedLabel || "").replace(/[.\s]+$/g, "");
      if (!label) return row;
      const existing = this.toText(row[COL.SYSTEM_NOTES]).trim();
      if (!existing.toLowerCase().includes(label.toLowerCase())) {
        row[COL.SYSTEM_NOTES] = this.appendSentenceNote(existing, `${label}.`);
      }
      const attributionColumnIndex = this.getAttributionColumnIndex();
      if (!this.toText(row[attributionColumnIndex]) && !this.isSameAddressLabel(label, selectedAddress)) {
        row[attributionColumnIndex] = label;
      }
      return row;
    },

    getBridgeDisplayName(candidate = {}) {
      const candidateValues = [
        candidate.serviceName,
        candidate.service,
        candidate.bridgeName,
        candidate.bridge_service,
        candidate.providerLabel,
        candidate.methodLabel,
        this.attributionLookupResult?.displayLabel,
        this.attributionLookupResult?.serverNote,
        this.attributionLookupResult?.vendorLabel,
      ];
      for (const value of candidateValues) {
        const clean = this.cleanAttributionLabel(value).replace(/[.\s]+$/g, "");
        if (!clean || /^(unknown|bridge|bridge solver|no hit|no visible hit)$/i.test(clean)) continue;
        const matched = this.getMatchedBridgeServiceName(clean);
        if (matched) return matched;
        if (this.hasBridgeSignal(clean)) return clean;
      }
      const method = this.toText(candidate.methodLabel || candidate.method || "");
      const matchedFromMethod = this.getMatchedBridgeServiceName(method);
      if (matchedFromMethod) return matchedFromMethod;
      if (/bk|bitget/i.test(method)) return "BKBridge";
      if (/transit/i.test(method)) return "TransitSwap";
      if (/swft/i.test(method)) return "SWFT Blockchain";
      return "bridge service";
    },

    appendRawDataBridgeInitiationNote(row, bridgeName = "bridge service") {
      if (!row) return row;
      const clean = this.cleanAttributionLabel(bridgeName).replace(/[.\s]+$/g, "") || "bridge service";
      row[COL.SYSTEM_NOTES] = this.appendSentenceNote(row[COL.SYSTEM_NOTES], `The funds are bridged using ${clean}.`);
      return row;
    },

    appendRawDataBridgeReceiveNote(row, bridgeName = "bridge service", destinationNetwork = "") {
      if (!row) return row;
      const clean = this.cleanAttributionLabel(bridgeName).replace(/[.\s]+$/g, "") || "bridge service";
      const network = this.cleanAttributionLabel(destinationNetwork || "destination network").replace(/[.\s]+$/g, "") || "destination network";
      row[COL.SYSTEM_NOTES] = this.appendSentenceNote(row[COL.SYSTEM_NOTES], `The funds are received on the ${network} network via ${clean}.`);
      return row;
    },

    appendRawDataSwapNote(row, candidate = {}, attributionLabel = "") {
      if (!row) return row;
      const sentToken = this.toText(candidate.sent?.token || candidate.fundsIn?.token || "TOKEN").trim() || "TOKEN";
      const receivedToken = this.toText(candidate.received?.token || row[COL.TOKEN] || "TOKEN").trim() || "TOKEN";
      const cleanAttribution = this.cleanAttributionLabel(attributionLabel || candidate.attributionLabel || "swap service").replace(/[.\s]+$/g, "") || "swap service";
      const sentAmount = Number(candidate.sent?.amount || 0);
      const contractAddress = this.toText(candidate.swapContractAddress || candidate.selectedAddress || this.selectedTargetAddress || "").trim();
      const contractClause = contractAddress ? `, contract: ${contractAddress}` : "";
      const sentClause = Number.isFinite(sentAmount) && sentAmount > 0 ? `, ${sentAmount.toLocaleString("en-US", { maximumFractionDigits: 8, useGrouping: true })} ${sentToken}` : "";
      const rawSentence = `The funds are swapped from ${sentToken} to ${receivedToken} using ${cleanAttribution}${sentClause}${contractClause}.`;
      row[COL.SYSTEM_NOTES] = this.appendSentenceNote(row[COL.SYSTEM_NOTES], rawSentence);
      return row;
    },

    normalizeTraceThroughNoteText(value = "") {
      const text = this.toText(value);
      if (!text.trim()) return "";
      return text
        .split(/\r?\n/)
        .map((line) => this.toText(line)
          .replace(/\s+([,.;:!?])/g, "$1")
          .replace(/([,;:])\s*\./g, ".")
          .replace(/\.\s*\./g, ".")
          .replace(/\s{2,}/g, " ")
          .replace(/^[\s,;:]+|[\s,;:]+$/g, "")
          .trim())
        .filter(Boolean)
        .join("\n")
        .trim();
    },

    removeRawPathEndingPhraseForTraceThrough(value = "") {
      const text = this.toText(value);
      if (!text.trim()) return "";

      const pathEndsPattern = /Path\s+ends\.?(?!\s+at\b)(?=\s|$|[.;,])/gi;
      const cleaned = text
        .split(/\r?\n/)
        .map((line) => {
          let cleanedLine = this.toText(line)
            .replace(new RegExp(`\\s*,\\s*${pathEndsPattern.source}`, "gi"), ". ")
            .replace(new RegExp(`\\s*;\\s*${pathEndsPattern.source}`, "gi"), ". ")
            .replace(new RegExp(`^\\s*${pathEndsPattern.source}`, "gi"), "")
            .replace(new RegExp(`\\s+${pathEndsPattern.source}`, "gi"), " ")
            .replace(/\s+([,.;:!?])/g, "$1")
            .replace(/([,;:])\s*$/g, ".")
            .replace(/\.\s*\./g, ".")
            .replace(/\s{2,}/g, " ")
            .trim();
          if (cleanedLine === ".") cleanedLine = "";
          return cleanedLine;
        })
        .filter(Boolean)
        .join("\n");

      return this.normalizeTraceThroughNoteText(cleaned);
    },

    removeTool88PathEndingNotesForTraceThrough(value = "") {
      const text = this.toText(value);
      if (!text.trim()) return "";

      const dropPatterns = [
        /tool\s*88:\s*path\s+ends\s+(?:at|by)\b/i,
        /tool\s*88\s+flow:\s*assessed;\s*action\s*=\s*(?:manual-)?path-ending\b/i,
        /\baction\s*=\s*(?:manual-)?path-ending\b/i,
        /\baction\s*=\s*low-value-ended\b/i,
        /tool\s*88\s+automatic\s+decision:.*\b(?:path\s+ending|path\s+ends|not\s+ordinary\s+trace-through|should\s+not\s+ordinary\s+trace-through)\b/i,
        /tool\s*88\s+automatic\s+decision:.*meaningful\s+service\/entity\s+attribution/i,
        /\bdormant\/(?:left-bound|funds-followed)\s+amount\b/i,
        /\bfollowed\s+btc\s+amount\s+at\s+path\s+ending\b/i,
      ];

      const cleaned = text
        .split(/\r?\n/)
        .map((line) => this.toText(line).trim())
        .filter((line) => line && !dropPatterns.some((pattern) => pattern.test(line)))
        .map((line) => this.removeRawPathEndingPhraseForTraceThrough(line))
        .filter(Boolean)
        .join("\n");

      return this.normalizeTraceThroughNoteText(cleaned);
    },

    removeReverseDormantNotesForTraceThrough(row) {
      if (!row || this.traceDirection !== 'reverse') return row;
      const clean = (value = '') => this.toText(value)
        .split(/\r?\n/u)
        .map(line => this.toText(line).trim())
        .filter(Boolean)
        .filter(line => !/A portion of the funds remains at the receiving address\s*\([^)]*\)\.?/iu.test(line))
        .filter(line => !/Tool\s*88\s+dormant\s+flow:\s*assessed/iu.test(line))
        .join('\n');
      row[COL.SYSTEM_NOTES] = clean(row[COL.SYSTEM_NOTES]);
      row[COL.TOOL88_NOTES] = clean(row[COL.TOOL88_NOTES]);
      return row;
    },

    removeRawDataPathEndsSentence(row) {
      if (!row) return row;
      row[COL.SYSTEM_NOTES] = this.removeRawPathEndingPhraseForTraceThrough(row[COL.SYSTEM_NOTES]);
      row[COL.TOOL88_NOTES] = this.removeTool88PathEndingNotesForTraceThrough(row[COL.TOOL88_NOTES]);
      return row;
    },

    removeTool88ManualPathEndingNotes(row) {
      if (!row) return row;
      row[COL.TOOL88_NOTES] = this.removeTool88PathEndingNotesForTraceThrough(row[COL.TOOL88_NOTES]);
      return row;
    },

    isBridgeServiceAttributionLabel(label = "") {
      const clean = this.cleanAttributionLabel(label);
      if (!clean) return false;
      const matchedService = this.getMatchedBridgeServiceName(clean);
      return Boolean(matchedService || this.hasBridgeSignal(clean) || /bridge|bridgers|bkbridge|bitget\s*bridge|cross[-\s]?chain|wormhole|swft|transit|allbridge|rango|thorchain/i.test(clean));
    },

    isSelectedEntryBridgeReceiveContext(selectedAddress = this.selectedTargetAddress, entry = this.selectedEntry) {
      if (!entry) return false;
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      let selectedIsToAddress = false;
      let bridgeHopMarker = false;
      let bridgeSolvedMarker = false;
      let destinationNetworkMarker = false;

      (entry.rows || []).forEach(({ row }) => {
        if (!row) return;
        if (selectedKey && this.normalizeAddressKey(row[COL.TO_ADDRESS]) === selectedKey) selectedIsToAddress = true;
        const hop = this.toText(row[COL.HOP_LOGIC]).toLowerCase();
        if (hop.includes("bridge")) bridgeHopMarker = true;
        const chainText = `${this.toText(row[COL.CHAIN_ID])} ${this.toText(row[COL.CHAIN_NAME])}`.toLowerCase();
        if (/tron|bridge\s+destination/.test(chainText)) destinationNetworkMarker = true;
        const noteText = [row[COL.SYSTEM_NOTES], row[COL.TOOL88_NOTES], row[COL.CONTINUES_AS]]
          .map((value) => this.toText(value))
          .join("\n")
          .toLowerCase();
        if (/tool\s*88:\s*bridge\s+solved|bridge\s+solved|post[-\s]?bridge|received\s+on\s+the\s+.*network\s+via|bridge\s+result/.test(noteText)) {
          bridgeSolvedMarker = true;
        }
      });

      return Boolean(selectedIsToAddress && (bridgeHopMarker || bridgeSolvedMarker) && (destinationNetworkMarker || bridgeSolvedMarker));
    },

    isSelectedEntryPostBridgeContinuationContext(selectedAddress = this.selectedTargetAddress, entry = this.selectedEntry) {
      if (!entry || !selectedAddress) return false;
      if (this.isSelectedEntryBridgeReceiveContext(selectedAddress, entry)) return true;
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      let selectedIsToAddress = false;
      let rowBridgeMarker = false;
      let destinationChainMarker = false;
      let parentHash = "";
      (entry.rows || []).forEach(({ row }) => {
        if (!row) return;
        if (selectedKey && this.normalizeAddressKey(row[COL.TO_ADDRESS]) === selectedKey) selectedIsToAddress = true;
        if (!parentHash) parentHash = this.normalizeHashKey(row[COL.FROM_HASH]);
        const hop = this.toText(row[COL.HOP_LOGIC]).toLowerCase();
        const chainText = `${this.toText(row[COL.CHAIN_ID])} ${this.toText(row[COL.CHAIN_NAME])}`.toLowerCase();
        const noteText = [row[COL.SYSTEM_NOTES], row[COL.TOOL88_NOTES], row[COL.CONTINUES_AS]].map((value) => this.toText(value)).join("\n").toLowerCase();
        if (/\bbridge\b|post[-\s]?bridge|destination\s+chain|received\s+on\s+the\s+.*network\s+via|bridge\s+result|swft|transit/.test(`${hop}\n${noteText}`)) rowBridgeMarker = true;
        if (/tron|bitcoin|btc|ethereum|bsc|polygon|arbitrum|optimism|base|avalanche|bridge\s+destination/.test(chainText)) destinationChainMarker = true;
      });
      if (!selectedIsToAddress) return false;
      if (rowBridgeMarker && destinationChainMarker) return true;
      const entries = Array.isArray(this.parsedEntries) ? this.parsedEntries : [];
      const byHash = new Map();
      entries.forEach((candidate) => {
        const key = this.normalizeHashKey(candidate?.txHash || candidate?.hash || "");
        if (key && !byHash.has(key)) byHash.set(key, candidate);
      });
      let guard = 0;
      let cursor = parentHash;
      while (cursor && guard < 8) {
        const parent = byHash.get(cursor);
        if (!parent) break;
        const parentTo = parent?.toAddress || parent?.rows?.[0]?.row?.[COL.TO_ADDRESS] || "";
        if (this.isSelectedEntryBridgeReceiveContext(parentTo, parent)) return true;
        const parentText = this.collectEntrySearchText(parent);
        if (/tool\s*88:\s*bridge\s+solved|bridge\s+solved|post[-\s]?bridge|destination:\s|received\s+on\s+the\s+.*network\s+via|swft|transit|\bbridge\b/i.test(parentText)) return true;
        cursor = "";
        (parent.rows || []).some(({ row }) => {
          const next = this.normalizeHashKey(row?.[COL.FROM_HASH]);
          if (next && next !== this.normalizeHashKey(parent?.txHash || parent?.hash || "")) {
            cursor = next;
            return true;
          }
          return false;
        });
        guard += 1;
      }
      return false;
    },

    formatTokenChainLabel(chainKey) {
      const normalized = normalizeTokenChainKey(chainKey);
      const found = TOKEN_CHAIN_OPTIONS.find((option) => option.value === normalized);
      if (found) return found.label;
      if (normalized.startsWith("evm:")) return `EVM chain ${normalized.slice(4)}`;
      return normalized || "Unknown";
    },

    normalizeTokenChainForContext(chainId, chainName, networkFamily = "") {
      const family = this.toText(networkFamily).toLowerCase();
      const id = this.normalizeChainIdForCount(chainId);
      const name = this.toText(chainName).toLowerCase();

      if (family === "tron" || id === "tron" || /tron|trc/.test(name)) return "tron";
      if (family === "sol" || id === "sol" || /solana/.test(name)) return "solana";
      if (family === "ton" || id === "ton" || /\bton\b/.test(name)) return "ton";
      if (/pulse/.test(name) || id === "pulsechain") return "pulsechain";
      if (id) return normalizeTokenChainKey(id);
      const evmMeta = this.getEvmCountChainMeta(chainId, chainName);
      if (evmMeta?.chainId) return `evm:${evmMeta.chainId}`;
      return "";
    },

    lookupTokenBySymbolForChain(symbol, chainKey) {
      const normalizedSymbol = normalizeTokenSymbolText(symbol);
      const normalizedChain = normalizeTokenChainKey(chainKey);
      if (!normalizedSymbol || !normalizedChain) {
        return null;
      }
      return this.effectiveTokenRegistry.find((token) =>
        token.symbol === normalizedSymbol && normalizeTokenChainKey(token.chainKey) === normalizedChain
      ) || null;
    },

    lookupTokenByContract(contractAddress, chainKey = "") {
      const contract = normalizeTokenContractText(contractAddress);
      const chain = normalizeTokenChainKey(chainKey);
      if (!contract) {
        return null;
      }
      return this.effectiveTokenRegistry.find((token) => {
        if (normalizeTokenContractText(token.contractAddress) !== contract) return false;
        return !chain || normalizeTokenChainKey(token.chainKey) === chain;
      }) || null;
    },

    resolveTokenForContext(tokenSymbol, chainId, chainName, networkFamily = "", explicitContract = "") {
      const chainKey = this.normalizeTokenChainForContext(chainId, chainName, networkFamily);
      const normalizedContract = normalizeTokenContractText(explicitContract);
      const byContract = normalizedContract ? this.lookupTokenByContract(normalizedContract, chainKey) : null;
      const bySymbol = this.lookupTokenBySymbolForChain(tokenSymbol, chainKey);
      const normalizedSymbol = normalizeTokenSymbolText(tokenSymbol);

      if (byContract) {
        return {
          status: normalizedSymbol && byContract.symbol !== normalizedSymbol ? "warn" : "ok",
          token: byContract,
          chainKey,
          contractAddress: byContract.contractAddress,
          decimals: byContract.decimals,
          source: byContract.source,
          title: normalizedSymbol && byContract.symbol !== normalizedSymbol ? "Contract found, symbol mismatch" : "Trusted token contract found",
          summary: normalizedSymbol && byContract.symbol !== normalizedSymbol
            ? `The supplied contract is known as ${byContract.symbol}, but the row is following ${normalizedSymbol}. Review for fake-token risk.`
            : `${byContract.symbol} is trusted on ${this.formatTokenChainLabel(byContract.chainKey)}.`,
        };
      }

      if (bySymbol) {
        return {
          status: "ok",
          token: bySymbol,
          chainKey,
          contractAddress: bySymbol.contractAddress,
          decimals: bySymbol.decimals,
          source: bySymbol.source,
          title: "Contract resolved from token manager",
          summary: `${bySymbol.symbol} resolves to ${this.shortenMiddle(bySymbol.contractAddress, 12, 10)} on ${this.formatTokenChainLabel(bySymbol.chainKey)}.`,
        };
      }

      if (normalizedSymbol && chainKey && normalizedContract) {
        return {
          status: "warn",
          token: null,
          chainKey,
          contractAddress: normalizedContract,
          decimals: null,
          source: "unknown",
          title: "Untrusted token contract",
          summary: `${normalizedSymbol} has a contract value, but it is not in the effective token manager list for ${this.formatTokenChainLabel(chainKey)}.`,
        };
      }

      if (normalizedSymbol && chainKey) {
        return {
          status: "neutral",
          token: null,
          chainKey,
          contractAddress: "",
          decimals: null,
          source: "missing",
          title: "No token-manager match",
          summary: `${normalizedSymbol} is not in the effective token manager list for ${this.formatTokenChainLabel(chainKey)} yet.`,
        };
      }

      return null;
    },

    extractTokenContractFromProviderTransaction(tx = {}) {
      const tokenInfo = tx?.tokenInfo || tx?.token_info || {};
      return this.toText(
        tx?.token_contract ||
          tx?.tokenContract ||
          tx?.contractAddress ||
          tx?.contract_address ||
          tx?.contract ||
          tx?.address ||
          tokenInfo?.tokenId ||
          tokenInfo?.address ||
          tokenInfo?.contract_address ||
          ""
      );
    },

    buildTokenAuthenticityResult({ tokenSymbol = "", contractAddress = "", chainId = "", chainName = "", networkFamily = "" } = {}) {
      const contract = normalizeTokenContractText(contractAddress);
      const symbol = normalizeTokenSymbolText(tokenSymbol);
      if (!contract) {
        return {
          status: "not_applicable",
          label: "No contract",
          detail: "Native/BTC rows do not need token-manager validation.",
          hasIssue: false,
        };
      }

      const chainKey = this.normalizeTokenChainForContext(chainId, chainName, networkFamily);
      const matchedByContract = this.lookupTokenByContract(contract, chainKey);
      if (matchedByContract) {
        const symbolMismatch = symbol && matchedByContract.symbol !== symbol;
        return {
          status: symbolMismatch ? "symbol_mismatch" : "trusted",
          label: symbolMismatch ? "Symbol mismatch" : "Trusted",
          detail: symbolMismatch
            ? `Contract is trusted as ${matchedByContract.symbol}, but provider row says ${symbol}.`
            : `${matchedByContract.symbol} matched ${this.formatTokenChainLabel(matchedByContract.chainKey)} token manager.`,
          token: matchedByContract,
          chainKey,
          hasIssue: symbolMismatch,
        };
      }

      const sameSymbol = symbol ? this.lookupTokenBySymbolForChain(symbol, chainKey) : null;
      return {
        status: sameSymbol ? "unrecognized_contract" : "unknown_contract",
        label: sameSymbol ? "Unrecognized contract" : "Unknown token",
        detail: sameSymbol
          ? `${symbol} is known on ${this.formatTokenChainLabel(chainKey)}, but this contract is not in the active token manager list.`
          : `Contract is not in the active token manager list${chainKey ? ` for ${this.formatTokenChainLabel(chainKey)}` : ""}.`,
        chainKey,
        expectedToken: sameSymbol,
        hasIssue: true,
      };
    },

    formatTokenCheckStatusClass(result = {}) {
      const status = this.toText(result.status);
      if (status === "ok" || status === "trusted") return "token-check-ok";
      if (status === "warn" || status === "symbol_mismatch" || status === "unrecognized_contract" || status === "unknown_contract") return "token-check-warn";
      return "token-check-neutral";
    },

    getSelectedTokenResolution() {
      if (!this.selectedEntry || !this.selectedTargetAddress) {
        return null;
      }
      const countContext = this.inferCountRequestContext(this.selectedEntry, this.selectedTargetAddress);
      if (this.toText(countContext.count_kind).toLowerCase() !== "token_transfer") {
        return null;
      }
      const tokenSymbol = countContext.token_symbol || this.getSelectedTokenSymbol(this.selectedEntry, this.selectedTargetAddress);
      const contract = countContext.token_contract || countContext.contract_address || "";
      return this.resolveTokenForContext(
        tokenSymbol,
        countContext.chain_id,
        countContext.chain_name,
        countContext.network_family,
        contract
      );
    },

    buildTokenRegistryCheck() {
      const resolution = this.selectedTokenResolution;
      if (!resolution) {
        return null;
      }
      const statusMap = { ok: "ok", warn: "warn", neutral: "neutral" };
      return {
        key: "token-manager",
        title: "Token manager",
        status: statusMap[resolution.status] || "neutral",
        statusLabel: resolution.status === "ok" ? "Ready" : resolution.status === "warn" ? "Review" : "Missing",
        summary: resolution.title,
        detail: resolution.summary,
      };
    },

    buildTokenRegistryInspectorNote(row = {}) {
      const contract = row.contractAddress || row.raw?.contractAddress || row.raw?.contract_address || row.raw?.token_contract || row.raw?.tokenInfo?.tokenId || row.raw?.token_info?.tokenId || "";
      const tokenSymbol = row.token || row.raw?.tokenSymbol || row.raw?.token_symbol || row.raw?.tokenInfo?.tokenAbbr || row.raw?.token_info?.symbol || "";
      if (!contract && !tokenSymbol) {
        return "";
      }

      const chainId = row.chainId || row.raw?.chain_id || row.raw?.chainId || "";
      const chainName = row.chainName || row.raw?.chain_name || row.raw?.chainName || row.raw?.network || "";
      const networkFamily = row.raw?.network_family || row.raw?.provider_hint || row.transactionKind || "";
      const resolution = this.resolveTokenForContext(tokenSymbol, chainId, chainName, networkFamily, contract);
      if (!resolution) {
        return "";
      }

      if (resolution.status === "ok") {
        return `Token manager: trusted ${resolution.token?.symbol || tokenSymbol}`;
      }
      if (resolution.status === "warn") {
        return `Token manager warning: ${resolution.title}`;
      }
      if (resolution.status === "neutral" && tokenSymbol) {
        return `Token manager: no trusted ${normalizeTokenSymbolText(tokenSymbol)} contract yet`;
      }
      return "";
    },

    toText(value) {
      if (value === null || value === undefined) {
        return "";
      }
      return String(value).trim();
    },

    makeBlankRow(columnCount = COLUMN_COUNT) {
      return Array(columnCount).fill("");
    },

    makeBlankGrid(rowCount = DEFAULT_ROW_COUNT, columnCount = COLUMN_COUNT) {
      return Array(rowCount)
        .fill(null)
        .map(() => this.makeBlankRow(columnCount));
    },

    cloneRowPreservingValues(row) {
      const source = Array.isArray(row) ? row : [];
      const outputLength = Math.max(COLUMN_COUNT, source.length);
      const cloned = Array(outputLength).fill("");

      for (let index = 0; index < outputLength; index += 1) {
        const value = source[index];
        cloned[index] = value === null || value === undefined ? "" : value;
      }

      return cloned;
    },

    rowHasAnyData(row) {
      return (row || []).some((cell) => this.toText(cell) !== "");
    },

    trimTrailingBlankRows(rows) {
      const cloned = (Array.isArray(rows) ? rows : []).map((row) =>
        this.migrateLegacyColumnPTool88Note(this.cloneRowPreservingValues(row))
      );

      while (cloned.length && !this.rowHasAnyData(cloned[cloned.length - 1])) {
        cloned.pop();
      }

      return cloned;
    },

    ensureMinimumRows(rows) {
      const output = Array.isArray(rows)
        ? rows.map((row) => this.migrateLegacyColumnPTool88Note(this.cloneRowPreservingValues(row)))
        : [];

      while (output.length < DEFAULT_ROW_COUNT) {
        output.push(this.makeBlankRow());
      }

      return output;
    },

    normalizeAddressKey(address) {
      const text = this.toText(address);
      if (!text) {
        return "";
      }
      if (/^0x/i.test(text)) {
        return text.toLowerCase();
      }
      return text;
    },

    isLikelyHeaderRow(row) {
      const txHeader = this.toText(row?.[COL.TX_HASH]).toLowerCase();
      const toHeader = this.toText(row?.[COL.TO_ADDRESS]).toLowerCase();
      const fromHeader = this.toText(row?.[COL.FROM_ADDRESS]).toLowerCase();
      const fundsHeader = this.toText(row?.[COL.FUNDS_FOLLOWED]).toLowerCase();

      return (
        txHeader.includes("transaction") ||
        txHeader.includes("hash") ||
        toHeader.includes("to address") ||
        fromHeader.includes("from address") ||
        fundsHeader.includes("funds followed")
      );
    },

    parseNumberLike(value) {
      const text = this.toText(value).replace(/,/g, "");
      if (!text) {
        return null;
      }

      const match = text.match(/-?\d+(?:\.\d+)?/);
      if (!match) {
        return null;
      }

      const parsed = Number(match[0]);
      return Number.isFinite(parsed) ? parsed : null;
    },

    parseTokenLike(value) {
      const text = this.toText(value);
      if (!text) {
        return "";
      }

      const withoutNumber = text
        .replace(/[-+]?\d[\d,]*(?:\.\d+)?/g, "")
        .trim();

      return withoutNumber || "";
    },

    parseUtcDate(value) {
      const text = this.toText(value).trim();
      if (!text) {
        return null;
      }

      const buildUtcDate = (year, month, day, rawHour = 0, minute = 0, second = 0, meridiem = "") => {
        let hour = Number(rawHour || 0);
        const marker = this.toText(meridiem).toLowerCase().replace(/\./g, "");
        if (marker === "pm" && hour < 12) {
          hour += 12;
        } else if (marker === "am" && hour === 12) {
          hour = 0;
        }
        const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), hour, Number(minute || 0), Number(second || 0)));
        return Number.isNaN(date.getTime()) ? null : date;
      };

      const isoLikeMatch = text.match(
        /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM|A\.M\.|P\.M\.)?)?/i
      );

      if (isoLikeMatch) {
        return buildUtcDate(
          isoLikeMatch[1],
          isoLikeMatch[2],
          isoLikeMatch[3],
          isoLikeMatch[4] || 0,
          isoLikeMatch[5] || 0,
          isoLikeMatch[6] || 0,
          isoLikeMatch[7] || ""
        );
      }

      const usLikeMatch = text.match(
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM|A\.M\.|P\.M\.)?)?/i
      );

      if (usLikeMatch) {
        return buildUtcDate(
          usLikeMatch[3],
          usLikeMatch[1],
          usLikeMatch[2],
          usLikeMatch[4] || 0,
          usLikeMatch[5] || 0,
          usLikeMatch[6] || 0,
          usLikeMatch[7] || ""
        );
      }

      const timestamp = Date.parse(text);
      if (Number.isNaN(timestamp)) {
        return null;
      }
      return new Date(timestamp);
    },

    formatUtcDate(date) {
      const safeDate = date instanceof Date && !Number.isNaN(date.getTime())
        ? date
        : new Date();
      // Case Buddy rows are canonical UTC rows. Keep the timezone marker in the
      // cell so later browser parsing, exports, and manual-review comparisons do
      // not silently reinterpret Tool 88 timestamps as local time.
      return `${safeDate.toISOString().replace("T", " ").slice(0, 19)} UTC`;
    },

    addHours(date, hours) {
      const safeDate = date instanceof Date && !Number.isNaN(date.getTime())
        ? date
        : new Date();
      return new Date(safeDate.getTime() + Number(hours || 0) * 60 * 60 * 1000);
    },

    formatAmount(value) {
      if (!Number.isFinite(Number(value))) {
        return "";
      }

      const number = Number(value);
      return number.toLocaleString("en-US", {
        maximumFractionDigits: 12,
        useGrouping: false,
      });
    },

    isStableCaseBuddyToken(token = "") {
      const symbol = this.toText(token).trim().toUpperCase();
      return ["USDT", "USDC", "DAI", "BUSD", "TUSD", "FDUSD", "PYUSD", "USDD", "USD"].includes(symbol);
    },

    formatCaseBuddyLeftBoundsAmount(value, token = "") {
      const number = Number(value);
      if (!Number.isFinite(number)) return "";
      const symbol = this.toText(token).trim().toUpperCase();
      if (symbol === "BTC") {
        return number
          .toFixed(8)
          .replace(/(\.\d*?[1-9])0+$/u, "$1")
          .replace(/\.0+$/u, ".00");
      }
      if (this.isStableCaseBuddyToken(symbol) || Math.abs(number) >= 1 || Math.abs(number) <= 1e-12) {
        return number.toFixed(2);
      }
      return this.formatAmount(number);
    },

    formatInteger(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return "0";
      }
      return Math.round(number).toLocaleString("en-US");
    },

    normalizeTransactionCount(lookup) {
      const candidates = [
        lookup?.transaction_count,
        lookup?.transactionCount,
        lookup?.count,
      ];

      for (const candidate of candidates) {
        const number = Number(candidate);
        if (Number.isFinite(number)) {
          return Math.trunc(number);
        }
      }

      return UNKNOWN_TRANSACTION_COUNT;
    },

    formatCountValue(valueOrResult) {
      const result = valueOrResult && typeof valueOrResult === "object"
        ? valueOrResult
        : { transactionCount: valueOrResult, countAvailable: true };
      const number = Number(result.transactionCount);
      if (result.countAvailable === false || !Number.isFinite(number) || number < 0) {
        return "Unavailable";
      }
      return this.formatInteger(number);
    },

    formatCountKind(kind) {
      const normalized = this.toText(kind).toLowerCase();
      const labels = {
        native_transaction: "Native transactions",
        token_transfer: "Token transfers",
        unsupported_count: "Unavailable",
        native_unavailable: "Native count unavailable",
        sample_placeholder: "Sample placeholder",
        mock: "Mock placeholder",
      };
      return labels[normalized] || this.toText(kind) || "Auto";
    },

    formatTransactionCount(value, countAvailable = true) {
      const number = Number(value);
      if (countAvailable === false || !Number.isFinite(number) || number < 0) {
        return "Unavailable";
      }
      return this.formatInteger(number);
    },

    formatCountWorkflowText(countResult) {
      if (!countResult || countResult.countAvailable === false || Number(countResult.transactionCount) < 0) {
        return "Count unavailable";
      }
      return `${this.formatInteger(countResult.transactionCount)} transactions`;
    },


    formatFunds(value, token) {
      const amountText = this.formatAmount(value);
      const rawTokenText = this.toText(token);
      const tokenText = /^(?:null|undefined|nan)$/i.test(rawTokenText) ? "" : rawTokenText;
      if (!amountText && !tokenText) {
        return "";
      }
      return `${amountText}${tokenText ? ` ${tokenText}` : ""}`.trim();
    },

    isCaseBuddyStablecoinToken(token = '') {
      return new Set(['USDT', 'USDC', 'USDD', 'DAI', 'BUSD', 'TUSD', 'USDP', 'GUSD', 'PYUSD', 'FDUSD']).has(this.toText(token).toUpperCase());
    },

    extractCaseBuddyNumericParts(value = '') {
      const match = this.toText(value).match(/[-+]?\d[\d,]*(?:\.\d+)?/u);
      if (!match) return null;
      const numericText = match[0].replace(/,/gu, '');
      const sign = numericText.startsWith('-') ? '-' : '';
      const unsigned = numericText.replace(/^[-+]/u, '');
      const [wholeRaw = '0', fractionRaw = ''] = unsigned.split('.');
      const whole = wholeRaw.replace(/^0+(?=\d)/u, '') || '0';
      const fraction = fractionRaw.replace(/[^0-9]/gu, '');
      const nonZero = /[1-9]/u.test(whole) || /[1-9]/u.test(fraction);
      return { sign, whole, fraction, nonZero };
    },

    formatCaseBuddyTransactionAmountNoRound(value = '', token = '') {
      const parts = this.extractCaseBuddyNumericParts(value);
      if (!parts) return this.toText(value);
      const tokenText = this.toText(token).toUpperCase();
      const baseDecimals = this.isCaseBuddyStablecoinToken(tokenText) ? 2 : 4;
      const addCommas = (whole) => (String(whole || '0').replace(/^0+(?=\d)/u, '') || '0').replace(/\B(?=(\d{3})+(?!\d))/gu, ',');
      const makeNumberText = (fractionDigits) => {
        const padded = `${parts.fraction}${'0'.repeat(fractionDigits)}`.slice(0, fractionDigits);
        return `${parts.sign}${addCommas(parts.whole)}${fractionDigits ? `.${padded}` : ''}`;
      };
      let output = makeNumberText(baseDecimals);
      if (parts.nonZero && /^-?0(?:\.0+)?$/u.test(output.replace(/,/gu, ''))) {
        const maxDecimals = Math.max(8, Math.min(24, parts.fraction.length || 8));
        for (let decimals = Math.max(baseDecimals + 1, 8); decimals <= maxDecimals; decimals += 1) {
          output = makeNumberText(decimals);
          if (!/^-?0(?:\.0+)?$/u.test(output.replace(/,/gu, ''))) break;
        }
      }
      return output;
    },

    cleanCaseBuddyAttributionColumnK(value = '') {
      const raw = this.toText(value);
      if (!raw) return '';
      const parts = raw.split(/[;|\n]+/u).map((part) => part.trim()).filter(Boolean);
      const toPart = parts.find((part) => /^(to|receiver|recipient|destination)\s*:/iu.test(part));
      if (toPart) return this.cleanAttributionLabel(toPart.replace(/^(to|receiver|recipient|destination)\s*:/iu, ''));
      if (parts.some((part) => /^(from|sender|source)\s*:/iu.test(part)) && !toPart) return '';
      return this.cleanAttributionLabel(raw);
    },

    appendUniqueNote(existingValue, note) {
      const existing = this.toText(existingValue);
      const cleanNote = this.toText(note);
      const lines = [];
      const pushLines = (value) => {
        this.toText(value)
          .split(/\r?\n/u)
          .flatMap((line) => String(line || '').split(/\s{2,}(?=(?:Tool\s*88|Case\s+Buddy|Funds\s+move|Branches\s+as)\b)/iu))
          .map((line) => line.replace(/\s+/gu, ' ').trim())
          .filter(Boolean)
          .forEach((line) => {
            const key = line.toLowerCase();
            if (!lines.some((existingLine) => existingLine.toLowerCase() === key)) lines.push(line);
          });
      };
      pushLines(existing);
      pushLines(cleanNote);
      return lines.join('\n');
    },

    lineLooksLikeTool88Diagnostic(line = '') {
      const text = this.toText(line);
      if (!text) return false;
      return /\bTool\s*88\b|Case\s+Buddy\s+manual\s+start|Case\s+Buddy\s*:\s*Investigator|Case\s+Buddy\s+inferred|action\s*=\s*(?:traced-through|manual-path-ending|low-value-ended)|trace-through\s+preview|hot[-\s]?wallet\s+assessor|address\s+has\s+features\s+of\s+an\s+unattributed\s+exchange\s+hot\s+wallet|service[-\s]?pattern\s+score|high\s+stablecoin\s+flow|high\s+erc20\s+transfer\s+count/iu.test(text);
    },

    splitCaseBuddySystemAndToolNotes(systemNotes = '', toolNotes = '') {
      const systemLines = this.appendUniqueNote('', systemNotes).split(/\r?\n/u).map((line) => line.trim()).filter(Boolean);
      const toolLines = this.appendUniqueNote('', toolNotes).split(/\r?\n/u).map((line) => line.trim()).filter(Boolean);
      const cleanSystemLines = [];
      const cleanToolLines = [];
      const pushUnique = (target, line) => {
        const clean = this.toText(line).replace(/\s+/g, ' ').trim();
        const key = clean.toLowerCase();
        if (!key || target.some((existing) => existing.toLowerCase() === key)) return;
        target.push(clean);
      };
      systemLines.forEach((line) => {
        if (this.lineLooksLikeTool88Diagnostic(line)) pushUnique(cleanToolLines, line);
        else pushUnique(cleanSystemLines, line);
      });
      toolLines.forEach((line) => pushUnique(cleanToolLines, line));
      return { systemNotes: cleanSystemLines.join('\n'), toolNotes: cleanToolLines.join('\n') };
    },

    sanitizeCaseBuddyContinuesAsCellForExport(value = '') {
      const text = this.toText(value).trim();
      if (!text) return '';
      if (/^path\s*ends\.?$/iu.test(text)) return 'Path ends.';
      const match = text.match(/continues\s+as\s+(.+)$/iu);
      if (!match) return text;
      const hashes = match[1]
        .split(',')
        .map((hash) => this.toText(hash).trim())
        .filter((hash) => this.isTool88LikelyTransactionHash(hash));
      return hashes.length ? `Continues as ${hashes.join(', ')}` : 'Path ends.';
    },

    sanitizeCaseBuddyOutputRowForExport(row = [], options = {}) {
      const cleanRow = this.migrateLegacyColumnPTool88Note(this.cloneRowPreservingValues(row));
      while (cleanRow.length < COLUMN_COUNT) cleanRow.push('');
      const token = this.toText(cleanRow[COL.TOKEN]);
      if (this.toText(cleanRow[COL.AMOUNT])) {
        cleanRow[COL.AMOUNT] = this.formatCaseBuddyTransactionAmountNoRound(cleanRow[COL.AMOUNT], token);
      }
      cleanRow[COL.ATTRIBUTION] = this.cleanCaseBuddyAttributionColumnK(cleanRow[COL.ATTRIBUTION]);
      cleanRow[COL.FROM_ATTRIBUTION] = this.cleanAttributionLabel(cleanRow[COL.FROM_ATTRIBUTION]);
      const notes = this.splitCaseBuddySystemAndToolNotes(cleanRow[COL.SYSTEM_NOTES], cleanRow[COL.TOOL88_NOTES]);
      cleanRow[COL.SYSTEM_NOTES] = this.stripCaseBuddyTransferNarrativeFromDescendantNotes(notes.systemNotes, cleanRow, options);
      cleanRow[COL.TOOL88_NOTES] = notes.toolNotes;
      cleanRow[COL.CONTINUES_AS] = this.sanitizeCaseBuddyContinuesAsCellForExport(cleanRow[COL.CONTINUES_AS]);
      return cleanRow;
    },

    isCaseBuddySnapshotZeroPaddingRow(row = []) {
      const cells = Array.isArray(row)
        ? row.map((cell) => this.toText(cell).trim()).filter(Boolean)
        : [];
      if (!cells.length) return true;
      return cells.every((cell) => /^0(?:\.0+)?$/u.test(cell));
    },


    isCaseBuddySnapshotFakeStatusRow(row = []) {
      if (!Array.isArray(row)) return false;
      const cells = row.map((cell) => this.toText(cell).trim()).filter(Boolean);
      if (!cells.length) return false;
      const txHash = this.toText(row?.[COL.TX_HASH]).trim();
      if (txHash) return !this.isTool88LikelyTransactionHash(txHash);
      const joined = cells.join(' ').replace(/\s+/gu, ' ').trim();
      const hasSolvingOrHop = /\b(?:solving|hop)\b/iu.test(joined);
      const hasScaffoldNumber = cells.some((cell) => /^(?:88|0(?:\.0+)?|14000(?:\.0+)?)$/u.test(cell));
      if (/\b88\s+(?:solving|hop)\b/iu.test(joined)) return true;
      if (hasSolvingOrHop && hasScaffoldNumber) return true;
      if (/\bfifo\b/iu.test(joined) && /\b(?:ethereum|tron|bitcoin)\b/iu.test(joined) && hasSolvingOrHop) return true;
      return false;
    },

    isCaseBuddySnapshotHeaderRow(row = []) {
      const cells = Array.isArray(row) ? row : [];
      const joined = cells.slice(0, 8).map((cell) => this.toText(cell).toLowerCase()).join('|');
      return joined.includes('transaction hash') || joined.includes('path label|transaction');
    },

    isCaseBuddySnapshotOutputRowUsable(row = []) {
      if (!Array.isArray(row)) return false;
      if (this.isCaseBuddySnapshotHeaderRow(row)) return true;
      if (this.isCaseBuddySnapshotZeroPaddingRow(row)) return false;
      if (this.isCaseBuddySnapshotFakeStatusRow(row)) return false;
      return this.rowLooksLikeCaseBuddyEntryData(row);
    },

    sanitizeCaseBuddySnapshotRows(rows = []) {
      if (!Array.isArray(rows)) return [];
      const sanitized = [];
      let sawHeader = false;
      const sourceRows = this.trimTrailingBlankRows(rows);
      const entries = this.parseCaseBuddyEntriesFromRows(sourceRows);
      sourceRows.forEach((sourceRow, rowIndex) => {
        const row = this.sanitizeCaseBuddyOutputRowForExport(Array.isArray(sourceRow) ? sourceRow : [], { rowIndex, rows: sourceRows, entries });
        if (this.isCaseBuddySnapshotHeaderRow(row)) {
          if (!sawHeader) {
            sanitized.push(row);
            sawHeader = true;
          }
          return;
        }
        if (!this.isCaseBuddySnapshotOutputRowUsable(row)) return;
        sanitized.push(row);
      });
      return sanitized;
    },

    rowLooksLikeCaseBuddyContinuationData(row = []) {
      if (!Array.isArray(row)) return false;
      if (this.isCaseBuddySnapshotZeroPaddingRow(row) || this.isCaseBuddySnapshotFakeStatusRow(row)) return false;
      const chainText = [row[COL.CHAIN_ID], row[COL.CHAIN_NAME], row[COL.TOKEN], row[COL.FUNDS_FOLLOWED]]
        .map((value) => this.toText(value).toLowerCase())
        .filter(Boolean)
        .join(' ');
      const isBtcLike = /\b(bitcoin|btc)\b/u.test(chainText);
      if (!isBtcLike) return false;
      const directCols = [COL.FROM_ADDRESS, COL.TO_ADDRESS, COL.DATE, COL.AMOUNT, COL.TOKEN, COL.FUNDS_FOLLOWED, COL.FROM_HASH];
      if (directCols.some((index) => this.toText(row[index]))) return true;
      const continuesAs = this.toText(row[COL.CONTINUES_AS]);
      if (/^Continues\s+as\b/i.test(continuesAs)) return true;
      const leftDisplay = this.toText(row[COL.LEFT_BOUNDS_DISPLAY]);
      const leftNumeric = this.toText(row[COL.LEFT_BOUNDS_NUMERIC]);
      if ((leftDisplay && !/^0(?:\.0+)?(?:\s+[A-Za-z0-9._-]+)?$/u.test(leftDisplay)) || (leftNumeric && !/^0(?:\.0+)?$/u.test(leftNumeric))) return true;
      return false;
    },

    rowLooksLikeCaseBuddyEntryData(row = []) {
      if (!row || !Array.isArray(row)) return false;
      if (this.isCaseBuddySnapshotZeroPaddingRow(row) || this.isCaseBuddySnapshotFakeStatusRow(row)) return false;
      const txHash = this.toText(row[COL.TX_HASH]).trim();
      if (txHash) return this.isTool88LikelyTransactionHash(txHash);
      return this.rowLooksLikeCaseBuddyContinuationData(row);
    },

    scoreCaseBuddyOutputRowsForExport(rows = []) {
      const cleanRows = this.trimTrailingBlankRows(Array.isArray(rows) ? rows : []);
      const entryRows = cleanRows.filter((row) => this.rowLooksLikeCaseBuddyEntryData(row));
      const hashCount = entryRows.filter((row) => this.isTool88LikelyTransactionHash(this.toText(row?.[COL.TX_HASH]))).length;
      return (hashCount * 1000) + (entryRows.length * 10) + cleanRows.length;
    },

    appendSentenceNote(existingValue, note) {
      const existing = this.toText(existingValue).trim();
      const cleanNote = this.toText(note).replace(/\s+/g, " ").trim();
      if (!cleanNote) {
        return existing;
      }
      if (!existing) {
        return cleanNote;
      }
      if (existing.toLowerCase().includes(cleanNote.toLowerCase())) {
        return existing;
      }
      const separator = /[.!?;:]$/.test(existing) ? " " : ". ";
      return `${existing}${separator}${cleanNote}`;
    },

    isRawDataPathEndingAttributionLabel(label, selectedAddress = "") {
      const clean = this.cleanAttributionLabel(label);
      if (!clean) return false;
      const lower = clean.toLowerCase();
      if (/^(unknown|none|no hit|no visible hit|unattributed|no server\/vendor label found|no server\/vendor label)$/i.test(clean)) {
        return false;
      }
      if (/unknown|contract\s*like|raw\s+address|address\s+has\s+features|no\s+server\/vendor\s+label|no\s+visible\s+hit/i.test(clean)) {
        return false;
      }
      if (selectedAddress && this.isSameAddressLabel(clean, selectedAddress)) {
        return false;
      }
      if (this.isValidAddressLikeLabel(clean)) {
        return false;
      }
      if (this.isTraceThroughFriendlyHandleAttributionLabel(clean)) {
        return false;
      }
      if (this.hasBridgeSignal(lower) || this.hasSwapSignal(lower)) {
        return false;
      }
      if (this.traceDirection === "reverse" && this.isDepositAttributionLabel(clean)) {
        return false;
      }
      return true;
    },

    getRawDataPathEndingAttributionLabel(entry = this.selectedEntry, selectedAddress = this.selectedTargetAddress, entryRow = null) {
      const result = this.attributionLookupResult || {};
      const labels = [
        result.displayLabel,
        result.serverNote,
        result.vendorLabel,
        result.columnKValue,
        entryRow?.[this.getAttributionColumnIndex()],
        ...(entry === this.selectedEntry && selectedAddress === this.selectedTargetAddress && this.getSelectedAttributionValues ? this.getSelectedAttributionValues() : this.getAttributionValuesForEntry(entry, selectedAddress)),
        this.extractFlowMeaningfulIdentity ? this.extractFlowMeaningfulIdentity(entry, selectedAddress) : "",
      ];

      for (const label of labels) {
        const clean = this.cleanAttributionLabel(label);
        if (this.isRawDataPathEndingAttributionLabel(clean, selectedAddress)) {
          return clean;
        }
      }
      return "";
    },

    appendRawDataPathEndingAttributionNote(row, attributionLabel, selectedAddress = this.selectedTargetAddress) {
      if (!row) return row;
      const clean = this.cleanAttributionLabel(attributionLabel).replace(/[.\s]+$/g, "");
      if (!this.isRawDataPathEndingAttributionLabel(clean, selectedAddress)) {
        return row;
      }
      const existing = this.toText(row[COL.SYSTEM_NOTES]).trim();
      const lowerExisting = existing.toLowerCase();
      const lowerLabel = clean.toLowerCase();
      if (lowerExisting.includes(lowerLabel) && /path\s+ends\.?/i.test(existing)) {
        return row;
      }

      const base = existing.replace(/\s*path\s+ends\.\s*$/i, "").trim();
      const sentence = lowerExisting.includes(lowerLabel) ? "Path ends." : `${clean}. Path ends.`;
      row[COL.SYSTEM_NOTES] = this.appendSentenceNote(base, sentence);
      const attributionColumnIndex = this.getAttributionColumnIndex();
      if (!this.toText(row[attributionColumnIndex])) {
        row[attributionColumnIndex] = clean;
      }
      return row;
    },

    isPotentialServicePathEndingReason(reason = "") {
      const text = this.toText(reason).toLowerCase();
      return Boolean(
        /unattributed\s+exchange\s+hot\s+wallet|exchange\s+hot\s+wallet|hot[-\s]?wallet|potential\s+service|service\s+address/.test(text) &&
        /unknown|unattributed|service|hot[-\s]?wallet/.test(text)
      );
    },

    appendRawDataPotentialServicePathEndingNote(row) {
      if (!row) return row;
      const cleanSentence = "Unknown. Address has features of an unattributed exchange hot wallet or service address. Path ends.";
      const existing = this.toText(row[COL.SYSTEM_NOTES]).trim();
      const attributionColumnIndex = this.getAttributionColumnIndex();
      if (/address\s+has\s+features\s+of\s+an\s+unattributed\s+exchange\s+hot\s+wallet\s+or\s+service\s+address/i.test(existing) && /path\s+ends\.?/i.test(existing)) {
        if (!this.toText(row[attributionColumnIndex])) {
          row[attributionColumnIndex] = "Unknown";
        }
        return row;
      }
      row[COL.SYSTEM_NOTES] = this.appendSentenceNote(existing, cleanSentence);
      if (!this.toText(row[attributionColumnIndex])) {
        row[attributionColumnIndex] = "Unknown";
      }
      return row;
    },

    getTool88NoteColumnIndex() {
      // Embedded Manual Solve keeps Tool 88 diagnostics in Column W so Column J stays
      // available for Case Buddy's case narrative/system-note story.
      if (this.caseBuddyEmbedded) return COL.TOOL88_NOTES;
      return this.tool88NoteColumn === "W" ? COL.TOOL88_NOTES : COL.SYSTEM_NOTES;
    },

    looksLikeLegacyColumnPTool88Note(value = "") {
      const text = this.toText(value).trim();
      if (!text) return false;
      if (/^[0-9]+[a-z]*$/iu.test(text)) return false;
      return /tool\s*88|case\s*buddy|path\s*ends|manual[-\s]?path|unknown|service|hot[-\s]?wallet|action\s*=|trace\s+through|resolved ending/iu.test(text)
        || /[.!?]\s*$/u.test(text)
        || text.length > 24;
    },

    migrateLegacyColumnPTool88Note(row = []) {
      if (!Array.isArray(row)) return row;
      const legacy = this.toText(row[COL.FROM_LABEL]).trim();
      if (this.looksLikeLegacyColumnPTool88Note(legacy)) {
        row[COL.TOOL88_NOTES] = this.appendUniqueNote(row[COL.TOOL88_NOTES], legacy);
        row[COL.FROM_LABEL] = "";
      }
      return row;
    },

    appendTool88NoteToRow(row, note) {
      if (!row) return row;
      const columnIndex = this.getTool88NoteColumnIndex();
      row[columnIndex] = this.appendUniqueNote(row[columnIndex], note);
      return row;
    },

    appendFlowCompletionMarkerToRow(row, action, detail = "") {
      if (!row) return row;
      const actionText = this.toText(action) || "assessed";
      const direction = this.traceDirection === "reverse" ? "reverse" : "forward";
      const detailText = this.toText(detail).replace(/\s+/g, " ").trim();
      const marker = `Tool 88 flow: assessed; action=${actionText}; direction=${direction}${detailText ? `; ${detailText}` : ""}`;
      row[COL.TOOL88_NOTES] = this.appendUniqueNote(row[COL.TOOL88_NOTES], marker);
      return row;
    },

    entryHasAddressPoisoningEndingMarker(entry) {
      const rows = Array.isArray(entry?.rows) ? entry.rows : [];
      const text = rows
        .map(({ row }) => [
          row?.[COL.SYSTEM_NOTES],
          row?.[COL.TOOL88_NOTES],
          row?.[COL.ATTRIBUTION],
          row?.[COL.FROM_ATTRIBUTION],
        ].map((value) => this.toText(value)).join("\n"))
        .join("\n")
        .toLowerCase();
      const continuesText = rows
        .map(({ row }) => this.toText(row?.[COL.CONTINUES_AS]))
        .filter(Boolean)
        .join("\n");
      if (!/^path\s+ends\.?$/im.test(continuesText)) return false;
      if (!/(?:likely\s+)?address[-_\s]*poison(?:ing)?|poisoning[-_\s]*dust|action\s*=\s*address-poisoning-ending/i.test(text)) return false;

      const expectedDirection = this.traceDirection === "reverse" ? "reverse" : "forward";
      const actionMarkers = Array.from(text.matchAll(/action\s*=\s*address-poisoning-ending([^\n]*)/gi));
      if (!actionMarkers.length) return expectedDirection === "reverse";
      return actionMarkers.some((match) => {
        const suffix = this.toText(match?.[1]).toLowerCase();
        const directionMatch = suffix.match(/(?:^|;)\s*direction\s*=\s*(forward|reverse)\b/i);
        return !directionMatch || directionMatch[1].toLowerCase() === expectedDirection;
      });
    },

    entryHasFlowCompletionMarker(entry) {
      const text = (entry?.rows || [])
        .map(({ row }) => [row?.[COL.TOOL88_NOTES], row?.[COL.SYSTEM_NOTES]].map((value) => this.toText(value)).join("\n"))
        .join("\n")
        .toLowerCase();
      if (this.entryHasAddressPoisoningEndingMarker(entry)) return true;
      if (!text) return false;

      const currentDirection = this.traceDirection === "reverse" ? "reverse" : "forward";
      const markerMatches = Array.from(text.matchAll(/tool\s*88\s+flow:\s*assessed;\s*action=([a-z0-9-]+)([^\n]*)/gi));
      const compatibleMarkers = markerMatches.filter((match) => {
        const suffix = this.toText(match?.[2]).toLowerCase();
        const directionMatch = suffix.match(/(?:^|;)\s*direction\s*=\s*(forward|reverse)\b/i);
        if (directionMatch) return directionMatch[1].toLowerCase() === currentDirection;

        // Direction was not written into historical markers. They remain safe
        // for forward cases, but Reverse must reassess them because older Case
        // Buddy integrations could mark the receiver/deposit side as terminal.
        return currentDirection === "forward";
      });
      const compatibleText = compatibleMarkers.map((match) => this.toText(match?.[0])).join("\n");

      const looksLikeSolvedSwapRow = /funds\s+are\s+swapped\s+from|tool\s*88\s*:\s*swap\s+solved|\bswap\//i.test(text);
      const staleComplexSignalPathEnding =
        looksLikeSolvedSwapRow &&
        /tool\s*88\s+flow:\s*assessed;\s*action=manual-path-ending/i.test(compatibleText) &&
        /transaction-level signal does not look like a simple transfer/i.test(text);

      if (staleComplexSignalPathEnding && !/no qualifying exit|dormant|hot wallet|service address|low-value|action=traced-through|action=low-value-ended/i.test(compatibleText)) {
        return false;
      }

      const actions = compatibleMarkers
        .map((match) => this.toText(match?.[1]).toLowerCase())
        .filter(Boolean);
      const terminalActions = new Set([
        "complete",
        "done",
        "manual-path-ending",
        "dormant-ending",
        "service-stop",
        "low-value-ended",
        "address-poisoning-ending",
        "swap",
        "bridge",
      ]);
      if (actions.some((action) => terminalActions.has(action))) return true;

      // A traced-through marker describes an attempted action, not a terminal
      // ending. It is current only when the spreadsheet also contains the
      // outgoing continuation that the action was supposed to create.
      if (actions.includes("traced-through")) {
        return this.entryHasExplicitOutgoingContinuation(entry);
      }

      if (currentDirection === "reverse") return false;
      return /tool\s*88\s+flow:\s*(?:complete|done)\b/i.test(text);
    },

    setTool88NoteOnRow(row, note) {
      if (!row) return row;
      const columnIndex = this.getTool88NoteColumnIndex();
      row[columnIndex] = this.appendUniqueNote(row[columnIndex], note);
      return row;
    },


    getTool88NoteTextForRow(row = []) {
      const values = [row?.[COL.SYSTEM_NOTES], row?.[COL.TOOL88_NOTES]];
      return values.map((value) => this.toText(value)).filter(Boolean).join("\n");
    },

    resetDecisionState() {
      this.targetCandidates = [];
      this.selectedTargetAddress = "";
      this.simpleRecommendationApplied = false;
      this.simpleFlowActionReady = false;
      this.countLookupResult = null;
      this.attributionLookupResult = null;
      this.transactionSignalResult = null;
      this.traceResult = null;
      this.dormantResult = null;
      this.traceTransactionPayload = null;
      this.pathEndingResult = null;
      this.simpleFlowActionReady = false;
      this.flowCompletionMessage = "";
      this.backendJobMeta = null;
      this.countJobMeta = null;
      this.attributionJobMeta = null;
      this.signalJobMeta = null;
      this.resetSwapUi();
      this.resetBridgeUi();
      this.resetTraceFetchUi(null);
      this.outputBaseRows = [];
      this.reportText = "";
      this.lastRunMeta = null;
      this.caseBuddySelectedOutputInferenceBlock = null;
    },

    resetSwapUi() {
      this.swapExplorer = null;
      this.swapExplorerActiveTab = "main";
      this.swapSelectedTransferKey = "";
      this.swapApplyResult = null;
    },

    resetBridgeUi() {
      this.bridgeExplorer = null;
      this.bridgeSelectedCandidateKey = "";
      this.bridgeApplyResult = null;
    },

    getStoredAuthToken() {
      try {
        return (
          localStorage.getItem("token") ||
          localStorage.getItem("jwtToken") ||
          localStorage.getItem("jwt_token") ||
          localStorage.getItem("authToken") ||
          localStorage.getItem("auth_token") ||
          localStorage.getItem("access_token") ||
          localStorage.getItem("jwt") ||
          sessionStorage.getItem("token") ||
          sessionStorage.getItem("jwtToken") ||
          sessionStorage.getItem("jwt_token") ||
          sessionStorage.getItem("authToken") ||
          sessionStorage.getItem("auth_token") ||
          sessionStorage.getItem("access_token") ||
          sessionStorage.getItem("jwt") ||
          ""
        );
      } catch (error) {
        console.warn("Could not read auth token from browser storage.", error);
        return "";
      }
    },

    getAuthHeaders() {
      const token = this.getStoredAuthToken();
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      return headers;
    },

    async ensureTool88Auth({ force = false } = {}) {
      const token = this.getStoredAuthToken();
      if (!token) {
        throw new Error("You need to be logged in before using Tool 88 server actions.");
      }

      const now = Date.now();
      if (
        !force &&
        this.authSessionCache &&
        this.authSessionCache.token === token &&
        now - this.authSessionCache.checkedAt < TOOL88_SESSION_CACHE_MS
      ) {
        return this.authSessionCache.session;
      }

      const session = await AuthService.getSession();
      if (!session || !session.isAuthenticated) {
        this.authSessionCache = null;
        throw new Error("Your login session could not be verified. Please log in again before using Tool 88.");
      }

      this.authSessionCache = {
        token,
        checkedAt: now,
        session,
      };

      return session;
    },

    formatEntryOption(entry) {
      if (!entry) {
        return "";
      }

      const pathLabel = this.toText(entry.pathLabel) || "no path label";
      const hash = this.toText(entry.txHash) || "no tx hash";
      return `Row ${entry.startRowIndex + 1}: ${pathLabel} / ${hash}`;
    },

    getTargetColumnIndex() {
      return this.traceDirection === "reverse"
        ? COL.FROM_ADDRESS
        : COL.TO_ADDRESS;
    },

    getAttributionColumnIndex() {
      return this.traceDirection === "reverse"
        ? COL.FROM_ATTRIBUTION
        : COL.ATTRIBUTION;
    },

    getCounterpartyAttributionColumnIndex() {
      return this.traceDirection === "reverse"
        ? COL.ATTRIBUTION
        : COL.FROM_ATTRIBUTION;
    },

    getCounterpartyColumnIndex() {
      return this.traceDirection === "reverse"
        ? COL.TO_ADDRESS
        : COL.FROM_ADDRESS;
    },

    collectEntries(rows, dataStartIndex) {
      const entries = [];
      let currentEntry = null;
      const warnings = [];

      rows.forEach((row, rowIndex) => {
        if (rowIndex < dataStartIndex) {
          return;
        }

        const hasAnyData = this.rowHasAnyData(row);
        const txHash = this.toText(row?.[COL.TX_HASH]);

        if (txHash) {
          currentEntry = {
            key: `entry-${rowIndex}-${txHash}`,
            startRowIndex: rowIndex,
            endRowIndex: rowIndex,
            rows: [{ rowIndex, row: this.cloneRowPreservingValues(row) }],
            txHash,
            pathLabel: this.toText(row?.[COL.PATH_LABEL]),
            dateText: this.toText(row?.[COL.DATE]),
            chainId: this.toText(row?.[COL.CHAIN_ID]),
            chainName: this.toText(row?.[COL.CHAIN_NAME]),
          };
          entries.push(currentEntry);
          return;
        }

        if (currentEntry) {
          currentEntry.rows.push({
            rowIndex,
            row: this.cloneRowPreservingValues(row),
          });
          currentEntry.endRowIndex = rowIndex;
          return;
        }

        if (hasAnyData) {
          warnings.push(
            `Row ${rowIndex + 1} has data before the first populated transaction hash in column B and was not assigned to an entry.`
          );
        }
      });

      return { entries, warnings };
    },

    collectTargetCandidates(entry) {
      if (!entry) {
        return [];
      }

      const targetColumnIndex = this.getTargetColumnIndex();
      const map = new Map();

      entry.rows.forEach(({ rowIndex, row }) => {
        const address = this.toText(row?.[targetColumnIndex]);
        const key = this.normalizeAddressKey(address);
        if (!key) {
          return;
        }

        if (!map.has(key)) {
          map.set(key, {
            key,
            address,
            rowNumbers: [],
            amountTotal: 0,
            amountCount: 0,
            token: this.toText(row?.[COL.TOKEN]),
          });
        }

        const candidate = map.get(key);
        candidate.rowNumbers.push(rowIndex + 1);

        const amount = this.parseNumberLike(row?.[COL.AMOUNT]);
        if (Number.isFinite(amount)) {
          candidate.amountTotal += amount;
          candidate.amountCount += 1;
        }

        if (!candidate.token) {
          candidate.token = this.toText(row?.[COL.TOKEN]);
        }
      });

      return [...map.values()];
    },

    getEntryDate(entry) {
      if (!entry) {
        return null;
      }

      for (const { row } of entry.rows) {
        const date = this.parseUtcDate(row?.[COL.DATE]);
        if (date) {
          return date;
        }
      }

      return null;
    },

    deriveFundsIn(entry, selectedAddress) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const targetColumnIndex = this.getTargetColumnIndex();
      let fallbackRow = null;
      let matchingRow = null;

      for (const { row } of entry.rows) {
        if (!fallbackRow && this.rowHasAnyData(row)) {
          fallbackRow = row;
        }

        const rowAddressKey = this.normalizeAddressKey(row?.[targetColumnIndex]);
        if (rowAddressKey && rowAddressKey === selectedKey) {
          matchingRow = row;
          break;
        }
      }

      const row = matchingRow || fallbackRow || this.makeBlankRow();
      const swapConversion = this.getSwapRowConversionContext(row);
      if (swapConversion?.canConvert && Number.isFinite(swapConversion.followedAfter)) {
        return {
          amount: swapConversion.followedAfter,
          token: swapConversion.receivedToken,
          source: "swap row conversion from Column M and Column J",
          swapConversion,
        };
      }

      const fundsFollowedText = this.toText(row?.[COL.FUNDS_FOLLOWED]);
      const amountFromFundsFollowed = this.parseNumberLike(fundsFollowedText);
      const tokenFromFundsFollowed = this.parseTokenLike(fundsFollowedText);
      const amountFromRow = this.parseNumberLike(row?.[COL.AMOUNT]);
      const tokenFromRow = this.toText(row?.[COL.TOKEN]);

      const amount = Number.isFinite(amountFromFundsFollowed)
        ? amountFromFundsFollowed
        : amountFromRow;

      const rawToken = tokenFromFundsFollowed || tokenFromRow;
      const token = this.isCaseBuddyBlankTokenText(rawToken) ? "" : rawToken;

      return {
        amount: Number.isFinite(amount) ? amount : null,
        token,
        source: Number.isFinite(amountFromFundsFollowed)
          ? "column M funds followed"
          : "column F amount",
      };
    },

    selectedReverseEntryHasZeroFundsAllocation(entry = this.selectedEntry, selectedAddress = this.selectedTargetAddress) {
      if (this.traceDirection !== "reverse" || !entry || !selectedAddress) return false;
      const row = this.getSelectedTargetRow(entry, selectedAddress) || [];
      const fundsText = this.toText(row?.[COL.FUNDS_FOLLOWED]);
      if (!fundsText) return false;
      const funds = this.parseNumberLike(fundsText);
      const amount = this.parseNumberLike(row?.[COL.AMOUNT]);
      const fromHash = this.toText(row?.[COL.FROM_HASH]);
      if (!Number.isFinite(funds) || Math.abs(funds) > 1e-12 || !Number.isFinite(amount) || amount <= 1e-12 || !fromHash) return false;
      const evidence = [
        row?.[COL.SYSTEM_NOTES],
        row?.[COL.TOOL88_NOTES],
        row?.[COL.HOP_LOGIC],
      ].map((value) => this.toText(value)).filter(Boolean).join(" ");
      if (!/(?:direction\s*=\s*reverse|tool\s*88\s*:\s*reverse\s+(?:fifo|lifo|next\s+chronological)|prior\s+source\s+(?:transfer|output))/i.test(evidence)) return false;
      return !/(?:accepted-report-boundary|manual-path-ending|address-poisoning-ending|reverse[-_\s]*swap|further\s+manual\s+work)/i.test(evidence);
    },

    getSwapRowConversionContext(row = []) {
      if (!row) return null;
      const combinedText = [row[COL.SYSTEM_NOTES], row[COL.TOOL88_NOTES], row[COL.HOP_LOGIC]]
        .map((value) => this.toText(value))
        .filter(Boolean)
        .join("\n");
      const looksLikeSwapRow = /funds\s+are\s+swapped\s+from/i.test(combinedText) || /\bSWAP\b/i.test(this.toText(row[COL.HOP_LOGIC]));
      if (!looksLikeSwapRow) return null;

      const receivedAmount = this.parseNumberLike(row[COL.AMOUNT]);
      const receivedToken = this.toText(row[COL.TOKEN]).trim();
      const fundsFollowedText = this.toText(row[COL.FUNDS_FOLLOWED]);
      const followedBefore = this.parseNumberLike(fundsFollowedText);
      const sentToken = this.parseTokenLike(fundsFollowedText);
      if (!Number.isFinite(receivedAmount) || receivedAmount <= 0 || !Number.isFinite(followedBefore) || followedBefore <= 0 || !receivedToken || !sentToken) {
        return null;
      }
      if (this.tokenSymbolsMatchForSwap(sentToken, receivedToken)) {
        return null;
      }

      const sentTotal = this.extractSwapSentTotalFromText(combinedText, sentToken);
      if (!Number.isFinite(sentTotal) || sentTotal <= 0) {
        return null;
      }

      const rate = receivedAmount / sentTotal;
      const leftBefore = this.getPlainRowLeftBoundsAmount(row);
      const followedAfter = followedBefore * rate;
      const leftAfter = Number.isFinite(leftBefore) ? leftBefore * rate : 0;
      return {
        canConvert: Number.isFinite(rate) && rate > 0 && Number.isFinite(followedAfter),
        sentToken,
        receivedToken,
        sentTotal,
        receivedAmount,
        followedBefore,
        followedAfter,
        leftBefore: Number.isFinite(leftBefore) ? leftBefore : 0,
        leftAfter,
        rate,
      };
    },

    isSelectedEntrySolvedSwapContext(selectedAddress = this.selectedTargetAddress, entry = this.selectedEntry) {
      if (!entry || this.traceDirection === "reverse") return false;
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      if (!selectedKey) return false;

      let selectedIsTarget = false;
      let hasSwapMarker = false;
      let hasFromToSameSelectedAddress = false;
      let hasConvertibleSwapRow = false;
      const targetColumnIndex = this.getTargetColumnIndex();

      (entry.rows || []).forEach(({ row }) => {
        if (!row) return;
        if (this.normalizeAddressKey(row[targetColumnIndex]) === selectedKey) selectedIsTarget = true;
        const fromKey = this.normalizeAddressKey(row[COL.FROM_ADDRESS]);
        const toKey = this.normalizeAddressKey(row[COL.TO_ADDRESS]);
        if (fromKey === selectedKey && toKey === selectedKey) hasFromToSameSelectedAddress = true;

        const combinedText = [row[COL.SYSTEM_NOTES], row[COL.TOOL88_NOTES], row[COL.HOP_LOGIC]]
          .map((value) => this.toText(value))
          .filter(Boolean)
          .join("\n");
        if (/funds\s+are\s+swapped\s+from|tool\s*88:\s*swap\s+solved|\bSWAP\b/i.test(combinedText)) {
          hasSwapMarker = true;
        }

        const conversion = this.getSwapRowConversionContext(row);
        if (conversion?.canConvert) hasConvertibleSwapRow = true;
      });

      return Boolean(selectedIsTarget && (hasConvertibleSwapRow || (hasSwapMarker && hasFromToSameSelectedAddress)));
    },

    isSelectedEntrySolvedReverseSwapContext(selectedAddress = this.selectedTargetAddress, entry = this.selectedEntry) {
      if (!entry || this.traceDirection !== "reverse") return false;
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      if (!selectedKey) return false;

      return (entry.rows || []).some(({ row }) => {
        if (!row) return false;
        const fromKey = this.normalizeAddressKey(row[COL.FROM_ADDRESS]);
        const toKey = this.normalizeAddressKey(row[COL.TO_ADDRESS]);
        if (fromKey !== selectedKey || toKey !== selectedKey) return false;

        const combinedText = [row[COL.SYSTEM_NOTES], row[COL.TOOL88_NOTES], row[COL.HOP_LOGIC]]
          .map((value) => this.toText(value))
          .filter(Boolean)
          .join("\n");
        const hasReverseSwapMarker = /tool\s*88\s*:\s*reverse\s+swap\s+solved|action\s*=\s*reverse-swap[^\n]*direction\s*=\s*reverse|reverse[-_\s]*swap\s+funds\s+followed/i.test(combinedText);
        if (!hasReverseSwapMarker) return false;

        const followedText = this.toText(row[COL.FUNDS_FOLLOWED]);
        const followedAmount = this.parseNumberLike(followedText);
        const followedToken = this.parseTokenLike(followedText);
        const rowToken = this.toText(row[COL.TOKEN]);
        return Number.isFinite(followedAmount)
          && followedAmount > 0
          && Boolean(followedToken)
          && this.tokenSymbolsMatchForSwap(followedToken, rowToken);
      });
    },

    getSelectedEntryPostSwapTraceContext(selectedAddress = this.selectedTargetAddress, entry = this.selectedEntry) {
      if (!this.isSelectedEntrySolvedSwapContext(selectedAddress, entry)) return null;
      const row = this.getSelectedTargetRow(entry, selectedAddress) || entry?.rows?.[0]?.row || [];
      const converted = this.getSwapRowConversionContext(row);
      const receivedToken = this.toText(converted?.receivedToken || row?.[COL.TOKEN] || "").trim();
      const followedAfter = Number(converted?.followedAfter);
      const leftAfter = Number(converted?.leftAfter);
      return {
        row,
        canConvert: Boolean(converted?.canConvert),
        receivedToken,
        followedAfter: Number.isFinite(followedAfter) ? followedAfter : null,
        leftAfter: Number.isFinite(leftAfter) ? leftAfter : null,
        conversion: converted,
      };
    },

    extractSwapSentTotalFromText(text = "", sentToken = "") {
      const token = this.toText(sentToken).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      if (!token) return null;
      const candidates = [];
      const source = this.toText(text);
      const swapSentence = source.match(/funds\s+are\s+swapped\s+from[\s\S]*?(?:\.|$)/i)?.[0] || source;
      const regex = new RegExp(`([0-9][0-9,]*(?:\\.[0-9]+)?)\\s+${token}\\b`, "ig");
      let match;
      while ((match = regex.exec(swapSentence))) {
        const parsed = this.parseNumberLike(match[1]);
        if (Number.isFinite(parsed) && parsed > 0) candidates.push(parsed);
      }
      if (!candidates.length && swapSentence !== source) {
        while ((match = regex.exec(source))) {
          const parsed = this.parseNumberLike(match[1]);
          if (Number.isFinite(parsed) && parsed > 0) candidates.push(parsed);
        }
      }
      return candidates.length ? Math.max(...candidates) : null;
    },

    getPlainRowLeftBoundsAmount(row = []) {
      const numeric = this.parseNumberLike(row?.[COL.LEFT_BOUNDS_NUMERIC]);
      if (Number.isFinite(numeric)) return Math.max(0, numeric);
      const display = this.parseNumberLike(row?.[COL.LEFT_BOUNDS_DISPLAY]);
      return Number.isFinite(display) ? Math.max(0, display) : 0;
    },

    getSelectedTargetRow(entry, selectedAddress) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const targetColumnIndex = this.getTargetColumnIndex();
      if (!entry || !selectedKey) {
        return null;
      }

      for (const { row } of entry.rows) {
        const rowAddressKey = this.normalizeAddressKey(row?.[targetColumnIndex]);
        if (rowAddressKey === selectedKey) {
          return row;
        }
      }

      return entry.rows?.find(({ row }) => this.rowHasAnyData(row))?.row || null;
    },

    getSelectedTargetRowRecord(entry, selectedAddress) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const targetColumnIndex = this.getTargetColumnIndex();
      if (!entry || !selectedKey) {
        return null;
      }

      for (const item of entry.rows || []) {
        const rowAddressKey = this.normalizeAddressKey(item?.row?.[targetColumnIndex]);
        if (rowAddressKey === selectedKey) {
          return item;
        }
      }

      return (entry.rows || []).find(({ row }) => this.rowHasAnyData(row)) || null;
    },

    isCaseBuddyBlankTokenText(value) {
      const text = this.toText(value).trim();
      return !text || /^(?:null|undefined|nan)$/i.test(text);
    },

    tool88FlagIsTrue(value) {
      if (value === true) return true;
      if (value === false || value === null || value === undefined) return false;
      if (typeof value === 'number') return Number.isFinite(value) && value !== 0;
      const text = this.toText(value).trim().toLowerCase();
      if (!text) return false;
      if (['0', 'false', 'no', 'n', 'off', 'null', 'undefined', 'nan', 'none', 'neutral'].includes(text)) return false;
      if (['1', 'true', 'yes', 'y', 'on', 'ok', 'pass', 'present', 'matched'].includes(text)) return true;
      return Boolean(text);
    },

    tool88AnyFlagIsTrue(...values) {
      return values.some((value) => this.tool88FlagIsTrue(value));
    },

    tool88FirstFiniteSignalNumber(...values) {
      for (const value of values.flat(Infinity)) {
        if (typeof value === "number" && Number.isFinite(value)) return value;
        const text = this.toText(value).replace(/,/g, "").trim();
        if (!text) continue;
        const direct = Number(text);
        if (Number.isFinite(direct)) return direct;
        const match = text.match(/-?\d+(?:\.\d+)?(?:e[+-]?\d+)?/i);
        if (match) {
          const parsed = Number(match[0]);
          if (Number.isFinite(parsed)) return parsed;
        }
      }
      return null;
    },

    tool88SignalTextBundle(raw = {}) {
      if (!raw || typeof raw !== "object") return "";
      if (typeof this.buildCaseBuddySignalTextBundle === "function") {
        return this.buildCaseBuddySignalTextBundle(raw);
      }
      const pieces = [
        raw.detail_text,
        raw.detailText,
        raw.evidence_summary,
        raw.evidenceSummary,
        raw.evidence_label,
        raw.evidenceLabel,
        raw.message,
        raw.summary,
        raw.summary_label,
        raw.summaryLabel,
        raw.recommendation,
        raw.guidance,
        raw.decision_guidance,
        raw.status_label,
        raw.statusLabel,
      ];
      if (Array.isArray(raw.warnings)) pieces.push(...raw.warnings);
      if (Array.isArray(raw.evidence)) pieces.push(...raw.evidence);
      return pieces.map((value) => this.toText(value)).filter(Boolean).join("\n");
    },

    tool88SignalTextShowsSingleTransfer(raw = {}) {
      const text = this.tool88SignalTextBundle(raw).toLowerCase();
      if (!text) return false;
      return /\b1\s+logs?\s*\(\s*1\s+transfer\s*,\s*0\s+other\s*\)/i.test(text) ||
        /\b(?:one|1)\s+(?:erc20\s+|trc20\s+|token\s+)?transfer\s+logs?\b[\s\S]{0,120}\b(?:no|0)\s+(?:non[-\s]?transfer|other)\s+logs?\b/i.test(text) ||
        /\bexactly\s+one\s+(?:erc20\s+|trc20\s+|token\s+)?transfer\s+logs?\b/i.test(text);
    },

    getFirstCaseBuddyEntryRowRecord(entry = null) {
      return (Array.isArray(entry?.rows) ? entry.rows : []).find((item) => Array.isArray(item?.row) && this.rowHasAnyData(item.row)) || null;
    },

    caseBuddyCanInferMissingTargetFromSingleTransfer(entry = null) {
      if (!entry || this.traceDirection === 'reverse') return false;
      if (!this.caseBuddyEmbedded) return false;
      const txHash = this.toText(entry.txHash);
      if (!txHash) return false;
      const candidates = this.collectTargetCandidates(entry);
      return candidates.length === 0;
    },

    applyCaseBuddyInferredSelectedTarget(inputRows = [], entry = null, inferredAddress = '') {
      const address = this.toText(inferredAddress);
      if (!entry || !address || this.traceDirection === 'reverse') return false;
      const record = this.getFirstCaseBuddyEntryRowRecord(entry);
      const row = record?.row;
      if (!Array.isArray(row)) return false;
      const rowIndex = Number.isInteger(record.rowIndex) ? record.rowIndex : -1;
      if (this.toText(row[COL.TO_ADDRESS])) return false;
      row[COL.TO_ADDRESS] = address;
      if (rowIndex >= 0 && Array.isArray(inputRows[rowIndex])) {
        inputRows[rowIndex] = row;
      }
      if (rowIndex >= 0 && Array.isArray(this.inputSnapshotRows?.[rowIndex])) {
        this.inputSnapshotRows[rowIndex] = this.cloneRowPreservingValues(row);
      }
      if (rowIndex >= 0 && Array.isArray(this.outputBaseRows?.[rowIndex])) {
        this.outputBaseRows[rowIndex] = this.cloneRowPreservingValues(row);
      }
      return true;
    },

    async inferCaseBuddySelectedTargetFromSparseEntry(inputRows = [], entry = null, options = {}) {
      if (!this.caseBuddyCanInferMissingTargetFromSingleTransfer(entry)) {
        return { inferred: false, changed: false };
      }
      const countContext = this.inferCountRequestContext(entry, '');
      this.statusMessage = 'Hydrating sparse starting row: looking up the transaction receipt so Tool 88 can infer the only transfer recipient.';
      try {
        const detail = await this.lookupCaseBuddySelectedOutputDetails(entry, '', countContext, null);
        const inference = detail?.inference || null;
        const inferredAddress = this.toText(
          inference?.toAddress ||
          inference?.selectedTargetAddress ||
          inference?.selectedAddress ||
          inference?.targetAddress ||
          ''
        );
        if (!inference || !inferredAddress) {
          return {
            inferred: false,
            changed: false,
            blocked: true,
            detail,
            message: 'Tool 88 could not infer a missing To Address from a single transfer log. Add the To Address when the transaction has multiple outputs, internal transfers, or ambiguous token transfers.'
          };
        }
        const targetChanged = this.applyCaseBuddyInferredSelectedTarget(inputRows, entry, inferredAddress);
        const outputChanged = this.applyCaseBuddySelectedOutputInference(inputRows, entry, inferredAddress, inference);
        this.selectedTargetAddress = inferredAddress;
        this.$refs.inputSpreadsheet?.loadData?.(this.ensureMinimumRows(inputRows));
        this.pushUniqueWarnings([
          `Tool 88 inferred missing Case Buddy To Address ${this.shortenMiddle(inferredAddress, 10, 8)} from the only transfer log and hydrated the sparse starting row with ${this.formatFunds(inference.amount, inference.token)}.`
        ]);
        this.scrollCaseBuddyEmbeddedSimpleViewToTop();
        return {
          inferred: true,
          changed: targetChanged || outputChanged,
          selectedAddress: inferredAddress,
          inference,
          detail,
          source: detail?.source || options.source || 'single-transfer selected-output hydration'
        };
      } catch (error) {
        const message = `Tool 88 could not infer the missing To Address: ${error?.message || 'unknown selected-output lookup error'}.`;
        this.pushUniqueWarnings([message]);
        return { inferred: false, changed: false, blocked: true, error: error?.message || 'unknown selected-output lookup error', message };
      }
    },

    caseBuddySelectedHopNeedsOutputInference(entry, selectedAddress) {
      if (!entry || !selectedAddress) {
        return false;
      }
      const record = this.getSelectedTargetRowRecord(entry, selectedAddress);
      const row = record?.row || [];
      const fundsIn = this.deriveFundsIn(entry, selectedAddress);
      const rowAmount = this.parseNumberLike(row?.[COL.AMOUNT]);
      const rowToken = this.toText(row?.[COL.TOKEN]);
      const fundsToken = this.parseTokenLike(row?.[COL.FUNDS_FOLLOWED]);
      const fundsText = this.toText(row?.[COL.FUNDS_FOLLOWED]);
      const leftBoundsText = this.toText(row?.[COL.LEFT_BOUNDS_NUMERIC] || row?.[COL.LEFT_BOUNDS_DISPLAY]);
      return !Number.isFinite(rowAmount) || rowAmount <= 0 ||
        this.isCaseBuddyBlankTokenText(rowToken || fundsToken || fundsIn?.token) ||
        !Number.isFinite(Number(fundsIn?.amount)) || Number(fundsIn.amount) <= 0 ||
        !fundsText || !leftBoundsText;
    },

    parseCaseBuddyIntegerAmountWithDecimals(rawValue, decimals = null) {
      const text = this.toText(rawValue).replace(/,/g, '').trim();
      const decimalCount = Number(decimals);
      if (!text || !Number.isInteger(decimalCount) || decimalCount < 0 || decimalCount > 36) {
        return null;
      }

      const multiplyDecimalStringBySmall = (digits, multiplier, addend = 0) => {
        let carry = Number(addend) || 0;
        let output = '';
        for (let index = digits.length - 1; index >= 0; index -= 1) {
          const product = (Number(digits[index]) || 0) * multiplier + carry;
          output = String(product % 10) + output;
          carry = Math.floor(product / 10);
        }
        while (carry > 0) {
          output = String(carry % 10) + output;
          carry = Math.floor(carry / 10);
        }
        return output.replace(/^0+(?=\d)/, '') || '0';
      };

      const hexToDecimalString = (hexText) => {
        let output = '0';
        for (const character of String(hexText || '').replace(/^0x/i, '')) {
          const value = Number.parseInt(character, 16);
          if (!Number.isFinite(value)) return '';
          output = multiplyDecimalStringBySmall(output, 16, value);
        }
        return output;
      };

      const negative = text.startsWith('-');
      const unsignedText = negative ? text.slice(1) : text;
      let integerDigits = '';
      if (/^0x[0-9a-f]+$/i.test(unsignedText)) {
        integerDigits = hexToDecimalString(unsignedText);
      } else if (/^\d+$/.test(unsignedText)) {
        integerDigits = unsignedText.replace(/^0+(?=\d)/, '') || '0';
      }
      if (!integerDigits) return null;

      const paddedDigits = integerDigits.padStart(decimalCount + 1, '0');
      const splitIndex = paddedDigits.length - decimalCount;
      const whole = decimalCount > 0 ? paddedDigits.slice(0, splitIndex) : paddedDigits;
      const fraction = decimalCount > 0
        ? paddedDigits.slice(splitIndex).replace(/0+$/g, '')
        : '';
      const amountText = `${negative ? '-' : ''}${whole.replace(/^0+(?=\d)/, '') || '0'}${fraction ? `.${fraction}` : ''}`;
      const amount = Number(amountText);
      return Number.isFinite(amount) ? amount : null;
    },

    parseCaseBuddySelectedTransferAmount(transfer = {}, tokenInfo = {}) {
      const raw = transfer.raw || {};
      const decimalCandidates = [
        transfer.amountDecimal,
        transfer.amount_decimal,
        transfer.valueDecimal,
        transfer.value_decimal,
        transfer.formattedValue,
        transfer.formatted_value,
        transfer.humanValue,
        transfer.human_value,
        transfer.amountDisplay,
        transfer.amount_display,
        raw.amount_decimal,
        raw.amountDecimal,
        raw.value_decimal,
        raw.valueDecimal,
        raw.token_amount_decimal,
        raw.tokenAmountDecimal,
        raw.formatted_value,
        raw.formattedValue,
        raw.human_value,
        raw.humanValue,
        raw.value_display,
        raw.valueDisplay,
        raw.amount_display,
        raw.amountDisplay,
      ];
      for (const value of decimalCandidates) {
        const parsed = this.parseNumberLike(value);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
      }

      const decimals = Number(tokenInfo.decimals);
      const integerCandidates = [
        transfer.rawAmount,
        transfer.raw_amount,
        transfer.valueRaw,
        transfer.value_raw,
        transfer.tokenValue,
        transfer.token_value,
        transfer.quantity,
        transfer.quant,
        transfer.value,
        transfer.amount,
        raw.raw_amount,
        raw.rawAmount,
        raw.value_raw,
        raw.valueRaw,
        raw.token_value,
        raw.tokenValue,
        raw.quantity,
        raw.quant,
        raw.value,
        raw.amount,
      ];
      for (const value of integerCandidates) {
        const text = this.toText(value).trim();
        if (!text) continue;
        if (text.includes('.') || /e[+-]?\d+$/i.test(text)) {
          const parsed = this.parseNumberLike(text);
          if (Number.isFinite(parsed) && parsed > 0) return parsed;
          continue;
        }
        const parsedWithDecimals = this.parseCaseBuddyIntegerAmountWithDecimals(text, decimals);
        if (Number.isFinite(parsedWithDecimals) && parsedWithDecimals > 0) return parsedWithDecimals;
        if (!Number.isFinite(decimals)) {
          const parsed = this.parseNumberLike(text);
          if (Number.isFinite(parsed) && parsed > 0) return parsed;
        }
      }
      return null;
    },

    buildCaseBuddyTransferTokenInfo(transfer = {}, rawSignal = {}, countContext = {}) {
      const raw = transfer.raw || {};
      const decoded = raw.decoded || raw.decoded_event || raw.decodedEvent || raw.event_data || raw.eventData || {};
      const rawTokenInfo = raw.tokenInfo || raw.token_info || raw.token || raw.asset || decoded.tokenInfo || decoded.token_info || decoded.token || {};
      const signalTokenInfo = rawSignal.tokenInfo || rawSignal.token_info || rawSignal.token || rawSignal.asset || {};
      const contractAddress = this.toText(
        transfer.contractAddress ||
        transfer.contract_address ||
        transfer.tokenAddress ||
        transfer.token_address ||
        transfer.address ||
        raw.contractAddress ||
        raw.contract_address ||
        raw.tokenAddress ||
        raw.token_address ||
        raw.token_contract ||
        raw.address ||
        decoded.contractAddress ||
        decoded.contract_address ||
        decoded.tokenAddress ||
        decoded.token_address ||
        rawTokenInfo.contractAddress ||
        rawTokenInfo.contract_address ||
        rawTokenInfo.tokenId ||
        rawTokenInfo.address ||
        signalTokenInfo.contractAddress ||
        signalTokenInfo.contract_address ||
        signalTokenInfo.tokenId ||
        signalTokenInfo.address ||
        rawSignal.token_contract ||
        rawSignal.tokenContract ||
        rawSignal.contract_address ||
        rawSignal.contractAddress ||
        ''
      );
      const symbol = this.toText(
        transfer.tokenSymbol ||
        transfer.token_symbol ||
        transfer.symbol ||
        transfer.token ||
        transfer.asset_symbol ||
        transfer.assetSymbol ||
        raw.tokenSymbol ||
        raw.token_symbol ||
        raw.symbol ||
        raw.token ||
        raw.asset_symbol ||
        raw.assetSymbol ||
        decoded.tokenSymbol ||
        decoded.token_symbol ||
        decoded.symbol ||
        rawTokenInfo.symbol ||
        rawTokenInfo.tokenAbbr ||
        rawTokenInfo.tokenSymbol ||
        rawTokenInfo.token_symbol ||
        signalTokenInfo.symbol ||
        signalTokenInfo.tokenAbbr ||
        signalTokenInfo.tokenSymbol ||
        signalTokenInfo.token_symbol ||
        rawSignal.token_symbol ||
        rawSignal.tokenSymbol ||
        rawSignal.asset_symbol ||
        rawSignal.assetSymbol ||
        countContext.token_symbol ||
        ''
      ).toUpperCase();
      const resolution = this.resolveTokenForContext(
        symbol,
        countContext.chain_id || rawSignal.chain_id || rawSignal.chainId || '',
        countContext.chain_name || rawSignal.chain_name || rawSignal.chainName || '',
        countContext.network_family || rawSignal.network_family || rawSignal.chain_family || 'evm',
        contractAddress
      );
      const decimalsRaw = transfer.tokenDecimals ?? transfer.token_decimals ?? transfer.decimals ?? raw.tokenDecimals ?? raw.token_decimals ?? raw.decimals ?? decoded.tokenDecimals ?? decoded.token_decimals ?? decoded.decimals ?? rawTokenInfo.decimals ?? rawTokenInfo.tokenDecimal ?? rawTokenInfo.token_decimals ?? signalTokenInfo.decimals ?? signalTokenInfo.tokenDecimal ?? signalTokenInfo.token_decimals ?? rawSignal.token_decimals ?? rawSignal.tokenDecimals ?? resolution?.decimals;
      const decimals = Number(decimalsRaw);
      return {
        symbol: symbol || resolution?.token?.symbol || '',
        contractAddress: resolution?.contractAddress || contractAddress,
        decimals: Number.isFinite(decimals) ? decimals : null,
        resolution,
      };
    },

    collectCaseBuddySignalPayloadObjects(value, depth = 0, seen = new WeakSet()) {
      if (depth > 7 || value === null || value === undefined) return [];
      if (typeof value === 'string') {
        const parsed = this.tool88TryParseJsonText(value);
        return parsed ? this.collectCaseBuddySignalPayloadObjects(parsed, depth + 1, seen) : [];
      }
      if (typeof value !== 'object') return [];
      if (seen.has(value)) return [];
      seen.add(value);
      const objects = [value];
      if (Array.isArray(value)) {
        value.forEach(item => {
          objects.push(...this.collectCaseBuddySignalPayloadObjects(item, depth + 1, seen));
        });
        return objects;
      }
      Object.keys(value).forEach((key) => {
        const child = value[key];
        if (!child || typeof child !== 'object') return;
        const normalizedKey = this.toText(key).toLowerCase();
        if (/^(result|raw|data|payload|response|receipt|transaction|tx|log|logs|events?|decoded|decoded_logs|decodedlogs|transfers?|token_transfers|tokentransfers|erc20|erc20_transfers|erc20transfers|trc20|trc20_transfers|trc20transfers|selected|selected_output|selectedoutput|selected_transfer|selectedtransfer|matched|matched_transfer|matchedtransfer)$/i.test(normalizedKey) || /(?:log|event|transfer|receipt|output|token|result|raw|payload|response)/i.test(normalizedKey)) {
          objects.push(...this.collectCaseBuddySignalPayloadObjects(child, depth + 1, seen));
        }
      });
      return objects;
    },

    buildCaseBuddySignalTextBundle(value, depth = 0, seen = new WeakSet()) {
      if (depth > 6 || value === null || value === undefined) return '';
      if (typeof value !== 'object') return this.toText(value);
      if (seen.has(value)) return '';
      seen.add(value);
      const pieces = [];
      if (Array.isArray(value)) {
        value.forEach(item => {
          const text = this.buildCaseBuddySignalTextBundle(item, depth + 1, seen);
          if (text) pieces.push(text);
        });
        return pieces.join('\n');
      }
      const preferredKeys = [
        'detail_text', 'detailText', 'evidence_summary', 'evidenceSummary', 'evidence_label', 'evidenceLabel',
        'message', 'summary', 'summary_label', 'summaryLabel', 'recommendation', 'guidance', 'decision_guidance',
        'status_label', 'statusLabel', 'description', 'text', 'html', 'selected_output_text', 'selectedOutputText',
        'selected_transfer_text', 'selectedTransferText', 'token_name', 'tokenName', 'token_symbol', 'tokenSymbol',
        'asset_symbol', 'assetSymbol', 'amount_display', 'amountDisplay', 'selected_output_amount', 'selectedOutputAmount'
      ];
      preferredKeys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          const text = this.toText(this.tool88ExtractPrimitiveProviderValue(value[key]));
          if (text) pieces.push(text);
        }
      });
      Object.keys(value).forEach((key) => {
        const child = value[key];
        if (child === null || child === undefined) return;
        const relevant = /(?:selected|output|transfer|token|symbol|amount|value|quantity|recipient|receiver|to|from|address|detail|evidence|message|summary|label|description|text|html|name|asset|currency|etherscan|explorer|receipt|log|event|raw|result|payload|response)/i.test(key);
        if (typeof child !== 'object') {
          if (relevant || depth <= 2) {
            const text = this.toText(this.tool88ExtractPrimitiveProviderValue(child));
            if (text && text.length <= 12000) pieces.push(`${key}: ${text}`);
          }
          return;
        }
        if (depth >= 3 && !relevant) return;
        const text = this.buildCaseBuddySignalTextBundle(child, depth + 1, seen);
        if (text) pieces.push(text);
      });
      if (Array.isArray(value.warnings)) pieces.push(...value.warnings.map(item => this.toText(item)).filter(Boolean));
      if (Array.isArray(value.evidence)) pieces.push(...value.evidence.map(item => this.toText(item)).filter(Boolean));
      return [...new Set(pieces.filter(Boolean))].join('\n');
    },

    inferCaseBuddySelectedOutputFromSignalText(rawSignal = {}, selectedAddress = '', countContext = {}) {
      const text = this.buildCaseBuddySignalTextBundle(rawSignal);
      if (!text) return null;
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const normalizedText = text.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ');
      const escapedAddress = selectedKey ? selectedKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : '';
      const candidateWindows = [];
      if (escapedAddress) {
        const addressPattern = new RegExp(escapedAddress, 'ig');
        let match;
        while ((match = addressPattern.exec(normalizedText)) && candidateWindows.length < 8) {
          const start = Math.max(0, match.index - 280);
          const end = Math.min(normalizedText.length, match.index + escapedAddress.length + 360);
          candidateWindows.push(normalizedText.slice(start, end));
        }
      }
      candidateWindows.push(normalizedText);

      const knownToken = this.toText(countContext.token_symbol || countContext.tokenSymbol || '').toUpperCase();
      const tokenContract = this.toText(countContext.token_contract || countContext.contract_address || countContext.tokenContract || countContext.contractAddress || '');
      const tokenResolution = this.resolveTokenForContext(
        knownToken,
        countContext.chain_id || countContext.chainId || '',
        countContext.chain_name || countContext.chainName || '',
        countContext.network_family || countContext.networkFamily || countContext.chain_family || 'evm',
        tokenContract
      );
      const fallbackToken = knownToken || tokenResolution?.token?.symbol || '';
      const parseWindow = (windowText = '') => {
        const patterns = [
          /\b(?:for|amount|value|selected\s+output|selected\s+transfer|transferred|transfer(?:red)?\s+amount)\b[^0-9-]{0,120}(-?\d[\d,]*(?:\.\d+)?)(?:\s*\([^)]*\))?\s*(?:[A-Za-z][A-Za-z0-9 ._-]{0,40}\s*)?\(([A-Z][A-Z0-9._/-]{1,12})\)/i,
          /\b(?:for|amount|value|selected\s+output|selected\s+transfer|transferred|transfer(?:red)?\s+amount)\b[^0-9-]{0,120}(-?\d[\d,]*(?:\.\d+)?)\s+([A-Z][A-Z0-9._/-]{1,12})\b/i,
          /(-?\d[\d,]*(?:\.\d+)?)(?:\s*\([^)]*\))?\s*(?:Tether\s+USD|USD\s*Tether|USD\s+Coin|Dai\s+Stablecoin|Bitcoin|Ether|Ethereum)\s*\(([A-Z][A-Z0-9._/-]{1,12})\)/i,
          /(-?\d[\d,]*(?:\.\d+)?)\s+([A-Z][A-Z0-9._/-]{1,12})\b/i
        ];
        for (const pattern of patterns) {
          const match = windowText.match(pattern);
          if (!match) continue;
          const amount = this.parseNumberLike(match[1]);
          const token = this.toText(match[2] || fallbackToken).toUpperCase();
          const tokenLooksLikeNarrativeWord = /^(?:LOG|LOGS|TRANSFER|TRANSFERS|OTHER|OUTPUT|OUTPUTS|RECIPIENT|RECIPIENTS|ADDRESS|ADDRESSES|FROM|TO|FOR|VALUE|AMOUNT)$/i.test(token);
          const tokenLooksSupported = !!fallbackToken || /^(?:USDT|USDC|DAI|USDD|BTC|ETH|WETH|TRX|BNB|MATIC|POL|SOL|AVAX|FTM)$/i.test(token);
          if (Number.isFinite(amount) && amount > 0 && !this.isCaseBuddyBlankTokenText(token) && !tokenLooksLikeNarrativeWord && tokenLooksSupported) {
            return { amount, token };
          }
        }
        return null;
      };
      for (const windowText of candidateWindows) {
        const parsed = parseWindow(windowText);
        if (parsed) {
          return {
            amount: parsed.amount,
            token: parsed.token,
            toAddress: selectedAddress,
            leftBoundsDisplay: '0.00',
            leftBoundsNumeric: '0',
            source: 'transaction signal selected output text'
          };
        }
      }
      return null;
    },

    async lookupCaseBuddySelectedOutputDetails(entry = null, selectedAddress = '', countContext = {}, rawSignal = null) {
      const primary = rawSignal || null;
      const primaryInference = this.extractCaseBuddySelectedOutputInferenceFromSignal(primary, selectedAddress, countContext);
      if (primaryInference) return { inference: primaryInference, signal: primary, source: 'transaction_signal_check' };

      if (this.useMockBackend || !entry) {
        return { inference: null, signal: primary, source: 'transaction_signal_check' };
      }
      const selectedAddressRequired = this.traceDirection === 'reverse' || !!this.toText(selectedAddress);
      const allowSingleTransferTargetInference = this.traceDirection !== 'reverse' && !this.toText(selectedAddress);

      const attribution = this.attributionLookupResult || {};
      const baseBody = {
        action: 'transaction_signal_check',
        tx_hash: entry?.txHash || '',
        hash: entry?.txHash || '',
        address: selectedAddress,
        selected_address: selectedAddress,
        selected_to_address: this.traceDirection === 'reverse' ? '' : selectedAddress,
        selected_from_address: this.traceDirection === 'reverse' ? selectedAddress : '',
        selected_target_address: selectedAddress,
        target_address: selectedAddress,
        selected_output_address: selectedAddress,
        selected_recipient_address: this.traceDirection === 'reverse' ? '' : selectedAddress,
        selected_sender_address: this.traceDirection === 'reverse' ? selectedAddress : '',
        selected_output_recipient: this.traceDirection === 'reverse' ? '' : selectedAddress,
        selected_output_to: this.traceDirection === 'reverse' ? '' : selectedAddress,
        selected_output_to_address: this.traceDirection === 'reverse' ? '' : selectedAddress,
        selected_output_sender: this.traceDirection === 'reverse' ? selectedAddress : '',
        selected_output_from: this.traceDirection === 'reverse' ? selectedAddress : '',
        selected_output_from_address: this.traceDirection === 'reverse' ? selectedAddress : '',
        selected_transfer_must_match_to_address: this.traceDirection !== 'reverse' && selectedAddressRequired ? 1 : 0,
        allow_single_transfer_target_inference: allowSingleTransferTargetInference ? 1 : 0,
        infer_target_from_single_transfer: allowSingleTransferTargetInference ? 1 : 0,
        selected_output_address_optional: allowSingleTransferTargetInference ? 1 : 0,
        allow_missing_selected_address: allowSingleTransferTargetInference ? 1 : 0,
        direction: this.traceDirection,
        chain_id: countContext.chain_id || entry?.chainId || '',
        chain_name: countContext.chain_name || entry?.chainName || '',
        chain_label: countContext.chain_label || '',
        network_family: countContext.network_family || '',
        provider_hint: countContext.provider_hint || '',
        count_kind: countContext.count_kind || 'auto',
        token_symbol: countContext.token_symbol || this.getSelectedTokenSymbol(entry, selectedAddress) || '',
        token_contract: countContext.token_contract || countContext.contract_address || '',
        contract_address: countContext.token_contract || countContext.contract_address || '',
        token_decimals: countContext.token_decimals ?? null,
        native_symbol: countContext.native_symbol || '',
        moralis_chain: countContext.moralis_chain || '',
        case_buddy_embedded: 1,
        case_buddy_modal_embedded: this.caseBuddyEmbedded ? 1 : 0,
        case_buddy_style_input: 1,
        case_buddy_selected_output_inference: 1,
        case_buddy_root_output_hydration: 1,
        hydrate_sparse_root_entry: 1,
        sparse_starting_entry: 1,
        force_receipt_lookup: 1,
        force_selected_output_lookup: 1,
        force_selected_transfer_lookup: 1,
        require_selected_output_details: 1,
        require_selected_output_amount: 1,
        require_selected_output_token: 1,
        return_selected_transfer: 1,
        return_selected_output: 1,
        return_selected_output_details: 1,
        require_selected_transfer: 1,
        require_selected_transfer_amount: 1,
        infer_selected_output_details: 1,
        infer_selected_transfer_details: 1,
        resolve_selected_output: 1,
        resolve_selected_transfer: 1,
        selected_output_amount_required: 1,
        selected_output_token_required: 1,
        include_receipt: 1,
        return_receipt: 1,
        include_raw_response: 1,
        return_raw_response: 1,
        include_transfer_logs: 1,
        include_raw_logs: 1,
        include_token_transfers: 1,
        return_token_transfers: 1,
        include_logs: 1,
        include_decoded_logs: 1,
        return_logs: 1,
        return_decoded_logs: 1,
        decode_transfer_logs: 1,
        include_token_metadata: 1,
        require_token_metadata: 0,
        count_context: countContext,
        attribution_context: {
          display_label: attribution.displayLabel || attribution.display_label || '',
          classification: attribution.classification || '',
          entity_type: attribution.entityType || attribution.entity_type || '',
          signals: attribution.signals || [],
        },
        entry_context: this.serializeEntryContext(entry),
      };

      const attempts = [
        baseBody,
        { ...baseBody, action: 'selected_output_lookup' },
        { ...baseBody, action: 'transaction_selected_output' },
        { ...baseBody, action: 'transaction_output_details' },
      ];

      let lastSignal = primary;
      for (const body of attempts) {
        try {
          const json = await this.postTool88(body);
          const result = json?.result || json;
          if (!result || result.status === 'error' || result.error === true) continue;
          lastSignal = result;
          const inference = this.extractCaseBuddySelectedOutputInferenceFromSignal(result, selectedAddress, countContext);
          if (inference) return { inference, signal: result, source: body.action || 'transaction_signal_check' };
        } catch (error) {
          // Older backends may not expose the detail actions yet. Keep trying supported routes.
        }
      }

      return { inference: null, signal: lastSignal, source: 'transaction_signal_check' };
    },

    extractCaseBuddySelectedOutputInferenceFromSignal(rawSignal = {}, selectedAddress = '', countContext = {}) {
      const payloadObjects = this.collectCaseBuddySignalPayloadObjects(rawSignal);
      const top = rawSignal && typeof rawSignal === 'object' ? rawSignal : {};
      const root = top.result && typeof top.result === 'object' ? top.result : top;
      const nestedRaw = root.raw && typeof root.raw === 'object' ? root.raw : {};
      const payloadMerged = payloadObjects.reduce((merged, object) => {
        if (object && typeof object === 'object' && !Array.isArray(object)) return { ...merged, ...object };
        return merged;
      }, {});
      const raw = { ...top, ...nestedRaw, ...root, ...payloadMerged };
      const family = this.toText(countContext.network_family || raw.network_family || raw.chain_family || '').toLowerCase();
      const selectedKey = this.normalizeAddressKey(selectedAddress);

      if ((family === 'btc' || this.isBtcAddress(selectedAddress)) && (raw.selected_output_btc || raw.selectedOutputBtc)) {
        const amount = this.parseNumberLike(raw.selected_output_btc || raw.selectedOutputBtc);
        if (Number.isFinite(amount) && amount > 0) {
          return {
            amount,
            token: 'BTC',
            leftBoundsDisplay: '0.00',
            leftBoundsNumeric: '0',
            source: 'transaction signal selected BTC output',
          };
        }
      }

      const directContractAddress = raw.selected_output_token_contract ?? raw.selectedOutputTokenContract ?? raw.selected_transfer_token_contract ?? raw.selectedTransferTokenContract ?? raw.selected_token_contract ?? raw.selectedTokenContract ?? raw.token_contract ?? raw.tokenContract ?? raw.contract_address ?? raw.contractAddress ?? '';
      const directSymbol = this.toText(
        raw.selected_output_token ??
        raw.selectedOutputToken ??
        raw.selected_token_symbol ??
        raw.selectedTokenSymbol ??
        raw.selected_transfer_token ??
        raw.selectedTransferToken ??
        raw.selected_transfer_symbol ??
        raw.selectedTransferSymbol ??
        raw.token_symbol ??
        raw.tokenSymbol ??
        raw.asset_symbol ??
        raw.assetSymbol ??
        countContext.token_symbol ??
        ''
      ).toUpperCase();
      const directTokenResolution = this.resolveTokenForContext(
        directSymbol,
        countContext.chain_id || raw.chain_id || raw.chainId || '',
        countContext.chain_name || raw.chain_name || raw.chainName || '',
        countContext.network_family || raw.network_family || raw.chain_family || 'evm',
        directContractAddress
      );
      const directToken = directSymbol || directTokenResolution?.token?.symbol || '';
      const directDisplayAmount = this.parseNumberLike(
        raw.selected_output_amount ??
        raw.selectedOutputAmount ??
        raw.selected_token_amount ??
        raw.selectedTokenAmount ??
        raw.selected_transfer_amount ??
        raw.selectedTransferAmount ??
        raw.output_amount ??
        raw.outputAmount ??
        raw.amount_display ??
        raw.amountDisplay ??
        ''
      );
      const directRawAmountCandidate = raw.selected_output_raw_amount ??
        raw.selectedOutputRawAmount ??
        raw.selected_output_value_hex ??
        raw.selectedOutputValueHex ??
        raw.selected_output_data ??
        raw.selectedOutputData ??
        raw.selected_transfer_raw_amount ??
        raw.selectedTransferRawAmount ??
        raw.selected_transfer_value ??
        raw.selectedTransferValue ??
        raw.selected_transfer_value_hex ??
        raw.selectedTransferValueHex ??
        raw.selected_value_hex ??
        raw.selectedValueHex ??
        raw.selected_raw_value ??
        raw.selectedRawValue ??
        '';
      const directRawAmount = this.parseCaseBuddyIntegerAmountWithDecimals(directRawAmountCandidate, raw.selected_token_decimals ?? raw.selectedTokenDecimals ?? raw.selected_transfer_token_decimals ?? raw.selectedTransferTokenDecimals ?? raw.token_decimals ?? raw.tokenDecimals ?? directTokenResolution?.decimals);
      const directAmount = Number.isFinite(directDisplayAmount) && directDisplayAmount > 0
        ? directDisplayAmount
        : directRawAmount;
      if (Number.isFinite(directAmount) && directAmount > 0 && !this.isCaseBuddyBlankTokenText(directToken)) {
        return {
          amount: directAmount,
          token: directToken,
          fromAddress: raw.selected_from_address || raw.selectedFromAddress || raw.selected_transfer_from || raw.selectedTransferFrom || '',
          toAddress: raw.selected_to_address || raw.selectedToAddress || raw.selected_transfer_to || raw.selectedTransferTo || raw.inferred_selected_target_address || raw.inferredSelectedTargetAddress || selectedAddress,
          contractAddress: directTokenResolution?.contractAddress || directContractAddress,
          leftBoundsDisplay: '0.00',
          leftBoundsNumeric: '0',
          source: 'transaction signal selected output fields',
        };
      }

      const selectedTransferObject = raw.selected_transfer || raw.selectedTransfer || raw.selected_transfer_log || raw.selectedTransferLog || raw.selected_output_transfer || raw.selectedOutputTransfer || raw.selected_token_transfer || raw.selectedTokenTransfer || raw.selected_output_details || raw.selectedOutputDetails || raw.selected_output_context || raw.selectedOutputContext || raw.selected_output || raw.selectedOutput || raw.matched_output || raw.matchedOutput || raw.matched_transfer || raw.matchedTransfer || raw.transfer || null;
      if (selectedTransferObject && typeof selectedTransferObject === 'object') {
        const selectedTransferRows = this.collectTransferEventRows(selectedTransferObject);
        const selectedTransfer = selectedTransferRows[0] || {
          raw: selectedTransferObject,
          fromAddress: this.normalizeAddressKey(selectedTransferObject.from || selectedTransferObject.from_address || selectedTransferObject.fromAddress || selectedTransferObject.sender || selectedTransferObject.src),
          toAddress: this.normalizeAddressKey(selectedTransferObject.to || selectedTransferObject.to_address || selectedTransferObject.toAddress || selectedTransferObject.receiver || selectedTransferObject.recipient || selectedTransferObject.destination || selectedTransferObject.dst || selectedAddress),
          value: this.toText(selectedTransferObject.value || selectedTransferObject.amount || selectedTransferObject.value_hex || selectedTransferObject.valueHex || selectedTransferObject.data_hex || selectedTransferObject.dataHex || selectedTransferObject.value_raw || selectedTransferObject.valueRaw || selectedTransferObject.raw_value || selectedTransferObject.rawValue || selectedTransferObject.raw_amount || selectedTransferObject.rawAmount || selectedTransferObject.amount_raw || selectedTransferObject.amountRaw || selectedTransferObject.token_value || selectedTransferObject.tokenValue || selectedTransferObject.quantity || selectedTransferObject.quant || selectedTransferObject.data),
          contractAddress: this.normalizeAddressKey(selectedTransferObject.contractAddress || selectedTransferObject.contract_address || selectedTransferObject.tokenAddress || selectedTransferObject.token_address || selectedTransferObject.address),
        };
        const tokenInfo = this.buildCaseBuddyTransferTokenInfo(selectedTransfer, raw, countContext);
        const amount = this.parseCaseBuddySelectedTransferAmount(selectedTransfer, tokenInfo);
        const token = this.toText(tokenInfo.symbol).toUpperCase();
        if (Number.isFinite(amount) && amount > 0 && !this.isCaseBuddyBlankTokenText(token)) {
          return {
            amount,
            token,
            fromAddress: selectedTransfer.fromAddress || '',
            toAddress: selectedTransfer.toAddress || raw.inferred_selected_target_address || raw.inferredSelectedTargetAddress || selectedAddress,
            contractAddress: tokenInfo.contractAddress || '',
            leftBoundsDisplay: '0.00',
            leftBoundsNumeric: '0',
            source: 'transaction signal selected transfer object',
          };
        }
      }

      const transfers = this.collectTransferEventRows([
        raw.selected_transfer,
        raw.selectedTransfer,
        raw.selected_transfer_log,
        raw.selectedTransferLog,
        raw.selected_output_transfer,
        raw.selectedOutputTransfer,
        raw.selected_token_transfer,
        raw.selectedTokenTransfer,
        raw.selected_output_details,
        raw.selectedOutputDetails,
        raw.selected_output_context,
        raw.selectedOutputContext,
        raw.selected_output,
        raw.selectedOutput,
        raw.matched_output,
        raw.matchedOutput,
        raw.matched_transfer,
        raw.matchedTransfer,
        raw.transfer,
        raw,
        ...payloadObjects
      ]);
      let candidates = transfers.filter((transfer) => selectedKey && transfer.toAddress === selectedKey);
      if (!candidates.length && transfers.length === 1) {
        candidates = transfers.slice();
      }
      if (!candidates.length) {
        return this.inferCaseBuddySelectedOutputFromSignalText(rawSignal, selectedAddress, countContext);
      }

      for (const transfer of candidates) {
        const tokenInfo = this.buildCaseBuddyTransferTokenInfo(transfer, raw, countContext);
        const amount = this.parseCaseBuddySelectedTransferAmount(transfer, tokenInfo);
        const token = this.toText(tokenInfo.symbol).toUpperCase();
        if (!Number.isFinite(amount) || amount <= 0 || this.isCaseBuddyBlankTokenText(token)) {
          continue;
        }
        return {
          amount,
          token,
          fromAddress: transfer.fromAddress || '',
          toAddress: transfer.toAddress || selectedAddress,
          contractAddress: tokenInfo.contractAddress || '',
          leftBoundsDisplay: '0.00',
          leftBoundsNumeric: '0',
          source: tokenInfo.contractAddress ? 'transaction signal Transfer log + token manager' : 'transaction signal Transfer log',
        };
      }
      return this.inferCaseBuddySelectedOutputFromSignalText(rawSignal, selectedAddress, countContext);
    },

    applyCaseBuddySelectedOutputInference(inputRows = [], entry = null, selectedAddress = '', inference = null) {
      if (!inference || !entry || !selectedAddress) return false;
      const record = this.getSelectedTargetRowRecord(entry, selectedAddress);
      const row = record?.row;
      if (!row) return false;

      const rowIndex = Number.isInteger(record.rowIndex) ? record.rowIndex : -1;
      const token = this.toText(inference.token).toUpperCase();
      const amount = Number(inference.amount);
      if (!Number.isFinite(amount) || amount <= 0 || this.isCaseBuddyBlankTokenText(token)) return false;

      let changed = false;
      if (inference.fromAddress && !this.toText(row[COL.FROM_ADDRESS])) {
        row[COL.FROM_ADDRESS] = inference.fromAddress;
        changed = true;
      }
      if (!Number.isFinite(this.parseNumberLike(row[COL.AMOUNT])) || this.parseNumberLike(row[COL.AMOUNT]) <= 0) {
        row[COL.AMOUNT] = this.formatAmount(amount);
        changed = true;
      }
      if (this.isCaseBuddyBlankTokenText(row[COL.TOKEN])) {
        row[COL.TOKEN] = token;
        changed = true;
      }
      if (!this.toText(row[COL.FUNDS_FOLLOWED]) || /\b(?:null|undefined|nan)\b/i.test(this.toText(row[COL.FUNDS_FOLLOWED]))) {
        row[COL.FUNDS_FOLLOWED] = this.formatFunds(amount, token);
        changed = true;
      }
      if (!this.toText(row[COL.LEFT_BOUNDS_DISPLAY]) || /\b(?:null|undefined|nan)\b/i.test(this.toText(row[COL.LEFT_BOUNDS_DISPLAY]))) {
        row[COL.LEFT_BOUNDS_DISPLAY] = inference.leftBoundsDisplay || '0.00';
        changed = true;
      }
      if (!this.toText(row[COL.LEFT_BOUNDS_NUMERIC]) || /\b(?:null|undefined|nan)\b/i.test(this.toText(row[COL.LEFT_BOUNDS_NUMERIC]))) {
        row[COL.LEFT_BOUNDS_NUMERIC] = inference.leftBoundsNumeric || '0';
        changed = true;
      }

      if (rowIndex >= 0 && Array.isArray(inputRows[rowIndex])) {
        inputRows[rowIndex] = row;
      }
      if (rowIndex >= 0 && Array.isArray(this.inputSnapshotRows?.[rowIndex])) {
        this.inputSnapshotRows[rowIndex] = this.cloneRowPreservingValues(row);
      }
      if (rowIndex >= 0 && Array.isArray(this.outputBaseRows?.[rowIndex])) {
        this.outputBaseRows[rowIndex] = this.cloneRowPreservingValues(row);
      }
      return changed;
    },

    async lookupCaseBuddySelectedOutputDetailsData(entry = null, selectedAddress = '', countContext = {}) {
      if (this.useMockBackend || !entry) return null;
      const selectedAddressRequired = this.traceDirection === 'reverse' || !!this.toText(selectedAddress);
      const allowSingleTransferTargetInference = this.traceDirection !== 'reverse' && !this.toText(selectedAddress);
      const body = {
        tx_hash: entry?.txHash || '',
        hash: entry?.txHash || '',
        address: selectedAddress,
        selected_address: selectedAddress,
        selected_target_address: selectedAddress,
        target_address: selectedAddress,
        selected_output_address: selectedAddress,
        selected_to_address: this.traceDirection === 'reverse' ? '' : selectedAddress,
        selected_recipient_address: this.traceDirection === 'reverse' ? '' : selectedAddress,
        selected_output_to_address: this.traceDirection === 'reverse' ? '' : selectedAddress,
        selected_from_address: this.traceDirection === 'reverse' ? selectedAddress : '',
        selected_sender_address: this.traceDirection === 'reverse' ? selectedAddress : '',
        selected_output_from_address: this.traceDirection === 'reverse' ? selectedAddress : '',
        direction: this.traceDirection,
        chain_id: countContext.chain_id || entry?.chainId || '',
        chain_name: countContext.chain_name || entry?.chainName || '',
        chain_label: countContext.chain_label || '',
        network_family: countContext.network_family || '',
        provider_hint: countContext.provider_hint || '',
        token_symbol: countContext.token_symbol || this.getSelectedTokenSymbol(entry, selectedAddress) || '',
        token_contract: countContext.token_contract || countContext.contract_address || '',
        contract_address: countContext.token_contract || countContext.contract_address || '',
        token_decimals: countContext.token_decimals ?? null,
        case_buddy_embedded: 1,
        case_buddy_modal_embedded: this.caseBuddyEmbedded ? 1 : 0,
        case_buddy_style_input: 1,
        case_buddy_root_output_hydration: 1,
        hydrate_sparse_root_entry: 1,
        sparse_starting_entry: 1,
        require_selected_output_details: 1,
        require_selected_output_amount: 1,
        require_selected_output_token: 1,
        selected_transfer_must_match_to_address: this.traceDirection === 'reverse' ? 0 : (selectedAddressRequired ? 1 : 0),
        allow_single_transfer_target_inference: allowSingleTransferTargetInference ? 1 : 0,
        infer_target_from_single_transfer: allowSingleTransferTargetInference ? 1 : 0,
        selected_output_address_optional: allowSingleTransferTargetInference ? 1 : 0,
        allow_missing_selected_address: allowSingleTransferTargetInference ? 1 : 0,
        include_transfer_logs: 1,
        include_token_transfers: 1,
        return_token_transfers: 1,
        include_raw_logs: 1,
        include_logs: 1,
        return_logs: 1,
        include_decoded_logs: 1,
        return_decoded_logs: 1,
        decode_logs: 1,
        decode_transfer_logs: 1,
        include_token_metadata: 1,
        return_token_metadata: 1,
        entry_context: this.serializeEntryContext(entry),
        count_context: countContext,
      };
      const actions = ['selected_output_lookup', 'transaction_selected_output', 'transaction_output_details', 'transaction_signal_check'];
      for (const action of actions) {
        try {
          const json = await this.postTool88({ ...body, action });
          const result = json?.result || json;
          if (result && typeof result === 'object' && result.status !== 'error' && !result.error) return result;
        } catch {
          // Older Tool 88 deployments may not expose the detail actions yet. Continue to the next option.
        }
      }
      return null;
    },

    async ensureCaseBuddySelectedOutputContext(inputRows = [], entry = null, selectedAddress = '') {
      this.caseBuddySelectedOutputInferenceBlock = null;
      if (!this.caseBuddySelectedHopNeedsOutputInference(entry, selectedAddress)) {
        return { required: false, changed: false };
      }

      const countContext = this.inferCountRequestContext(entry, selectedAddress);
      const record = this.getSelectedTargetRowRecord(entry, selectedAddress);
      const row = record?.row || [];
      const existingAmount = this.parseNumberLike(row?.[COL.AMOUNT]);
      const existingToken = this.toText(row?.[COL.TOKEN]).toUpperCase();
      const existingFunds = this.deriveFundsIn(entry, selectedAddress);
      const existingFundsAmount = Number(existingFunds?.amount);
      const existingFundsToken = this.toText(existingFunds?.token || existingToken).toUpperCase();
      if (Number.isFinite(existingAmount) && existingAmount > 0 && !this.isCaseBuddyBlankTokenText(existingToken)) {
        const fallbackInference = {
          amount: Number.isFinite(existingFundsAmount) && existingFundsAmount > 0 ? existingFundsAmount : existingAmount,
          token: this.isCaseBuddyBlankTokenText(existingFundsToken) ? existingToken : existingFundsToken,
          fromAddress: row?.[COL.FROM_ADDRESS] || '',
          toAddress: selectedAddress,
          leftBoundsDisplay: this.toText(row?.[COL.LEFT_BOUNDS_DISPLAY]) || '0.00',
          leftBoundsNumeric: this.toText(row?.[COL.LEFT_BOUNDS_NUMERIC]) || '0',
          source: 'existing Case Buddy amount/token with left bounds zero fallback',
        };
        const changed = this.applyCaseBuddySelectedOutputInference(inputRows, entry, selectedAddress, fallbackInference);
        return { required: true, changed, inference: fallbackInference };
      }
      this.statusMessage = 'Hydrating sparse starting row: looking up the selected transaction output amount/token before Tool 88 traces...';
      try {
        const rawSignal = await this.lookupTransactionSignalData(entry, selectedAddress);
        const detail = await this.lookupCaseBuddySelectedOutputDetails(entry, selectedAddress, countContext, rawSignal);
        const inference = detail?.inference || null;
        const detailSignal = detail?.signal || rawSignal;
        if (!inference) {
          const normalizedSignal = this.normalizeTransactionSignalResult(detailSignal || rawSignal, entry, selectedAddress);
          this.transactionSignalResult = normalizedSignal;
          const signalSummary = normalizedSignal?.detailText || normalizedSignal?.evidenceSummary || normalizedSignal?.recommendation || '';
          const message = `Tool 88 could not hydrate the sparse starting row with the selected output amount/token. It paused instead of tracing with an unknown/native asset context or creating incomplete raw rows.${signalSummary ? ` Signal: ${signalSummary}` : ''}`;
          this.caseBuddySelectedOutputInferenceBlock = {
            reason: 'selected-output-missing',
            message,
            selectedAddress: this.toText(selectedAddress),
            selectedEntryKey: entry?.key || '',
            signalSummary,
          };
          this.pushUniqueWarnings([message]);
          return { required: true, changed: false, blocked: true, rawSignal, detailSignal };
        }
        const changed = this.applyCaseBuddySelectedOutputInference(inputRows, entry, selectedAddress, inference);
        if (changed) {
          this.pushUniqueWarnings([`Tool 88 hydrated the sparse starting row with selected output ${this.formatFunds(inference.amount, inference.token)} from ${inference.source || 'transaction signal data'} and used left bounds zero before count/trace checks.`]);
          this.$refs.inputSpreadsheet?.loadData?.(this.ensureMinimumRows(inputRows));
        }
        return { required: true, changed, rawSignal, detailSignal, inference };
      } catch (error) {
        const message = `Tool 88 sparse-row hydration failed: ${error?.message || 'unknown signal inference error'}. Tool 88 paused instead of creating incomplete raw rows.`;
        this.caseBuddySelectedOutputInferenceBlock = {
          reason: 'selected-output-inference-error',
          message,
          selectedAddress: this.toText(selectedAddress),
          selectedEntryKey: entry?.key || '',
        };
        this.pushUniqueWarnings([message]);
        return { required: true, changed: false, blocked: true, error: error?.message || 'unknown signal inference error' };
      }
    },

    isEvmAddress(address) {
      return /^0x[a-fA-F0-9]{40}$/.test(this.toText(address));
    },

    isTronAddress(address) {
      return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(this.toText(address));
    },

    isBtcAddress(address) {
      return /^(bc1[a-zA-HJ-NP-Z0-9]{20,90}|[13][a-km-zA-HJ-NP-Z1-9]{25,39})$/.test(this.toText(address));
    },

    normalizeChainIdForCount(chainId) {
      const text = this.toText(chainId).toLowerCase();
      if (!text) {
        return "";
      }
      if (/^0x[0-9a-f]+$/.test(text)) {
        return String(parseInt(text.slice(2), 16));
      }
      return text;
    },

    getEvmCountChainMeta(chainId, chainName) {
      const normalizedId = this.normalizeChainIdForCount(chainId);
      const normalizedName = this.toText(chainName).toLowerCase();
      const byId = {
        "1": { chainId: "1", chainName: "Ethereum", chainLabel: "ETHEREUM", nativeSymbol: "ETH", moralisChain: "0x1", providerHint: "etherscan" },
        "56": { chainId: "56", chainName: "BSC", chainLabel: "BSC", nativeSymbol: "BNB", moralisChain: "0x38", providerHint: "moralis" },
        "137": { chainId: "137", chainName: "Polygon", chainLabel: "POLYGON", nativeSymbol: "MATIC", moralisChain: "0x89", providerHint: "moralis" },
        "42161": { chainId: "42161", chainName: "Arbitrum", chainLabel: "ARBITRUM", nativeSymbol: "ETH", moralisChain: "0xa4b1", providerHint: "moralis" },
        "10": { chainId: "10", chainName: "Optimism", chainLabel: "OPTIMISM", nativeSymbol: "ETH", moralisChain: "0xa", providerHint: "moralis" },
        "8453": { chainId: "8453", chainName: "Base", chainLabel: "BASE", nativeSymbol: "ETH", moralisChain: "0x2105", providerHint: "moralis" },
        "250": { chainId: "250", chainName: "Fantom", chainLabel: "FANTOM", nativeSymbol: "FTM", moralisChain: "0xfa", providerHint: "moralis" },
        "59144": { chainId: "59144", chainName: "Linea", chainLabel: "LINEA", nativeSymbol: "ETH", moralisChain: "0xe708", providerHint: "moralis" },
        "43114": { chainId: "43114", chainName: "Avalanche", chainLabel: "AVAX", nativeSymbol: "AVAX", moralisChain: "0xa86a", providerHint: "moralis" },
      };

      if (byId[normalizedId]) {
        return byId[normalizedId];
      }

      const aliases = [
        [/ethereum|\beth\b|mainnet/, "1"],
        [/binance|bsc|bnb/, "56"],
        [/polygon|matic/, "137"],
        [/arbitrum/, "42161"],
        [/optimism|op mainnet/, "10"],
        [/base/, "8453"],
        [/fantom|ftm/, "250"],
        [/linea/, "59144"],
        [/avalanche|avax/, "43114"],
      ];

      for (const [pattern, mappedId] of aliases) {
        if (pattern.test(normalizedName)) {
          return byId[mappedId];
        }
      }

      if (/^\d+$/.test(normalizedId)) {
        return {
          chainId: normalizedId,
          chainName: this.toText(chainName) || `EVM ${normalizedId}`,
          chainLabel: this.toText(chainName) || `EVM ${normalizedId}`,
          nativeSymbol: "",
          moralisChain: "",
          providerHint: "unsupported_evm",
        };
      }

      return null;
    },

    isNativeTokenForCount(tokenSymbol, chainMeta) {
      const token = this.toText(tokenSymbol).toUpperCase().replace(/[^A-Z0-9.]/g, "");
      if (!token) {
        return true;
      }

      const native = this.toText(chainMeta?.nativeSymbol).toUpperCase();
      if (native && token === native) {
        return true;
      }
      if (native === "MATIC" && token === "POL") {
        return true;
      }
      return false;
    },

    inferCountRequestContext(entry, selectedAddress) {
      const selectedRow = this.getSelectedTargetRow(entry, selectedAddress) || [];
      const fundsIn = this.deriveFundsIn(entry, selectedAddress);
      const tokenSymbol = this.toText(fundsIn.token || selectedRow?.[COL.TOKEN]).toUpperCase();
      const explicitTokenContract = this.toText(selectedRow?.[COL.ATTRIBUTION] || "").match(/^T[1-9A-HJ-NP-Za-km-z]{33}$|^0x[a-fA-F0-9]{40}$/) ? this.toText(selectedRow?.[COL.ATTRIBUTION]) : "";
      const rawChainId = this.toText(entry?.chainId || selectedRow?.[COL.CHAIN_ID]);
      const rawChainName = this.toText(entry?.chainName || selectedRow?.[COL.CHAIN_NAME]);
      const chainNameLower = rawChainName.toLowerCase();
      const normalizedChainId = this.normalizeChainIdForCount(rawChainId);
      const address = this.toText(selectedAddress);

      if (/tron|trc/.test(chainNameLower) || ["tron", "trx"].includes(normalizedChainId) || this.isTronAddress(address)) {
        const wantsNativeTrx = tokenSymbol === "TRX";
        const tokenResolution = wantsNativeTrx ? null : this.resolveTokenForContext(
          tokenSymbol,
          rawChainId || "tron",
          rawChainName || "Tron",
          "tron",
          explicitTokenContract
        );
        const tokenContract = wantsNativeTrx ? "" : (tokenResolution?.contractAddress || explicitTokenContract);
        return {
          network_family: "tron",
          provider_hint: "tronscan",
          count_kind: wantsNativeTrx ? "native_transaction" : "token_transfer",
          token_symbol: tokenSymbol || (wantsNativeTrx ? "TRX" : ""),
          token_contract: tokenContract,
          contract_address: tokenContract,
          token_decimals: tokenResolution?.decimals ?? null,
          token_registry_status: tokenResolution?.status || "",
          chain_id: rawChainId || "tron",
          chain_name: rawChainName || "Tron",
          chain_label: "TRON",
          native_symbol: "TRX",
        };
      }

      if (/bitcoin|\bbtc\b/.test(chainNameLower) || ["btc", "bitcoin"].includes(normalizedChainId) || tokenSymbol === "BTC" || this.isBtcAddress(address)) {
        return {
          network_family: "btc",
          provider_hint: "mempool.space",
          count_kind: "native_transaction",
          token_symbol: tokenSymbol || "BTC",
          chain_id: rawChainId || "btc",
          chain_name: rawChainName || "Bitcoin",
          chain_label: "BITCOIN",
          native_symbol: "BTC",
        };
      }

      if (/solana|\bsol\b/.test(chainNameLower) || normalizedChainId === "sol") {
        return {
          network_family: "sol",
          provider_hint: "unsupported",
          count_kind: "unsupported_count",
          token_symbol: tokenSymbol,
          chain_id: rawChainId || "sol",
          chain_name: rawChainName || "Solana",
          chain_label: "SOLANA",
          native_symbol: "SOL",
        };
      }

      if (/xrp|ripple/.test(chainNameLower) || normalizedChainId === "xrp") {
        return {
          network_family: "xrp",
          provider_hint: "unsupported",
          count_kind: "unsupported_count",
          token_symbol: tokenSymbol,
          chain_id: rawChainId || "xrp",
          chain_name: rawChainName || "XRP",
          chain_label: "XRP",
          native_symbol: "XRP",
        };
      }

      const evmMeta = this.getEvmCountChainMeta(rawChainId, rawChainName) || (this.toText(address).startsWith("0x")
        ? { chainId: "1", chainName: "Ethereum", chainLabel: "ETHEREUM", nativeSymbol: "ETH", moralisChain: "0x1", providerHint: "etherscan" }
        : null);

      if (evmMeta) {
        const isNative = this.isNativeTokenForCount(tokenSymbol, evmMeta);
        const tokenResolution = isNative ? null : this.resolveTokenForContext(
          tokenSymbol,
          evmMeta.chainId,
          evmMeta.chainName,
          "evm",
          explicitTokenContract
        );
        const tokenContract = isNative ? "" : (tokenResolution?.contractAddress || explicitTokenContract);
        return {
          network_family: "evm",
          provider_hint: evmMeta.providerHint,
          count_kind: isNative ? "native_transaction" : "token_transfer",
          token_symbol: tokenSymbol,
          token_contract: tokenContract,
          contract_address: tokenContract,
          token_decimals: tokenResolution?.decimals ?? null,
          token_registry_status: tokenResolution?.status || "",
          chain_id: evmMeta.chainId,
          chain_name: evmMeta.chainName,
          chain_label: evmMeta.chainLabel,
          native_symbol: evmMeta.nativeSymbol,
          moralis_chain: evmMeta.moralisChain,
        };
      }

      return {
        network_family: "unsupported",
        provider_hint: "unsupported",
        count_kind: "unsupported_count",
        token_symbol: tokenSymbol,
        chain_id: rawChainId,
        chain_name: rawChainName,
        chain_label: rawChainName || "Unsupported / unknown",
        native_symbol: "",
      };
    },

    isSamplePlaceholderAddress(address) {
      return /research|busy|blocked|dormant|noexit|eventlog|sender|exchange/i.test(this.toText(address));
    },

    getSelectedTokenSymbol(entry, selectedAddress) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const targetColumnIndex = this.getTargetColumnIndex();

      if (entry && selectedKey) {
        for (const { row } of entry.rows) {
          const rowAddressKey = this.normalizeAddressKey(row?.[targetColumnIndex]);
          if (rowAddressKey === selectedKey) {
            const tokenFromRow = this.toText(row?.[COL.TOKEN]);
            const tokenFromFunds = this.parseTokenLike(row?.[COL.FUNDS_FOLLOWED]);
            if (tokenFromRow || tokenFromFunds) {
              return tokenFromRow || tokenFromFunds;
            }
          }
        }
      }

      const funds = entry ? this.deriveFundsIn(entry, selectedAddress) : null;
      return this.toText(funds?.token);
    },

    getAttributionValuesForEntry(entry, selectedAddress) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const targetColumnIndex = this.getTargetColumnIndex();
      const attributionColumnIndex = this.getAttributionColumnIndex();
      const values = new Set();

      if (!entry || !selectedKey) {
        return [];
      }

      entry.rows.forEach(({ row }) => {
        const rowAddressKey = this.normalizeAddressKey(row?.[targetColumnIndex]);
        if (rowAddressKey !== selectedKey) {
          return;
        }

        const attribution = this.toText(row?.[attributionColumnIndex]);
        if (attribution) {
          values.add(attribution);
        }
      });

      return [...values];
    },

    getSelectedAttributionValues() {
      return this.getAttributionValuesForEntry(this.selectedEntry, this.selectedTargetAddress);
    },

    collectEntrySearchText(entry) {
      if (!entry) {
        return "";
      }
      return entry.rows
        .map(({ row }) => row.map((cell) => this.toText(cell)).filter(Boolean).join(" "))
        .join(" ")
        .toLowerCase();
    },

    stripNegativeBtcMemoEvidenceText(value = "") {
      const text = this.toText(value).toLowerCase();
      if (!text) return "";
      const markerPattern = /op[_\s-]?return|\bmemo\b/i;
      const negativePattern = new RegExp(
        [
          "(?:\\bno\\b|\\bnot\\b|\\bwithout\\b|\\babsent\\b|\\bmissing\\b|\\bnone\\b).{0,96}(?:op[_\\s-]?return|\\bmemo\\b)",
          "(?:op[_\\s-]?return|\\bmemo\\b).{0,96}(?:\\bnot\\s+(?:found|detected|present|observed|included|contained)\\b|\\babsent\\b|\\bmissing\\b|\\bnone\\b)",
          "(?:does|did|do|could|can|was|were|is|are)\\s+not.{0,96}(?:op[_\\s-]?return|\\bmemo\\b)",
          "(?:failed|unable)\\s+to.{0,64}(?:find|detect|observe|identify).{0,64}(?:op[_\\s-]?return|\\bmemo\\b)",
        ].join("|"),
        "i"
      );
      return text
        .split(/[.!?;]\s+|[\r\n]+/u)
        .filter((fragment) => !markerPattern.test(fragment) || !negativePattern.test(fragment))
        .join(" ")
        .trim();
    },

    hasPositiveBtcMemoEvidence(value = "") {
      const rawText = this.toText(value).toLowerCase();
      if (!rawText) return false;
      if (/\bbtc_(?:op_return_memo|op_return_present|memo_present)\b/i.test(rawText)) return true;
      const text = this.stripNegativeBtcMemoEvidenceText(rawText);
      if (!text || !/(?:op[_\s-]?return|\bmemo\b)/i.test(text)) return false;
      return /(?:tool\s*88\s+signal\s+check:\s*btc\s+(?:op[_\s-]?return|memo)(?:\s*\/\s*(?:op[_\s-]?return|memo))?\s+(?:found|detected|present)|(?:op[_\s-]?return|\bmemo\b).{0,80}\b(?:found|detected|present|observed|included|contained)\b|\b(?:found|detected|present|observed|included|contained)\b.{0,80}(?:op[_\s-]?return|\bmemo\b)|\b(?:has|contains?|includes?)\b.{0,80}(?:op[_\s-]?return|\bmemo\b)|(?:op[_\s-]?return|\bmemo\b)(?:\s+(?:payload|text|value|message|data))?\s*[:=]\s*\S)/i.test(text);
    },

    buildTransactionSignalCheck() {
      const signal = this.transactionSignalResult;
      if (signal) {
        return {
          key: "chain-specific",
          title: signal.checkTitle || "Transaction signal check",
          status: signal.checkStatus || signal.adviceStatus || "neutral",
          statusLabel: signal.statusLabel || "Review",
          summary: signal.summary || signal.recommendation || "Tool 88 checked transaction-level signals for this entry.",
          detail: signal.detailText || signal.evidenceSummary || "Review the signal details before deciding whether to trace through.",
        };
      }

      const entry = this.selectedEntry;
      const selectedAddress = this.toText(this.selectedTargetAddress);
      const entryText = this.collectEntrySearchText(entry);
      const countContext = this.inferCountRequestContext(entry, selectedAddress);
      const family = this.toText(countContext.network_family).toLowerCase();

      if (family === "btc") {
        const hasMemo = this.hasPositiveBtcMemoEvidence(entryText);
        return {
          key: "chain-specific",
          title: "BTC memo / OP_RETURN check",
          status: hasMemo ? "warn" : "neutral",
          statusLabel: hasMemo ? "Review" : "Pending provider check",
          summary: hasMemo
            ? "Visible entry text mentions an OP_RETURN, memo, message, or similar marker."
            : "No visible OP_RETURN/memo text was found in the pasted entry.",
          detail: "Tool 88 now has a server-side BTC transaction-detail route for OP_RETURN/memo checks; rerun the count/check pass if this was not returned.",
        };
      }

      if (family === "tron") {
        return {
          key: "chain-specific",
          title: "Tron/TRC20 event-log check",
          status: "neutral",
          statusLabel: "Pending provider check",
          summary: "Tool 88 identified this as a Tron/TRC20-style route.",
          detail: "Tool 88 can query Tronscan transaction-info and review TVM logs for simple-transfer versus complex contract signals.",
        };
      }

      if (["sol", "xrp", "unsupported"].includes(family)) {
        return {
          key: "chain-specific",
          title: "Chain support check",
          status: "neutral",
          statusLabel: "Signal unavailable",
          summary: `Tool 88 does not have a transaction-signal route for ${countContext.chain_label || countContext.chain_name || "this network"} yet.`,
          detail: "The user can continue with attribution/review and decide whether to trace.",
        };
      }

      const hasDdfMarker = /0xddf/i.test(entryText);
      return {
        key: "chain-specific",
        title: "EVM event-log check",
        status: hasDdfMarker ? "warn" : "neutral",
        statusLabel: hasDdfMarker ? "Review visible marker" : "Pending provider check",
        summary: hasDdfMarker
          ? "Visible entry text contains a 0xddf-style marker that should be reviewed before tracing."
          : "No visible 0xddf-style marker was found in the pasted entry.",
        detail: "Tool 88 now has a server-side EVM receipt route for checking whether the transaction has only the expected ERC20 Transfer event or additional contract logs.",
      };
    },

    buildChainSpecificCheck() {
      return this.buildTransactionSignalCheck();
    },

    buildBaseMeta(rows, entries, candidates, selectedAddress) {
      return {
        rowsLoaded: rows.filter((row) => this.rowHasAnyData(row)).length,
        headerDetected: this.headerDetected,
        entriesFound: entries.length,
        candidateCount: candidates.length,
        selectedAddress: selectedAddress || "",
        exitCount: 0,
        dormantAmountText: "",
        jobId: this.backendJobMeta?.job_id || "",
      };
    },


    parseInputRowsForFlowCompletion() {
      const rawRows = this.$refs.inputSpreadsheet?.getData?.() || [];
      const rows = this.trimTrailingBlankRows(rawRows);
      const headerDetected = rows.length ? this.isLikelyHeaderRow(rows[0]) : false;
      const dataStartIndex = headerDetected ? 1 : 0;
      const { entries, warnings } = this.collectEntries(rows, dataStartIndex);
      return { rows, headerDetected, dataStartIndex, entries, warnings };
    },

    estimateFlowEntryDepth(entry) {
      const path = this.toText(entry?.pathLabel);
      if (!path) return 0;
      const suffixMatches = path.match(/[a-z]+|\d+/gi) || [];
      return Math.max(0, suffixMatches.length - 1);
    },

    getEntryContinuesText(entry) {
      if (!entry) return "";
      const values = [];
      entry.rows.forEach(({ row }) => {
        values.push(this.toText(row?.[COL.CONTINUES_AS]));
      });
      return values.filter(Boolean).join("\n");
    },

    entryHasExplicitOutgoingContinuation(entry) {
      const continuesText = this.getEntryContinuesText(entry).toLowerCase();
      const entryText = this.collectEntrySearchText(entry).toLowerCase();
      const combined = `${continuesText}\n${entryText}`;
      return /continues\s+as|branches\s+as/.test(combined);
    },

    entryLooksLikeFlowEnding(entry) {
      if (!entry) return false;
      if (this.entryHasExplicitOutgoingContinuation(entry)) return false;
      const continuesText = this.getEntryContinuesText(entry).toLowerCase();
      if (!continuesText) return true;
      return /path\s+ends|ends\s+at|dormant|brick\s*wall/.test(continuesText);
    },

    entryHasDormantMarker(entry) {
      // The legacy dormant language describes the forward receiving side. In
      // Reverse mode the unresolved endpoint is the sender/source side, so a
      // forward dormant note must not end the ancestry trace. Legitimate
      // reverse endings are recorded with a direction-aware flow marker.
      if (this.traceDirection === "reverse") return false;
      const text = this.collectEntrySearchText(entry).toLowerCase();
      return /portion\s+of\s+the\s+funds\s+remains|remains\s+at\s+the\s+receiving\s+address|dormant|brick\s*wall|left-bound amount|funds\s+located/.test(text);
    },

    entryHasMemoMarker(entry) {
      // OP_RETURN/memo evidence can explain a forward destination, but it does
      // not identify where the sender obtained the funds in a reverse trace.
      if (this.traceDirection === "reverse") return false;
      return this.hasPositiveBtcMemoEvidence(this.collectEntrySearchText(entry));
    },

    extractFlowMeaningfulIdentity(entry, selectedAddress = "") {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const targetColumnIndex = this.getTargetColumnIndex();
      const attributionColumnIndex = this.getAttributionColumnIndex();
      const labels = [];
      const entryRows = Array.isArray(entry?.rows) ? entry.rows : [];
      const matchingRows = selectedKey
        ? entryRows.filter(({ row }) => this.normalizeAddressKey(row?.[targetColumnIndex]) === selectedKey)
        : entryRows;
      (matchingRows.length ? matchingRows : entryRows).forEach(({ row }) => {
        labels.push(this.toText(row?.[attributionColumnIndex]));
        const noteText = `${this.toText(row?.[COL.SYSTEM_NOTES])}\n${this.toText(row?.[COL.TOOL88_NOTES])}`;
        const attributionMatch = noteText.match(/Tool\s*88\s+attribution:\s*([^\n.]+)/i);
        if (attributionMatch) labels.push(attributionMatch[1]);
      });

      const bridgeReceiveContext = this.isSelectedEntryBridgeReceiveContext(selectedAddress, entry);
      for (const label of labels) {
        const clean = this.cleanAttributionLabel(label).trim();
        if (!clean) continue;
        const lower = clean.toLowerCase();
        if (lower === "unknown" || lower.includes("no server/vendor label")) continue;
        if (this.traceDirection === "reverse" && this.isDepositAttributionLabel(clean)) continue;
        if (this.isFlowTraceThroughEntityLabel(clean)) continue;
        if (this.isTraceThroughFriendlyHandleAttributionLabel(clean)) continue;
        if (selectedKey && this.normalizeAddressKey(clean) === selectedKey) continue;
        if (this.isValidAddressLikeLabel(clean)) continue;
        if (this.isFlowTraceThroughEntity(clean)) continue;
        if (bridgeReceiveContext && this.isBridgeServiceAttributionLabel(clean)) continue;
        if (/contract\s*like|address\s+has\s+features|raw\s+address/i.test(clean)) continue;
        return clean;
      }
      return "";
    },

    getFlowTraceThroughEntitySet() {
      const values = [];
      splitTraceThroughEntityList(this.flowCompletionTraceThroughEntitiesText).forEach((value) => {
        const clean = this.cleanAttributionLabel(value).toLowerCase();
        if (clean) values.push(clean);
      });
      this.effectiveTraceThroughEntityRegistry.forEach((entity) => {
        [entity.canonicalName, ...(entity.aliases || [])].forEach((value) => {
          const clean = this.cleanAttributionLabel(value).toLowerCase();
          if (clean) values.push(clean);
        });
      });
      return new Set(values);
    },

    isFlowTraceThroughEntity(label) {
      const clean = this.cleanAttributionLabel(label).toLowerCase();
      if (!clean) return false;
      if (this.getFlowTraceThroughEntitySet().has(clean)) return true;
      return Boolean(this.findTraceThroughEntityByLabel(label) || this.findTraceThroughEntityByAddress(label));
    },

    isFlowTraceThroughEntityLabel(label) {
      return this.isFlowTraceThroughEntity(label);
    },

    effectiveFlowLowValueRuleEnabled() {
      if (typeof this.caseBuddyLowValueRuleEnabledOverride === 'boolean') {
        return this.caseBuddyLowValueRuleEnabledOverride;
      }
      return this.flowCompletionValueTooLowEnabled !== false;
    },

    flowLowValueRuleLabel() {
      const enabled = this.effectiveFlowLowValueRuleEnabled();
      if (typeof this.caseBuddyLowValueRuleEnabledOverride === 'boolean') {
        const policy = this.caseBuddyLowValueRulePolicy || {};
        const suffixText = this.toText(policy.message || policy.reason);
        const suffix = suffixText ? ` — ${suffixText}` : ' — Case Buddy integration override';
        return `${enabled ? 'On' : 'Off'}${suffix}`;
      }
      return enabled ? 'On' : 'Off';
    },

    getFlowLowValueThreshold(token) {
      const normalized = this.toText(token).trim().toUpperCase().replace(/[^A-Z0-9.-]/g, "");
      const policy = this.caseBuddyLowValueRulePolicy || this.caseBuddyLowValuePolicyOverride || {};
      const policyThresholds = policy && typeof policy === "object" && !Array.isArray(policy) && policy.thresholds && typeof policy.thresholds === "object"
        ? policy.thresholds
        : {};
      const pickThreshold = (bucket, fallback) => {
        const override = Number(policyThresholds[bucket] ?? policyThresholds[bucket.toUpperCase()]);
        return Number.isFinite(override) && override >= 0 ? override : Number(fallback);
      };
      if (["BTC", "WBTC", "BTC.B", "BTCB"].includes(normalized)) return pickThreshold("btc", this.lowValueThresholdBtc);
      if (["ETH", "WETH", "BNB", "WBNB", "MATIC", "WMATIC", "AVAX", "WAVAX", "TRX"].includes(normalized)) return pickThreshold("native", this.lowValueThresholdNative);
      if (["USDT", "USDC", "DAI", "USDD", "BUSD", "TUSD", "USDP", "PYUSD", "AUSDT", "BSCUSD", "BSC-USD"].includes(normalized)) return pickThreshold("stable", this.lowValueThresholdStable);
      return pickThreshold("other", this.lowValueThresholdOther);
    },

    getFlowLowValueAssessment(entry, selectedAddress) {
      const funds = this.deriveFundsIn(entry, selectedAddress);
      const amount = Number(funds?.amount);
      const token = funds?.token || this.getSelectedTokenSymbol(entry, selectedAddress) || this.toText(entry?.rows?.[0]?.row?.[COL.TOKEN]);
      const threshold = this.getFlowLowValueThreshold(token);
      const enabled = this.effectiveFlowLowValueRuleEnabled();
      const isLow = enabled && Number.isFinite(amount) && Number.isFinite(threshold) && threshold > 0 && amount > 0 && amount < threshold;
      return { enabled, isLow, amount, token, threshold };
    },

    evaluateFlowCompletionEntry(entry) {
      const candidates = this.collectTargetCandidates(entry);
      const targetAddress = candidates[0]?.address || "";
      const depth = this.estimateFlowEntryDepth(entry);
      const label = this.formatEntryOption(entry);
      const base = {
        entry,
        entryKey: entry?.key || "",
        rowNumber: (entry?.startRowIndex ?? 0) + 1,
        txHash: entry?.txHash || "",
        label,
        targetAddress,
        targetStatus: targetAddress ? "" : "No target address",
        depth,
        complete: false,
        reason: "",
        autoMarkable: false,
        lowValue: null,
      };

      if (depth > this.normalizedFlowCompletionMaxDepth) {
        return { ...base, complete: true, depthSkipped: true, reason: `Skipped: depth ${depth} exceeds max ${this.normalizedFlowCompletionMaxDepth}.` };
      }
      if (!this.entryLooksLikeFlowEnding(entry)) {
        return { ...base, complete: true, reason: "Not a path ending; it already continues." };
      }
      if (this.entryHasFlowCompletionMarker(entry)) {
        return { ...base, complete: true, reason: "Already reviewed by Tool 88 flow helper." };
      }
      if (this.entryHasMemoMarker(entry)) {
        return { ...base, complete: true, reason: "Memo / OP_RETURN marker tells a complete story." };
      }
      if (this.entryHasDormantMarker(entry)) {
        return { ...base, complete: true, reason: "Dormant / brick-wall marker found with no outgoing continuation." };
      }

      const identity = this.extractFlowMeaningfulIdentity(entry, targetAddress);
      if (identity) {
        return { ...base, complete: true, reason: `Meaningful attribution found: ${identity}.` };
      }

      const lowValue = this.getFlowLowValueAssessment(entry, targetAddress);
      if (lowValue.isLow) {
        return {
          ...base,
          complete: false,
          autoMarkable: true,
          lowValue,
          reason: `Below low-value threshold: ${this.formatFunds(lowValue.amount, lowValue.token)} < ${this.formatFunds(lowValue.threshold, lowValue.token)}.`,
        };
      }

      if (!targetAddress) {
        return { ...base, complete: false, reason: "Unresolved ending: no target address could be selected." };
      }

      return { ...base, complete: false, reason: "Unresolved ending: no dormant marker or meaningful non-address attribution." };
    },

    buildFlowCompletionReport(result) {
      const lines = [
        "Tool 88 Flow Completion Review",
        "----------------------------------------",
        `Entries reviewed: ${result.totalEntries}`,
        `Path endings found: ${result.endingCount}`,
        `Complete endings: ${result.completeCount}`,
        `Unresolved endings: ${result.incompleteCount}`,
        `Skipped by depth: ${result.depthSkippedCount}`,
        `Max depth: ${this.normalizedFlowCompletionMaxDepth}`,
        `Low-value rule: ${this.flowLowValueRuleLabel()}`,
        `Trace-through entity registry: ${this.traceThroughEntitySummary}. Quick extra labels: ${this.flowCompletionTraceThroughEntitiesText || "None"}`,
      ];

      if (result.incomplete.length) {
        lines.push("", "Next unresolved ending:");
        lines.push(`- ${result.incomplete[0].label}`);
        lines.push(`- ${result.incomplete[0].reason}`);
      }

      if (result.endings.length) {
        lines.push("", "Ending details:");
        result.endings.forEach((ending) => {
          lines.push(`- Row ${ending.rowNumber}: ${ending.complete ? "Complete" : "Unresolved"} — ${ending.reason}`);
        });
      }
      return lines.join("\n");
    },

    evaluateFlowCompletion(parsed) {
      const endings = [];
      const complete = [];
      const incomplete = [];
      let depthSkippedCount = 0;

      parsed.entries.forEach((entry) => {
        if (!this.entryLooksLikeFlowEnding(entry)) return;
        const evaluated = this.evaluateFlowCompletionEntry(entry);
        endings.push(evaluated);
        if (evaluated.depthSkipped) depthSkippedCount += 1;
        if (evaluated.complete) complete.push(evaluated);
        else incomplete.push(evaluated);
      });

      const result = {
        success: true,
        done: incomplete.length === 0,
        summary: incomplete.length === 0
          ? `Flow complete. ${complete.length} ending${complete.length === 1 ? "" : "s"} resolved.`
          : `Flow not complete. ${incomplete.length} unresolved ending${incomplete.length === 1 ? "" : "s"} found.`,
        totalEntries: parsed.entries.length,
        endingCount: endings.length,
        completeCount: complete.length,
        incompleteCount: incomplete.length,
        depthSkippedCount,
        endings,
        complete,
        incomplete,
      };
      result.report = this.buildFlowCompletionReport(result);
      return result;
    },

    reviewFlowCompletionStatus() {
      const parsed = this.parseInputRowsForFlowCompletion();
      if (!parsed.rows.length || !parsed.rows.some((row) => this.rowHasAnyData(row))) {
        this.flowCompletionScanResult = null;
        this.flowCompletionMessage = "No input data was found to review.";
        return;
      }

      const result = this.evaluateFlowCompletion(parsed);
      this.flowCompletionScanResult = result;
      this.flowCompletionMessage = result.summary;
      this.reportText = result.report;
    },

    applyFlowCompletionMarkers() {
      const parsed = this.parseInputRowsForFlowCompletion();
      if (!parsed.rows.length || !parsed.rows.some((row) => this.rowHasAnyData(row))) {
        this.flowCompletionScanResult = null;
        this.flowCompletionMessage = "No input data was found to review.";
        return;
      }

      const initialResult = this.evaluateFlowCompletion(parsed);
      const outputRows = parsed.rows.map((row) => this.cloneRowPreservingValues(row));
      let markedComplete = 0;
      let lowValueEnded = 0;

      initialResult.endings.forEach((ending) => {
        const rowIndex = ending?.entry?.startRowIndex;
        if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex >= outputRows.length) {
          return;
        }

        const row = outputRows[rowIndex] || this.makeBlankRow();
        if (ending.complete) {
          if (!ending.depthSkipped && !this.entryHasFlowCompletionMarker(ending.entry)) {
            this.appendFlowCompletionMarkerToRow(row, "complete", ending.reason || "resolved ending");
            markedComplete += 1;
          }
        } else if (ending.autoMarkable && ending.lowValue) {
          const amountText = this.formatFunds(ending.lowValue.amount, ending.lowValue.token);
          const thresholdText = this.formatFunds(ending.lowValue.threshold, ending.lowValue.token);
          const reason = `Low-value threshold rule: ${amountText} is below ${thresholdText}.`;
          row[COL.CONTINUES_AS] = "Path ends.";
          this.appendTool88NoteToRow(row, `Tool 88: Path ends by low-value flow rule. ${reason}`);
          this.appendFlowCompletionMarkerToRow(row, "low-value-ended", reason);
          const numericAmount = this.parseNumberLike(amountText);
          if (Number.isFinite(numericAmount)) {
            const numericText = this.formatAmount(numericAmount);
            row[COL.LEFT_BOUNDS_DISPLAY] = `${numericText}${ending.lowValue.token ? ` ${ending.lowValue.token}` : ""}\nLow-value Tool 88 flow ending.`;
            row[COL.LEFT_BOUNDS_NUMERIC] = numericText;
          }
          row[COL.HOP_LOGIC] = this.hopLogicType;
          lowValueEnded += 1;
        }
        outputRows[rowIndex] = row;
      });

      const recollected = this.collectEntries(outputRows, parsed.dataStartIndex);
      const postParsed = {
        rows: outputRows,
        headerDetected: parsed.headerDetected,
        dataStartIndex: parsed.dataStartIndex,
        entries: recollected.entries,
        warnings: recollected.warnings,
      };
      const postResult = this.evaluateFlowCompletion(postParsed);
      this.loadOutputRows(outputRows);
      const markerSummary = `Marked ${markedComplete} resolved ending${markedComplete === 1 ? "" : "s"} and ${lowValueEnded} low-value ending${lowValueEnded === 1 ? "" : "s"} in the output. Use Output as New Input to continue.`;
      this.flowAbsorbResult = { markedComplete, lowValueEnded, rows: outputRows, summary: markerSummary };
      this.simpleFlowActionReady = lowValueEnded > 0;
      this.flowCompletionScanResult = postResult;
      this.flowCompletionMessage = markerSummary;
      this.reportText = `${initialResult.report}\n\nFlow marker pass:\nResolved endings marked in Column W: ${markedComplete}\nLow-value endings finalized: ${lowValueEnded}\n\nAfter marker pass:\n${postResult.summary}`;
    },

    shouldIgnoreManualFlowSelectionDepthLimit(options = {}) {
      if (options.ignoreManualDepthLimit === true || options.caseBuddyAutomation === true || options.directAutomation === true) {
        return true;
      }
      if (this.caseBuddyIntegrationRunState?.active === true) return true;
      if (this.directAutomationRunState?.active === true) return true;
      return false;
    },

    clearStaleFlowSelectionForGuardrail() {
      this.isRefreshingSelections = true;
      this.selectedEntryKey = "";
      this.targetCandidates = [];
      this.selectedTargetAddress = "";
      this.isRefreshingSelections = false;
      this.countLookupResult = null;
      this.attributionLookupResult = null;
      this.transactionSignalResult = null;
      this.traceResult = null;
      this.dormantResult = null;
      this.traceTransactionPayload = null;
      this.pathEndingResult = null;
      this.simpleFlowActionReady = false;
      this.simpleRecommendationApplied = false;
      this.lastRunMeta = null;
      this.caseBuddySelectedOutputInferenceBlock = null;
    },

    buildFlowSelectionResult(options = {}) {
      const result = {
        atUtc: new Date().toISOString(),
        source: this.toText(options.source || "manual-select"),
        selected: options.selected === true,
        done: options.done === true,
        blocked: options.blocked === true,
        empty: options.empty === true,
        reason: this.toText(options.reason || ""),
        message: this.toText(options.message || ""),
        entryKey: this.toText(options.entryKey || ""),
        txHash: this.toText(options.txHash || ""),
        targetAddress: this.toText(options.targetAddress || ""),
        manualDepthLimitBypassed: options.manualDepthLimitBypassed === true,
        flowCompletionDepth: Number(this.flowCompletionDepth || 0),
        flowCompletionMaxDepth: this.normalizedFlowCompletionMaxDepth,
      };
      this.lastFlowSelectionResult = result;
      return result;
    },

    async selectNextUnresolvedEnding(options = {}) {
      const parsed = this.parseInputRowsForFlowCompletion();
      const source = this.toText(options.source || "manual-select");
      if (!parsed.rows.length || !parsed.rows.some((row) => this.rowHasAnyData(row))) {
        this.flowCompletionScanResult = null;
        this.flowCompletionMessage = "No input data was found to review.";
        return this.buildFlowSelectionResult({
          source,
          empty: true,
          reason: "no-input",
          message: this.flowCompletionMessage,
        });
      }

      const result = this.evaluateFlowCompletion(parsed);
      this.flowCompletionScanResult = result;
      if (!result.incomplete.length) {
        this.flowCompletionMessage = result.summary;
        this.reportText = result.report;
        return this.buildFlowSelectionResult({
          source,
          done: true,
          reason: "flow-complete",
          message: this.flowCompletionMessage,
        });
      }

      const ignoreManualDepthLimit = this.shouldIgnoreManualFlowSelectionDepthLimit(options);
      if (!ignoreManualDepthLimit && Number(this.flowCompletionDepth || 0) >= this.normalizedFlowCompletionMaxDepth) {
        this.clearStaleFlowSelectionForGuardrail();
        this.flowCompletionMessage = `Max manual depth reached (${this.normalizedFlowCompletionMaxDepth}). Review the unresolved endings before continuing.`;
        this.simpleViewMessage = this.flowCompletionMessage;
        this.reportText = `${result.report}\n\nTool 88 stopped before selecting the next unresolved ending because the manual depth limit was reached.`;
        return this.buildFlowSelectionResult({
          source,
          blocked: true,
          reason: "manual-depth-limit",
          message: this.flowCompletionMessage,
        });
      }

      const next = result.incomplete[0];
      if (next.autoMarkable && next.lowValue) {
        const selectedAddress = next.targetAddress || this.collectTargetCandidates(next.entry)[0]?.address || "Unknown";
        this.inputSnapshotRows = parsed.rows.map((row) => this.cloneRowPreservingValues(row));
        this.outputBaseRows = this.inputSnapshotRows.map((row) => this.cloneRowPreservingValues(row));
        this.parsedEntries = parsed.entries;
        this.selectedEntryKey = next.entry.key;
        this.selectedTargetAddress = selectedAddress;
        const reason = `Low-value threshold rule: ${this.formatFunds(next.lowValue.amount, next.lowValue.token)} is below ${this.formatFunds(next.lowValue.threshold, next.lowValue.token)}.`;
        const finalized = this.finalizeEntryAsPathEnding({
          baseRows: this.outputBaseRows,
          entry: next.entry,
          selectedAddress,
          reason,
          dormantAmountText: this.formatFunds(next.lowValue.amount, next.lowValue.token),
        });
        const row = finalized.rows[next.entry.startRowIndex];
        this.appendFlowCompletionMarkerToRow(row, "low-value-ended", reason);
        this.loadOutputRows(finalized.rows);
        this.pathEndingResult = { note: finalized.note, reason: finalized.reason, rows: finalized.rows, lowValueAuto: true };
        this.flowCompletionMessage = "Low-value unresolved ending was finalized in the output. Use Output as New Input to continue.";
        this.reportText = `${result.report}\n\nLow-value action taken:\n${reason}`;
        this.lastRunMeta = this.buildBaseMeta(parsed.rows, parsed.entries, this.collectTargetCandidates(next.entry), selectedAddress);
        return this.buildFlowSelectionResult({
          source,
          selected: true,
          reason: "low-value-output-ready",
          message: this.flowCompletionMessage,
          entryKey: next.entry.key,
          txHash: next.txHash || next.entry?.txHash || "",
          targetAddress: selectedAddress,
          manualDepthLimitBypassed: ignoreManualDepthLimit,
        });
      }

      this.isRefreshingSelections = true;
      this.inputSnapshotRows = parsed.rows.map((row) => this.cloneRowPreservingValues(row));
      this.outputBaseRows = this.inputSnapshotRows.map((row) => this.cloneRowPreservingValues(row));
      this.headerDetected = parsed.headerDetected;
      this.dataStartIndex = parsed.dataStartIndex;
      this.parsedEntries = parsed.entries;
      this.selectedEntryKey = next.entry.key;
      this.targetCandidates = this.collectTargetCandidates(next.entry);
      this.selectedTargetAddress = next.targetAddress || (this.targetCandidates.length === 1 ? this.targetCandidates[0].address : "");
      this.isRefreshingSelections = false;
      const expectedEntryKey = next.entry.key;
      const expectedTargetAddress = this.selectedTargetAddress;
      this.flowCompletionMessage = `Selected ${next.label}. Running Tool 88 checks for ${expectedTargetAddress || "the unresolved address"}.`;
      await this.processData({
        entryKey: expectedEntryKey,
        selectedTargetAddress: expectedTargetAddress,
      });

      // processData should retain the entry explicitly selected by the flow scan.
      // Restore it defensively rather than allowing a stale previous parent to
      // become the next automation action.
      if (this.selectedEntryKey !== expectedEntryKey) {
        this.selectedEntryKey = expectedEntryKey;
      }
      if (expectedTargetAddress && this.selectedTargetAddress !== expectedTargetAddress) {
        this.selectedTargetAddress = expectedTargetAddress;
      }

      return this.buildFlowSelectionResult({
        source,
        selected: true,
        reason: "next-unresolved-selected",
        message: this.flowCompletionMessage,
        entryKey: expectedEntryKey,
        txHash: next.txHash || next.entry?.txHash || "",
        targetAddress: expectedTargetAddress,
        manualDepthLimitBypassed: ignoreManualDepthLimit,
      });
    },

    markFlowResolvedEndings() {
      const parsed = this.parseInputRowsForFlowCompletion();
      if (!parsed.rows.length || !parsed.rows.some((row) => this.rowHasAnyData(row))) {
        this.flowCompletionMessage = "No input data was found to mark.";
        return;
      }
      const result = this.evaluateFlowCompletion(parsed);
      let marked = 0;
      result.complete.forEach((ending) => {
        if (!ending || !ending.entry || this.entryHasFlowCompletionMarker(ending.entry)) return;
        const row = parsed.rows[ending.entry.startRowIndex];
        if (!row) return;
        this.appendFlowCompletionMarkerToRow(row, "complete", ending.reason);
        marked += 1;
      });
      if (!marked) {
        this.flowCompletionScanResult = result;
        this.flowCompletionMessage = "No resolved endings needed a new Column W marker.";
        this.reportText = result.report;
        return;
      }
      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(parsed.rows));
      this.inputSnapshotRows = parsed.rows.map((row) => this.cloneRowPreservingValues(row));
      this.outputBaseRows = parsed.rows.map((row) => this.cloneRowPreservingValues(row));
      this.loadOutputRows(parsed.rows);
      const updated = this.evaluateFlowCompletion(this.parseInputRowsForFlowCompletion());
      this.flowCompletionScanResult = updated;
      this.flowCompletionMessage = `Marked ${marked} resolved ending${marked === 1 ? "" : "s"} in Column W. ${updated.summary}`;
      this.reportText = updated.report;
    },

    absorbOutputIntoInput() {
      const rows = this.getCurrentOutputRowsForExport();
      if (!rows.length || !rows.some((row) => this.rowHasAnyData(row))) {
        this.flowCompletionMessage = "No output rows are available to use as the next input.";
        return;
      }

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.inputSnapshotRows = rows.map((row) => this.cloneRowPreservingValues(row));
      this.outputBaseRows = rows.map((row) => this.cloneRowPreservingValues(row));
      this.loadOutputRows(rows);
      this.flowCompletionDepth = Math.min(this.normalizedFlowCompletionMaxDepth, Number(this.flowCompletionDepth || 0) + 1);
      this.resetDecisionState();
      const parsed = this.parseInputRowsForFlowCompletion();
      const result = this.evaluateFlowCompletion(parsed);
      this.flowCompletionScanResult = result;
      this.flowAbsorbResult = null;
      this.flowCompletionMessage = `Output absorbed into input. ${result.summary}`;
      this.reportText = result.report;
    },

    loadOutputRows(rows) {
      const trimmedRows = this.trimTrailingBlankRows(Array.isArray(rows) ? rows : []);
      const normalizedRows = this.caseBuddyEmbedded
        ? this.sanitizeCaseBuddySnapshotRows(trimmedRows)
        : trimmedRows.map((row) => this.cloneRowPreservingValues(row));
      const snapshotRows = normalizedRows.map((row) => this.cloneRowPreservingValues(row));
      this.outputSnapshotRows = snapshotRows.map((row) => this.cloneRowPreservingValues(row));
      this.outputBaseRows = snapshotRows.map((row) => this.cloneRowPreservingValues(row));
      this.$refs.outputSpreadsheet?.loadData(this.ensureMinimumRows(snapshotRows));
    },

    async processData(options = {}) {
      if (this.isProcessing) {
        return;
      }

      const forcedEntryKey = this.toText(options.entryKey);
      const forcedTargetAddress = this.toText(options.selectedTargetAddress);
      const previouslySelectedEntryKey = forcedEntryKey || this.selectedEntryKey;
      const previouslySelectedTargetAddress = forcedTargetAddress || this.selectedTargetAddress;

      this.warnings = [];
      this.reportText = "";
      this.simpleRecommendationApplied = false;
      this.simpleFlowActionReady = false;
      this.countLookupResult = null;
      this.attributionLookupResult = null;
      this.transactionSignalResult = null;
      this.traceResult = null;
      this.dormantResult = null;
      this.traceTransactionPayload = null;
      this.pathEndingResult = null;
      this.flowAbsorbResult = null;
      this.backendJobMeta = null;
      this.countJobMeta = null;
      this.attributionJobMeta = null;
      this.signalJobMeta = null;
      this.resetSwapUi();
      this.resetBridgeUi();
      this.resetTraceFetchUi(null);
      this.statusMessage = "Parsing input spreadsheet...";

      const rawRows = this.$refs.inputSpreadsheet?.getData?.() || [];
      let inputRows = this.trimTrailingBlankRows(rawRows);
      const looseHashNormalization = this.normalizeLooseHashOnlyInputRows(inputRows);
      if (looseHashNormalization.changed) {
        inputRows = this.trimTrailingBlankRows(looseHashNormalization.rows);
        this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(inputRows));
      }

      if (!inputRows.length || !inputRows.some((row) => this.rowHasAnyData(row))) {
        this.inputSnapshotRows = [];
        this.outputBaseRows = [];
        this.parsedEntries = [];
        this.selectedEntryKey = "";
        this.targetCandidates = [];
        this.selectedTargetAddress = "";
        this.lastRunMeta = null;
        this.loadOutputRows(this.makeBlankGrid());
        this.reportText = "No input data was found. Paste Case Buddy raw/main data and run Process Entry + Checks again.";
        return;
      }

      this.headerDetected = this.isLikelyHeaderRow(inputRows[0]);
      this.dataStartIndex = this.headerDetected ? 1 : 0;
      if (!this.headerDetected) {
        this.warnings.push(
          "No header row was detected. The tool will still process the rows, but Flow Solver expects Case Buddy A-X data with headers in row 1."
        );
      }

      const inferenceResult = await this.inferMissingTransactionDetailsForRows(inputRows, this.dataStartIndex, options);
      if (inferenceResult.changed) {
        inputRows = this.trimTrailingBlankRows(inferenceResult.rows);
        this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(inputRows));
      }
      this.warnings.push(...(inferenceResult.warnings || []));

      let { entries, warnings } = this.collectEntries(inputRows, this.dataStartIndex);
      this.warnings.push(...warnings);

      this.inputSnapshotRows = inputRows.map((row) => this.cloneRowPreservingValues(row));
      this.outputBaseRows = this.inputSnapshotRows.map((row) => this.cloneRowPreservingValues(row));
      this.parsedEntries = entries;

      if (!entries.length) {
        this.selectedEntryKey = "";
        this.targetCandidates = [];
        this.selectedTargetAddress = "";
        this.lastRunMeta = this.buildBaseMeta(inputRows, entries, [], "");
        this.loadOutputRows(inputRows);
        this.reportText = this.buildParsingReport({
          rows: inputRows,
          entries,
          selectedEntry: null,
          candidates: [],
          selectedAddress: "",
          stageMessage: "No valid transaction entries were found. Column B needs a transaction hash to begin an entry.",
        });
        return;
      }

      const selectedEntryStillExists = entries.some(
        (entry) => entry.key === previouslySelectedEntryKey
      );

      this.isRefreshingSelections = true;
      this.selectedEntryKey = selectedEntryStillExists
        ? previouslySelectedEntryKey
        : entries[0].key;

      let selectedEntry =
        entries.find((entry) => entry.key === this.selectedEntryKey) || entries[0];

      let candidates = this.collectTargetCandidates(selectedEntry);
      this.targetCandidates = candidates;

      const previousAddressStillExists = candidates.some(
        (candidate) =>
          this.normalizeAddressKey(candidate.address) ===
          this.normalizeAddressKey(previouslySelectedTargetAddress)
      );

      if (forcedTargetAddress && previousAddressStillExists) {
        this.selectedTargetAddress = forcedTargetAddress;
      } else if (candidates.length === 1) {
        this.selectedTargetAddress = candidates[0].address;
      } else if (previousAddressStillExists) {
        this.selectedTargetAddress = previouslySelectedTargetAddress;
      } else {
        this.selectedTargetAddress = "";
      }
      await this.$nextTick();
      this.isRefreshingSelections = false;

      this.loadOutputRows(inputRows);
      this.lastRunMeta = this.buildBaseMeta(
        inputRows,
        entries,
        candidates,
        this.selectedTargetAddress
      );

      if (!candidates.length) {
        const targetInference = await this.inferCaseBuddySelectedTargetFromSparseEntry(inputRows, selectedEntry, { source: 'process-data' });
        if (targetInference?.inferred && targetInference.selectedAddress) {
          const refreshed = this.collectEntries(inputRows, this.dataStartIndex);
          entries = refreshed.entries;
          this.parsedEntries = entries;
          selectedEntry = entries.find((entry) => entry.key === this.selectedEntryKey) || selectedEntry || entries[0];
          candidates = this.collectTargetCandidates(selectedEntry);
          this.targetCandidates = candidates;
          this.selectedTargetAddress = targetInference.selectedAddress;
          this.inputSnapshotRows = inputRows.map((row) => this.cloneRowPreservingValues(row));
          this.outputBaseRows = this.inputSnapshotRows.map((row) => this.cloneRowPreservingValues(row));
          this.loadOutputRows(inputRows);
          this.lastRunMeta = this.buildBaseMeta(
            inputRows,
            entries,
            this.targetCandidates,
            this.selectedTargetAddress
          );
        } else if (targetInference?.blocked) {
          this.reportText = this.buildParsingReport({
            rows: inputRows,
            entries,
            selectedEntry,
            candidates,
            selectedAddress: "",
            stageMessage: targetInference.message || `No ${this.targetColumnLabel} values were found in the selected entry.`,
          });
          this.statusMessage = targetInference.message || 'Tool 88 needs a target address before solving this sparse Case Buddy row.';
          return;
        }
      }

      if (!candidates.length) {
        this.reportText = this.buildParsingReport({
          rows: inputRows,
          entries,
          selectedEntry,
          candidates,
          selectedAddress: "",
          stageMessage: `No ${this.targetColumnLabel} values were found in the selected entry.`,
        });
        return;
      }

      if (candidates.length > 1 && !this.selectedTargetAddress) {
        this.reportText = this.buildParsingReport({
          rows: inputRows,
          entries,
          selectedEntry,
          candidates,
          selectedAddress: "",
          stageMessage: `Multiple ${this.targetColumnLabel} candidates were found. Select the address to research, then run the checks.`,
        });
        return;
      }

      const caseBuddyInference = await this.ensureCaseBuddySelectedOutputContext(inputRows, selectedEntry, this.selectedTargetAddress);
      if (caseBuddyInference?.blocked) {
        this.reportText = this.buildParsingReport({
          rows: inputRows,
          entries,
          selectedEntry,
          candidates: this.targetCandidates,
          selectedAddress: this.selectedTargetAddress,
          stageMessage: caseBuddyInference?.error
            ? `Selected output inference failed: ${caseBuddyInference.error}`
            : 'Selected output amount/token could not be inferred. Add amount/token/funds-followed data or review the transaction-signal response before solving this hop.',
        });
        this.statusMessage = this.caseBuddySelectedOutputInferenceBlock?.message || 'Selected output details are required before embedded Tool 88 can solve this Case Buddy hop.';
        return;
      }
      if (caseBuddyInference?.changed) {
        const refreshed = this.collectEntries(inputRows, this.dataStartIndex);
        entries = refreshed.entries;
        this.parsedEntries = entries;
        selectedEntry = entries.find((entry) => entry.key === this.selectedEntryKey) || selectedEntry || entries[0];
        candidates = this.collectTargetCandidates(selectedEntry);
        this.targetCandidates = candidates;
        if (!candidates.some((candidate) => this.normalizeAddressKey(candidate.address) === this.normalizeAddressKey(this.selectedTargetAddress)) && candidates.length === 1) {
          this.selectedTargetAddress = candidates[0].address;
        }
        this.inputSnapshotRows = inputRows.map((row) => this.cloneRowPreservingValues(row));
        this.outputBaseRows = this.inputSnapshotRows.map((row) => this.cloneRowPreservingValues(row));
        this.loadOutputRows(inputRows);
        this.lastRunMeta = this.buildBaseMeta(
          inputRows,
          entries,
          this.targetCandidates,
          this.selectedTargetAddress
        );
      }

      await this.runCountLookup(selectedEntry, this.selectedTargetAddress);
    },

    async runCountLookup(entry, selectedAddress) {
      this.isProcessing = true;
      this.statusMessage = "Looking up transaction count...";

      try {
        const lookup = await this.lookupTransactionCount(entry, selectedAddress);
        this.countLookupResult = this.normalizeCountLookupResult(lookup, selectedAddress);
        (this.countLookupResult.warnings || []).forEach((warning) => {
          const text = this.toText(warning);
          if (text && !this.warnings.includes(text)) {
            this.warnings.push(text);
          }
        });

        this.statusMessage = "Looking up attribution and address role...";
        try {
          const attribution = await this.lookupAttributionData(entry, selectedAddress);
          this.attributionLookupResult = this.normalizeAttributionLookupResult(attribution, selectedAddress);
          (this.attributionLookupResult.warnings || []).forEach((warning) => {
            const text = this.toText(warning);
            if (text && !this.warnings.includes(text)) {
              this.warnings.push(text);
            }
          });
        } catch (attributionError) {
          const attributionMessage = attributionError && attributionError.message ? attributionError.message : "Unknown attribution error";
          this.attributionLookupResult = this.buildLocalAttributionFallback(selectedAddress, attributionMessage);
          this.warnings.push(`Attribution lookup warning: ${attributionMessage}`);
        }

        this.statusMessage = "Checking transaction-level signals...";
        try {
          const signal = await this.lookupTransactionSignalData(entry, selectedAddress);
          this.transactionSignalResult = this.normalizeTransactionSignalResult(signal, entry, selectedAddress);
          (this.transactionSignalResult.warnings || []).forEach((warning) => {
            const text = this.toText(warning);
            if (text && !this.warnings.includes(text)) {
              this.warnings.push(text);
            }
          });
        } catch (signalError) {
          const signalMessage = signalError && signalError.message ? signalError.message : "Unknown transaction-signal error";
          this.transactionSignalResult = this.buildLocalTransactionSignalFallback(entry, selectedAddress, signalMessage);
          this.warnings.push(`Transaction-signal lookup warning: ${signalMessage}`);
        }

        const annotatedRows = this.applyAttributionToSelectedRows(
          this.inputSnapshotRows,
          entry,
          selectedAddress,
          this.attributionLookupResult
        );
        const signalAnnotatedRows = this.applySignalCheckToSelectedRows(
          annotatedRows,
          entry,
          selectedAddress,
          this.transactionSignalResult
        );
        this.inputSnapshotRows = signalAnnotatedRows.map((row) => this.cloneRowPreservingValues(row));
        this.outputBaseRows = signalAnnotatedRows;
        this.loadOutputRows(signalAnnotatedRows);

        this.lastRunMeta = {
          ...this.buildBaseMeta(
            this.inputSnapshotRows,
            this.parsedEntries,
            this.targetCandidates,
            selectedAddress
          ),
          transactionCount: this.countLookupResult.transactionCount,
          countAvailable: this.countLookupResult.countAvailable,
          countProvider: this.countLookupResult.provider,
          countRoute: this.countLookupResult.route,
          attributionLabel: this.attributionLookupResult?.displayLabel || "",
          attributionClassification: this.attributionLookupResult?.classification || this.attributionLookupResult?.entityType || "",
          attributionRole: this.attributionLookupResult?.classification || this.attributionLookupResult?.serviceRole || this.attributionLookupResult?.entityType || "",
          signalClassification: this.transactionSignalResult?.classification || "",
          signalSummary: this.transactionSignalResult?.evidenceSummary || this.transactionSignalResult?.evidenceLabel || "",
          traceFetchedCount: this.traceFetchFetchedCount,
          traceInputTransactionFound: this.traceInputTransactionFound,
        };

        this.reportText = this.buildCountReport(entry, selectedAddress);
      } catch (error) {
        this.countLookupResult = null;
        const message = error && error.message ? error.message : "Unknown error";
        this.warnings.push(`Count lookup failed: ${message}`);
        this.reportText = `Count lookup failed: ${message}`;
      } finally {
        this.isProcessing = false;
        this.statusMessage = "";
      }
    },

    normalizeSessionCacheKeyPart(value) {
      return this.toText(value).trim().toLowerCase() || "-";
    },

    pruneTool88SessionCache(map, maxEntries) {
      while (map.size > maxEntries) {
        const oldestKey = map.keys().next().value;
        if (oldestKey === undefined) break;
        map.delete(oldestKey);
      }
    },

    cloneSessionCachedPayload(payload = {}) {
      const cloned = { ...payload };
      if (payload.trace_fetch && typeof payload.trace_fetch === "object") {
        cloned.trace_fetch = { ...payload.trace_fetch };
      }
      if (Array.isArray(payload.transactions)) {
        cloned.transactions = payload.transactions.map((tx) => (tx && typeof tx === "object" ? { ...tx } : tx));
      }
      if (Array.isArray(payload.batch_transactions)) {
        cloned.batch_transactions = payload.batch_transactions.map((tx) => (tx && typeof tx === "object" ? { ...tx } : tx));
      }
      return cloned;
    },

    touchSessionCacheRevision() {
      this.sessionCacheRevision += 1;
    },

    clearSessionDataCache(options = {}) {
      TOOL88_SESSION_DATA_CACHE.counts.clear();
      TOOL88_SESSION_DATA_CACHE.traces.clear();
      TOOL88_SESSION_DATA_CACHE.stats.countHits = 0;
      TOOL88_SESSION_DATA_CACHE.stats.countStores = 0;
      TOOL88_SESSION_DATA_CACHE.stats.traceHits = 0;
      TOOL88_SESSION_DATA_CACHE.stats.traceStores = 0;
      TOOL88_SESSION_DATA_CACHE.createdAt = Date.now();
      this.touchSessionCacheRevision();
      if (!options || options.silent !== true) {
        this.statusMessage = "";
        this.warnings = this.warnings.filter((warning) => !this.toText(warning).includes("Session cache"));
        this.reportText = this.reportText || "Session cache cleared.";
      }
    },

    buildCountSessionCacheKey(entry, selectedAddress, countContext = null, fundsIn = null) {
      const context = countContext || this.inferCountRequestContext(entry, selectedAddress);
      const funds = fundsIn || this.deriveFundsIn(entry, selectedAddress);
      return [
        "count",
        this.normalizeAddressKey(selectedAddress),
        this.normalizeSessionCacheKeyPart(context.network_family || context.chain_family),
        this.normalizeSessionCacheKeyPart(context.count_kind || "auto"),
        this.normalizeSessionCacheKeyPart(context.chain_id || entry?.chainId),
        this.normalizeSessionCacheKeyPart(context.chain_name || entry?.chainName),
        this.normalizeSessionCacheKeyPart(context.chain_label),
        this.normalizeSessionCacheKeyPart(context.moralis_chain),
        this.normalizeSessionCacheKeyPart(context.provider_hint),
        this.normalizeSessionCacheKeyPart(context.token_contract || context.contract_address),
        this.normalizeSessionCacheKeyPart(funds.token || context.token_symbol || context.asset_symbol),
        this.isSampleDataLoaded ? "sample" : "live",
      ].join("|");
    },

    getSessionCountCache(cacheKey) {
      if (this.enableSessionDataCache === false || !cacheKey) return null;
      const record = TOOL88_SESSION_DATA_CACHE.counts.get(cacheKey);
      if (!record) return null;
      TOOL88_SESSION_DATA_CACHE.stats.countHits += 1;
      this.touchSessionCacheRevision();
      const payload = this.cloneSessionCachedPayload(record.payload || {});
      return {
        ...payload,
        session_cache_hit: true,
        cache_hit: true,
        cached_at: record.cachedAt,
        source: payload.source ? `${payload.source}+session_cache` : "session_cache",
      };
    },

    storeSessionCountCache(cacheKey, payload = {}) {
      if (this.enableSessionDataCache === false || !cacheKey || !payload || typeof payload !== "object") return;
      const hasCountValue = payload.transaction_count !== undefined || payload.transactionCount !== undefined || payload.count_available === false || payload.countAvailable === false;
      if (!hasCountValue) return;
      TOOL88_SESSION_DATA_CACHE.counts.set(cacheKey, {
        cachedAt: new Date().toISOString(),
        payload: this.cloneSessionCachedPayload(payload),
      });
      this.pruneTool88SessionCache(TOOL88_SESSION_DATA_CACHE.counts, TOOL88_SESSION_CACHE_MAX_COUNT_ENTRIES);
      TOOL88_SESSION_DATA_CACHE.stats.countStores += 1;
      this.touchSessionCacheRevision();
    },

    buildTraceSessionCacheKey(entry, selectedAddress, countContext = null, fundsIn = null, options = {}) {
      const context = countContext || this.inferCountRequestContext(entry, selectedAddress);
      const funds = fundsIn || this.deriveFundsIn(entry, selectedAddress);
      const serviceChecksEnabled = options.additionalServiceChecksEnabled !== undefined
        ? Boolean(options.additionalServiceChecksEnabled)
        : this.enableAdditionalServiceChecks !== false;
      return [
        "trace",
        this.traceDirection === "reverse" ? "reverse" : "forward",
        this.buildTraceRequestType(context),
        this.normalizeAddressKey(selectedAddress),
        this.normalizeSessionCacheKeyPart(entry?.txHash || entry?.hash || entry?.transactionHash),
        this.normalizeSessionCacheKeyPart(entry?.dateText || entry?.date || entry?.utcDate),
        this.normalizeSessionCacheKeyPart(funds.amount),
        this.normalizeSessionCacheKeyPart(context.network_family || context.chain_family),
        this.normalizeSessionCacheKeyPart(context.count_kind || "auto"),
        this.normalizeSessionCacheKeyPart(context.chain_id || entry?.chainId),
        this.normalizeSessionCacheKeyPart(context.chain_name || entry?.chainName),
        this.normalizeSessionCacheKeyPart(context.chain_label),
        this.normalizeSessionCacheKeyPart(context.moralis_chain),
        this.normalizeSessionCacheKeyPart(context.provider_hint),
        this.normalizeSessionCacheKeyPart(context.token_contract || context.contract_address),
        this.normalizeSessionCacheKeyPart(funds.token || context.token_symbol || context.asset_symbol),
        this.normalizeSessionCacheKeyPart(context.token_decimals),
        serviceChecksEnabled ? "service-checks-on" : "service-checks-off",
        options.serviceStopOverride ? "manual-service-override" : "no-service-override",
        options.depositEntityHopExtensionProbe ? "deposit-entity-probe" : "standard-trace",
        this.isSampleDataLoaded ? "sample" : "live",
      ].join("|");
    },

    getSessionTraceCache(cacheKey) {
      if (this.enableSessionDataCache === false || !cacheKey) return null;
      const record = TOOL88_SESSION_DATA_CACHE.traces.get(cacheKey);
      if (!record) return null;
      TOOL88_SESSION_DATA_CACHE.stats.traceHits += 1;
      this.touchSessionCacheRevision();
      const payload = this.cloneSessionCachedPayload(record.payload || {});
      const transactions = Array.isArray(payload.transactions) ? payload.transactions : [];
      return {
        ...payload,
        status: payload.status || "complete",
        trace_status: payload.trace_status || "complete",
        source: payload.source ? `${payload.source}+session_cache` : "session_cache",
        session_cache_hit: true,
        cache_hit: true,
        cached_at: record.cachedAt,
        fetched_transaction_count: transactions.length,
        trace_fetch: {
          ...(payload.trace_fetch || {}),
          status: payload.trace_fetch?.status || "complete",
          cache_hit: true,
          cached_at: record.cachedAt,
          fetched_count: payload.trace_fetch?.fetched_count ?? transactions.length,
        },
      };
    },

    shouldStoreSessionTraceCache(payload = {}, transactions = []) {
      if (this.enableSessionDataCache === false || !payload || typeof payload !== "object") return false;
      if (this.traceFetchCancelledByUser(payload)) return false;
      const status = this.getTraceFetchStatusText(payload);
      const completeLike = ["complete", "completed", "success", "service_stop", "stopped_for_service", "potential_service_stop"].includes(status);
      const hasRows = Array.isArray(transactions) && transactions.length > 0;
      if (!completeLike && !hasRows && !this.traceFetchStoppedForService(payload)) return false;
      if (this.traceFetchLooksSuspiciouslyIncomplete(payload)) return false;
      if (this.traceFetchLooksSuspiciouslyEmpty(payload, transactions)) return false;
      return true;
    },

    storeSessionTraceCache(cacheKey, payload = {}) {
      const transactions = Array.isArray(payload.transactions) ? payload.transactions : [];
      if (!cacheKey || !this.shouldStoreSessionTraceCache(payload, transactions)) return;
      TOOL88_SESSION_DATA_CACHE.traces.set(cacheKey, {
        cachedAt: new Date().toISOString(),
        payload: this.cloneSessionCachedPayload(payload),
      });
      this.pruneTool88SessionCache(TOOL88_SESSION_DATA_CACHE.traces, TOOL88_SESSION_CACHE_MAX_TRACE_ENTRIES);
      TOOL88_SESSION_DATA_CACHE.stats.traceStores += 1;
      this.touchSessionCacheRevision();
    },

    normalizeCountLookupResult(lookup, selectedAddress) {
      const transactionCount = this.normalizeTransactionCount(lookup);
      const countAvailable = lookup?.count_available !== undefined
        ? Boolean(lookup.count_available)
        : lookup?.countAvailable !== undefined
          ? Boolean(lookup.countAvailable)
          : lookup?.count_known !== undefined
            ? Boolean(lookup.count_known)
            : Number.isFinite(transactionCount) && transactionCount >= 0;
      const exceedsWarning = countAvailable && transactionCount > Number(this.warningThreshold || 0);
      const exceedsHardLimit = countAvailable && transactionCount > Number(this.hardLimitThreshold || 0);
      const backendAllowsTrace = lookup?.allow_trace ?? lookup?.can_trace;
      const canTrace = backendAllowsTrace === undefined
        ? !exceedsHardLimit
        : Boolean(backendAllowsTrace) && !exceedsHardLimit;

      let recommendation = lookup?.recommendation || "";
      if (!recommendation) {
        recommendation = !countAvailable
          ? "Count unavailable: trace-through remains available after reviewing the other checks."
          : !canTrace
            ? "Blocked: transaction volume is too high for trace-through."
            : exceedsWarning
              ? "Caution: transaction volume is high; review before tracing."
              : "OK to trace: transaction count is within the current trace gate.";
      }

      const countUnavailableReason = this.toText(
        lookup?.count_unavailable_reason ||
          lookup?.countUnavailableReason ||
          lookup?.unavailable_reason ||
          lookup?.message ||
          ""
      );

      return {
        address: lookup?.address || selectedAddress,
        transactionCount: Number.isFinite(transactionCount) ? transactionCount : UNKNOWN_TRANSACTION_COUNT,
        countAvailable,
        countUnavailable: !countAvailable,
        countUnavailableReason,
        canTrace,
        exceedsWarning,
        recommendation,
        message: lookup?.message || "",
        source: lookup?.source || (this.useMockBackend ? "mock" : "server"),
        provider: lookup?.provider || "",
        providerLabel: lookup?.provider_label || lookup?.providerLabel || "",
        route: lookup?.route || lookup?.method || "",
        routeLabel: lookup?.route_label || lookup?.routeLabel || lookup?.provider_label || "",
        countKind: lookup?.count_kind || lookup?.countKind || lookup?.count_scope || "",
        networkFamily: lookup?.chain_family || lookup?.network_family || lookup?.networkFamily || "",
        chainId: lookup?.chain_id || lookup?.chainId || "",
        chainName: lookup?.chain_name || lookup?.chainName || lookup?.chain_label || "",
        chainLabel: lookup?.chain_label || lookup?.chainLabel || "",
        tokenSymbol: lookup?.token_symbol || lookup?.tokenSymbol || lookup?.asset_symbol || "",
        nativeSymbol: lookup?.native_symbol || lookup?.nativeSymbol || "",
        moralisChain: lookup?.moralis_chain || lookup?.moralisChain || "",
        cacheHit: Boolean(lookup?.session_cache_hit || lookup?.cache_hit),
        cachedAt: lookup?.cached_at || lookup?.cachedAt || "",
        warnings: Array.isArray(lookup?.warnings) ? lookup.warnings : [],
        raw: lookup,
      };
    },

    normalizeAttributionCode(value) {
      const normalized = this.toText(value).toLowerCase().replace(/[\s-]+/g, "_");
      const labels = {
        deposit: "deposit_address",
        deposit_wallet: "deposit_address",
        deposit_address: "deposit_address",
        hot: "hot_wallet",
        hot_wallet: "hot_wallet",
        bridge: "bridge_like",
        bridge_like: "bridge_like",
        swap: "swap_like",
        swaps: "swap_like",
        wrapped_asset: "swap_like",
        wrapped_native_or_swap_contract: "swap_like",
        swap_like: "swap_like",
        swap_or_bridge: "swap_or_bridge",
        contract: "contract_like",
        contract_like: "contract_like",
        known_entity: "attributed_service",
        service_wallet: "attributed_service",
        attributed_service: "attributed_service",
        address_or_unattributed_wallet: "unknown",
      };
      return labels[normalized] || normalized;
    },

    formatAttributionClassification(value) {
      const normalized = this.normalizeAttributionCode(value);
      const labels = {
        deposit_address: "Deposit address",
        hot_wallet: "Hot wallet",
        bridge_like: "Bridge-like",
        swap_like: "Swap-like",
        swap_or_bridge: "Swap/bridge-like",
        contract_like: "Contract-like",
        attributed_service: "Attributed service",
        unknown: "No clear classification",
      };
      return labels[normalized] || this.toText(value) || "No clear classification";
    },

    formatAttributionRole(value) {
      return this.formatAttributionClassification(value);
    },

    buildAttributionSourceLabel(result) {
      const parts = [];
      if (result?.serverNotePresent) {
        parts.push("server note");
      }
      if (this.toText(result?.vendorSource)) {
        parts.push(result.vendorSource.replace(/_/g, " "));
      } else if (this.toText(result?.source) && result.source !== "none") {
        parts.push(result.source.replace(/_/g, " "));
      }
      return parts.length ? parts.join(" + ") : "No hit";
    },

    normalizeAttributionLookupResult(result, selectedAddress) {
      const rawSignalValue = result?.signals;
      let rawSignals = [];
      if (Array.isArray(rawSignalValue)) {
        rawSignals = rawSignalValue;
      } else if (rawSignalValue && typeof rawSignalValue === "object") {
        if (rawSignalValue.isBridgeCandidate) rawSignals.push("bridge_like");
        if (rawSignalValue.isSwapCandidate) rawSignals.push("swap_like");
        if (rawSignalValue.hasWalletRole && result?.wallet_role) rawSignals.push(this.normalizeAttributionCode(result.wallet_role));
        if (rawSignalValue.hasServerNote) rawSignals.push("server_note");
        if (rawSignalValue.hasArkhamLabel) rawSignals.push("arkham_label");
      }

      const serverNoteRaw = result?.server_note_text || result?.serverNoteText || result?.server_note?.note || result?.serverNote?.note || result?.server_note || result?.serverNote || "";
      const vendorLabelRaw = result?.vendor_label || result?.vendorLabel || result?.arkham_label || result?.arkhamLabel || result?.arkham?.label || "";
      const columnKValue = this.cleanAttributionLabel(result?.column_k_value || result?.columnKValue || "");
      const displayLabel = this.cleanAttributionLabel(
        result?.display_label ||
          result?.displayLabel ||
          result?.primary_label ||
          result?.primaryLabel ||
          result?.label ||
          serverNoteRaw ||
          vendorLabelRaw ||
          columnKValue ||
          ""
      );
      const serverNote = this.cleanAttributionLabel(serverNoteRaw);
      const vendorLabel = this.cleanAttributionLabel(vendorLabelRaw);
      const vendorSource = result?.vendor_source || result?.vendorSource || result?.arkham?.source || "";
      const normalizedSignals = [...new Set(rawSignals.map((signal) => this.normalizeAttributionCode(signal)).filter(Boolean))];
      let classification = this.normalizeAttributionCode(result?.classification || result?.deposit_hot_wallet || result?.depositHotWallet || result?.wallet_role || result?.walletRole || "");
      let entityType = this.normalizeAttributionCode(result?.entity_type || result?.entityType || result?.entity_kind || result?.entityKind || result?.entity_category || result?.entityCategory || "unknown");
      const attributionCandidates = [displayLabel, serverNote, vendorLabel, columnKValue].filter(Boolean);
      const meaningfulLabels = attributionCandidates.filter((value) => !this.isUnknownAttributionPlaceholder(value));
      const explicitSpecificClassification = [
        classification,
        entityType,
        ...normalizedSignals,
      ].some((value) => ["deposit_address", "hot_wallet", "bridge_like", "swap_like", "swap_or_bridge", "contract_like", "wrapped_asset"].includes(value));
      const unknownPlaceholderOnly = Boolean(
        result?.unknown_placeholder_only ||
        result?.unknownPlaceholderOnly ||
        (attributionCandidates.some((value) => this.isUnknownAttributionPlaceholder(value)) && !meaningfulLabels.length && !explicitSpecificClassification)
      );
      if (unknownPlaceholderOnly) {
        // `Unknown` means no attribution decision. It must never be promoted into
        // `attributed_service`, otherwise a harmless placeholder becomes a false
        // service/hot-wallet path ending.
        if (classification === "attributed_service") classification = "";
        if (entityType === "attributed_service") entityType = "unknown";
      }
      const hasMeaningfulAttribution = Boolean(meaningfulLabels.length || explicitSpecificClassification);
      const adviceStatus = unknownPlaceholderOnly
        ? "neutral"
        : this.toText(result?.advice_status || result?.adviceStatus || (hasMeaningfulAttribution ? "warn" : "neutral"));
      const recommendation = unknownPlaceholderOnly
        ? "No meaningful attribution hit was found. Continue with transaction count and chain-specific checks."
        : (this.toText(result?.recommendation || result?.guidance || result?.decision_guidance) || "Review attribution before deciding whether to trace through.");

      const normalized = {
        address: result?.address || selectedAddress,
        displayLabel,
        label: displayLabel,
        serverNote,
        serverNotePresent: Boolean(result?.server_note_present || result?.serverNotePresent || serverNote || result?.signals?.hasServerNote),
        serverNoteSource: result?.server_note_source || result?.serverNoteSource || (serverNote ? "address_notes" : ""),
        vendorLabel,
        vendorSource,
        classification,
        entityType,
        signals: normalizedSignals,
        adviceStatus,
        source: result?.source || "attribution_lookup",
        sourceSummary: result?.source_summary || result?.sourceSummary || "",
        sourceLabel: "",
        cacheStatus: result?.cache_status || result?.cacheStatus || "",
        recommendation,
        suggestedColumnJNote: unknownPlaceholderOnly ? "" : this.toText(result?.suggested_column_j_note || result?.suggestedColumnJNote || ""),
        columnKValue,
        unknownPlaceholderOnly,
        hasMeaningfulAttribution,
        meaningfulLabels,
        rawClassification: this.normalizeAttributionCode(result?.classification || result?.entity_type || result?.entityType || ""),
        warnings: Array.isArray(result?.warnings) ? result.warnings : [],
        raw: result || {},
      };
      normalized.signalsText = normalized.signals.length ? normalized.signals.map((signal) => signal.replace(/_/g, " ")).join(", ") : "";
      normalized.sourceLabel = normalized.sourceSummary || this.buildAttributionSourceLabel(normalized);
      return normalized;
    },

    buildLocalAttributionFallback(selectedAddress, errorMessage = "") {
      const localValues = this.getSelectedAttributionValues();
      const label = this.cleanAttributionLabel(localValues.join("; "));
      const reverse = this.traceDirection === "reverse";
      const columnLabel = reverse ? "Column X / From Address Attribution" : "Column K / To Address Attribution";
      return this.normalizeAttributionLookupResult({
        address: selectedAddress,
        display_label: label,
        label,
        source: reverse ? "local_column_x_fallback" : "local_column_k_fallback",
        vendor_source: "none",
        server_note_source: reverse ? "local_column_x" : "local_column_k",
        classification: "",
        entity_type: label ? this.classifyAttributionEntityType(label) : "unknown",
        signals: label ? this.buildLocalAttributionSignals(label) : [],
        advice_status: label ? "warn" : "neutral",
        recommendation: label
          ? `${columnLabel} contains attribution, but the server attribution lookup did not complete. Review the local label before tracing.`
          : `No attribution was available from ${columnLabel} or the server lookup.`,
        warnings: errorMessage ? [errorMessage] : [],
      }, selectedAddress);
    },

    cleanAttributionLabel(value) {
      return this.toText(value).replace(/\s+/g, " ").replace(/\s*:+\s*$/g, "").trim();
    },

    isUnknownAttributionPlaceholder(value) {
      const clean = this.cleanAttributionLabel(value);
      if (!clean) return false;
      const parts = clean
        .split(/[;|\n]+/g)
        .map((part) => part
          .toLowerCase()
          .replace(/[._-]+/g, " ")
          .replace(/[^a-z0-9/ ]+/g, " ")
          .replace(/\s+/g, " ")
          .trim())
        .filter(Boolean);
      if (!parts.length) return false;
      return parts.every((part) => /^(?:unknown|unattributed|none|null|n\/?a|not available|not attributed|no attribution(?: found)?|no hit|no visible hit|no label|no server vendor label(?: found)?|address or unattributed wallet)$/.test(part));
    },

    buildLocalAttributionSignals(text) {
      const normalized = this.toText(text).toLowerCase();
      const signals = [];
      if (/hot\s*wallet/.test(normalized)) signals.push("hot_wallet");
      if (/deposit/.test(normalized)) signals.push("deposit_address");
      if (this.hasBridgeSignal(normalized)) signals.push("bridge_like");
      if (this.hasSwapSignal(normalized)) signals.push("swap_like");
      if (/\bcontract\b/.test(normalized)) signals.push("contract_like");
      return [...new Set(signals)];
    },

    classifyAttributionEntityType(text) {
      const normalized = this.toText(text).toLowerCase();
      if (this.hasBridgeSignal(normalized)) return "bridge_like";
      if (this.hasSwapSignal(normalized)) return "swap_like";
      if (/\bcontract\b/.test(normalized)) return "contract_like";
      if (this.isUnknownAttributionPlaceholder(text)) return "unknown";
      return normalized ? "attributed_service" : "unknown";
    },

    getMatchedBridgeServiceName(text = "") {
      const normalized = this.toText(text).toLowerCase();
      if (!normalized) return "";
      const match = TOOL88_BRIDGE_SERVICE_DEFINITIONS.find((service) =>
        (service.aliases || []).some((alias) => {
          const cleanAlias = this.toText(alias).trim().toLowerCase();
          return cleanAlias && normalized.includes(cleanAlias);
        })
      );
      return match ? match.canonicalName : "";
    },

    hasBridgeSignal(text = "") {
      const normalized = this.toText(text);
      if (!normalized) return false;
      if (this.getMatchedBridgeServiceName(normalized)) return true;
      return /bitget\s+bridge|okx\s*web\s*3|okxweb3|hop\s*protocol|hopprotocol|li\.fi|wormhole|rango|chainflip|transit\s*swap|transitswap|bridgers|bkbridge|stargate|across|orbiter|portal\s+bridge|near[-\s]?intents|allbridge|allchainbridge|crosschainx|chainflip|butterswap|hifiswap|tele\s*swap|teleswap|teleport|thorchain|bridge/i.test(normalized);
    },

    hasSwapSignal(text = "") {
      const normalized = this.toText(text);
      if (!normalized) return false;
      const lower = normalized.toLowerCase();
      const bridgeMatch = this.getMatchedBridgeServiceName(normalized);
      const onlyBridgeStyleSwap = bridgeMatch && !/(uniswap|sushiswap|curve|1inch|tokenlon|cow|dodo|bkswap|sun\.io|0x\s*protocol|0xprocessing|universal\s*router|universalrouter|metamask|mimic|arrakis|pendle|seawise|rizzolver)/i.test(normalized);
      if (onlyBridgeStyleSwap) return false;
      const bitgetSwap = /\bbitget\b/i.test(normalized) && !/bitget\s+bridge|\bbridge\b/i.test(normalized);
      const exact0xProtocolLabel = lower.trim() === "0x";
      return bitgetSwap || exact0xProtocolLabel || /\bswop\b|\bswap\b|\bcow\b|sun\.io|rizzolver|metamask|\bmev\b|wrapped\s*ether|wrappedether|\bweth\b|\bdodo\b|pendle|seawise|\baave\b|bkswap|sushiswap|\bkiln\b|universal\s*router|universalrouter|tokenlon|1inch|mimic|\bcurve\b|arrakis|0x\s*protocol|0xprocessing|uniswap|\bdex\b|\bamm\b|router/.test(lower);
    },

    hasBridgeEventNameSignal(text = "") {
      return TOOL88_BRIDGE_EVENT_NAME_PATTERN.test(this.toText(text));
    },

    hasSwapEventNameSignal(text = "") {
      const normalized = this.toText(text);
      if (!normalized || this.hasBridgeEventNameSignal(normalized)) return false;
      return TOOL88_SWAP_EVENT_NAME_PATTERN.test(normalized);
    },

    collectSignalNameFields(value, depth = 0) {
      if (value === null || value === undefined || depth > 3) return [];
      if (Array.isArray(value)) {
        return value.flatMap((item) => this.collectSignalNameFields(item, depth + 1));
      }
      if (typeof value !== "object") {
        const text = this.toText(value).trim();
        return text ? [text] : [];
      }

      const nameKeys = [
        "event",
        "eventName",
        "event_name",
        "eventSignature",
        "event_signature",
        "logName",
        "log_name",
        "name",
        "method",
        "methodName",
        "method_name",
        "functionName",
        "function_name",
        "decoded_event",
        "decodedEvent",
        "eventType",
        "event_type",
        "type",
      ];
      const names = [];
      nameKeys.forEach((key) => {
        const text = this.toText(value[key]).trim();
        if (text && !/^0x[a-f0-9]{8,}$/i.test(text)) names.push(text);
      });

      [value.event_names, value.eventNames, value.logs, value.events, value.event_logs, value.eventLogs, value.decoded_logs, value.decodedLogs].forEach((nested) => {
        names.push(...this.collectSignalNameFields(nested, depth + 1));
      });

      return [...new Set(names.filter(Boolean))];
    },

    getSignalEventNameText(raw = {}) {
      const names = this.collectSignalNameFields(raw);
      return names.join(" ");
    },

    getCurrentOutputBaseRows() {
      const visibleOutputRows = this.trimTrailingBlankRows(this.$refs.outputSpreadsheet?.getData?.() || []);
      const visibleOutputHasData = visibleOutputRows.some((row) => this.rowHasAnyData(row));
      const snapshotRows = Array.isArray(this.outputSnapshotRows) ? this.trimTrailingBlankRows(this.outputSnapshotRows) : [];
      const source = snapshotRows.length
        ? snapshotRows
        : (visibleOutputHasData
            ? visibleOutputRows
            : (this.outputBaseRows && this.outputBaseRows.length
                ? this.outputBaseRows
                : this.inputSnapshotRows));
      return source.map((row) => this.cloneRowPreservingValues(row));
    },

    normalizeRowForMaterialComparison(row = []) {
      const cells = Array.from({ length: COLUMN_COUNT }, (_, index) => this.toText(row?.[index]).trim());
      while (cells.length && cells[cells.length - 1] === "") {
        cells.pop();
      }
      return cells;
    },

    rowsMateriallySame(leftRows = [], rightRows = []) {
      const left = this.trimTrailingBlankRows(Array.isArray(leftRows) ? leftRows : []);
      const right = this.trimTrailingBlankRows(Array.isArray(rightRows) ? rightRows : []);
      if (left.length !== right.length) return false;
      for (let index = 0; index < left.length; index += 1) {
        const leftKey = JSON.stringify(this.normalizeRowForMaterialComparison(left[index]));
        const rightKey = JSON.stringify(this.normalizeRowForMaterialComparison(right[index]));
        if (leftKey !== rightKey) return false;
      }
      return true;
    },

    ensureSimpleTraceOutputCanAdvance() {
      if (!this.traceResult || !Array.isArray(this.traceResult.exitRows) || !this.traceResult.exitRows.length) {
        return true;
      }
      const inputRows = this.trimTrailingBlankRows(this.inputSnapshotRows || []);
      const outputRows = this.getCurrentOutputRowsForExport();
      const verification = this.verifyTraceOutputMutation(outputRows, this.traceResult.exitRows, inputRows);
      if (verification.ok) return true;

      const missingText = verification.missing.length ? verification.missing.join(", ") : "the expected inserted trace row(s)";
      this.traceResult = {
        ...this.traceResult,
        outputMutationVerified: false,
        outputMutationVerification: verification,
      };
      this.simpleRecommendationApplied = false;
      this.simpleViewMessage = `Tool 88 found a candidate hop, but the output spreadsheet did not change correctly (${missingText}). It will not advance the simple workflow until this is reviewed.`;
      this.pushUniqueWarnings([
        `Simple view stopped before absorbing output because trace-through insertion was not verified: ${missingText}.`,
      ]);
      return false;
    },

    buildAttributionAnnotationNote(attributionResult) {
      if (!attributionResult) {
        return "";
      }
      const suggested = this.toText(attributionResult.suggestedColumnJNote);
      if (suggested) {
        return suggested;
      }
      const label = this.cleanAttributionLabel(attributionResult.displayLabel || attributionResult.vendorLabel || attributionResult.serverNote || "");
      if (this.isUnknownAttributionPlaceholder(label) || attributionResult.unknownPlaceholderOnly) {
        return "";
      }
      const classification = this.normalizeAttributionCode(attributionResult.classification || attributionResult.serviceRole || attributionResult.entityType);
      if (label && (classification === "deposit_address" || attributionResult.isDepositAddress)) {
        return `Tool 88 attribution: ${label}. Wallet role: Deposit Address.`;
      }
      if (label && (classification === "hot_wallet" || attributionResult.isHotWallet)) {
        return `Tool 88 attribution: ${label}. Wallet role: Hot Wallet.`;
      }
      if (label && (attributionResult.isBridgeLike || this.normalizeAttributionCode(attributionResult.entityType) === "bridge_like")) {
        return `Tool 88 attribution: ${label}. Bridge-like signal; review before tracing through.`;
      }
      if (label && (attributionResult.isSwapLike || attributionResult.isWrappedNative || this.normalizeAttributionCode(attributionResult.entityType) === "swap_like")) {
        return `Tool 88 attribution: ${label}. Swap-like signal; review before tracing through.`;
      }
      if (label) {
        return `Tool 88 attribution: ${label}.`;
      }
      return "";
    },

    applyAttributionToSelectedRows(baseRows, entry, selectedAddress, attributionResult) {
      const outputRows = (Array.isArray(baseRows) ? baseRows : []).map((row) => this.cloneRowPreservingValues(row));
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const targetColumnIndex = this.getTargetColumnIndex();
      const attributionColumnIndex = this.getAttributionColumnIndex();
      const label = this.cleanAttributionLabel(attributionResult?.displayLabel || attributionResult?.columnKValue || "");
      const note = this.buildAttributionAnnotationNote(attributionResult);

      if (!entry || !selectedKey || (!label && !note)) {
        return outputRows;
      }

      entry.rows.forEach(({ rowIndex }) => {
        const row = outputRows[rowIndex] || this.makeBlankRow();
        const rowAddressKey = this.normalizeAddressKey(row?.[targetColumnIndex]);
        if (rowAddressKey !== selectedKey) {
          return;
        }

        if (label) {
          const currentAttribution = this.toText(row[attributionColumnIndex]);
          if (!currentAttribution) {
            row[attributionColumnIndex] = label;
          } else if (!currentAttribution.toLowerCase().includes(label.toLowerCase())) {
            row[attributionColumnIndex] = `${currentAttribution}; ${label}`;
          }
        }

        if (note) {
          this.appendTool88NoteToRow(row, note);
        }
        outputRows[rowIndex] = row;
      });

      return outputRows;
    },

    normalizeSignalCode(value) {
      const normalized = this.toText(value).toLowerCase().replace(/[\s-]+/g, "_");
      const labels = {
        likely_simple_transfer: "likely_simple_transfer",
        simple_erc20_transfer: "likely_simple_transfer",
        simple_erc20_transfer_likely: "likely_simple_transfer",
        batch_token_withdrawal_likely: "likely_batch_transfer",
        evm_batch_token_withdrawal_likely: "likely_batch_transfer",
        erc20_batch_withdrawal_likely: "likely_batch_transfer",
        exchange_batch_withdrawal_likely: "likely_batch_transfer",
        batch_transfer_with_selected_recipient: "likely_batch_transfer",
        evm_batch_withdrawal_likely: "likely_batch_transfer",
        evm_internal_batch_withdrawal_likely: "likely_batch_transfer",
        native_internal_batch_withdrawal_likely: "likely_batch_transfer",
        internal_batch_withdrawal_likely: "likely_batch_transfer",
        batch_withdrawal_transfer_events: "likely_batch_transfer",
        internal_batch_withdrawal: "likely_batch_transfer",
        exchange_batch_withdrawal: "likely_batch_transfer",
        simple_trc20_transfer: "likely_simple_transfer",
        simple_trc20_transfer_likely: "likely_simple_transfer",
        simple_native_transfer_likely: "likely_simple_transfer",
        native_transfer_no_logs: "likely_simple_transfer",
        tron_simple_token_transfer_likely: "likely_simple_transfer",
        tron_native_transfer_likely: "likely_simple_transfer",
        tron_batch_withdrawal_likely: "likely_batch_transfer",
        tron_bridge_withdrawal_receive_likely: "likely_bridge_withdrawal_receive",
        bridge_withdrawal_receive_likely: "likely_bridge_withdrawal_receive",
        post_bridge_withdrawal_receive_likely: "likely_bridge_withdrawal_receive",
        likely_bridge_withdrawal_receive: "likely_bridge_withdrawal_receive",
        btc_no_memo: "likely_simple_transfer",
        btc_no_op_return_detected: "likely_simple_transfer",
        btc_batch_withdrawal_receive_likely: "likely_batch_transfer",
        btc_exchange_withdrawal_receive_likely: "likely_batch_transfer",
        btc_withdrawal_receive_likely: "likely_batch_transfer",
        no_op_return_detected: "likely_simple_transfer",
        complex_evm_event_logs: "complex_contract_activity",
        complex_tron_event_logs: "complex_contract_activity",
        tron_bridge_or_swap_marker: "complex_contract_activity",
        multi_transfer: "complex_contract_activity",
        multi_transfer_events: "complex_contract_activity",
        evm_batch_transfer_candidate: "likely_batch_transfer",
        evm_batch_transfer_with_selected_output: "likely_batch_transfer",
        batch_transfer_candidate: "likely_batch_transfer",
        batch_with_selected_output: "likely_batch_transfer",
        batch_transfer_withdrawal: "likely_batch_transfer",
        likely_batch_transfer: "likely_batch_transfer",
        complex_contract_activity: "complex_contract_activity",
        btc_op_return_memo: "btc_op_return_memo",
        btc_op_return_present: "btc_op_return_memo",
        op_return: "btc_op_return_memo",
        unavailable: "unavailable",
        unsupported: "unavailable",
        no_logs_found: "unavailable",
        invalid_transaction_hash: "unavailable",
      };
      return labels[normalized] || normalized || "unknown";
    },

    formatSignalClassification(value) {
      const normalized = this.normalizeSignalCode(value);
      const labels = {
        likely_simple_transfer: "Likely simple transfer",
        likely_batch_transfer: "Likely exchange batch withdrawal",
        likely_bridge_withdrawal_receive: "Likely bridge withdrawal receive",
        btc_no_memo: "BTC transaction without memo marker",
        complex_contract_activity: "Complex contract activity",
        btc_op_return_memo: "BTC OP_RETURN / memo",
        unavailable: "Signal unavailable",
        unknown: "No clear signal",
      };
      return labels[normalized] || this.toText(value) || "No clear signal";
    },

    normalizeEventTopic(value) {
      const text = this.toText(value).trim().toLowerCase();
      if (!text) return "";
      return text.startsWith("0x") ? text : `0x${text}`;
    },

    isTransferEventTopic(value) {
      return this.normalizeEventTopic(value) === ERC20_TRANSFER_TOPIC;
    },

    topicToEvmAddress(value) {
      const hex = this.normalizeEventTopic(value).replace(/^0x/i, "");
      if (!/^[0-9a-f]{40,}$/i.test(hex)) return "";
      return `0x${hex.slice(-40)}`.toLowerCase();
    },

    tool88TryParseJsonText(value = '') {
      const text = this.toText(value).trim();
      if (!text || (!text.startsWith('{') && !text.startsWith('['))) return null;
      try {
        const parsed = JSON.parse(text);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        return null;
      }
    },

    tool88ExtractPrimitiveProviderValue(value = '') {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
      if (Array.isArray(value)) {
        for (const item of value) {
          const primitive = this.tool88ExtractPrimitiveProviderValue(item);
          const text = this.toText(primitive);
          if (text && text !== '[object Object]') return primitive;
        }
        return '';
      }
      if (typeof value !== 'object') return '';
      const directKeys = [
        'value', 'data', 'raw', 'rawValue', 'raw_value', 'display', 'displayValue', 'display_value',
        'amount', 'quantity', 'tokenAmount', 'token_amount', 'tokenValue', 'token_value',
        'address', 'hash', 'symbol', 'tokenSymbol', 'token_symbol', 'name', 'text'
      ];
      for (const key of directKeys) {
        if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
        const next = value[key];
        if (next === null || next === undefined || typeof next === 'object') continue;
        const text = this.toText(next);
        if (text) return next;
      }
      return '';
    },

    collectTransferEventRows(value, depth = 0, seenObjects = null) {
      if (depth > 8 || value === null || value === undefined) return [];
      if (typeof value === 'string') {
        const parsed = this.tool88TryParseJsonText(value);
        return parsed ? this.collectTransferEventRows(parsed, depth + 1, seenObjects) : [];
      }
      if (Array.isArray(value)) {
        return value.flatMap((item) => this.collectTransferEventRows(item, depth + 1, seenObjects));
      }
      if (typeof value !== "object") return [];
      const seen = seenObjects || new WeakSet();
      if (seen.has(value)) return [];
      seen.add(value);

      const rows = [];
      const parsedRaw = this.tool88TryParseJsonText(value.raw_response || value.rawResponse || value.receipt_json || value.receiptJson || value.logs_json || value.logsJson || '');
      if (parsedRaw) rows.push(...this.collectTransferEventRows(parsedRaw, depth + 1, seen));

      const topicsRaw = Array.isArray(value.topics) ? value.topics : [value.topic0, value.topic_0, value.topic1, value.topic_1, value.topic2, value.topic_2, value.topic3, value.topic_3].filter(Boolean);
      const topic0 = topicsRaw.length ? this.normalizeEventTopic(topicsRaw[0]) : "";
      const decoded = [
        value.decoded,
        value.decoded_event,
        value.decodedEvent,
        value.decoded_log,
        value.decodedLog,
        value.event_data,
        value.eventData,
        value.parsed,
      ].find((candidate) => candidate && typeof candidate === 'object' && !Array.isArray(candidate)) || {};
      const args = [value.args, value.arguments, decoded.args, decoded.arguments]
        .find((candidate) => candidate && typeof candidate === 'object' && !Array.isArray(candidate)) || {};
      const params = [value.params, value.parameters, decoded.params, decoded.parameters, decoded.args, decoded.arguments]
        .find((candidate) => Array.isArray(candidate)) || [];
      const paramValue = (name) => {
        const target = this.toText(name).toLowerCase();
        const match = params.find((param) => this.toText(param?.name || param?.key || param?.param || param?.label || param?.paramName || param?.param_name).toLowerCase() === target);
        return this.tool88ExtractPrimitiveProviderValue(match?.value ?? match?.data ?? match?.raw ?? match?.rawValue ?? match?.raw_value ?? match ?? '');
      };
      const firstValue = (...values) => {
        for (const candidate of values) {
          if (candidate === null || candidate === undefined) continue;
          const primitive = this.tool88ExtractPrimitiveProviderValue(candidate);
          const text = this.toText(primitive);
          if (text && text !== '[object Object]') return primitive;
        }
        return '';
      };
      const eventNameSource = firstValue(value.event, value.eventName, value.event_name, value.name, value.logName, value.log_name, value.type, decoded.event, decoded.eventName, decoded.name);
      const nameText = this.toText(eventNameSource).toLowerCase();
      const looksTransfer = topic0 === ERC20_TRANSFER_TOPIC || /^transfer(?:\s*\(|$)/i.test(nameText) || /\berc\s*20\s+transfer\b/i.test(nameText) || /\btrc\s*20\s+transfer\b/i.test(nameText);
      const fromAddress = this.normalizeAddressKey(firstValue(
        value.from, value.from_address, value.fromAddress, value.sender, value.src, value.source,
        decoded.from, decoded.from_address, decoded.fromAddress, decoded.sender, decoded.source,
        args.from, args._from, args.from_address, args.sender, args.source,
        paramValue('from'), paramValue('_from'), paramValue('sender'), this.topicToEvmAddress(topicsRaw[1])
      ));
      const toAddress = this.normalizeAddressKey(firstValue(
        value.to, value.to_address, value.toAddress, value.receiver, value.recipient, value.destination, value.dst,
        decoded.to, decoded.to_address, decoded.toAddress, decoded.receiver, decoded.recipient, decoded.destination,
        args.to, args._to, args.to_address, args.receiver, args.recipient, args.destination,
        paramValue('to'), paramValue('_to'), paramValue('receiver'), paramValue('recipient'), this.topicToEvmAddress(topicsRaw[2])
      ));
      const tokenInfo = value.tokenInfo || value.token_info || value.token || value.asset || decoded.tokenInfo || decoded.token_info || decoded.token || {};
      const contractAddress = this.normalizeAddressKey(firstValue(
        value.contractAddress, value.contract_address, value.tokenAddress, value.token_address, value.token_contract, value.contract, value.address,
        decoded.contractAddress, decoded.contract_address, decoded.tokenAddress, decoded.token_address,
        args.contractAddress, args.contract_address, args.tokenAddress, args.token_address, args.token, args.contract,
        tokenInfo.contractAddress, tokenInfo.contract_address, tokenInfo.tokenId, tokenInfo.address,
        paramValue('contractAddress'), paramValue('contract_address'), paramValue('tokenAddress'), paramValue('token_address'), paramValue('token')
      ));
      const transferValue = firstValue(
        value.value, value.amount, value.quantity, value.quant, value.value_hex, value.valueHex, value.data_hex, value.dataHex,
        value.raw_value, value.rawValue, value.value_raw, value.valueRaw,
        value.token_value, value.tokenValue, value.token_amount, value.tokenAmount, value.raw_amount, value.rawAmount, value.amount_raw, value.amountRaw, value.data,
        decoded.value, decoded._value, decoded.amount, decoded.quantity, decoded.value_hex, decoded.valueHex, decoded.raw_value, decoded.rawValue,
        args.value, args._value, args.amount, args.quantity, args.quant, args.value_hex, args.valueHex, args.rawValue, args.raw_value,
        paramValue('value'), paramValue('_value'), paramValue('amount'), paramValue('quantity'), paramValue('tokens')
      );
      const tokenSignal = firstValue(
        value.tokenSymbol, value.token_symbol, value.symbol, value.asset_symbol, value.assetSymbol, value.token,
        decoded.tokenSymbol, decoded.token_symbol, decoded.symbol,
        args.tokenSymbol, args.token_symbol, args.symbol,
        tokenInfo.symbol, tokenInfo.tokenAbbr, tokenInfo.tokenSymbol, tokenInfo.token_symbol,
        paramValue('tokenSymbol'), paramValue('token_symbol'), paramValue('symbol')
      );
      const decimals = firstValue(
        value.tokenDecimals, value.token_decimals, value.decimals,
        decoded.tokenDecimals, decoded.token_decimals, decoded.decimals,
        tokenInfo.decimals, tokenInfo.tokenDecimal, tokenInfo.token_decimals,
        paramValue('tokenDecimals'), paramValue('token_decimals'), paramValue('decimals')
      );
      const hasTransferShape = (fromAddress || toAddress) && (this.toText(transferValue) || contractAddress || this.toText(tokenSignal));

      if (looksTransfer || hasTransferShape) {
        rows.push({
          contractAddress,
          fromAddress,
          toAddress,
          value: this.toText(transferValue),
          tokenSymbol: this.toText(tokenSignal),
          tokenDecimals: this.toText(decimals),
          raw: value,
        });
      }

      [
        value.selected_transfer, value.selectedTransfer, value.selected_output_transfer, value.selectedOutputTransfer, value.selected_transfer_log, value.selectedTransferLog,
        value.selected_token_transfer, value.selectedTokenTransfer, value.selected_output_details, value.selectedOutputDetails, value.selected_output_context, value.selectedOutputContext,
        value.matching_transfer, value.matchingTransfer, value.matched_output, value.matchedOutput, value.matched_transfer, value.matchedTransfer, value.selected_output, value.selectedOutput,
        value.decoded, value.decoded_event, value.decodedEvent, value.decoded_log, value.decodedLog, value.event_data, value.eventData, value.parsed,
        value.args, value.arguments, value.params, value.parameters,
        value.logs, value.event_logs, value.eventLogs, value.events, value.decoded_events, value.decodedEvents, value.decoded_logs, value.decodedLogs,
        value.transfer_logs, value.transferLogs, value.transfers, value.token_transfers, value.tokenTransfers, value.erc20_transfers, value.erc20Transfers,
        value.erc_20_transfers, value.erc20TransferLogs, value.tokenTransferEvents, value.trc20_transfers, value.trc20Transfers,
        value.receipt, value.result, value.raw, value.data,
      ].forEach((nested) => {
        if (nested && nested !== value) rows.push(...this.collectTransferEventRows(nested, depth + 1, seen));
      });
      if (depth < 4) {
        Object.entries(value).forEach(([key, nested]) => {
          if (!nested || nested === value || typeof nested !== 'object') return;
          const keyText = this.toText(key).toLowerCase();
          if (/(log|event|transfer|receipt|token|selected|output|result|raw|payload|response|signal|decoded)/i.test(keyText) || Array.isArray(nested)) {
            rows.push(...this.collectTransferEventRows(nested, depth + 1, seen));
          }
        });
      }

      const uniqueRows = new Set();
      return rows.filter((row) => {
        const key = [row.contractAddress, row.fromAddress, row.toAddress, row.value, row.tokenSymbol, row.tokenDecimals].join("|");
        if (uniqueRows.has(key)) return false;
        uniqueRows.add(key);
        return true;
      });
    },

    detectBatchWithdrawalSignal({ raw = {}, classification = "", logCount = null, transferLogCount = null, nonTransferLogCount = null, selectedAddress = "", eventNameText = "" } = {}) {
      if (this.tool88AnyFlagIsTrue(
        raw.is_batch_withdrawal_candidate,
        raw.isBatchWithdrawalCandidate,
        raw.batch_withdrawal_candidate,
        raw.batchWithdrawalCandidate,
        raw.is_batch_withdrawal,
        raw.isBatchWithdrawal,
        raw.batch_withdrawal_likely,
        raw.batchWithdrawalLikely,
        raw.exchange_batch_withdrawal_likely
      )) {
        return { isBatchWithdrawalCandidate: true, confidence: "high", evidenceLabel: raw.batch_withdrawal_evidence || raw.batchWithdrawalEvidence || "Provider marked this as a batch withdrawal candidate." };
      }

      if (this.tool88AnyFlagIsTrue(raw.has_swap_or_bridge_signal, raw.hasSwapOrBridgeSignal)) {
        return { isBatchWithdrawalCandidate: false };
      }

      const family = this.toText(raw.network_family || raw.chain_family || raw.chainFamily).toLowerCase();
      if (family && !["evm", "tron"].includes(family)) {
        return { isBatchWithdrawalCandidate: false };
      }

      const normalizedClass = this.normalizeSignalCode(classification);
      const transferCount = Number(transferLogCount);
      const totalLogs = Number(logCount);
      const otherLogs = Number(nonTransferLogCount);
      const allLogsAreTransfers = Number.isFinite(transferCount) && transferCount > 1 && (!Number.isFinite(otherLogs) || otherLogs === 0) && (!Number.isFinite(totalLogs) || totalLogs === transferCount);
      if (!allLogsAreTransfers && !["batch_transfer_withdrawal", "likely_batch_transfer", "evm_batch_withdrawal_likely"].includes(normalizedClass)) {
        return { isBatchWithdrawalCandidate: false };
      }

      const combinedText = `${eventNameText} ${this.getSignalEventNameText(raw)} ${this.toText(raw.method || raw.methodName || raw.functionName)}`.toLowerCase();
      if (this.hasBridgeEventNameSignal(combinedText) || this.hasSwapEventNameSignal(combinedText) || /\b(swap|bridge|router|depositforburn|redeem|mint|burn)\b/i.test(combinedText)) {
        return { isBatchWithdrawalCandidate: false };
      }

      const transfers = this.collectTransferEventRows([raw, ...this.collectCaseBuddySignalPayloadObjects(raw)]);
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const selectedIsRecipient = selectedKey && transfers.some((row) => row.toAddress === selectedKey);
      const selectedIsSender = selectedKey && transfers.some((row) => row.fromAddress === selectedKey);
      const fromCounts = new Map();
      const toSet = new Set();
      const contractSet = new Set();
      transfers.forEach((row) => {
        if (row.fromAddress) fromCounts.set(row.fromAddress, (fromCounts.get(row.fromAddress) || 0) + 1);
        if (row.toAddress) toSet.add(row.toAddress);
        if (row.contractAddress) contractSet.add(row.contractAddress);
      });
      const maxSameSenderTransfers = Math.max(0, ...Array.from(fromCounts.values()));
      const decodedEvidenceStrong = transfers.length > 1
        ? Boolean(selectedIsRecipient && !selectedIsSender && (maxSameSenderTransfers >= 2 || toSet.size >= 2))
        : true;

      if (!decodedEvidenceStrong) {
        return { isBatchWithdrawalCandidate: false };
      }

      const detailParts = [];
      detailParts.push(`${this.formatInteger(transferCount)} Transfer logs and no non-transfer logs`);
      if (transfers.length) {
        if (maxSameSenderTransfers >= 2) detailParts.push(`${this.formatInteger(maxSameSenderTransfers)} transfers share the same sender`);
        if (toSet.size >= 2) detailParts.push(`${this.formatInteger(toSet.size)} recipient addresses`);
        if (contractSet.size >= 2) detailParts.push(`${this.formatInteger(contractSet.size)} token contracts`);
        if (selectedIsRecipient) detailParts.push("selected target appears as a token recipient");
      } else {
        detailParts.push("decoded transfer rows were not returned by the provider response");
      }

      return {
        isBatchWithdrawalCandidate: true,
        confidence: transfers.length ? "high" : "medium",
        transferRows: transfers,
        repeatedSenderCount: maxSameSenderTransfers,
        recipientCount: toSet.size || null,
        contractCount: contractSet.size || null,
        selectedIsRecipient,
        evidenceLabel: detailParts.join("; "),
      };
    },

    normalizeTransactionSignalResult(result, entry, selectedAddress) {
      const top = result && typeof result === 'object' ? result : {};
      const root = top.result && typeof top.result === 'object' && !Array.isArray(top.result) ? top.result : top;
      const nestedRaw = root.raw && typeof root.raw === 'object' && !Array.isArray(root.raw) ? root.raw : {};
      const signalPayloadObjects = this.collectCaseBuddySignalPayloadObjects(top);
      const payloadPrimitives = {};
      signalPayloadObjects.forEach((object) => {
        if (!object || typeof object !== 'object' || Array.isArray(object)) return;
        Object.entries(object).forEach(([key, value]) => {
          if (payloadPrimitives[key] !== undefined || value === null || value === undefined || typeof value === 'object') return;
          payloadPrimitives[key] = value;
        });
      });
      const raw = { ...payloadPrimitives, ...top, ...nestedRaw, ...root };
      let classification = this.normalizeSignalCode(
        raw.classification || raw.signal_classification || raw.signalClassification || raw.result_type || ""
      );
      let adviceStatus = this.toText(raw.advice_status || raw.adviceStatus || raw.check_status || raw.checkStatus || "neutral") || "neutral";
      let confidence = this.toText(raw.simple_transfer_confidence || raw.simpleTransferConfidence || "unknown").toLowerCase() || "unknown";
      const evidenceParts = [];
      const preDecodedTransferRows = this.collectTransferEventRows([raw, ...signalPayloadObjects]);
      const textShowsSingleTransfer = this.tool88SignalTextShowsSingleTransfer(raw);
      let logCount = this.tool88FirstFiniteSignalNumber(
        raw.log_count,
        raw.logCount,
        raw.logs_count,
        raw.logsCount,
        raw.total_log_count,
        raw.totalLogCount,
        raw.receipt_log_count,
        raw.receiptLogCount,
        raw.event_log_count,
        raw.eventLogCount,
        Array.isArray(raw.logs) ? raw.logs.length : null,
        textShowsSingleTransfer ? 1 : null
      );
      let transferLogCount = this.tool88FirstFiniteSignalNumber(
        raw.transfer_log_count,
        raw.transferLogCount,
        raw.transfer_logs_count,
        raw.transferLogsCount,
        raw.erc20_transfer_log_count,
        raw.erc20TransferLogCount,
        raw.trc20_transfer_log_count,
        raw.trc20TransferLogCount,
        raw.token_transfer_log_count,
        raw.tokenTransferLogCount,
        raw.transfer_event_count,
        raw.transferEventCount,
        preDecodedTransferRows.length === 1 ? 1 : null,
        textShowsSingleTransfer ? 1 : null
      );
      let nonTransferLogCount = this.tool88FirstFiniteSignalNumber(
        raw.non_transfer_log_count,
        raw.nonTransferLogCount,
        raw.non_transfer_logs_count,
        raw.nonTransferLogsCount,
        raw.other_log_count,
        raw.otherLogCount,
        raw.other_logs_count,
        raw.otherLogsCount,
        raw.non_transfer_event_count,
        raw.nonTransferEventCount,
        textShowsSingleTransfer ? 0 : null
      );
      if (textShowsSingleTransfer) {
        logCount = Number.isFinite(Number(logCount)) ? Number(logCount) : 1;
        transferLogCount = Number.isFinite(Number(transferLogCount)) ? Number(transferLogCount) : 1;
        nonTransferLogCount = Number.isFinite(Number(nonTransferLogCount)) ? Number(nonTransferLogCount) : 0;
      }
      if (Number.isFinite(logCount)) evidenceParts.push(`${logCount} log${logCount === 1 ? "" : "s"}`);
      if (Number.isFinite(transferLogCount)) evidenceParts.push(`${transferLogCount} transfer log${transferLogCount === 1 ? "" : "s"}`);
      if (Number.isFinite(nonTransferLogCount) && nonTransferLogCount > 0) evidenceParts.push(`${nonTransferLogCount} non-transfer log${nonTransferLogCount === 1 ? "" : "s"}`);
      if (raw.memo_text || raw.memoText) evidenceParts.push("memo found");
      const btcInputCount = Number(raw.btc_input_count ?? raw.btcInputCount ?? raw.input_count ?? raw.inputCount);
      const btcOutputCount = Number(raw.btc_output_count ?? raw.btcOutputCount ?? raw.output_count ?? raw.outputCount);
      if (Number.isFinite(btcInputCount) && Number.isFinite(btcOutputCount) && btcOutputCount > 0) evidenceParts.push(`${btcInputCount} BTC input${btcInputCount === 1 ? "" : "s"} / ${btcOutputCount} output${btcOutputCount === 1 ? "" : "s"}`);
      if (raw.selected_output_btc || raw.selectedOutputBtc) evidenceParts.push(`selected BTC output ${raw.selected_output_btc || raw.selectedOutputBtc}`);
      const rawBatchCandidate = raw.batch_transfer_candidate || raw.batchTransferCandidate || null;
      const rawBatchCandidateIsTrue = Boolean(
        rawBatchCandidate &&
          this.tool88AnyFlagIsTrue(rawBatchCandidate.is_batch_transfer, rawBatchCandidate.isBatchTransfer, rawBatchCandidate.is_batch, rawBatchCandidate.isBatch)
      );
      if (this.tool88AnyFlagIsTrue(raw.batch_withdrawal_candidate, raw.batchWithdrawalCandidate, raw.is_batch_transfer_candidate, raw.isBatchTransferCandidate) || rawBatchCandidateIsTrue) evidenceParts.push("batch-withdrawal pattern");
      const selectedAddressIsTransferRecipientFlag = this.tool88AnyFlagIsTrue(raw.selected_address_is_transfer_recipient, raw.selectedAddressIsTransferRecipient, raw.selected_transfer_matched, raw.selectedTransferMatched, raw.selected_target_matched, raw.selectedTargetMatched, raw.selected_address_is_output, raw.selectedAddressIsOutput, rawBatchCandidate?.selected_target_matched, rawBatchCandidate?.selectedTargetMatched);
      if (selectedAddressIsTransferRecipientFlag) evidenceParts.push("selected address is a Transfer recipient");
      if (raw.op_return_count || raw.opReturnCount) evidenceParts.push(`${raw.op_return_count || raw.opReturnCount} OP_RETURN output(s)`);
      if (raw.simple_attribution_term_matched || raw.simpleAttributionTermMatched) evidenceParts.push("trace-through attribution term");
      const isBatchWithdrawalSignal = Boolean(
        this.tool88AnyFlagIsTrue(
          raw.is_batch_withdrawal,
          raw.isBatchWithdrawal,
          raw.batch_withdrawal_likely,
          raw.batchWithdrawalLikely,
          raw.exchange_batch_withdrawal_likely,
          raw.internal_batch_withdrawal_likely,
          raw.internalBatchWithdrawalLikely,
          raw.evm_internal_batch_withdrawal_likely,
          raw.evmInternalBatchWithdrawalLikely,
          raw.tron_batch_withdrawal_likely,
          raw.tronBatchWithdrawalLikely,
          raw.btc_batch_withdrawal_receive_likely,
          raw.btcBatchWithdrawalReceiveLikely,
          raw.btc_withdrawal_receive_likely,
          raw.btcWithdrawalReceiveLikely
        ) ||
          classification === "batch_token_withdrawal_likely" || classification === "evm_internal_batch_withdrawal_likely" || classification === "tron_batch_withdrawal_likely" || classification === "btc_batch_withdrawal_receive_likely" || classification === "btc_withdrawal_receive_likely" || classification === "likely_batch_transfer"
      );
      if (isBatchWithdrawalSignal) evidenceParts.push("batch token withdrawal pattern");
      const batchRecipientCount = Number(raw.batch_unique_recipient_count ?? raw.batchUniqueRecipientCount ?? raw.internal_unique_recipient_count ?? raw.internalUniqueRecipientCount ?? raw.unique_recipient_count ?? raw.uniqueRecipientCount ?? raw.batch_unique_recipient_count ?? raw.batchUniqueRecipientCount);
      if (Number.isFinite(batchRecipientCount) && batchRecipientCount > 0) evidenceParts.push(`${batchRecipientCount} batch recipient${batchRecipientCount === 1 ? "" : "s"}`);
      const eventNameText = this.getSignalEventNameText(raw);
      if (eventNameText) evidenceParts.push(`event names: ${eventNameText}`);
      const batchWithdrawal = this.detectBatchWithdrawalSignal({
        raw,
        classification,
        logCount: Number.isFinite(logCount) ? logCount : null,
        transferLogCount: Number.isFinite(transferLogCount) ? transferLogCount : null,
        nonTransferLogCount: Number.isFinite(nonTransferLogCount) ? nonTransferLogCount : null,
        selectedAddress,
        eventNameText,
      });
      if (batchWithdrawal.isBatchWithdrawalCandidate && classification === "complex_contract_activity") {
        classification = "likely_batch_transfer";
        if (["warn", "neutral", ""].includes(this.normalizeCheckStatus(adviceStatus))) {
          adviceStatus = "ok";
        }
        if (["low", "unknown", ""].includes(confidence)) {
          confidence = batchWithdrawal.confidence || "medium";
        }
        if (batchWithdrawal.evidenceLabel) evidenceParts.push(batchWithdrawal.evidenceLabel);
      }

      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const transferRows = this.collectTransferEventRows([raw, ...signalPayloadObjects]);
      const selectedIsSingleTransferRecipient = selectedKey && transferRows.some((row) => row.toAddress === selectedKey);
      const eventNamesLookBridgeOrSwap = this.hasBridgeEventNameSignal(eventNameText) || this.hasSwapEventNameSignal(eventNameText);
      const hasSwapOrBridgeSignalFlag = this.tool88AnyFlagIsTrue(raw.has_swap_or_bridge_signal, raw.hasSwapOrBridgeSignal);
      const singleTransferNoOtherLogs = Number.isFinite(transferLogCount) && transferLogCount === 1 && (!Number.isFinite(nonTransferLogCount) || nonTransferLogCount === 0) && (!Number.isFinite(logCount) || logCount === 1);
      const selectedOnlyTransfer = (singleTransferNoOtherLogs || textShowsSingleTransfer) && !eventNamesLookBridgeOrSwap && !batchWithdrawal.isBatchWithdrawalCandidate && (selectedIsSingleTransferRecipient || transferRows.length <= 1 || selectedAddressIsTransferRecipientFlag || textShowsSingleTransfer);
      if (selectedOnlyTransfer && classification !== "likely_simple_transfer") {
        classification = "likely_simple_transfer";
        adviceStatus = "ok";
        confidence = selectedIsSingleTransferRecipient ? "high" : "medium";
        evidenceParts.push(selectedIsSingleTransferRecipient ? "selected address is the single Transfer recipient" : "single Transfer log and no other event logs");
      }

      const rawDetailText = this.toText(
        raw.detail_text || raw.detailText || raw.evidence_summary || raw.evidenceSummary || raw.message || ""
      );
      const detailText = batchWithdrawal.isBatchWithdrawalCandidate
        ? `Likely batch withdrawal/distribution pattern. ${batchWithdrawal.evidenceLabel || "Multiple Transfer events were present without non-transfer event logs."}`
        : selectedOnlyTransfer
          ? "The receipt has one Transfer log and no non-transfer logs for the selected output context."
          : rawDetailText;
      const rawRecommendation = this.toText(raw.recommendation || raw.guidance || raw.decision_guidance || "");
      const batchEvidenceText = batchWithdrawal.evidenceLabel || evidenceParts.join("; ") || "multiple Transfer events with no non-Transfer event logs";
      const recommendation = batchWithdrawal.isBatchWithdrawalCandidate
        ? "Multiple token Transfer events appear to be a batch withdrawal/distribution rather than a swap or bridge. If the selected To Address is the intended token recipient and the receiving address has no path-ending attribution, Tool 88 can trace through."
        : selectedOnlyTransfer
          ? "The selected output looks like an ordinary token transfer, so Tool 88 can trace through automatically if count and attribution checks also allow it."
          : rawRecommendation || "Review transaction-level signals before deciding whether this is a path ending or a trace-through.";

      return {
        txHash: raw.tx_hash || raw.txHash || entry?.txHash || "",
        address: raw.address || selectedAddress || "",
        chainFamily: raw.network_family || raw.chain_family || raw.chainFamily || "",
        provider: raw.provider || "",
        providerLabel: raw.provider_label || raw.providerLabel || raw.source || "",
        route: raw.route || raw.method || "",
        source: raw.source || "transaction_signal_check",
        classification,
        classificationLabel: this.formatSignalClassification(classification),
        summaryLabel: batchWithdrawal.isBatchWithdrawalCandidate || selectedOnlyTransfer ? this.formatSignalClassification(classification) : (raw.summary_label || raw.summaryLabel || this.formatSignalClassification(classification)),
        checkTitle: raw.check_title || raw.checkTitle || this.defaultSignalCheckTitle(raw, classification),
        checkStatus: this.normalizeCheckStatus(adviceStatus),
        adviceStatus: this.normalizeCheckStatus(adviceStatus),
        statusLabel: batchWithdrawal.isBatchWithdrawalCandidate || selectedOnlyTransfer ? this.defaultSignalStatusLabel(classification, adviceStatus) : (raw.status_label || raw.statusLabel || this.defaultSignalStatusLabel(classification, adviceStatus)),
        simpleTransferConfidence: confidence,
        simpleTransferConfidenceLabel: confidence === "high" ? "High" : confidence === "medium" ? "Medium" : confidence === "low" ? "Low" : "Unknown",
        isSimpleTransfer: Boolean(selectedOnlyTransfer || this.tool88AnyFlagIsTrue(raw.is_simple_transfer, raw.isSimpleTransfer)),
        hasSwapOrBridgeSignal: selectedOnlyTransfer ? false : (!batchWithdrawal.isBatchWithdrawalCandidate && hasSwapOrBridgeSignalFlag),
        isBatchWithdrawalCandidate: Boolean(batchWithdrawal.isBatchWithdrawalCandidate || this.tool88AnyFlagIsTrue(raw.batch_withdrawal_candidate, raw.batchWithdrawalCandidate, raw.is_batch_transfer_candidate, raw.isBatchTransferCandidate, raw.batch_transfer_candidate?.is_batch_transfer, raw.batchTransferCandidate?.isBatchTransfer)),
        isBatchTransferCandidate: Boolean(batchWithdrawal.isBatchWithdrawalCandidate || this.tool88AnyFlagIsTrue(raw.is_batch_transfer_candidate, raw.isBatchTransferCandidate, raw.batch_transfer_candidate?.is_batch_transfer, raw.batchTransferCandidate?.isBatchTransfer)),
        batchWithdrawalEvidence: batchWithdrawal.evidenceLabel || raw.batch_withdrawal_evidence || raw.batchWithdrawalEvidence || "",
        selectedAddressIsTransferRecipient: Boolean(batchWithdrawal.selectedIsRecipient || selectedAddressIsTransferRecipientFlag),
        selectedTargetMatchedBatchTransfer: Boolean(batchWithdrawal.selectedIsRecipient || selectedAddressIsTransferRecipientFlag),
        batchTransferCandidate: raw.batch_transfer_candidate || raw.batchTransferCandidate || null,
        dominantTransferSender: raw.dominant_transfer_sender || raw.dominantTransferSender || raw.batch_transfer_candidate?.common_sender || raw.batchTransferCandidate?.commonSender || "",
        hasMemo: Boolean(raw.has_memo ?? raw.hasMemo),
        memoText: raw.memo_text || raw.memoText || "",
        eventNameText,
        hasBridgeEventSignal: this.hasBridgeEventNameSignal(eventNameText),
        hasSwapEventSignal: this.hasSwapEventNameSignal(eventNameText),
        logCount: Number.isFinite(logCount) ? logCount : null,
        transferLogCount: Number.isFinite(transferLogCount) ? transferLogCount : null,
        nonTransferLogCount: Number.isFinite(nonTransferLogCount) ? nonTransferLogCount : null,
        evidenceSummary: batchWithdrawal.isBatchWithdrawalCandidate ? batchEvidenceText : (selectedOnlyTransfer ? evidenceParts.join("; ") : (raw.evidence_summary || raw.evidenceSummary || evidenceParts.join("; "))),
        evidenceLabel: batchWithdrawal.isBatchWithdrawalCandidate ? batchEvidenceText : (selectedOnlyTransfer ? (evidenceParts.join("; ") || "single Transfer log and no other event logs") : (raw.evidence_label || raw.evidenceLabel || evidenceParts.join("; ") || "No provider evidence returned")),
        detailText,
        recommendation,
        warnings: Array.isArray(raw.warnings) ? raw.warnings : [],
        raw,
      };
    },

    normalizeCheckStatus(value) {
      const status = this.toText(value).toLowerCase();
      if (["ok", "warn", "block", "neutral"].includes(status)) return status;
      if (["simple", "success", "pass", "likely_simple_transfer", "likely_batch_transfer", "evm_batch_withdrawal_likely", "batch_withdrawal_transfer_events"].includes(status)) return "ok";
      if (["review", "caution", "complex", "memo"].includes(status)) return "warn";
      if (["blocked", "stop"].includes(status)) return "block";
      return "neutral";
    },

    defaultSignalCheckTitle(raw, classification) {
      const family = this.toText(raw.network_family || raw.chain_family || "").toLowerCase();
      if (family === "btc" || classification === "btc_op_return_memo") return "BTC memo / OP_RETURN check";
      if (family === "tron") return "Tron/TRC20 event-log check";
      if (family === "evm") return "EVM event-log check";
      return "Transaction signal check";
    },

    defaultSignalStatusLabel(classification, adviceStatus) {
      if (classification === "likely_simple_transfer") return "Likely simple";
      if (classification === "likely_batch_transfer") return "Batch withdrawal";
      if (classification === "btc_op_return_memo") return "Memo found";
      if (classification === "complex_contract_activity") return "Review";
      if (adviceStatus === "block") return "Blocked";
      if (adviceStatus === "warn") return "Review";
      return "Checked";
    },

    buildTransactionSignalAnnotationNote(signalResult) {
      if (!signalResult) return "";
      const suggestedNote = this.toText(signalResult.raw?.suggested_column_j_note || signalResult.raw?.suggestedColumnJNote || signalResult.suggestedColumnJNote || "");
      if (suggestedNote) return suggestedNote;
      const classification = this.normalizeSignalCode(signalResult.classification);
      if (classification === "likely_simple_transfer") {
        return `Tool 88 signal check: ${signalResult.classificationLabel}; ${signalResult.evidenceSummary || signalResult.evidenceLabel || "provider details looked simple"}.`;
      }
      if (classification === "likely_batch_transfer" || this.isLikelyBatchWithdrawalSignal(signalResult)) {
        return `Tool 88 signal check: likely exchange/service batch withdrawal or batch token payout (${this.getBatchWithdrawalSummary(signalResult)}). Selected output can be traced if the receiving address is not itself a path-ending entity.`;
      }
      if (classification === "btc_op_return_memo") {
        return `Tool 88 signal check: BTC OP_RETURN/memo found${signalResult.memoText ? `: ${signalResult.memoText}` : ""}. Review for bridge/swap context before ordinary hop logic.`;
      }
      if (classification === "complex_contract_activity") {
        return `Tool 88 signal check: complex event/log activity detected. ${signalResult.evidenceSummary || "Review logs before tracing through."}`;
      }
      if (signalResult.detailText || signalResult.recommendation) {
        return `Tool 88 signal check: ${signalResult.detailText || signalResult.recommendation}`;
      }
      return "";
    },

    applySignalCheckToSelectedRows(baseRows, entry, selectedAddress, signalResult) {
      const outputRows = (Array.isArray(baseRows) ? baseRows : []).map((row) => this.cloneRowPreservingValues(row));
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const targetColumnIndex = this.getTargetColumnIndex();
      const note = this.buildTransactionSignalAnnotationNote(signalResult);
      if (!entry || !selectedKey || !note) return outputRows;

      entry.rows.forEach(({ rowIndex }) => {
        const row = outputRows[rowIndex] || this.makeBlankRow();
        const rowAddressKey = this.normalizeAddressKey(row?.[targetColumnIndex]);
        if (rowAddressKey !== selectedKey) return;
        this.appendTool88NoteToRow(row, note);
        outputRows[rowIndex] = row;
      });
      return outputRows;
    },

    buildLocalTransactionSignalFallback(entry, selectedAddress, message) {
      const countContext = this.inferCountRequestContext(entry, selectedAddress);
      return this.normalizeTransactionSignalResult({
        status: "success",
        tx_hash: entry?.txHash || "",
        address: selectedAddress,
        network_family: countContext.network_family || "unsupported",
        classification: "unavailable",
        check_status: "neutral",
        status_label: "Unavailable",
        simple_transfer_confidence: "unknown",
        evidence_summary: message || "Transaction-signal lookup did not complete.",
        recommendation: "Continue with the count and attribution results, but do not treat the transaction as confirmed simple/complex based on event logs from this pass.",
        source: "local_signal_fallback",
        warnings: message ? [message] : [],
      }, entry, selectedAddress);
    },

    buildMockTransactionSignalResult(entry, selectedAddress) {
      const entryText = this.collectEntrySearchText(entry);
      const countContext = this.inferCountRequestContext(entry, selectedAddress);
      const family = this.toText(countContext.network_family).toLowerCase();
      if (family === "btc" || /op[_\s-]?return|memo|message/i.test(entryText)) {
        return {
          status: "success",
          tx_hash: entry?.txHash || "mock-btc-tx",
          address: selectedAddress,
          network_family: "btc",
          classification: /op[_\s-]?return|memo|message/i.test(entryText) ? "btc_op_return_memo" : "btc_no_memo",
          check_status: /op[_\s-]?return|memo|message/i.test(entryText) ? "warn" : "ok",
          status_label: /op[_\s-]?return|memo|message/i.test(entryText) ? "Memo found" : "No memo",
          simple_transfer_confidence: /op[_\s-]?return|memo|message/i.test(entryText) ? "low" : "medium",
          op_return_count: /op[_\s-]?return|memo|message/i.test(entryText) ? 1 : 0,
          memo_text: /op[_\s-]?return|memo|message/i.test(entryText) ? "SAMPLE-MEMO" : "",
          evidence_summary: /op[_\s-]?return|memo|message/i.test(entryText) ? "Mock OP_RETURN memo found." : "Mock BTC transaction has no OP_RETURN output.",
          recommendation: /op[_\s-]?return|memo|message/i.test(entryText) ? "Review this as possible bridge/swap memo context before tracing through." : "No memo marker was found in this mock BTC transaction.",
          source: "mock_transaction_signal",
        };
      }
      const complex = /bridge|swap|wormhole|li\.fi|transit|complex/i.test(entryText);
      return {
        status: "success",
        tx_hash: entry?.txHash || "mock-evm-tx",
        address: selectedAddress,
        network_family: family || "evm",
        classification: complex ? "complex_evm_event_logs" : "likely_simple_transfer",
        check_status: complex ? "warn" : "ok",
        status_label: complex ? "Review logs" : "Likely simple",
        simple_transfer_confidence: complex ? "low" : "high",
        log_count: complex ? 3 : 1,
        transfer_log_count: 1,
        non_transfer_log_count: complex ? 2 : 0,
        evidence_summary: complex ? "Mock receipt has one Transfer log plus additional non-transfer logs." : "Mock receipt has exactly one ERC20 Transfer log and no other logs.",
        recommendation: complex ? "Review as possible swap/bridge/contract interaction before ordinary hop logic." : "This mock EVM receipt looks like a simple ERC20 transfer.",
        source: "mock_transaction_signal",
      };
    },

    async lookupTransactionSignalData(entry, selectedAddress) {
      if (this.useMockBackend) {
        return this.buildMockTransactionSignalResult(entry, selectedAddress);
      }
      const countContext = this.inferCountRequestContext(entry, selectedAddress);
      const selectedAddressRequired = this.traceDirection === 'reverse' || !!this.toText(selectedAddress);
      const allowSingleTransferTargetInference = this.caseBuddyEmbedded && this.traceDirection !== 'reverse' && !this.toText(selectedAddress);
      const attribution = this.attributionLookupResult || {};
      const signalExpectedTransactionCount = Number(
        this.countLookupResult?.transactionCount ??
          this.countLookupResult?.transaction_count ??
          this.countLookupResult?.count ??
          -1
      );
      const signalKnownCount = Number.isFinite(signalExpectedTransactionCount) ? Math.max(-1, Math.trunc(signalExpectedTransactionCount)) : -1;
      const body = {
        action: "transaction_signal_check",
        tx_hash: entry?.txHash || "",
        address: selectedAddress,
        selected_address: selectedAddress,
        selected_to_address: this.traceDirection === "reverse" ? "" : selectedAddress,
        selected_from_address: this.traceDirection === "reverse" ? selectedAddress : "",
        selected_target_address: selectedAddress,
        target_address: selectedAddress,
        selected_output_address: selectedAddress,
        selected_recipient_address: this.traceDirection === "reverse" ? "" : selectedAddress,
        selected_sender_address: this.traceDirection === "reverse" ? selectedAddress : "",
        selected_output_recipient: this.traceDirection === "reverse" ? "" : selectedAddress,
        selected_output_to: this.traceDirection === "reverse" ? "" : selectedAddress,
        selected_output_to_address: this.traceDirection === "reverse" ? "" : selectedAddress,
        selected_output_sender: this.traceDirection === "reverse" ? selectedAddress : "",
        selected_output_from: this.traceDirection === "reverse" ? selectedAddress : "",
        selected_output_from_address: this.traceDirection === "reverse" ? selectedAddress : "",
        selected_transfer_must_match_to_address: this.caseBuddyEmbedded && this.traceDirection !== "reverse" && selectedAddressRequired ? 1 : 0,
        allow_single_transfer_target_inference: allowSingleTransferTargetInference ? 1 : 0,
        infer_target_from_single_transfer: allowSingleTransferTargetInference ? 1 : 0,
        selected_output_address_optional: allowSingleTransferTargetInference ? 1 : 0,
        allow_missing_selected_address: allowSingleTransferTargetInference ? 1 : 0,
        direction: this.traceDirection,
        chain_id: countContext.chain_id || entry?.chainId || "",
        chain_name: countContext.chain_name || entry?.chainName || "",
        chain_label: countContext.chain_label || "",
        network_family: countContext.network_family || "",
        provider_hint: countContext.provider_hint || "",
        count_kind: countContext.count_kind || "auto",
        token_symbol: countContext.token_symbol || this.getSelectedTokenSymbol(entry, selectedAddress) || "",
        token_contract: countContext.token_contract || countContext.contract_address || "",
        contract_address: countContext.token_contract || countContext.contract_address || "",
        token_decimals: countContext.token_decimals ?? null,
        native_symbol: countContext.native_symbol || "",
        moralis_chain: countContext.moralis_chain || "",
        sample_mode: this.isSampleDataLoaded ? 1 : 0,
        case_buddy_embedded: this.caseBuddyEmbedded ? 1 : 0,
        force_receipt_lookup: this.caseBuddyEmbedded ? 1 : 0,
        force_selected_output_lookup: this.caseBuddyEmbedded ? 1 : 0,
        force_selected_transfer_lookup: this.caseBuddyEmbedded ? 1 : 0,
        require_selected_output_details: this.caseBuddyEmbedded ? 1 : 0,
        require_selected_output_amount: this.caseBuddyEmbedded ? 1 : 0,
        require_selected_output_token: this.caseBuddyEmbedded ? 1 : 0,
        return_selected_transfer: this.caseBuddyEmbedded ? 1 : 0,
        return_selected_output: this.caseBuddyEmbedded ? 1 : 0,
        return_selected_output_details: this.caseBuddyEmbedded ? 1 : 0,
        require_selected_transfer: this.caseBuddyEmbedded ? 1 : 0,
        require_selected_transfer_amount: this.caseBuddyEmbedded ? 1 : 0,
        infer_selected_output_details: this.caseBuddyEmbedded ? 1 : 0,
        infer_selected_transfer_details: this.caseBuddyEmbedded ? 1 : 0,
        resolve_selected_output: this.caseBuddyEmbedded ? 1 : 0,
        resolve_selected_transfer: this.caseBuddyEmbedded ? 1 : 0,
        selected_output_amount_required: this.caseBuddyEmbedded ? 1 : 0,
        selected_output_token_required: this.caseBuddyEmbedded ? 1 : 0,
        include_receipt: this.caseBuddyEmbedded ? 1 : 0,
        return_receipt: this.caseBuddyEmbedded ? 1 : 0,
        include_raw_response: this.caseBuddyEmbedded ? 1 : 0,
        return_raw_response: this.caseBuddyEmbedded ? 1 : 0,
        include_transfer_logs: this.caseBuddyEmbedded ? 1 : 0,
        include_raw_logs: this.caseBuddyEmbedded ? 1 : 0,
        include_token_transfers: this.caseBuddyEmbedded ? 1 : 0,
        return_token_transfers: this.caseBuddyEmbedded ? 1 : 0,
        include_logs: this.caseBuddyEmbedded ? 1 : 0,
        include_decoded_logs: this.caseBuddyEmbedded ? 1 : 0,
        return_logs: this.caseBuddyEmbedded ? 1 : 0,
        return_decoded_logs: this.caseBuddyEmbedded ? 1 : 0,
        decode_transfer_logs: this.caseBuddyEmbedded ? 1 : 0,
        include_token_metadata: this.caseBuddyEmbedded ? 1 : 0,
        require_token_metadata: this.caseBuddyEmbedded ? 1 : 0,
        expected_transaction_count: signalKnownCount,
        expected_count: signalKnownCount,
        transaction_count: signalKnownCount,
        known_count: signalKnownCount,
        count_context: {
          ...countContext,
          expected_transaction_count: signalKnownCount,
          expected_count: signalKnownCount,
          transaction_count: signalKnownCount,
          known_count: signalKnownCount,
        },
        attribution_context: {
          display_label: attribution.displayLabel || attribution.display_label || "",
          classification: attribution.classification || "",
          entity_type: attribution.entityType || attribution.entity_type || "",
          signals: attribution.signals || [],
        },
        entry_context: this.serializeEntryContext(entry),
      };

      const json = await this.postTool88(body);
      const result = json.result || json;
      if (result && result.job_id && (result.status === "queued" || result.signal_status === "queued")) {
        return this.pollSignalJob(result.job_id, result.next_poll_ms);
      }
      return result;
    },

    async pollSignalJob(jobId, nextPollMs = 0) {
      const maxAttempts = 24;
      const defaultDelayMs = 2000;
      const initialDelayMs = Number(nextPollMs) > 0 ? Number(nextPollMs) : defaultDelayMs;
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        this.statusMessage = `Waiting for transaction-signal job ${jobId} (${attempt}/${maxAttempts})...`;
        await this.sleep(attempt === 1 ? initialDelayMs : defaultDelayMs);
        const status = await this.postTool88({ action: "signal_status", job_id: jobId });
        const result = status.result || status;
        if (!result || result.status === "failed" || result.signal_status === "failed") {
          throw new Error(result?.message || result?.error_message || "Queued transaction-signal check failed.");
        }
        if (result.status === "complete" || result.signal_status === "complete" || result.classification !== undefined || result.signal_classification !== undefined) {
          return result;
        }
      }
      throw new Error("Queued transaction-signal check did not complete before the polling limit.");
    },

    async lookupAttributionData(entry, selectedAddress) {
      if (this.useMockBackend) {
        return this.buildMockAttributionResult(entry, selectedAddress);
      }

      const countContext = this.inferCountRequestContext(entry, selectedAddress);
      const localAttributionValues = this.getAttributionValuesForEntry(entry, selectedAddress);
      const meaningfulLocalAttributionValues = localAttributionValues.filter((value) => !this.isUnknownAttributionPlaceholder(value));
      const body = {
        action: "attribution_lookup",
        address: selectedAddress,
        direction: this.traceDirection,
        chain_id: countContext.chain_id || entry?.chainId || "",
        chain_name: countContext.chain_name || entry?.chainName || "",
        chain_label: countContext.chain_label || "",
        network_family: countContext.network_family || "",
        provider_hint: countContext.provider_hint || "",
        token_symbol: countContext.token_symbol || this.getSelectedTokenSymbol(entry, selectedAddress) || "",
        sample_mode: this.isSampleDataLoaded ? 1 : 0,
        prefer_cache: true,
        cache_first: true,
        lookup_arkham_even_with_server_note: true,
        require_fresh_cache: false,
        refresh_vendor: true,
        refresh_vendor_if_stale: true,
        allow_stale_cache: true,
        ttl_hours: TOOL88_ATTRIBUTION_TTL_HOURS,
        column_k_values: meaningfulLocalAttributionValues,
        column_k_unknown_present: localAttributionValues.some((value) => this.isUnknownAttributionPlaceholder(value)) ? 1 : 0,
        count_context: countContext,
        entry_context: this.serializeEntryContext(entry),
      };

      const json = await this.postTool88(body);
      const result = json.result || json;
      if (result && result.job_id && (result.status === "queued" || result.attribution_status === "queued")) {
        return this.pollAttributionJob(result.job_id, result.next_poll_ms);
      }
      return result;
    },

    async pollAttributionJob(jobId, nextPollMs = 0) {
      const maxAttempts = 24;
      const defaultDelayMs = 2000;
      const initialDelayMs = Number(nextPollMs) > 0 ? Number(nextPollMs) : defaultDelayMs;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        this.statusMessage = `Waiting for attribution job ${jobId} (${attempt}/${maxAttempts})...`;
        await this.sleep(attempt === 1 ? initialDelayMs : defaultDelayMs);

        const status = await this.postTool88({
          action: "attribution_status",
          job_id: jobId,
        });
        const result = status.result || status;
        if (!result || result.status === "failed" || result.attribution_status === "failed") {
          throw new Error(result?.message || result?.error_message || "Queued attribution lookup failed.");
        }
        if (result.status === "complete" || result.attribution_status === "complete" || result.display_label !== undefined || result.label !== undefined) {
          return result;
        }
      }

      throw new Error("Queued attribution lookup did not complete before the polling limit.");
    },

    buildMockAttributionResult(entry, selectedAddress) {
      const entryText = this.collectEntrySearchText(entry);
      const addressText = this.toText(selectedAddress).toLowerCase();
      let label = "Tool 88 Sample Exchange";
      let searchable = "Tool 88 Sample Exchange Deposit Address";
      if (/bridge|wormhole|li\.fi|0xddf/.test(entryText) || /bridge/.test(addressText)) {
        label = "Bitget Bridge";
        searchable = "Bitget Bridge bridge deposit address";
      } else if (/swap|uniswap|weth/.test(entryText) || /swap/.test(addressText)) {
        label = "Uniswap";
        searchable = "Uniswap swap router contract";
      } else if (/hot\s*wallet/.test(entryText)) {
        label = "Tool 88 Sample Exchange";
        searchable = "Tool 88 Sample Exchange Hot Wallet";
      }

      const signals = this.buildLocalAttributionSignals(`${label} ${searchable}`);
      return {
        address: selectedAddress,
        display_label: label,
        label,
        server_note: "",
        server_note_present: false,
        vendor_label: label,
        vendor_source: "mock_placeholder_attribution",
        source: "mock_placeholder_attribution",
        classification: signals.includes("hot_wallet") ? "hot_wallet" : signals.includes("deposit_address") ? "deposit_address" : "",
        entity_type: this.classifyAttributionEntityType(`${label} ${searchable}`),
        signals,
        advice_status: "warn",
        recommendation: "Mock attribution result for UI review; real Tool 88 requests use server-side address_notes and Arkham cache/fresh lookup.",
        warnings: [],
      };
    },

    deterministicMockCount(address) {
      const text = this.toText(address);
      let seed = 0;
      for (let index = 0; index < text.length; index += 1) {
        seed = (seed + text.charCodeAt(index) * (index + 17)) % 300000;
      }

      if (/blocked|huge/i.test(text)) {
        return 260000;
      }
      if (/busy|warning/i.test(text)) {
        return 12000;
      }
      return Math.max(1, seed % 7500);
    },

    async lookupTransactionCount(entry, selectedAddress) {
      const fundsIn = this.deriveFundsIn(entry, selectedAddress);
      const countContext = this.inferCountRequestContext(entry, selectedAddress);

      if (this.useMockBackend) {
        return {
          status: "success",
          address: selectedAddress,
          transaction_count: this.deterministicMockCount(selectedAddress),
          count_available: true,
          count_known: true,
          source: "mock_placeholder",
          provider: "mock",
          provider_label: "Mock placeholder",
          route: "mock_count",
          route_label: "Mock placeholder count",
          count_kind: "mock",
          network_family: countContext.network_family || "sample",
          chain_id: countContext.chain_id || entry?.chainId || "",
          chain_name: countContext.chain_name || entry?.chainName || "",
          chain_label: countContext.chain_label || countContext.chain_name || "Sample",
          token_symbol: fundsIn.token || countContext.token_symbol || "",
        };
      }

      const cacheKey = this.buildCountSessionCacheKey(entry, selectedAddress, countContext, fundsIn);
      const cached = this.getSessionCountCache(cacheKey);
      if (cached) {
        this.statusMessage = "Using session-cached transaction count...";
        return cached;
      }

      const body = {
        action: "transaction_count",
        address: selectedAddress,
        direction: this.traceDirection,
        chain_id: countContext.chain_id || entry?.chainId || "",
        chain_name: countContext.chain_name || entry?.chainName || "",
        chain_label: countContext.chain_label || "",
        network_family: countContext.network_family || "",
        provider_hint: countContext.provider_hint || "",
        count_kind: countContext.count_kind || "auto",
        token_symbol: fundsIn.token || countContext.token_symbol || "",
        asset_symbol: fundsIn.token || countContext.token_symbol || "",
        native_symbol: countContext.native_symbol || "",
        moralis_chain: countContext.moralis_chain || "",
        amount: fundsIn.amount,
        funds_source: fundsIn.source || "",
        sample_mode: this.isSampleDataLoaded ? 1 : 0,
        count_context: countContext,
        entry_context: this.serializeEntryContext(entry),
      };

      const json = await this.postTool88(body);
      const rawResult = json.result || json;
      let result = rawResult;
      if (rawResult && rawResult.job_id && (rawResult.status === "queued" || rawResult.count_status === "queued")) {
        result = await this.pollCountJob(rawResult.job_id, rawResult.next_poll_ms);
      }
      this.storeSessionCountCache(cacheKey, result);
      return result;
    },

    async pollCountJob(jobId, nextPollMs = 0) {
      const maxAttempts = 36;
      const defaultDelayMs = 2500;
      const initialDelayMs = Number(nextPollMs) > 0 ? Number(nextPollMs) : defaultDelayMs;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        this.statusMessage = `Waiting for transaction-count job ${jobId} (${attempt}/${maxAttempts})...`;
        await this.sleep(attempt === 1 ? initialDelayMs : defaultDelayMs);

        const status = await this.postTool88({
          action: "count_status",
          job_id: jobId,
        });
        const result = status.result || status;

        if (!result || result.status === "failed" || result.count_status === "failed") {
          const message = result?.message || result?.error_message || "Queued transaction-count lookup failed.";
          throw new Error(message);
        }

        if (result.status === "complete" || result.count_status === "complete" || result.count_available === true || result.countAvailable === true) {
          return result;
        }

        if (result.count_available === false && result.transaction_count !== undefined) {
          return result;
        }
      }

      throw new Error("Queued transaction-count lookup did not complete before the polling limit.");
    },

    async traceThrough(options = {}) {
      this.applyCaseBuddyPreferredSettings(options);
      if (!this.canTraceThrough || this.isProcessing) {
        const message = this.isProcessing
          ? 'Tool 88 is still processing the previous request; trace-through was not run.'
          : 'Process an entry and select a target address before Tool 88 can trace through.';
        this.statusMessage = message;
        this.traceResult = {
          status: 'trace_not_ready',
          exitRows: [],
          exitTransactions: [],
          outputMutationVerified: false,
          pathEndingReason: message,
        };
        const snapshot = this.buildCaseBuddyHopSolverSnapshot({
          includeRows: true,
          status: 'trace-through-not-ready',
          integrationAction: 'manual-trace-through',
          message,
          guardrail: { reason: 'trace-through-not-ready', message }
        });
        this.emitCaseBuddyIntegrationEvent('case-buddy-status', snapshot);
        return snapshot;
      }

      if (options.resetCaseBuddyStopState === true || !this.caseBuddyIntegrationRunState?.active) {
        this.caseBuddyClearStaleStopState(options.resetCaseBuddyStopReason || 'Case Buddy started a fresh trace-through action.');
      }

      const entry = this.selectedEntry;
      const selectedAddress = this.selectedTargetAddress;
      this.dormantResult = null;
      this.traceTransactionPayload = null;
      const depositEntityHopExtensionProbe = this.shouldUseDepositEntityHopExtensionProbe(entry, selectedAddress);
      const serviceOverrideRequested = Boolean(
        options.allowPotentialServiceTraceOverride ||
        options.overridePotentialServiceStop ||
        options.serviceOverride ||
        options.disableAdditionalServiceChecksForThisTrace ||
        options.skipAdditionalServiceChecks
      );
      const serviceOverrideReason = this.toText(options.serviceOverrideReason || "Case Buddy manual hop inspector override: user chose to trace through after Tool 88 flagged possible Unknown service / hot-wallet behavior.");
      const additionalServiceChecksEnabledForTrace = serviceOverrideRequested
        ? false
        : (depositEntityHopExtensionProbe ? false : this.enableAdditionalServiceChecks !== false);

      this.isProcessing = true;
      this.statusMessage = "Fetching trace transactions...";
      this.traceResult = null;
      this.dormantResult = null;
      this.traceTransactionPayload = null;
      this.pathEndingResult = null;
      this.resetTraceFetchUi(entry);

      try {
        const transactionPayload = await this.fetchTraceTransactions(entry, selectedAddress, {
          additionalServiceChecksEnabled: additionalServiceChecksEnabledForTrace,
          depositEntityHopExtensionProbe,
          serviceStopOverride: serviceOverrideRequested,
          manualServiceOverride: serviceOverrideRequested,
          serviceOverrideReason,
          caseWorkflowRawData: options.caseWorkflowRawData === true,
          caseBuddyRawDataAutomation: options.caseBuddyRawDataAutomation === true,
          rawDataAutomation: options.rawDataAutomation === true,
        });
        this.traceTransactionPayload = transactionPayload;
        const transactions = Array.isArray(transactionPayload.transactions)
          ? transactionPayload.transactions
          : [];
        this.traceInspectorRows = this.buildTraceInspectorRows(transactions, entry, selectedAddress);
        this.updateTraceProgressFromResult(transactionPayload, transactions.length);

        if (this.traceFetchCancelledByUser(transactionPayload)) {
          this.traceResult = null;
          this.pathEndingResult = null;
          this.lastRunMeta = {
            ...this.buildBaseMeta(
              this.inputSnapshotRows,
              this.parsedEntries,
              this.targetCandidates,
              selectedAddress
            ),
            jobId: transactionPayload.job_id || this.backendJobMeta?.job_id || "",
            transactionCount: this.countLookupResult?.transactionCount,
            traceFetchedCount: transactions.length,
            traceFetchStatus: transactionPayload.trace_fetch?.status || transactionPayload.trace_status || transactionPayload.status || "cancelled",
          };
          this.reportText = `Trace preview fetch was stopped before Tool 88 applied any changes. ${this.formatInteger(transactions.length)} provider row${transactions.length === 1 ? "" : "s"} had been fetched for review but were not applied.`;
          return;
        }

        if (this.traceFetchStoppedForService(transactionPayload) && !serviceOverrideRequested) {
          const serviceAssessment = this.getTraceServiceAssessment(transactionPayload);
          const serviceReason = this.buildServiceStopReason(serviceAssessment, transactions.length);
          const finalized = this.finalizeEntryAsPathEnding({
            baseRows: this.getCurrentOutputBaseRows(),
            entry,
            selectedAddress,
            reason: serviceReason,
            dormantAmountText: "",
            suppressDormantFallback: true,
          });
          this.traceResult = null;
          this.pathEndingResult = {
            note: finalized.note,
            reason: finalized.reason,
            specialTreatment: "potential_service_address",
            serviceAssessment,
          };
          this.loadOutputRows(finalized.rows);
          this.lastRunMeta = {
            ...this.buildBaseMeta(
              this.inputSnapshotRows,
              this.parsedEntries,
              this.targetCandidates,
              selectedAddress
            ),
            exitCount: 0,
            jobId: transactionPayload.job_id || this.backendJobMeta?.job_id || "",
            transactionCount: this.countLookupResult?.transactionCount,
            traceFetchedCount: this.traceFetchFetchedCount,
            traceInputTransactionFound: this.traceInputTransactionFound,
            traceFetchStatus: transactionPayload.trace_fetch?.status || transactionPayload.trace_status || transactionPayload.status || "",
            serviceScore: serviceAssessment?.score ?? null,
            serviceSummary: serviceAssessment?.summary || "",
          };
          this.reportText = this.buildServiceStopReport(entry, selectedAddress, transactionPayload, transactions.length, serviceAssessment);
          return;
        }

        if (this.traceFetchLooksUnfinished(transactionPayload)) {
          throw new Error(
            "Trace history fetch is still queued/running. Tool 88 did not run hop logic or record a path ending yet."
          );
        }

        if (this.traceFetchLooksSuspiciouslyIncomplete(transactionPayload)) {
          const expectedTraceCount = this.getExpectedTraceCountFromPayload(transactionPayload);
          const warning = this.buildSuspiciousIncompleteTraceMessage(transactionPayload, transactions.length);
          if (!this.warnings.includes(warning)) {
            this.warnings.push(warning);
          }
          this.traceResult = null;
          this.pathEndingResult = null;
          this.lastRunMeta = {
            ...this.buildBaseMeta(
              this.inputSnapshotRows,
              this.parsedEntries,
              this.targetCandidates,
              selectedAddress
            ),
            jobId: transactionPayload.job_id || this.backendJobMeta?.job_id || "",
            transactionCount: this.countLookupResult?.transactionCount,
            traceFetchedCount: this.traceFetchFetchedCount,
            traceExpectedCount: expectedTraceCount,
            traceInputTransactionFound: this.traceInputTransactionFound,
            traceFetchStatus: transactionPayload.trace_fetch?.status || transactionPayload.trace_status || transactionPayload.status || "",
          };
          this.reportText = this.buildTraceFetchIncompleteReport(entry, selectedAddress, transactionPayload, transactions.length, expectedTraceCount);
          return;
        }

        if (this.traceFetchLooksSuspiciouslyEmpty(transactionPayload, transactions)) {
          const expectedTraceCount = this.getExpectedTraceCountFromPayload(transactionPayload);
          const warning = this.buildSuspiciousEmptyTraceMessage(transactionPayload);
          if (!this.warnings.includes(warning)) {
            this.warnings.push(warning);
          }
          this.traceResult = null;
          this.pathEndingResult = null;
          this.lastRunMeta = {
            ...this.buildBaseMeta(
              this.inputSnapshotRows,
              this.parsedEntries,
              this.targetCandidates,
              selectedAddress
            ),
            jobId: transactionPayload.job_id || this.backendJobMeta?.job_id || "",
            transactionCount: this.countLookupResult?.transactionCount,
            traceFetchedCount: 0,
            traceExpectedCount: expectedTraceCount,
            traceInputTransactionFound: false,
            traceFetchStatus: transactionPayload.trace_fetch?.status || transactionPayload.trace_status || transactionPayload.status || "",
          };
          this.reportText = this.buildTraceFetchNoRowsReport(entry, selectedAddress, transactionPayload, expectedTraceCount);
          return;
        }

        let solution = this.solveHopForEntry(entry, selectedAddress, transactions);
        if (serviceOverrideRequested) {
          solution = {
            ...solution,
            serviceStopOverride: true,
            manualServiceOverride: true,
            serviceOverrideReason,
          };
        }
        const originalTraceSolution = solution;
        const depositEntityHopExtension = depositEntityHopExtensionProbe
          ? await this.evaluateDepositEntityHopExtension(entry, selectedAddress, solution)
          : null;

        if (depositEntityHopExtension?.keepCandidate) {
          solution = this.annotateDepositEntityHopExtensionExitRows(solution, depositEntityHopExtension);
        } else if (depositEntityHopExtension?.dropCandidate) {
          const rejectedFundsIn = solution?.fundsIn || this.deriveFundsIn(entry, selectedAddress);
          const rejectedDormantAmountText = Number.isFinite(Number(rejectedFundsIn?.amount))
            ? this.formatFunds(Number(rejectedFundsIn.amount), rejectedFundsIn.token || "")
            : (solution?.dormantAmountText || "");
          solution = {
            ...solution,
            status: "deposit_entity_hop_extension_rejected",
            rejectedExitRows: Array.isArray(solution.exitRows)
              ? solution.exitRows.map((row) => this.cloneRowPreservingValues(row))
              : [],
            rejectedExitTransactions: Array.isArray(solution.exitTransactions)
              ? solution.exitTransactions.map((exit) => ({ ...exit }))
              : [],
            exitRows: [],
            exitTransactions: [],
            dormantAmountText: rejectedDormantAmountText,
            feeConsumedAmountText: "",
            leftBoundAmount: Number.isFinite(Number(rejectedFundsIn?.amount)) ? Number(rejectedFundsIn.amount) : null,
            pathEndingReason: depositEntityHopExtension.pathEndingReason,
            depositEntityHopExtension,
          };
        } else if (depositEntityHopExtension?.applies) {
          solution = {
            ...solution,
            depositEntityHopExtension,
          };
        }

        this.traceInspectorRows = this.applyTraceAnalysisToInspectorRows(this.traceInspectorRows, depositEntityHopExtension?.dropCandidate ? solution : originalTraceSolution);

        let outputRows = this.getCurrentOutputBaseRows();
        outputRows = this.updateBtcSelectedEntryTransactionDetailsInRows(outputRows, entry, selectedAddress, transactions, solution);
        outputRows = this.updateBtcSelectedEntryPriorBalanceInRows(outputRows, entry, solution);
        outputRows = this.updateAccountSelectedEntryPriorBalanceInRows(outputRows, entry, solution);
        const outputRowsBeforeTraceMutation = outputRows.map((row) => this.cloneRowPreservingValues(row));
        let outputMutationVerification = { ok: true, missing: [], expected: [], changed: true };
        if (depositEntityHopExtension?.dropCandidate) {
          const finalized = this.finalizeEntryAsPathEnding({
            baseRows: outputRows,
            entry,
            selectedAddress,
            reason: depositEntityHopExtension.pathEndingReason,
            dormantAmountText: solution.dormantAmountText,
            dormantOutcome: Boolean(solution.dormantAmountText),
          });
          outputRows = finalized.rows;
          this.traceResult = null;
          this.dormantResult = this.buildTool88StructuredDormantResult(solution, entry, selectedAddress, {
            transactions,
            transactionPayload,
            runId: transactionPayload.job_id || this.backendJobMeta?.job_id || ''
          });
          this.pathEndingResult = {
            note: finalized.note,
            dormantNote: finalized.dormantNote || this.dormantResult?.note || '',
            dormantAmountText: solution.dormantAmountText,
            dormantResult: this.dormantResult ? { ...this.dormantResult } : null,
            reason: finalized.reason,
            specialTreatment: "deposit_entity_hop_extension_rejected",
            depositEntityHopExtension,
          };
        } else {
          if (solution.exitRows.length) {
            outputRows = this.markEntryContinuesAs(
              outputRows,
              entry,
              solution.exitTransactions
            );
            if (solution.dormantAmountText || solution.feeConsumedAmountText) {
              const dormantReason = solution.dormantReason || `Some followed ${solution.fundsIn?.token || "funds"} remains at the selected address after the solved exits.`;
              outputRows = this.annotateEntryWithDormantRemainder(
                outputRows,
                entry,
                solution.dormantAmountText,
                dormantReason,
                solution.feeConsumedAmountText
              );
            }
            if (solution.dormantAmountText) {
              this.dormantResult = this.buildTool88StructuredDormantResult(solution, entry, selectedAddress, {
                transactions,
                transactionPayload,
                runId: transactionPayload.job_id || this.backendJobMeta?.job_id || ''
              });
            }
            const insertionRepair = this.repairTraceOutputInsertion(
              outputRows,
              entry,
              solution.exitRows,
              outputRowsBeforeTraceMutation
            );
            outputRows = insertionRepair.rows;
            outputMutationVerification = {
              ...insertionRepair.verification,
              repaired: insertionRepair.repaired,
            };
            solution = {
              ...solution,
              outputMutationVerified: outputMutationVerification.ok,
              outputMutationVerification,
            };
            this.traceResult = solution;
            if (insertionRepair.repaired) {
              this.pushUniqueWarnings([
                insertionRepair.verification?.forcedSelectedParentRepair
                  ? "Tool 88 reconciled trace output by forcing the solved child row onto the currently selected parent branch before continuing."
                  : "Tool 88 reconciled trace output using stable branch identity before continuing.",
              ]);
            }
            if (!outputMutationVerification.ok) {
              const missingText = outputMutationVerification.missing.length
                ? outputMutationVerification.missing.join(", ")
                : "expected inserted row(s)";
              outputRows = outputRowsBeforeTraceMutation.map((row) => this.cloneRowPreservingValues(row));
              this.pushUniqueWarnings([
                `Trace-through found ${solution.exitRows.length} row(s), but Tool 88 could not verify the inserted output row(s): ${missingText}. The output was left unchanged for this pass.`,
              ]);
            }
          } else if (solution.preventPathEnding) {
            this.traceResult = solution;
            const unresolvedReason = solution.pathEndingReason || "Tool 88 could not resolve a source/exit confidently enough to modify the output spreadsheet.";
            this.pushUniqueWarnings([unresolvedReason]);
            this.pathEndingResult = null;
          } else {
            this.traceResult = solution;
            const feeNoExitReason = solution.feeConsumedAmountText
              ? (solution.btcAnalysis
                  ? `No qualifying BTC output exit was found during Tool 88 trace-through. ${solution.feeConsumedAmountText} appears consumed by BTC miner fees under Tool 88 BTC FIFO allocation.`
                  : `No qualifying address exit was found during Tool 88 trace-through. ${solution.feeConsumedAmountText} appears consumed by native network/gas fees under Tool 88 account FIFO allocation.`)
              : "";
            const noExitReason = feeNoExitReason || (solution.pathEndingReason || "No qualifying exit was found during Tool 88 trace-through.");
            const finalized = this.finalizeEntryAsPathEnding({
              baseRows: outputRows,
              entry,
              selectedAddress,
              reason: noExitReason,
              dormantAmountText: solution.dormantAmountText || solution.feeConsumedAmountText,
              dormantOutcome: Boolean(solution.dormantAmountText && !solution.feeConsumedAmountText),
            });
            outputRows = finalized.rows;
            this.dormantResult = this.buildTool88StructuredDormantResult(solution, entry, selectedAddress, {
              transactions,
              transactionPayload,
              runId: transactionPayload.job_id || this.backendJobMeta?.job_id || ''
            });
            this.pathEndingResult = {
              note: finalized.note,
              dormantNote: finalized.dormantNote || this.dormantResult?.note || '',
              dormantAmountText: solution.dormantAmountText || solution.feeConsumedAmountText,
              dormantResult: this.dormantResult ? { ...this.dormantResult } : null,
              reason: finalized.reason,
            };
          }
        }

        this.loadOutputRows(outputRows);
        await this.$nextTick();
        if (solution.exitRows?.length) {
          const committedRows = this.getCurrentOutputRowsForExport();
          const rowVerification = this.verifyTraceOutputMutation(
            committedRows,
            solution.exitRows,
            outputRowsBeforeTraceMutation
          );
          const continuationState = this.caseBuddyTraceContinuationState(committedRows, solution.exitRows);
          const committedMissing = rowVerification.missing.slice();
          if (!continuationState.parentContinuationVerified) {
            committedMissing.push('selected parent does not retain the expected Continues as transition');
          }
          const committedVerification = {
            ...rowVerification,
            ok: rowVerification.ok && continuationState.parentContinuationVerified,
            missing: committedMissing,
            parentContinuationVerified: continuationState.parentContinuationVerified,
            selectedParentRowIndex: continuationState.parentRowIndex,
            expectedChildHashes: continuationState.expectedChildHashes,
            referencedChildHashes: continuationState.referencedChildHashes,
            repaired: Boolean(outputMutationVerification.repaired),
          };
          solution = {
            ...solution,
            outputMutationVerified: committedVerification.ok,
            outputMutationVerification: committedVerification,
          };
          this.traceResult = solution;
          if (!committedVerification.ok) {
            const missingText = committedVerification.missing.length
              ? committedVerification.missing.join(", ")
              : "expected inserted row(s)";
            this.pushUniqueWarnings([
              `Trace-through found row(s), but the committed output snapshot does not contain ${missingText}. Use advanced review before continuing.`,
            ]);
          }
        }

        this.lastRunMeta = {
          ...this.buildBaseMeta(
            this.inputSnapshotRows,
            this.parsedEntries,
            this.targetCandidates,
            selectedAddress
          ),
          exitCount: solution.exitRows.length,
          dormantAmountText: solution.dormantAmountText,
          dormantResult: this.dormantResult ? { ...this.dormantResult } : null,
          dormantAnalysisDebug: this.dormantResult ? {
            selectedPathLabel: this.dormantResult.evidence?.selectedPathLabel || '',
            selectedTransactionHash: this.dormantResult.evidence?.selectedTransactionHash || '',
            receivingAddress: this.dormantResult.receivingAddress || '',
            token: this.dormantResult.token || '',
            fundsFollowed: this.dormantResult.fundsFollowedText || this.dormantResult.fundsFollowed || '',
            priorBalance: this.dormantResult.evidence?.priorBalance || '',
            leftBounds: this.dormantResult.evidence?.leftBounds || '',
            depositOfInterestFound: this.dormantResult.evidence?.depositOfInterestFound === true,
            downloadedTransactionCount: this.dormantResult.evidence?.downloadedTransactionCount || 0,
            candidateOutgoingTransactionCount: this.dormantResult.evidence?.candidateExitCount || 0,
            validExitCount: this.dormantResult.validExitCount || 0,
            excludedExitCount: this.dormantResult.evidence?.excludedExitCount || 0,
            remainingDormantAmount: this.dormantResult.dormantAmountText || '',
            dustTolerance: this.dormantResult.evidence?.dustTolerance || '',
            resultAction: this.dormantResult.action || '',
            columnJNote: this.dormantResult.note || '',
            columnQValue: this.dormantResult.continuesAs || '',
            caseBuddyStructuredDormantResult: true
          } : null,
          jobId: transactionPayload.job_id || this.backendJobMeta?.job_id || "",
          transactionCount: this.countLookupResult?.transactionCount,
          attributionLabel: this.attributionLookupResult?.displayLabel || "",
          attributionClassification: this.attributionLookupResult?.classification || this.attributionLookupResult?.entityType || "",
          attributionRole: this.attributionLookupResult?.classification || this.attributionLookupResult?.serviceRole || this.attributionLookupResult?.entityType || "",
          signalClassification: this.transactionSignalResult?.classification || "",
          signalSummary: this.transactionSignalResult?.evidenceSummary || this.transactionSignalResult?.evidenceLabel || "",
          depositEntityHopExtension: depositEntityHopExtension?.action || "",
          depositEntityHopExtensionSummary: depositEntityHopExtension?.summary || "",
          serviceStopOverride: serviceOverrideRequested,
          manualServiceOverride: serviceOverrideRequested,
          serviceOverrideReason: serviceOverrideRequested ? serviceOverrideReason : "",
        };

        this.reportText = this.buildTraceReport(entry, selectedAddress, transactionPayload, solution);
        if (serviceOverrideRequested) {
          const overrideLines = [
            "",
            "Manual service / hot-wallet override",
            "----------------------------------------",
            "Additional service checks were bypassed for this trace-through run only.",
            `Reason: ${serviceOverrideReason}`,
            "Case Buddy should ask the user to acknowledge this warning before applying the returned rows.",
          ];
          this.reportText = `${this.reportText}\n\n${overrideLines.join("\n")}`;
        }
        if (solution.outputMutationVerification) {
          const verification = solution.outputMutationVerification;
          const verificationLines = [
            "",
            "Output insertion verification",
            "----------------------------------------",
            `Verified: ${verification.ok ? "yes" : "no"}`,
            `Output changed: ${verification.changed ? "yes" : "no"}`,
            `Row count delta: ${verification.rowCountDelta ?? "n/a"}`,
            `Repair applied: ${verification.repaired ? "yes" : "no"}`,
            verification.missing?.length ? `Missing/failed checks: ${verification.missing.join(", ")}` : "Missing/failed checks: none",
          ];
          this.reportText = `${this.reportText}\n\n${verificationLines.join("\n")}`;
        }
      } catch (error) {
        const message = error && error.message ? error.message : "Unknown error";
        this.traceResult = null;
        this.pathEndingResult = null;
        this.pushUniqueWarnings([`Trace-through failed: ${message}`]);
        this.reportText = `Trace-through failed: ${message}`;
      } finally {
        this.isProcessing = false;
        this.statusMessage = "";
      }
    },

    getTraceFetchStatusText(result = {}) {
      return this.toText(
        result.trace_fetch?.status ||
          result.trace_status ||
          result.status ||
          ""
      ).toLowerCase();
    },

    traceFetchStoppedForService(result = {}) {
      const traceFetch = result && typeof result.trace_fetch === "object" ? result.trace_fetch : {};
      const assessor = traceFetch.service_assessment || traceFetch.service_assessor || traceFetch.hot_wallet_assessor || traceFetch.hot_wallet_assessor_placeholder || {};
      const status = this.getTraceFetchStatusText(result);
      return Boolean(
        traceFetch.service_stop ||
        traceFetch.service_stop_detected ||
        traceFetch.stopped_for_service ||
        assessor.stop_early ||
        assessor.stopEarly ||
        assessor.status === "stop" ||
        ["service_stop", "stopped_for_service", "potential_service_stop"].includes(status)
      );
    },

    traceFetchCancelledByUser(result = {}) {
      const traceFetch = result && typeof result.trace_fetch === "object" ? result.trace_fetch : {};
      const status = this.getTraceFetchStatusText(result);
      return Boolean(
        this.traceFetchStopRequested ||
        result.user_cancelled ||
        result.userCanceled ||
        result.cancelled_by_user ||
        result.cancelledByUser ||
        traceFetch.user_cancelled ||
        traceFetch.userCanceled ||
        traceFetch.cancelled_by_user ||
        traceFetch.cancelledByUser ||
        ["cancelled", "canceled", "user_cancelled", "user-cancelled", "stopped_by_user", "stopped-by-user"].includes(status)
      );
    },

    requestTraceFetchStop(message = "Trace fetch stop requested.") {
      const cleanMessage = this.toText(message) || "Trace fetch stop requested.";
      this.traceFetchStopRequested = true;
      this.traceFetchStopMessage = cleanMessage;
      this.statusMessage = cleanMessage;
      const current = this.traceFetchProgress && typeof this.traceFetchProgress === "object"
        ? this.traceFetchProgress
        : {};
      const traceFetch = current.trace_fetch && typeof current.trace_fetch === "object"
        ? current.trace_fetch
        : {};
      this.traceFetchProgress = {
        ...current,
        status: "cancelled",
        trace_status: "cancelled",
        user_cancelled: true,
        cancelled_by_user: true,
        trace_fetch: {
          ...traceFetch,
          status: "cancelled",
          message: cleanMessage,
          user_cancelled: true,
          cancelled_by_user: true,
        },
      };
      return this.traceFetchProgress;
    },

    buildTraceFetchCancelledPayload(jobId = "", transactions = []) {
      const rows = Array.isArray(transactions) ? transactions : [];
      const traceFetch = this.traceFetchProgress && typeof this.traceFetchProgress.trace_fetch === "object"
        ? this.traceFetchProgress.trace_fetch
        : {};
      const message = this.traceFetchStopMessage || "Trace fetch stopped by user.";
      const payload = {
        job_id: jobId,
        status: "cancelled",
        trace_status: "cancelled",
        user_cancelled: true,
        cancelled_by_user: true,
        transactions: rows,
        fetched_transaction_count: rows.length,
        trace_fetch: {
          ...traceFetch,
          status: "cancelled",
          message,
          user_cancelled: true,
          cancelled_by_user: true,
          fetched_count: rows.length,
          expected_count: traceFetch.expected_count ?? this.traceFetchProgress?.expected_count ?? -1,
        },
      };
      this.updateTraceProgressFromResult(payload, rows.length);
      return payload;
    },

    getTraceServiceAssessment(payload = null) {
      const traceFetch = payload?.trace_fetch || this.traceFetchProgress?.trace_fetch || {};
      const raw = traceFetch.service_assessment || traceFetch.service_assessor || traceFetch.hot_wallet_assessor || traceFetch.hot_wallet_assessor_placeholder || traceFetch.service_pattern_placeholder || null;
      if (!raw || typeof raw !== "object") {
        return null;
      }
      const enabled = raw.enabled !== false && raw.enabled !== 0 && raw.enabled !== "0";
      const reasons = Array.isArray(raw.reasons)
        ? raw.reasons.map((reason) => this.toText(reason)).filter(Boolean).slice(0, 8)
        : [];
      const scoreNumber = Number(raw.score01 ?? raw.score ?? raw.Score01);
      const score = Number.isFinite(scoreNumber) ? scoreNumber : null;
      const likely = Boolean(raw.likely_hot_wallet || raw.likelyHotWallet || raw.LikelyHotWallet || raw.stop_early || raw.stopEarly || raw.status === "stop" || raw.stop_reason || raw.stopReason);
      const summary = this.toText(raw.short_summary || raw.summary || raw.note || raw.message || "");
      const observed = Number(raw.observed_transfers ?? raw.observedTransfers ?? traceFetch.fetched_count ?? 0);
      const knownTotal = Number(raw.known_total_transfers ?? raw.knownTotalTransfers ?? traceFetch.expected_count ?? -1);
      const depositProbeMode = Boolean(traceFetch.deposit_entity_hop_extension_probe || traceFetch.depositEntityHopExtensionProbe || traceFetch.deposit_hot_wallet_probe || traceFetch.depositHotWalletProbe);
      const title = !enabled
        ? (depositProbeMode ? "Service checks paused for deposit probe" : "Additional service checks are off")
        : likely
          ? "Potential service/hot-wallet-like activity detected"
          : score !== null
            ? "Service-pattern check did not trigger"
            : "Service-pattern check collecting data";
      return {
        enabled,
        likely,
        stopEarly: Boolean(raw.stop_early || raw.stopEarly),
        score,
        observed: Number.isFinite(observed) ? observed : 0,
        knownTotal: Number.isFinite(knownTotal) ? knownTotal : -1,
        severity: !enabled ? "muted" : likely ? "warn" : "ok",
        title,
        summary: summary || (!enabled
          ? (depositProbeMode
              ? "Tool 88 intentionally paused broad service-pattern stopping during the deposit hot-wallet probe; same-entity attribution validation decides whether the extra hop is kept."
              : "Tool 88 will not stop the fetch for hot-wallet/service patterns while this setting is off.")
          : likely
            ? "Tool 88 stopped before hop logic because the address activity looks like an unattributed exchange hot wallet or service address."
            : "Tool 88 is watching transaction-history shape while it fetches pages."),
        reasons,
      };
    },

    buildServiceStopReason(serviceAssessment = null, fetchedCount = 0) {
      const assessment = serviceAssessment || this.getTraceServiceAssessment();
      const parts = [
        "Unknown. Address has features of an unattributed exchange hot wallet or service address.",
      ];
      if (assessment?.summary) {
        parts.push(assessment.summary);
      }
      if (Number.isFinite(Number(assessment?.score))) {
        parts.push(`Service-pattern score ${Number(assessment.score).toFixed(2)}.`);
      }
      const observed = Number(assessment?.observed ?? fetchedCount);
      if (Number.isFinite(observed) && observed > 0) {
        parts.push(`Tool 88 stopped after reviewing ${this.formatInteger(observed)} fetched rows.`);
      }
      if (assessment?.reasons?.length) {
        parts.push(`Characteristics: ${assessment.reasons.slice(0, 4).join("; ")}.`);
      }
      parts.push("Uncheck Additional service checks and run Trace Through again only if you intentionally need to trace through this suspected service address.");
      return parts.join(" ");
    },

    traceFetchLooksUnfinished(result = {}) {
      const status = this.getTraceFetchStatusText(result);
      return ["queued", "running", "pending", "processing"].includes(status);
    },

    traceFetchLooksSuspiciouslyEmpty(result = {}, transactions = []) {
      const traceFetch = result.trace_fetch && typeof result.trace_fetch === "object"
        ? result.trace_fetch
        : {};
      const status = this.getTraceFetchStatusText(result);
      const expected = this.getExpectedTraceCountFromPayload(result);
      const fetched = Number(traceFetch.fetched_count ?? result.fetched_transaction_count ?? transactions.length);

      return ["complete", "completed", "success"].includes(status) &&
        Number.isFinite(expected) &&
        expected > 0 &&
        Number.isFinite(fetched) &&
        fetched === 0 &&
        transactions.length === 0;
    },

    traceFetchLooksSuspiciouslyIncomplete(result = {}) {
      const traceFetch = result.trace_fetch && typeof result.trace_fetch === "object"
        ? result.trace_fetch
        : {};
      return traceFetch.suspicious_pagination === true || traceFetch.suspicious_pagination === 1 || traceFetch.suspicious_pagination === "1";
    },

    buildSuspiciousIncompleteTraceMessage(result = {}, fetchedCount = 0) {
      const traceFetch = result.trace_fetch && typeof result.trace_fetch === "object"
        ? result.trace_fetch
        : {};
      const expected = this.getExpectedTraceCountFromPayload(result);
      const provider = this.toText(traceFetch.provider || result.provider || result.trace_provider || "provider");
      const route = this.toText(traceFetch.route_label || traceFetch.route || result.route || result.trace_route || "");
      return [
        `Trace history fetch completed with ${this.formatInteger(fetchedCount)} usable rows, which is far below the prior count estimate of ${expected >= 0 ? this.formatInteger(expected) : "Unknown"}.`,
        `Provider: ${provider}${route ? ` / ${route}` : ""}.`,
        "Tool 88 left the output unchanged because this looks like a pagination/completeness issue rather than a safe hop-logic result.",
      ].join(" ");
    },

    buildSuspiciousEmptyTraceMessage(result = {}) {
      const traceFetch = result.trace_fetch && typeof result.trace_fetch === "object"
        ? result.trace_fetch
        : {};
      const expected = this.getExpectedTraceCountFromPayload(result);
      const provider = this.toText(traceFetch.provider || result.provider || result.trace_provider || "provider");
      const route = this.toText(traceFetch.route || result.route || result.trace_route || "");
      const source = this.toText(result.source || traceFetch.source || "");
      const pages = Number(traceFetch.pages_completed ?? 0);
      const requests = Number(traceFetch.requests_made ?? result.requests_made ?? 0);
      const httpCode = this.toText(traceFetch.last_http_code || result.last_http_code || "");
      const rawCount = Number(traceFetch.last_raw_count ?? result.last_raw_count ?? -1);
      const upstreamMessage = this.toText(traceFetch.last_provider_message || traceFetch.upstream_message || result.upstream_message || "");
      const parts = [
        `Trace history fetch returned 0 usable rows even though the prior count estimate was ${this.formatInteger(expected)}.`,
        `Provider: ${provider}${route ? ` / ${route}` : ""}.`,
        source ? `Source: ${source}.` : "",
        requests ? `Requests made: ${this.formatInteger(requests)}.` : "",
        pages ? `Pages completed: ${this.formatInteger(pages)}.` : "",
        httpCode ? `Last HTTP code: ${httpCode}.` : "",
        rawCount >= 0 ? `Last raw provider row count: ${this.formatInteger(rawCount)}.` : "",
        upstreamMessage ? `Provider message: ${upstreamMessage}.` : "",
        "Tool 88 did not record a path ending because this looks like a fetch/route/normalization problem rather than dormant funds.",
      ].filter(Boolean);
      return parts.join(" ");
    },


    getExpectedTraceCountFromPayload(payload = {}) {
      const traceFetch = payload && typeof payload.trace_fetch === "object" ? payload.trace_fetch : {};
      const candidates = [
        traceFetch.expected_count,
        payload.expected_count,
        payload.expected_transaction_count,
        payload.transaction_count,
        payload.known_count,
        this.countLookupResult?.transactionCount,
        this.countLookupResult?.transaction_count,
      ];
      for (const candidate of candidates) {
        const value = Number(candidate);
        if (Number.isFinite(value) && value >= 0) {
          return Math.trunc(value);
        }
      }
      return -1;
    },

    buildTraceFetchIncompleteReport(entry, selectedAddress, transactionPayload = {}, fetchedCount = 0, expectedTraceCount = -1) {
      const lines = [];
      lines.push(this.buildCountReport(entry, selectedAddress));
      lines.push("");
      lines.push("Pass 3: Trace-history fetch completeness review");
      lines.push("Tool 88 fetched transaction rows, but it did not run hop logic because the provider result looked materially incomplete.");
      lines.push("");
      const traceFetch = transactionPayload.trace_fetch || {};
      if (transactionPayload.job_id) lines.push(`Trace job id: ${transactionPayload.job_id}`);
      lines.push(`Trace status: ${traceFetch.status || transactionPayload.trace_status || transactionPayload.status || "unknown"}`);
      lines.push(`Provider: ${traceFetch.provider || transactionPayload.provider || "unknown"}`);
      lines.push(`Route: ${traceFetch.route_label || traceFetch.route || transactionPayload.route || "unknown"}`);
      lines.push(`Rows fetched: ${this.formatInteger(fetchedCount)}`);
      lines.push(`Prior transaction-count estimate: ${expectedTraceCount >= 0 ? this.formatInteger(expectedTraceCount) : "Unknown"}`);
      if (traceFetch.pages_completed !== undefined) lines.push(`Provider pages completed: ${traceFetch.pages_completed}`);
      if (traceFetch.requests_made !== undefined) lines.push(`Provider requests made: ${traceFetch.requests_made}`);
      if (traceFetch.filtered_non_transfer !== undefined) lines.push(`Filtered non-transfer provider rows: ${traceFetch.filtered_non_transfer}`);
      if (traceFetch.missing_type !== undefined) lines.push(`Rows missing provider type field: ${traceFetch.missing_type}`);
      if (traceFetch.had_to_avoid_loop) lines.push("Pagination note: Tool 88 had to stop because the provider fingerprint did not advance.");
      if (traceFetch.message) lines.push(`Fetch note: ${traceFetch.message}`);
      lines.push("");
      lines.push("Safety result: Tool 88 left the output spreadsheet unchanged for this trace-through attempt. The fetched rows remain visible in the address inspector for review.");
      lines.push("Next check: verify the chain, address, token scope, and provider route before relying on hop logic.");
      return lines.join("\n");
    },

    buildTraceFetchNoRowsReport(entry, selectedAddress, transactionPayload = {}, expectedTraceCount = -1) {
      const lines = [];
      lines.push(this.buildCountReport(entry, selectedAddress));
      lines.push("");
      lines.push("Pass 3: Trace-history fetch review");
      lines.push("Tool 88 did not run hop logic because the provider history fetch returned no transaction rows.");
      lines.push("");
      const traceFetch = transactionPayload.trace_fetch || {};
      if (transactionPayload.job_id) lines.push(`Trace job id: ${transactionPayload.job_id}`);
      lines.push(`Trace status: ${traceFetch.status || transactionPayload.trace_status || transactionPayload.status || "unknown"}`);
      lines.push(`Provider: ${traceFetch.provider || transactionPayload.provider || "unknown"}`);
      lines.push(`Route: ${traceFetch.route_label || traceFetch.route || transactionPayload.route || "unknown"}`);
      lines.push(`Rows fetched: ${this.formatInteger(Number(traceFetch.fetched_count ?? transactionPayload.fetched_transaction_count ?? 0) || 0)}`);
      lines.push(`Prior transaction-count estimate: ${expectedTraceCount >= 0 ? this.formatInteger(expectedTraceCount) : "Unknown"}`);
      if (traceFetch.pages_completed !== undefined) lines.push(`Provider pages completed: ${traceFetch.pages_completed}`);
      if (traceFetch.requests_made !== undefined) lines.push(`Provider requests made: ${traceFetch.requests_made}`);
      if (traceFetch.last_http_code !== undefined && traceFetch.last_http_code !== null) lines.push(`Last provider HTTP code: ${traceFetch.last_http_code}`);
      if (traceFetch.upstream_status) lines.push(`Provider status: ${traceFetch.upstream_status}`);
      if (traceFetch.upstream_message) lines.push(`Provider message: ${traceFetch.upstream_message}`);
      if (traceFetch.last_error) lines.push(`Last fetch note: ${traceFetch.last_error}`);
      if (traceFetch.message && traceFetch.message !== traceFetch.last_error) lines.push(`Fetch message: ${traceFetch.message}`);
      const requestDebug = traceFetch.request_debug || transactionPayload.request_debug || null;
      if (requestDebug && typeof requestDebug === "object") {
        const debugParts = [
          requestDebug.chainid ? `chainid=${requestDebug.chainid}` : "",
          requestDebug.action ? `action=${requestDebug.action}` : "",
          requestDebug.page ? `page=${requestDebug.page}` : "",
          requestDebug.offset ? `offset=${requestDebug.offset}` : "",
          requestDebug.startblock !== undefined ? `startblock=${requestDebug.startblock}` : "",
          requestDebug.contractaddress ? `contractaddress=${requestDebug.contractaddress}` : "",
          requestDebug.moralis_chain ? `moralis_chain=${requestDebug.moralis_chain}` : "",
          requestDebug.cursor ? "cursor=present" : "",
        ].filter(Boolean);
        if (debugParts.length) {
          lines.push(`Provider request context: ${debugParts.join(", ")}`);
        }
      }
      lines.push("");
      lines.push("Safety result: Tool 88 left the output spreadsheet unchanged for this trace-through attempt. It did not mark the path as ending, because a zero-row history conflicts with the earlier transaction-count result.");
      lines.push("Next check: verify the chain, token context, provider route, and whether the count route and trace-history route are querying the same address/asset scope.");
      return lines.join("\n");
    },

    getAttributionOnlyCombinedText() {
      const result = this.attributionLookupResult || {};
      return [
        result.displayLabel,
        result.serverNote,
        result.vendorLabel,
        result.columnKValue,
        result.classification,
        result.entityType,
        result.serviceRole,
        result.walletRole,
        result.signalsText,
      ].map((value) => this.toText(value)).filter(Boolean).join(" ").toLowerCase();
    },

    getPositiveSignalMarkerText() {
      const result = this.transactionSignalResult || {};
      const raw = result.raw || {};
      return [
        result.classification,
        result.classificationLabel,
        result.eventNameText,
        result.memoText,
        raw.classification,
        raw.signal_classification,
        raw.result_type,
        raw.event_name,
        raw.eventName,
        raw.method,
        raw.methodName,
        raw.method_name,
        raw.functionName,
        raw.function_name,
        this.getSignalEventNameText(raw),
        Array.isArray(raw.signals) ? raw.signals.join(" ") : "",
        Array.isArray(raw.bridge_field_hits) ? raw.bridge_field_hits.join(" ") : "",
        Array.isArray(raw.op_return_outputs) ? raw.op_return_outputs.map((item) => item.memo || item.scriptpubkey_asm || "").join(" ") : "",
      ].map((value) => this.toText(value)).filter(Boolean).join(" ").toLowerCase();
    },

    getAttributionCombinedText() {
      const result = this.attributionLookupResult || {};
      const signal = this.transactionSignalResult || {};
      const rawSignal = signal.raw || {};
      return [
        result.displayLabel,
        result.serverNote,
        result.vendorLabel,
        result.classification,
        result.entityType,
        result.signalsText,
        signal.classification,
        signal.classificationLabel,
        signal.evidenceSummary,
        signal.evidenceLabel,
        signal.detailText,
        signal.recommendation,
        signal.eventNameText,
        this.getSignalEventNameText(rawSignal),
        Array.isArray(rawSignal.signals) ? rawSignal.signals.join(" ") : "",
        Array.isArray(rawSignal.bridge_field_hits) ? rawSignal.bridge_field_hits.join(" ") : "",
      ].map((value) => this.toText(value)).filter(Boolean).join(" ").toLowerCase();
    },

    getSignalCombinedText() {
      const result = this.transactionSignalResult || {};
      const raw = result.raw || {};
      return [
        result.chainFamily,
        result.classification,
        result.classificationLabel,
        result.summaryLabel,
        result.statusLabel,
        result.evidenceSummary,
        result.evidenceLabel,
        result.detailText,
        result.recommendation,
        result.memoText,
        result.eventNameText,
        this.getSignalEventNameText(raw),
        raw.classification,
        raw.signal_classification,
        raw.signal_status,
        raw.check_status,
        Array.isArray(raw.signals) ? raw.signals.join(" ") : "",
        Array.isArray(raw.bridge_field_hits) ? raw.bridge_field_hits.join(" ") : "",
        Array.isArray(raw.op_return_outputs) ? raw.op_return_outputs.map((item) => item.memo || item.scriptpubkey_asm || "").join(" ") : "",
      ].map((value) => this.toText(value)).filter(Boolean).join(" ").toLowerCase();
    },

    getAttributionHandleCore(label = "") {
      const clean = this.cleanAttributionLabel(label)
        .replace(/\s*\.?\s*path\s+ends\.?\s*$/i, "")
        .trim();
      if (!clean) return "";
      const match = clean.match(/(^|\s)(@[A-Za-z0-9][A-Za-z0-9_.-]{1,80})(?=$|[\s.,;:)\]}>])/);
      return match ? match[2] : "";
    },

    normalizeAttributionHandleForComparison(handle = "") {
      const clean = this.toText(handle).trim().replace(/^@+/, "").toLowerCase();
      if (!clean) return "";
      if (clean === "crypto.com") return "cryptocom";
      return clean.replace(/[^a-z0-9._-]+/g, "");
    },

    isKnownPathEndingHandleAttribution(label = "") {
      const handle = this.getAttributionHandleCore(label);
      if (!handle) return false;
      const normalized = this.normalizeAttributionHandleForComparison(handle);
      const compact = normalized.replace(/[._-]+/g, "");
      const serviceHandles = new Set(TOOL88_HANDLE_ATTRIBUTION_SERVICE_EXCEPTIONS.map((value) => this.normalizeAttributionHandleForComparison(value)));
      const compactServiceHandles = new Set([...serviceHandles].map((value) => value.replace(/[._-]+/g, "")));
      if (serviceHandles.has(normalized) || compactServiceHandles.has(compact)) return true;
      if (TOOL88_HANDLE_ATTRIBUTION_SERVICE_PATTERN.test(handle) || TOOL88_HANDLE_ATTRIBUTION_SERVICE_PATTERN.test(this.cleanAttributionLabel(label))) return true;
      return this.hasBridgeSignal(label) || this.hasSwapSignal(label);
    },

    isTraceThroughFriendlyHandleAttributionLabel(label = "") {
      const handle = this.getAttributionHandleCore(label);
      if (!handle) return false;
      return !this.isKnownPathEndingHandleAttribution(label);
    },

    getTraceThroughFriendlyHandleAttributionLabel(labels = null) {
      const labelList = Array.isArray(labels) ? labels : this.getAutomaticDecisionAttributionLabels();
      for (const label of labelList) {
        const handle = this.getAttributionHandleCore(label);
        if (handle && this.isTraceThroughFriendlyHandleAttributionLabel(label)) {
          return handle;
        }
      }
      return "";
    },

    appendRawDataTraceThroughHandleAttributionNote(row, handleLabel) {
      if (!row) return row;
      const handle = this.getAttributionHandleCore(handleLabel);
      if (!handle || this.isKnownPathEndingHandleAttribution(handle)) return row;
      const existing = this.toText(row[COL.SYSTEM_NOTES]).trim();
      if (!existing.toLowerCase().includes(handle.toLowerCase())) {
        row[COL.SYSTEM_NOTES] = this.appendSentenceNote(existing, `${handle}.`);
      }
      return row;
    },

    hasSimpleTraceThroughAttributionSignal(text = "") {
      const value = this.cleanAttributionLabel(text);
      if (!value) {
        return false;
      }
      if (this.findTraceThroughEntityByLabel(value) || this.findTraceThroughEntityByAddress(value)) {
        return true;
      }
      if (this.isTraceThroughFriendlyHandleAttributionLabel(value)) {
        return true;
      }
      if (this.isSameAddressLabel(value, this.selectedTargetAddress) || this.isValidAddressLikeLabel(value)) {
        return true;
      }
      return SIMPLE_ATTRIBUTION_TRACE_THROUGH_PATTERNS.some((pattern) => pattern.test(value));
    },

    getBatchTransferCandidate(signal = null) {
      const row = signal || this.transactionSignalResult || {};
      const raw = row.raw || row || {};
      return raw.batch_transfer_candidate || raw.batchTransferCandidate || row.batchTransferCandidate || null;
    },

    getBatchTransferCandidateValue(candidate = {}, snakeKey, camelKey, fallback = null) {
      if (!candidate || typeof candidate !== "object") return fallback;
      if (candidate[snakeKey] !== undefined) return candidate[snakeKey];
      if (candidate[camelKey] !== undefined) return candidate[camelKey];
      return fallback;
    },

    isLikelyBatchWithdrawalSignal(signal = null) {
      const row = signal || this.transactionSignalResult || {};
      if (!row) return false;
      const raw = row.raw || {};
      const classification = this.normalizeSignalCode(row.classification || raw.classification || raw.signal_classification || "");
      if (["likely_batch_transfer", "evm_batch_withdrawal_likely", "evm_internal_batch_withdrawal_likely", "native_internal_batch_withdrawal_likely", "internal_batch_withdrawal_likely", "tron_batch_withdrawal_likely", "btc_batch_withdrawal_receive_likely", "btc_exchange_withdrawal_receive_likely", "btc_withdrawal_receive_likely", "batch_withdrawal_transfer_events", "exchange_batch_withdrawal"].includes(classification)) return true;
      const positiveBatchText = [
        row.title,
        row.displayLabel,
        row.summary,
        row.evidenceSummary,
        row.recommendation,
        raw.title,
        raw.display_label,
        raw.displayLabel,
        raw.summary,
        raw.evidence_summary,
        raw.recommendation,
      ].map((value) => this.toText(value).toLowerCase()).join(" ");
      if (/batch\s+(withdrawal|transfer|payout|distribution)|exchange\s+batch\s+withdrawal|batch\s+token\s+payout/.test(positiveBatchText)) return true;

      const candidate = this.getBatchTransferCandidate(row) || {};
      const candidateSaysBatch = this.tool88AnyFlagIsTrue(
        this.getBatchTransferCandidateValue(candidate, "is_batch_transfer", "isBatchTransfer", false),
        raw.is_batch_transfer_candidate,
        raw.isBatchTransferCandidate,
        raw.batch_withdrawal_candidate,
        raw.batchWithdrawalCandidate
      );
      const selectedMatched = this.tool88AnyFlagIsTrue(
        this.getBatchTransferCandidateValue(candidate, "selected_target_matched", "selectedTargetMatched", false),
        raw.selected_target_matched,
        raw.selectedTargetMatched,
        row.selectedTargetMatchedBatchTransfer,
        raw.selected_address_is_transfer_recipient,
        raw.selectedAddressIsTransferRecipient,
        raw.selected_address_is_output,
        raw.selectedAddressIsOutput,
        raw.internal_selected_recipient_matched,
        raw.internalSelectedRecipientMatched,
        row.selectedAddressIsTransferRecipient,
        row.internalSelectedRecipientMatched
      );
      const sameSender = Boolean(this.getBatchTransferCandidateValue(candidate, "same_sender", "sameSender", false));
      const uniqueSenderCount = Number(
        this.getBatchTransferCandidateValue(candidate, "unique_sender_count", "uniqueSenderCount", raw.unique_sender_count ?? raw.uniqueSenderCount ?? raw.dominant_transfer_sender_count ?? raw.dominantTransferSenderCount)
      );
      const uniqueRecipientCount = Number(
        this.getBatchTransferCandidateValue(candidate, "unique_recipient_count", "uniqueRecipientCount", raw.unique_recipient_count ?? raw.uniqueRecipientCount ?? raw.batch_unique_recipient_count ?? raw.batchUniqueRecipientCount)
      );
      const transferCount = Number(row.transferLogCount ?? raw.transfer_log_count ?? raw.transferLogCount ?? raw.internal_transfer_count ?? raw.internalTransferCount ?? 0);
      const nonTransferCount = Number(row.nonTransferLogCount ?? raw.non_transfer_log_count ?? raw.nonTransferLogCount ?? 0);
      const logCount = Number(row.logCount ?? raw.log_count ?? raw.logCount ?? transferCount);
      const noNonTransferLogs = Number.isFinite(nonTransferCount) && nonTransferCount === 0;
      const allLogsAreTransfers = Number.isFinite(transferCount) && transferCount >= 3 && noNonTransferLogs && (!Number.isFinite(logCount) || logCount === transferCount);
      const rawSignalText = Array.isArray(raw.signals) ? raw.signals.join(" ") : this.toText(raw.signals);
      const hasExplicitSwapBridge = Boolean(
        this.tool88AnyFlagIsTrue(row.hasSwapOrBridgeSignal, raw.has_swap_or_bridge_signal, raw.hasSwapOrBridgeSignal) ||
        (Array.isArray(raw.bridge_field_hits) && raw.bridge_field_hits.length) ||
        this.hasBridgeSignal(rawSignalText) ||
        this.hasSwapSignal(rawSignalText) ||
        this.hasBridgeEventNameSignal(row.eventNameText || "") ||
        this.hasSwapEventNameSignal(row.eventNameText || "")
      );
      if (hasExplicitSwapBridge) return false;

      // Best case: backend decoded the receipt and confirmed the selected target
      // appears as one of many token-transfer recipients from a common sender.
      if (candidateSaysBatch && selectedMatched) return true;

      // Safe fallback for older backend responses: a receipt made only of multiple
      // Transfer events can be a batch withdrawal/payout. This is not enough to
      // auto-trace if the selected receiving address has meaningful attribution,
      // which is checked separately by the automatic decision path.
      if (allLogsAreTransfers && (sameSender || uniqueSenderCount >= 3 || !Number.isFinite(uniqueSenderCount)) && (uniqueRecipientCount >= 3 || !Number.isFinite(uniqueRecipientCount))) {
        return true;
      }
      return false;
    },

    isTraceFriendlyBridgeWithdrawalSignal(signal = null) {
      const row = signal || this.transactionSignalResult || {};
      if (!row) return false;
      const raw = row.raw || {};
      const classification = this.normalizeSignalCode(row.classification || raw.classification || raw.signal_classification || "");
      if (["likely_bridge_withdrawal_receive", "tron_bridge_withdrawal_receive_likely", "bridge_withdrawal_receive_likely", "post_bridge_withdrawal_receive_likely"].includes(classification)) return true;
      const selectedKey = this.normalizeAddressKey(this.selectedTargetAddress);
      const selectedMatched = Boolean(
        raw.bridge_withdrawal_receive_likely ||
        raw.is_bridge_withdrawal_receive ||
        raw.bridgeWithdrawalReceiveLikely ||
        raw.isBridgeWithdrawalReceive ||
        raw.post_bridge_receive_likely ||
        raw.postBridgeReceiveLikely
      );
      const selectedRecipient = this.normalizeAddressKey(
        raw.selected_bridge_withdrawal_recipient ||
        raw.bridgeWithdrawalRecipient ||
        raw.selected_transfer_recipient ||
        raw.selectedTransferRecipient ||
        ""
      );
      return Boolean(selectedMatched && (!selectedKey || !selectedRecipient || selectedRecipient === selectedKey));
    },

    getBridgeWithdrawalReceiveSummary(signal = null) {
      const row = signal || this.transactionSignalResult || {};
      const raw = row.raw || {};
      const sender = raw.bridge_withdrawal_sender || raw.bridgeWithdrawalSender || raw.dominant_transfer_sender || raw.dominantTransferSender || "";
      const token = raw.bridge_withdrawal_token || raw.bridgeWithdrawalToken || raw.token_symbol || raw.tokenSymbol || "";
      const amount = raw.bridge_withdrawal_amount || raw.bridgeWithdrawalAmount || raw.amount_raw || raw.amountRaw || "";
      const parts = [];
      if (sender) parts.push(`sender ${this.shortenMiddle(sender, 8, 6)}`);
      if (token) parts.push(`token ${token}`);
      if (amount) parts.push(`raw amount ${amount}`);
      return parts.join("; ") || "bridge/router withdrawal event plus selected-address token receipt";
    },

    getBatchWithdrawalSummary(signal = null) {
      const row = signal || this.transactionSignalResult || {};
      const candidate = this.getBatchTransferCandidate(row) || {};
      const raw = row.raw || {};
      const transferCount = Number(row.transferLogCount ?? raw.transfer_log_count ?? raw.transferLogCount ?? 0);
      const uniqueRecipientCount = Number(this.getBatchTransferCandidateValue(candidate, "unique_recipient_count", "uniqueRecipientCount", raw.unique_recipient_count ?? raw.uniqueRecipientCount ?? raw.batch_unique_recipient_count ?? raw.batchUniqueRecipientCount));
      const uniqueTokenCount = Number(this.getBatchTransferCandidateValue(candidate, "unique_contract_count", "uniqueContractCount", raw.unique_contract_count ?? raw.uniqueContractCount ?? raw.batch_token_contract_count ?? raw.batchTokenContractCount));
      const btcInputCount = Number(raw.btc_input_count ?? raw.btcInputCount ?? raw.input_count ?? raw.inputCount ?? 0);
      const btcOutputCount = Number(raw.btc_output_count ?? raw.btcOutputCount ?? raw.output_count ?? raw.outputCount ?? 0);
      const selectedBtc = this.toText(raw.selected_output_btc ?? raw.selectedOutputBtc ?? raw.selected_output_amount_btc ?? raw.selectedOutputAmountBtc ?? "");
      const source = this.getBatchTransferCandidateValue(candidate, "common_sender", "commonSender", raw.dominant_transfer_sender || raw.dominantTransferSender || "");
      const pieces = [];
      if (Number.isFinite(btcInputCount) && btcInputCount > 0 && Number.isFinite(btcOutputCount) && btcOutputCount > 0) pieces.push(`${btcInputCount} BTC input${btcInputCount === 1 ? "" : "s"} / ${btcOutputCount} output${btcOutputCount === 1 ? "" : "s"}`);
      else if (Number.isFinite(btcOutputCount) && btcOutputCount > 0) pieces.push(`${btcOutputCount} BTC output${btcOutputCount === 1 ? "" : "s"}`);
      if (selectedBtc) pieces.push(`selected output ${selectedBtc} BTC`);
      if (Number.isFinite(transferCount) && transferCount > 0) pieces.push(`${transferCount} Transfer logs`);
      if (Number.isFinite(uniqueRecipientCount) && uniqueRecipientCount > 0) pieces.push(`${uniqueRecipientCount} recipients`);
      if (Number.isFinite(uniqueTokenCount) && uniqueTokenCount > 0) pieces.push(`${uniqueTokenCount} token contract${uniqueTokenCount === 1 ? "" : "s"}`);
      if (source) pieces.push(`common sender ${this.shortenMiddle(source, 8, 6)}`);
      return pieces.join("; ") || "multiple outputs/transfers with no swap, bridge, or memo marker";
    },

    getBtcExchangeWithdrawalRecipientContext(entry = this.selectedEntry, selectedAddress = this.selectedTargetAddress, options = {}) {
      if (this.traceDirection !== "forward") return null;
      if (!entry || !selectedAddress) return null;

      const selectedKey = this.normalizeAddressKey(selectedAddress);
      if (!selectedKey || !this.isBtcAddress(selectedAddress)) return null;

      const family = this.toText(options.family || this.getAutoDecisionNetworkFamily()).toLowerCase();
      if (family && family !== "btc") return null;

      const selectedRow = this.getSelectedTargetRow(entry, selectedAddress) || [];
      if (this.normalizeAddressKey(selectedRow?.[COL.TO_ADDRESS]) !== selectedKey) return null;

      const meaningfulLabels = Array.isArray(options.meaningfulLabels) ? options.meaningfulLabels : [];
      if (meaningfulLabels.length || options.pathEndingAttributionClass) return null;

      const count = Number(options.count);
      const countAvailable = Boolean(options.countAvailable && Number.isFinite(count) && count >= 0);
      if (countAvailable && count >= BTC_WITHDRAWAL_RECIPIENT_TRACE_COUNT_LIMIT) return null;

      const signal = options.signal || this.transactionSignalResult || null;
      const raw = signal?.raw || {};
      const btcInputCount = Number(raw.btc_input_count ?? raw.btcInputCount ?? raw.input_count ?? raw.inputCount);
      const btcOutputCount = Number(raw.btc_output_count ?? raw.btcOutputCount ?? raw.output_count ?? raw.outputCount);
      const signalShowsOneInputManyOutputs = Number.isFinite(btcInputCount) && btcInputCount === 1 && Number.isFinite(btcOutputCount) && btcOutputCount >= 3;
      const selectedOutputMatched = Boolean(
        raw.selected_address_is_output ||
          raw.selectedAddressIsOutput ||
          raw.selected_address_is_transfer_recipient ||
          raw.selectedAddressIsTransferRecipient ||
          raw.selected_target_matched ||
          raw.selectedTargetMatched ||
          signal?.selectedAddressIsTransferRecipient
      );

      const entryText = this.collectEntrySearchText(entry).toLowerCase();
      const selectedRowText = (Array.isArray(selectedRow) ? selectedRow : [])
        .map((value) => this.toText(value).toLowerCase())
        .join(" ");
      const combinedText = `${entryText} ${selectedRowText}`;

      const oneInputMatch = combinedText.match(/\bbtc\s+1\s*in\s*\/\s*(\d+)\s*out\b/i) ||
        combinedText.match(/\b1\s*in\s*[·/]\s*(\d+)\s*out\b/i) ||
        combinedText.match(/\b1\s+input\b[\s\S]{0,80}?\b(\d+)\s+outputs?\b/i);
      const outputCountFromText = oneInputMatch ? Number(oneInputMatch[1]) : 0;
      const hasOneInput = Boolean(signalShowsOneInputManyOutputs || oneInputMatch || /\b(?:btc\s+)?1\s+(?:in|input)\b/i.test(combinedText));
      const outputCount = signalShowsOneInputManyOutputs ? btcOutputCount : outputCountFromText;
      const manyOutputs = hasOneInput && Number.isFinite(outputCount) && outputCount >= 3;
      if (!manyOutputs) return null;

      const exchangeOrWithdrawalText = /\b(?:coinbase|binance|kraken|okx|kucoin|bitget|bybit|mexc|gate(?:\.io)?|gemini|bitstamp|crypto\.com|busha|remitano|exchange|withdrawal|withdraw|payout|distribution)\b/i.test(combinedText);
      const signalLooksSafe = Boolean(
        options.simpleSignal ||
          this.normalizeSignalCode(signal?.classification || raw.classification || "") === "likely_simple_transfer" ||
          this.normalizeSignalCode(signal?.classification || raw.classification || "") === "likely_batch_transfer" ||
          this.isLikelyBatchWithdrawalSignal(signal)
      );
      if (options.complexSignal && !signalLooksSafe) return null;
      if (!exchangeOrWithdrawalText && !signalLooksSafe && !selectedOutputMatched) return null;

      const countText = countAvailable ? `${this.formatTransactionCount(count)} transaction${count === 1 ? "" : "s"}` : "count not blocking";
      const outputText = `${outputCount} output${outputCount === 1 ? "" : "s"}`;
      return {
        isLikely: true,
        outputCount,
        count,
        countAvailable,
        selectedOutputMatched,
        reason: `BTC row looks like a one-input/${outputText} exchange or service withdrawal to the selected To address, and that recipient has ${countText}.`,
      };
    },

    hasTreatmentSupport(type) {
      const attributionText = this.getAttributionOnlyCombinedText();
      const signalText = this.getPositiveSignalMarkerText();
      const combined = `${attributionText} ${signalText}`;
      if (type === "bridge") {
        return this.hasBridgeSignal(combined) || /bridge_like|destination|totoken|fromamount|minreturnamount|op_return|memo|thorchain|wormhole|swft|transit|cross-chain|cross chain/.test(combined);
      }
      if (type === "swap") {
        if (/bridge_like/.test(attributionText) || this.hasBridgeSignal(attributionText)) {
          return /swap_like|swap|router|dex|amm|complex_contract_activity|complex_evm_event_logs|complex_tron_event_logs|non-transfer|non_transfer|fromamount|minreturnamount/.test(signalText);
        }
        return this.hasSwapSignal(combined) || /swap_like|router|dex|amm|complex_contract_activity|complex_evm_event_logs|complex_tron_event_logs|non-transfer|non_transfer|multiple transfer|fromamount|minreturnamount/.test(combined);
      }
      return false;
    },

    isValidEthereumAddress(address) {
      return /^0x[a-fA-F0-9]{40}$/.test(this.toText(address));
    },

    isValidTronAddress(address) {
      return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(this.toText(address));
    },

    isValidXrpAddress(address) {
      return /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/.test(this.toText(address));
    },

    isValidBitcoinAddress(address) {
      const value = this.toText(address);
      return /^[13][1-9A-HJ-NP-Za-km-z]{25,34}$/.test(value) || /^(bc1|tb1|bcrt1)[ac-hj-np-z02-9]{11,90}$/i.test(value);
    },

    isValidBlockchainAddress(address) {
      const value = this.toText(address);
      return Boolean(
        value &&
          (this.isValidEthereumAddress(value) ||
            this.isValidTronAddress(value) ||
            this.isValidXrpAddress(value) ||
            this.isValidBitcoinAddress(value))
      );
    },

    isValidAddress(address) {
      return this.isValidBlockchainAddress(address);
    },

    isValidAddressLikeLabel(label) {
      const value = this.cleanAttributionLabel(label).replace(/^address\s*:?\s*/i, "");
      return this.isValidBlockchainAddress(value);
    },

    isSameAddressLabel(label, address) {
      const cleanLabel = this.cleanAttributionLabel(label).replace(/^address\s*:?\s*/i, "");
      return Boolean(cleanLabel && address && this.normalizeAddressKey(cleanLabel) === this.normalizeAddressKey(address));
    },

    getAutomaticDecisionAttributionLabels() {
      const result = this.attributionLookupResult || {};
      const labels = [
        result.displayLabel,
        result.serverNote,
        result.vendorLabel,
        result.columnKValue,
        ...(this.getSelectedAttributionValues ? this.getSelectedAttributionValues() : []),
      ]
        .map((value) => this.cleanAttributionLabel(value))
        .filter(Boolean)
        .filter((value, index, values) => values.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === index);
      return labels;
    },

    isTraceThroughFriendlyAttributionLabel(label, selectedAddress) {
      const cleanLabel = this.cleanAttributionLabel(label);
      if (!cleanLabel) return true;
      const unknownServiceStopLabelRegex = /(unknown.*(unattributed\s+exchange\s+hot\s+wallet|features\s+of\s+an\s+unattributed\s+exchange|service\s+address|service[-\s]?pattern|service\s*\/\s*hot|hot[-\s]?wallet|potential\s+service)|(?:unattributed\s+exchange\s+hot\s+wallet|features\s+of\s+an\s+unattributed\s+exchange|service\s+address|service[-\s]?pattern|service\s*\/\s*hot|hot[-\s]?wallet|potential\s+service).*unknown|additional\s+service\s+checks|trace\s+through\s+again\s+only\s+if|manual-path-ending|action\s*=\s*manual-path-ending)/i;
      if (unknownServiceStopLabelRegex.test(cleanLabel)) return false;
      if (this.isUnknownAttributionPlaceholder(cleanLabel)) return true;
      if (this.traceDirection === "reverse" && this.isDepositAttributionLabel(cleanLabel)) return true;
      if (this.isSelectedEntryBridgeReceiveContext(selectedAddress) && this.isBridgeServiceAttributionLabel(cleanLabel)) return true;
      if ((this.isSelectedEntrySolvedSwapContext(selectedAddress) || this.isSelectedEntrySolvedReverseSwapContext(selectedAddress)) && this.hasSwapSignal(cleanLabel)) return true;
      if (this.findTraceThroughEntityByLabel(cleanLabel)) return true;
      if (selectedAddress && this.findTraceThroughEntityByAddress(selectedAddress)) return true;
      if (this.isTraceThroughFriendlyHandleAttributionLabel(cleanLabel)) return true;
      // Address-like attribution only becomes trace-through-friendly when it is the
      // selected target address repeated back as the label. A different valid
      // address still deserves review because it is not the same as "no entity hit."
      if (this.isSameAddressLabel(cleanLabel, selectedAddress) && this.isValidAddressLikeLabel(cleanLabel)) return true;
      return this.hasSimpleTraceThroughAttributionSignal(cleanLabel);
    },

    getMeaningfulAutoDecisionAttributionLabels(selectedAddress) {
      return this.getAutomaticDecisionAttributionLabels().filter(
        (label) => !this.isTraceThroughFriendlyAttributionLabel(label, selectedAddress)
      );
    },

    getAttributionLabelsFromResult(result = {}, fallbackLabels = []) {
      const rawColumnKValues = Array.isArray(result?.columnKValues)
        ? result.columnKValues
        : (Array.isArray(result?.column_k_values) ? result.column_k_values : []);
      const labels = [
        result?.displayLabel,
        result?.display_label,
        result?.primaryLabel,
        result?.primary_label,
        result?.label,
        result?.name,
        result?.entityName,
        result?.entity_name,
        result?.serviceName,
        result?.service_name,
        result?.exchangeName,
        result?.exchange_name,
        result?.raw?.displayLabel,
        result?.raw?.display_label,
        result?.raw?.primaryLabel,
        result?.raw?.primary_label,
        result?.raw?.label,
        result?.raw?.vendorLabel,
        result?.raw?.vendor_label,
        result?.raw?.arkhamLabel,
        result?.raw?.arkham_label,
        result?.raw?.name,
        result?.raw?.entityName,
        result?.raw?.entity_name,
        result?.raw?.serviceName,
        result?.raw?.service_name,
        result?.raw?.exchangeName,
        result?.raw?.exchange_name,
        result?.serverNote,
        result?.server_note,
        result?.serverNoteText,
        result?.server_note_text,
        result?.vendorLabel,
        result?.vendor_label,
        result?.arkhamLabel,
        result?.arkham_label,
        result?.columnKValue,
        result?.column_k_value,
        ...rawColumnKValues,
        ...(Array.isArray(fallbackLabels) ? fallbackLabels : []),
      ];
      return labels
        .map((value) => this.cleanAttributionLabel(value))
        .filter(Boolean)
        .filter((value) => !this.isUnknownAttributionPlaceholder(value))
        .filter((value, index, values) => values.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === index);
    },

    isDepositAttributionLabel(label = "") {
      const clean = this.cleanAttributionLabel(label);
      if (!clean) return false;
      if (this.hasBridgeSignal(clean) || this.hasSwapSignal(clean)) return false;
      return /\bdeposit(?:s|\s+address|\s+wallet)?\b/i.test(clean);
    },

    reverseDepositShouldTraceThrough(labels = null, evidenceText = "") {
      if (this.traceDirection !== "reverse") return false;
      const attribution = this.attributionLookupResult || {};
      const attributionLabels = (Array.isArray(labels) ? labels : this.getAutomaticDecisionAttributionLabels())
        .map((value) => this.cleanAttributionLabel(value))
        .filter(Boolean);
      const activeLabels = [
        ...attributionLabels,
        attribution.displayLabel,
        attribution.columnKValue,
        attribution.vendorLabel
      ].map((value) => this.cleanAttributionLabel(value)).filter(Boolean);
      const meaningfulNonDepositLabel = activeLabels.some((label) => {
        if (this.isDepositAttributionLabel(label)) return false;
        if (this.isUnknownAttributionPlaceholder(label)) return false;
        if (this.isTraceThroughFriendlyAttributionLabel(label, this.selectedTargetAddress)) return false;
        return !this.hasBridgeSignal(label) && !this.hasSwapSignal(label);
      });
      if (meaningfulNonDepositLabel) return false;
      const classification = this.normalizeAttributionCode(attribution.classification || attribution.serviceRole || attribution.service_role || attribution.entityType || attribution.entity_type || "");
      const fallbackEvidence = this.toText(evidenceText);
      const combined = [
        ...activeLabels,
        attribution.serverNote,
        fallbackEvidence
      ].map((value) => this.toText(value)).filter(Boolean).join(" ");
      const depositLike = classification === "deposit_address"
        || activeLabels.some((label) => this.isDepositAttributionLabel(label))
        || this.isDepositAttributionLabel(fallbackEvidence);
      if (!depositLike) return false;
      return !this.hasBridgeSignal(combined) && !this.hasSwapSignal(combined);
    },

    attributionResultLooksLikeDeposit(result = this.attributionLookupResult, labels = null) {
      const row = result || {};
      const classification = this.normalizeAttributionCode(row.classification || row.serviceRole || row.service_role || row.entityType || row.entity_type || row.depositHotWallet || row.deposit_hot_wallet || "");
      const rawSignals = Array.isArray(row.signals) ? row.signals : [];
      const signals = rawSignals.map((signal) => this.normalizeAttributionCode(signal));
      if (classification === "deposit_address" || signals.includes("deposit_address")) {
        return true;
      }
      const labelText = (Array.isArray(labels) ? labels : this.getAttributionLabelsFromResult(row)).join(" ");
      return /\bdeposit(?:s|\s+address|\s+wallet)?\b/i.test(labelText);
    },

    shouldUseDepositEntityHopExtensionProbe(entry = this.selectedEntry, selectedAddress = this.selectedTargetAddress) {
      if (this.enableDepositEntityHopExtension !== true) return false;
      if (this.traceDirection !== "forward") return false;
      if (!entry || !selectedAddress) return false;
      const labels = this.getAttributionLabelsFromResult(
        this.attributionLookupResult || {},
        this.getAttributionValuesForEntry(entry, selectedAddress)
      );
      const attributionText = labels.join(" ");
      if (!this.attributionResultLooksLikeDeposit(this.attributionLookupResult || {}, labels)) return false;
      if (this.hasBridgeSignal(attributionText) || this.hasSwapSignal(attributionText)) return false;
      const originalBasis = this.buildDepositEntityComparisonBasis(labels);
      return originalBasis.keys.length > 0;
    },

    splitDepositEntityLabelParts(label = "") {
      const clean = this.cleanAttributionLabel(label);
      if (!clean) return [];
      return clean
        .split(/[\n;|]+/g)
        .map((part) => this.cleanAttributionLabel(part))
        .filter(Boolean);
    },

    normalizeDepositEntityComparisonKey(label = "") {
      let value = this.cleanAttributionLabel(label);
      if (!value) return "";

      value = value
        .replace(/^tool\s*88\s+attribution\s*:\s*/i, "")
        .replace(/\bwallet\s+role\s*:\s*(deposit|hot|cold)(?:\s+address|\s+wallet)?\.?/gi, " ")
        .replace(/\b(?:appears|identified|found|known)\s+(?:to\s+be\s+)?(?:as\s+)?/gi, " ")
        .trim();

      const routerMatch = value.match(/\brouter\s*:\s*([^.;|\n]+)/i);
      if (routerMatch && routerMatch[1]) {
        value = routerMatch[1];
      }

      value = value
        .replace(/^[^:]{0,40}\b(?:router|service|entity|exchange)\s*:\s*/i, "")
        .replace(/\(([^)]*)\)/g, " $1 ")
        .replace(/\bdeposit(?:s|\s+address|\s+wallet)?\b/gi, " ")
        .replace(/\b(?:hot|cold)\s+wallet\b/gi, " ")
        .replace(/\bwallet\b/gi, " ")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      if (!value) return "";

      const genericTerms = new Set(TOOL88_DEPOSIT_ENTITY_GENERIC_TERMS);
      const tokenTerms = new Set(TOOL88_DEPOSIT_ENTITY_TOKEN_TERMS);
      const tokens = value
        .split(" ")
        .map((token) => token.trim())
        .filter(Boolean)
        .filter((token) => !genericTerms.has(token))
        .filter((token) => !tokenTerms.has(token))
        .filter((token) => !/^\d+$/.test(token));

      return tokens.join(" ").trim();
    },

    getDepositEntityComparisonKeysFromLabels(labels = []) {
      const keys = new Set();
      const addKey = (key) => {
        const clean = this.toText(key).replace(/\s+/g, " ").trim();
        if (clean) keys.add(clean);
      };

      (Array.isArray(labels) ? labels : []).forEach((label) => {
        this.splitDepositEntityLabelParts(label).forEach((part) => {
          const key = this.normalizeDepositEntityComparisonKey(part);
          addKey(key);
          const tokens = key.split(" ").filter(Boolean);
          const firstToken = tokens[0] || "";
          if (tokens.length > 1 && firstToken && firstToken !== "crypto" && this.isStrongDepositEntityToken(firstToken)) {
            addKey(firstToken);
          }
        });
      });

      return [...keys];
    },

    isStrongDepositEntityToken(token = "") {
      const clean = this.toText(token).toLowerCase().replace(/[^a-z0-9]+/g, "");
      if (!clean || /^\d+$/.test(clean)) return false;
      if (TOOL88_DEPOSIT_ENTITY_GENERIC_TERMS.includes(clean) || TOOL88_DEPOSIT_ENTITY_TOKEN_TERMS.includes(clean)) return false;
      return clean.length >= 3;
    },

    buildDepositEntityComparisonBasis(labels = []) {
      const cleanLabels = (Array.isArray(labels) ? labels : [])
        .map((value) => this.cleanAttributionLabel(value))
        .filter(Boolean)
        .filter((value, index, values) => values.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === index);
      const keys = this.getDepositEntityComparisonKeysFromLabels(cleanLabels);
      return {
        labels: cleanLabels,
        displayLabel: cleanLabels[0] || "",
        keys,
      };
    },

    compareDepositEntityBases(originalBasis = {}, nextBasis = {}) {
      const originalKeys = Array.isArray(originalBasis.keys) ? originalBasis.keys : [];
      const nextKeys = Array.isArray(nextBasis.keys) ? nextBasis.keys : [];
      for (const originalKey of originalKeys) {
        for (const nextKey of nextKeys) {
          const match = this.compareDepositEntityKeyPair(originalKey, nextKey);
          if (match.matched) {
            return {
              matched: true,
              matchedKey: match.matchedKey || originalKey,
              originalKey,
              nextKey,
            };
          }
        }
      }
      return { matched: false, matchedKey: "", originalKey: "", nextKey: "" };
    },

    compareDepositEntityKeyPair(originalKey = "", nextKey = "") {
      const left = this.toText(originalKey).replace(/\s+/g, " ").trim();
      const right = this.toText(nextKey).replace(/\s+/g, " ").trim();
      if (!left || !right) return { matched: false, matchedKey: "" };
      if (left === right) return { matched: true, matchedKey: left };

      const leftTokens = left.split(" ").filter((token) => this.isStrongDepositEntityToken(token));
      const rightTokens = right.split(" ").filter((token) => this.isStrongDepositEntityToken(token));
      if (!leftTokens.length || !rightTokens.length) return { matched: false, matchedKey: "" };

      const shorter = leftTokens.length <= rightTokens.length ? leftTokens : rightTokens;
      const longer = leftTokens.length <= rightTokens.length ? rightTokens : leftTokens;
      const shorterMatches = shorter.every((token) => longer.includes(token));
      if (shorterMatches) {
        return { matched: true, matchedKey: shorter.join(" ") };
      }

      return { matched: false, matchedKey: "" };
    },

    getDepositEntityHopExtensionCandidateAddresses(solution = {}, selectedAddress = this.selectedTargetAddress) {
      if (this.traceDirection !== "forward") return [];
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const addresses = [];
      (Array.isArray(solution.exitTransactions) ? solution.exitTransactions : []).forEach((exit) => {
        const address = this.toText(exit?.toAddress || exit?.to_address || exit?.to || "");
        if (address) addresses.push(address);
      });
      (Array.isArray(solution.exitRows) ? solution.exitRows : []).forEach((row) => {
        const address = this.toText(row?.[COL.TO_ADDRESS]);
        if (address) addresses.push(address);
      });
      const seen = new Set();
      return addresses.filter((address) => {
        const key = this.normalizeAddressKey(address) || address.toLowerCase();
        if (!key || (selectedKey && key === selectedKey) || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },

    async lookupDepositEntityCandidateAttribution(entry, candidateAddress) {
      try {
        const raw = await this.lookupAttributionData(entry, candidateAddress);
        return this.normalizeAttributionLookupResult(raw, candidateAddress);
      } catch (error) {
        const message = error?.message || "Unknown attribution error";
        return this.normalizeAttributionLookupResult({
          address: candidateAddress,
          display_label: "",
          classification: "",
          entity_type: "unknown",
          advice_status: "neutral",
          source: "deposit_extension_attribution_error",
          recommendation: `Next-hop attribution lookup failed: ${message}`,
          warnings: [message],
        }, candidateAddress);
      }
    },

    async evaluateDepositEntityHopExtension(entry, selectedAddress, solution = {}) {
      if (!this.shouldUseDepositEntityHopExtensionProbe(entry, selectedAddress)) {
        return { applies: false, action: "not_applicable" };
      }

      const originalLabels = this.getAttributionLabelsFromResult(
        this.attributionLookupResult || {},
        this.getAttributionValuesForEntry(entry, selectedAddress)
      );
      const originalBasis = this.buildDepositEntityComparisonBasis(originalLabels);
      const candidateAddresses = this.getDepositEntityHopExtensionCandidateAddresses(solution, selectedAddress);
      const baseDecision = {
        applies: true,
        originalAddress: selectedAddress,
        originalLabel: originalBasis.displayLabel || "Deposit address",
        originalEntityKeys: originalBasis.keys,
        candidateAddresses,
        nextLookups: [],
        keepCandidate: false,
        dropCandidate: false,
        action: "review",
        summary: "",
        pathEndingReason: "",
      };

      if (!candidateAddresses.length) {
        return {
          ...baseDecision,
          action: "drop_candidate",
          dropCandidate: true,
          summary: "The deposit hot-wallet probe was enabled, but Tool 88 did not find a distinct outgoing candidate to validate.",
          pathEndingReason: "Deposit hot-wallet probe was enabled, but Tool 88 did not find a distinct next-hop address to validate, so the extra hop was dropped and the path remains stopped at the deposit address.",
        };
      }

      if (!originalBasis.keys.length) {
        return {
          ...baseDecision,
          action: "drop_candidate",
          dropCandidate: true,
          summary: "Tool 88 found a deposit-address candidate but could not extract a reliable base entity from the original attribution.",
          pathEndingReason: "Deposit hot-wallet probe was enabled, but Tool 88 could not extract a reliable same-entity name from the original deposit attribution, so the extra hop was dropped and the path remains stopped at the deposit address.",
        };
      }

      if (candidateAddresses.length > 10) {
        return {
          ...baseDecision,
          action: "drop_candidate",
          dropCandidate: true,
          summary: `Tool 88 found ${this.formatInteger(candidateAddresses.length)} candidate next-hop addresses, which is too many for the deposit hot-wallet probe to validate automatically.`,
          pathEndingReason: `Deposit hot-wallet probe was enabled, but ${this.formatInteger(candidateAddresses.length)} candidate next-hop addresses were found. Tool 88 dropped the extra hop and kept the original deposit as the path ending for review.`,
        };
      }

      this.statusMessage = "Checking whether the deposit hop stays inside the same entity...";
      const nextLookups = [];
      for (const candidateAddress of candidateAddresses) {
        const attribution = await this.lookupDepositEntityCandidateAttribution(entry, candidateAddress);
        const nextLabels = this.getAttributionLabelsFromResult(attribution);
        const nextBasis = this.buildDepositEntityComparisonBasis(nextLabels);
        const comparison = this.compareDepositEntityBases(originalBasis, nextBasis);
        nextLookups.push({
          address: candidateAddress,
          label: nextBasis.displayLabel || attribution.displayLabel || "",
          classification: attribution.classification || attribution.entityType || "",
          sourceLabel: attribution.sourceLabel || attribution.source || "",
          entityKeys: nextBasis.keys,
          matched: comparison.matched,
          matchedKey: comparison.matchedKey,
          originalKey: comparison.originalKey,
          nextKey: comparison.nextKey,
          warnings: attribution.warnings || [],
          attribution,
        });
      }

      const failedLookup = nextLookups.find((lookup) => !lookup.matched);
      if (failedLookup) {
        const nextLabel = failedLookup.label || this.shortenMiddle(failedLookup.address, 12, 8) || "unattributed next hop";
        return {
          ...baseDecision,
          nextLookups,
          action: "drop_candidate",
          dropCandidate: true,
          summary: `The next-hop attribution (${nextLabel}) did not confirm the same base entity as ${originalBasis.displayLabel || "the original deposit"}.`,
          pathEndingReason: `Deposit hot-wallet probe was enabled. Tool 88 traced one hop beyond the deposit address, but the next-hop attribution (${nextLabel}) did not match the original deposit entity (${originalBasis.displayLabel || originalBasis.keys.join(" / ") || "unknown"}). The extra hop was dropped and the path remains stopped at the deposit address.`,
        };
      }

      const matchedLabels = nextLookups.map((lookup) => lookup.label || this.shortenMiddle(lookup.address, 12, 8)).filter(Boolean);
      return {
        ...baseDecision,
        nextLookups,
        action: "keep_candidate",
        keepCandidate: true,
        summary: `The deposit hot-wallet probe kept the extra hop because the next-hop attribution matched the same entity (${matchedLabels.join("; ") || originalBasis.keys.join(" / ")}).`,
      };
    },

    annotateDepositEntityHopExtensionExitRows(solution = {}, decision = {}) {
      if (!solution || !decision?.keepCandidate || !Array.isArray(solution.exitRows)) return solution;
      const lookupByAddress = new Map();
      (decision.nextLookups || []).forEach((lookup) => {
        const key = this.normalizeAddressKey(lookup.address);
        if (key) lookupByAddress.set(key, lookup);
      });

      solution.exitRows = solution.exitRows.map((row) => {
        const cloned = this.cloneRowPreservingValues(row);
        const lookup = lookupByAddress.get(this.normalizeAddressKey(cloned?.[COL.TO_ADDRESS]));
        const nextLabel = this.cleanAttributionLabel(lookup?.label || lookup?.attribution?.displayLabel || "");
        if (nextLabel && !this.toText(cloned[COL.ATTRIBUTION])) {
          cloned[COL.ATTRIBUTION] = nextLabel;
        }
        this.appendTool88NoteToRow(
          cloned,
          `Tool 88: Deposit hot-wallet probe kept this hop because ${nextLabel || "the next-hop attribution"} matched the original deposit entity ${decision.originalLabel || "same entity"}.`
        );
        return cloned;
      });
      solution.depositEntityHopExtension = decision;
      return solution;
    },

    getAutoDecisionNetworkFamily() {
      const countResult = this.countLookupResult || {};
      const entry = this.selectedEntry;
      const selectedAddress = this.selectedTargetAddress;
      const inferred = selectedAddress ? this.inferCountRequestContext(entry, selectedAddress) : {};
      return this.toText(
        countResult.networkFamily ||
          countResult.network_family ||
          inferred.network_family ||
          countResult.chainFamily ||
          inferred.chain_family ||
          ""
      ).toLowerCase();
    },

    stripBatchWithdrawalDecisionText(text = "") {
      return this.toText(text)
        .replace(/likely_batch_transfer/gi, " ")
        .replace(/evm_batch_withdrawal_likely/gi, " ")
        .replace(/evm_internal_batch_withdrawal_likely/gi, " ")
        .replace(/native_internal_batch_withdrawal_likely/gi, " ")
        .replace(/tron_batch_withdrawal_likely/gi, " ")
        .replace(/btc_batch_withdrawal_receive_likely/gi, " ")
        .replace(/btc_exchange_withdrawal_receive_likely/gi, " ")
        .replace(/btc_withdrawal_receive_likely/gi, " ")
        .replace(/exchange\s+batch\s+withdrawal/gi, " ")
        .replace(/batch\s+(withdrawal|transfer|payout|distribution)/gi, " ")
        .replace(/batch\s+token\s+payout/gi, " ")
        .replace(/provider\s+marked\s+this\s+as\s+a\s+batch\s+withdrawal\s+candidate/gi, " ");
    },

    buildAutomaticDecision({ action, title, summary, reasons = [], status = "neutral", confidenceLabel = "Review", buttonLabel = "Auto Decide", specialTreatment = "", metadata = null }) {
      return { action, title, summary, reasons, status, confidenceLabel, buttonLabel, specialTreatment, metadata };
    },

    addressPoisoningAffixMatch(leftAddress = "", rightAddress = "") {
      const normalize = (value) => {
        const text = this.toText(value).trim().toLowerCase();
        return /^0x[a-f0-9]{40}$/.test(text) ? text.slice(2) : "";
      };
      const left = normalize(leftAddress);
      const right = normalize(rightAddress);
      if (!left || !right || left === right || left.length !== right.length) return null;
      let prefixLength = 0;
      while (prefixLength < left.length && left[prefixLength] === right[prefixLength]) prefixLength += 1;
      let suffixLength = 0;
      while (
        suffixLength < left.length - prefixLength
          && left[left.length - 1 - suffixLength] === right[right.length - 1 - suffixLength]
      ) {
        suffixLength += 1;
      }
      const totalMatchLength = prefixLength + suffixLength;
      const qualifies = totalMatchLength >= 8
        && ((prefixLength >= 5 && suffixLength >= 3) || (prefixLength >= 4 && suffixLength >= 4));
      return qualifies ? { prefixLength, suffixLength, totalMatchLength } : null;
    },

    buildAddressPoisoningEvidence(entry = this.selectedEntry, selectedAddress = this.selectedTargetAddress) {
      if (this.traceDirection !== "reverse" || !entry || !selectedAddress) return null;
      const selectedRow = this.getSelectedTargetRow(entry, selectedAddress)
        || entry.rows?.find(({ row }) => this.normalizeAddressKey(row?.[COL.FROM_ADDRESS]) === this.normalizeAddressKey(selectedAddress))?.row
        || entry.rows?.[0]?.row
        || [];
      const token = this.toText(selectedRow?.[COL.TOKEN] || this.getSelectedTokenSymbol(entry, selectedAddress)).trim().toUpperCase();
      const stablecoins = new Set(["USDT", "USDC", "USDD", "DAI", "BUSD", "TUSD", "USDP", "GUSD", "PYUSD", "FDUSD", "AUSDT", "BSCUSD", "BSC-USD"]);
      const amount = this.parseNumberLike(selectedRow?.[COL.AMOUNT]);
      const funds = this.deriveFundsIn(entry, selectedAddress);
      const fundsFollowed = Number(funds?.amount);
      if (!stablecoins.has(token)) return null;
      if (!Number.isFinite(amount) || amount <= 0 || amount > 1) return null;
      if (Number.isFinite(fundsFollowed) && fundsFollowed > 1) return null;
      if (!/^0x[a-fA-F0-9]{40}$/.test(this.toText(selectedAddress))) return null;
      if (!/^0x[a-fA-F0-9]{40}$/.test(this.toText(selectedRow?.[COL.TO_ADDRESS]))) return null;
      if (this.getMeaningfulAutoDecisionAttributionLabels(selectedAddress).length) return null;

      const parentHash = this.normalizeHashKey(selectedRow?.[COL.FROM_HASH]);
      if (!parentHash) return null;
      const counterpartyKey = this.normalizeAddressKey(selectedRow?.[COL.TO_ADDRESS]);
      const parentEntries = (Array.isArray(this.parsedEntries) ? this.parsedEntries : [])
        .filter((candidate) => this.normalizeHashKey(candidate?.txHash) === parentHash);
      let best = null;
      parentEntries.forEach((parentEntry) => {
        const parentRows = Array.isArray(parentEntry?.rows) ? parentEntry.rows.map(item => item?.row || []) : [];
        const connected = parentRows.some(row => this.normalizeAddressKey(row?.[COL.FROM_ADDRESS]) === counterpartyKey);
        parentRows.forEach((row) => {
          const lookalikeAddress = this.toText(row?.[COL.TO_ADDRESS]);
          const match = this.addressPoisoningAffixMatch(selectedAddress, lookalikeAddress);
          if (!match) return;
          const score = (connected ? 100 : 0) + (match.totalMatchLength * 10) + match.prefixLength + match.suffixLength;
          if (!best || score > best.score) {
            best = {
              ...match,
              score,
              connected,
              lookalikeAddress,
              parentHash: parentEntry?.txHash || parentHash,
              parentPathLabel: parentEntry?.pathLabel || "",
            };
          }
        });
      });
      if (!best || !best.connected) return null;

      const amountText = this.formatFunds(amount, token);
      const fundsText = Number.isFinite(fundsFollowed) ? this.formatFunds(fundsFollowed, funds?.token || token) : "";
      const fundsContext = Number.isFinite(fundsFollowed)
        ? (fundsFollowed <= 0
          ? " and no case funds were allocated to this branch"
          : ` and ${fundsText} of case funds were allocated to this branch`)
        : "";
      const reason = `Likely address-poisoning dust transfer: ${selectedAddress} closely resembles ${best.lookalikeAddress}, which appears one hop later in this reverse trace (${best.prefixLength} matching leading and ${best.suffixLength} matching trailing hexadecimal characters). The transfer amount is ${amountText}${fundsContext}.`;
      return {
        selectedAddress,
        lookalikeAddress: best.lookalikeAddress,
        parentHash: best.parentHash,
        parentPathLabel: best.parentPathLabel,
        prefixLength: best.prefixLength,
        suffixLength: best.suffixLength,
        totalMatchLength: best.totalMatchLength,
        amount,
        amountText,
        fundsFollowed,
        fundsText,
        token,
        reason,
      };
    },

    isBatchTransferFriendlySignal(signal, entry = this.selectedEntry, selectedAddress = this.selectedTargetAddress) {
      if (!signal) return false;
      const raw = signal.raw || {};
      const classification = this.normalizeSignalCode(signal.classification || raw.classification || raw.signal_classification || "");
      const family = this.toText(signal.chainFamily || raw.network_family || raw.chain_family || this.getAutoDecisionNetworkFamily()).toLowerCase();
      const transferLogCount = Number(
        signal.transferLogCount ?? raw.transfer_log_count ?? raw.transferLogCount ?? raw.transfer_logs_count ?? raw.transferLogsCount
      );
      const nonTransferLogCount = Number(
        signal.nonTransferLogCount ?? raw.non_transfer_log_count ?? raw.nonTransferLogCount ?? raw.non_transfer_logs_count ?? raw.nonTransferLogsCount
      );
      const logCount = Number(signal.logCount ?? raw.log_count ?? raw.logCount ?? raw.logs_count ?? raw.logsCount);
      const explicitBatchFlag = Boolean(
        this.tool88AnyFlagIsTrue(
          signal.isBatchWithdrawal,
          signal.isBatchTransfer,
          raw.is_batch_withdrawal,
          raw.isBatchWithdrawal,
          raw.batch_withdrawal_likely,
          raw.batchWithdrawalLikely,
          raw.exchange_batch_withdrawal_likely
        ) ||
          classification === "batch_token_withdrawal_likely" || classification === "likely_batch_transfer"
      );
      const allLogsAreTransfers = Number.isFinite(transferLogCount) && transferLogCount > 1 && (
        (Number.isFinite(nonTransferLogCount) && nonTransferLogCount === 0) ||
        (Number.isFinite(logCount) && logCount === transferLogCount)
      );
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const targetColumnIndex = this.getTargetColumnIndex();
      const selectedRow = entry && selectedAddress ? this.getSelectedTargetRow(entry, selectedAddress) : null;
      const selectedAppearsInEntry = Boolean(
        selectedKey &&
          selectedRow &&
          this.normalizeAddressKey(selectedRow?.[targetColumnIndex]) === selectedKey
      );
      const selectedMatchedInProvider = Boolean(
        this.tool88AnyFlagIsTrue(
          signal.selectedTransferMatched,
          raw.selected_transfer_matched,
          raw.selectedTransferMatched,
          raw.selected_address_is_transfer_recipient,
          raw.selectedAddressIsTransferRecipient,
          raw.selected_address_is_output,
          raw.selectedAddressIsOutput,
          raw.batch_selected_recipient_matched,
          raw.batchSelectedRecipientMatched
        ) ||
          (raw.batch_selected_recipient && this.normalizeAddressKey(raw.batch_selected_recipient) === selectedKey) ||
          (signal.batchSelectedRecipient && this.normalizeAddressKey(signal.batchSelectedRecipient) === selectedKey)
      );
      const countContext = entry && selectedAddress ? this.inferCountRequestContext(entry, selectedAddress) : {};
      const tokenTransferContext = this.toText(countContext.count_kind || raw.count_kind || raw.countKind).toLowerCase().includes("token") ||
        Boolean(countContext.token_contract || countContext.contract_address || raw.token_contract || raw.contract_address);
      const hasExplicitSwapBridge = Boolean(
        this.tool88AnyFlagIsTrue(signal.hasMemo, raw.has_memo, raw.hasMemo, signal.hasBridgeEventSignal, signal.hasSwapEventSignal) ||
          (!explicitBatchFlag && !allLogsAreTransfers && this.tool88AnyFlagIsTrue(signal.hasSwapOrBridgeSignal, raw.has_swap_or_bridge_signal, raw.hasSwapOrBridgeSignal))
      );
      if (hasExplicitSwapBridge) return false;
      if (explicitBatchFlag && (selectedMatchedInProvider || selectedAppearsInEntry)) return true;
      if (family === "btc" && explicitBatchFlag && selectedAppearsInEntry) return true;
      return ["evm", "tron"].includes(family) && tokenTransferContext && allLogsAreTransfers && selectedAppearsInEntry;
    },


    getAutomaticDecisionSelectedEntryEvidenceText() {
      const entry = this.selectedEntry;
      if (!entry || !Array.isArray(entry.rows)) return "";
      const selectedKey = this.normalizeAddressKey(this.selectedTargetAddress);
      const targetColumnIndex = this.getTargetColumnIndex();
      const attributionColumnIndex = this.getAttributionColumnIndex();
      const matchingRows = selectedKey
        ? entry.rows.filter(({ row }) => this.normalizeAddressKey(row?.[targetColumnIndex]) === selectedKey)
        : entry.rows;
      const rows = matchingRows.length ? matchingRows : entry.rows;
      return rows
        .map(({ row }) => [
          row?.[COL.SYSTEM_NOTES],
          row?.[attributionColumnIndex],
          row?.[COL.USER_NOTES],
          row?.[COL.TOOL88_NOTES],
          row?.[COL.CONTINUES_AS]
        ].map((value) => this.toText(value)).filter(Boolean).join(" "))
        .filter(Boolean)
        .join(" ");
    },

    getAutomaticDecisionServiceEndpointEvidence() {
      const selectedEntryText = this.getAutomaticDecisionSelectedEntryEvidenceText();
      const lookupText = [
        this.attributionLookupResult?.displayLabel,
        this.attributionLookupResult?.label,
        this.attributionLookupResult?.serverNote,
        this.attributionLookupResult?.vendorLabel,
        this.attributionLookupResult?.suggestedColumnJNote,
        this.attributionLookupResult?.recommendation,
        this.pathEndingResult?.note,
        this.pathEndingResult?.reason,
        this.pathEndingResult?.specialTreatment,
        ...(Array.isArray(this.attributionLookupResult?.signals) ? this.attributionLookupResult.signals : []),
        ...(Array.isArray(this.countLookupResult?.warnings) ? this.countLookupResult.warnings : []),
        this.countLookupResult?.recommendation,
        this.countLookupResult?.message
      ].map((value) => this.toText(value)).filter(Boolean).join(" ");
      let combined = `${selectedEntryText} ${lookupText}`.trim();
      if (!combined) return null;
      const attribution = this.attributionLookupResult || {};
      const currentAttributionClass = this.normalizeAttributionCode(attribution.classification || attribution.entityType || "");
      const currentAttributionSupportsService = [
        "deposit_address",
        "hot_wallet",
        "attributed_service",
        "bridge_like",
        "swap_like",
        "swap_or_bridge",
      ].includes(currentAttributionClass);
      const currentAttributionDoesNotSupportService = Boolean(
        attribution.unknownPlaceholderOnly ||
        (this.isUnknownAttributionPlaceholder(attribution.displayLabel || attribution.columnKValue || "") && !attribution.hasMeaningfulAttribution) ||
        (!attribution.hasMeaningfulAttribution && !currentAttributionSupportsService)
      );
      const bridgeEvidenceText = [
        selectedEntryText,
        lookupText,
        this.getPositiveSignalMarkerText ? this.getPositiveSignalMarkerText() : '',
        this.transactionSignalResult?.eventNameText,
        this.getSignalEventNameText ? this.getSignalEventNameText(this.transactionSignalResult?.raw || {}) : '',
      ].map((value) => this.toText(value)).filter(Boolean).join(' ');
      const selectedAddress = this.selectedTargetAddress || '';
      const selectedIsPostBridge = Boolean(
        (this.isSelectedEntryBridgeReceiveContext && this.isSelectedEntryBridgeReceiveContext(selectedAddress)) ||
        (this.isSelectedEntryPostBridgeContinuationContext && this.isSelectedEntryPostBridgeContinuationContext(selectedAddress))
      );
      const namedBridgeService = this.getMatchedBridgeServiceName ? this.getMatchedBridgeServiceName(bridgeEvidenceText) : '';
      const explicitBridgeLikeAttribution = ['bridge_like', 'swap_or_bridge'].includes(currentAttributionClass)
        || /\btool\s*88\s+attribution\s*:[^\n.]*\bbridge\s*like\b/i.test(bridgeEvidenceText);
      const namedBridgeText = Boolean(namedBridgeService || /\b(?:transitswap|transit\s+swap|swft\s+blockchain|swft\s+bridge|wormhole|rango|bridgers|allbridge|chainflip|butterswap|near[-\s]?intents)\b/i.test(bridgeEvidenceText));
      if (!selectedIsPostBridge && (namedBridgeText || explicitBridgeLikeAttribution)) {
        // Bridge services can also look like high-count service/hot-wallet
        // addresses. Do not let a generated Unknown/service path-ending note
        // win before the bridge decision has a chance to run; the bridge solver
        // is the constructive next step for a named bridge initiation row.
        return null;
      }
      const serviceAssessment = this.pathEndingResult?.serviceAssessment || {};
      const explicitAssessorStop = Boolean(
        serviceAssessment.likely ||
        serviceAssessment.likely_hot_wallet ||
        serviceAssessment.likelyHotWallet ||
        serviceAssessment.stop_early ||
        serviceAssessment.stopEarly ||
        serviceAssessment.stop_reason ||
        serviceAssessment.stopReason ||
        this.toText(serviceAssessment.status).toLowerCase() === "stop"
      );
      if (currentAttributionDoesNotSupportService && !explicitAssessorStop && this.pathEndingResult?.specialTreatment !== "potential_service_address") {
        // Older runs could write this synthesized sentence after misclassifying
        // Column K `Unknown`. Do not let that generated sentence become its own
        // evidence on a later run when the current checks show no service signal.
        combined = combined
          .replace(/Unknown\.\s*Address\s+has\s+features\s+of\s+an\s+unattributed\s+exchange\s+hot\s+wallet\s+or\s+service\s+address\.?(?:\s*Path\s+ends\.?)?/gi, " ")
          .replace(/Tool\s*88\s+automatic\s+decision:\s*The\s+attribution\s+lookup\s+classified\s+this\s+address\s+as\s+a\s+service\/deposit\/hot-wallet\s+style\s+endpoint[^.]*\.?/gi, " ")
          .replace(/Tool\s*88\s+attribution:\s*Unknown\.?/gi, " ")
          .replace(/\s+/g, " ")
          .trim();
        if (!combined) return null;
      }
      const hasUnknown = /\bunknown\b|no\s+visible\s+hit/i.test(combined);
      const hasServiceEvidence = /unattributed\s+exchange\s+hot\s+wallet|features\s+of\s+an\s+unattributed\s+exchange|exchange\s+hot\s+wallet|service\s+address|service[-\s]?pattern|service\s*\/\s*hot|hot\s*wallet|potential\s+service|additional\s+service\s+checks|high\s+stablecoin\s+flow|high\s+erc20\s+transfer\s+count|trace\s+through\s+again\s+only\s+if/i.test(combined);
      const hasPathEndingEvidence = /path\s*ends|manual-path-ending|tool\s*88\s+stopped|action\s*=\s*manual-path-ending|stop\s+early|stopped\s+after\s+reviewing|uncheck\s+additional\s+service\s+checks/i.test(combined);
      if (hasUnknown && hasServiceEvidence && hasPathEndingEvidence) {
        const reasonMatch = combined.match(/Unknown\.\s*Address\s+has\s+features\s+of\s+an\s+unattributed\s+exchange\s+hot\s+wallet\s+or\s+service\s+address\.?/i);
        return {
          reason: reasonMatch?.[0] || "The selected row already contains Tool 88 Unknown / potential service-address evidence from the hot-wallet assessor."
        };
      }
      if (this.pathEndingResult?.specialTreatment === "potential_service_address") {
        return {
          reason: "Tool 88's service-address assessor already classified this endpoint as a potential service/hot-wallet style path ending."
        };
      }
      return null;
    },

    buildAutomaticDecisionPreview() {
      if (!this.countLookupResult || !this.selectedEntry || !this.selectedTargetAddress) {
        return this.buildAutomaticDecision({
          action: "review",
          title: "Waiting for checks",
          summary: "Process an entry and select a target address before Tool 88 can make an automatic recommendation.",
          status: "neutral",
          confidenceLabel: "Not ready",
          buttonLabel: "Auto Decide",
        });
      }

      const selectedAddress = this.selectedTargetAddress;
      const family = this.getAutoDecisionNetworkFamily();
      const btcContext = family === 'btc' || this.isBtcHopContext(this.selectedEntry, selectedAddress, []);
      const btcReverseInputSelection = this.traceDirection === 'reverse'
        ? this.getBtcReverseInputSelectionMetadata(this.selectedEntry, selectedAddress)
        : null;
      if (this.selectedReverseEntryHasZeroFundsAllocation(this.selectedEntry, selectedAddress)) {
        return this.buildAutomaticDecision({
          action: "review",
          title: "Reverse allocation needs recalculation",
          summary: "This prior source row has a positive transaction amount but zero Funds Followed. Tracing a zero allocation cannot produce a meaningful earlier source.",
          reasons: [
            "The row appears to come from an older reverse allocation whose sub-cent precision was reduced to zero.",
            "Rebuild or review the parent reverse allocation before continuing this branch.",
          ],
          status: "warn",
          confidenceLabel: "Allocation issue",
          buttonLabel: "Review allocation",
        });
      }
      if (btcReverseInputSelection?.explicit) {
        return this.buildAutomaticDecision({
          action: 'trace',
          title: 'Trace backward through selected Bitcoin input',
          summary: 'The investigator selected a specific Bitcoin input. Tool 88 should follow that input to its previous output and identify the input UTXOs that funded it.',
          reasons: [
            btcReverseInputSelection.previousTransactionHash
              ? `Selected input spends previous transaction ${btcReverseInputSelection.previousTransactionHash}${btcReverseInputSelection.previousOutputIndex !== null ? ` output #${btcReverseInputSelection.previousOutputIndex}` : ''}.`
              : 'The selected input metadata identifies the source side to trace backward.',
            'Bitcoin UTXO ancestry is determined from the selected input. Transaction outputs remain visible as context and are not swap evidence.'
          ],
          status: 'ok',
          confidenceLabel: 'Reverse BTC input',
          buttonLabel: 'Auto Decide: Trace Input Backward',
        });
      }
      const bridgeReceiveEntryContext = this.isSelectedEntryBridgeReceiveContext(selectedAddress);
      const postBridgeContinuationContext = this.isSelectedEntryPostBridgeContinuationContext(selectedAddress);
      const postBridgeTraceContext = bridgeReceiveEntryContext || postBridgeContinuationContext;
      const preServiceBridgeEvidenceText = [
        this.getAutomaticDecisionSelectedEntryEvidenceText(),
        this.getAttributionOnlyCombinedText(),
        ...this.getAutomaticDecisionAttributionLabels(),
        this.getPositiveSignalMarkerText(),
        this.transactionSignalResult?.eventNameText,
        this.transactionSignalResult?.memoText,
        this.transactionSignalResult?.classification,
        this.transactionSignalResult?.classificationLabel,
        this.transactionSignalResult?.evidenceSummary,
        this.transactionSignalResult?.detailText,
      ].map((value) => this.toText(value)).filter(Boolean).join(" ");
      const preServiceBridgeName = !postBridgeTraceContext ? this.getMatchedBridgeServiceName(preServiceBridgeEvidenceText) : "";
      const preServiceStrongBridgeEvidence = /\b(?:transitswap|transit\s+swap|swft\s+blockchain|swft\s+bridge|wormhole|rango|bridgers|allbridge|chainflip|butterswap|near[-\s]?intents|portal\s+bridge|destination\s*[:=]|to\s*token|minreturn|fromamount|tochain|dstchain)\b/i.test(preServiceBridgeEvidenceText)
        || /\btool\s*88\s+attribution\s*:[^\n.]*\bbridge\s*like\b/i.test(preServiceBridgeEvidenceText);
      if (!postBridgeTraceContext && (preServiceBridgeName || preServiceStrongBridgeEvidence)) {
        return this.buildAutomaticDecision({
          action: "bridge",
          title: "Treat as bridge",
          summary: "The selected address has bridge-style attribution or bridge transaction evidence, so Tool 88 should try the bridge solver before accepting a generic service/hot-wallet path ending.",
          reasons: [preServiceBridgeName ? `Matched bridge service: ${preServiceBridgeName}.` : "Bridge-style evidence matched before the service endpoint stop rule."],
          status: "warn",
          confidenceLabel: "Bridge evidence",
          buttonLabel: "Auto Decide: Bridge",
        });
      }
      const reverseDepositTraceThrough = this.reverseDepositShouldTraceThrough(
        this.getAutomaticDecisionAttributionLabels(),
        this.getAutomaticDecisionSelectedEntryEvidenceText()
      );
      const reverseSwapResultEntryContext = this.isSelectedEntrySolvedReverseSwapContext(selectedAddress);
      if (reverseSwapResultEntryContext && this.canTraceThrough) {
        const funds = this.deriveFundsIn(this.selectedEntry, selectedAddress);
        const leftBounds = this.getEntryLeftBoundsAmount(this.selectedEntry, selectedAddress);
        return this.buildAutomaticDecision({
          action: "trace",
          title: "Trace backward through the pre-swap asset",
          summary: "This reverse entry is already a solved swap result. Tool 88 should continue into the earlier transactions that funded the asset sent into the swap.",
          reasons: [
            Number.isFinite(funds?.amount) && funds?.token
              ? `Reverse tracing continues with ${this.formatFunds(funds.amount, funds.token)}${Number.isFinite(leftBounds) ? ` at left bounds ${this.formatFunds(leftBounds, funds.token)}` : ""}.`
              : "The row stores the pre-swap asset in the normal funds-followed fields for reverse tracing.",
            "The swap service attribution describes the same-hash conversion and is not treated as the source-of-funds ending."
          ],
          status: "ok",
          confidenceLabel: "Reverse swap continued",
          buttonLabel: "Auto Decide: Trace Backward",
        });
      }
      const reverseSwapEvidenceText = [
        this.getAutomaticDecisionSelectedEntryEvidenceText(),
        this.getAttributionOnlyCombinedText(),
        ...this.getAutomaticDecisionAttributionLabels(),
        this.getPositiveSignalMarkerText(),
        this.transactionSignalResult?.eventNameText,
        this.transactionSignalResult?.classification,
        this.transactionSignalResult?.classificationLabel,
        this.transactionSignalResult?.evidenceSummary,
        this.transactionSignalResult?.detailText,
      ].map((value) => this.toText(value)).filter(Boolean).join(" ");
      const reverseSwapSignalBeforeService = this.traceDirection === "reverse"
        && !btcContext
        && !postBridgeTraceContext
        && !reverseSwapResultEntryContext
        && !this.hasBridgeSignal(reverseSwapEvidenceText)
        && (this.hasSwapSignal(reverseSwapEvidenceText) || /swap_like|router|dex|amm|swap|swop|uniswap|sushiswap|curve|1inch|tokenlon|cow|dodo|bkswap|universalrouter|universal router/i.test(reverseSwapEvidenceText));
      if (reverseSwapSignalBeforeService) {
        return this.buildAutomaticDecision({
          action: "swap",
          title: "Solve the swap backward",
          summary: "The reverse endpoint has swap/DEX evidence. Tool 88 should inspect the same transaction and convert the followed output slice back into the asset that entered the swap.",
          reasons: ["Reverse mode treats the received asset as the current slice and follows the initiating address's sent asset farther backward."],
          status: "warn",
          confidenceLabel: "Reverse swap evidence",
          buttonLabel: "Auto Decide: Solve Reverse Swap",
        });
      }
      const addressPoisoningEvidence = this.buildAddressPoisoningEvidence(this.selectedEntry, selectedAddress);
      if (addressPoisoningEvidence) {
        return this.buildAutomaticDecision({
          action: "path_end",
          title: "Likely address-poisoning transfer",
          summary: "This tiny reverse-trace transfer came from a look-alike address and is safer to document as a likely address-poisoning path ending than to follow as source-of-funds activity.",
          reasons: [addressPoisoningEvidence.reason],
          status: "warn",
          confidenceLabel: "Likely poisoning",
          buttonLabel: "Auto Decide: End Poisoning Path",
          specialTreatment: "likely_address_poisoning",
          metadata: { addressPoisoning: addressPoisoningEvidence },
        });
      }
      const serviceEndpointEvidence = this.getAutomaticDecisionServiceEndpointEvidence();
      if (serviceEndpointEvidence && !reverseDepositTraceThrough && !reverseSwapResultEntryContext) {
        return this.buildAutomaticDecision({
          action: "path_end",
          title: "Mark as path ending",
          summary: "Tool 88 already has evidence that this address is an unknown service or hot-wallet style endpoint, so ordinary trace-through is not the safe default.",
          reasons: [serviceEndpointEvidence.reason],
          status: "warn",
          confidenceLabel: "Service-pattern stop",
          buttonLabel: "Auto Decide: End Path",
        });
      }
      const swapResultEntryContext = this.isSelectedEntrySolvedSwapContext(selectedAddress) || reverseSwapResultEntryContext;
      const rawMeaningfulLabels = this.getMeaningfulAutoDecisionAttributionLabels(selectedAddress);
      const meaningfulLabels = postBridgeTraceContext
        ? rawMeaningfulLabels.filter((label) => !this.isBridgeServiceAttributionLabel(label) && !this.hasBridgeSignal(label))
        : rawMeaningfulLabels;
      const postSwapBlockingAttributionLabels = swapResultEntryContext
        ? meaningfulLabels.filter((label) => !this.hasSwapSignal(label) && !this.hasBridgeSignal(label))
        : meaningfulLabels;
      const attributionText = meaningfulLabels.join("; " );
      const attributionClass = this.normalizeAttributionCode(this.attributionLookupResult?.classification || this.attributionLookupResult?.entityType || "");
      const allAttributionLabels = this.getAutomaticDecisionAttributionLabels();
      const friendlyAttributionLabels = allAttributionLabels.filter((label) =>
        this.isTraceThroughFriendlyAttributionLabel(label, selectedAddress)
      );
      const traceThroughHandleLabel = this.getTraceThroughFriendlyHandleAttributionLabel(allAttributionLabels);
      const pathEndingAttributionClass = !traceThroughHandleLabel
        && !postBridgeTraceContext
        && !swapResultEntryContext
        && !(reverseDepositTraceThrough && attributionClass === "deposit_address")
        && ["deposit_address", "hot_wallet", "attributed_service", "bridge_like", "swap_like", "swap_or_bridge"].includes(attributionClass);
      const signal = this.transactionSignalResult || null;
      const signalClass = this.normalizeSignalCode(signal?.classification || "");
      const signalAvailable = Boolean(signal && !["unavailable", "unknown", ""].includes(signalClass));
      const batchWithdrawalSignal = this.isLikelyBatchWithdrawalSignal(signal);
      const bridgeWithdrawalReceiveSignal = this.isTraceFriendlyBridgeWithdrawalSignal(signal) || (postBridgeTraceContext && this.isBridgeServiceAttributionLabel(allAttributionLabels.join("; ")));
      const simpleSignal = Boolean(
        signalAvailable &&
          (signalClass === "likely_simple_transfer" ||
            signalClass === "likely_batch_transfer" ||
            signalClass === "likely_bridge_withdrawal_receive" ||
            batchWithdrawalSignal ||
            bridgeWithdrawalReceiveSignal ||
            this.tool88FlagIsTrue(signal?.isSimpleTransfer) ||
            (signal?.adviceStatus === "ok" && ["high", "medium"].includes(this.toText(signal?.simpleTransferConfidence).toLowerCase())))
      );
      const complexSignal = Boolean(
        signalAvailable &&
          !simpleSignal &&
          !swapResultEntryContext &&
          !batchWithdrawalSignal &&
          !bridgeWithdrawalReceiveSignal &&
          (signalClass === "complex_contract_activity" ||
            signalClass === "btc_op_return_memo" ||
            this.tool88FlagIsTrue(signal?.hasSwapOrBridgeSignal) ||
            this.tool88FlagIsTrue(signal?.hasMemo) ||
            ["warn", "block"].includes(signal?.adviceStatus))
      );
      const signalText = this.getPositiveSignalMarkerText();
      const attributionOnlyText = this.getAttributionOnlyCombinedText();
      const signalEventNameText = signal?.eventNameText || this.getSignalEventNameText(signal?.raw || {});
      const combinedDecisionText = `${attributionText} ${attributionOnlyText} ${signalText} ${signalEventNameText}`;
      const batchSafeDecisionText = this.stripBatchWithdrawalDecisionText(combinedDecisionText);
      const batchSafeSignalNameText = this.stripBatchWithdrawalDecisionText(signalEventNameText || signalText);
      const matchedBridgeServiceName = this.getMatchedBridgeServiceName(batchSafeDecisionText);
      const eventBridgeSignal = !batchWithdrawalSignal && this.hasBridgeEventNameSignal(batchSafeSignalNameText);
      const eventSwapSignal = !batchWithdrawalSignal && this.hasSwapEventNameSignal(batchSafeSignalNameText);
      const explicitBridgeSignal = !swapResultEntryContext && !postBridgeTraceContext && !batchWithdrawalSignal && !bridgeWithdrawalReceiveSignal && (Boolean(matchedBridgeServiceName) || this.hasBridgeSignal(batchSafeDecisionText) || eventBridgeSignal || /bridge_like|destination|totoken|fromamount|minreturnamount|thorchain|wormhole|swft|transit|cross-chain|cross chain|dstchain|tochain/.test(batchSafeDecisionText));
      const explicitSwapSignal = !btcContext && !swapResultEntryContext && !postBridgeTraceContext && !batchWithdrawalSignal && !bridgeWithdrawalReceiveSignal && !explicitBridgeSignal && (this.hasSwapSignal(batchSafeDecisionText) || eventSwapSignal || /swap_like|router|dex|amm|swap|swop|uniswap|sushiswap|curve|1inch|tokenlon|cow|dodo|bkswap|universalrouter|universal router/.test(batchSafeDecisionText));
      const count = Number(this.countLookupResult.transactionCount);
      const countAvailable = this.countLookupResult.countAvailable !== false && Number.isFinite(count) && count >= 0;
      const highCountLimit = family === "btc"
        ? AUTO_BTC_TRACE_COUNT_LIMIT
        : family === "tron"
          ? AUTO_TRON_TRACE_COUNT_LIMIT
          : family === "evm"
            ? AUTO_EVM_TRACE_COUNT_LIMIT
            : null;
      const aboveAutoCountLimit = Boolean(!meaningfulLabels.length && countAvailable && highCountLimit !== null && count >= highCountLimit);
      const btcExchangeWithdrawalRecipientContext = this.getBtcExchangeWithdrawalRecipientContext(this.selectedEntry, selectedAddress, {
        family,
        count,
        countAvailable,
        meaningfulLabels,
        pathEndingAttributionClass,
        signal,
        simpleSignal,
        complexSignal,
      });

      if (this.countLookupResult.canTrace === false || this.hasBlockingChecks) {
        return this.buildAutomaticDecision({
          action: "path_end",
          title: "Mark as path ending",
          summary: "The count/check gate is blocking trace-through, so the automatic decision is to end the path here.",
          reasons: ["Trace-through is disabled by Tool 88's current checks."],
          status: "block",
          confidenceLabel: "High confidence",
          buttonLabel: "Auto Decide: End Path",
        });
      }

      if (reverseDepositTraceThrough && !explicitBridgeSignal && !explicitSwapSignal && this.canTraceThrough) {
        return this.buildAutomaticDecision({
          action: "trace",
          title: "Trace backward through deposit address",
          summary: "A deposit address is a destination in the forward direction, not a meaningful source-of-funds ending for a reverse trace.",
          reasons: [
            "Reverse mode continues toward earlier source transactions until it reaches a meaningful non-deposit ending.",
            "Bridge and swap evidence still keeps its normal review safeguards."
          ],
          status: "ok",
          confidenceLabel: "Reverse trace",
          buttonLabel: "Auto Decide: Trace Backward",
        });
      }

      if (bridgeReceiveEntryContext && !meaningfulLabels.length && !pathEndingAttributionClass && this.canTraceThrough) {
        return this.buildAutomaticDecision({
          action: "trace",
          title: "Trace through post-bridge receive",
          summary: "This row was created as a solved bridge destination receive. Bridge/swap wording belongs to the previous bridge step, so Tool 88 should keep tracing the recipient address on the destination chain.",
          reasons: [
            "Column V / Tool 88 notes identify this as a bridge destination receive row.",
            "The selected To address does not have endpoint attribution, so bridge-service markers on the receive transaction are not treated as a new swap/bridge stop.",
          ],
          status: "ok",
          confidenceLabel: "Post-bridge receive",
          buttonLabel: "Auto Decide: Trace Through",
        });
      }

      if (postBridgeContinuationContext && !meaningfulLabels.length && !pathEndingAttributionClass && this.canTraceThrough) {
        return this.buildAutomaticDecision({
          action: "trace",
          title: "Trace through post-bridge continuation",
          summary: "This row is downstream of a solved bridge receive. Bridge-service markers belong to the earlier bridge step, so Tool 88 should continue ordinary tracing on the destination chain.",
          reasons: [
            "An ancestor row or this row carries solved-bridge / destination-chain context.",
            "The selected To address has no endpoint attribution, so bridge and swap markers are treated as historical bridge context rather than a new stop.",
          ],
          status: "ok",
          confidenceLabel: "Post-bridge trace",
          buttonLabel: "Auto Decide: Trace Through",
        });
      }

      const traceThroughEntityOverride = this.getAutomaticTraceThroughEntityOverrideMatch(selectedAddress, allAttributionLabels);
      if (traceThroughEntityOverride && this.canTraceThrough) {
        const basis = traceThroughEntityOverride.matchedAddress
          ? `address ${this.shortenMiddle(traceThroughEntityOverride.matchedAddress, 12, 8)}`
          : `label ${traceThroughEntityOverride.matchedLabel}`;
        return this.buildAutomaticDecision({
          action: "trace",
          title: "Trace through selected address",
          summary: `${traceThroughEntityOverride.canonicalName} is on the active trace-through entity list, so Tool 88 treats this attribution/address as trace-through-friendly.`,
          reasons: [`Trace-through entity override matched ${basis}.`, "This override is checked before service, swap, and bridge attribution lists."],
          status: "ok",
          confidenceLabel: "User override",
          buttonLabel: "Auto Decide: Trace Through",
        });
      }

      if (traceThroughHandleLabel && this.canTraceThrough) {
        return this.buildAutomaticDecision({
          action: "trace",
          title: "Trace through selected address",
          summary: `${traceThroughHandleLabel} looks like a personal/platform handle rather than an exchange or service endpoint, so Tool 88 treats it as trace-through-friendly.`,
          reasons: [
            `Handle-style attribution matched: ${traceThroughHandleLabel}.`,
            "Known service handles such as @Cryptobot remain exceptions and can still act as path-ending/service attribution.",
          ],
          status: "ok",
          confidenceLabel: "Handle attribution",
          buttonLabel: "Auto Decide: Trace Through",
        });
      }

      const postSwapTraceContext = this.getSelectedEntryPostSwapTraceContext(selectedAddress);
      if (postSwapTraceContext && !postSwapBlockingAttributionLabels.length && !pathEndingAttributionClass && !aboveAutoCountLimit && this.canTraceThrough) {
        const amountReason = postSwapTraceContext.canConvert && postSwapTraceContext.followedAfter !== null
          ? `Tool 88 can continue with the converted post-swap slice: ${this.formatFunds(postSwapTraceContext.followedAfter, postSwapTraceContext.receivedToken)}${postSwapTraceContext.leftAfter !== null ? ` at left bounds ${this.formatFunds(postSwapTraceContext.leftAfter, postSwapTraceContext.receivedToken)}` : ""}.`
          : "The row is a solved swap result; Column F/G identify the post-swap asset and Tool 88 will derive the trace amount from the swap context.";
        return this.buildAutomaticDecision({
          action: "trace",
          title: "Trace through post-swap asset",
          summary: "This entry is already a solved swap result, so the complex same-hash receipt belongs to the completed swap step. Tool 88 should keep tracing the received asset unless attribution, count, or another hard gate says otherwise.",
          reasons: [amountReason, "The old swap/contract event-log signal is not treated as a path-ending reason for the post-swap receiving row."],
          status: "ok",
          confidenceLabel: "Post-swap trace",
          buttonLabel: "Auto Decide: Trace Through",
        });
      }

      if (bridgeWithdrawalReceiveSignal && !meaningfulLabels.length && !pathEndingAttributionClass && this.canTraceThrough) {
        return this.buildAutomaticDecision({
          action: "trace",
          title: "Trace through selected address",
          summary: "The transaction looks like a bridge withdrawal/settlement to the selected address, not the bridge initiation step.",
          reasons: [
            `Bridge-withdrawal evidence: ${this.getBridgeWithdrawalReceiveSummary(signal)}.`,
            bridgeReceiveEntryContext ? "This entry was created as a bridge destination receive, so bridge-service wording applies to the sending bridge/service address rather than the Column D recipient." : "Because the selected address is the recipient and does not have path-ending attribution, Tool 88 can continue tracing from the post-bridge receive.",
          ],
          status: "ok",
          confidenceLabel: "Post-bridge receive",
          buttonLabel: "Auto Decide: Trace Through",
        });
      }

      if (batchWithdrawalSignal && !meaningfulLabels.length && !pathEndingAttributionClass && this.canTraceThrough) {
        const btcBatchWithdrawalSignal = family === "btc";
        return this.buildAutomaticDecision({
          action: "trace",
          title: "Trace through selected address",
          summary: btcBatchWithdrawalSignal
            ? "The BTC transaction looks like an exchange/service withdrawal or distribution to the user-selected receiving address."
            : "The receipt looks like a batch ERC20/TRC20 withdrawal or token payout, and the user-selected receiving address is the output to follow.",
          reasons: [
            `Batch-transfer evidence: ${this.getBatchWithdrawalSummary(signal)}.`,
            btcBatchWithdrawalSignal
              ? "Many outputs in the BTC withdrawal transaction should not block trace-through when the selected receiving address itself is not attributed as an endpoint."
              : "Multiple Transfer events alone should not force a path ending when there are no swap/bridge markers and the selected receiving address is not attributed as an endpoint.",
          ],
          status: "ok",
          confidenceLabel: btcBatchWithdrawalSignal ? "BTC withdrawal receive" : "Batch withdrawal",
          buttonLabel: "Auto Decide: Trace Through",
        });
      }

      if (btcExchangeWithdrawalRecipientContext?.isLikely && this.canTraceThrough) {
        return this.buildAutomaticDecision({
          action: "trace",
          title: "Trace through selected BTC withdrawal recipient",
          summary: "This looks like a BTC exchange/service withdrawal to the user-selected recipient address, not a hot-wallet endpoint at the selected address.",
          reasons: [
            btcExchangeWithdrawalRecipientContext.reason,
            "One-input/many-output BTC withdrawals can look complex, but the selected To address is the recipient to follow when attribution and count checks do not block it.",
          ],
          status: "ok",
          confidenceLabel: "BTC withdrawal recipient",
          buttonLabel: "Auto Decide: Trace Through",
        });
      }

      if (explicitBridgeSignal) {
        return this.buildAutomaticDecision({
          action: "bridge",
          title: "Treat as bridge",
          summary: "Tool 88 found bridge-style attribution, service, field, or event/log evidence, so ordinary trace-through is not the safest default.",
          reasons: [matchedBridgeServiceName ? `Matched bridge service: ${matchedBridgeServiceName}.` : (eventBridgeSignal ? `Event/log name suggests bridge activity: ${signalEventNameText || signalText}.` : "Bridge-style field or attribution signal was detected.")],
          status: "warn",
          confidenceLabel: matchedBridgeServiceName ? "Bridge-service match" : "Review-friendly",
          buttonLabel: "Auto Decide: Bridge",
        });
      }

      if (explicitSwapSignal) {
        return this.buildAutomaticDecision({
          action: "swap",
          title: "Treat as swap",
          summary: "Tool 88 found swap/DEX-style attribution or event/log evidence, so ordinary trace-through is not the safest default.",
          reasons: [eventSwapSignal ? `Event/log name suggests swap activity: ${signalEventNameText || signalText}.` : "Swap/DEX-style attribution or signal was detected."],
          status: "warn",
          confidenceLabel: "Review-friendly",
          buttonLabel: "Auto Decide: Swap",
        });
      }

      if (this.shouldUseDepositEntityHopExtensionProbe(this.selectedEntry, selectedAddress) && this.canTraceThrough) {
        const originalBasis = this.buildDepositEntityComparisonBasis(
          this.getAttributionLabelsFromResult(this.attributionLookupResult || {}, this.getAttributionValuesForEntry(this.selectedEntry, selectedAddress))
        );
        return this.buildAutomaticDecision({
          action: "trace",
          title: "Probe one hop to same-entity wallet",
          summary: "The selected address is classified as a deposit address and the deposit hot-wallet probe is enabled. Tool 88 will trace one hop further, keep that hop only if next-hop attribution matches the same entity, and otherwise keep the original deposit as Path ends.",
          reasons: [
            `Original deposit attribution: ${originalBasis.displayLabel || attributionText || "deposit address"}.`,
            "The extra hop is conditional: unknown or unrelated next-hop attribution will be dropped instead of inserted into the output.",
          ],
          status: "warn",
          confidenceLabel: "Deposit probe",
          buttonLabel: "Auto Decide: Probe Same Entity",
        });
      }

      if (complexSignal) {
        if (explicitBridgeSignal) {
          return this.buildAutomaticDecision({
            action: "bridge",
            title: "Treat as bridge",
            summary: "The transaction-level signal does not look like a simple transfer and contains bridge-style evidence.",
            reasons: [matchedBridgeServiceName ? `Matched bridge service: ${matchedBridgeServiceName}.` : (signal?.recommendation || signal?.detailText || "Bridge-style signal was detected.")],
            status: "warn",
            confidenceLabel: "Review-friendly",
            buttonLabel: "Auto Decide: Bridge",
          });
        }
        if (explicitSwapSignal) {
          return this.buildAutomaticDecision({
            action: "swap",
            title: "Treat as swap",
            summary: "The transaction-level signal does not look like a simple transfer and contains swap/contract evidence.",
            reasons: [signal?.hasSwapEventSignal ? `Event/log name suggests swap activity: ${signal.eventNameText}.` : (signal?.recommendation || signal?.detailText || "Swap/contract signal was detected.")],
            status: "warn",
            confidenceLabel: "Review-friendly",
            buttonLabel: "Auto Decide: Swap",
          });
        }
        return this.buildAutomaticDecision({
          action: "path_end",
          title: "Mark as path ending",
          summary: "The transaction-level signal does not look like a simple transfer, so Tool 88 should not trace through automatically.",
          reasons: [signal?.recommendation || signal?.detailText || "Complex, memo, or non-simple transaction evidence was detected."],
          status: "warn",
          confidenceLabel: "Cautious",
          buttonLabel: "Auto Decide: End Path",
        });
      }

      if (!meaningfulLabels.length && pathEndingAttributionClass) {
        if (["bridge_like", "swap_or_bridge"].includes(attributionClass)) {
          return this.buildAutomaticDecision({
            action: "bridge",
            title: "Treat as bridge",
            summary: "The attribution lookup classified this address as bridge-like, so Tool 88 should not ordinary trace-through by default.",
            reasons: [`Attribution classification: ${this.formatAttributionClassification(attributionClass)}.`],
            status: "warn",
            confidenceLabel: "Attribution-based",
            buttonLabel: "Auto Decide: Bridge",
          });
        }
        if (attributionClass === "swap_like" && !btcContext) {
          return this.buildAutomaticDecision({
            action: "swap",
            title: "Treat as swap",
            summary: "The attribution lookup classified this address as swap-like, so Tool 88 should not ordinary trace-through by default.",
            reasons: [`Attribution classification: ${this.formatAttributionClassification(attributionClass)}.`],
            status: "warn",
            confidenceLabel: "Attribution-based",
            buttonLabel: "Auto Decide: Swap",
          });
        }
        return this.buildAutomaticDecision({
          action: "path_end",
          title: "Mark as path ending",
          summary: "The attribution lookup classified this address as a service/deposit/hot-wallet style endpoint, so Tool 88 should not ordinary trace-through automatically.",
          reasons: [`Attribution classification: ${this.formatAttributionClassification(attributionClass)}.`],
          status: "warn",
          confidenceLabel: "Attribution-based",
          buttonLabel: "Auto Decide: End Path",
        });
      }

      if (meaningfulLabels.length) {
        if (this.hasBridgeSignal(attributionText)) {
          return this.buildAutomaticDecision({
            action: "bridge",
            title: "Treat as bridge",
            summary: "The selected address has bridge-style attribution, so Tool 88 should not ordinary trace-through by default.",
            reasons: [matchedBridgeServiceName ? `Attribution matched bridge service: ${matchedBridgeServiceName}.` : `Attribution: ${attributionText}.`],
            status: "warn",
            confidenceLabel: "Attribution-based",
            buttonLabel: "Auto Decide: Bridge",
          });
        }
        if (!btcContext && this.hasSwapSignal(attributionText)) {
          return this.buildAutomaticDecision({
            action: "swap",
            title: "Treat as swap",
            summary: "The selected address has swap/DEX-style attribution, so Tool 88 should not ordinary trace-through by default.",
            reasons: [eventSwapSignal ? `Event/log name suggests swap activity: ${signalEventNameText}.` : `Attribution: ${attributionText}.`],
            status: "warn",
            confidenceLabel: "Attribution-based",
            buttonLabel: "Auto Decide: Swap",
          });
        }
        return this.buildAutomaticDecision({
          action: "path_end",
          title: "Mark as path ending",
          summary: "The selected address has meaningful service/entity attribution, so Tool 88 should not ordinary trace-through automatically.",
          reasons: [`Attribution: ${attributionText}.`],
          status: "warn",
          confidenceLabel: "Attribution-based",
          buttonLabel: "Auto Decide: End Path",
        });
      }

      if (aboveAutoCountLimit) {
        const familyLabel = family === "btc" ? "BTC" : (family === "tron" ? "TRON" : "EVM");
        return this.buildAutomaticDecision({
          action: "path_end",
          title: "Mark as path ending",
          summary: `${familyLabel} count is at or above Tool 88's automatic trace-through limit, and there is no meaningful attribution explaining the address.`,
          reasons: [`Count ${this.formatTransactionCount(count)} is greater than or equal to ${this.formatTransactionCount(highCountLimit)} for ${familyLabel}.`],
          status: "warn",
          confidenceLabel: "Count-based",
          buttonLabel: "Auto Decide: End Path",
        });
      }

      if (!signalAvailable) {
        return this.buildAutomaticDecision({
          action: "review",
          title: "Manual review recommended",
          summary: "Tool 88 could not confirm whether the transaction looks simple, so it will not auto trace or auto end this path.",
          reasons: [signal?.recommendation || signal?.detailText || "Transaction signal check was unavailable."],
          status: "neutral",
          confidenceLabel: "Needs review",
          buttonLabel: "Auto Decide",
        });
      }

      if (!simpleSignal && (!signal || !signalAvailable) && !["evm", "btc", "tron"].includes(family)) {
        return this.buildAutomaticDecision({
          action: "review",
          title: "Manual review recommended",
          summary: "Tool 88 does not have a supported transaction-signal route for this network yet, so it will not automatically trace through.",
          reasons: ["Unsupported or unknown network family for automatic decision-making."],
          status: "neutral",
          confidenceLabel: "Needs review",
          buttonLabel: "Auto Decide",
        });
      }

      if (!countAvailable) {
        return this.buildAutomaticDecision({
          action: "review",
          title: "Manual review recommended",
          summary: "The transaction count is unavailable, so Tool 88 will not auto trace or auto end this path.",
          reasons: ["Use the manual controls after reviewing attribution and transaction-signal checks."],
          status: "neutral",
          confidenceLabel: "Needs review",
          buttonLabel: "Auto Decide",
        });
      }

      if (this.canTraceThrough && simpleSignal) {
        const friendlyReason = friendlyAttributionLabels.length
          ? `Attribution label was trace-through friendly or address-like: ${friendlyAttributionLabels.join("; " )}.`
          : "No meaningful service attribution was found.";
        const signalTraceReason = batchWithdrawalSignal
          ? `Transaction signal looks like a batch withdrawal (${this.getBatchWithdrawalSummary(signal)}).`
          : "Transaction signal looks like a simple transfer.";
        return this.buildAutomaticDecision({
          action: "trace",
          title: "Trace through selected address",
          summary: batchWithdrawalSignal
            ? "The checks look compatible with a batch withdrawal, and the selected output address is clear."
            : "The checks do not show a service/path-ending label, high count block, or complex transaction marker.",
          reasons: [friendlyReason, signalTraceReason],
          status: "ok",
          confidenceLabel: batchWithdrawalSignal ? "Batch withdrawal" : "High confidence",
          buttonLabel: "Auto Decide: Trace Through",
        });
      }

      return this.buildAutomaticDecision({
        action: "review",
        title: "Manual review recommended",
        summary: "Tool 88 did not find enough evidence to make a safe automatic decision.",
        reasons: ["Use the manual path-ending, swap/bridge, or trace-through controls after reviewing the checks."],
        status: "neutral",
        confidenceLabel: "Needs review",
        buttonLabel: "Auto Decide",
      });
    },

    recordPathEndingWithReason(reason, { autoDecision = false, specialTreatment = "", specialMetadata = null } = {}) {
      if (!this.selectedEntry || !this.selectedTargetAddress) {
        this.reportText = "Path ending could not be recorded because no selected entry/address is available.";
        return;
      }

      const fundsIn = this.deriveFundsIn(this.selectedEntry, this.selectedTargetAddress);
      const dormantAmountText = Number.isFinite(fundsIn.amount)
        ? this.formatFunds(fundsIn.amount, fundsIn.token)
        : "";

      const finalized = this.finalizeEntryAsPathEnding({
        baseRows: this.getCurrentOutputBaseRows(),
        entry: this.selectedEntry,
        selectedAddress: this.selectedTargetAddress,
        reason,
        dormantAmountText,
      });
      const addressPoisoningEvidence = specialTreatment === "likely_address_poisoning"
        ? (specialMetadata?.addressPoisoning || specialMetadata || null)
        : null;
      if (addressPoisoningEvidence && Array.isArray(finalized.rows)) {
        const bounds = this.findEntryBoundsInRows(finalized.rows, this.selectedEntry);
        const rowIndex = bounds?.startRowIndex ?? Math.max(0, Number(this.selectedEntry?.startRowIndex || 0));
        const row = this.cloneRowPreservingValues(finalized.rows[rowIndex] || this.makeBlankRow());
        const attributionColumnIndex = this.traceDirection === "reverse" ? COL.FROM_ATTRIBUTION : COL.ATTRIBUTION;
        const existingAttribution = this.cleanAttributionLabel(row[attributionColumnIndex]);
        if (!existingAttribution || this.isUnknownAttributionPlaceholder(existingAttribution) || /address[-_\s]*poison/i.test(existingAttribution)) {
          row[attributionColumnIndex] = "Likely Address Poisoning";
        }
        row[COL.SYSTEM_NOTES] = this.appendSentenceNote(row[COL.SYSTEM_NOTES], "Likely Address Poisoning.");
        row[COL.SYSTEM_NOTES] = this.appendSentenceNote(row[COL.SYSTEM_NOTES], addressPoisoningEvidence.reason || reason);
        this.appendTool88NoteToRow(
          row,
          [
            "Tool 88: likely address poisoning detected",
            "action=address-poisoning-ending",
            `direction=${this.traceDirection}`,
            addressPoisoningEvidence.selectedAddress ? `target=${addressPoisoningEvidence.selectedAddress}` : "",
            addressPoisoningEvidence.lookalikeAddress ? `resembles=${addressPoisoningEvidence.lookalikeAddress}` : "",
            Number.isFinite(addressPoisoningEvidence.prefixLength) ? `prefix_match=${addressPoisoningEvidence.prefixLength}` : "",
            Number.isFinite(addressPoisoningEvidence.suffixLength) ? `suffix_match=${addressPoisoningEvidence.suffixLength}` : "",
          ].filter(Boolean).join("; ") + "."
        );
        finalized.rows[rowIndex] = row;
      }

      this.bridgeApplyResult = null;
      this.pathEndingResult = {
        note: finalized.note,
        dormantAmountText,
        reason: finalized.reason,
        specialTreatment,
        specialMetadata,
        addressPoisoning: addressPoisoningEvidence,
        autoDecision,
        rawPathEndingAttributionLabel: finalized.rawPathEndingAttributionLabel || "",
      };
      this.traceResult = null;
      this.loadOutputRows(finalized.rows);

      this.lastRunMeta = {
        ...this.buildBaseMeta(
          this.inputSnapshotRows,
          this.parsedEntries,
          this.targetCandidates,
          this.selectedTargetAddress
        ),
        exitCount: 0,
        dormantAmountText,
        transactionCount: this.countLookupResult?.transactionCount,
        attributionLabel: this.attributionLookupResult?.displayLabel || "",
        attributionClassification: specialTreatment || this.attributionLookupResult?.classification || this.attributionLookupResult?.entityType || "",
      };

      this.reportText = this.buildManualPathEndingReport(
        this.selectedEntry,
        this.selectedTargetAddress,
        this.pathEndingResult
      );
    },

    async applyAutomaticDecision(options = {}) {
      const decision = this.buildAutomaticDecisionPreview();
      if (!decision || decision.action === "review") {
        const message = decision?.summary || "Tool 88 needs manual review before making a decision.";
        this.warnings.push(message);
        this.reportText = this.reportText
          ? `${this.reportText}

Automatic decision: manual review recommended. ${message}`
          : `Automatic decision: manual review recommended. ${message}`;
        return;
      }

      const reason = `Tool 88 automatic decision: ${decision.summary} ${decision.reasons.join(" " )}`.trim();

      if (decision.action === "trace") {
        if (!this.canTraceThrough) {
          this.warnings.push("Automatic trace-through was recommended but the trace-through gate is currently unavailable.");
          return;
        }
        await this.traceThrough(options);
        if (this.reportText) {
          this.reportText = `${this.reportText}

Automatic decision
${reason}`;
        }
        return;
      }

      if (decision.action === "swap") {
        await this.openSwapExplorer();
        if (this.reportText) {
          this.reportText = `${this.reportText}

Automatic decision
${reason}`;
        }
        return;
      }

      if (decision.action === "bridge") {
        await this.openBridgeSolver();
        if (this.reportText) {
          this.reportText = `${this.reportText}

Automatic decision
${reason}`;
        }
        return;
      }

      this.recordPathEndingWithReason(reason, {
        autoDecision: true,
        specialTreatment: decision.specialTreatment || "",
        specialMetadata: decision.metadata || null,
      });
    },

    getSwapInitiatingAddress(entry, selectedAddress) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const targetColumnIndex = this.getTargetColumnIndex();
      const counterpartyColumnIndex = this.getCounterpartyColumnIndex();
      if (entry && selectedKey) {
        for (const { row } of entry.rows || []) {
          const targetKey = this.normalizeAddressKey(row?.[targetColumnIndex]);
          if (targetKey === selectedKey) {
            const counterparty = this.toText(row?.[counterpartyColumnIndex]);
            if (counterparty) return counterparty;
          }
        }
      }
      const fallbackRow = entry?.rows?.find(({ row }) => this.rowHasAnyData(row))?.row || [];
      return this.toText(fallbackRow?.[counterpartyColumnIndex] || fallbackRow?.[COL.FROM_ADDRESS] || selectedAddress);
    },

    async openSwapExplorer() {
      if (!this.selectedEntry || this.isProcessing) {
        return;
      }
      const entry = this.selectedEntry;
      const selectedAddress = this.selectedTargetAddress;
      const initiatingAddress = this.getSwapInitiatingAddress(entry, selectedAddress);
      const countContext = this.inferCountRequestContext(entry, selectedAddress);

      this.isProcessing = true;
      this.statusMessage = "Loading transaction explorer for swap review...";
      this.swapApplyResult = null;
      try {
        const response = await this.postTool88({
          action: "swap_transaction_explorer",
          tx_hash: entry.txHash,
          transaction_hash: entry.txHash,
          selected_address: selectedAddress,
          target_address: selectedAddress,
          initiating_address: initiatingAddress,
          direction: this.traceDirection,
          chain_id: entry.chainId || countContext.chain_id || "",
          chain_name: entry.chainName || countContext.chain_name || "",
          network_family: countContext.network_family || "",
          attribution_label: this.attributionLookupResult?.displayLabel || this.toText(this.getSelectedTargetRow(entry, selectedAddress)?.[this.getAttributionColumnIndex()] || ""),
          sample_mode: this.isSampleDataLoaded ? 1 : 0,
          entry_context: this.serializeEntryContext(entry),
        });
        const result = response.result || response;
        this.swapExplorer = this.normalizeSwapExplorerPayload(result, entry, selectedAddress, initiatingAddress);
        this.swapExplorerActiveTab = this.swapExplorer.tabs.erc20?.length ? "erc20" : "main";
        this.swapSelectedTransferKey = "";
        this.reportText = this.buildSwapExplorerReport(this.swapExplorer);
      } catch (error) {
        const message = error?.message || "Unknown swap explorer error";
        this.warnings.push(`Swap explorer failed: ${message}`);
        this.reportText = `Swap explorer failed: ${message}`;
      } finally {
        this.isProcessing = false;
        this.statusMessage = "";
      }
    },

    normalizeSwapExplorerPayload(payload = {}, entry = null, selectedAddress = "", initiatingAddress = "") {
      const txHash = this.toText(payload.tx_hash || payload.txHash || entry?.txHash || "");
      const initiator = this.toText(payload.initiating_address || payload.initiatingAddress || initiatingAddress);
      const selectedKey = this.normalizeAddressKey(initiator);
      const normalizeRows = (rows, kind) => (Array.isArray(rows) ? rows : []).map((row, index) => this.normalizeSwapExplorerRow(row, kind, selectedKey, index)).filter(Boolean);
      const mainRows = normalizeRows(payload.main_transactions || payload.mainTransactions || payload.main || payload.main_transaction ? [payload.main_transactions || payload.mainTransactions || payload.main || payload.main_transaction].flat() : [], "main");
      const internalRows = normalizeRows(payload.internal || payload.internal_transactions || [], "internal");
      const ercRows = normalizeRows(payload.erc20 || payload.erc20_transfers || payload.trc20 || payload.token_transfers || [], "erc20");
      const nftRows = normalizeRows(payload.nft || payload.nft_transfers || [], "nft");
      const explorer = {
        txHash,
        initiatingAddress: initiator,
        selectedAddress,
        attributionLabel: this.cleanAttributionLabel(payload.attribution_label || payload.attributionLabel || this.attributionLookupResult?.displayLabel || ""),
        source: this.toText(payload.source || "tool88_swap_transaction_explorer"),
        tabs: { main: mainRows, internal: internalRows, erc20: ercRows, nft: nftRows },
        raw: payload,
        nftWarning: nftRows.length > 0,
        summary: this.toText(payload.summary || "Tool 88 grouped same-hash main, internal, token-transfer, and NFT rows for swap review."),
      };
      explorer.allRows = [...mainRows, ...internalRows, ...ercRows, ...nftRows];
      explorer.automaticCandidate = this.buildAutomaticSwapCandidate(explorer, entry, selectedAddress);
      return explorer;
    },

    normalizeSwapExplorerRow(row = {}, kind = "erc20", initiatingKey = "", index = 0) {
      if (!row || typeof row !== "object") return null;
      const tokenInfo = row.tokenInfo || row.token_info || {};
      const hash = this.toText(row.hash || row.transaction_hash || row.tx_hash || row.transaction_id || row.parentHash || "");
      const fromAddress = this.toText(row.from_address || row.from || row.sender || row.ownerAddress || "");
      const toAddress = this.toText(row.to_address || row.to || row.receiver || row.toAddress || "");
      const contractAddress = this.normalizeAddressKey(row.contract_address || row.contractAddress || row.address || tokenInfo.tokenId || tokenInfo.address || "");
      const tokenFromRegistry = contractAddress ? this.findTokenByContract(contractAddress) : null;
      const tokenOverride = contractAddress ? this.getTokenDecimalOverrideByContract(contractAddress) : null;
      const providerToken = this.toText(row.token_symbol || row.tokenSymbol || row.token || row.symbol || tokenInfo.tokenAbbr || tokenInfo.symbol || "");
      const providerTokenNormalized = normalizeTokenSymbolText(providerToken);
      const tokenLooksPlaceholder = !providerTokenNormalized || providerTokenNormalized === "UNKNOWN" || providerTokenNormalized === "TOKEN";
      const token = tokenLooksPlaceholder && (tokenOverride?.symbol || tokenFromRegistry?.symbol)
        ? (tokenOverride?.symbol || tokenFromRegistry.symbol)
        : this.toText(providerToken || tokenOverride?.symbol || tokenFromRegistry?.symbol || (kind === "main" || kind === "internal" ? this.inferNativeSymbolForSelectedEntry() : "TOKEN"));
      const providerDecimalsRaw = row.token_decimals ?? row.tokenDecimal ?? row.decimals ?? tokenInfo.tokenDecimal ?? tokenInfo.decimals;
      const decimals = this.chooseBestTokenDecimals({
        providerDecimals: providerDecimalsRaw,
        contractAddress,
        registryDecimals: tokenFromRegistry?.decimals,
      });
      const amount = this.getSwapRowAmount(row, Number.isFinite(decimals) ? decimals : null, {
        preferAtomic: Boolean(tokenOverride && (row.raw_value ?? row.rawValue ?? row.value ?? row.quant ?? row.amount_raw ?? row.amountRaw ?? row.quantity_raw ?? row.quantityRaw)),
        tokenSymbol: token,
        allowStablecoinScaleRepair: Boolean(tokenOverride),
      });
      const decimalsSource = tokenOverride ? tokenOverride.label || "known contract override" : (this.toText(providerDecimalsRaw) ? "provider" : (tokenFromRegistry?.decimals !== undefined ? "token manager" : "unknown"));
      const date = this.parseProviderDate(row.date || row.date_utc || row.timeStamp || row.timestamp || row.block_ts || row.block_timestamp || row.time);
      const fromKey = this.normalizeAddressKey(fromAddress);
      const toKey = this.normalizeAddressKey(toAddress);
      let direction = "other";
      if (initiatingKey) {
        if (fromKey === initiatingKey && toKey === initiatingKey) direction = "self";
        else if (fromKey === initiatingKey) direction = "outgoing";
        else if (toKey === initiatingKey) direction = "incoming";
      }
      const isNft = kind === "nft" || /erc721|erc1155|trc721|trc1155/i.test(this.toText(row.type || row.token_type));
      const key = [kind, hash, row.logIndex || row.log_index || row.traceId || row.id || index, contractAddress, fromAddress, toAddress, row.value || row.raw_value || row.token_id || row.tokenId].filter(Boolean).join("|");
      return {
        key,
        raw: row,
        hash,
        kind,
        kindLabel: kind === "erc20" ? "Token transfer" : kind === "internal" ? "Internal" : kind === "nft" ? "NFT" : "Main",
        fromAddress,
        toAddress,
        contractAddress,
        token,
        tokenSymbol: token,
        amount,
        tokenDecimals: Number.isFinite(decimals) ? decimals : null,
        tokenDecimalsSource: decimalsSource,
        date,
        dateText: date ? this.formatUtcDate(date) : this.toText(row.date || row.date_utc || ""),
        direction,
        canUseAsSwapResult: direction === (this.traceDirection === "reverse" ? "outgoing" : "incoming") && !isNft && Number.isFinite(amount) && amount > 0,
        detailText: isNft
          ? `${this.toText(row.type || "NFT")} ${this.toText(row.tokenId || row.token_id || "")} ${contractAddress ? `contract ${this.shortenMiddle(contractAddress, 8, 6)}` : ""}`.trim()
          : `${contractAddress ? `contract ${this.shortenMiddle(contractAddress, 8, 6)}` : ""}${Number.isFinite(decimals) ? ` decimals ${decimals}` : ""}${row.raw_value || row.rawValue || row.value ? ` raw ${this.toText(row.raw_value || row.rawValue || row.value)}` : ""}`.trim(),
      };
    },

    inferNativeSymbolForSelectedEntry() {
      const countContext = this.selectedEntry ? this.inferCountRequestContext(this.selectedEntry, this.selectedTargetAddress) : {};
      return this.toText(countContext.native_symbol || countContext.token_symbol || this.selectedEntry?.chainName || "ETH").toUpperCase() || "ETH";
    },

    findTokenByContract(contractAddress) {
      const key = this.normalizeAddressKey(contractAddress);
      if (!key) return null;
      const fields = [
        "contractAddress",
        "tronContractAddress",
        "avaxContractAddress",
        "pulseChainContractAddress",
        "solanaContractAddress",
        "tonContractAddress",
      ];
      return this.effectiveTokenRegistry.find((token) => fields.some((field) => this.normalizeAddressKey(token?.[field]) === key)) || null;
    },

    getTokenDecimalOverrideByContract(contractAddress) {
      const key = this.normalizeAddressKey(contractAddress);
      if (!key) return null;
      return TOOL88_TOKEN_DECIMAL_OVERRIDES[key] || TOOL88_TOKEN_DECIMAL_OVERRIDES[key.toLowerCase()] || null;
    },

    chooseBestTokenDecimals({ providerDecimals = null, contractAddress = "", registryDecimals = null } = {}) {
      const override = this.getTokenDecimalOverrideByContract(contractAddress);
      if (override && Number.isFinite(Number(override.decimals))) {
        return Math.trunc(Number(override.decimals));
      }
      const providerNumber = Number(providerDecimals);
      if (Number.isFinite(providerNumber) && providerNumber >= 0 && providerNumber <= 36) {
        return Math.trunc(providerNumber);
      }
      const registryNumber = Number(registryDecimals);
      if (Number.isFinite(registryNumber) && registryNumber >= 0 && registryNumber <= 36) {
        return Math.trunc(registryNumber);
      }
      return null;
    },

    getSwapRowAmount(row = {}, decimals = null, options = {}) {
      const rawValue = this.toText(row.raw_value ?? row.rawValue ?? row.value ?? row.quant ?? row.amount_raw ?? row.amountRaw ?? row.quantity_raw ?? row.quantityRaw);
      const atomicParsed = /^\d+$/.test(rawValue) && Number.isFinite(decimals)
        ? this.parseNumberLike(this.formatAtomicAmountString(rawValue, decimals))
        : NaN;
      if (options.preferAtomic && Number.isFinite(atomicParsed)) {
        return Math.max(0, atomicParsed);
      }

      const directCandidates = [row.amount, row.token_amount, row.amount_formatted, row.value_formatted, row.formatted_amount, row.valueAfterAccountingForDecimals, row.amountFormatted, row.valueFormatted];
      for (const candidate of directCandidates) {
        const parsed = this.parseNumberLike(candidate);
        if (!Number.isFinite(parsed)) continue;
        const tokenText = this.toText(options.tokenSymbol || row.token_symbol || row.tokenSymbol || row.token || row.symbol).toUpperCase();
        const directText = this.toText(candidate);
        const fractionalLength = (directText.split(".")[1] || "").replace(/[^0-9]/g, "").length;
        const looksLikeOldBadStablecoinScale = Boolean(
          options.allowStablecoinScaleRepair &&
          Number(decimals) === 6 &&
          ["USDT", "USDC"].includes(tokenText) &&
          parsed > 0 &&
          parsed < 1 &&
          fractionalLength >= 9 &&
          !Number.isFinite(atomicParsed)
        );
        if (looksLikeOldBadStablecoinScale) return Math.max(0, parsed * 1000000);
        return Math.max(0, parsed);
      }

      if (Number.isFinite(atomicParsed)) return Math.max(0, atomicParsed);
      return 0;
    },

    formatAtomicAmountString(rawValue, decimals = 0) {
      let raw = this.toText(rawValue).replace(/[^0-9]/g, "").replace(/^0+/, "");
      const places = Math.max(0, Math.trunc(Number(decimals) || 0));
      if (!raw) return "0";
      if (!places) return raw;
      if (raw.length <= places) raw = raw.padStart(places + 1, "0");
      const whole = raw.slice(0, raw.length - places) || "0";
      const fraction = raw.slice(-places).replace(/0+$/, "");
      return fraction ? `${whole}.${fraction}` : whole;
    },

    groupSwapRowsByAsset(rows = []) {
      const groups = new Map();
      rows.forEach((row) => {
        if (!row || !Number.isFinite(row.amount) || row.amount <= 0 || row.kind === "nft") return;
        const token = this.toText(row.token || row.tokenSymbol || "TOKEN").toUpperCase();
        const contract = this.normalizeAddressKey(row.contractAddress || token);
        const key = `${token}|${contract}`;
        if (!groups.has(key)) {
          groups.set(key, { key, token, contractAddress: contract, amount: 0, rows: [], firstDate: row.date || null });
        }
        const group = groups.get(key);
        group.amount += row.amount;
        group.rows.push(row);
        if (!group.firstDate || (row.date && row.date < group.firstDate)) group.firstDate = row.date;
      });
      return [...groups.values()];
    },

    buildAutomaticSwapCandidate(explorer, entry, selectedAddress) {
      const rows = explorer?.allRows || [];
      const outgoingGroups = this.groupSwapRowsByAsset(rows.filter((row) => row.direction === "outgoing"));
      const incomingGroups = this.groupSwapRowsByAsset(rows.filter((row) => row.direction === "incoming"));
      if (explorer?.nftWarning) {
        return {
          title: "NFT transfer present - manual review",
          summary: "This same-hash transaction includes NFT movement. Tool 88 shows the tabs but does not auto-solve NFT-involved swaps.",
          canApply: false,
        };
      }
      if (outgoingGroups.length !== 1 || incomingGroups.length !== 1) {
        return {
          title: "Manual swap review needed",
          summary: `Tool 88 found ${outgoingGroups.length} outgoing asset group(s) and ${incomingGroups.length} incoming asset group(s) for the initiating address. Select the correct incoming transfer if this is the swap result.`,
          canApply: false,
          outgoingGroups,
          incomingGroups,
        };
      }
      return this.buildSwapCandidateFromGroups(explorer, entry, selectedAddress, outgoingGroups[0], incomingGroups[0], "automatic");
    },

    buildSwapCandidateFromSelectedTransfer(selectedKey) {
      const explorer = this.swapExplorer;
      if (!explorer || !selectedKey) return null;
      const selectedRow = (explorer.allRows || []).find((row) => row.key === selectedKey);
      if (!selectedRow) return null;
      const reverse = this.traceDirection === "reverse";
      const matchingRows = (direction) => (explorer.allRows || []).filter((row) => row.direction === direction
        && row.kind !== "nft"
        && this.toText(row.token).toUpperCase() === this.toText(selectedRow.token).toUpperCase()
        && this.normalizeAddressKey(row.contractAddress) === this.normalizeAddressKey(selectedRow.contractAddress));
      const outgoingGroups = this.groupSwapRowsByAsset(reverse
        ? matchingRows("outgoing")
        : (explorer.allRows || []).filter((row) => row.direction === "outgoing"));
      const incomingGroups = this.groupSwapRowsByAsset(reverse
        ? (explorer.allRows || []).filter((row) => row.direction === "incoming")
        : matchingRows("incoming"));
      if (outgoingGroups.length !== 1 || incomingGroups.length !== 1) {
        return {
          title: "Selected transfer needs more review",
          summary: reverse
            ? `Tool 88 needs exactly one selected outgoing asset group and one incoming output group. Found ${outgoingGroups.length} selected outgoing and ${incomingGroups.length} incoming group(s).`
            : `Tool 88 needs exactly one outgoing asset group and one matching incoming asset group. Found ${outgoingGroups.length} outgoing and ${incomingGroups.length} selected incoming group(s).`,
          canApply: false,
        };
      }
      return this.buildSwapCandidateFromGroups(explorer, this.selectedEntry, this.selectedTargetAddress, outgoingGroups[0], incomingGroups[0], "manual");
    },

    buildSwapCandidateFromGroups(explorer, entry, selectedAddress, sentGroup, receivedGroup, mode = "automatic") {
      const reverse = this.traceDirection === "reverse";
      const fundsIn = this.deriveFundsIn(entry, selectedAddress);
      const currentFollowed = Number.isFinite(fundsIn.amount) ? fundsIn.amount : 0;
      const currentLeftBounds = this.getEntryLeftBoundsAmount(entry, selectedAddress);
      const sentAmount = Number(sentGroup?.amount || 0);
      const receivedAmount = Number(receivedGroup?.amount || 0);
      const rate = sentAmount > 0 ? receivedAmount / sentAmount : 0;
      const tokenMatches = this.tokenSymbolsMatchForSwap(fundsIn.token, reverse ? receivedGroup.token : sentGroup.token);
      const followedBefore = reverse
        ? (tokenMatches && rate > 0 ? currentFollowed / rate : 0)
        : currentFollowed;
      const followedAfter = reverse
        ? currentFollowed
        : (tokenMatches ? currentFollowed * rate : 0);
      const leftBefore = reverse
        ? (tokenMatches && rate > 0 ? currentLeftBounds / rate : 0)
        : currentLeftBounds;
      const leftAfter = reverse
        ? currentLeftBounds
        : (tokenMatches ? currentLeftBounds * rate : 0);
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const initiatingKey = this.normalizeAddressKey(explorer?.initiatingAddress);
      const outgoingCounterparty = (sentGroup?.rows || [])
        .map((row) => row?.to || row?.toAddress)
        .find((address) => this.normalizeAddressKey(address) && this.normalizeAddressKey(address) !== initiatingKey);
      const isFullAddressLike = (value) => this.isValidBlockchainAddress(this.toText(value).trim());
      const selectedLooksLikeSwapContract = selectedKey && selectedKey !== initiatingKey && isFullAddressLike(selectedAddress) ? selectedAddress : "";
      const outgoingCounterpartyFull = isFullAddressLike(outgoingCounterparty) ? outgoingCounterparty : "";
      const swapContractAddress = outgoingCounterpartyFull || selectedLooksLikeSwapContract || outgoingCounterparty || (selectedKey && selectedKey !== initiatingKey ? this.toText(selectedAddress) : "");
      const canApply = tokenMatches && sentAmount > 0 && receivedAmount > 0 && followedBefore > 0 && followedAfter > 0;
      const expectedToken = reverse ? receivedGroup?.token : sentGroup?.token;
      return {
        mode,
        traceDirection: reverse ? "reverse" : "forward",
        title: canApply
          ? `${mode === "manual" ? "Selected" : "Solved"}: ${sentGroup.token} → ${receivedGroup.token}${reverse ? " (traced backward)" : ""}`
          : "Swap candidate not safe to apply",
        summary: canApply
          ? (reverse
            ? `The initiating address sent ${this.formatFunds(sentAmount, sentGroup.token)} and received ${this.formatFunds(receivedAmount, receivedGroup.token)}. Following ${this.formatFunds(followedAfter, receivedGroup.token)} backward becomes ${this.formatFunds(followedBefore, sentGroup.token)} before the swap.`
            : `The initiating address sent ${this.formatFunds(sentAmount, sentGroup.token)} and received ${this.formatFunds(receivedAmount, receivedGroup.token)}. Following ${this.formatFunds(followedBefore, fundsIn.token)} becomes ${this.formatFunds(followedAfter, receivedGroup.token)}.`)
          : `The followed token (${fundsIn.token || "unknown"}) does not match the ${reverse ? "received/output" : "sent/input"} asset Tool 88 expected (${expectedToken || "unknown"}), or one side of the swap is zero.`,
        canApply,
        txHash: explorer.txHash,
        initiatingAddress: explorer.initiatingAddress,
        selectedAddress,
        attributionLabel: explorer.attributionLabel,
        sent: sentGroup,
        received: receivedGroup,
        fundsIn,
        followedBefore,
        followedAfter,
        leftBefore,
        leftAfter,
        swapRate: rate,
        swapContractAddress,
        receivedTokenContract: receivedGroup.contractAddress,
        date: receivedGroup.firstDate || sentGroup.firstDate || this.getEntryDate(entry),
      };
    },

    getSwapContractAddress(entry, selectedAddress) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const initiatingKey = this.normalizeAddressKey(this.getSwapInitiatingAddress(entry, selectedAddress));
      if (selectedKey && selectedKey !== initiatingKey) {
        return selectedAddress;
      }
      const firstRow = entry?.rows?.find(({ row }) => this.rowHasAnyData(row))?.row || [];
      const toAddress = firstRow[COL.TO_ADDRESS];
      const toKey = this.normalizeAddressKey(toAddress);
      if (toKey && toKey !== initiatingKey) {
        return toAddress;
      }
      return "";
    },

    getEntrySourceHash(entry) {
      for (const { row } of entry?.rows || []) {
        const value = this.toText(row?.[COL.FROM_HASH]).trim();
        if (value) return value;
      }
      return "";
    },

    getAccountInterestEntryHash(entry = null) {
      const direct = this.toText(entry?.txHash || entry?.hash || entry?.transactionHash || "");
      if (direct) return this.normalizeHashKey(direct);
      for (const { row } of entry?.rows || []) {
        const value = this.toText(row?.[COL.TX_HASH]).trim();
        if (value) return this.normalizeHashKey(value);
      }
      return this.normalizeHashKey(this.traceTransactionOfInterestHash || "");
    },

    tokenSymbolsMatchForSwap(left, right) {
      const normalize = (value) => this.toText(value).toUpperCase().replace(/RENDER/g, "RNDR").replace(/\s+/g, "");
      const leftNormalized = normalize(left);
      const rightNormalized = normalize(right);
      if (!leftNormalized || !rightNormalized) return false;
      if (leftNormalized === rightNormalized) return true;
      const aliasGroups = [
        ["ETH", "WETH", "WRAPPEDETHER"],
        ["BNB", "WBNB"],
        ["MATIC", "WMATIC", "POL"],
        ["AVAX", "WAVAX"],
        ["BTC", "WBTC", "BTCB", "BTC.B"],
      ];
      return aliasGroups.some((group) => group.includes(leftNormalized) && group.includes(rightNormalized));
    },

    applySwapCandidate(candidate) {
      if (!candidate || !candidate.canApply || !this.selectedEntry) return;
      const row = this.buildSwapHopRow(candidate, this.selectedEntry);
      let outputRows = this.getCurrentOutputBaseRows();
      outputRows = this.replaceEntryWithRows(outputRows, this.selectedEntry, [row]);
      this.loadOutputRows(outputRows);
      const reverse = candidate.traceDirection === "reverse" || this.traceDirection === "reverse";
      const note = reverse
        ? `Tool 88 solved the reverse swap ${candidate.sent.token} to ${candidate.received.token}; followed ${this.formatFunds(candidate.followedAfter, candidate.received.token)} backward to ${this.formatFunds(candidate.followedBefore, candidate.sent.token)} before the swap.`
        : `Tool 88 solved swap ${candidate.sent.token} to ${candidate.received.token}; followed ${this.formatFunds(candidate.followedBefore, candidate.fundsIn?.token || candidate.sent.token)} becomes ${this.formatFunds(candidate.followedAfter, candidate.received.token)}.`;
      this.swapApplyResult = { note, candidate, row, rows: [row] };
      this.pathEndingResult = null;
      this.bridgeApplyResult = null;
      this.traceResult = null;
      this.lastRunMeta = {
        ...this.buildBaseMeta(this.inputSnapshotRows, this.parsedEntries, this.targetCandidates, this.selectedTargetAddress),
        swapSolved: true,
        swapSentToken: candidate.sent.token,
        swapReceivedToken: candidate.received.token,
        swapRate: candidate.swapRate,
      };
      this.reportText = this.buildSwapApplyReport(candidate);
    },

    buildSwapHopRow(candidate, entry) {
      const row = this.makeBlankRow();
      const selectedRow = this.getSelectedTargetRow(entry, this.selectedTargetAddress) || entry?.rows?.[0]?.row || [];
      const dateText = candidate.date ? this.formatUtcDate(candidate.date) : (entry?.dateText || this.toText(selectedRow[COL.DATE]));
      const reverse = candidate.traceDirection === "reverse" || this.traceDirection === "reverse";
      const receivedToken = candidate.received?.token || "TOKEN";
      const sentToken = candidate.sent?.token || candidate.fundsIn?.token || "TOKEN";
      const beforeToken = candidate.fundsIn?.token || sentToken;
      const attribution = this.cleanAttributionLabel(candidate.attributionLabel || this.attributionLookupResult?.displayLabel || "swap service").replace(/[.\s]+$/g, "") || "swap service";
      row[COL.PATH_LABEL] = this.toText(selectedRow[COL.PATH_LABEL]);
      row[COL.TX_HASH] = candidate.txHash;
      row[COL.FROM_ADDRESS] = candidate.initiatingAddress;
      row[COL.TO_ADDRESS] = candidate.initiatingAddress;
      row[COL.DATE] = dateText;

      if (reverse) {
        row[COL.AMOUNT] = this.formatAmount(candidate.sent?.amount || 0);
        row[COL.TOKEN] = sentToken;
        const sentPriorBalance = this.parseNumberLike(
          candidate.sent?.priorBalanceBeforeTransaction
            ?? candidate.sent?.balanceBeforeTransaction
            ?? candidate.sent?.assetBalanceBefore
            ?? candidate.inputPriorBalanceBeforeTransaction
            ?? candidate.inputPriorBalance
        );
        if (Number.isFinite(sentPriorBalance) && sentPriorBalance >= 0) {
          row[COL.PRIOR_BALANCE] = Number(sentPriorBalance).toFixed(sentToken === 'BTC' ? 8 : 4);
        }

        // Reverse mode reconstructs the asset immediately before the swap. The
        // initiating address is placed on both sides of the compact row so the
        // ordinary reverse source solver continues from that address, while
        // Column F/G/M/R/S all use the pre-swap asset and its converted slice.
        this.appendRawDataSwapNote(row, candidate, attribution);
        row[COL.ATTRIBUTION] = "";
        row[COL.FROM_ATTRIBUTION] = "";
        row[COL.FUNDS_FOLLOWED] = this.formatFunds(candidate.followedBefore, sentToken);
        row[COL.FROM_HASH] = this.toText(selectedRow[COL.FROM_HASH]) || "";
        row[COL.CONTINUES_AS] = "Path ends.";
        row[COL.LEFT_BOUNDS_DISPLAY] = this.formatCaseBuddyLeftBoundsAmount(candidate.leftBefore || 0, sentToken);
        row[COL.LEFT_BOUNDS_NUMERIC] = this.formatAmount(candidate.leftBefore || 0);
        row[COL.CHAIN_ID] = entry?.chainId || this.toText(selectedRow[COL.CHAIN_ID]) || "";
        row[COL.CHAIN_NAME] = entry?.chainName || this.toText(selectedRow[COL.CHAIN_NAME]) || "";
        row[COL.HOP_LOGIC] = `SWAP/${this.hopLogicType}`;
        row[COL.TOOL88_NOTES] = this.appendUniqueNote(row[COL.TOOL88_NOTES], [
          `Tool 88: Reverse swap solved using ${attribution}.`,
          `Sent total: ${this.formatFunds(candidate.sent?.amount || 0, sentToken)}. Received total: ${this.formatFunds(candidate.received?.amount || 0, receivedToken)}.`,
          `Reverse-swap funds followed: ${this.formatFunds(candidate.followedBefore, sentToken)} before the swap from ${this.formatFunds(candidate.followedAfter, receivedToken)} after the swap.`,
          `Reverse-swap left bounds: ${this.formatFunds(candidate.leftBefore || 0, sentToken)} before the swap from ${this.formatFunds(candidate.leftAfter || 0, receivedToken)} after the swap.`,
          `Tool 88 computed pre-swap funds followed: ${this.formatFunds(candidate.followedBefore, sentToken)} from ${this.formatFunds(candidate.followedAfter, receivedToken)} after the swap.`,
          `Tool 88 computed pre-swap left bounds: ${this.formatFunds(candidate.leftBefore || 0, sentToken)} from ${this.formatFunds(candidate.leftAfter || 0, receivedToken)} after the swap.`,
          candidate.swapContractAddress ? `Swap contract/router: ${candidate.swapContractAddress}.` : "",
          candidate.receivedTokenContract ? `Received token contract: ${candidate.receivedTokenContract}.` : "",
          `Tool 88 flow: assessed; action=reverse-swap; direction=reverse; target=${candidate.initiatingAddress || "unknown"}.`,
        ].filter(Boolean).join(" "));
        return row;
      }

      // Forward behavior is intentionally preserved byte-for-byte in substance:
      // F/G show the received asset while M/R/S retain the pre-swap slice.
      row[COL.AMOUNT] = this.formatAmount(candidate.received?.amount || 0);
      row[COL.TOKEN] = receivedToken;
      const receivedPriorBalance = this.parseNumberLike(
        candidate.received?.priorBalanceBeforeTransaction
          ?? candidate.received?.balanceBeforeTransaction
          ?? candidate.received?.assetBalanceBefore
          ?? candidate.receivedPriorBalanceBeforeTransaction
          ?? candidate.outputPriorBalanceBeforeTransaction
          ?? candidate.outputPriorBalance
          ?? 0
      );
      if (Number.isFinite(receivedPriorBalance) && receivedPriorBalance >= 0) {
        row[COL.PRIOR_BALANCE] = Number(receivedPriorBalance).toFixed(receivedToken === 'BTC' ? 8 : 4);
      }

      // Column J gets the clean WayFinder-style swap sentence regardless of the
      // Tool 88 diagnostic-note setting. Column K intentionally stays blank
      // because the swap/router service is not attribution for the receiving address.
      this.appendRawDataSwapNote(row, candidate, attribution);
      row[COL.ATTRIBUTION] = "";

      // WayFinder-style raw data keeps the pre-swap followed slice in Column M
      // and the pre-swap left bounds in R/S, while F/G show the received asset.
      row[COL.FUNDS_FOLLOWED] = this.formatFunds(candidate.followedBefore, beforeToken);
      row[COL.FROM_HASH] = this.toText(selectedRow[COL.FROM_HASH]) || "";
      row[COL.CONTINUES_AS] = "Path ends.";
      row[COL.LEFT_BOUNDS_DISPLAY] = this.formatCaseBuddyLeftBoundsAmount(candidate.leftBefore || 0, beforeToken);
      row[COL.LEFT_BOUNDS_NUMERIC] = this.formatAmount(candidate.leftBefore || 0);
      row[COL.CHAIN_ID] = entry?.chainId || this.toText(selectedRow[COL.CHAIN_ID]) || "";
      row[COL.CHAIN_NAME] = entry?.chainName || this.toText(selectedRow[COL.CHAIN_NAME]) || "";
      row[COL.HOP_LOGIC] = `SWAP/${this.hopLogicType}`;

      row[COL.TOOL88_NOTES] = this.appendUniqueNote(row[COL.TOOL88_NOTES], [
        `Tool 88: Swap solved using ${attribution}.`,
        `Sent total: ${this.formatFunds(candidate.sent?.amount || 0, sentToken)}. Received total: ${this.formatFunds(candidate.received?.amount || 0, receivedToken)}.`,
        `Post-swap funds followed: ${this.formatFunds(candidate.followedAfter, receivedToken)}.`,
        `Post-swap left bounds: ${this.formatFunds(candidate.leftAfter, receivedToken)}.`,
        `Tool 88 computed post-swap funds followed: ${this.formatFunds(candidate.followedAfter, receivedToken)} from ${this.formatFunds(candidate.followedBefore, beforeToken)} before the swap.`,
        `Tool 88 computed post-swap left bounds: ${this.formatFunds(candidate.leftAfter, receivedToken)} from ${this.formatFunds(candidate.leftBefore || 0, beforeToken)} before the swap.`,
        `Pre-swap raw-data values retained in Column M/R: ${this.formatFunds(candidate.followedBefore, beforeToken)} at left bounds ${this.formatFunds(candidate.leftBefore || 0, beforeToken)}.`,
        candidate.swapContractAddress ? `Swap contract/router: ${candidate.swapContractAddress}.` : "",
        candidate.receivedTokenContract ? `Received token contract: ${candidate.receivedTokenContract}.` : "",
      ].filter(Boolean).join(" "));
      return row;
    },

    buildSwapExplorerReport(explorer = {}) {
      const tabs = explorer.tabs || {};
      const candidate = explorer.automaticCandidate;
      const lines = [
        "Tool 88 Swap Explorer Report",
        "----------------------------------------",
        `Transaction hash: ${explorer.txHash || ""}`,
        `Initiating address: ${explorer.initiatingAddress || ""}`,
        `Main rows: ${(tabs.main || []).length}`,
        `Internal rows: ${(tabs.internal || []).length}`,
        `ERC20/TRC20 rows: ${(tabs.erc20 || []).length}`,
        `NFT rows: ${(tabs.nft || []).length}`,
        "",
        candidate ? `Automatic solver: ${candidate.title}` : "Automatic solver: not available",
        candidate ? candidate.summary : "Review the transaction explorer tabs and select the correct incoming transfer if this is a swap.",
      ];
      return lines.join("\n");
    },

    buildSwapApplyReport(candidate = {}) {
      const reverse = candidate.traceDirection === "reverse" || this.traceDirection === "reverse";
      if (!reverse) {
        return [
          "Tool 88 Swap Solver Report",
          "----------------------------------------",
          `Transaction hash: ${candidate.txHash || ""}`,
          `Initiating address: ${candidate.initiatingAddress || ""}`,
          `Sent side: ${this.formatFunds(candidate.sent?.amount || 0, candidate.sent?.token || "")}`,
          `Received side: ${this.formatFunds(candidate.received?.amount || 0, candidate.received?.token || "")}`,
          `Swap ratio: ${Number(candidate.swapRate || 0).toPrecision(12)}`,
          `Funds followed before swap: ${this.formatFunds(candidate.followedBefore || 0, candidate.sent?.token || candidate.fundsIn?.token || "")}`,
          `Funds followed after swap: ${this.formatFunds(candidate.followedAfter || 0, candidate.received?.token || "")}`,
          `Left bounds before swap: ${this.formatFunds(candidate.leftBefore || 0, candidate.sent?.token || candidate.fundsIn?.token || "")}`,
          `Left bounds after swap: ${this.formatFunds(candidate.leftAfter || 0, candidate.received?.token || "")}`,
          "",
          "The selected same-hash entry was replaced with one compact WayFinder-style swap row. Column F/G show the received swap asset; Column M/R/S retain the pre-swap followed amount and left bounds for raw-data compatibility. Tool 88 stores the converted post-swap followed amount in its diagnostic note so the row can still be traced safely.",
        ].join("\n");
      }
      return [
        "Tool 88 Reverse Swap Solver Report",
        "----------------------------------------",
        `Transaction hash: ${candidate.txHash || ""}`,
        `Initiating address: ${candidate.initiatingAddress || ""}`,
        `Sent side: ${this.formatFunds(candidate.sent?.amount || 0, candidate.sent?.token || "")}`,
        `Received side: ${this.formatFunds(candidate.received?.amount || 0, candidate.received?.token || "")}`,
        `Swap ratio: ${Number(candidate.swapRate || 0).toPrecision(12)}`,
        `Funds followed before swap: ${this.formatFunds(candidate.followedBefore || 0, candidate.sent?.token || candidate.fundsIn?.token || "")}`,
        `Funds followed after swap: ${this.formatFunds(candidate.followedAfter || 0, candidate.received?.token || "")}`,
        `Left bounds before swap: ${this.formatFunds(candidate.leftBefore || 0, candidate.sent?.token || candidate.fundsIn?.token || "")}`,
        `Left bounds after swap: ${this.formatFunds(candidate.leftAfter || 0, candidate.received?.token || "")}`,
        "",
        "The selected same-hash entry was replaced with one compact reverse-swap row. Column F/G/M/R/S now describe the reconstructed pre-swap asset and slice so Tool 88 can continue tracing the initiating address backward.",
      ].join("\n");
    },

    getBridgeInitiatingAddress(entry, selectedAddress) {
      return this.getSwapInitiatingAddress(entry, selectedAddress);
    },

    async openBridgeSolver(options = {}) {
      if (!this.selectedEntry || this.isProcessing) {
        return;
      }
      const entry = this.selectedEntry;
      const selectedAddress = this.selectedTargetAddress;
      const initiatingAddress = this.getBridgeInitiatingAddress(entry, selectedAddress);
      const countContext = this.inferCountRequestContext(entry, selectedAddress);
      const fundsIn = this.deriveFundsIn(entry, selectedAddress);

      this.isProcessing = true;
      this.statusMessage = options.bruteForce ? "Running bridge destination candidate scan..." : "Loading bridge solver details...";
      this.bridgeApplyResult = null;
      try {
        const response = await this.postTool88({
          action: options.bruteForce ? "bridge_bruteforce" : "bridge_solver",
          tx_hash: entry.txHash,
          transaction_hash: entry.txHash,
          selected_address: selectedAddress,
          target_address: selectedAddress,
          initiating_address: initiatingAddress,
          direction: this.traceDirection,
          chain_id: entry.chainId || countContext.chain_id || "",
          chain_name: entry.chainName || countContext.chain_name || "",
          network_family: countContext.network_family || "",
          attribution_label: this.attributionLookupResult?.displayLabel || this.toText(this.getSelectedTargetRow(entry, selectedAddress)?.[this.getAttributionColumnIndex()] || ""),
          destination_network_hint: "tron",
          lookup_destination_receipts: 1,
          brute_force: options.bruteForce ? 1 : 0,
          run_bruteforce: options.bruteForce ? 1 : 0,
          sample_mode: this.isSampleDataLoaded ? 1 : 0,
          source_token_symbol: fundsIn.token || countContext.token_symbol || "",
          source_amount: fundsIn.amount,
          source_left_bounds: this.getEntryLeftBoundsAmount(entry, selectedAddress),
          token_contract: countContext.token_contract || countContext.contract_address || "",
          token_decimals: countContext.token_decimals ?? null,
          entry_context: this.serializeEntryContext(entry),
        });
        const result = response.result || response;
        this.bridgeExplorer = this.normalizeBridgeExplorerPayload(result, entry, selectedAddress, initiatingAddress);
        this.bridgeSelectedCandidateKey = this.bridgeBestCandidate?.canApply ? this.bridgeBestCandidate.key : "";
        this.reportText = this.buildBridgeExplorerReport(this.bridgeExplorer);
      } catch (error) {
        const message = error?.message || "Unknown bridge solver error";
        this.warnings.push(`Bridge solver failed: ${message}`);
        this.reportText = `Bridge solver failed: ${message}`;
      } finally {
        this.isProcessing = false;
        this.statusMessage = "";
      }
    },

    normalizeBridgeExplorerPayload(payload = {}, entry = null, selectedAddress = "", initiatingAddress = "") {
      const txHash = this.toText(payload.tx_hash || payload.txHash || payload.transaction_hash || entry?.txHash || "");
      const rawCandidates = Array.isArray(payload.candidates)
        ? payload.candidates
        : Array.isArray(payload.bridge_candidates)
          ? payload.bridge_candidates
          : [];
      const bestKey = this.toText(payload.best_candidate?.key || payload.bestCandidate?.key || "");
      const candidates = rawCandidates.map((candidate, index) => this.normalizeBridgeCandidate(candidate, index, bestKey, entry, selectedAddress)).filter(Boolean);
      const logs = this.normalizeBridgeLogRows(payload.logs || payload.bridge_logs || payload.transfer_logs || []);
      return {
        txHash,
        selectedAddress,
        initiatingAddress: this.toText(payload.initiating_address || payload.initiatingAddress || initiatingAddress),
        provider: this.toText(payload.provider || payload.source || ""),
        source: this.toText(payload.source || "tool88_bridge_solver"),
        destinationNetworkHint: this.toText(payload.destination_network_hint || payload.destinationNetworkHint || "TRON"),
        summary: this.toText(payload.summary || payload.message || "Tool 88 reviewed bridge/service logs, procedural decoders, and optional brute-force address candidates."),
        candidates,
        logs,
        stages: Array.isArray(payload.stages) ? payload.stages.map((stage, index) => ({
          key: this.toText(stage.key || `bridge-stage-${index}`),
          label: this.toText(stage.label || `Stage ${index + 1}`),
          title: this.toText(stage.title || stage.status || "Review"),
          summary: this.toText(stage.summary || stage.message || ""),
          status: this.toText(stage.status || "neutral").toLowerCase(),
        })) : [],
        raw: payload,
      };
    },

    normalizeBridgeCandidate(candidate = {}, index = 0, bestKey = "", entry = null, selectedAddress = "") {
      if (!candidate || typeof candidate !== "object") return null;
      const destinationAddress = this.toText(candidate.destination_address || candidate.destinationAddress || candidate.normalized_destination || candidate.normalizedDestination || candidate.address || "");
      const method = this.toText(candidate.method || candidate.solver || candidate.source || "candidate");
      const confidence = this.toText(candidate.confidence || candidate.confidence_label || candidate.confidenceLabel || (Number(candidate.score || 0) >= 700 ? "high" : Number(candidate.score || 0) >= 250 ? "medium" : "review")).toLowerCase() || "review";
      const tokenSymbol = this.toText(candidate.token_symbol || candidate.tokenSymbol || candidate.dst_token_symbol || candidate.dstTokenSymbol || candidate.to_token_symbol || candidate.toTokenSymbol || "USDT");
      const resolved = candidate.resolved_transfer || candidate.resolvedTransfer || candidate.receive || candidate.receiving_transfer || candidate.receivingTransfer || null;
      const amountInfo = this.deriveBridgeCandidateAmounts(candidate, resolved, entry, selectedAddress, tokenSymbol);
      const key = this.toText(candidate.key || `${method}-${destinationAddress || "unknown"}-${index}`);
      const resolvedHash = this.toText(resolved?.hash || resolved?.transaction_id || resolved?.transaction_hash || candidate.resolved_transaction_hash || candidate.receive_tx_hash || candidate.receiveTxHash || candidate.destination_tx_hash || "");
      const resolvedAmount = this.parseNumberLike(resolved?.amount_formatted ?? resolved?.amount ?? resolved?.quantity ?? "");
      const resolvedLabel = resolvedHash
        ? `${resolvedHash}${Number.isFinite(resolvedAmount) ? ` / ${this.formatFunds(resolvedAmount, tokenSymbol)}` : ""}`
        : (candidate.lookup_status === "searched_no_match" ? "No likely receiving transfer found" : "Not looked up / manual research");
      const score = Number(candidate.score || 0);
      const serverCanApply = candidate.can_apply ?? candidate.canApply;
      const canApply = typeof serverCanApply === "boolean"
        ? Boolean(destinationAddress && serverCanApply)
        : Boolean(destinationAddress && (resolvedHash || candidate.allow_placeholder_apply || confidence === "high" || confidence === "medium"));
      return {
        ...candidate,
        key,
        destinationAddress,
        destinationNetwork: this.toText(candidate.destination_network_label || candidate.destinationNetworkLabel || candidate.destination_network || candidate.destinationNetwork || candidate.dst_chain || candidate.dstChain || "TRON"),
        destinationAddressRaw: this.toText(candidate.destination_address_raw || candidate.destinationAddressRaw || ""),
        method,
        methodLabel: method.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
        confidence,
        confidenceLabel: confidence === "high" ? "High" : confidence === "medium" ? "Medium" : "Review",
        score,
        tokenSymbol,
        amountDisplay: this.toText(candidate.amount_label || candidate.amountLabel || "") || amountInfo.amountDisplay,
        followedBefore: amountInfo.sourceFundsFollowed,
        leftBefore: amountInfo.sourceLeftBefore,
        followedAfter: amountInfo.followedAfter,
        leftAfter: amountInfo.leftAfter,
        bridgeRate: amountInfo.bridgeRate,
        bridgeSameAsset: amountInfo.bridgeSameAsset,
        bridgeAmountMethod: amountInfo.bridgeSameAsset ? "preserve-same-asset-queue" : (Number.isFinite(amountInfo.bridgeRate) ? "convert-by-bridge-rate" : "preserve-unknown-rate"),
        sourceAmount: amountInfo.sourceAmount,
        destinationAmount: amountInfo.destinationAmount,
        resolvedTransfer: resolved,
        resolvedTransferLabel: resolvedLabel,
        resolvedTransactionHash: resolvedHash,
        receiveTxHash: this.toText(candidate.receive_tx_hash || candidate.receiveTxHash || resolvedHash),
        reason: this.toText(candidate.reason || candidate.score_reason || candidate.scoreReason || candidate.detail || ""),
        summary: this.toText(candidate.summary || candidate.reason || candidate.score_reason || "Bridge candidate found for review."),
        title: destinationAddress ? `${tokenSymbol || "Asset"} to ${destinationAddress}` : "Bridge candidate needs review",
        canApply,
        isBestCandidate: Boolean(bestKey && key === bestKey) || Boolean(candidate.is_best_candidate || candidate.isBestCandidate),
      };
    },

    deriveBridgeCandidateAmounts(candidate = {}, resolved = null, entry = null, selectedAddress = "", tokenSymbol = "") {
      const sourceEntry = entry || this.selectedEntry;
      const sourceSelectedAddress = selectedAddress || this.selectedTargetAddress;
      const fundsIn = this.deriveFundsIn(sourceEntry, sourceSelectedAddress);
      const candidateFundsBefore = this.parseNumberLike(
        candidate.source_funds_followed ??
        candidate.sourceFundsFollowed ??
        candidate.followed_before ??
        candidate.followedBefore ??
        candidate.funds_followed ??
        candidate.fundsFollowed
      );
      const candidateLeftBefore = this.parseNumberLike(
        candidate.source_left_bounds ??
        candidate.sourceLeftBefore ??
        candidate.left_before ??
        candidate.leftBefore ??
        candidate.left_bounds ??
        candidate.leftBounds
      );
      const entryLeftBefore = this.getEntryLeftBoundsAmount(sourceEntry, sourceSelectedAddress);
      const leftBefore = Number.isFinite(candidateLeftBefore) && candidateLeftBefore > 0
        ? Math.max(0, candidateLeftBefore)
        : entryLeftBefore;
      const token = tokenSymbol || candidate.token_symbol || fundsIn.token || "TOKEN";
      const decimals = Number(candidate.token_decimals ?? candidate.decimals ?? resolved?.token_decimals ?? (String(token).toUpperCase() === "USDT" ? 6 : 18));
      const parseAtomic = (value) => {
        const text = this.toText(value);
        if (!/^\d+$/.test(text) || !Number.isFinite(decimals)) return NaN;
        return this.parseNumberLike(this.formatAtomicAmountString(text, decimals));
      };
      const sourceAmount = this.parseNumberLike(candidate.source_amount_formatted ?? candidate.from_amount_formatted ?? candidate.sourceAmount ?? candidate.fromAmount);
      const sourceAmountAtomic = parseAtomic(candidate.source_amount_raw ?? candidate.from_amount_raw ?? candidate.fromAmountRaw);
      const destinationAmountDirect = this.parseNumberLike(resolved?.amount_formatted ?? candidate.destination_amount_formatted ?? candidate.min_return_amount_formatted ?? candidate.amount_formatted ?? candidate.amountFormatted);
      const destinationAmountAtomic = parseAtomic(resolved?.amount_raw ?? resolved?.quant ?? candidate.destination_amount_raw ?? candidate.min_return_amount_raw ?? candidate.min_return_amount ?? candidate.minReturnAmount ?? candidate.amount_raw);
      const source = Number.isFinite(sourceAmount) ? sourceAmount : sourceAmountAtomic;
      const destination = Number.isFinite(destinationAmountDirect) ? destinationAmountDirect : destinationAmountAtomic;
      const bridgeRate = Number.isFinite(source) && source > 0 && Number.isFinite(destination) && destination > 0 ? destination / source : NaN;
      const fundsBefore = Number.isFinite(candidateFundsBefore) && candidateFundsBefore > 0
        ? Math.max(0, candidateFundsBefore)
        : Number(fundsIn.amount || 0);
      const sameAssetAcrossBridge = this.tokenSymbolsMatchForSwap(fundsIn.token, token) || this.isStableCaseBuddyToken(fundsIn.token) && this.isStableCaseBuddyToken(token);
      const rawFollowedAfter = sameAssetAcrossBridge
        ? fundsBefore
        : Number.isFinite(bridgeRate)
          ? fundsBefore * bridgeRate
          : (Number.isFinite(destination) && destination > 0 ? Math.min(fundsBefore || destination, destination) : fundsBefore);
      const rawLeftAfter = sameAssetAcrossBridge
        ? leftBefore
        : Number.isFinite(bridgeRate)
          ? leftBefore * bridgeRate
          : leftBefore;
      const destinationAmount = Number.isFinite(destination) && destination > 0 ? destination : NaN;
      const followedAfter = Number.isFinite(rawFollowedAfter)
        ? Math.max(0, Number.isFinite(destinationAmount) ? Math.min(rawFollowedAfter, destinationAmount) : rawFollowedAfter)
        : 0;
      // Preserve the forensic slice first, then cap the left-bound queue to the
      // destination-side transfer capacity. For same-asset bridges such as
      // USDT -> USDT via SWFT/Transit, fees reduce spare capacity, not the
      // followed slice. This keeps post-bridge FIFO/LIFO rows aligned with the
      // pre-bridge left-bounds story instead of resetting to 0 or shrinking
      // the followed amount unnecessarily.
      const maxLeftAfterFollowed = Number.isFinite(destinationAmount) ? Math.max(0, destinationAmount - followedAfter) : NaN;
      const leftAfter = Number.isFinite(rawLeftAfter)
        ? Math.max(0, Number.isFinite(maxLeftAfterFollowed) ? Math.min(rawLeftAfter, maxLeftAfterFollowed) : rawLeftAfter)
        : 0;
      return {
        sourceAmount: source,
        destinationAmount: destination,
        bridgeRate,
        followedAfter: Number.isFinite(followedAfter) ? Math.max(0, followedAfter) : 0,
        leftAfter: Number.isFinite(leftAfter) ? Math.max(0, leftAfter) : 0,
        sourceFundsFollowed: Number.isFinite(fundsBefore) ? Math.max(0, fundsBefore) : 0,
        sourceLeftBefore: Number.isFinite(leftBefore) ? Math.max(0, leftBefore) : 0,
        bridgeSameAsset: sameAssetAcrossBridge,
        amountDisplay: Number.isFinite(destination) ? this.formatFunds(destination, token) : this.toText(candidate.amount_label || candidate.min_return_amount_formatted || candidate.min_return_amount_raw || candidate.amount_raw || ""),
      };
    },

    normalizeBridgeCandidateAccounting(candidate = {}, entry = null) {
      if (!candidate || typeof candidate !== "object") return candidate || {};
      const sourceEntry = entry || this.selectedEntry;
      const sourceSelectedAddress = this.selectedTargetAddress || candidate.sourceSelectedAddress || candidate.selectedAddress || "";
      const token = this.toText(candidate.tokenSymbol || candidate.token_symbol || "TOKEN");
      const amountInfo = this.deriveBridgeCandidateAmounts(candidate, candidate.resolvedTransfer || candidate.receive || candidate.resolved_transfer || null, sourceEntry, sourceSelectedAddress, token);
      const next = { ...candidate };
      next.followedBefore = amountInfo.sourceFundsFollowed;
      next.leftBefore = amountInfo.sourceLeftBefore;
      next.sourceFundsFollowed = amountInfo.sourceFundsFollowed;
      next.sourceLeftBefore = amountInfo.sourceLeftBefore;
      next.followedAfter = amountInfo.followedAfter;
      next.leftAfter = amountInfo.leftAfter;
      next.bridgeRate = amountInfo.bridgeRate;
      next.bridgeSameAsset = amountInfo.bridgeSameAsset;
      next.sourceAmount = amountInfo.sourceAmount;
      next.destinationAmount = amountInfo.destinationAmount;
      next.amountDisplay = next.amountDisplay || amountInfo.amountDisplay;
      next.bridgeAccountingAdjusted = true;
      return next;
    },

    normalizeBridgeLogRows(rows = []) {
      return (Array.isArray(rows) ? rows : []).map((row, index) => {
        const topics = Array.isArray(row?.topics) ? row.topics : [];
        const data = this.toText(row?.data || row?.raw_data || row?.rawData || "");
        return {
          key: `bridge-log-${index}`,
          rowNumber: index + 1,
          address: this.toText(row?.address || row?.contract_address || row?.contractAddress || ""),
          topic0: this.toText(topics[0] || row?.topic0 || ""),
          dataPreview: data ? `${data.slice(0, 96)}${data.length > 96 ? "…" : ""}` : "No data",
          raw: row,
        };
      });
    },

    applyBridgeCandidate(candidate) {
      if (!candidate || !candidate.canApply || !this.selectedEntry) return;
      const adjustedCandidate = this.normalizeBridgeCandidateAccounting(candidate, this.selectedEntry);
      const bridgeName = this.getBridgeDisplayName(adjustedCandidate);
      const row = this.buildBridgeHopRow(adjustedCandidate, this.selectedEntry, bridgeName);
      let outputRows = this.getCurrentOutputBaseRows();
      outputRows = this.markEntryContinuesAs(outputRows, this.selectedEntry, [row[COL.TX_HASH] || adjustedCandidate.destinationAddress]);
      const entryBounds = this.findEntryBoundsInRows(outputRows, this.selectedEntry);
      const entryStartIndex = entryBounds?.startRowIndex ?? Math.max(0, Number(this.selectedEntry?.startRowIndex || 0));
      const entryRow = outputRows[entryStartIndex];
      if (entryRow) {
        this.appendRawDataBridgeInitiationNote(entryRow, bridgeName);
        outputRows[entryStartIndex] = entryRow;
      }
      outputRows = this.insertRowsAfterEntry(outputRows, this.selectedEntry, [row]);
      this.loadOutputRows(outputRows);
      const note = `Bridge result recorded. Tool 88 added a bridge initiation note and a destination-side receive row for ${adjustedCandidate.destinationNetwork || "destination network"}: ${adjustedCandidate.destinationAddress}.`;
      this.bridgeApplyResult = { note, candidate: adjustedCandidate, row, rows: [row] };
      this.swapApplyResult = null;
      this.pathEndingResult = null;
      this.traceResult = null;
      this.lastRunMeta = {
        ...this.buildBaseMeta(this.inputSnapshotRows, this.parsedEntries, this.targetCandidates, this.selectedTargetAddress),
        bridgeSolved: true,
        bridgeDestination: adjustedCandidate.destinationAddress,
        bridgeDestinationNetwork: adjustedCandidate.destinationNetwork,
        bridgeToken: adjustedCandidate.tokenSymbol,
      };
      this.reportText = this.buildBridgeApplyReport(adjustedCandidate);
    },

    buildBridgeHopRow(candidate, entry, bridgeName = "") {
      candidate = this.normalizeBridgeCandidateAccounting(candidate || {}, entry);
      const row = this.makeBlankRow();
      const resolved = candidate.resolvedTransfer || candidate.receive || {};
      const hash = this.toText(candidate.resolvedTransactionHash || candidate.receiveTxHash || candidate.receive_tx_hash || resolved.hash || resolved.transaction_hash || resolved.transaction_id || entry?.txHash || "");
      const token = candidate.tokenSymbol || this.deriveFundsIn(entry, this.selectedTargetAddress).token || "TOKEN";
      const date = this.parseProviderDate(resolved.timeStamp || resolved.block_ts || resolved.date || resolved.timestamp || "") || this.getEntryDate(entry);
      const fromAddress = this.toText(resolved.from_address || resolved.fromAddress || resolved.from || candidate.bridge_contract || this.selectedTargetAddress || "");
      const toAddress = this.toText(resolved.to_address || resolved.toAddress || resolved.to || candidate.destinationAddress || "");
      const amount = Number.isFinite(candidate.destinationAmount) && candidate.destinationAmount > 0
        ? candidate.destinationAmount
        : Number(candidate.followedAfter || 0);
      row[COL.PATH_LABEL] = "";
      row[COL.TX_HASH] = hash;
      row[COL.FROM_ADDRESS] = fromAddress;
      row[COL.TO_ADDRESS] = toAddress;
      row[COL.DATE] = date ? this.formatUtcDate(date) : (entry?.dateText || "");
      row[COL.AMOUNT] = this.formatAmount(amount || candidate.followedAfter || 0);
      row[COL.TOKEN] = token;
      const bridgeLabel = this.cleanAttributionLabel(bridgeName || this.getBridgeDisplayName(candidate));
      this.appendRawDataBridgeReceiveNote(row, bridgeLabel, candidate.destinationNetwork || row[COL.CHAIN_NAME] || "destination network");
      this.setTool88NoteOnRow(row, [
        `Tool 88: Bridge solved using ${candidate.methodLabel || "bridge solver"}.`,
        `Destination: ${candidate.destinationAddress}${candidate.destinationNetwork ? ` on ${candidate.destinationNetwork}` : ""}.`,
        candidate.resolvedTransactionHash ? `Likely receiving transaction: ${candidate.resolvedTransactionHash}.` : "No exact receiving transfer was confirmed; review this bridge destination before continuing.",
        candidate.bridgeSameAsset ? `Bridge accounting preserved the incoming funds-followed and left-bounds allocation because the source and destination assets match: ${this.formatFunds(candidate.followedAfter || 0, token)} followed at left bounds ${this.formatFunds(candidate.leftAfter || 0, token)}.` : (Number.isFinite(candidate.bridgeRate) ? `Bridge ratio used for funds-followed conversion: ${candidate.bridgeRate}.` : "Bridge ratio was not fully known; Tool 88 used the best available destination amount/funds-followed estimate."),
        Number.isFinite(candidate.sourceLeftBefore) && candidate.sourceLeftBefore > 0 ? `Source left bounds before bridge: ${this.formatFunds(candidate.sourceLeftBefore, token)}.` : "",
        candidate.reason ? `Solver note: ${candidate.reason}` : "",
      ].filter(Boolean).join(" "));
      // Do not write the bridge service into Column K for the destination-side receive row.
      // Column K describes the receiving/To address; bridge attribution usually belongs to the sending bridge/service side.
      row[COL.ATTRIBUTION] = "";
      row[COL.FUNDS_FOLLOWED] = this.formatFunds(candidate.followedAfter || amount || 0, token);
      row[COL.FROM_HASH] = entry?.txHash || "";
      row[COL.CONTINUES_AS] = "Path ends.";
      row[COL.LEFT_BOUNDS_DISPLAY] = this.formatCaseBuddyLeftBoundsAmount(candidate.leftAfter || 0, token);
      row[COL.LEFT_BOUNDS_NUMERIC] = this.formatAmount(candidate.leftAfter || 0);
      row[COL.CHAIN_ID] = candidate.destinationChainId || (candidate.destinationNetwork === "tron" || candidate.destinationNetwork === "TRON" ? "tron" : "");
      row[COL.CHAIN_NAME] = candidate.destinationNetwork || "Bridge destination";
      row[COL.HOP_LOGIC] = `BRIDGE/${this.hopLogicType}`;
      return row;
    },

    buildBridgeExplorerReport(explorer = {}) {
      const candidates = explorer.candidates || [];
      const best = this.bridgeBestCandidate;
      return [
        "Tool 88 Bridge Solver Report",
        "----------------------------------------",
        `Transaction hash: ${explorer.txHash || ""}`,
        `Initiating address: ${explorer.initiatingAddress || ""}`,
        `Candidates found: ${candidates.length}`,
        `Log rows reviewed: ${(explorer.logs || []).length}`,
        "",
        best ? `Best candidate: ${best.title}` : "Best candidate: not available",
        best ? `Confidence: ${best.confidenceLabel}; method: ${best.methodLabel}` : "Run the brute-force scan if the procedural solver did not find a destination.",
        best?.summary || "Bridge solver shows decoded service candidates and possible destinations for manual review.",
      ].join("\n");
    },

    buildBridgeApplyReport(candidate = {}) {
      return [
        "Tool 88 Bridge Solver Apply Report",
        "----------------------------------------",
        `Method: ${candidate.methodLabel || candidate.method || ""}`,
        `Confidence: ${candidate.confidenceLabel || candidate.confidence || ""}`,
        `Destination network: ${candidate.destinationNetwork || ""}`,
        `Destination address: ${candidate.destinationAddress || ""}`,
        `Token: ${candidate.tokenSymbol || ""}`,
        `Destination amount: ${candidate.amountDisplay || "Unknown"}`,
        `Funds followed after bridge: ${this.formatFunds(candidate.followedAfter || 0, candidate.tokenSymbol || "")}`,
        `Left bounds after bridge: ${this.formatFunds(candidate.leftAfter || 0, candidate.tokenSymbol || "")}`,
        candidate.bridgeAmountMethod ? `Bridge allocation method: ${candidate.bridgeAmountMethod}` : "",
        candidate.resolvedTransactionHash ? `Likely receiving transaction: ${candidate.resolvedTransactionHash}` : "Likely receiving transaction: not confirmed; review before continuing.",
        "",
        "The bridge row was inserted below the selected input entry. This is a first-pass bridge module: high-confidence BK/Bitget and Transit-style decoders can create a bridge hop, while uncertain candidates remain review-first.",
      ].join("\n");
    },

    treatAsSpecialPathEnding(type, options = {}) {
      if (!this.selectedEntry || !this.selectedTargetAddress) {
        this.reportText = "Special path-ending treatment could not be recorded because no selected entry/address is available.";
        return;
      }

      const normalizedType = type === "bridge" ? "bridge" : "swap";
      const label = normalizedType === "bridge" ? "Bridge" : "Swap";
      const supported = this.hasTreatmentSupport(normalizedType);
      if (!options.skipConfirm && !supported && typeof window !== "undefined" && typeof window.confirm === "function") {
        const proceed = window.confirm(
          `Tool 88 did not find a strong ${label.toLowerCase()} signal from attribution, event logs, or memo checks yet. Record this as a ${label} path ending anyway?`
        );
        if (!proceed) {
          return;
        }
      }

      const attributionLabel = this.cleanAttributionLabel(this.attributionLookupResult?.displayLabel || "");
      const reason = options.autoReason || `User intentionally treated this target address as a ${label} after Tool 88 checks.${attributionLabel ? ` Attribution: ${attributionLabel}.` : ""}${supported ? " Supporting attribution/event/memo signal was present." : " No strong attribution/event/memo signal was detected; user overrode the caution."}`;
      const fundsIn = this.deriveFundsIn(this.selectedEntry, this.selectedTargetAddress);
      const dormantAmountText = Number.isFinite(fundsIn.amount)
        ? this.formatFunds(fundsIn.amount, fundsIn.token)
        : "";

      const finalized = this.finalizeEntryAsPathEnding({
        baseRows: this.getCurrentOutputBaseRows(),
        entry: this.selectedEntry,
        selectedAddress: this.selectedTargetAddress,
        reason,
        dormantAmountText,
        suppressRawPathEndingAttributionNote: true,
      });

      this.bridgeApplyResult = null;
      this.pathEndingResult = {
        note: finalized.note,
        dormantAmountText,
        reason: finalized.reason,
        specialTreatment: label,
        autoDecision: Boolean(options.autoReason),
        autoBridgeTerminalFallback: options.autoBridgeTerminalFallback === true,
        bridgeEvidence: options.bridgeEvidence ? { ...options.bridgeEvidence } : null,
      };
      this.traceResult = null;
      this.loadOutputRows(finalized.rows);

      this.lastRunMeta = {
        ...this.buildBaseMeta(
          this.inputSnapshotRows,
          this.parsedEntries,
          this.targetCandidates,
          this.selectedTargetAddress
        ),
        exitCount: 0,
        dormantAmountText,
        transactionCount: this.countLookupResult?.transactionCount,
        attributionLabel: this.attributionLookupResult?.displayLabel || "",
        attributionClassification: label,
        autoBridgeTerminalFallback: options.autoBridgeTerminalFallback === true,
        bridgeEvidence: options.bridgeEvidence ? { ...options.bridgeEvidence } : null,
      };

      this.reportText = this.buildManualPathEndingReport(
        this.selectedEntry,
        this.selectedTargetAddress,
        this.pathEndingResult
      );
    },

    markPathEnding() {
      this.recordPathEndingWithReason(
        "User intentionally marked this target address as a path ending after Tool 88 checks."
      );
    },

    buildTraceRequestType(countContext = {}) {
      const family = this.toText(countContext.network_family).toLowerCase();
      const countKind = this.toText(countContext.count_kind).toLowerCase();
      if (family === "tron" && countKind === "token_transfer") {
        return "trc20_transfer_history";
      }
      if (family === "tron" && countKind === "native_transaction") {
        return "tron_native_transaction_history";
      }
      if (family === "btc" || family === "bitcoin") {
        return "btc_transaction_history";
      }
      if (family === "evm" && countKind === "token_transfer") {
        return "erc20_transfer_history";
      }
      if (family === "evm" && countKind === "native_transaction") {
        return "evm_native_transaction_history";
      }
      return "auto";
    },

    async fetchTraceTransactions(entry, selectedAddress, options = {}) {
      if (this.traceFetchStopRequested) {
        return this.buildTraceFetchCancelledPayload("", []);
      }
      const countContext = this.inferCountRequestContext(entry, selectedAddress);
      const fundsIn = this.deriveFundsIn(entry, selectedAddress);
      const effectiveAdditionalServiceChecks = options.additionalServiceChecksEnabled !== undefined
        ? Boolean(options.additionalServiceChecksEnabled)
        : this.enableAdditionalServiceChecks !== false;
      const expectedTraceCount = Number(
        this.countLookupResult?.transactionCount ??
        this.countLookupResult?.transaction_count ??
        -1
      );
      const pollContext = {
        entry,
        selectedAddress,
        countContext,
        expectedCount: Number.isFinite(expectedTraceCount) ? Math.trunc(expectedTraceCount) : -1,
        rawDataAutomation: this.caseBuddyRawDataAutomationContext(options),
        options,
      };

      if (this.useMockBackend) {
        return {
          status: "success",
          source: "mock_placeholder",
          job_id: "mock-local",
          transactions: this.buildMockTransactions(entry, selectedAddress),
        };
      }

      const cacheKey = this.buildTraceSessionCacheKey(entry, selectedAddress, countContext, fundsIn, {
        additionalServiceChecksEnabled: effectiveAdditionalServiceChecks,
        serviceStopOverride: options.serviceStopOverride === true || options.manualServiceOverride === true,
        depositEntityHopExtensionProbe: options.depositEntityHopExtensionProbe === true,
      });
      const manualServiceOverride = options.serviceStopOverride === true || options.manualServiceOverride === true;
      const shouldCacheManualOverridePayload = (payload) => !(manualServiceOverride && this.traceFetchStoppedForService(payload));
      const cached = this.getSessionTraceCache(cacheKey);
      if (cached) {
        this.backendJobMeta = {
          job_id: cached.job_id || "session-cache",
          status: "complete",
          source: "session_cache",
          cached_at: cached.cached_at,
        };
        this.updateTraceProgressFromResult(cached, Array.isArray(cached.transactions) ? cached.transactions.length : 0);
        this.statusMessage = "Using session-cached transaction history...";
        return cached;
      }

      const queueBody = {
        action: "queue_trace",
        trace_request_type: this.buildTraceRequestType(countContext),
        network_family: countContext.network_family || "",
        moralis_chain: countContext.moralis_chain || "",
        trace_provider: this.countLookupResult?.provider || countContext.provider_hint || "",
        count_route: this.countLookupResult?.route || countContext.route || "",
        address: selectedAddress,
        direction: this.traceDirection,
        hop_logic_type: this.hopLogicType,
        chain_id: entry?.chainId || countContext.chain_id || "",
        chain_name: entry?.chainName || countContext.chain_name || "",
        chain_label: countContext.chain_label || "",
        count_kind: countContext.count_kind || "",
        provider_hint: countContext.provider_hint || "",
        token_symbol: fundsIn.token || countContext.token_symbol || "",
        asset_symbol: fundsIn.token || countContext.token_symbol || "",
        token_contract: countContext.token_contract || countContext.contract_address || "",
        contract_address: countContext.token_contract || countContext.contract_address || "",
        token_decimals: countContext.token_decimals ?? null,
        token_registry_status: countContext.token_registry_status || "",
        expected_transaction_count: this.countLookupResult?.transactionCount ?? this.countLookupResult?.transaction_count ?? -1,
        expected_count: this.countLookupResult?.transactionCount ?? this.countLookupResult?.transaction_count ?? -1,
        transaction_count: this.countLookupResult?.transactionCount ?? this.countLookupResult?.transaction_count ?? -1,
        known_count: this.countLookupResult?.transactionCount ?? this.countLookupResult?.transaction_count ?? -1,
        absolute_hard_limit: INTERNAL_HARD_LIMIT_THRESHOLD,
        additional_service_checks_enabled: effectiveAdditionalServiceChecks ? 1 : 0,
        service_checks_enabled: effectiveAdditionalServiceChecks ? 1 : 0,
        enable_service_address_checks: effectiveAdditionalServiceChecks ? 1 : 0,
        service_address_checks_enabled: effectiveAdditionalServiceChecks ? 1 : 0,
        more_potential_service_checks: effectiveAdditionalServiceChecks ? 1 : 0,
        service_stop_override: options.serviceStopOverride === true || options.manualServiceOverride === true ? 1 : 0,
        manual_service_override: options.serviceStopOverride === true || options.manualServiceOverride === true ? 1 : 0,
        service_override_reason: options.serviceOverrideReason || "",
        deposit_entity_hop_extension_probe: options.depositEntityHopExtensionProbe === true ? 1 : 0,
        depositEntityHopExtensionProbe: options.depositEntityHopExtensionProbe === true,
        deposit_hot_wallet_probe: options.depositEntityHopExtensionProbe === true ? 1 : 0,
        depositHotWalletProbe: options.depositEntityHopExtensionProbe === true,
        sample_mode: this.isSampleDataLoaded ? 1 : 0,
        input_transaction_hash: entry?.txHash || "",
        entry_date: entry?.dateText || "",
        amount: fundsIn.amount,
        count_context: {
          ...countContext,
          transaction_count: this.countLookupResult?.transactionCount ?? this.countLookupResult?.transaction_count ?? -1,
          route: this.countLookupResult?.route || countContext.route || "",
          provider: this.countLookupResult?.provider || countContext.provider_hint || "",
          token_symbol: fundsIn.token || countContext.token_symbol || "",
          moralis_chain: this.countLookupResult?.moralisChain || this.countLookupResult?.moralis_chain || countContext.moralis_chain || "",
          token_contract: countContext.token_contract || countContext.contract_address || "",
          contract_address: countContext.token_contract || countContext.contract_address || "",
          token_decimals: countContext.token_decimals ?? null,
          token_registry_status: countContext.token_registry_status || "",
          service_stop_override: options.serviceStopOverride === true || options.manualServiceOverride === true,
          manual_service_override: options.serviceStopOverride === true || options.manualServiceOverride === true,
          service_override_reason: options.serviceOverrideReason || "",
          deposit_entity_hop_extension_probe: options.depositEntityHopExtensionProbe === true,
        },
        entry_context: {
          ...this.serializeEntryContext(entry),
          token: fundsIn.token || countContext.token_symbol || "",
          funds: fundsIn,
        },
      };

      const queued = await this.postTool88(queueBody);
      const result = queued.result || queued;
      const statusText = this.toText(
        result.trace_fetch?.status ||
          result.trace_status ||
          result.status ||
          ""
      ).toLowerCase();
      const hasTraceJob = Boolean(result.job_id);
      const isComplete = ["complete", "completed", "success"].includes(statusText);
      const isQueuedOrRunning = ["queued", "running", "pending", "processing", ""].includes(statusText);

      if (hasTraceJob && !isComplete && isQueuedOrRunning) {
        this.backendJobMeta = result;
        this.updateTraceProgressFromResult(result, 0);
        this.statusMessage = `Queued server trace job ${result.job_id}. Waiting for provider batches...`;
        const payload = await this.pollTraceJob(result.job_id, pollContext);
        if (shouldCacheManualOverridePayload(payload)) this.storeSessionTraceCache(cacheKey, payload);
        return payload;
      }

      if (hasTraceJob && result.trace_fetch && !isComplete) {
        this.backendJobMeta = result;
        this.updateTraceProgressFromResult(result, 0);
        this.statusMessage = `Queued server trace job ${result.job_id}. Waiting for provider batches...`;
        const payload = await this.pollTraceJob(result.job_id, pollContext);
        if (shouldCacheManualOverridePayload(payload)) this.storeSessionTraceCache(cacheKey, payload);
        return payload;
      }

      if (Array.isArray(result.transactions)) {
        if (result.job_id && Number(result.stored_in_queue_table || 0) === 1) {
          await this.releaseTraceJobStorage(result.job_id, "synchronous-or-placeholder-complete");
        }
        if (shouldCacheManualOverridePayload(result)) this.storeSessionTraceCache(cacheKey, result);
        return result;
      }

      if (!result.job_id) {
        throw new Error("Server did not return transactions or a trace job id.");
      }

      this.backendJobMeta = result;
      this.statusMessage = `Queued server trace job ${result.job_id}. Waiting for results...`;
      const payload = await this.pollTraceJob(result.job_id, pollContext);
      if (shouldCacheManualOverridePayload(payload)) this.storeSessionTraceCache(cacheKey, payload);
      return payload;
    },

    setMonitoringExecutionContext(context = null) {
      if (!context || typeof context !== "object") {
        this.monitoringExecutionContext = null;
        this.monitoringAttestation = null;
        return false;
      }
      const normalized = {
        item_id: Number(context.item_id),
        check_id: Number(context.check_id),
        state_version: Number(context.state_version),
        execution_token: String(context.execution_token || "").trim().toLowerCase(),
      };
      const valid = Number.isInteger(normalized.item_id) && normalized.item_id > 0
        && Number.isInteger(normalized.check_id) && normalized.check_id > 0
        && Number.isInteger(normalized.state_version) && normalized.state_version > 0
        && /^[a-f0-9]{64}$/.test(normalized.execution_token);
      if (!valid) {
        this.monitoringExecutionContext = null;
        this.monitoringAttestation = null;
        return false;
      }
      this.monitoringExecutionContext = normalized;
      this.monitoringAttestation = null;
      return true;
    },

    clearMonitoringExecutionContext() {
      this.monitoringExecutionContext = null;
      this.monitoringAttestation = null;
    },

    getMonitoringExecutionContext() {
      return this.monitoringExecutionContext
        ? { ...this.monitoringExecutionContext }
        : null;
    },

    getMonitoringAttestation() {
      return this.monitoringAttestation
        ? { ...this.monitoringAttestation }
        : null;
    },

    captureMonitoringAttestation(response) {
      const attestation = response?.monitoring_attestation;
      if (!attestation || typeof attestation !== "object") return;
      const current = this.monitoringExecutionContext;
      if (!current) return;
      if (
        Number(attestation.item_id) !== Number(current.item_id)
        || Number(attestation.check_id) !== Number(current.check_id)
        || Number(attestation.state_version) !== Number(current.state_version)
      ) return;
      this.monitoringAttestation = { ...attestation };
    },

    async parseTool88Response(response) {
      const text = await response.text();
      let json = null;
      const headerRequestId = response.headers?.get?.("X-Request-ID") || "";

      if (text) {
        try {
          json = JSON.parse(text);
        } catch (error) {
          throw new Error(
            `Tool 88 returned an unexpected HTTP ${response.status} response${headerRequestId ? ` (Request ${headerRequestId})` : ""}.`
          );
        }
      }

      if (!response.ok || !json || json.status !== "success") {
        const requestId = headerRequestId || json?.request_id || "";
        const detailParts = [
          json?.message,
          requestId ? `Request ${requestId}` : "",
        ].filter(Boolean);

        throw new Error(
          detailParts.length
            ? detailParts.join(" — ")
            : `Tool 88 server returned HTTP ${response.status}.`
        );
      }

      return json;
    },

    async postTool88(body) {
      await this.ensureTool88Auth();
      const monitoringContext = this.getMonitoringExecutionContext();
      const requestBody = {
        ...body,
        username: AuthService.getUsername() || "",
        tool88_client: "Tool88PageTool88Page_CaseBuddy monitoring-context-v67",
        ...(monitoringContext ? { monitoring_context: monitoringContext } : {}),
      };

      const response = await fetch(this.endpointUrl, {
        method: "POST",
        headers: this.getAuthHeaders(),
        credentials: "same-origin",
        cache: "no-store",
        body: JSON.stringify(requestBody),
      });

      const json = await this.parseTool88Response(response);
      this.captureMonitoringAttestation(json);
      return json;
    },

    getTracePollExpectedCount(context = {}, result = {}) {
      const traceFetch = result?.trace_fetch && typeof result.trace_fetch === "object" ? result.trace_fetch : {};
      const candidates = [
        context.expectedCount,
        traceFetch.expected_count,
        result.expected_count,
        result.expected_transaction_count,
        this.countLookupResult?.transactionCount,
        this.countLookupResult?.transaction_count,
      ];
      for (const candidate of candidates) {
        const number = Number(candidate);
        if (Number.isFinite(number) && number >= 0) return Math.trunc(number);
      }
      return -1;
    },

    canUseMempoolSpaceBrowserFallback(context = {}, result = {}) {
      if (!TOOL88_ALLOW_DIRECT_MEMPOOL_BROWSER_FALLBACK) return false;

      // Monitoring results must come through the server-attested Tool 88 path.
      // Direct browser provider fallbacks cannot be bound to a check receipt.
      if (this.monitoringExecutionContext) return false;

      const address = this.toText(context.selectedAddress || this.selectedTargetAddress);
      const family = this.toText(context.countContext?.network_family).toLowerCase();
      const expectedCount = this.getTracePollExpectedCount(context, result);
      const maxCount = context.rawDataAutomation
        ? TOOL88_MEMPOOL_BROWSER_AUTO_MAX_COUNT
        : TOOL88_MEMPOOL_BROWSER_MANUAL_MAX_COUNT;
      return Boolean(
        address &&
        (family === "btc" || family === "bitcoin" || this.isBtcAddress(address)) &&
        Number.isFinite(expectedCount) &&
        expectedCount > 0 &&
        expectedCount <= maxCount &&
        typeof fetch === "function"
      );
    },

    async fetchTool88ExternalJson(url, timeoutMs = TOOL88_MEMPOOL_BROWSER_REQUEST_TIMEOUT_MS) {
      const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      const timer = controller
        ? setTimeout(() => controller.abort(), Math.max(1000, Number(timeoutMs) || TOOL88_MEMPOOL_BROWSER_REQUEST_TIMEOUT_MS))
        : null;
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "omit",
          cache: "no-store",
          signal: controller?.signal,
        });
        if (!response.ok) {
          const preview = (await response.text()).slice(0, 300).replace(/\s+/g, " ").trim();
          throw new Error(`HTTP ${response.status}${preview ? `: ${preview}` : ""}`);
        }
        return response.json();
      } catch (error) {
        if (error?.name === "AbortError") throw new Error("request timed out");
        throw error;
      } finally {
        if (timer) clearTimeout(timer);
      }
    },

    buildMempoolSpaceProviderTransaction(tx = {}, selectedAddress = "", context = {}) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const vin = Array.isArray(tx?.vin) ? tx.vin : [];
      const vout = Array.isArray(tx?.vout) ? tx.vout : [];
      const btcInputs = vin.map((item, index) => {
        const prevout = item?.prevout && typeof item.prevout === "object" ? item.prevout : {};
        const value = Number(prevout.value);
        return {
          address: this.toText(prevout.scriptpubkey_address || prevout.address),
          value_satoshis: Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0,
          order: index,
          index,
          prev_txid: this.toText(item?.txid),
          prev_vout: Number.isFinite(Number(item?.vout)) ? Number(item.vout) : null,
          scriptpubkey: this.toText(prevout.scriptpubkey),
          scriptpubkey_type: this.toText(prevout.scriptpubkey_type),
        };
      });
      const btcOutputs = vout.map((item, index) => {
        const value = Number(item?.value);
        const scriptType = this.toText(item?.scriptpubkey_type).toLowerCase();
        const scriptAsm = this.toText(item?.scriptpubkey_asm).toUpperCase();
        const isOpReturn = scriptType === "op_return" || scriptAsm.startsWith("OP_RETURN");
        return {
          address: this.toText(item?.scriptpubkey_address || (isOpReturn ? "MESSAGE" : "")),
          value_satoshis: Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0,
          order: index,
          index,
          n: index,
          vout: index,
          scriptpubkey: this.toText(item?.scriptpubkey),
          scriptpubkey_asm: this.toText(item?.scriptpubkey_asm),
          scriptpubkey_type: this.toText(item?.scriptpubkey_type),
          is_op_return: isOpReturn,
          op_return: isOpReturn,
        };
      });
      const addressInputSatoshis = btcInputs.reduce((sum, item) =>
        this.normalizeAddressKey(item.address) === selectedKey ? sum + item.value_satoshis : sum, 0);
      const addressOutputSatoshis = btcOutputs.reduce((sum, item) =>
        this.normalizeAddressKey(item.address) === selectedKey ? sum + item.value_satoshis : sum, 0);
      const externalOutputSatoshis = btcOutputs.reduce((sum, item) =>
        this.normalizeAddressKey(item.address) !== selectedKey ? sum + item.value_satoshis : sum, 0);
      const netSatoshis = addressOutputSatoshis - addressInputSatoshis;
      const inputAddresses = [...new Set(btcInputs.map(item => item.address).filter(Boolean))];
      const outputAddresses = [...new Set(btcOutputs.filter(item => !item.is_op_return).map(item => item.address).filter(Boolean))];
      const status = tx?.status && typeof tx.status === "object" ? tx.status : {};
      const blockTime = Number(status.block_time);
      const blockHeight = Number(status.block_height);
      const fee = Number(tx?.fee);
      const direction = addressInputSatoshis > 0 && addressOutputSatoshis > 0
        ? "self"
        : addressInputSatoshis > 0
          ? "outgoing"
          : addressOutputSatoshis > 0
            ? "incoming"
            : "unknown";
      const txid = this.toText(tx?.txid || tx?.hash);
      return {
        id: txid,
        hash: txid,
        tx_hash: txid,
        transaction_hash: txid,
        transaction_kind: "btc_transaction",
        source_kind: "mempool_space_address_history",
        source: "mempool.space",
        provider: "mempool.space",
        network_family: "btc",
        chain_id: context.countContext?.chain_id || "BTC",
        chain_name: context.countContext?.chain_name || "Bitcoin",
        token: "BTC",
        token_symbol: "BTC",
        date: Number.isFinite(blockTime) && blockTime > 0 ? blockTime : "",
        timestamp: Number.isFinite(blockTime) && blockTime > 0 ? blockTime : "",
        block_number: Number.isFinite(blockHeight) ? blockHeight : 0,
        block_height: Number.isFinite(blockHeight) ? blockHeight : 0,
        confirmed: status.confirmed === true,
        direction,
        from_address: inputAddresses.slice(0, 8).join(", "),
        to_address: outputAddresses.slice(0, 8).join(", "),
        amount: Math.abs(netSatoshis) / 100000000,
        amount_formatted: (Math.abs(netSatoshis) / 100000000).toFixed(8),
        value_satoshis: Math.abs(netSatoshis),
        address_input_satoshis: addressInputSatoshis,
        address_output_satoshis: addressOutputSatoshis,
        external_output_satoshis: externalOutputSatoshis,
        net_satoshis: netSatoshis,
        fee_satoshis: Number.isFinite(fee) ? Math.max(0, Math.round(fee)) : 0,
        btc_input_count: btcInputs.length,
        btc_output_count: btcOutputs.length,
        btc_op_return_count: btcOutputs.filter(item => item.is_op_return).length,
        btc_inputs: btcInputs,
        btc_outputs: btcOutputs,
        raw_mempool_space_status: status,
      };
    },

    async fetchMempoolSpaceAddressHistoryFallback(context = {}, stalledJobId = "", reason = "server-job-no-progress") {
      const address = this.toText(context.selectedAddress || this.selectedTargetAddress);
      const expectedCount = this.getTracePollExpectedCount(context, {});
      if (!this.canUseMempoolSpaceBrowserFallback(context, {})) {
        throw new Error("the direct BTC fallback is not available for this address or transaction count");
      }

      const seenTxids = new Set();
      const rawTransactions = [];
      const appendRows = (rows = []) => {
        (Array.isArray(rows) ? rows : []).forEach((tx) => {
          const txid = this.toText(tx?.txid || tx?.hash).toLowerCase();
          if (!txid || seenTxids.has(txid)) return;
          seenTxids.add(txid);
          rawTransactions.push(tx);
        });
      };

      const encodedAddress = encodeURIComponent(address);
      let page = await this.fetchTool88ExternalJson(`${TOOL88_MEMPOOL_API_BASE_URL}/address/${encodedAddress}/txs`);
      if (!Array.isArray(page)) throw new Error("mempool.space returned an unexpected address-history response");
      appendRows(page);
      let requestsMade = 1;
      let pagesCompleted = 1;
      let confirmedPage = page.filter(tx => tx?.status?.confirmed === true);
      const maxPages = Math.max(1, Math.min(8, Math.ceil(Math.max(expectedCount, 25) / 25) + 1));

      while (
        rawTransactions.length < expectedCount &&
        confirmedPage.length >= 25 &&
        pagesCompleted < maxPages
      ) {
        const lastSeenTxid = this.toText(confirmedPage[confirmedPage.length - 1]?.txid);
        if (!lastSeenTxid) break;
        page = await this.fetchTool88ExternalJson(
          `${TOOL88_MEMPOOL_API_BASE_URL}/address/${encodedAddress}/txs/chain/${encodeURIComponent(lastSeenTxid)}`
        );
        requestsMade += 1;
        pagesCompleted += 1;
        if (!Array.isArray(page) || !page.length) break;
        appendRows(page);
        confirmedPage = page.filter(tx => tx?.status?.confirmed === true);
        if (page.length < 25) break;
      }

      if (expectedCount > 0 && rawTransactions.length === 0) {
        throw new Error(`mempool.space returned zero rows although the address count lookup reported ${expectedCount}`);
      }

      const transactions = rawTransactions
        .map(tx => this.buildMempoolSpaceProviderTransaction(tx, address, context))
        .filter(tx => tx.hash);
      const incomplete = expectedCount > 0 && transactions.length < expectedCount;
      const payload = {
        job_id: stalledJobId,
        status: "complete",
        trace_status: "complete",
        source: "mempool_space_browser_fallback",
        provider: "mempool.space",
        transactions,
        fetched_transaction_count: transactions.length,
        trace_fetch: {
          status: "complete",
          provider: "mempool.space",
          route: "btc_address_txs_browser_fallback",
          fetched_count: transactions.length,
          expected_count: expectedCount,
          requests_made: requestsMade,
          pages_completed: pagesCompleted,
          browser_fallback: true,
          fallback_reason: reason,
          stalled_job_id: stalledJobId,
          suspicious_pagination: incomplete,
          message: incomplete
            ? `Direct BTC fallback fetched ${transactions.length} of ${expectedCount} expected rows.`
            : `Direct BTC fallback fetched ${transactions.length} rows after the queued server job stopped making progress.`,
        },
      };
      this.backendJobMeta = {
        ...(this.backendJobMeta || {}),
        status: "complete",
        source: payload.source,
        stalled_job_id: stalledJobId,
        browser_fallback: true,
      };
      this.updateTraceProgressFromResult(payload, transactions.length);
      return payload;
    },

    async releaseTraceJobStorage(jobId, reason = "complete") {
      const cleanJobId = this.toText(jobId);
      if (!cleanJobId) return false;
      try {
        const response = await this.postTool88({
          action: "release_trace_job",
          job_id: cleanJobId,
          release_reason: reason,
          last_received_sequence: this.lastTraceBatchSequenceReceived || 0,
        });
        return Boolean((response?.result || response)?.released);
      } catch (error) {
        // Storage release is housekeeping. Never turn a successful trace into a
        // user-visible failure because cleanup could not be confirmed.
        console.warn(`Could not release Tool 88 trace job ${cleanJobId}.`, error);
        return false;
      }
    },

    async pollTraceJob(jobId, context = {}) {
      const collectedTransactions = [];
      const seenKeys = new Set();
      const startedAtMs = Date.now();
      const hardLimitMs = context.rawDataAutomation
        ? TOOL88_TRACE_EMBEDDED_HARD_LIMIT_MS
        : TOOL88_TRACE_MANUAL_HARD_LIMIT_MS;
      let lastReceivedSequence = 0;
      let inferredCompletePolls = 0;
      let browserFallbackAttempted = false;
      let browserFallbackError = "";

      const appendBatch = (batch) => {
        const rows = Array.isArray(batch) ? batch : [];
        let appended = 0;
        rows.forEach((tx) => {
          const key = this.getProviderTransactionUniqueKey(tx);
          if (key && seenKeys.has(key)) return;
          if (key) seenKeys.add(key);
          collectedTransactions.push(tx);
          appended += 1;
        });
        return appended;
      };

      const tryBrowserFallback = async (reason, result = {}) => {
        if (browserFallbackAttempted || !this.canUseMempoolSpaceBrowserFallback(context, result)) return null;
        browserFallbackAttempted = true;
        this.statusMessage = `Server trace job ${jobId} has not returned usable BTC rows. Trying direct mempool.space history...`;
        try {
          const fallbackPayload = await this.fetchMempoolSpaceAddressHistoryFallback(context, jobId, reason);
          appendBatch(fallbackPayload.transactions);
          return {
            ...fallbackPayload,
            transactions: collectedTransactions,
            fetched_transaction_count: collectedTransactions.length,
            trace_fetch: {
              ...(fallbackPayload.trace_fetch || {}),
              fetched_count: collectedTransactions.length,
            },
          };
        } catch (error) {
          browserFallbackError = error?.message || "unknown direct fallback error";
          this.statusMessage = `Direct mempool.space fallback did not complete (${browserFallbackError}). The queued trace job is still being checked.`;
          return null;
        }
      };

      for (let attempt = 1; attempt <= TOOL88_TRACE_POLL_MAX_ATTEMPTS; attempt += 1) {
        if (this.traceFetchStopRequested) {
          await this.releaseTraceJobStorage(jobId, "cancelled-before-poll");
          return this.buildTraceFetchCancelledPayload(jobId, collectedTransactions);
        }
        this.statusMessage = `Fetching trace history ${jobId} (${this.formatInteger(collectedTransactions.length)} rows received)...`;
        await this.sleep(attempt === 1 ? 350 : TOOL88_TRACE_POLL_DELAY_MS);
        if (this.traceFetchStopRequested) {
          await this.releaseTraceJobStorage(jobId, "cancelled-before-poll");
          return this.buildTraceFetchCancelledPayload(jobId, collectedTransactions);
        }

        let status;
        try {
          status = await this.postTool88({
            action: "job_status",
            job_id: jobId,
            last_received_sequence: lastReceivedSequence,
          });
        } catch (error) {
          const fallback = await tryBrowserFallback("server-job-status-error", {});
          if (fallback) {
            await this.releaseTraceJobStorage(jobId, "browser-fallback-after-status-error");
            return fallback;
          }
          await this.releaseTraceJobStorage(jobId, "poll-request-error");
          throw error;
        }

        const result = status.result || status;
        const batchSequence = Number(result.batch_sequence || result.batchSequence || 0);
        const batch = Array.isArray(result.batch_transactions)
          ? result.batch_transactions
          : Array.isArray(result.transactions_batch)
            ? result.transactions_batch
            : Array.isArray(result.transactions_chunk)
              ? result.transactions_chunk
              : Array.isArray(result.transactionsBatch)
                ? result.transactionsBatch
                : Array.isArray(result.transactionsChunk)
                  ? result.transactionsChunk
                  : Array.isArray(result.transactions)
                    ? result.transactions
                    : [];

        if (batch.length && (!batchSequence || batchSequence > lastReceivedSequence || result.replayed_batch)) {
          appendBatch(batch);
          if (batchSequence) {
            lastReceivedSequence = Math.max(lastReceivedSequence, batchSequence);
            this.lastTraceBatchSequenceReceived = lastReceivedSequence;
          }
        }

        this.updateTraceProgressFromResult(result, collectedTransactions.length);
        this.traceInspectorRows = this.buildTraceInspectorRows(collectedTransactions, this.selectedEntry, this.selectedTargetAddress);

        if (this.caseBuddyEmbedded && this.caseBuddyIntegrationRunState?.stopRequested) {
          this.requestTraceFetchStop(this.caseBuddyIntegrationRunState.detail || "Case Buddy requested that Tool 88 stop this trace preview.");
          await this.releaseTraceJobStorage(jobId, "case-buddy-cancelled");
          return this.buildTraceFetchCancelledPayload(jobId, collectedTransactions);
        }

        if (this.traceFetchStopRequested || this.traceFetchCancelledByUser(result)) {
          await this.releaseTraceJobStorage(jobId, "cancelled");
          return this.buildTraceFetchCancelledPayload(jobId, collectedTransactions);
        }

        const failed = result.status === "failed" || result.trace_status === "failed" || result.trace_fetch?.status === "failed";
        if (failed) {
          const fallback = await tryBrowserFallback("server-job-failed", result);
          if (fallback) {
            await this.releaseTraceJobStorage(jobId, "browser-fallback-after-failure");
            return fallback;
          }
          await this.releaseTraceJobStorage(jobId, "failed");
          throw new Error(result.message || result.error_message || result.trace_fetch?.message || `Trace job ${jobId} failed.`);
        }

        if (result.status === "complete" || result.trace_status === "complete" || result.trace_fetch?.status === "complete") {
          const completedPayload = {
            ...result,
            job_id: jobId,
            transactions: collectedTransactions,
            fetched_transaction_count: collectedTransactions.length,
          };
          await this.releaseTraceJobStorage(jobId, "complete");
          return completedPayload;
        }

        const expectedCount = this.getTracePollExpectedCount(context, result);
        if (expectedCount > 0 && collectedTransactions.length >= expectedCount) {
          inferredCompletePolls += 1;
          if (inferredCompletePolls >= TOOL88_TRACE_INFER_COMPLETE_POLLS) {
            const payload = {
              ...result,
              job_id: jobId,
              status: "complete",
              trace_status: "complete",
              transactions: collectedTransactions,
              fetched_transaction_count: collectedTransactions.length,
              trace_fetch: {
                ...(result.trace_fetch || {}),
                status: "complete",
                fetched_count: collectedTransactions.length,
                expected_count: expectedCount,
                completion_inferred_from_expected_count: true,
                message: `Tool 88 received all ${expectedCount} expected rows; completion was inferred because the server job did not close itself.`,
              },
            };
            this.updateTraceProgressFromResult(payload, collectedTransactions.length);
            await this.releaseTraceJobStorage(jobId, "completion-inferred");
            return payload;
          }
        } else {
          inferredCompletePolls = 0;
        }

        const elapsedMs = Date.now() - startedAtMs;
        if (elapsedMs >= TOOL88_TRACE_BROWSER_FALLBACK_AFTER_MS && collectedTransactions.length === 0) {
          const fallback = await tryBrowserFallback("server-job-zero-row-stall", result);
          if (fallback) {
            await this.releaseTraceJobStorage(jobId, "browser-fallback-after-zero-row-stall");
            return fallback;
          }
        }

        if (elapsedMs >= hardLimitMs) {
          const fallback = await tryBrowserFallback("server-job-hard-timeout", result);
          if (fallback) {
            await this.releaseTraceJobStorage(jobId, "browser-fallback-after-timeout");
            return fallback;
          }
          await this.releaseTraceJobStorage(jobId, "hard-timeout");
          const fallbackDetail = browserFallbackError ? ` Direct BTC fallback also failed: ${browserFallbackError}.` : "";
          throw new Error(
            `Trace job ${jobId} did not complete within ${Math.round(hardLimitMs / 1000)} seconds and returned ${collectedTransactions.length} row${collectedTransactions.length === 1 ? "" : "s"}.${fallbackDetail}`
          );
        }

        const retryAfter = Number(result.trace_fetch?.retry_after_ms || result.retry_after_ms || result.next_poll_ms || 0);
        if (retryAfter > TOOL88_TRACE_POLL_DELAY_MS) {
          await this.sleep(Math.min(retryAfter, 10000));
          if (this.traceFetchStopRequested) {
            await this.releaseTraceJobStorage(jobId, "cancelled-during-retry-wait");
            return this.buildTraceFetchCancelledPayload(jobId, collectedTransactions);
          }
        }
      }

      await this.releaseTraceJobStorage(jobId, "polling-limit");
      const fallbackDetail = browserFallbackError ? ` Direct BTC fallback also failed: ${browserFallbackError}.` : "";
      throw new Error(`Trace job ${jobId} did not complete before the polling limit.${fallbackDetail}`);
    },

    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    resetTraceFetchUi(entry = null) {
      this.traceFetchProgress = null;
      this.traceInspectorRows = [];
      this.traceInspectorFilter = "all";
      this.traceInspectorDisplayLimit = 250;
      this.traceCopiedHash = "";
      this.traceTransactionOfInterestHash = this.toText(entry?.txHash || "");
      this.lastTraceBatchSequenceReceived = 0;
      this.lastTraceProgressEventKey = "";
      this.lastTraceProgressEventAtMs = 0;
      this.traceFetchStopRequested = false;
      this.traceFetchStopMessage = "";
    },

    updateTraceProgressFromResult(result = {}, fallbackFetchedCount = 0) {
      const traceFetch = result.trace_fetch && typeof result.trace_fetch === "object"
        ? result.trace_fetch
        : {};
      const fetched = Number(
        traceFetch.fetched_count ??
          result.fetched_count ??
          result.fetched_transaction_count ??
          fallbackFetchedCount
      );
      const expected = Number(
        traceFetch.expected_count ??
          result.expected_count ??
          result.expected_transaction_count ??
          this.traceFetchExpectedCount
      );
      this.traceFetchProgress = {
        ...result,
        trace_fetch: {
          ...traceFetch,
          fetched_count: Number.isFinite(fetched) ? Math.max(0, Math.trunc(fetched)) : fallbackFetchedCount,
          expected_count: Number.isFinite(expected) ? Math.trunc(expected) : -1,
          status: traceFetch.status || result.trace_status || result.status || "running",
        },
      };
      if (this.caseBuddyEmbedded) {
        const progress = this.traceFetchProgress.trace_fetch;
        const progressEventKey = [
          progress.status || "",
          progress.fetched_count ?? 0,
          progress.expected_count ?? -1,
          result.batch_sequence ?? result.batchSequence ?? "",
          progress.pages_completed ?? result.pages_completed ?? "",
          progress.requests_made ?? result.requests_made ?? "",
          progress.last_http_code ?? result.last_http_code ?? "",
          progress.browser_fallback ? "browser-fallback" : "",
        ].join("|");
        const now = Date.now();
        const terminalStatus = ["complete", "completed", "failed", "cancelled", "canceled"].includes(this.toText(progress.status).toLowerCase());
        const shouldEmit = terminalStatus ||
          progressEventKey !== this.lastTraceProgressEventKey ||
          now - Number(this.lastTraceProgressEventAtMs || 0) >= 10000;
        if (shouldEmit) {
          this.lastTraceProgressEventKey = progressEventKey;
          this.lastTraceProgressEventAtMs = now;
          const progressSnapshot = this.buildCaseBuddyHopSolverSnapshot({
            includeRows: false,
            status: "trace-fetch-progress",
            integrationAction: "trace-fetch-progress",
            message: `Tool 88 fetched ${this.formatInteger(progress.fetched_count || 0)} trace row${Number(progress.fetched_count || 0) === 1 ? "" : "s"}.`,
          });
          this.emitCaseBuddyIntegrationEvent("case-buddy-progress", {
            ...progressSnapshot,
            traceFetchProgress: this.traceFetchProgress,
            trace_fetch: progress,
          });
        }
      }
    },

    normalizeHashKey(value) {
      return this.toText(value).toLowerCase();
    },

    isTool88LikelyTransactionHash(value) {
      const text = this.toText(value).trim().toLowerCase();
      if (!text) return false;
      return /^0x[0-9a-f]{64}$/u.test(text)
        || /^[0-9a-f]{64}$/u.test(text)
        || /^0x[0-9a-f]+\.\.\.[0-9a-f]+$/u.test(text)
        || /^[0-9a-f]+\.\.\.[0-9a-f]+$/u.test(text);
    },

    shortenMiddle(value, start = 8, end = 6) {
      const text = this.toText(value);
      if (!text) {
        return "";
      }
      if (text.length <= start + end + 3) {
        return text;
      }
      return `${text.slice(0, start)}…${text.slice(-end)}`;
    },

    getProviderTransactionUniqueKey(tx = {}) {
      const direct = this.toText(
        tx.unique_transfer_key ||
          tx.uniqueTransferKey ||
          tx.transfer_id ||
          tx.id
      );
      if (direct) {
        return direct;
      }

      const hash = this.normalizeHashKey(tx.hash || tx.transaction_hash || tx.tx_hash || tx.transaction_id);
      const logIndex = this.toText(tx.logIndex || tx.log_index || tx.traceId || tx.trace_id || tx.transactionIndex || tx.transaction_index || tx.event_index || tx.eventIndex);
      const internalFlag = this.toText(tx.is_internal || tx.internal || tx.transaction_kind || tx.type);
      const tokenInfo = tx.tokenInfo || tx.token_info || {};
      const contract = this.normalizeAddressKey(
        tx.contractAddress ||
          tx.contract_address ||
          tx.token_contract ||
          tx.address ||
          tokenInfo.tokenId ||
          tokenInfo.address ||
          tokenInfo.contract_address
      );
      const from = this.normalizeAddressKey(tx.from_address || tx.from || tx.sender || tx.from_address_base58);
      const to = this.normalizeAddressKey(tx.to_address || tx.to || tx.receiver || tx.to_address_base58);
      const value = this.toText(tx.value || tx.quant || tx.amount || tx.quantity);
      return [hash, logIndex, internalFlag, contract, from, to, value].filter(Boolean).join("|");
    },

    parseProviderDate(value) {
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value;
      }

      const text = this.toText(value);
      if (!text) {
        return null;
      }

      if (/^\d+$/.test(text)) {
        const raw = Number(text);
        if (!Number.isFinite(raw)) {
          return null;
        }
        const milliseconds = raw > 100000000000 ? raw : raw * 1000;
        const date = new Date(milliseconds);
        return Number.isNaN(date.getTime()) ? null : date;
      }

      return this.parseUtcDate(text);
    },

    buildTraceInspectorRows(transactions = [], entry = null, selectedAddress = "") {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const interestHash = this.normalizeHashKey(entry?.txHash || this.traceTransactionOfInterestHash);
      const normalized = (Array.isArray(transactions) ? transactions : [])
        .map((tx) => this.normalizeProviderTransaction(tx))
        .filter((tx) => tx.hash || tx.fromAddress || tx.toAddress);

      normalized.sort((left, right) => {
        const leftTime = left.date ? left.date.getTime() : 0;
        const rightTime = right.date ? right.date.getTime() : 0;
        if (leftTime !== rightTime) {
          return leftTime - rightTime;
        }
        const leftBlock = Number(left.raw?.blockNumber ?? left.raw?.block_number ?? left.blockNumber ?? 0);
        const rightBlock = Number(right.raw?.blockNumber ?? right.raw?.block_number ?? right.blockNumber ?? 0);
        if (Number.isFinite(leftBlock) && Number.isFinite(rightBlock) && leftBlock !== rightBlock) return leftBlock - rightBlock;
        const leftIndex = Number(left.raw?.transactionIndex ?? left.raw?.transaction_index ?? left.raw?.logIndex ?? left.raw?.log_index ?? 0);
        const rightIndex = Number(right.raw?.transactionIndex ?? right.raw?.transaction_index ?? right.raw?.logIndex ?? right.raw?.log_index ?? 0);
        if (Number.isFinite(leftIndex) && Number.isFinite(rightIndex) && leftIndex !== rightIndex) return leftIndex - rightIndex;
        return String(left.uniqueKey).localeCompare(String(right.uniqueKey));
      });

      const accountContext = entry && selectedAddress && !this.isBtcHopContext(entry, selectedAddress, normalized)
        ? this.buildAccountFifoContext(entry, selectedAddress)
        : null;
      let runningAssetBalance = 0;

      return normalized.map((tx, index) => {
        let direction = tx.direction || "other";
        const fromMatches = this.normalizeAddressKey(tx.fromAddress) === selectedKey;
        const toMatches = this.normalizeAddressKey(tx.toAddress) === selectedKey;
        if (!tx.direction || tx.direction === "unknown") {
          if (fromMatches && toMatches) direction = "self";
          else if (fromMatches) direction = "outgoing";
          else if (toMatches) direction = "incoming";
        }

        const balanceImpact = this.computeTraceInspectorBalanceImpact(tx, direction, accountContext);
        const balanceBefore = runningAssetBalance;
        let balanceAfter = runningAssetBalance;
        if (balanceImpact.assetMatchedForBalance) {
          balanceAfter = balanceBefore + balanceImpact.delta;
          runningAssetBalance = balanceAfter;
        }

        const isTransactionOfInterest = Boolean(interestHash && this.normalizeHashKey(tx.hash) === interestHash);
        const dateText = tx.date ? this.formatUtcDate(tx.date) : tx.dateText;
        const btcInputCount = Number(tx.btcInputCount || tx.raw?.btc_input_count || 0);
        const btcOutputCount = Number(tx.btcOutputCount || tx.raw?.btc_output_count || 0);
        const btcLike = this.toText(tx.transactionKind || tx.raw?.transaction_kind).toLowerCase().includes("btc") || btcInputCount > 0 || btcOutputCount > 0;
        const fromDisplay = btcLike && btcInputCount > 1 ? `${this.formatInteger(btcInputCount)} inputs` : this.shortenMiddle(tx.fromAddress, 8, 6);
        const toDisplay = btcLike && btcOutputCount > 1 ? `${this.formatInteger(btcOutputCount)} outputs` : this.shortenMiddle(tx.toAddress, 8, 6);
        const fromTitle = btcLike ? (tx.btcInputSummary || tx.fromAddress) : tx.fromAddress;
        const toTitle = btcLike ? (tx.btcOutputSummary || tx.toAddress) : tx.toAddress;
        const statusDisplay = tx.failedOrReverted
          ? "Failed / reverted"
          : tx.nonBalanceAffecting
            ? "Non-balance"
            : tx.tokenRegistryStatus === "warn"
              ? "Token contract review"
              : btcLike && (tx.btcOpReturnCount || tx.opReturnCount)
                ? `Success; ${this.formatInteger(tx.btcOpReturnCount || tx.opReturnCount)} OP_RETURN`
                : "Success";

        return {
          ...tx,
          rowNumber: index + 1,
          direction,
          dateText,
          isTransactionOfInterest,
          kindLabel: this.formatTraceRowKind(tx),
          shortHash: this.shortenMiddle(tx.hash, 10, 8),
          shortFrom: this.shortenMiddle(tx.fromAddress, 8, 6),
          shortTo: this.shortenMiddle(tx.toAddress, 8, 6),
          fromDisplay,
          toDisplay,
          fromTitle,
          toTitle,
          statusDisplay,
          tokenAuthenticity: tx.tokenAuthenticity,
          tokenCheckLabel: tx.tokenAuthenticity?.label || tx.tokenRegistryTitle || (tx.tokenRegistryStatus ? this.toText(tx.tokenRegistryStatus).toUpperCase() : "No contract"),
          tokenCheckDetail: tx.tokenAuthenticity?.detail || tx.tokenRegistrySummary || "",
          tokenCheckStatusClass: this.formatTokenCheckStatusClass(tx.tokenAuthenticity || { status: tx.tokenRegistryStatus }),
          hasTokenIssue: Boolean(tx.tokenAuthenticity?.hasIssue || tx.tokenRegistryStatus === "warn"),
          assetMatchedForBalance: balanceImpact.assetMatchedForBalance,
          assetBalanceBefore: balanceImpact.assetMatchedForBalance ? balanceBefore : null,
          assetBalanceAfter: balanceImpact.assetMatchedForBalance ? balanceAfter : null,
          assetBalanceDelta: balanceImpact.assetMatchedForBalance ? balanceImpact.delta : null,
          balanceToken: balanceImpact.token || accountContext?.token || tx.token,
          balanceRoleLabel: balanceImpact.roleLabel,
        };
      });
    },

    computeTraceInspectorBalanceImpact(tx = {}, direction = "", accountContext = null) {
      if (!accountContext || !tx || tx.failedOrReverted || tx.nonBalanceAffecting || tx.analysisEligible === false) {
        return { assetMatchedForBalance: false, delta: 0, token: accountContext?.token || tx?.token || "", roleLabel: "Ignored" };
      }
      if (!this.accountTransactionMatchesAsset(tx, accountContext)) {
        return { assetMatchedForBalance: false, delta: 0, token: accountContext.token, roleLabel: "Other asset" };
      }

      const amount = Math.max(0, Number(this.getAccountTraceAmount(tx)) || 0);
      const feeAmount = Math.max(0, Number(this.getAccountTraceFeeAmount(tx, accountContext)) || 0);
      let delta = 0;
      let roleLabel = "Balance row";

      if (direction === "incoming") {
        delta = amount;
        roleLabel = "Incoming balance";
      } else if (direction === "outgoing") {
        delta = -amount - (accountContext.isNative ? feeAmount : 0);
        roleLabel = "Outgoing balance";
      } else if (direction === "self") {
        delta = accountContext.isNative ? -feeAmount : 0;
        roleLabel = accountContext.isNative && feeAmount > 0 ? "Self / fee" : "Self / net zero";
      } else {
        roleLabel = "Matched asset";
      }

      return {
        assetMatchedForBalance: amount > 0 || feeAmount > 0,
        delta,
        token: accountContext.token || tx.token,
        roleLabel,
      };
    },

    formatTraceRowKind(row = {}) {
      const kind = this.toText(row.transactionKind || row.raw?.transaction_kind || row.raw?.source_kind || row.raw?.type).toLowerCase();
      const btcLike = kind.includes("btc") || kind.includes("utxo") || (Array.isArray(row.btcInputs) && row.btcInputs.length) || (Array.isArray(row.raw?.btc_inputs) && row.raw.btc_inputs.length);
      if (btcLike) {
        const inputCount = Number(row.btcInputCount || row.raw?.btc_input_count || row.raw?.input_count || 0);
        const outputCount = Number(row.btcOutputCount || row.raw?.btc_output_count || row.raw?.output_count || 0);
        if (inputCount || outputCount) {
          return `BTC ${this.formatInteger(inputCount)} in / ${this.formatInteger(outputCount)} out`;
        }
        return "BTC / UTXO";
      }
      if (row.isInternal || kind.includes("internal")) return "Internal";
      if (kind.includes("native_trx")) return "Native TRX";
      if (kind.includes("native")) return "Native";
      if (kind.includes("trc20")) return "TRC20";
      if (kind.includes("erc20") || row.raw?.contractAddress || row.raw?.contract_address) return "Token";
      if (row.nonBalanceAffecting) return "Contract / non-balance";
      return "Transfer";
    },

    formatTraceInspectorDetails(row = {}) {
      const parts = [];
      const inputCount = Number(row.inputCount ?? row.raw?.input_count ?? row.raw?.inputs_count ?? -1);
      const outputCount = Number(row.outputCount ?? row.raw?.output_count ?? row.raw?.outputs_count ?? -1);
      if (Number.isFinite(inputCount) && inputCount >= 0) {
        parts.push(`${this.formatInteger(inputCount)} in`);
      }
      if (Number.isFinite(outputCount) && outputCount >= 0) {
        parts.push(`${this.formatInteger(outputCount)} out`);
      }
      const netSats = Number(row.netSatoshis ?? row.raw?.net_satoshis ?? NaN);
      if (Number.isFinite(netSats) && this.toText(row.transactionKind).toLowerCase().includes("btc")) {
        const sign = netSats > 0 ? "+" : "";
        parts.push(`net ${sign}${this.formatBtcFromSatoshis(netSats)}`);
      }
      const opReturnCount = Number(row.opReturnCount ?? row.raw?.op_return_count ?? 0);
      if (Number.isFinite(opReturnCount) && opReturnCount > 0) {
        parts.push(`${this.formatInteger(opReturnCount)} OP_RETURN`);
      }
      if (row.multipleExitOutputs || row.raw?.multiple_exit_outputs) {
        parts.push("multiple possible exits");
      }
      if (row.tokenRegistryStatus === "warn") {
        parts.push("token contract not trusted");
      } else if (row.tokenRegistryStatus === "ok") {
        parts.push("trusted token contract");
      }
      if (row.tokenContractAddress) {
        parts.push(`contract ${this.shortenMiddle(row.tokenContractAddress, 8, 6)}`);
      }
      if (row.hasTokenIssue && row.tokenRegistrySummary) {
        parts.push(row.tokenRegistrySummary);
      }
      if (row.analysisDetails) {
        parts.push(row.analysisDetails);
      }
      return parts.length ? parts.join(" · ") : "—";
    },

    formatBtcFromSatoshis(value) {
      const sats = Number(value);
      if (!Number.isFinite(sats)) {
        return "0 BTC";
      }
      return `${(sats / 100000000).toFixed(8).replace(/\.?0+$/, "")} BTC`;
    },

    formatSignedFunds(value, token) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return "—";
      }
      const sign = number > 0 ? "+" : "";
      return `${sign}${this.formatFunds(number, token)}`;
    },

    formatTraceInspectorBalanceDelta(row = {}) {
      if (!row || !row.assetMatchedForBalance || !Number.isFinite(Number(row.assetBalanceDelta))) {
        return "—";
      }
      return this.formatSignedFunds(Number(row.assetBalanceDelta), row.balanceToken || row.token);
    },

    formatTraceInspectorBalanceAfter(row = {}) {
      if (!row || !row.assetMatchedForBalance || !Number.isFinite(Number(row.assetBalanceAfter))) {
        return "—";
      }
      return this.formatFunds(Number(row.assetBalanceAfter), row.balanceToken || row.token);
    },

    formatTraceInspectorDirection(direction) {
      const value = this.toText(direction).toLowerCase();
      if (value === "incoming") return "Incoming";
      if (value === "outgoing") return "Outgoing";
      if (value === "self") return "Self";
      if (value === "other") return "Other";
      return value ? value.replace(/_/g, " ") : "Unknown";
    },

    formatTraceInspectorAmount(row = {}) {
      if (row.amountText) {
        return `${row.amountText}${row.token ? ` ${row.token}` : ""}`.trim();
      }
      if (Number.isFinite(Number(row.amount))) {
        return this.formatFunds(Number(row.amount), row.token);
      }
      return row.token ? `Unknown ${row.token}` : "Unknown";
    },

    showMoreTraceRows() {
      this.traceInspectorDisplayLimit += 250;
    },

    focusTraceTransactionOfInterest() {
      if (!this.traceInputTransactionFound) {
        return;
      }
      this.traceInspectorFilter = "all";
      this.$nextTick(() => {
        const element = document.getElementById("tool88-trace-interest-row");
        if (element && typeof element.scrollIntoView === "function") {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },

    async copyBridgeValue(value) {
      const text = this.toText(value);
      if (!text) {
        return;
      }
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const temp = document.createElement("textarea");
          temp.value = text;
          temp.setAttribute("readonly", "readonly");
          temp.style.position = "absolute";
          temp.style.left = "-9999px";
          document.body.appendChild(temp);
          temp.select();
          document.execCommand("copy");
          document.body.removeChild(temp);
        }
        this.bridgeCopiedValue = this.shortenMiddle(text, 14, 12);
        window.setTimeout(() => {
          if (this.bridgeCopiedValue === this.shortenMiddle(text, 14, 12)) {
            this.bridgeCopiedValue = "";
          }
        }, 2000);
      } catch (error) {
        this.warnings.push(`Could not copy bridge value: ${error?.message || "browser denied clipboard access"}.`);
      }
    },

    async copyTraceHash(hash) {
      const text = this.toText(hash);
      if (!text) {
        return;
      }
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const temp = document.createElement("textarea");
          temp.value = text;
          temp.setAttribute("readonly", "readonly");
          temp.style.position = "absolute";
          temp.style.left = "-9999px";
          document.body.appendChild(temp);
          temp.select();
          document.execCommand("copy");
          document.body.removeChild(temp);
        }
        this.traceCopiedHash = this.shortenMiddle(text, 10, 8);
        window.setTimeout(() => {
          if (this.traceCopiedHash === this.shortenMiddle(text, 10, 8)) {
            this.traceCopiedHash = "";
          }
        }, 2000);
      } catch (error) {
        this.warnings.push(`Could not copy transaction hash: ${error?.message || "browser denied clipboard access"}.`);
      }
    },

    serializeEntryContext(entry) {
      if (!entry) {
        return null;
      }

      return {
        tx_hash: entry.txHash,
        path_label: entry.pathLabel,
        start_row_number: entry.startRowIndex + 1,
        end_row_number: entry.endRowIndex + 1,
        date: entry.dateText,
        chain_id: entry.chainId,
        chain_name: entry.chainName,
        rows: entry.rows.map(({ rowIndex, row }) => ({
          row_number: rowIndex + 1,
          row,
        })),
      };
    },

    buildMockTransactions(entry, selectedAddress) {
      const entryDate = this.getEntryDate(entry) || new Date();
      const fundsIn = this.deriveFundsIn(entry, selectedAddress);
      const token = fundsIn.token || "TOKEN";
      const amountIn = Number.isFinite(fundsIn.amount) ? fundsIn.amount : 1;
      const exitAmount = Math.max(0, amountIn * 0.75);
      const addressTail = this.toText(selectedAddress).replace(/[^A-Za-z0-9]/g, "").slice(-8) || "address";
      const chainId = entry?.chainId || "1";
      const chainName = entry?.chainName || "Ethereum";

      if (/dormant|noexit/i.test(selectedAddress)) {
        return [];
      }

      if (this.traceDirection === "reverse") {
        return [
          {
            hash: `mock-prior-${addressTail}`,
            date: this.formatUtcDate(this.addHours(entryDate, -2)),
            from_address: "0xprior000000000000000000000000000000000000",
            to_address: selectedAddress,
            amount: exitAmount,
            token,
            chain_id: chainId,
            chain_name: chainName,
            direction: "incoming",
          },
        ];
      }

      return [
        {
          hash: `mock-next-${addressTail}`,
          date: this.formatUtcDate(this.addHours(entryDate, 2)),
          from_address: selectedAddress,
          to_address: "0xexit000000000000000000000000000000000000",
          amount: exitAmount,
          token,
          chain_id: chainId,
          chain_name: chainName,
          direction: "outgoing",
        },
      ];
    },

    providerTransactionFailedOrReverted(tx = {}) {
      const raw = tx?.raw && typeof tx.raw === "object" ? tx.raw : {};
      const statusText = this.toText(
        tx?.status ??
          tx?.tx_status ??
          tx?.transaction_status ??
          raw.status ??
          raw.tx_status ??
          raw.transaction_status ??
          ""
      ).trim().toLowerCase();
      const executionStatus = this.toText(
        tx?.execution_status ??
          tx?.executionStatus ??
          raw.execution_status ??
          raw.executionStatus ??
          ""
      ).trim().toLowerCase();
      const receiptStatus = this.toText(
        tx?.txreceipt_status ??
          tx?.receipt_status ??
          tx?.receiptStatus ??
          raw.txreceipt_status ??
          raw.receipt_status ??
          raw.receiptStatus ??
          ""
      ).trim().toLowerCase();
      const contractRet = this.toText(
        tx?.contractRet ??
          tx?.contract_ret ??
          raw.contractRet ??
          raw.contract_ret ??
          ""
      ).trim().toUpperCase();
      const errorCode = this.toText(
        tx?.errCode ??
          tx?.err_code ??
          tx?.errorCode ??
          tx?.error_code ??
          raw.errCode ??
          raw.err_code ??
          raw.errorCode ??
          raw.error_code ??
          ""
      ).trim().toLowerCase();
      const isErrorRaw = this.toText(
        tx?.is_error ??
          tx?.isError ??
          tx?.is_error_tx ??
          raw.is_error ??
          raw.isError ??
          raw.is_error_tx ??
          ""
      ).trim().toLowerCase();
      const explicitFailed = Boolean(
        tx?.failed_or_reverted === true ||
          tx?.failedOrReverted === true ||
          raw.failed_or_reverted === true ||
          raw.failedOrReverted === true
      );
      const isErrorFailed = ["1", "true", "yes", "y", "failed", "reverted", "error"].includes(isErrorRaw);
      const statusFailed = ["0", "failed", "failure", "reverted", "error", "execution reverted", "failed_or_reverted", "failed-or-reverted"].includes(statusText);
      const executionFailed = /failed|revert|error/u.test(executionStatus);
      const receiptFailed = receiptStatus === "0" || /failed|revert|error/u.test(receiptStatus);
      const contractFailed = Boolean(contractRet && !["SUCCESS", "SUCESS", "OK"].includes(contractRet));
      const errorCodeFailed = Boolean(errorCode && !["0", "none", "ok", "success"].includes(errorCode));
      return Boolean(explicitFailed || isErrorFailed || statusFailed || executionFailed || receiptFailed || contractFailed || errorCodeFailed);
    },

    normalizeProviderTransaction(tx) {
      const hash = this.toText(tx?.hash || tx?.transaction_hash || tx?.tx_hash || tx?.transaction_id);
      const rawDateValue = tx?.date ?? tx?.datetime ?? tx?.timestamp ?? tx?.time ?? tx?.timeStamp ?? tx?.block_ts ?? tx?.blockTs ?? tx?.block_timestamp ?? tx?.blockTimestamp;
      const date = this.parseProviderDate(rawDateValue);
      const dateText = date ? this.formatUtcDate(date) : this.toText(rawDateValue);
      const amountCandidate = tx?.amount_formatted ?? tx?.amount ?? tx?.net_amount ?? tx?.value_formatted ?? tx?.quantity ?? tx?.quant ?? tx?.value;
      const amount = this.parseNumberLike(amountCandidate);
      const transactionKindHint = this.toText(tx?.transaction_kind || tx?.transactionKind || tx?.type || "").toLowerCase();
      const token = this.toText(tx?.token || tx?.token_symbol || tx?.tokenSymbol || tx?.symbol || tx?.asset || tx?.tokenInfo?.tokenAbbr || tx?.token_info?.symbol || (transactionKindHint.includes("btc") ? "BTC" : ""));
      const btcInputs = Array.isArray(tx?.btc_inputs) ? tx.btc_inputs : (Array.isArray(tx?.inputs) && transactionKindHint.includes("btc") ? tx.inputs : []);
      const btcOutputs = Array.isArray(tx?.btc_outputs) ? tx.btc_outputs : (Array.isArray(tx?.outputs) && transactionKindHint.includes("btc") ? tx.outputs : []);
      const btcInputAddresses = btcInputs.map((item) => this.toText(item?.address || item?.coin?.address)).filter(Boolean);
      const btcOutputAddresses = btcOutputs.map((item) => this.toText(item?.address || (item?.is_op_return ? "OP_RETURN" : ""))).filter(Boolean);
      let fromAddress = this.toText(tx?.from_address || tx?.from || tx?.sender);
      let toAddress = this.toText(tx?.to_address || tx?.to || tx?.receiver);
      if (!fromAddress && btcInputAddresses.length) {
        fromAddress = btcInputAddresses.slice(0, 3).join(", ");
      }
      if (!toAddress && btcOutputAddresses.length) {
        toAddress = btcOutputAddresses.slice(0, 3).join(", ");
      }
      const inputCount = Number(tx?.btc_input_count ?? tx?.input_count ?? tx?.inputs_count ?? btcInputs.length);
      const outputCount = Number(tx?.btc_output_count ?? tx?.output_count ?? tx?.outputs_count ?? btcOutputs.length);
      const chainId = this.toText(tx?.chain_id || tx?.chainId);
      const chainName = this.toText(tx?.chain_name || tx?.chainName || tx?.network);
      const contractAddress = this.toText(
        tx?.contractAddress ||
          tx?.contract_address ||
          tx?.token_contract ||
          tx?.address ||
          tx?.tokenInfo?.tokenId ||
          tx?.token_info?.address ||
          tx?.token_info?.tokenId
      );
      const direction = this.toText(tx?.direction || "").toLowerCase() || "unknown";
      const executionStatus = this.toText(tx?.execution_status ?? tx?.executionStatus ?? tx?.raw?.execution_status ?? tx?.raw?.executionStatus).toLowerCase();
      const failedOrReverted = this.providerTransactionFailedOrReverted(tx);
      const isInternal = Boolean(tx?.is_internal === true || tx?.isInternal === true || this.toText(tx?.transaction_kind).toLowerCase().includes("internal"));
      const transactionKind = this.toText(tx?.transaction_kind || tx?.transactionKind || tx?.type || "");
      const nonBalanceAffecting = Boolean(
        tx?.is_non_balance_affecting === true ||
          tx?.is_non_balance_affecting === 1 ||
          tx?.is_non_balance_affecting === "1" ||
          tx?.non_balance_affecting === true ||
          tx?.non_balance_affecting === 1 ||
          tx?.non_balance_affecting === "1" ||
          tx?.nonBalanceAffecting === true ||
          tx?.balance_affecting === false ||
          tx?.balanceAffecting === false ||
          executionStatus.includes("non_balance") ||
          executionStatus.includes("non_trx_contract")
      );
      const analysisEligibleRaw = this.toText(tx?.analysis_eligible ?? tx?.analysisEligible ?? "").toLowerCase();
      const analysisEligible = analysisEligibleRaw
        ? !["0", "false", "no", "ineligible", "not_eligible"].includes(analysisEligibleRaw)
        : !nonBalanceAffecting;
      const registryNetworkFamily = this.toText(
        this.countLookupResult?.networkFamily ||
          this.countLookupResult?.network_family ||
          tx?.network_family ||
          ""
      );
      const tokenRegistryResolution = contractAddress && token
        ? this.resolveTokenForContext(
            token,
            chainId || this.countLookupResult?.chainId || this.countLookupResult?.chain_id,
            chainName || this.countLookupResult?.chainName || this.countLookupResult?.chain_name,
            registryNetworkFamily,
            contractAddress
          )
        : null;
      const tokenAuthenticity = this.buildTokenAuthenticityResult({
        tokenSymbol: token,
        contractAddress,
        chainId: chainId || this.countLookupResult?.chainId || this.countLookupResult?.chain_id,
        chainName: chainName || this.countLookupResult?.chainName || this.countLookupResult?.chain_name,
        networkFamily: registryNetworkFamily,
      });
      const providerBalanceBefore = this.parseNumberLike(
        tx?.assetBalanceBefore
          ?? tx?.asset_balance_before
          ?? tx?.balanceBeforeTransaction
          ?? tx?.balance_before_transaction
          ?? tx?.priorBalanceBeforeTransaction
          ?? tx?.prior_balance_before_transaction
          ?? tx?.balance_before
      );
      const providerBalanceAfter = this.parseNumberLike(
        tx?.assetBalanceAfter
          ?? tx?.asset_balance_after
          ?? tx?.balanceAfterTransaction
          ?? tx?.balance_after_transaction
          ?? tx?.postTransactionBalance
          ?? tx?.post_transaction_balance
          ?? tx?.balance_after
      );

      return {
        hash,
        dateText,
        date,
        amount,
        amountText: this.toText(tx?.amount_formatted || tx?.amount),
        token,
        contractAddress,
        fromAddress,
        toAddress,
        chainId,
        chainName,
        tokenContractAddress: contractAddress,
        tokenAuthenticity,
        tokenRegistryStatus: tokenRegistryResolution?.status || "",
        tokenRegistryTitle: tokenRegistryResolution?.title || "",
        tokenRegistrySummary: tokenRegistryResolution?.summary || "",
        tokenRegistrySymbol: tokenRegistryResolution?.token?.symbol || "",
        tokenRegistrySource: tokenRegistryResolution?.source || "",
        direction,
        failedOrReverted,
        isInternal,
        transactionKind,
        nonBalanceAffecting,
        analysisEligible,
        btcInputs,
        btcOutputs,
        btcInputCount: Number.isFinite(inputCount) ? inputCount : 0,
        btcOutputCount: Number.isFinite(outputCount) ? outputCount : 0,
        inputCount: Number.isFinite(inputCount) ? inputCount : null,
        outputCount: Number.isFinite(outputCount) ? outputCount : null,
        btcInputSummary: this.toText(tx?.btc_input_summary || btcInputAddresses.slice(0, 8).join(", ")),
        btcOutputSummary: this.toText(tx?.btc_output_summary || btcOutputAddresses.slice(0, 8).join(", ")),
        netSatoshis: Number(tx?.net_satoshis ?? tx?.netSatoshis ?? NaN),
        addressInputSatoshis: Number(tx?.address_input_satoshis ?? NaN),
        addressOutputSatoshis: Number(tx?.address_output_satoshis ?? NaN),
        externalOutputSatoshis: Number(tx?.external_output_satoshis ?? NaN),
        btcOpReturnCount: Number(tx?.btc_op_return_count ?? tx?.op_return_count ?? 0),
        opReturnCount: Number(tx?.op_return_count ?? tx?.btc_op_return_count ?? 0),
        btcMemoText: this.toText(tx?.btc_memo_text || tx?.memo_text || ""),
        multipleExitOutputs: Boolean(tx?.multiple_exit_outputs),
        assetBalanceBefore: Number.isFinite(providerBalanceBefore) ? providerBalanceBefore : null,
        assetBalanceAfter: Number.isFinite(providerBalanceAfter) ? providerBalanceAfter : null,
        fromBalanceBefore: this.parseNumberLike(tx?.fromBalanceBefore ?? tx?.from_balance_before),
        toBalanceBefore: this.parseNumberLike(tx?.toBalanceBefore ?? tx?.to_balance_before),
        fromBalanceAfter: this.parseNumberLike(tx?.fromBalanceAfter ?? tx?.from_balance_after),
        toBalanceAfter: this.parseNumberLike(tx?.toBalanceAfter ?? tx?.to_balance_after),
        uniqueKey: this.getProviderTransactionUniqueKey(tx),
        raw: tx,
      };
    },

    providerTransactionMatchesFollowedToken(tx = {}, followedToken = "") {
      const normalizeToken = (value) => this.toText(value).trim().toUpperCase().replace(/\s+/g, "");
      const expected = normalizeToken(followedToken);
      if (!expected) return true;

      const actual = normalizeToken(tx.token || tx.raw?.token || tx.raw?.token_symbol || tx.raw?.tokenSymbol || tx.raw?.native_symbol);
      const txContract = normalizeTokenContractText(
        tx.tokenContractAddress ||
          tx.contractAddress ||
          tx.raw?.contractAddress ||
          tx.raw?.contract_address ||
          tx.raw?.token_contract ||
          tx.raw?.address ||
          tx.raw?.tokenInfo?.tokenId ||
          tx.raw?.token_info?.tokenId
      );
      const chainKey = this.normalizeTokenChainForContext(
        tx.chainId || tx.raw?.chain_id || this.countLookupResult?.chainId || this.countLookupResult?.chain_id,
        tx.chainName || tx.raw?.chain_name || tx.raw?.network || this.countLookupResult?.chainName || this.countLookupResult?.chain_name,
        tx.raw?.network_family || this.countLookupResult?.networkFamily || this.countLookupResult?.network_family || ""
      );
      const expectedTokenRecord = this.lookupTokenBySymbolForChain(expected, chainKey);

      if (expectedTokenRecord && txContract) {
        return normalizeTokenContractText(expectedTokenRecord.contractAddress) === txContract;
      }

      if (!actual) return true;
      if (expected === actual) return true;
      const aliases = {
        MATIC: ["POL"],
        POL: ["MATIC"],
      };
      return Boolean(aliases[expected] && aliases[expected].includes(actual));
    },
    isBtcHopContext(entry, selectedAddress, transactions = []) {
      const fundsIn = entry ? this.deriveFundsIn(entry, selectedAddress) : null;
      const token = this.toText(fundsIn?.token || "").trim().toUpperCase();
      const family = this.toText(this.countLookupResult?.networkFamily || this.countLookupResult?.network_family || "").toLowerCase();
      const route = this.toText(this.countLookupResult?.route || this.traceFetchProgress?.trace_fetch?.route || "").toLowerCase();
      const entryChainText = `${this.toText(entry?.chainId)} ${this.toText(entry?.chainName)}`.toLowerCase();
      const hasBtcRows = (Array.isArray(transactions) ? transactions : []).some((tx) => {
        const kind = this.toText(tx?.transaction_kind || tx?.source_kind || tx?.type || "").toLowerCase();
        return kind.includes("btc") || kind.includes("utxo") || Array.isArray(tx?.btc_inputs) || Array.isArray(tx?.btc_outputs);
      });
      return token === "BTC"
        || family === "btc"
        || route.includes("btc")
        || /\b(?:btc|bitcoin)\b/iu.test(entryChainText)
        || this.isBtcAddress(selectedAddress)
        || hasBtcRows;
    },

    shouldUseBtcFifoSolver(entry, selectedAddress, transactions = []) {
      if (!this.isBtcHopContext(entry, selectedAddress, transactions)) {
        return false;
      }

      // For BTC, Reverse + NEXT_CHRONOLOGICAL should still use the indexed UTXO source mapper.
      // The generic reverse fallback only picks a prior incoming transaction by date, which can be
      // wrong when the transaction of interest has multiple inputs/outputs.
      return this.hopLogicType === "FIFO" || this.hopLogicType === "NEXT_CHRONOLOGICAL" || this.hopLogicType === "LIFO";
    },

    roundToSatoshis(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return null;
      }
      return Math.max(0, Math.round(number));
    },

    btcDecimalToSatoshis(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return null;
      }
      return Math.max(0, Math.round(number * 100000000));
    },

    parseBtcSatoshis(value, referenceBtcAmount = null) {
      const text = this.toText(value);
      const parsed = this.parseNumberLike(text);
      if (!Number.isFinite(parsed)) {
        return null;
      }

      const lower = text.toLowerCase();
      if (/sat|satoshi/.test(lower)) {
        return this.roundToSatoshis(parsed);
      }

      const reference = Number(referenceBtcAmount);
      if (Number.isFinite(reference) && reference > 0 && parsed > reference * 10) {
        return this.roundToSatoshis(parsed);
      }

      if (!text.includes(".") && parsed > 21000000) {
        return this.roundToSatoshis(parsed);
      }

      return this.btcDecimalToSatoshis(parsed);
    },

    getBtcReverseAllocationEvidenceFromRow(row = []) {
      if (this.traceDirection !== 'reverse') return null;
      const text = [
        row?.[COL.SYSTEM_NOTES],
        row?.[COL.TOOL88_NOTES],
      ].map(value => this.toText(value)).filter(Boolean).join('\n');
      if (!text || !/(?:Tool\s*88\s+BTC\s+reverse\s+input|Followed\s+BTC\s+range)/iu.test(text)) return null;

      let allocation = null;
      const rangePattern = /Followed\s+BTC\s+range:\s*sats\s+([\d,]+)\s+to\s+([\d,]+)\s+of\s+this\s+output/giu;
      let rangeMatch = rangePattern.exec(text);
      while (rangeMatch) {
        const start = Number(String(rangeMatch[1] || '').replace(/,/gu, ''));
        const end = Number(String(rangeMatch[2] || '').replace(/,/gu, ''));
        if (Number.isSafeInteger(start) && Number.isSafeInteger(end) && start >= 0 && end > start) {
          allocation = {
            fundsFollowedSatoshis: end - start,
            leftBoundsSatoshis: start,
            source: 'tool88-btc-range-note',
          };
        }
        rangeMatch = rangePattern.exec(text);
      }

      if (!allocation) {
        const decimalPattern = /Tool\s*88\s+BTC\s+reverse\s+input:[^\n]*?\bfollowed\s+([0-9][\d,]*(?:\.\d+)?)\s*BTC\s+from\s+source\s+offset\s+([0-9][\d,]*(?:\.\d+)?)\s*BTC/giu;
        let decimalMatch = decimalPattern.exec(text);
        while (decimalMatch) {
          const followed = Number(String(decimalMatch[1] || '').replace(/,/gu, ''));
          const leftBounds = Number(String(decimalMatch[2] || '').replace(/,/gu, ''));
          const fundsFollowedSatoshis = Math.round(followed * 100000000);
          const leftBoundsSatoshis = Math.round(leftBounds * 100000000);
          if (Number.isSafeInteger(fundsFollowedSatoshis) && fundsFollowedSatoshis > 0 && Number.isSafeInteger(leftBoundsSatoshis) && leftBoundsSatoshis >= 0) {
            allocation = {
              fundsFollowedSatoshis,
              leftBoundsSatoshis,
              source: 'tool88-btc-decimal-note',
            };
          }
          decimalMatch = decimalPattern.exec(text);
        }
      }
      return allocation;
    },

    formatBtcSatsCompact(satoshis) {
      const sats = Number(satoshis);
      if (!Number.isFinite(sats)) {
        return "0 BTC";
      }
      return `${(sats / 100000000).toFixed(8).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1")} BTC`;
    },

    getBtcValueSatoshis(item = {}) {
      const satoshiCandidates = [
        item?.value_satoshis,
        item?.value_sats,
        item?.satoshis,
        item?.valueSat,
        item?.value_sat,
        item?.amount_satoshis,
        item?.amountSats,
        item?.coin?.value,
        item?.prevout?.value,
      ];
      for (const raw of satoshiCandidates) {
        if (raw === null || raw === undefined || raw === "") continue;
        const text = this.toText(raw).replace(/,/g, "").trim();
        if (/^-?\d+(?:\.0+)?$/.test(text)) {
          return Math.round(Number(text));
        }
      }

      const btcCandidates = [item?.value_btc, item?.amount_btc, item?.btc_value, item?.btcAmount];
      for (const raw of btcCandidates) {
        if (raw === null || raw === undefined || raw === "") continue;
        const numeric = Number(this.toText(raw).replace(/,/g, ""));
        if (Number.isFinite(numeric)) {
          return Math.round(numeric * 100000000);
        }
      }

      const raw = item?.value ?? item?.amount ?? item?.quantity;
      if (raw === null || raw === undefined || raw === "") {
        return 0;
      }
      if (typeof raw === "number") {
        if (!Number.isFinite(raw)) return 0;
        return Number.isInteger(raw) ? Math.round(raw) : Math.round(raw * 100000000);
      }
      const text = this.toText(raw).replace(/,/g, "").trim();
      if (!text) return 0;
      const numeric = Number(text);
      if (!Number.isFinite(numeric)) return 0;
      if (/\.\d+/.test(text) && numeric <= 21000000) {
        return Math.round(numeric * 100000000);
      }
      return Math.round(numeric);
    },

    getBtcItemAddress(item = {}) {
      return this.toText(item?.address || item?.coin?.address || item?.prevout?.address || item?.scriptpubkey_address || item?.scriptPubKeyAddress || "");
    },

    getBtcItemOrder(item = {}, fallbackIndex = 0) {
      const raw = item?.order ?? item?.index ?? item?.n ?? item?.vout ?? item?.prev_index ?? item?.prevIndex;
      const value = Number(raw);
      return Number.isFinite(value) ? value : fallbackIndex;
    },

    normalizeBtcIo(items = [], fallbackKind = "output") {
      return (Array.isArray(items) ? items : []).map((item, index) => ({
        ...item,
        address: this.getBtcItemAddress(item),
        valueSatoshis: this.getBtcValueSatoshis(item),
        order: this.getBtcItemOrder(item, index),
        isOpReturn: Boolean(item?.op_return || item?.is_op_return || this.toText(item?.address).toUpperCase() === "MESSAGE" || this.toText(item?.type).toLowerCase().includes("op_return")),
        kind: fallbackKind,
        originalIndex: index,
      })).sort((left, right) => {
        if (left.order !== right.order) return left.order - right.order;
        return left.originalIndex - right.originalIndex;
      });
    },

    getBtcTxSortParts(tx = {}) {
      const time = tx.date ? tx.date.getTime() : 0;
      const block = Number(tx.raw?.block_number ?? tx.raw?.blockNumber ?? tx.raw?.blockHeight ?? tx.raw?.height ?? 0);
      const index = Number(tx.raw?.index ?? tx.raw?.transaction_index ?? tx.raw?.transactionIndex ?? 0);
      return {
        time: Number.isFinite(time) ? time : 0,
        block: Number.isFinite(block) ? block : 0,
        index: Number.isFinite(index) ? index : 0,
        hash: this.normalizeHashKey(tx.hash),
      };
    },

    compareBtcTransactions(left = {}, right = {}) {
      const a = this.getBtcTxSortParts(left);
      const b = this.getBtcTxSortParts(right);
      if (a.time !== b.time) return a.time - b.time;
      if (a.block !== b.block) return a.block - b.block;
      if (a.index !== b.index) return a.index - b.index;
      return a.hash.localeCompare(b.hash);
    },

    getBtcTxInputSatoshis(tx = {}, selectedKey = "") {
      return this.normalizeBtcIo(tx.btcInputs, "input").reduce((sum, input) => {
        if (!selectedKey || this.normalizeAddressKey(input.address) === selectedKey) {
          return sum + input.valueSatoshis;
        }
        return sum;
      }, 0);
    },

    getBtcTxOutputSatoshis(tx = {}, selectedKey = "") {
      return this.normalizeBtcIo(tx.btcOutputs, "output").reduce((sum, output) => {
        if (!output.isOpReturn && (!selectedKey || this.normalizeAddressKey(output.address) === selectedKey)) {
          return sum + output.valueSatoshis;
        }
        return sum;
      }, 0);
    },

    getFiniteBtcSatoshisFromTx(tx = {}, keys = []) {
      for (const key of Array.isArray(keys) ? keys : [keys]) {
        const raw = key.split(".").reduce((value, part) => (value && value[part] !== undefined ? value[part] : undefined), tx);
        if (raw === null || raw === undefined || raw === "") continue;
        const numeric = Number(this.toText(raw).replace(/,/g, ""));
        if (Number.isFinite(numeric)) return Math.round(numeric);
      }
      return null;
    },

    btcAddressFieldContainsSelected(value = "", selectedKey = "") {
      if (!selectedKey) return false;
      const raw = this.toText(value);
      if (!raw) return false;
      if (this.normalizeAddressKey(raw) === selectedKey) return true;
      return raw
        .split(/[\s,;|]+/)
        .map((part) => this.normalizeAddressKey(part))
        .some((key) => key && key === selectedKey);
    },

    getBtcSelectedInputSatoshisForAnalysis(tx = {}, selectedKey = "") {
      const direct = this.getBtcTxInputSatoshis(tx, selectedKey);
      if (direct > 0) return direct;

      const addressInput = this.getFiniteBtcSatoshisFromTx(tx, [
        "addressInputSatoshis",
        "raw.address_input_satoshis",
        "raw.addressInputSatoshis",
        "raw.selected_input_satoshis",
        "raw.selectedInputSatoshis",
      ]);
      if (addressInput !== null && addressInput > 0) return addressInput;

      const selectedOutputSats = this.getBtcTxOutputSatoshis(tx, selectedKey);
      const netSats = this.getFiniteBtcSatoshisFromTx(tx, ["netSatoshis", "raw.net_satoshis", "raw.netSatoshis"]);
      if (netSats !== null && netSats < 0 && this.isBtcTransactionOutgoingForSelected(tx, selectedKey, { allowInputFallback: false })) {
        return Math.max(0, selectedOutputSats + Math.abs(netSats));
      }

      return 0;
    },

    getBtcSelectedNetSatoshisForAnalysis(tx = {}, selectedKey = "") {
      const selectedInputSats = this.getBtcTxInputSatoshis(tx, selectedKey);
      const selectedOutputSats = this.getBtcTxOutputSatoshis(tx, selectedKey);
      if (selectedInputSats > 0 || selectedOutputSats > 0) {
        return selectedOutputSats - selectedInputSats;
      }

      const addressOutput = this.getFiniteBtcSatoshisFromTx(tx, [
        "addressOutputSatoshis",
        "raw.address_output_satoshis",
        "raw.addressOutputSatoshis",
        "raw.selected_output_satoshis",
        "raw.selectedOutputSatoshis",
      ]);
      const addressInput = this.getFiniteBtcSatoshisFromTx(tx, [
        "addressInputSatoshis",
        "raw.address_input_satoshis",
        "raw.addressInputSatoshis",
        "raw.selected_input_satoshis",
        "raw.selectedInputSatoshis",
      ]);
      if ((addressOutput !== null && addressOutput > 0) || (addressInput !== null && addressInput > 0)) {
        return Math.max(0, addressOutput || 0) - Math.max(0, addressInput || 0);
      }

      const netSats = this.getFiniteBtcSatoshisFromTx(tx, ["netSatoshis", "raw.net_satoshis", "raw.netSatoshis"]);
      if (netSats !== null && (this.isBtcTransactionOutgoingForSelected(tx, selectedKey, { allowInputFallback: false }) || this.isBtcTransactionIncomingForSelected(tx, selectedKey))) {
        return netSats;
      }

      return 0;
    },

    isBtcTransactionOutgoingForSelected(tx = {}, selectedKey = "", options = {}) {
      const allowInputFallback = options.allowInputFallback !== false;
      if (!selectedKey) return false;
      if (allowInputFallback && this.getBtcTxInputSatoshis(tx, selectedKey) > 0) return true;
      const direction = this.toText(tx.direction || tx.raw?.direction || "").toLowerCase();
      if (/out|sent|send|debit|withdraw/.test(direction)) return true;
      if (this.btcAddressFieldContainsSelected(tx.fromAddress || tx.raw?.from_address || tx.raw?.from, selectedKey)) return true;
      const addressInput = this.getFiniteBtcSatoshisFromTx(tx, [
        "addressInputSatoshis",
        "raw.address_input_satoshis",
        "raw.addressInputSatoshis",
        "raw.selected_input_satoshis",
        "raw.selectedInputSatoshis",
      ]);
      if (addressInput !== null && addressInput > 0) return true;
      const netSats = this.getFiniteBtcSatoshisFromTx(tx, ["netSatoshis", "raw.net_satoshis", "raw.netSatoshis"]);
      return netSats !== null && netSats < 0;
    },

    isBtcTransactionIncomingForSelected(tx = {}, selectedKey = "") {
      if (!selectedKey) return false;
      if (this.getBtcTxOutputSatoshis(tx, selectedKey) > 0) return true;
      const direction = this.toText(tx.direction || tx.raw?.direction || "").toLowerCase();
      if (/in|receive|credit|deposit/.test(direction)) return true;
      if (this.btcAddressFieldContainsSelected(tx.toAddress || tx.raw?.to_address || tx.raw?.to, selectedKey)) return true;
      const addressOutput = this.getFiniteBtcSatoshisFromTx(tx, [
        "addressOutputSatoshis",
        "raw.address_output_satoshis",
        "raw.addressOutputSatoshis",
        "raw.selected_output_satoshis",
        "raw.selectedOutputSatoshis",
      ]);
      if (addressOutput !== null && addressOutput > 0) return true;
      const netSats = this.getFiniteBtcSatoshisFromTx(tx, ["netSatoshis", "raw.net_satoshis", "raw.netSatoshis"]);
      return netSats !== null && netSats > 0;
    },

    getBtcPriorInputSatsBeforeSelectedAddress(tx = {}, selectedKey = "") {
      const inputs = this.normalizeBtcIo(tx.btcInputs, "input");
      let prior = 0;
      for (const input of inputs) {
        const key = this.normalizeAddressKey(input.address);
        if (key && key === selectedKey) {
          return prior;
        }
        prior += input.valueSatoshis;
      }
      return 0;
    },

    getBtcFeeSatoshis(tx = {}) {
      const rawFee = tx.raw?.fee ?? tx.raw?.fee_satoshis ?? tx.raw?.feeSatoshis;
      if (rawFee !== undefined && rawFee !== null && rawFee !== "") {
        return this.getBtcValueSatoshis({ value_satoshis: rawFee });
      }
      const totalInputs = this.normalizeBtcIo(tx.btcInputs, "input").reduce((sum, input) => sum + input.valueSatoshis, 0);
      const totalOutputs = this.normalizeBtcIo(tx.btcOutputs, "output").reduce((sum, output) => sum + output.valueSatoshis, 0);
      return Math.max(0, totalInputs - totalOutputs);
    },

    getBtcSelectedRowFundsContext(entry, selectedAddress) {
      const row = this.getSelectedTargetRow(entry, selectedAddress) || this.makeBlankRow();
      const fundsIn = this.deriveFundsIn(entry, selectedAddress);
      const rowAmountBtc = this.parseNumberLike(row?.[COL.AMOUNT]);
      const fundsFollowedText = this.toText(row?.[COL.FUNDS_FOLLOWED]);
      const reverseAllocationEvidence = this.getBtcReverseAllocationEvidenceFromRow(row);
      const fundsFollowedSats = Number.isFinite(Number(reverseAllocationEvidence?.fundsFollowedSatoshis))
        ? Number(reverseAllocationEvidence.fundsFollowedSatoshis)
        : this.parseBtcSatoshis(fundsFollowedText, rowAmountBtc);
      const rowAmountSats = this.parseBtcSatoshis(row?.[COL.AMOUNT], rowAmountBtc);
      const fallbackFundsSats = this.parseBtcSatoshis(fundsIn?.amount, rowAmountBtc);
      const inputAmountSats = Number.isFinite(rowAmountSats) ? rowAmountSats : fallbackFundsSats;
      const followedSats = Number.isFinite(fundsFollowedSats)
        ? fundsFollowedSats
        : (Number.isFinite(inputAmountSats) ? inputAmountSats : 0);

      const leftNumericText = this.toText(row?.[COL.LEFT_BOUNDS_NUMERIC]);
      const leftDisplayText = this.toText(row?.[COL.LEFT_BOUNDS_DISPLAY]);
      const leftBoundsSats = Number.isFinite(Number(reverseAllocationEvidence?.leftBoundsSatoshis))
        ? Number(reverseAllocationEvidence.leftBoundsSatoshis)
        : (this.parseBtcSatoshis(leftNumericText || leftDisplayText, rowAmountBtc) || 0);
      const priorBalanceText = this.toText(row?.[COL.PRIOR_BALANCE]);
      const priorBalanceSats = this.parseBtcSatoshis(priorBalanceText, rowAmountBtc);

      return {
        fundsIn: {
          ...fundsIn,
          amount: followedSats / 100000000,
          token: "BTC",
        },
        row,
        fundsFollowedSatoshis: Math.max(0, followedSats),
        inputAmountSatoshis: Math.max(0, inputAmountSats || followedSats || 0),
        leftBoundsSatoshis: Math.max(0, leftBoundsSats),
        priorBalanceSatoshis: Number.isFinite(priorBalanceSats) ? Math.max(0, priorBalanceSats) : null,
        originalFundsFollowedText: fundsFollowedText,
        originalLeftBoundsText: leftNumericText || leftDisplayText,
        originalPriorBalanceText: priorBalanceText,
      };
    },

    buildBtcNormalizedTransactions(transactions = []) {
      return (Array.isArray(transactions) ? transactions : [])
        .map((tx) => this.normalizeProviderTransaction(tx))
        .filter((tx) => {
          const kind = this.toText(tx.transactionKind || tx.raw?.transaction_kind || "").toLowerCase();
          return tx.hash &&
            this.isTool88LikelyTransactionHash(tx.hash) &&
            tx.date &&
            (kind.includes("btc") || kind.includes("utxo") || tx.btcInputCount || tx.btcOutputCount || Array.isArray(tx.btcInputs) || Array.isArray(tx.btcOutputs));
        })
        .map((tx) => ({
          ...tx,
          btcInputs: this.normalizeBtcIo(tx.btcInputs, "input"),
          btcOutputs: this.normalizeBtcIo(tx.btcOutputs, "output"),
        }))
        .sort((left, right) => this.compareBtcTransactions(left, right));
    },

    getBtcBalanceBeforeTransactionSatoshis(btcTransactions = [], selectedKey = '', targetTx = null) {
      if (!targetTx || !selectedKey) return 0;
      const targetHash = this.normalizeHashKey(targetTx.hash || targetTx.txHash || '');
      let balance = 0;
      for (const tx of (Array.isArray(btcTransactions) ? btcTransactions : [])) {
        if (!tx) continue;
        const txHash = this.normalizeHashKey(tx.hash || tx.txHash || '');
        if (targetHash && txHash === targetHash) break;
        const compare = this.compareBtcTransactions(tx, targetTx);
        if (compare >= 0) break;
        balance += this.getBtcSelectedNetSatoshisForAnalysis(tx, selectedKey);
      }
      if (!Number.isFinite(balance) || balance < 0) return 0;
      return Math.max(0, Math.round(balance));
    },

    getBtcOutputAddressPriorBalanceSatoshis() {
      // The fetched BTC history used for a forward solve belongs to the selected/source
      // address. It is safe for FIFO dismissal, but it is not necessarily complete
      // history for every output/recipient address. Do not infer a recipient prior
      // balance from the source-address history; generated child rows should show
      // zero/blank rather than a misleading source-side balance. The selected row's
      // own prior balance is still written by updateBtcSelectedEntryPriorBalanceInRows().
      return 0;
    },

    getBtcSelectedEntryPriorBalanceSatoshis(solution = null) {
      const analysis = solution?.btcAnalysis || null;
      if (!analysis || analysis.analysisDirection === 'reverse') return null;
      const candidates = [
        analysis.reconstructedPriorBalanceSatoshis,
        analysis.actualPriorBalanceSatoshis,
        analysis.priorBalanceBeforeTransactionSatoshis,
        analysis.priorBalanceSatoshis,
      ];
      for (const value of candidates) {
        const numeric = Number(value);
        if (Number.isFinite(numeric) && numeric >= 0) return Math.round(numeric);
      }
      return null;
    },

    updateBtcSelectedEntryPriorBalanceInRows(baseRows = [], entry = null, solution = null) {
      const outputRows = (Array.isArray(baseRows) ? baseRows : []).map((row) => this.cloneRowPreservingValues(row));
      const priorBalanceSatoshis = this.getBtcSelectedEntryPriorBalanceSatoshis(solution);
      if (!Number.isFinite(priorBalanceSatoshis)) return outputRows;
      const bounds = this.findEntryBoundsInRows(outputRows, entry);
      const entryStartIndex = bounds?.startRowIndex ?? Math.max(0, Number(entry?.startRowIndex || 0));
      if (!Number.isInteger(entryStartIndex) || entryStartIndex < 0 || entryStartIndex >= outputRows.length) return outputRows;
      const entryRow = outputRows[entryStartIndex] || this.makeBlankRow();
      const token = this.toText(entryRow?.[COL.TOKEN] || entry?.token || solution?.fundsIn?.token || '').toUpperCase();
      const isBtcRow = token === 'BTC' || this.isBtcAddress(entryRow?.[COL.FROM_ADDRESS]) || this.isBtcAddress(entryRow?.[COL.TO_ADDRESS]);
      if (!isBtcRow) return outputRows;
      entryRow[COL.PRIOR_BALANCE] = this.formatBtcSatsCompact(priorBalanceSatoshis).replace(/ BTC$/, '');
      outputRows[entryStartIndex] = entryRow;
      return outputRows;
    },

    updateBtcSelectedEntryTransactionDetailsInRows(baseRows = [], entry = null, selectedAddress = '', transactions = [], solution = null) {
      const outputRows = (Array.isArray(baseRows) ? baseRows : []).map(row => this.cloneRowPreservingValues(row));
      if (!entry || !outputRows.length) return outputRows;
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const tokenHint = this.toText(entry?.rows?.[0]?.row?.[COL.TOKEN] || solution?.fundsIn?.token || '').toUpperCase();
      if (tokenHint !== 'BTC' && !this.isBtcAddress(selectedAddress) && !solution?.btcAnalysis) return outputRows;
      const btcTransactions = this.buildBtcNormalizedTransactions(Array.isArray(transactions) ? transactions : []);
      const entryHashKey = this.normalizeHashKey(entry?.txHash || entry?.rows?.[0]?.row?.[COL.TX_HASH]);
      const interestTx = btcTransactions.find(tx => entryHashKey && this.normalizeHashKey(tx?.hash) === entryHashKey) || null;
      if (!interestTx) return outputRows;
      const bounds = this.findEntryBoundsInRows(outputRows, entry);
      const rowIndex = bounds?.startRowIndex ?? Math.max(0, Number(entry?.startRowIndex || 0));
      if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex >= outputRows.length) return outputRows;
      const row = outputRows[rowIndex] || this.makeBlankRow();
      const inputs = this.normalizeBtcIo(interestTx.btcInputs || [], 'input');
      const firstInputAddress = inputs
        .map(input => this.toText(input?.address).trim())
        .find(address => address && !/^op_return$/iu.test(address)) || '';
      const outputs = this.normalizeBtcIo(interestTx.btcOutputs || [], 'output');
      const selectedOutput = (selectedKey
        ? outputs.find(output => this.normalizeAddressKey(output?.address) === selectedKey)
        : null) || (outputs.filter(output => !output?.isOpReturn && this.toText(output?.address)).length === 1
        ? outputs.find(output => !output?.isOpReturn && this.toText(output?.address))
        : null);
      const reverse = this.traceDirection === 'reverse' || solution?.analysisDirection === 'reverse' || solution?.btcAnalysis?.analysisDirection === 'reverse';
      const reverseFundsContext = reverse
        ? this.getBtcSelectedRowFundsContext(entry, selectedAddress)
        : null;
      const selectedInputResolution = reverse
        ? this.findBtcReverseSelectedInput({ entry, selectedAddress, interestTx, fundsContext: reverseFundsContext })
        : null;
      const selectedInput = selectedInputResolution?.input || null;
      const selectedAmountSatoshis = reverse && selectedInput
        ? Math.max(0, Number(selectedInput.valueSatoshis) || 0)
        : (selectedOutput && Number.isFinite(Number(selectedOutput.valueSatoshis)) ? Math.max(0, Number(selectedOutput.valueSatoshis)) : 0);
      const selectedAmount = selectedAmountSatoshis > 0 ? selectedAmountSatoshis / 100000000 : null;

      if (reverse) {
        if (selectedAddress) row[COL.FROM_ADDRESS] = selectedAddress;
        if (this.normalizeAddressKey(row?.[COL.TO_ADDRESS]) === selectedKey) row[COL.TO_ADDRESS] = '';
        if (!this.toText(row?.[COL.TO_ADDRESS]).trim()) {
          const nonSelfOutputs = outputs.filter(output => !output.isOpReturn && this.toText(output.address) && this.normalizeAddressKey(output.address) !== selectedKey);
          if (nonSelfOutputs.length === 1) row[COL.TO_ADDRESS] = nonSelfOutputs[0].address;
        }
        this.removeReverseDormantNotesForTraceThrough(row);
      } else {
        if (!this.toText(row?.[COL.FROM_ADDRESS]).trim() && firstInputAddress) row[COL.FROM_ADDRESS] = firstInputAddress;
        if (!this.toText(row?.[COL.TO_ADDRESS]).trim() && selectedAddress) row[COL.TO_ADDRESS] = selectedAddress;
      }
      if (!this.toText(row?.[COL.DATE]).trim() && interestTx.dateText) row[COL.DATE] = interestTx.dateText;
      if ((reverse || !this.toText(row?.[COL.AMOUNT]).trim()) && Number.isFinite(selectedAmount) && selectedAmount > 0) {
        row[COL.AMOUNT] = this.formatBtcSatsCompact(Math.round(selectedAmount * 100000000)).replace(/ BTC$/, '');
      }
      if (!this.toText(row?.[COL.TOKEN]).trim()) row[COL.TOKEN] = 'BTC';
      const reverseFollowedSatoshis = reverse
        ? Math.max(0, Number(reverseFundsContext?.fundsFollowedSatoshis) || 0)
        : 0;
      if (reverse && reverseFollowedSatoshis > 0) {
        // Column F describes the full selected input/output value. Column M
        // remains the investigator's followed tranche and must not expand to
        // the full UTXO merely because another reverse hop was solved.
        row[COL.FUNDS_FOLLOWED] = this.formatBtcSatsCompact(reverseFollowedSatoshis);
      } else if (
        !this.toText(row?.[COL.FUNDS_FOLLOWED]).trim()
        && Number.isFinite(selectedAmount)
        && selectedAmount > 0
        && (!reverse || !this.toText(row?.[COL.FROM_HASH]).trim())
      ) {
        // A brand-new root may legitimately start with the selected UTXO value.
        // A reverse descendant must arrive with an explicit followed allocation;
        // never manufacture one from the full input value.
        row[COL.FUNDS_FOLLOWED] = this.formatBtcSatsCompact(Math.round(selectedAmount * 100000000));
      }
      if (!this.toText(row?.[COL.CHAIN_ID]).trim()) row[COL.CHAIN_ID] = interestTx.chainId || 'BTC';
      if (!this.toText(row?.[COL.CHAIN_NAME]).trim()) row[COL.CHAIN_NAME] = interestTx.chainName || 'Bitcoin';
      if (!this.toText(row?.[COL.HOP_LOGIC]).trim()) row[COL.HOP_LOGIC] = this.hopLogicType;
      outputRows[rowIndex] = row;
      return outputRows;
    },


    getAccountSelectedEntryPriorBalanceAmount(solution = null) {
      const analysis = solution?.accountAnalysis || null;
      if (!analysis || solution?.analysisDirection === 'reverse' || analysis.analysisDirection === 'reverse') return null;
      const candidates = [
        analysis.accountPriorBalanceDebug?.finalPriorBalance,
        analysis.accountPriorBalanceDebug?.balanceBefore,
        analysis.actualPriorBalanceAmount,
      ];
      for (const candidate of candidates) {
        const numeric = Number(candidate);
        if (Number.isFinite(numeric) && numeric >= 0) return numeric;
      }
      return null;
    },

    buildAccountFifoInspectorPayload(solution = null, priorBalance = null, tokenText = '') {
      const analysis = solution?.accountAnalysis || null;
      if (!analysis) return null;
      const token = this.toText(tokenText || solution?.fundsIn?.token || analysis.asset || '').toUpperCase();
      const balanceNumber = Number(priorBalance ?? analysis.actualPriorBalanceAmount ?? analysis.priorBalanceAmount);
      if (!Number.isFinite(balanceNumber) || balanceNumber < 0) return null;
      const debug = Array.isArray(analysis.accountPriorBalanceDebug)
        ? analysis.accountPriorBalanceDebug[0]
        : (analysis.accountPriorBalanceDebug || null);
      const includedEvents = Array.isArray(debug?.includedEvents) ? debug.includedEvents : [];
      const excludedEvents = Array.isArray(debug?.excludedEvents) ? debug.excludedEvents : [];
      const numberOrNull = (value) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
      };
      const formatEvent = (event = {}, index = 0) => ({
        index: index + 1,
        role: this.toText(event.role || event.deltaRole || ''),
        hash: this.toText(event.hash || ''),
        from: this.toText(event.fromAddress || ''),
        to: this.toText(event.toAddress || ''),
        amount: numberOrNull(event.amount),
        feeAmount: numberOrNull(event.feeAmount),
        delta: numberOrNull(event.delta),
        token: this.toText(event.token || token),
        order: event.order || null,
      });
      const formatExitCandidate = (exit = {}, index = 0) => ({
        index: index + 1,
        hash: this.toText(exit.hash || ''),
        from: this.toText(exit.fromAddress || ''),
        to: this.toText(exit.toAddress || ''),
        date: this.toText(exit.date || exit.timeStamp || ''),
        amount: numberOrNull(exit.amount),
        followedAmount: numberOrNull(exit.followedAmount),
        leftBoundAmount: numberOrNull(exit.leftBoundAmount),
        rightBoundAmount: numberOrNull(exit.rightBoundAmount),
        token: this.toText(exit.token || token),
        chainId: this.toText(exit.chainId || ''),
        chainName: this.toText(exit.chainName || ''),
        kind: this.toText(exit.transactionKind || ''),
      });
      const eventFeeTotal = includedEvents.reduce((sum, event) => {
        const fee = Number(event?.feeAmount);
        const role = this.toText(event?.deltaRole || event?.role || '').toLowerCase();
        if (!Number.isFinite(fee) || fee <= 0) return sum;
        return /outgoing|self|fee/u.test(role) ? sum + fee : sum;
      }, 0);
      const includedFeeEventCount = Number(debug?.includedFeeEventCount ?? includedEvents.filter((event) => Number(event?.feeAmount) > 0).length);
      const targetEventFeeAmount = numberOrNull(debug?.targetEventFeeAmount ?? analysis.nativeDirectSpendFeeAmount);
      const sampleLimit = 40;
      return {
        version: 1,
        token,
        selectedAddressPriorBalance: balanceNumber,
        selectedAddressPriorBalanceText: this.formatFunds(balanceNumber, token),
        priorBalanceSource: this.toText(analysis.actualPriorBalanceSource || analysis.priorBalanceSource || debug?.source || 'unknown'),
        targetHash: this.toText(debug?.targetHash || ''),
        targetEventKey: this.toText(debug?.targetEventKey || ''),
        includedEventCount: includedEvents.length,
        excludedEventCount: excludedEvents.length,
        failedOrRevertedRowsExcluded: numberOrNull(analysis.failedOrRevertedRowsExcluded),
        nonBalanceRowsExcluded: numberOrNull(analysis.nonBalanceRowsExcluded),
        ineligibleRowsExcluded: numberOrNull(analysis.ineligibleRowsExcluded),
        providerRowsReviewed: numberOrNull(analysis.providerRowsReviewed),
        nativeFeeAmountBeforeSelectedTx: eventFeeTotal > 0 ? eventFeeTotal : 0,
        nativeFeeAmountBeforeSelectedTxText: eventFeeTotal > 0 ? this.formatFunds(eventFeeTotal, token) : '',
        includedFeeEventCount: Number.isFinite(includedFeeEventCount) ? includedFeeEventCount : null,
        targetEventFeeAmount,
        targetEventFeeText: targetEventFeeAmount !== null && targetEventFeeAmount > 0 ? this.formatFunds(targetEventFeeAmount, token) : '',
        followedFeeConsumedAmount: numberOrNull(analysis.feeConsumedAmount),
        followedFeeConsumedText: Number(analysis.feeConsumedAmount) > 0 ? this.formatFunds(analysis.feeConsumedAmount, token) : '',
        nativeResidualPrunedAmount: numberOrNull(analysis.nativeResidualPrunedAmount),
        nativeResidualPrunedText: Number(analysis.nativeResidualPrunedAmount) > 0 ? this.formatFunds(analysis.nativeResidualPrunedAmount, token) : '',
        nativeDirectSpendFeeAmount: numberOrNull(analysis.nativeDirectSpendFeeAmount),
        nativeDirectSpendFeeText: Number(analysis.nativeDirectSpendFeeAmount) > 0 ? this.formatFunds(analysis.nativeDirectSpendFeeAmount, token) : '',
        sampleLimit,
        includedEvents: includedEvents.slice(Math.max(0, includedEvents.length - sampleLimit)).map(formatEvent),
        excludedEvents: excludedEvents.slice(0, sampleLimit).map(formatEvent),
        exitCandidates: Array.isArray(solution?.exitTransactions) ? solution.exitTransactions.slice(0, 80).map(formatExitCandidate) : [],
        warnings: Array.isArray(analysis.warnings) ? analysis.warnings.slice(0, 40).map((warning) => this.toText(warning)).filter(Boolean) : [],
      };
    },

    appendAccountFifoInspectorPayloadNoteToRow(row = [], solution = null, priorBalance = null, tokenText = '') {
      if (!Array.isArray(row)) return row;
      const payload = this.buildAccountFifoInspectorPayload(solution, priorBalance, tokenText);
      if (!payload) return row;
      let json = '';
      try {
        json = JSON.stringify(payload);
      } catch {
        json = '';
      }
      if (json.length > 18000) {
        const compactPayload = {
          ...payload,
          includedEvents: payload.includedEvents.slice(-12),
          excludedEvents: payload.excludedEvents.slice(0, 12),
          exitCandidates: Array.isArray(payload.exitCandidates) ? payload.exitCandidates.slice(0, 20) : [],
          warnings: payload.warnings.slice(0, 16),
          sampleLimit: 12,
        };
        json = JSON.stringify(compactPayload);
      }
      if (json) this.appendTool88NoteToRow(row, `Tool 88 FIFO inspector: ${json}`);
      return row;
    },

    appendAccountFifoDiagnosticNoteToRow(row = [], solution = null, priorBalance = null, tokenText = '') {
      const analysis = solution?.accountAnalysis || null;
      if (!analysis || !Array.isArray(row)) return row;
      const token = this.toText(tokenText || solution?.fundsIn?.token || analysis.asset || '').toUpperCase();
      const balanceNumber = Number(priorBalance ?? analysis.actualPriorBalanceAmount ?? analysis.priorBalanceAmount);
      if (!Number.isFinite(balanceNumber) || balanceNumber < 0) return row;
      const debug = Array.isArray(analysis.accountPriorBalanceDebug)
        ? analysis.accountPriorBalanceDebug[0]
        : (analysis.accountPriorBalanceDebug || null);
      const source = this.toText(analysis.actualPriorBalanceSource || analysis.priorBalanceSource || debug?.source || 'unknown');
      const included = Number(debug?.includedEvents?.length ?? analysis.priorBalanceIncludedEventCount ?? NaN);
      const excluded = Number(debug?.excludedEvents?.length ?? analysis.priorBalanceExcludedEventCount ?? NaN);
      const providerRowsReviewed = Number(analysis.providerRowsReviewed ?? NaN);
      const failedExcluded = Number(analysis.failedOrRevertedRowsExcluded ?? NaN);
      const nonBalanceExcluded = Number(analysis.nonBalanceRowsExcluded ?? NaN);
      const ineligibleExcluded = Number(analysis.ineligibleRowsExcluded ?? NaN);
      const feeConsumed = Number(analysis.feeConsumedAmount ?? NaN);
      const targetEventFee = Number(debug?.targetEventFeeAmount ?? analysis.nativeDirectSpendFeeAmount ?? NaN);
      const includedFeeEventCountForNote = Number(debug?.includedFeeEventCount ?? NaN);
      const priorFeeTotal = Array.isArray(debug?.includedEvents)
        ? debug.includedEvents.reduce((sum, event) => {
            const fee = Number(event?.feeAmount);
            const role = this.toText(event?.deltaRole || event?.role || '').toLowerCase();
            return Number.isFinite(fee) && fee > 0 && /outgoing|self|fee/u.test(role) ? sum + fee : sum;
          }, 0)
        : 0;
      const parts = [
        `Tool 88 FIFO diagnostics: selected-address prior balance before this transaction was ${this.formatFunds(balanceNumber, token)}`,
        source ? `source=${source}` : '',
        Number.isFinite(providerRowsReviewed) ? `provider rows reviewed=${this.formatInteger(providerRowsReviewed)}` : '',
        Number.isFinite(included) ? `included prior events=${this.formatInteger(included)}` : '',
        Number.isFinite(excluded) ? `excluded events=${this.formatInteger(excluded)}` : '',
        Number.isFinite(failedExcluded) ? `failed/reverted rows excluded=${this.formatInteger(failedExcluded)}` : '',
        Number.isFinite(nonBalanceExcluded) ? `non-balance rows excluded=${this.formatInteger(nonBalanceExcluded)}` : '',
        Number.isFinite(ineligibleExcluded) ? `ineligible rows excluded=${this.formatInteger(ineligibleExcluded)}` : '',
        Number.isFinite(includedFeeEventCountForNote) ? `fee events included=${this.formatInteger(includedFeeEventCountForNote)}` : '',
        priorFeeTotal > 0 ? `native fees before selected tx=${this.formatFunds(priorFeeTotal, token)}` : '',
        Number.isFinite(targetEventFee) && targetEventFee > 0 ? `target-event fee=${this.formatFunds(targetEventFee, token)}` : '',
        Number.isFinite(feeConsumed) && feeConsumed > 0 ? `followed native fees consumed=${this.formatFunds(feeConsumed, token)}` : '',
      ].filter(Boolean);
      this.appendTool88NoteToRow(row, `${parts.join('; ')}.`);
      this.appendAccountFifoInspectorPayloadNoteToRow(row, solution, priorBalance, token);
      return row;
    },

    updateAccountSelectedEntryPriorBalanceInRows(baseRows = [], entry = null, solution = null) {
      const outputRows = (Array.isArray(baseRows) ? baseRows : []).map((row) => this.cloneRowPreservingValues(row));
      const priorBalance = this.getAccountSelectedEntryPriorBalanceAmount(solution);
      if (!Number.isFinite(priorBalance)) return outputRows;
      const bounds = this.findEntryBoundsInRows(outputRows, entry);
      const entryStartIndex = bounds?.startRowIndex ?? Math.max(0, Number(entry?.startRowIndex || 0));
      if (!Number.isInteger(entryStartIndex) || entryStartIndex < 0 || entryStartIndex >= outputRows.length) return outputRows;
      const entryRow = outputRows[entryStartIndex] || this.makeBlankRow();
      const token = this.toText(entryRow?.[COL.TOKEN] || entry?.token || solution?.fundsIn?.token || '').toUpperCase();
      if (token === 'BTC' || this.isBtcAddress(entryRow?.[COL.FROM_ADDRESS]) || this.isBtcAddress(entryRow?.[COL.TO_ADDRESS])) return outputRows;
      entryRow[COL.PRIOR_BALANCE] = Number(priorBalance).toFixed(4);
      this.appendAccountFifoDiagnosticNoteToRow(entryRow, solution, priorBalance, token);
      outputRows[entryStartIndex] = entryRow;
      return outputRows;
    },

    buildBtcExitTransaction({ tx, output, followedSatoshis, leftBoundsSatoshis, dismissedByPriorInputSatoshis = 0, dismissedByPriorBalanceSatoshis = 0 }) {
      const outputIndex = Number.isFinite(Number(output.order)) ? Number(output.order) : output.originalIndex;
      const outputBtc = output.valueSatoshis / 100000000;
      const followedBtc = followedSatoshis / 100000000;
      const leftBtc = leftBoundsSatoshis / 100000000;
      return {
        ...tx,
        hash: tx.hash,
        fromAddress: this.selectedTargetAddress,
        toAddress: output.address,
        amount: outputBtc,
        amountText: this.formatBtcSatsCompact(output.valueSatoshis).replace(/ BTC$/, ""),
        token: "BTC",
        outputIndex,
        outputValueSatoshis: output.valueSatoshis,
        followedSatoshis,
        followedAmount: followedBtc,
        leftBoundsSatoshis,
        leftBoundAmount: leftBtc,
        rightBoundsSatoshis: leftBoundsSatoshis + followedSatoshis,
        dismissedByPriorInputSatoshis,
        dismissedByPriorBalanceSatoshis,
        transactionKind: "btc_fifo_exit_output",
        continuesAsLabel: `${tx.hash} output ${outputIndex}`,
      };
    },

    solveBtcPtdqForEntry(entry, selectedAddress, transactions = []) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const btcTransactions = this.buildBtcNormalizedTransactions(transactions);
      const fundsContext = this.getBtcSelectedRowFundsContext(entry, selectedAddress);
      const transactionOfInterestHash = this.normalizeHashKey(entry?.txHash || "");
      const interestTx = btcTransactions.find((tx) => transactionOfInterestHash && this.normalizeHashKey(tx.hash) === transactionOfInterestHash) || null;
      const entryDate = this.getEntryDate(entry);
      const interestComparable = interestTx || {
        hash: entry?.txHash || "",
        date: entryDate,
        raw: { block_number: 0, index: 0 },
      };

      const diagnostics = {
        solver: "BTC_LIFO_POST_TXN_DEPOSIT_QUEUE",
        analysisDirection: "forward",
        priorBalanceSatoshis: 0,
        inputLeftBoundsSatoshis: fundsContext.leftBoundsSatoshis,
        fundsFollowedSatoshis: fundsContext.fundsFollowedSatoshis,
        inputRightTailSatoshis: Math.max(0, fundsContext.inputAmountSatoshis - fundsContext.leftBoundsSatoshis - fundsContext.fundsFollowedSatoshis),
        initialDismissalSatoshis: fundsContext.leftBoundsSatoshis,
        dismissedByPriorInputsSatoshis: 0,
        dismissedByPriorBalanceSatoshis: 0,
        fullyDismissedOutputsCount: 0,
        partiallyDismissedOutputsCount: 0,
        feeConsumedSatoshis: 0,
        memoConsumedSatoshis: 0,
        followedReturnedToSelfChangeSatoshis: 0,
        complexTransactionCount: 0,
        sameTimestampTieCount: 0,
        ptdqLaterDepositSatoshis: 0,
        ptdqLaterDepositCount: 0,
        ptdqEventsReviewed: 0,
        ptdqExitEventsReviewed: 0,
        ptdqEndingLaterQueueSatoshis: 0,
        ptdqRemainingPrefixSatoshis: 0,
        dormantSatoshis: 0,
        totalExitFollowedSatoshis: 0,
        exitCount: 0,
        transactionOfInterestFound: Boolean(interestTx),
        outgoingTransactionCount: 0,
        feeWarnings: [],
        structureWarnings: [],
      };

      if (!btcTransactions.length) {
        diagnostics.structureWarnings.push("No BTC transaction rows were available for LIFO/PTDQ analysis after normalization.");
      }
      if (!interestTx && transactionOfInterestHash) {
        diagnostics.structureWarnings.push("BTC LIFO/PTDQ did not find the input transaction hash in fetched BTC history; Tool 88 used the spreadsheet date as the timeline anchor.");
      }

      let depositAmountSatoshis = Math.max(0, fundsContext.inputAmountSatoshis || fundsContext.fundsFollowedSatoshis || 0);
      let prefixRemaining = Math.max(0, fundsContext.leftBoundsSatoshis || 0);
      if (depositAmountSatoshis > 0 && prefixRemaining > depositAmountSatoshis) {
        diagnostics.structureWarnings.push(`BTC LIFO/PTDQ clamped input left bounds from ${this.formatBtcSatsCompact(prefixRemaining)} to the selected deposit amount ${this.formatBtcSatsCompact(depositAmountSatoshis)}.`);
        prefixRemaining = depositAmountSatoshis;
      }
      let followRemaining = Math.max(0, fundsContext.fundsFollowedSatoshis || 0);
      if (depositAmountSatoshis > 0) {
        const availableAfterPrefix = Math.max(0, depositAmountSatoshis - prefixRemaining);
        if (followRemaining > availableAfterPrefix) {
          diagnostics.structureWarnings.push(`BTC LIFO/PTDQ clamped funds followed from ${this.formatBtcSatsCompact(followRemaining)} to the selected deposit's available right-side amount ${this.formatBtcSatsCompact(availableAfterPrefix)}.`);
          followRemaining = availableAfterPrefix;
        }
      }
      diagnostics.inputLeftBoundsSatoshis = prefixRemaining;
      diagnostics.fundsFollowedSatoshis = followRemaining;
      diagnostics.initialDismissalSatoshis = prefixRemaining;

      const afterInterest = btcTransactions
        .filter((tx) => {
          if (this.normalizeHashKey(tx.hash) === transactionOfInterestHash) return false;
          const compare = this.compareBtcTransactions(tx, interestComparable);
          return interestTx ? compare > 0 : (entryDate ? tx.date.getTime() >= entryDate.getTime() : true);
        })
        .sort((left, right) => this.compareBtcTransactions(left, right));

      const events = [];
      afterInterest.forEach((tx) => {
        const selectedInputSatoshis = this.getBtcTxInputSatoshis(tx, selectedKey);
        this.normalizeBtcIo(tx.btcOutputs, "output").forEach((output) => {
          if (!output.isOpReturn && output.valueSatoshis > 0 && this.normalizeAddressKey(output.address) === selectedKey) {
            events.push({ kind: "deposit", tx, output, amountSatoshis: output.valueSatoshis });
          }
        });
        if (selectedInputSatoshis > 0) {
          events.push({ kind: "exit", tx, amountSatoshis: selectedInputSatoshis });
        }
      });

      events.sort((left, right) => {
        const c = this.compareBtcTransactions(left.tx, right.tx);
        if (c !== 0) return c;
        if (left.kind === right.kind) return 0;
        return left.kind === "deposit" ? -1 : 1;
      });

      let laterQueue = 0;
      let feeConsumedSatoshis = 0;
      let memoConsumedSatoshis = 0;
      const exitsByKey = new Map();
      const relevantTransactionsByKey = new Map();

      const rememberTx = (tx) => {
        const key = this.normalizeHashKey(tx.hash) || JSON.stringify(this.getBtcTxSortParts(tx));
        if (!relevantTransactionsByKey.has(key)) relevantTransactionsByKey.set(key, tx);
      };

      const addExitPortion = (tx, output, followedSats, leftBoundWithinOutput) => {
        const key = `${tx.hash}|${output.order}|${this.normalizeAddressKey(output.address)}`;
        if (!exitsByKey.has(key)) {
          exitsByKey.set(key, {
            hash: tx.hash,
            date: tx.date,
            dateText: tx.dateText,
            blockNumber: tx.raw?.block_number || tx.raw?.blockNumber || "",
            transactionIndex: tx.raw?.index || tx.raw?.transactionIndex || "",
            fromAddress: selectedAddress,
            toAddress: output.address,
            outputSatoshis: output.valueSatoshis,
            followedSatoshis: 0,
            leftBoundsSatoshis: leftBoundWithinOutput,
            rightBoundsSatoshis: leftBoundWithinOutput,
            outputIndex: output.order,
            token: "BTC",
            transactionKind: "btc_lifo_ptdq_exit_output",
            continuesAsLabel: `${tx.hash} output ${output.order}`,
            nonContiguous: false,
          });
        }
        const exit = exitsByKey.get(key);
        if (exit.followedSatoshis > 0 && leftBoundWithinOutput > exit.rightBoundsSatoshis) {
          exit.nonContiguous = true;
          diagnostics.structureWarnings.push(`BTC LIFO/PTDQ output ${tx.hash} #${output.order} received non-contiguous followed slices; review left bounds manually.`);
        }
        if (exit.followedSatoshis === 0) {
          exit.leftBoundsSatoshis = leftBoundWithinOutput;
        }
        exit.followedSatoshis += followedSats;
        exit.rightBoundsSatoshis = Math.max(exit.rightBoundsSatoshis, leftBoundWithinOutput + followedSats);
      };

      const consumePtdqCapacity = (capacitySatoshis, tx, output = null, mode = "exit") => {
        let amountRemaining = Math.max(0, Number(capacitySatoshis) || 0);
        if (amountRemaining <= 0 || followRemaining <= 0) return;

        const laterConsumed = Math.min(amountRemaining, laterQueue);
        laterQueue -= laterConsumed;
        amountRemaining -= laterConsumed;

        if (amountRemaining <= 0) return;

        const prefixHere = Math.min(amountRemaining, prefixRemaining);
        prefixRemaining -= prefixHere;
        amountRemaining -= prefixHere;

        if (amountRemaining <= 0) return;

        const take = Math.min(amountRemaining, followRemaining);
        if (take <= 0) return;

        followRemaining -= take;
        if (mode === "fee") {
          feeConsumedSatoshis += take;
          return;
        }
        if (mode === "memo") {
          memoConsumedSatoshis += take;
          return;
        }
        if (output) {
          addExitPortion(tx, output, take, laterConsumed + prefixHere);
        }
      };

      events.forEach((event) => {
        if (followRemaining <= 0) return;
        rememberTx(event.tx);
        if (event.kind === "deposit") {
          laterQueue += Math.max(0, Number(event.amountSatoshis) || 0);
          diagnostics.ptdqLaterDepositCount += 1;
          diagnostics.ptdqLaterDepositSatoshis += Math.max(0, Number(event.amountSatoshis) || 0);
          return;
        }

        diagnostics.ptdqExitEventsReviewed += 1;
        const tx = event.tx;
        const outputs = this.normalizeBtcIo(tx.btcOutputs, "output").filter((output) => output.valueSatoshis > 0);
        outputs.forEach((output) => {
          if (followRemaining <= 0) return;
          const outputKey = this.normalizeAddressKey(output.address);
          const isSelfOutput = outputKey === selectedKey;
          const isMemoOutput = output.isOpReturn || this.toText(output.address).toUpperCase() === "MESSAGE" || this.toText(output.address).toUpperCase() === "OP_RETURN";
          if (isSelfOutput) return;
          if (isMemoOutput) {
            consumePtdqCapacity(output.valueSatoshis, tx, output, "memo");
            return;
          }
          consumePtdqCapacity(output.valueSatoshis, tx, output, "exit");
        });

        const feeSatoshis = this.getBtcFeeSatoshis(tx);
        if (feeSatoshis > 0 && followRemaining > 0) {
          const inputs = this.normalizeBtcIo(tx.btcInputs, "input").filter((input) => input.valueSatoshis > 0);
          const selectedInputs = inputs.filter((input) => this.normalizeAddressKey(input.address) === selectedKey);
          const otherInputs = inputs.filter((input) => this.normalizeAddressKey(input.address) !== selectedKey);
          if (selectedInputs.length && !otherInputs.length) {
            consumePtdqCapacity(feeSatoshis, tx, null, "fee");
          } else if (selectedInputs.length && otherInputs.length) {
            diagnostics.feeWarnings.push(`BTC LIFO/PTDQ saw mixed-input transaction ${this.shortenMiddle(tx.hash, 10, 8)} with miner fee ${this.formatBtcSatsCompact(feeSatoshis)}; fee payer is ambiguous and was not assigned to followed funds automatically.`);
          }
        }
      });

      const exitRecords = [...exitsByKey.values()].sort((left, right) => {
        const txCompare = this.compareBtcTransactions(left, right);
        if (txCompare !== 0) return txCompare;
        return (left.outputIndex || 0) - (right.outputIndex || 0);
      });
      const exitTransactions = exitRecords.map((exit) => {
        const followedAmount = exit.followedSatoshis / 100000000;
        const leftBoundAmount = exit.leftBoundsSatoshis / 100000000;
        const balanceBeforeSatoshis = this.getBtcOutputAddressPriorBalanceSatoshis(btcTransactions, exit);
        return {
          hash: exit.hash,
          date: exit.date,
          dateText: exit.dateText,
          fromAddress: exit.fromAddress,
          toAddress: exit.toAddress,
          amount: exit.outputSatoshis / 100000000,
          amountText: this.formatBtcSatsCompact(exit.outputSatoshis).replace(/ BTC$/, ""),
          token: "BTC",
          outputIndex: exit.outputIndex,
          outputValueSatoshis: exit.outputSatoshis,
          followedSatoshis: exit.followedSatoshis,
          followedAmount,
          leftBoundsSatoshis: exit.leftBoundsSatoshis,
          leftBoundAmount,
          rightBoundsSatoshis: exit.rightBoundsSatoshis,
          rightBoundAmount: exit.rightBoundsSatoshis / 100000000,
          priorBalanceSatoshis: balanceBeforeSatoshis,
          priorBalanceBeforeTransactionSatoshis: balanceBeforeSatoshis,
          priorBalanceBeforeTransaction: balanceBeforeSatoshis / 100000000,
          transactionKind: "btc_lifo_ptdq_exit_output",
          nonContiguous: Boolean(exit.nonContiguous),
          continuesAsLabel: exit.continuesAsLabel,
        };
      });

      const exitRows = exitTransactions.map((exit) => {
        const row = this.buildExitRow({
          entry,
          exit,
          followedAmount: exit.followedAmount,
          leftBoundAmount: exit.leftBoundAmount,
          token: "BTC",
        });
        return row;
      });

      diagnostics.ptdqEventsReviewed = events.length;
      diagnostics.ptdqEndingLaterQueueSatoshis = laterQueue;
      diagnostics.ptdqRemainingPrefixSatoshis = prefixRemaining;
      diagnostics.feeConsumedSatoshis = feeConsumedSatoshis;
      diagnostics.memoConsumedSatoshis = memoConsumedSatoshis;
      diagnostics.dormantSatoshis = Math.max(0, followRemaining);
      diagnostics.totalExitFollowedSatoshis = exitTransactions.reduce((sum, exit) => sum + (Number(exit.followedSatoshis) || 0), 0);
      diagnostics.exitCount = exitTransactions.length;
      diagnostics.outgoingTransactionCount = diagnostics.ptdqExitEventsReviewed;

      if (exitTransactions.length) {
        diagnostics.structureWarnings.push(`BTC LIFO/PTDQ found ${exitTransactions.length} exit output${exitTransactions.length === 1 ? "" : "s"} after newer post-deposit UTXOs were consumed first.`);
      }
      if (diagnostics.dormantSatoshis > 0) {
        diagnostics.structureWarnings.push(`BTC LIFO/PTDQ left ${this.formatBtcSatsCompact(diagnostics.dormantSatoshis)} dormant at the selected address.`);
      }
      if (feeConsumedSatoshis > 0) {
        diagnostics.feeWarnings.push(`BTC LIFO/PTDQ consumed ${this.formatBtcSatsCompact(feeConsumedSatoshis)} of followed funds as miner fee.`);
      }
      if (memoConsumedSatoshis > 0) {
        diagnostics.structureWarnings.push(`BTC LIFO/PTDQ consumed ${this.formatBtcSatsCompact(memoConsumedSatoshis)} of followed funds in OP_RETURN/message outputs; no destination branch was created for memo value.`);
      }

      this.pushUniqueWarnings([
        ...diagnostics.structureWarnings,
        ...diagnostics.feeWarnings,
      ].map((warning) => `BTC LIFO/PTDQ: ${warning}`));

      return {
        status: exitRows.length
          ? (diagnostics.dormantSatoshis > 0 ? "mixed_exit_with_dormant" : (feeConsumedSatoshis > 0 ? "exit_with_fee_consumed" : "exit"))
          : (feeConsumedSatoshis > 0 && diagnostics.dormantSatoshis <= 0 ? "fee_consumed" : "dormant"),
        analysisKind: "btc_lifo_ptdq",
        solverKind: "btc_lifo_ptdq",
        exitTransactions,
        exitRows,
        dormantAmountText: diagnostics.dormantSatoshis > 0 ? this.formatBtcSatsCompact(diagnostics.dormantSatoshis) : "",
        dormantReason: diagnostics.dormantSatoshis > 0 && exitRows.length ? "BTC LIFO/PTDQ found one or more exits, but some followed BTC remains at the selected address." : "",
        feeConsumedAmountText: feeConsumedSatoshis > 0 ? this.formatBtcSatsCompact(feeConsumedSatoshis) : "",
        pathEndingReason: !exitRows.length && feeConsumedSatoshis > 0 && diagnostics.dormantSatoshis <= 0
          ? `No qualifying BTC output exit was found. ${this.formatBtcSatsCompact(feeConsumedSatoshis)} appears to have been consumed by miner fees under Tool 88 LIFO/PTDQ allocation.`
          : "",
        fundsIn: {
          ...fundsContext.fundsIn,
          amount: (diagnostics.totalExitFollowedSatoshis + diagnostics.dormantSatoshis + feeConsumedSatoshis + memoConsumedSatoshis) / 100000000,
          token: "BTC",
          source: fundsContext.originalFundsFollowedText ? "column M funds followed" : "column F / selected BTC output",
        },
        followedAmount: diagnostics.totalExitFollowedSatoshis / 100000000,
        leftBoundAmount: exitTransactions.length === 1 ? exitTransactions[0].leftBoundAmount : null,
        relevantTransactions: [...relevantTransactionsByKey.values()],
        btcAnalysis: diagnostics,
      };
    },

    solveBtcReverseLifoForEntry(entry, selectedAddress, transactions = []) {
      const solution = this.solveBtcReverseFifoForEntry(entry, selectedAddress, transactions);
      if (solution?.btcAnalysis) {
        solution.btcAnalysis.solver = "BTC_REVERSE_LIFO_INDEXED_SOURCE_RANGE";
        solution.btcAnalysis.structureWarnings = solution.btcAnalysis.structureWarnings || [];
        solution.btcAnalysis.structureWarnings.push("Reverse BTC LIFO uses the indexed UTXO source mapper. For BTC, the transaction-of-interest inputs/outputs are explicit, so Tool 88 keeps the reproducible input/output allocation and labels the result as a LIFO reverse source-range review.");
        solution.solverKind = "btc_reverse_lifo_indexed_source_range";
        solution.analysisKind = "btc_reverse_lifo";
      }
      return solution;
    },


    pushUniqueWarnings(warnings = []) {
      (Array.isArray(warnings) ? warnings : [warnings]).forEach((warning) => {
        const text = this.toText(warning);
        if (text && !this.warnings.includes(text)) {
          this.warnings.push(text);
        }
      });
    },

    getBtcSourceFromAddressSummary(tx = {}, selectedKey = "", selectedAddress = "") {
      const inputs = this.normalizeBtcIo(tx.btcInputs, "input");
      const selectedInputCount = inputs.filter((input) => this.normalizeAddressKey(input.address) === selectedKey).length;
      if (selectedInputCount > 0) {
        return selectedAddress;
      }

      const unique = [];
      const seen = new Set();
      inputs.forEach((input) => {
        const address = this.toText(input.address);
        const key = this.normalizeAddressKey(address);
        if (!key || seen.has(key)) return;
        seen.add(key);
        unique.push(address);
      });

      if (unique.length === 1) {
        return unique[0];
      }
      if (unique.length > 1) {
        return `${unique.length} BTC inputs`;
      }
      return "Unknown BTC inputs";
    },

    getBtcOutputIndexFromEntryRow(row = []) {
      const text = [
        this.getTool88NoteTextForRow(row),
        row?.[COL.CONTINUES_AS],
        row?.[COL.LEFT_BOUNDS_DISPLAY],
        row?.[COL.FUNDS_FOLLOWED],
      ].map((value) => this.toText(value)).filter(Boolean).join("\n");
      const patterns = [
        /\bbtc\s+output\s+index\s*:?\s*(\d+)\b/i,
        /\bsource\s+output\s+index\s*:?\s*(\d+)\b/i,
        /\boutput\s+index\s*:?\s*(\d+)\b/i,
        /\boutput\s*#\s*(\d+)\b/i,
        /\boutput\s+(\d+)\b/i,
      ];
      for (const pattern of patterns) {
        const match = text.match(pattern);
        if (!match) continue;
        const value = Number(match[1]);
        if (Number.isFinite(value)) {
          return value;
        }
      }
      return null;
    },

    getBtcInputPreviousTransactionHash(input = {}) {
      return this.toText(
        input?.previousTransactionHash
          || input?.previous_transaction_hash
          || input?.prev_txid
          || input?.prevTxid
          || input?.prev_hash
          || input?.prevHash
          || input?.prevout?.txid
          || input?.prevout?.hash
          || input?.coin?.txid
          || input?.coin?.hash
          || ''
      ).trim();
    },

    getBtcInputPreviousOutputIndex(input = {}) {
      const raw = input?.previousOutputIndex
        ?? input?.previous_output_index
        ?? input?.prev_vout
        ?? input?.prevVout
        ?? input?.prev_index
        ?? input?.prevIndex
        ?? input?.prevout?.vout
        ?? input?.prevout?.index
        ?? input?.coin?.vout
        ?? input?.coin?.index;
      const text = this.toText(raw).trim();
      if (!text) return null;
      const numeric = Number(text);
      return Number.isFinite(numeric) && numeric >= 0 ? Math.trunc(numeric) : null;
    },

    getBtcReverseInputSelectionMetadata(entry = null, selectedAddress = '') {
      const rows = Array.isArray(entry?.rows)
        ? entry.rows.map(item => Array.isArray(item) ? item : item?.row).filter(Array.isArray)
        : [];
      const text = rows
        .map(row => [row?.[COL.SYSTEM_NOTES], row?.[COL.USER_NOTES], row?.[COL.TOOL88_NOTES]].map(value => this.toText(value)).filter(Boolean).join('\n'))
        .filter(Boolean)
        .join('\n');
      const marker = text.match(/Tool\s*88\s+BTC\s+reverse\s+input\s+selection:\s*([^\r\n]+)/iu);
      const markerText = marker?.[1] || '';
      const field = (name) => {
        const match = markerText.match(new RegExp(`(?:^|;)\\s*${name}\\s*=\\s*([^;\\r\\n]+)`, 'iu'));
        return this.toText(match?.[1]).trim();
      };
      const fallbackIndex = text.match(/selected\s+Input\s*#\s*(\d+)/iu);
      const inputIndexRaw = field('input_index') || fallbackIndex?.[1] || '';
      const inputValueRaw = field('input_value_satoshis');
      const previousOutputRaw = field('prev_vout');
      const inputIndexNumber = inputIndexRaw === '' ? Number.NaN : Number(inputIndexRaw);
      const inputValueNumber = inputValueRaw === '' ? Number.NaN : Number(inputValueRaw);
      const previousOutputNumber = previousOutputRaw === '' ? Number.NaN : Number(previousOutputRaw);
      return {
        explicit: Boolean(marker || fallbackIndex),
        inputIndex: Number.isFinite(inputIndexNumber) && inputIndexNumber >= 0 ? Math.trunc(inputIndexNumber) : null,
        inputAddress: field('input_address') || this.toText(selectedAddress).trim(),
        inputValueSatoshis: Number.isFinite(inputValueNumber) && inputValueNumber > 0 ? Math.trunc(inputValueNumber) : null,
        previousTransactionHash: field('prev_txid'),
        previousOutputIndex: Number.isFinite(previousOutputNumber) && previousOutputNumber >= 0 ? Math.trunc(previousOutputNumber) : null,
      };
    },

    findBtcReverseSelectedInput({ entry = null, selectedAddress = '', interestTx = null, fundsContext = null } = {}) {
      const metadata = this.getBtcReverseInputSelectionMetadata(entry, selectedAddress);
      const inputs = this.normalizeBtcIo(interestTx?.btcInputs || [], 'input');
      if (!metadata.explicit) return { explicit: false, metadata, input: null, inputs };
      let candidates = inputs.slice();
      const strictFilter = (predicate, reason) => {
        const matched = candidates.filter(predicate);
        if (!matched.length) {
          return { ok: false, reason };
        }
        candidates = matched;
        return { ok: true, reason: '' };
      };
      const previousHashKey = this.normalizeHashKey(metadata.previousTransactionHash);
      if (previousHashKey) {
        const result = strictFilter(
          input => this.normalizeHashKey(this.getBtcInputPreviousTransactionHash(input)) === previousHashKey,
          `No fetched Bitcoin input matched the selected previous transaction ${metadata.previousTransactionHash}.`
        );
        if (!result.ok) return { explicit: true, metadata, input: null, inputs, ambiguousCount: 0, mismatchReason: result.reason };
      }
      if (metadata.previousOutputIndex !== null) {
        const result = strictFilter(
          input => Number(this.getBtcInputPreviousOutputIndex(input)) === Number(metadata.previousOutputIndex),
          `No fetched Bitcoin input matched previous output index ${metadata.previousOutputIndex}.`
        );
        if (!result.ok) return { explicit: true, metadata, input: null, inputs, ambiguousCount: 0, mismatchReason: result.reason };
      }
      if (metadata.inputIndex !== null) {
        const result = strictFilter(
          input => Number(input.order) === Number(metadata.inputIndex) || Number(input.originalIndex) === Number(metadata.inputIndex),
          `No fetched Bitcoin input matched selected input #${metadata.inputIndex}.`
        );
        if (!result.ok) return { explicit: true, metadata, input: null, inputs, ambiguousCount: 0, mismatchReason: result.reason };
      }
      const selectedKey = this.normalizeAddressKey(metadata.inputAddress || selectedAddress);
      if (selectedKey) {
        const result = strictFilter(
          input => this.normalizeAddressKey(input.address) === selectedKey,
          'No fetched Bitcoin input matched the selected source address.'
        );
        if (!result.ok) return { explicit: true, metadata, input: null, inputs, ambiguousCount: 0, mismatchReason: result.reason };
      }
      const targetSatoshis = metadata.inputValueSatoshis
        || Math.max(0, Number(fundsContext?.fundsFollowedSatoshis) || Number(fundsContext?.inputAmountSatoshis) || 0);
      if (targetSatoshis > 0 && candidates.length > 1) {
        const matched = candidates.filter(input => Math.abs((Number(input.valueSatoshis) || 0) - targetSatoshis) <= 1);
        if (matched.length) candidates = matched;
      }
      candidates.sort((left, right) => {
        if (left.order !== right.order) return left.order - right.order;
        return left.originalIndex - right.originalIndex;
      });
      return { explicit: true, metadata, input: candidates[0] || null, inputs, ambiguousCount: candidates.length, mismatchReason: '' };
    },

    buildBtcReverseSelectedInputSourceRows(entry = null, source = {}) {
      const primary = this.buildExitRow({
        entry,
        exit: source,
        followedAmount: source.followedAmount,
        leftBoundAmount: source.leftBoundAmount,
        token: 'BTC',
      });
      this.appendTool88NoteToRow(
        primary,
        `Tool 88 BTC reverse input: mapped input${source.inputIndex !== null && source.inputIndex !== undefined ? ` #${source.inputIndex}` : ''} of ${source.hash} to output${source.outputIndex !== null && source.outputIndex !== undefined ? ` #${source.outputIndex}` : ''}; followed ${this.formatBtcSatsCompact(source.followedSatoshis || 0)} from source offset ${this.formatBtcSatsCompact(source.leftBoundsSatoshis || 0)}.`
      );
      const markerParts = [
        'Tool 88 BTC reverse input selection: direction=reverse',
        source.inputIndex !== null && source.inputIndex !== undefined ? `input_index=${source.inputIndex}` : '',
        source.fromAddress ? `input_address=${source.fromAddress}` : '',
        Number(source.inputValueSatoshis) > 0 ? `input_value_satoshis=${Math.trunc(Number(source.inputValueSatoshis))}` : '',
        source.previousTransactionHash ? `prev_txid=${source.previousTransactionHash}` : '',
        source.previousOutputIndex !== null && source.previousOutputIndex !== undefined ? `prev_vout=${source.previousOutputIndex}` : '',
      ].filter(Boolean).join('; ');
      if (markerParts) this.appendTool88NoteToRow(primary, markerParts);
      return [primary];
    },

    buildBtcReverseSelectedInputSourceSlices(previousTx = null, previousOutput = null, fundsContext = {}, diagnostics = {}) {
      const inputs = this.normalizeBtcIo(previousTx?.btcInputs || [], 'input')
        .filter(input => Number(input.valueSatoshis) > 0);
      const outputs = this.normalizeBtcIo(previousTx?.btcOutputs || [], 'output')
        .filter(output => Number(output.valueSatoshis) > 0);
      const targetOutputIndex = Number(previousOutput?.order);
      const targetOutputSatoshis = Math.max(0, Number(previousOutput?.valueSatoshis) || 0);
      const requestedFollowed = Math.max(0, Number(fundsContext?.fundsFollowedSatoshis) || targetOutputSatoshis || 0);
      const targetStart = Math.max(0, Math.min(Number(fundsContext?.leftBoundsSatoshis) || 0, targetOutputSatoshis));
      const targetEnd = Math.max(targetStart, Math.min(targetOutputSatoshis, targetStart + requestedFollowed));
      const expectedSatoshis = Math.max(0, targetEnd - targetStart);
      const queue = inputs.map(input => ({
        input,
        remainingSatoshis: Math.max(0, Number(input.valueSatoshis) || 0),
        consumedSatoshis: 0,
      }));
      const slices = [];
      let targetVisited = false;
      let representedSatoshis = 0;

      outputs.forEach((output) => {
        let outputRemaining = Math.max(0, Number(output.valueSatoshis) || 0);
        let outputCursor = 0;
        while (outputRemaining > 0 && queue.length) {
          const segment = queue[0];
          const take = Math.min(outputRemaining, segment.remainingSatoshis);
          if (take <= 0) {
            queue.shift();
            continue;
          }
          if (Number(output.order) === targetOutputIndex) {
            targetVisited = true;
            const sliceStart = outputCursor;
            const sliceEnd = outputCursor + take;
            const overlapStart = Math.max(sliceStart, targetStart);
            const overlapEnd = Math.min(sliceEnd, targetEnd);
            const overlap = Math.max(0, overlapEnd - overlapStart);
            if (overlap > 0) {
              const sourceOffset = segment.consumedSatoshis + (overlapStart - sliceStart);
              const address = this.toText(segment.input.address).trim();
              if (address && !/^op_return$/iu.test(address)) {
                slices.push({
                  input: segment.input,
                  followedSatoshis: overlap,
                  sourceLeftBoundsSatoshis: Math.max(0, sourceOffset),
                });
                representedSatoshis += overlap;
              }
            }
          }
          segment.remainingSatoshis -= take;
          segment.consumedSatoshis += take;
          outputRemaining -= take;
          outputCursor += take;
          if (segment.remainingSatoshis <= 0) queue.shift();
        }
      });

      const unresolvedSatoshis = Math.max(0, expectedSatoshis - representedSatoshis);
      diagnostics.reverseSelectedInputTargetVisited = targetVisited;
      diagnostics.reverseSelectedInputExpectedSatoshis = expectedSatoshis;
      diagnostics.reverseSelectedInputRepresentedSatoshis = representedSatoshis;
      diagnostics.reverseSelectedInputUnresolvedSatoshis = unresolvedSatoshis;
      if (!targetVisited) diagnostics.structureWarnings.push('The selected previous Bitcoin output was not visited while mapping input UTXOs to outputs.');
      if (unresolvedSatoshis > 0) diagnostics.structureWarnings.push(`The selected previous Bitcoin output range has ${this.formatBtcSatsCompact(unresolvedSatoshis)} that could not be assigned to a source input address.`);
      return { slices, expectedSatoshis, representedSatoshis, unresolvedSatoshis, targetStart, targetEnd };
    },

    buildBtcReverseSelectedInputUnresolvedResult({fundsContext = {}, diagnostics = {}, reason = '', relevantTransactions = [] } = {}) {
      const followedSatoshis = Math.max(0, Number(fundsContext?.fundsFollowedSatoshis) || Number(fundsContext?.inputAmountSatoshis) || 0);
      diagnostics.structureWarnings = diagnostics.structureWarnings || [];
      if (reason) diagnostics.structureWarnings.push(reason);
      this.pushUniqueWarnings(diagnostics.structureWarnings.map(warning => `BTC reverse selected input: ${warning}`));
      return {
        status: 'reverse_selected_input_unresolved',
        analysisKind: 'btc_reverse_selected_input',
        solverKind: 'btc_reverse_selected_input_utxo',
        analysisDirection: 'reverse',
        preventPathEnding: true,
        exitTransactions: [],
        exitRows: [],
        dormantAmountText: '',
        pathEndingReason: reason || 'The selected Bitcoin input could not be connected to its previous output, so Tool 88 left the path unresolved.',
        fundsIn: {
          ...(fundsContext?.fundsIn || {}),
          amount: followedSatoshis / 100000000,
          token: 'BTC',
        },
        relevantTransactions,
        btcAnalysis: diagnostics,
      };
    },

    solveBtcReverseSelectedInputForEntry({ entry = null, selectedAddress = '', btcTransactions = [], interestTx = null, fundsContext = {}, diagnostics = {} } = {}) {
      const resolution = this.findBtcReverseSelectedInput({ entry, selectedAddress, interestTx, fundsContext });
      if (!resolution.explicit) return null;
      diagnostics.reverseSelectedInput = true;
      diagnostics.reverseSelectedInputIndex = resolution.metadata.inputIndex;
      diagnostics.reverseSelectedInputAmbiguousCount = resolution.ambiguousCount || 0;
      const input = resolution.input;
      if (!input) {
        return this.buildBtcReverseSelectedInputUnresolvedResult({
           fundsContext, diagnostics,
          reason: resolution.mismatchReason || 'The investigator-selected Bitcoin input could not be matched to the fetched transaction inputs.',
          relevantTransactions: interestTx ? [interestTx] : [],
        });
      }
      const previousTransactionHash = this.getBtcInputPreviousTransactionHash(input) || resolution.metadata.previousTransactionHash;
      const previousHashKey = this.normalizeHashKey(previousTransactionHash);
      const previousOutputIndex = this.getBtcInputPreviousOutputIndex(input) ?? resolution.metadata.previousOutputIndex;
      diagnostics.reverseSelectedInputPreviousTransactionHash = previousTransactionHash;
      diagnostics.reverseSelectedInputPreviousOutputIndex = previousOutputIndex;
      if (!previousHashKey || !this.isTool88LikelyTransactionHash(previousTransactionHash)) {
        return this.buildBtcReverseSelectedInputUnresolvedResult({
          fundsContext, diagnostics,
          reason: 'The selected Bitcoin input did not include a valid previous transaction hash.',
          relevantTransactions: interestTx ? [interestTx] : [],
        });
      }
      const previousTx = (Array.isArray(btcTransactions) ? btcTransactions : [])
        .find(tx => this.normalizeHashKey(tx?.hash) === previousHashKey) || null;
      if (!previousTx) {
        return this.buildBtcReverseSelectedInputUnresolvedResult({
          fundsContext, diagnostics,
          reason: `The previous Bitcoin transaction ${previousTransactionHash} was not present in the fetched address history.`,
          relevantTransactions: interestTx ? [interestTx] : [],
        });
      }
      const previousOutputs = this.normalizeBtcIo(previousTx.btcOutputs || [], 'output').filter(output => !output.isOpReturn && output.valueSatoshis > 0);
      let previousOutput = previousOutputIndex !== null
        ? previousOutputs.find(output => Number(output.order) === Number(previousOutputIndex)) || null
        : null;
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const inputSatoshis = Math.max(0, Number(input.valueSatoshis) || Number(resolution.metadata.inputValueSatoshis) || 0);
      if (!previousOutput && selectedKey) {
        const matching = previousOutputs.filter(output => this.normalizeAddressKey(output.address) === selectedKey);
        previousOutput = matching.find(output => !inputSatoshis || Math.abs(output.valueSatoshis - inputSatoshis) <= 1) || (matching.length === 1 ? matching[0] : null);
      }
      if (!previousOutput) {
        return this.buildBtcReverseSelectedInputUnresolvedResult({
           fundsContext, diagnostics,
          reason: `The selected Bitcoin input's previous output could not be isolated in transaction ${previousTransactionHash}.`,
          relevantTransactions: [interestTx, previousTx].filter(Boolean),
        });
      }
      const outputSatoshis = Math.max(0, Number(previousOutput.valueSatoshis) || inputSatoshis || 0);
      const followedSatoshis = Math.max(0, Math.min(
        Number(fundsContext.fundsFollowedSatoshis) || inputSatoshis || outputSatoshis || 0,
        inputSatoshis || Number.POSITIVE_INFINITY,
        outputSatoshis || Number.POSITIVE_INFINITY
      ));
      if (!followedSatoshis) {
        return this.buildBtcReverseSelectedInputUnresolvedResult({
          fundsContext, diagnostics,
          reason: 'The selected Bitcoin input had no positive value to trace.',
          relevantTransactions: [interestTx, previousTx].filter(Boolean),
        });
      }
      const leftBoundsSatoshis = Math.max(0, Math.min(Number(fundsContext.leftBoundsSatoshis) || 0, Math.max(0, outputSatoshis - followedSatoshis)));
      const mapping = this.buildBtcReverseSelectedInputSourceSlices(
        previousTx,
        previousOutput,
        { ...fundsContext, fundsFollowedSatoshis: followedSatoshis, leftBoundsSatoshis },
        diagnostics
      );
      if (!mapping.slices.length || mapping.unresolvedSatoshis > 0 || mapping.representedSatoshis !== mapping.expectedSatoshis) {
        return this.buildBtcReverseSelectedInputUnresolvedResult({
        fundsContext, diagnostics,
          reason: mapping.slices.length
            ? 'The selected Bitcoin input was found, but its previous output could not be fully apportioned across source input addresses.'
            : `The previous Bitcoin transaction ${previousTransactionHash} did not yield a usable source input allocation for the selected output.`,
          relevantTransactions: [interestTx, previousTx].filter(Boolean),
        });
      }
      const sources = mapping.slices.map(({ input: sourceInput, followedSatoshis: sliceFollowed, sourceLeftBoundsSatoshis }) => {
        const sourceInputSatoshis = Math.max(0, Number(sourceInput.valueSatoshis) || 0);
        const sourcePreviousHash = this.getBtcInputPreviousTransactionHash(sourceInput);
        const sourcePreviousOutputIndex = this.getBtcInputPreviousOutputIndex(sourceInput);
        return {
          hash: previousTx.hash,
          date: previousTx.date,
          dateText: previousTx.dateText,
          fromAddress: this.toText(sourceInput.address).trim(),
          toAddress: previousOutput.address || selectedAddress,
          amount: sourceInputSatoshis / 100000000,
          amountText: this.formatBtcSatsCompact(sourceInputSatoshis).replace(/ BTC$/, ''),
          token: 'BTC',
          chainId: previousTx.chainId || entry?.chainId || 'BTC',
          chainName: previousTx.chainName || entry?.chainName || 'Bitcoin',
          inputIndex: sourceInput.order,
          inputValueSatoshis: sourceInputSatoshis,
          previousTransactionHash: sourcePreviousHash,
          previousOutputIndex: sourcePreviousOutputIndex,
          outputIndex: previousOutput.order,
          outputValueSatoshis: outputSatoshis,
          followedSatoshis: sliceFollowed,
          followedAmount: sliceFollowed / 100000000,
          leftBoundsSatoshis: sourceLeftBoundsSatoshis,
          leftBoundAmount: sourceLeftBoundsSatoshis / 100000000,
          rightBoundsSatoshis: sourceLeftBoundsSatoshis + sliceFollowed,
          transactionKind: 'btc_reverse_selected_input_source_slice',
          reverseTargetHash: interestTx?.hash || entry?.txHash || '',
          continuesAsLabel: `${previousTx.hash} input ${sourceInput.order}`,
        };
      });
      const sourceRows = sources.flatMap(source => this.buildBtcReverseSelectedInputSourceRows(entry, source));
      diagnostics.reverseSourceCount = sources.length;
      diagnostics.reverseResolvedSatoshis = mapping.representedSatoshis;
      diagnostics.reverseTargetSeenSatoshis = mapping.expectedSatoshis;
      diagnostics.reverseTargetResolvedSatoshis = mapping.representedSatoshis;
      diagnostics.reverseUnresolvedSatoshis = 0;
      diagnostics.reverseTargetOutputIndex = previousOutput.order;
      diagnostics.reverseTargetOutputSatoshis = outputSatoshis;
      diagnostics.fundsFollowedSatoshis = mapping.representedSatoshis;
      diagnostics.inputLeftBoundsSatoshis = leftBoundsSatoshis;
      if (sources.length > 1) diagnostics.structureWarnings.push(`Reverse BTC ${this.hopLogicType} split the selected previous output across ${sources.length} contributing input UTXOs.`);
      return {
        status: 'reverse_selected_input_source',
        analysisKind: 'btc_reverse_selected_input',
        solverKind: 'btc_reverse_selected_input_utxo',
        analysisDirection: 'reverse',
        preventPathEnding: false,
        exitTransactions: sources,
        exitRows: sourceRows,
        dormantAmountText: '',
        dormantReason: '',
        pathEndingReason: '',
        fundsIn: {
          ...(fundsContext.fundsIn || {}),
          amount: mapping.representedSatoshis / 100000000,
          token: 'BTC',
          source: 'investigator-selected Bitcoin input UTXO',
        },
        followedAmount: mapping.representedSatoshis / 100000000,
        leftBoundAmount: sources.length === 1 ? sources[0].leftBoundAmount : null,
        relevantTransactions: [interestTx, previousTx].filter(Boolean),
        btcAnalysis: diagnostics,
      };
    },

    getBtcReverseTargetOutput({ entry, selectedAddress, interestTx, fundsContext, diagnostics }) {
      const selectedRow = this.getSelectedTargetRow(entry, selectedAddress) || this.makeBlankRow();
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const counterpartyKey = this.normalizeAddressKey(selectedRow?.[this.getCounterpartyColumnIndex()]);
      const rowAmountBtc = this.parseNumberLike(selectedRow?.[COL.AMOUNT]);
      const rowAmountSats = this.parseBtcSatoshis(selectedRow?.[COL.AMOUNT], rowAmountBtc);
      const outputs = this.normalizeBtcIo(interestTx?.btcOutputs || [], "output")
        .filter((output) => !output.isOpReturn && output.valueSatoshis > 0);
      const explicitOutputIndex = this.getBtcOutputIndexFromEntryRow(selectedRow);
      if (Number.isFinite(explicitOutputIndex)) {
        const explicitOutput = outputs.find((output) => Number(output.order) === Number(explicitOutputIndex)) || null;
        if (explicitOutput) {
          return explicitOutput;
        }
        diagnostics.structureWarnings.push(`Reverse BTC FIFO found an explicit output index in the row (${explicitOutputIndex}), but that index was not present in the fetched BTC transaction.`);
      }

      let candidates = outputs.filter((output) => this.normalizeAddressKey(output.address) !== selectedKey);
      if (counterpartyKey) {
        const matchingCounterparty = candidates.filter((output) => this.normalizeAddressKey(output.address) === counterpartyKey);
        if (matchingCounterparty.length) {
          candidates = matchingCounterparty;
        } else {
          diagnostics.structureWarnings.push("Reverse BTC FIFO could not find an output in the transaction of interest matching the row's counterparty address; Tool 88 used amount/order fallback.");
        }
      }

      if (Number.isFinite(rowAmountSats) && rowAmountSats > 0) {
        const exactAmount = candidates.filter((output) => Math.abs(output.valueSatoshis - rowAmountSats) <= 1);
        if (exactAmount.length) {
          candidates = exactAmount;
        } else if (counterpartyKey) {
          diagnostics.structureWarnings.push("Reverse BTC FIFO found the counterparty output, but its fetched BTC amount did not exactly match Column F; Tool 88 kept the address match and carried the row amount as the followed range.");
        }
      }

      if (!candidates.length && outputs.length) {
        candidates = [outputs[0]];
        diagnostics.structureWarnings.push("Reverse BTC FIFO could not isolate the target outgoing output; Tool 88 used the first non-self BTC output for a conservative source check.");
      }

      candidates.sort((left, right) => {
        if (left.order !== right.order) return left.order - right.order;
        return left.originalIndex - right.originalIndex;
      });

      if (candidates.length > 1) {
        diagnostics.structureWarnings.push(`Reverse BTC FIFO found ${candidates.length} possible target outputs in the transaction of interest; Tool 88 used output index ${candidates[0].order}. Review same-address/same-amount ambiguity manually.`);
      }

      const targetOutput = candidates[0] || null;
      if (targetOutput && (!fundsContext.inputAmountSatoshis || fundsContext.inputAmountSatoshis <= 0)) {
        fundsContext.inputAmountSatoshis = targetOutput.valueSatoshis;
      }
      return targetOutput;
    },

    solveBtcReverseFifoForEntry(entry, selectedAddress, transactions = []) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const btcTransactions = this.buildBtcNormalizedTransactions(transactions);
      const fundsContext = this.getBtcSelectedRowFundsContext(entry, selectedAddress);
      const transactionOfInterestHash = this.normalizeHashKey(entry?.txHash || "");
      const interestTx = btcTransactions.find((tx) => transactionOfInterestHash && this.normalizeHashKey(tx.hash) === transactionOfInterestHash) || null;
      const entryDate = this.getEntryDate(entry);
      const interestComparable = interestTx || {
        hash: entry?.txHash || "",
        date: entryDate,
        raw: { block_number: 0, index: 0 },
      };
      const diagnostics = {
        solver: "BTC_REVERSE_FIFO_SOURCE_RANGE",
        analysisDirection: "reverse",
        priorBalanceSatoshis: 0,
        inputLeftBoundsSatoshis: 0,
        fundsFollowedSatoshis: 0,
        inputRightTailSatoshis: 0,
        initialDismissalSatoshis: 0,
        dismissedByPriorInputsSatoshis: 0,
        dismissedByPriorBalanceSatoshis: 0,
        fullyDismissedOutputsCount: 0,
        partiallyDismissedOutputsCount: 0,
        feeConsumedSatoshis: 0,
        memoConsumedSatoshis: 0,
        selfChangeRequeuedSatoshis: 0,
        followedSelfChangeRequeuedSatoshis: 0,
        selfChangeRequeuedCount: 0,
        ignoredPostInterestDepositCount: 0,
        ignoredPostInterestDepositSatoshis: 0,
        feeWarnings: [],
        structureWarnings: [],
        reverseSourceCount: 0,
        reverseTargetOutputIndex: null,
        reverseTargetOutputSatoshis: 0,
        reverseResolvedSatoshis: 0,
        reverseTargetSeenSatoshis: 0,
        reverseTargetResolvedSatoshis: 0,
        reverseUnresolvedSatoshis: 0,
      };

      if (!btcTransactions.length) {
        diagnostics.structureWarnings.push("No BTC transaction rows were available for reverse FIFO analysis after normalization.");
      }
      if (!interestTx) {
        diagnostics.structureWarnings.push("Reverse BTC FIFO requires the transaction of interest to be present in fetched BTC history so Tool 88 can inspect its input/output indexes.");
        this.pushUniqueWarnings(diagnostics.structureWarnings.map((warning) => `BTC reverse FIFO: ${warning}`));
        const fallbackAmount = this.formatBtcSatsCompact(fundsContext.fundsFollowedSatoshis || fundsContext.inputAmountSatoshis || 0);
        return {
          status: "reverse_source_unresolved",
          analysisKind: "btc_reverse_fifo",
          solverKind: "btc_reverse_fifo_source_range",
          analysisDirection: "reverse",
          preventPathEnding: true,
          exitTransactions: [],
          exitRows: [],
          dormantAmountText: fallbackAmount,
          pathEndingReason: "Reverse BTC FIFO could not inspect the transaction of interest, so Tool 88 left the output unchanged instead of recording a path ending.",
          fundsIn: {
            ...fundsContext.fundsIn,
            amount: (fundsContext.fundsFollowedSatoshis || fundsContext.inputAmountSatoshis || 0) / 100000000,
            token: "BTC",
          },
          relevantTransactions: [],
          btcAnalysis: diagnostics,
        };
      }

      const selectedInputSolution = this.solveBtcReverseSelectedInputForEntry({
        entry,
        selectedAddress,
        btcTransactions,
        interestTx,
        fundsContext,
        diagnostics,
      });
      if (selectedInputSolution) return selectedInputSolution;

      const targetOutput = this.getBtcReverseTargetOutput({ entry, selectedAddress, interestTx, fundsContext, diagnostics });
      if (!targetOutput) {
        diagnostics.structureWarnings.push("Reverse BTC FIFO could not find a non-self target output in the transaction of interest.");
        this.pushUniqueWarnings(diagnostics.structureWarnings.map((warning) => `BTC reverse FIFO: ${warning}`));
        return {
          status: "reverse_source_unresolved",
          analysisKind: "btc_reverse_fifo",
          solverKind: "btc_reverse_fifo_source_range",
          analysisDirection: "reverse",
          preventPathEnding: true,
          exitTransactions: [],
          exitRows: [],
          dormantAmountText: this.formatBtcSatsCompact(fundsContext.fundsFollowedSatoshis || fundsContext.inputAmountSatoshis || 0),
          pathEndingReason: "Reverse BTC FIFO could not isolate the outgoing output being traced, so Tool 88 left the output unchanged.",
          fundsIn: {
            ...fundsContext.fundsIn,
            amount: (fundsContext.fundsFollowedSatoshis || fundsContext.inputAmountSatoshis || 0) / 100000000,
            token: "BTC",
          },
          relevantTransactions: [interestTx],
          btcAnalysis: diagnostics,
        };
      }

      let inputAmountSatoshis = Math.max(0, Number(fundsContext.inputAmountSatoshis) || targetOutput.valueSatoshis || 0);
      let leftBoundsSatoshis = Math.max(0, Number(fundsContext.leftBoundsSatoshis) || 0);
      let fundsFollowedSatoshis = Math.max(0, Number(fundsContext.fundsFollowedSatoshis) || 0);
      if (!fundsFollowedSatoshis) {
        fundsFollowedSatoshis = inputAmountSatoshis || targetOutput.valueSatoshis || 0;
      }
      if (inputAmountSatoshis > 0 && leftBoundsSatoshis > inputAmountSatoshis) {
        diagnostics.structureWarnings.push(`Reverse BTC left bounds (${this.formatBtcSatsCompact(leftBoundsSatoshis)}) exceeded the outgoing output amount and were clamped.`);
        leftBoundsSatoshis = inputAmountSatoshis;
      }
      if (inputAmountSatoshis > 0 && leftBoundsSatoshis + fundsFollowedSatoshis > inputAmountSatoshis) {
        const clamped = Math.max(0, inputAmountSatoshis - leftBoundsSatoshis);
        diagnostics.structureWarnings.push(`Reverse BTC funds followed exceeded the selected outgoing output span after left bounds; Tool 88 clamped it to ${this.formatBtcSatsCompact(clamped)}.`);
        fundsFollowedSatoshis = clamped;
      }

      const rightTailSatoshis = inputAmountSatoshis > 0
        ? Math.max(0, inputAmountSatoshis - leftBoundsSatoshis - fundsFollowedSatoshis)
        : 0;
      diagnostics.inputLeftBoundsSatoshis = leftBoundsSatoshis;
      diagnostics.fundsFollowedSatoshis = fundsFollowedSatoshis;
      diagnostics.inputRightTailSatoshis = rightTailSatoshis;
      diagnostics.reverseTargetOutputIndex = targetOutput.order;
      diagnostics.reverseTargetOutputSatoshis = targetOutput.valueSatoshis;

      const sourceQueue = [];
      const queuePush = (segment) => {
        const sats = Math.max(0, Math.round(Number(segment?.sats) || 0));
        if (!sats) return;
        sourceQueue.push({
          ...segment,
          sats,
          sourceLeftBoundsSatoshis: Math.max(0, Math.round(Number(segment?.sourceLeftBoundsSatoshis) || 0)),
        });
      };
      const queuePull = (satsToPull, txHash, inputOrder) => {
        let remaining = Math.max(0, Math.round(Number(satsToPull) || 0));
        const pulled = [];
        while (remaining > 0 && sourceQueue.length) {
          const segment = sourceQueue[0];
          const take = Math.min(remaining, Math.max(0, Number(segment.sats) || 0));
          if (!take) {
            sourceQueue.shift();
            continue;
          }
          pulled.push({ ...segment, sats: take });
          segment.sats -= take;
          segment.sourceLeftBoundsSatoshis += take;
          remaining -= take;
          if (segment.sats <= 0) sourceQueue.shift();
        }
        if (remaining > 0) {
          diagnostics.structureWarnings.push(`Reverse BTC FIFO input order ${inputOrder ?? "?"} in ${txHash || "a BTC transaction"} referenced ${this.formatBtcSatsCompact(remaining)} more selected-address value than Tool 88 could reconstruct from prior fetched outputs.`);
          pulled.push({
            label: "unresolved_prior_source",
            sats: remaining,
            sourceTxHash: "",
            sourceOutputIndex: null,
            sourceOutputValueSatoshis: remaining,
            sourceLeftBoundsSatoshis: 0,
            sourceFromAddress: "Unknown prior BTC source",
            sourceDate: null,
            unresolved: true,
          });
        }
        return pulled;
      };

      const enqueueSelectedOutputs = (tx) => {
        tx.btcOutputs.forEach((output) => {
          if (output.isOpReturn || output.valueSatoshis <= 0) return;
          if (this.normalizeAddressKey(output.address) !== selectedKey) return;
          queuePush({
            label: "incoming_utxo",
            sats: output.valueSatoshis,
            sourceTxHash: tx.hash,
            sourceOutputIndex: output.order,
            sourceOutputValueSatoshis: output.valueSatoshis,
            sourceOutputAddress: output.address,
            sourceLeftBoundsSatoshis: 0,
            sourceDate: tx.date,
            sourceDateText: tx.dateText,
            sourceFromAddress: this.getBtcSourceFromAddressSummary(tx, selectedKey, selectedAddress),
            sourceTx: tx,
          });
        });
      };

      const processSpendBeforeInterest = (tx) => {
        const stream = [];
        tx.btcInputs.forEach((input) => {
          const inputSats = Math.max(0, Number(input.valueSatoshis) || 0);
          if (!inputSats) return;
          if (this.normalizeAddressKey(input.address) === selectedKey) {
            queuePull(inputSats, tx.hash, input.order).forEach((segment) => stream.push(segment));
          } else {
            stream.push({ label: "other_input", sats: inputSats, unresolved: true });
          }
        });

        tx.btcOutputs.forEach((output) => {
          let outputRemaining = Math.max(0, Number(output.valueSatoshis) || 0);
          if (!outputRemaining) return;
          let outputCursor = 0;
          while (outputRemaining > 0 && stream.length) {
            const segment = stream[0];
            const take = Math.min(outputRemaining, Math.max(0, Number(segment.sats) || 0));
            if (!take) {
              stream.shift();
              continue;
            }
            const isSelfOutput = this.normalizeAddressKey(output.address) === selectedKey;
            if (isSelfOutput) {
              queuePush({
                label: "change_utxo",
                sats: take,
                sourceTxHash: tx.hash,
                sourceOutputIndex: output.order,
                sourceOutputValueSatoshis: output.valueSatoshis,
                sourceOutputAddress: output.address,
                sourceLeftBoundsSatoshis: outputCursor,
                sourceDate: tx.date,
                sourceDateText: tx.dateText,
                sourceFromAddress: selectedAddress,
                sourceTx: tx,
              });
            }
            segment.sats -= take;
            if (!segment.unresolved) {
              segment.sourceLeftBoundsSatoshis += take;
            }
            outputRemaining -= take;
            outputCursor += take;
            if (segment.sats <= 0) stream.shift();
          }
        });
      };

      const priorTransactions = btcTransactions
        .filter((tx) => {
          if (this.normalizeHashKey(tx.hash) === this.normalizeHashKey(interestTx.hash)) return false;
          return this.compareBtcTransactions(tx, interestComparable) < 0;
        })
        .sort((left, right) => this.compareBtcTransactions(left, right));

      let priorDepositCount = 0;
      let priorSpendCount = 0;
      priorTransactions.forEach((tx) => {
        const selectedInputSatoshis = this.getBtcTxInputSatoshis(tx, selectedKey);
        const selectedOutputSatoshis = this.getBtcTxOutputSatoshis(tx, selectedKey);
        if (selectedInputSatoshis <= 0 && selectedOutputSatoshis > 0) {
          priorDepositCount += 1;
          enqueueSelectedOutputs(tx);
          return;
        }
        if (selectedInputSatoshis > 0) {
          priorSpendCount += 1;
          processSpendBeforeInterest(tx);
          return;
        }
        if (selectedOutputSatoshis > 0) {
          enqueueSelectedOutputs(tx);
        }
      });

      diagnostics.priorDepositTransactionCount = priorDepositCount;
      diagnostics.priorSpendTransactionCount = priorSpendCount;
      diagnostics.priorBalanceSatoshis = sourceQueue.reduce((sum, segment) => sum + Math.max(0, Number(segment.sats) || 0), 0);

      const reverseSourcesByKey = new Map();
      const addSourcePortion = (segment, followedSats, sourceLeftBoundsSatoshis) => {
        if (!followedSats || segment.unresolved || !segment.sourceTxHash) {
          diagnostics.reverseUnresolvedSatoshis += followedSats || 0;
          return;
        }
        const outputIndex = Number.isFinite(Number(segment.sourceOutputIndex)) ? Number(segment.sourceOutputIndex) : 0;
        const key = `${segment.sourceTxHash}|${outputIndex}|${this.normalizeAddressKey(segment.sourceOutputAddress || selectedAddress)}`;
        if (!reverseSourcesByKey.has(key)) {
          reverseSourcesByKey.set(key, {
            hash: segment.sourceTxHash,
            date: segment.sourceDate,
            dateText: segment.sourceDateText,
            fromAddress: segment.sourceFromAddress || "Unknown BTC inputs",
            toAddress: selectedAddress,
            outputSatoshis: Math.max(0, Number(segment.sourceOutputValueSatoshis) || followedSats),
            followedSatoshis: 0,
            leftBoundsSatoshis: sourceLeftBoundsSatoshis,
            rightBoundsSatoshis: sourceLeftBoundsSatoshis,
            outputIndex,
            token: "BTC",
            transactionKind: "btc_reverse_fifo_source_output",
            reverseTargetHash: interestTx.hash,
            reverseTargetOutputIndex: targetOutput.order,
            continuesAsLabel: `${segment.sourceTxHash} output ${outputIndex}`,
          });
        }
        const source = reverseSourcesByKey.get(key);
        if (source.followedSatoshis > 0 && sourceLeftBoundsSatoshis > source.rightBoundsSatoshis) {
          source.nonContiguous = true;
          diagnostics.structureWarnings.push(`Reverse BTC source output ${segment.sourceTxHash} #${outputIndex} contributed non-contiguous followed slices; review manually.`);
        }
        if (source.followedSatoshis === 0) {
          source.leftBoundsSatoshis = sourceLeftBoundsSatoshis;
        }
        source.followedSatoshis += followedSats;
        source.rightBoundsSatoshis = Math.max(source.rightBoundsSatoshis, sourceLeftBoundsSatoshis + followedSats);
      };

      const stream = [];
      interestTx.btcInputs.forEach((input) => {
        const inputSats = Math.max(0, Number(input.valueSatoshis) || 0);
        if (!inputSats) return;
        if (this.normalizeAddressKey(input.address) === selectedKey) {
          queuePull(inputSats, interestTx.hash, input.order).forEach((segment) => stream.push(segment));
        } else {
          stream.push({ label: "other_interest_input", sats: inputSats, unresolved: true });
        }
      });

      const targetStart = leftBoundsSatoshis;
      const targetEnd = leftBoundsSatoshis + fundsFollowedSatoshis;
      let targetResolvedSatoshis = 0;
      let targetSeenSatoshis = 0;
      let targetOutputWasVisited = false;

      interestTx.btcOutputs.forEach((output) => {
        let outputRemaining = Math.max(0, Number(output.valueSatoshis) || 0);
        if (!outputRemaining) return;
        let outputCursor = 0;
        while (outputRemaining > 0 && stream.length) {
          const segment = stream[0];
          const take = Math.min(outputRemaining, Math.max(0, Number(segment.sats) || 0));
          if (!take) {
            stream.shift();
            continue;
          }
          if (Number(output.order) === Number(targetOutput.order)) {
            targetOutputWasVisited = true;
            const sliceStart = outputCursor;
            const sliceEnd = outputCursor + take;
            const overlapStart = Math.max(sliceStart, targetStart);
            const overlapEnd = Math.min(sliceEnd, targetEnd);
            const overlap = Math.max(0, overlapEnd - overlapStart);
            if (overlap > 0) {
              targetSeenSatoshis += overlap;
              const segmentOffset = overlapStart - sliceStart;
              const sourceLeft = Math.max(0, Number(segment.sourceLeftBoundsSatoshis) || 0) + segmentOffset;
              addSourcePortion(segment, overlap, sourceLeft);
              if (!segment.unresolved && segment.sourceTxHash) {
                targetResolvedSatoshis += overlap;
              }
            }
          }
          segment.sats -= take;
          if (!segment.unresolved) {
            segment.sourceLeftBoundsSatoshis += take;
          }
          outputRemaining -= take;
          outputCursor += take;
          if (segment.sats <= 0) stream.shift();
        }
      });

      diagnostics.reverseTargetSeenSatoshis = targetSeenSatoshis;
      diagnostics.reverseTargetResolvedSatoshis = targetResolvedSatoshis;
      diagnostics.reverseResolvedSatoshis = targetResolvedSatoshis;

      if (!targetOutputWasVisited) {
        diagnostics.structureWarnings.push("Reverse BTC FIFO did not visit the selected output while allocating the transaction-of-interest stream. The output may not be funded by the selected address inputs or the fetched row shape may be incomplete.");
      }
      if (targetResolvedSatoshis < targetSeenSatoshis) {
        diagnostics.structureWarnings.push(`Reverse BTC FIFO saw ${this.formatBtcSatsCompact(targetSeenSatoshis)} in the selected target range, but only ${this.formatBtcSatsCompact(targetResolvedSatoshis)} could be tied to concrete prior BTC source outputs.`);
      }
      if (targetSeenSatoshis < fundsFollowedSatoshis) {
        diagnostics.reverseUnresolvedSatoshis += Math.max(0, fundsFollowedSatoshis - targetSeenSatoshis);
        diagnostics.structureWarnings.push(`Reverse BTC FIFO resolved only ${this.formatBtcSatsCompact(targetSeenSatoshis)} of the requested ${this.formatBtcSatsCompact(fundsFollowedSatoshis)} target range.`);
      }

      const sourceRecords = [...reverseSourcesByKey.values()].sort((left, right) => {
        const leftTx = { hash: left.hash, date: left.date, raw: { index: 0, block_number: 0 } };
        const rightTx = { hash: right.hash, date: right.date, raw: { index: 0, block_number: 0 } };
        const txCompare = this.compareBtcTransactions(leftTx, rightTx);
        if (txCompare !== 0) return txCompare;
        return (left.outputIndex || 0) - (right.outputIndex || 0);
      });

      const sourceTransactions = sourceRecords.map((source) => {
        const followedAmount = source.followedSatoshis / 100000000;
        const leftBoundAmount = source.leftBoundsSatoshis / 100000000;
        return {
          hash: source.hash,
          date: source.date,
          dateText: source.dateText,
          fromAddress: source.fromAddress,
          toAddress: source.toAddress,
          amount: source.outputSatoshis / 100000000,
          amountText: this.formatBtcSatsCompact(source.outputSatoshis).replace(/ BTC$/, ""),
          token: "BTC",
          outputIndex: source.outputIndex,
          outputValueSatoshis: source.outputSatoshis,
          followedSatoshis: source.followedSatoshis,
          followedAmount,
          leftBoundsSatoshis: source.leftBoundsSatoshis,
          leftBoundAmount,
          rightBoundsSatoshis: source.rightBoundsSatoshis,
          rightBoundAmount: source.rightBoundsSatoshis / 100000000,
          transactionKind: "btc_reverse_fifo_source_output",
          nonContiguous: Boolean(source.nonContiguous),
          continuesAsLabel: source.continuesAsLabel,
          reverseTargetHash: source.reverseTargetHash,
          reverseTargetOutputIndex: source.reverseTargetOutputIndex,
        };
      });

      const sourceRows = sourceTransactions.map((source) => {
        const row = this.buildExitRow({
          entry,
          exit: source,
          followedAmount: source.followedAmount,
          leftBoundAmount: source.leftBoundAmount,
          token: "BTC",
        });
        return row;
      });

      const totalSourceFollowedSatoshis = sourceTransactions.reduce((sum, source) => sum + (Number(source.followedSatoshis) || 0), 0);
      diagnostics.reverseSourceCount = sourceTransactions.length;
      diagnostics.totalExitFollowedSatoshis = totalSourceFollowedSatoshis;
      diagnostics.dormantSatoshis = Math.max(0, diagnostics.reverseUnresolvedSatoshis || 0);
      diagnostics.exitCount = sourceTransactions.length;
      diagnostics.transactionOfInterestFound = true;
      diagnostics.outgoingTransactionCount = 1;
      diagnostics.relevantPriorTransactionCount = priorTransactions.length;

      if (diagnostics.reverseUnresolvedSatoshis > 0) {
        diagnostics.structureWarnings.push(`Reverse BTC FIFO could not tie ${this.formatBtcSatsCompact(diagnostics.reverseUnresolvedSatoshis)} of the selected outgoing range to a concrete prior BTC output.`);
      }
      if (sourceTransactions.length > 1) {
        diagnostics.structureWarnings.push(`Reverse BTC FIFO found ${sourceTransactions.length} prior source outputs for the selected outgoing range.`);
      }

      this.pushUniqueWarnings([
        ...diagnostics.structureWarnings,
        ...diagnostics.feeWarnings,
      ].map((warning) => `BTC reverse FIFO: ${warning}`));

      return {
        status: sourceRows.length
          ? (diagnostics.reverseUnresolvedSatoshis > 0 ? "reverse_mixed_source_with_unresolved" : "reverse_source")
          : "reverse_source_unresolved",
        analysisKind: "btc_reverse_fifo",
        solverKind: "btc_reverse_fifo_source_range",
        analysisDirection: "reverse",
        preventPathEnding: sourceRows.length === 0,
        exitTransactions: sourceTransactions,
        exitRows: sourceRows,
        dormantAmountText: diagnostics.reverseUnresolvedSatoshis > 0 ? this.formatBtcSatsCompact(diagnostics.reverseUnresolvedSatoshis) : "",
        dormantReason: diagnostics.reverseUnresolvedSatoshis > 0 ? "Reverse BTC FIFO found prior source output(s), but part of the selected outgoing range could not be tied to fetched history." : "",
        feeConsumedAmountText: "",
        pathEndingReason: sourceRows.length === 0
          ? "No prior BTC source output could be resolved. Tool 88 left the output unchanged instead of marking a path ending."
          : "",
        fundsIn: {
          ...fundsContext.fundsIn,
          amount: fundsFollowedSatoshis / 100000000,
          token: "BTC",
          source: fundsContext.originalFundsFollowedText ? "column M funds followed" : "column F / selected outgoing BTC output",
        },
        followedAmount: totalSourceFollowedSatoshis / 100000000,
        leftBoundAmount: sourceTransactions.length === 1 ? sourceTransactions[0].leftBoundAmount : null,
        relevantTransactions: [interestTx],
        btcAnalysis: diagnostics,
      };
    },

    solveBtcFifoForEntry(entry, selectedAddress, transactions = []) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const btcTransactions = this.buildBtcNormalizedTransactions(transactions);
      const fundsContext = this.getBtcSelectedRowFundsContext(entry, selectedAddress);
      const transactionOfInterestHash = this.normalizeHashKey(entry?.txHash || "");
      const interestTx = btcTransactions.find((tx) => transactionOfInterestHash && this.normalizeHashKey(tx.hash) === transactionOfInterestHash) || null;
      const entryDate = this.getEntryDate(entry);
      const interestComparable = interestTx || {
        hash: entry?.txHash || "",
        date: entryDate,
        raw: { block_number: 0, index: 0 },
      };
      const diagnostics = {
        solver: this.hopLogicType === "NEXT_CHRONOLOGICAL" ? "BTC_NEXT_OUT_WAYFINDER_SPATIALIZED_ZERO_BALANCE" : "BTC_FIFO_WAYFINDER_SPATIALIZED",
        allocationModel: "wayfinder_advanced_funds_followed_spatialized",
        analysisDirection: "forward",
        wayFinderSpatializedMode: true,
        priorBalanceSatoshis: 0,
        actualPriorBalanceSatoshis: 0,
        reconstructedPriorBalanceSatoshis: 0,
        rowPriorBalanceSatoshis: null,
        priorBalanceSource: "reconstructed_provider_history",
        inputLeftBoundsSatoshis: fundsContext.leftBoundsSatoshis,
        fundsFollowedSatoshis: fundsContext.fundsFollowedSatoshis,
        inputRightTailSatoshis: 0,
        initialDismissalSatoshis: 0,
        dismissedByPriorInputsSatoshis: 0,
        dismissedByPriorBalanceSatoshis: 0,
        dismissedByLeftBoundsSatoshis: 0,
        fullyDismissedOutputsCount: 0,
        partiallyDismissedOutputsCount: 0,
        feeConsumedSatoshis: 0,
        unbranchedMinerFeeSatoshis: 0,
        memoConsumedSatoshis: 0,
        ignoredIncomingDepositSatoshis: 0,
        ignoredIncomingDepositCount: 0,
        ignoredPostInterestDepositSatoshis: 0,
        ignoredPostInterestDepositCount: 0,
        selectedInputTotalSatoshis: 0,
        selectedOutputTotalSatoshis: 0,
        complexTransactionCount: 0,
        sameTimestampTieCount: 0,
        dormantSatoshis: 0,
        totalExitFollowedSatoshis: 0,
        exitCount: 0,
        transactionOfInterestFound: Boolean(interestTx),
        outgoingTransactionCount: 0,
        relevantPriorTransactionCount: 0,
        feeWarnings: [],
        structureWarnings: [],
      };

      if (!selectedKey) {
        diagnostics.structureWarnings.push("No selected BTC address was available for FIFO analysis.");
      }
      if (!btcTransactions.length) {
        diagnostics.structureWarnings.push("No BTC transaction rows were available for FIFO analysis after normalization.");
      }
      if (!interestTx && transactionOfInterestHash) {
        diagnostics.structureWarnings.push("The transaction of interest was not found in fetched BTC history; Tool 88 used the input row date as a fallback ordering anchor.");
      }

      let priorBalance = 0;
      btcTransactions.forEach((tx) => {
        if (interestTx && this.normalizeHashKey(tx.hash) === this.normalizeHashKey(interestTx.hash)) {
          return;
        }
        const compare = this.compareBtcTransactions(tx, interestComparable);
        const isBeforeInterest = interestTx ? compare < 0 : (entryDate ? tx.date.getTime() < entryDate.getTime() : false);
        if (!isBeforeInterest) {
          return;
        }
        const selectedNetSatoshis = this.getBtcSelectedNetSatoshisForAnalysis(tx, selectedKey);
        if (selectedNetSatoshis !== 0) {
          diagnostics.relevantPriorTransactionCount += 1;
          priorBalance += selectedNetSatoshis;
        }
      });

      if (priorBalance < 0) {
        diagnostics.structureWarnings.push(`BTC prior balance calculated below zero (${priorBalance} satoshis). Tool 88 used zero for FIFO dismissal and kept the warning in the report.`);
        priorBalance = 0;
      }

      const actualPriorBalance = priorBalance;
      const rowPriorBalance = Number(fundsContext.priorBalanceSatoshis);
      const hasRowPriorBalance = Number.isFinite(rowPriorBalance) && rowPriorBalance >= 0;
      diagnostics.actualPriorBalanceSatoshis = actualPriorBalance;
      diagnostics.reconstructedPriorBalanceSatoshis = actualPriorBalance;
      diagnostics.rowPriorBalanceSatoshis = hasRowPriorBalance ? rowPriorBalance : null;

      if (this.hopLogicType === "NEXT_CHRONOLOGICAL") {
        priorBalance = 0;
        diagnostics.priorBalanceSource = "next_chronological_zero_balance";
        if (actualPriorBalance > 0 || hasRowPriorBalance) {
          const rowText = hasRowPriorBalance ? ` Column N shows ${this.formatBtcSatsCompact(rowPriorBalance)}.` : "";
          diagnostics.structureWarnings.push(`Next chronological mode uses the WayFinder-style BTC outgoing-exit scan with prior balance forced to zero.${rowText} The reconstructed pre-interest balance was ${this.formatBtcSatsCompact(actualPriorBalance)}.`);
        }
      } else if (hasRowPriorBalance) {
        priorBalance = rowPriorBalance;
        diagnostics.priorBalanceSource = "column_n_input";
        const difference = Math.abs(rowPriorBalance - actualPriorBalance);
        const tolerance = Math.max(1, Math.round(Math.max(rowPriorBalance, actualPriorBalance, 1) * 0.000001));
        if (difference > tolerance) {
          diagnostics.structureWarnings.push(`BTC Column N prior balance is ${this.formatBtcSatsCompact(rowPriorBalance)}, while provider history reconstructed ${this.formatBtcSatsCompact(actualPriorBalance)} before the selected transaction. Tool 88 used Column N for WayFinder-aligned FIFO branch selection.`);
        }
      } else {
        diagnostics.priorBalanceSource = "reconstructed_provider_history";
      }

      const inputAmountSatoshis = Number(fundsContext.inputAmountSatoshis) || 0;
      let leftBoundsSatoshis = Math.max(0, Number(fundsContext.leftBoundsSatoshis) || 0);
      let fundsFollowedSatoshis = Math.max(0, Number(fundsContext.fundsFollowedSatoshis) || 0);
      if (inputAmountSatoshis > 0 && leftBoundsSatoshis > inputAmountSatoshis) {
        diagnostics.structureWarnings.push(`Input left bounds (${this.formatBtcSatsCompact(leftBoundsSatoshis)}) exceeded the BTC input/output amount and were clamped.`);
        leftBoundsSatoshis = inputAmountSatoshis;
      }
      if (inputAmountSatoshis > 0 && leftBoundsSatoshis + fundsFollowedSatoshis > inputAmountSatoshis) {
        const clamped = Math.max(0, inputAmountSatoshis - leftBoundsSatoshis);
        diagnostics.structureWarnings.push(`Funds followed exceeded the selected BTC output span after left bounds; Tool 88 clamped it to ${this.formatBtcSatsCompact(clamped)}.`);
        fundsFollowedSatoshis = clamped;
      }
      if (!fundsFollowedSatoshis) {
        diagnostics.structureWarnings.push("Tool 88 could not derive a positive BTC funds-followed amount from Column M, Column F, or the selected BTC output.");
      }

      const rightTailSatoshis = inputAmountSatoshis > 0
        ? Math.max(0, inputAmountSatoshis - leftBoundsSatoshis - fundsFollowedSatoshis)
        : 0;
      diagnostics.priorBalanceSatoshis = priorBalance;
      diagnostics.inputLeftBoundsSatoshis = leftBoundsSatoshis;
      diagnostics.fundsFollowedSatoshis = fundsFollowedSatoshis;
      diagnostics.inputRightTailSatoshis = rightTailSatoshis;
      diagnostics.initialDismissalSatoshis = priorBalance + leftBoundsSatoshis;

      const afterInterest = btcTransactions
        .filter((tx) => {
          if (this.normalizeHashKey(tx.hash) === transactionOfInterestHash) return false;
          const compare = this.compareBtcTransactions(tx, interestComparable);
          return interestTx ? compare > 0 : (entryDate ? tx.date.getTime() >= entryDate.getTime() : true);
        })
        .sort((left, right) => this.compareBtcTransactions(left, right));

      const outgoingTransactions = [];
      afterInterest.forEach((tx) => {
        const selectedInputSatoshis = this.getBtcSelectedInputSatoshisForAnalysis(tx, selectedKey);
        const selectedOutputSatoshis = this.getBtcTxOutputSatoshis(tx, selectedKey);
        const isOutgoingForSelected = selectedInputSatoshis > 0 || this.isBtcTransactionOutgoingForSelected(tx, selectedKey);
        if (isOutgoingForSelected) {
          outgoingTransactions.push(tx);
          return;
        }
        if (selectedOutputSatoshis > 0 || this.isBtcTransactionIncomingForSelected(tx, selectedKey)) {
          const incomingSatoshis = selectedOutputSatoshis || Math.max(0, this.getBtcSelectedNetSatoshisForAnalysis(tx, selectedKey));
          diagnostics.ignoredIncomingDepositCount += 1;
          diagnostics.ignoredIncomingDepositSatoshis += incomingSatoshis;
          diagnostics.ignoredPostInterestDepositCount += 1;
          diagnostics.ignoredPostInterestDepositSatoshis += incomingSatoshis;
        }
      });

      if (diagnostics.ignoredPostInterestDepositCount > 0) {
        diagnostics.structureWarnings.push(`BTC forward FIFO ignored ${diagnostics.ignoredPostInterestDepositCount} later incoming deposit transaction${diagnostics.ignoredPostInterestDepositCount === 1 ? "" : "s"} (${this.formatBtcSatsCompact(diagnostics.ignoredPostInterestDepositSatoshis)}) while resolving the selected branch, matching WayFinder's AdvancedFundsFollowed outgoing-exit phase.`);
      }

      const exitsByKey = new Map();
      // Retain the transaction of interest alongside outgoing transactions so
      // Case Buddy snapshots can always hydrate the raw root's first BTC input
      // address and selected output details after the trace result is committed.
      const relevantTransactions = interestTx ? [interestTx] : [];
      let remainingFunds = fundsFollowedSatoshis;
      const dismissalQueue = [
        { label: "prior_balance", sats: priorBalance },
        { label: "input_left_bounds", sats: leftBoundsSatoshis },
      ].filter((segment) => segment.sats > 0);

      const consumeDismissal = (capacitySatoshis) => {
        let remaining = Math.max(0, Math.round(Number(capacitySatoshis) || 0));
        const consumed = { total: 0, priorBalance: 0, leftBounds: 0 };
        while (remaining > 0 && dismissalQueue.length) {
          const segment = dismissalQueue[0];
          const take = Math.min(remaining, Math.max(0, Math.round(Number(segment.sats) || 0)));
          if (!take) {
            dismissalQueue.shift();
            continue;
          }
          consumed.total += take;
          if (segment.label === "prior_balance") consumed.priorBalance += take;
          if (segment.label === "input_left_bounds") consumed.leftBounds += take;
          segment.sats -= take;
          remaining -= take;
          if (segment.sats <= 0) dismissalQueue.shift();
        }
        return consumed;
      };

      const getPriorInputsBeforeFirstSelectedInput = (tx) => {
        let prior = 0;
        const inputs = this.normalizeBtcIo(tx.btcInputs || [], "input");
        for (const input of inputs) {
          const inputKey = this.normalizeAddressKey(input.address);
          if (inputKey && inputKey === selectedKey) {
            return prior;
          }
          prior += Math.max(0, Number(input.valueSatoshis) || 0);
        }
        // If the selected input cannot be identified, do not dismiss every output as if
        // all inputs came before the followed input. WayFinder's spatialized BTC pass
        // skips the prior-input adjustment when the followed input index is unknown.
        if (this.isBtcTransactionOutgoingForSelected(tx, selectedKey)) {
          diagnostics.structureWarnings.push(`BTC transaction ${this.shortenMiddle(tx.hash, 10, 8)} was outgoing for the selected address, but Tool 88 could not identify the selected input index; prior-input dismissal was skipped for this transaction.`);
        }
        return 0;
      };

      const addExitPortion = (tx, output, followedSats, leftBoundWithinOutput, labels = []) => {
        const key = `${tx.hash}|${output.order}|${this.normalizeAddressKey(output.address)}`;
        if (!exitsByKey.has(key)) {
          exitsByKey.set(key, {
            hash: tx.hash,
            date: tx.date,
            dateText: tx.dateText,
            blockNumber: tx.raw?.block_number || tx.raw?.blockNumber || "",
            transactionIndex: tx.raw?.index || tx.raw?.transactionIndex || "",
            fromAddress: selectedAddress,
            toAddress: output.address,
            outputSatoshis: output.valueSatoshis,
            followedSatoshis: 0,
            leftBoundsSatoshis: leftBoundWithinOutput,
            rightBoundsSatoshis: leftBoundWithinOutput,
            outputIndex: output.order,
            token: "BTC",
            sourceLabels: new Set(),
            nonContiguous: false,
          });
        }
        const exit = exitsByKey.get(key);
        if (exit.followedSatoshis > 0 && leftBoundWithinOutput > exit.rightBoundsSatoshis) {
          exit.nonContiguous = true;
          diagnostics.structureWarnings.push(`BTC output ${tx.hash} #${output.order} received non-contiguous followed slices; review left bounds manually.`);
        }
        if (exit.followedSatoshis === 0) {
          exit.leftBoundsSatoshis = leftBoundWithinOutput;
        }
        exit.followedSatoshis += followedSats;
        exit.rightBoundsSatoshis = Math.max(exit.rightBoundsSatoshis, leftBoundWithinOutput + followedSats);
        (Array.isArray(labels) ? labels : [labels]).forEach((label) => {
          if (label) exit.sourceLabels.add(label);
        });
      };

      for (const tx of outgoingTransactions) {
        if (remainingFunds <= 0) break;
        const selectedInputSatoshis = this.getBtcSelectedInputSatoshisForAnalysis(tx, selectedKey);
        const selectedOutputSatoshis = this.getBtcTxOutputSatoshis(tx, selectedKey);
        if (selectedInputSatoshis <= 0 && !this.isBtcTransactionOutgoingForSelected(tx, selectedKey)) continue;

        relevantTransactions.push(tx);
        diagnostics.selectedInputTotalSatoshis += selectedInputSatoshis;
        diagnostics.selectedOutputTotalSatoshis += selectedOutputSatoshis;
        diagnostics.outgoingTransactionCount += 1;

        const hasConcreteSelectedInput = tx.btcInputs.some((input) => this.normalizeAddressKey(input.address) === selectedKey);
        const mixedInputs = hasConcreteSelectedInput && tx.btcInputs.some((input) => {
          const key = this.normalizeAddressKey(input.address);
          return key && key !== selectedKey;
        });
        if (tx.btcInputs.length > 1 || tx.btcOutputs.length > 2 || mixedInputs) {
          diagnostics.complexTransactionCount += 1;
        }
        if (tx.btcInputs.length > 10 || tx.btcOutputs.length > 10) {
          diagnostics.structureWarnings.push(`Complex BTC transaction ${this.shortenMiddle(tx.hash, 10, 8)} has ${tx.btcInputs.length} inputs and ${tx.btcOutputs.length} outputs. Tool 88 used WayFinder-style outgoing-output dismissal and highlighted the result for review.`);
        } else if (mixedInputs && tx.btcOutputs.length > 2) {
          diagnostics.structureWarnings.push(`BTC transaction ${this.shortenMiddle(tx.hash, 10, 8)} mixes selected-address inputs with other input addresses and has more than two outputs; Tool 88 dismissed earlier indexed inputs before assigning followed exits.`);
        }
        if (tx.date && interestComparable.date && tx.date.getTime() === interestComparable.date.getTime()) {
          diagnostics.sameTimestampTieCount += 1;
        }

        let priorInputDismissalRemaining = getPriorInputsBeforeFirstSelectedInput(tx);
        const priorInputDismissalOriginal = priorInputDismissalRemaining;

        tx.btcOutputs.forEach((output) => {
          if (remainingFunds <= 0) return;
          const outputKey = this.normalizeAddressKey(output.address);
          const outputSats = Math.max(0, Number(output.valueSatoshis) || 0);
          if (!outputSats) return;
          if (outputKey === selectedKey) return;

          const isMemoOutput = output.isOpReturn || this.toText(output.address).toUpperCase() === "MESSAGE" || this.toText(output.address).toUpperCase() === "OP_RETURN";
          let dismissed = 0;
          const globalDismissed = consumeDismissal(outputSats);
          dismissed += globalDismissed.total;
          diagnostics.dismissedByPriorBalanceSatoshis += globalDismissed.priorBalance;
          diagnostics.dismissedByLeftBoundsSatoshis += globalDismissed.leftBounds;

          if (priorInputDismissalRemaining > 0 && dismissed < outputSats) {
            const takePriorInputs = Math.min(priorInputDismissalRemaining, outputSats - dismissed);
            dismissed += takePriorInputs;
            priorInputDismissalRemaining -= takePriorInputs;
            diagnostics.dismissedByPriorInputsSatoshis += takePriorInputs;
          }

          if (dismissed >= outputSats) {
            diagnostics.fullyDismissedOutputsCount += 1;
            return;
          }
          if (dismissed > 0) diagnostics.partiallyDismissedOutputsCount += 1;

          const eligible = outputSats - dismissed;
          const takeFollowed = Math.min(remainingFunds, eligible);
          if (takeFollowed <= 0) return;

          if (isMemoOutput) {
            diagnostics.memoConsumedSatoshis += takeFollowed;
            remainingFunds -= takeFollowed;
            diagnostics.structureWarnings.push(`Followed BTC overlapped OP_RETURN/message output ${tx.hash} #${output.order}; no exit row was created for that memo output.`);
            return;
          }

          const labels = [];
          if (globalDismissed.priorBalance) labels.push("prior_balance_dismissed");
          if (globalDismissed.leftBounds) labels.push("left_bounds_dismissed");
          if (priorInputDismissalOriginal > 0) labels.push("prior_inputs_dismissed");
          addExitPortion(tx, output, takeFollowed, dismissed, labels.length ? labels : ["wayfinder_spatialized_fifo"]);
          remainingFunds -= takeFollowed;
        });

        const feeSatoshis = this.getBtcFeeSatoshis(tx);
        if (feeSatoshis > 0 && remainingFunds > 0) {
          const inputs = this.normalizeBtcIo(tx.btcInputs || [], "input").filter(input => Number(input.valueSatoshis) > 0);
          const selectedInputs = inputs.filter(input => this.normalizeAddressKey(input.address) === selectedKey);
          const selectedParticipationIsKnown = selectedInputs.length > 0 || this.isBtcTransactionOutgoingForSelected(tx, selectedKey);
          if (selectedParticipationIsKnown) {
            // Treat the fee as a virtual tail output. The same dismissal queues that
            // precede ordinary outputs also precede this tail, so mixed-input BTC
            // transactions remain spatially ordered instead of assigning the whole
            // fee to the selected funds merely because they participated.
            let feeCapacity = feeSatoshis;
            const globalDismissed = consumeDismissal(feeCapacity);
            feeCapacity -= globalDismissed.total;
            diagnostics.dismissedByPriorBalanceSatoshis += globalDismissed.priorBalance;
            diagnostics.dismissedByLeftBoundsSatoshis += globalDismissed.leftBounds;
            if (priorInputDismissalRemaining > 0 && feeCapacity > 0) {
              const takePriorInputs = Math.min(priorInputDismissalRemaining, feeCapacity);
              priorInputDismissalRemaining -= takePriorInputs;
              feeCapacity -= takePriorInputs;
              diagnostics.dismissedByPriorInputsSatoshis += takePriorInputs;
            }
            const followedFee = Math.min(remainingFunds, Math.max(0, feeCapacity));
            if (followedFee > 0) {
              remainingFunds -= followedFee;
              diagnostics.feeConsumedSatoshis += followedFee;
              diagnostics.unbranchedMinerFeeSatoshis += followedFee;
            }
          }
        }
      }

      if (diagnostics.feeConsumedSatoshis > 0) {
        diagnostics.feeWarnings.push(`BTC FIFO consumed ${this.formatBtcSatsCompact(diagnostics.feeConsumedSatoshis)} of followed funds as miner fee instead of reporting that amount as dormant.`);
      }
      if (diagnostics.complexTransactionCount > 0) {
        diagnostics.structureWarnings.push(`${diagnostics.complexTransactionCount} BTC transaction${diagnostics.complexTransactionCount === 1 ? "" : "s"} in the analysis path had multiple inputs or more than two outputs; Tool 88 used WayFinder-style indexed dismissal rather than allowing later incoming deposits to jump the queue.`);
      }
      if (diagnostics.sameTimestampTieCount > 0) {
        diagnostics.structureWarnings.push(`${diagnostics.sameTimestampTieCount} BTC outgoing transaction${diagnostics.sameTimestampTieCount === 1 ? "" : "s"} had the same timestamp as the transaction of interest; Tool 88 used block/index/hash ordering where available.`);
      }
      if (diagnostics.dismissedByLeftBoundsSatoshis > 0) {
        diagnostics.structureWarnings.push(`BTC FIFO applied ${this.formatBtcSatsCompact(diagnostics.dismissedByLeftBoundsSatoshis)} of incoming left bounds before assigning followed exits.`);
      }
      if (diagnostics.dismissedByPriorInputsSatoshis > 0) {
        diagnostics.structureWarnings.push(`BTC FIFO dismissed ${this.formatBtcSatsCompact(diagnostics.dismissedByPriorInputsSatoshis)} from earlier-indexed non-selected inputs before assigning selected-address followed exits.`);
      }

      const exitRecords = [...exitsByKey.values()].sort((left, right) => {
        const txCompare = this.compareBtcTransactions(left, right);
        if (txCompare !== 0) return txCompare;
        return (left.outputIndex || 0) - (right.outputIndex || 0);
      });
      const exitTransactions = exitRecords.map((exit) => {
        const followedAmount = exit.followedSatoshis / 100000000;
        const leftBoundAmount = exit.leftBoundsSatoshis / 100000000;
        const balanceBeforeSatoshis = this.getBtcOutputAddressPriorBalanceSatoshis(btcTransactions, exit);
        return {
          hash: exit.hash,
          date: exit.date,
          dateText: exit.dateText,
          fromAddress: exit.fromAddress,
          toAddress: exit.toAddress,
          amount: exit.outputSatoshis / 100000000,
          amountText: this.formatBtcSatsCompact(exit.outputSatoshis).replace(/ BTC$/, ""),
          token: "BTC",
          outputIndex: exit.outputIndex,
          outputValueSatoshis: exit.outputSatoshis,
          followedSatoshis: exit.followedSatoshis,
          followedAmount,
          leftBoundsSatoshis: exit.leftBoundsSatoshis,
          leftBoundAmount,
          rightBoundsSatoshis: exit.rightBoundsSatoshis,
          rightBoundAmount: exit.rightBoundsSatoshis / 100000000,
          priorBalanceSatoshis: balanceBeforeSatoshis,
          priorBalanceBeforeTransactionSatoshis: balanceBeforeSatoshis,
          priorBalanceBeforeTransaction: balanceBeforeSatoshis / 100000000,
          transactionKind: "btc_fifo_exit_output",
          nonContiguous: Boolean(exit.nonContiguous),
          continuesAsLabel: `${exit.hash} output ${exit.outputIndex}`,
        };
      });

      const exitRows = exitTransactions.map((exit) => {
        const row = this.buildExitRow({
          entry,
          exit,
          followedAmount: exit.followedAmount,
          leftBoundAmount: exit.leftBoundAmount,
          token: "BTC",
        });
        return row;
      });

      const dormantSatoshis = Math.max(0, remainingFunds);
      const totalExitFollowedSatoshis = exitTransactions.reduce((sum, exit) => sum + (Number(exit.followedSatoshis) || 0), 0);
      diagnostics.dormantSatoshis = dormantSatoshis;
      diagnostics.totalExitFollowedSatoshis = totalExitFollowedSatoshis;
      diagnostics.exitCount = exitTransactions.length;

      if (exitRows.length && dormantSatoshis > 0) {
        diagnostics.structureWarnings.push(`BTC FIFO found both exits and dormant funds. Dormant followed amount still at the address or outside fetched outgoing coverage: ${this.formatBtcSatsCompact(dormantSatoshis)}.`);
      }

      this.pushUniqueWarnings([
        ...diagnostics.structureWarnings,
        ...diagnostics.feeWarnings,
      ].map((warning) => `BTC FIFO: ${warning}`));

      return {
        status: exitRows.length
          ? (dormantSatoshis > 0 ? "mixed_exit_with_dormant" : "exit")
          : (diagnostics.feeConsumedSatoshis > 0 && dormantSatoshis === 0 ? "fee_consumed" : "dormant"),
        analysisKind: "btc_fifo",
        solverKind: "btc_fifo_wayfinder_spatialized",
        analysisDirection: "forward",
        exitTransactions,
        exitRows,
        dormantAmountText: dormantSatoshis > 0 ? this.formatBtcSatsCompact(dormantSatoshis) : "",
        dormantReason: dormantSatoshis > 0 && exitRows.length ? "BTC FIFO found one or more exits, but some followed BTC remains at the selected address or outside fetched outgoing coverage." : "",
        feeConsumedAmountText: diagnostics.feeConsumedSatoshis > 0 ? this.formatBtcSatsCompact(diagnostics.feeConsumedSatoshis) : "",
        pathEndingReason: !exitRows.length
          ? (diagnostics.feeConsumedSatoshis > 0 && dormantSatoshis === 0
            ? `The followed BTC amount was consumed as miner fee (${this.formatBtcSatsCompact(diagnostics.feeConsumedSatoshis)}); no spendable next-hop output remained.`
            : "No qualifying outgoing BTC output exit was found after WayFinder-style FIFO allocation.")
          : "",
        fundsIn: {
          ...fundsContext.fundsIn,
          amount: fundsFollowedSatoshis / 100000000,
          token: "BTC",
          source: fundsContext.originalFundsFollowedText ? "column M funds followed" : "column F / selected BTC output",
        },
        followedAmount: totalExitFollowedSatoshis / 100000000,
        leftBoundAmount: exitTransactions.length === 1 ? exitTransactions[0].leftBoundAmount : null,
        relevantTransactions,
        btcAnalysis: diagnostics,
      };
    },

    applyTraceAnalysisToInspectorRows(rows = [], solution = null) {
      if (!solution || !Array.isArray(solution.exitTransactions) || !solution.exitTransactions.length) {
        return rows;
      }
      const btcExitMap = new Map();
      const genericExitMap = new Map();
      solution.exitTransactions.forEach((exit, index) => {
        if (exit.outputIndex !== undefined && exit.outputIndex !== null) {
          btcExitMap.set(`${this.normalizeHashKey(exit.hash)}|${Number(exit.outputIndex)}`, { exit, index });
        }
        const genericKey = [
          this.normalizeHashKey(exit.hash),
          this.normalizeAddressKey(exit.fromAddress),
          this.normalizeAddressKey(exit.toAddress),
          this.formatAmount(exit.followedAmount ?? exit.amount),
        ].join("|");
        genericExitMap.set(genericKey, { exit, index });
        const fullAmountKey = [
          this.normalizeHashKey(exit.hash),
          this.normalizeAddressKey(exit.fromAddress),
          this.normalizeAddressKey(exit.toAddress),
          this.formatAmount(exit.amount),
        ].join("|");
        genericExitMap.set(fullAmountKey, { exit, index });
      });
      return (rows || []).map((row) => {
        const btcOutputs = this.normalizeBtcIo(row.btcOutputs || row.raw?.btc_outputs || [], "output");
        let matching = btcOutputs
          .map((output) => btcExitMap.get(`${this.normalizeHashKey(row.hash)}|${Number(output.order)}`))
          .find(Boolean);
        if (!matching) {
          const genericKey = [
            this.normalizeHashKey(row.hash),
            this.normalizeAddressKey(row.fromAddress),
            this.normalizeAddressKey(row.toAddress),
            this.formatAmount(row.followedAmount ?? row.amount),
          ].join("|");
          matching = genericExitMap.get(genericKey) || genericExitMap.get([
            this.normalizeHashKey(row.hash),
            this.normalizeAddressKey(row.fromAddress),
            this.normalizeAddressKey(row.toAddress),
            this.formatAmount(row.amount),
          ].join("|"));
        }
        if (!matching) {
          return row;
        }
        const isReverseSource = this.toText(matching.exit.transactionKind).includes("reverse_fifo_source");
        const isBtcLike = matching.exit.outputIndex !== undefined && matching.exit.outputIndex !== null;
        const role = isReverseSource ? "source" : "exit";
        const amountText = isBtcLike && Number.isFinite(matching.exit.followedSatoshis)
          ? this.formatBtcSatsCompact(matching.exit.followedSatoshis)
          : this.formatFunds(matching.exit.followedAmount, matching.exit.token || solution.fundsIn?.token);
        const leftText = isBtcLike && Number.isFinite(matching.exit.leftBoundsSatoshis)
          ? this.formatBtcSatsCompact(matching.exit.leftBoundsSatoshis)
          : this.formatFunds(matching.exit.leftBoundAmount || 0, matching.exit.token || solution.fundsIn?.token);
        const indexText = isBtcLike ? ` on output ${matching.exit.outputIndex}` : "";
        return {
          ...row,
          isExit: true,
          analysisRole: isReverseSource ? "Tool 88 reverse source" : "Tool 88 exit",
          analysisDetails: `${role.charAt(0).toUpperCase()}${role.slice(1)} ${matching.index + 1}: follows ${amountText} with left bounds ${leftText}${indexText}.`,
        };
      });
    },

    annotateEntryWithDormantRemainder(baseRows, entry, dormantAmountText, reason, feeConsumedAmountText = "") {
      const outputRows = baseRows.map((row) => this.cloneRowPreservingValues(row));
      const bounds = this.findEntryBoundsInRows(outputRows, entry);
      const entryStartIndex = bounds?.startRowIndex ?? Math.max(0, Number(entry?.startRowIndex || 0));
      const entryRow = outputRows[entryStartIndex] || this.makeBlankRow();
      if (dormantAmountText) {
        const dormantNote = this.buildTool88DormantFundsNote(dormantAmountText, false);
        if (dormantNote) {
          entryRow[COL.SYSTEM_NOTES] = this.appendSentenceNote(entryRow[COL.SYSTEM_NOTES], dormantNote);
          this.appendTool88NoteToRow(entryRow, dormantNote);
        }
        if (reason) this.appendTool88NoteToRow(entryRow, `Tool 88 dormant analysis: ${reason}`);
      }
      if (feeConsumedAmountText) {
        this.appendTool88NoteToRow(
          entryRow,
          `Tool 88: ${feeConsumedAmountText} appears consumed by network/gas or miner fees under Tool 88 FIFO allocation; no fee branch was created.`
        );
      }
      // Keep Column Q as the valid continuation list for partial dormant cases.
      // The dormant remainder belongs in Column J; it must not become a fake Continues-As token.
      outputRows[entryStartIndex] = entryRow;
      return outputRows;
    },

    shouldUseAccountFifoSolver(entry, selectedAddress, transactions = []) {
      if (this.isBtcHopContext(entry, selectedAddress, transactions)) {
        return false;
      }
      if (this.hopLogicType === "FIFO") {
        return true;
      }
      if (this.hopLogicType === "NEXT_CHRONOLOGICAL") {
        return true;
      }
      if (this.hopLogicType === "LIFO") {
        return true;
      }
      return false;
    },

    getEntryLeftBoundsAmount(entry, selectedAddress) {
      const candidateRows = [];
      const selectedRow = this.getSelectedTargetRow(entry, selectedAddress);
      if (selectedRow) candidateRows.push(selectedRow);
      const firstRow = entry?.rows?.find?.(({ row }) => this.rowHasAnyData(row))?.row || null;
      if (firstRow && !candidateRows.includes(firstRow)) candidateRows.push(firstRow);
      (entry?.rows || []).forEach(({ row }) => {
        if (row && !candidateRows.includes(row)) candidateRows.push(row);
      });
      let zeroFallback = 0;
      for (const row of candidateRows) {
        const swapConversion = this.getSwapRowConversionContext(row);
        if (swapConversion?.canConvert && Number.isFinite(swapConversion.leftAfter)) {
          const value = Math.max(0, swapConversion.leftAfter);
          if (value > 0) return value;
          zeroFallback = value;
          continue;
        }
        const plain = this.getPlainRowLeftBoundsAmount(row);
        if (Number.isFinite(plain)) {
          const value = Math.max(0, plain);
          if (value > 0) return value;
          zeroFallback = value;
        }
      }
      return zeroFallback;
    },

    getEntryPriorBalanceAmount(entry, selectedAddress) {
      const candidateRows = [];
      const selectedRow = this.getSelectedTargetRow(entry, selectedAddress);
      if (selectedRow) candidateRows.push(selectedRow);
      if (entry?.rows?.length) {
        candidateRows.push(entry.rows[0]?.row || []);
      }
      for (const row of candidateRows) {
        const parsed = this.parseNumberLike(row?.[COL.PRIOR_BALANCE]);
        if (Number.isFinite(parsed) && parsed >= 0) {
          return parsed;
        }
      }
      return null;
    },

    buildAccountFifoContext(entry, selectedAddress) {
      const fundsIn = this.deriveFundsIn(entry, selectedAddress);
      const countContext = this.inferCountRequestContext(entry, selectedAddress);
      const row = this.getSelectedTargetRow(entry, selectedAddress) || [];
      const tokenSymbol = this.toText(fundsIn.token || row?.[COL.TOKEN] || countContext.token_symbol || countContext.native_symbol).toUpperCase();
      const nativeSymbol = this.toText(countContext.native_symbol || "").toUpperCase();
      const countKind = this.toText(countContext.count_kind || this.countLookupResult?.countKind || this.countLookupResult?.count_kind).toLowerCase();
      const isNative = countKind.includes("native") || (nativeSymbol && tokenSymbol === nativeSymbol) || (!tokenSymbol && Boolean(nativeSymbol));
      const tokenResolution = isNative
        ? null
        : this.resolveTokenForContext(
            tokenSymbol,
            countContext.chain_id || this.countLookupResult?.chainId || this.countLookupResult?.chain_id,
            countContext.chain_name || this.countLookupResult?.chainName || this.countLookupResult?.chain_name,
            countContext.network_family || this.countLookupResult?.networkFamily || this.countLookupResult?.network_family,
            countContext.token_contract || countContext.contract_address || ""
          );
      const expectedContract = normalizeTokenContractText(
        countContext.token_contract ||
          countContext.contract_address ||
          tokenResolution?.contractAddress ||
          ""
      );
      const rowAmount = this.parseNumberLike(row?.[COL.AMOUNT]);
      let fundsAmount = Number.isFinite(fundsIn.amount) ? Math.max(0, Number(fundsIn.amount)) : (Number.isFinite(rowAmount) ? Math.max(0, rowAmount) : 0);
      let leftBoundsAmount = this.getEntryLeftBoundsAmount(entry, selectedAddress);
      const rowAmountCap = Number.isFinite(rowAmount) && rowAmount > 0 ? rowAmount : null;
      if (rowAmountCap !== null && leftBoundsAmount > rowAmountCap) {
        leftBoundsAmount = rowAmountCap;
      }
      if (rowAmountCap !== null) {
        const availableAfterLeftBounds = Math.max(0, rowAmountCap - leftBoundsAmount);
        if (fundsAmount > availableAfterLeftBounds) {
          fundsAmount = availableAfterLeftBounds;
        }
      }
      const maxAmount = Math.max(rowAmountCap || 0, fundsAmount + leftBoundsAmount);
      const actualToken = tokenSymbol || nativeSymbol || fundsIn.token || "TOKEN";
      const familyForContract = this.toText(countContext.network_family || this.countLookupResult?.networkFamily || this.countLookupResult?.network_family || "").toLowerCase();
      const requiresTokenContract = !isNative && ["evm", "tron"].includes(familyForContract);
      return {
        fundsIn: {
          ...fundsIn,
          amount: fundsAmount,
          token: actualToken,
          source: fundsIn.source || (Number.isFinite(rowAmount) ? "column F amount" : "unknown"),
        },
        token: actualToken,
        nativeSymbol,
        isNative,
        countKind,
        expectedContract,
        requiresTokenContract,
        tokenResolution,
        tokenRegistryStatus: tokenResolution?.status || "",
        tokenRegistrySummary: tokenResolution?.summary || "",
        leftBoundsAmount,
        rightBoundsAmount: leftBoundsAmount + fundsAmount,
        maxAmount,
        priorBalanceFromInput: this.getEntryPriorBalanceAmount(entry, selectedAddress),
        chainId: countContext.chain_id || entry?.chainId || "",
        chainName: countContext.chain_name || entry?.chainName || "",
        chainLabel: countContext.chain_label || countContext.chain_name || entry?.chainName || "",
        networkFamily: countContext.network_family || "",
      };
    },

    getTraceRowTokenDecimals(tx = {}) {
      const contractForLookup = tx.tokenContractAddress || tx.contractAddress || tx.raw?.contractAddress || tx.raw?.contract_address || tx.raw?.address || "";
      const override = this.getTokenDecimalOverrideByContract(contractForLookup);
      if (override && Number.isFinite(Number(override.decimals))) {
        return Math.trunc(Number(override.decimals));
      }
      const candidates = [
        tx.tokenDecimal,
        tx.token_decimals,
        tx.decimals,
        tx.raw?.tokenDecimal,
        tx.raw?.token_decimal,
        tx.raw?.token_decimals,
        tx.raw?.tokenInfo?.tokenDecimal,
        tx.raw?.tokenInfo?.decimals,
        tx.raw?.token_info?.tokenDecimal,
        tx.raw?.token_info?.decimals,
        tx.raw?.decimals,
      ];
      for (const candidate of candidates) {
        const number = Number(candidate);
        if (Number.isFinite(number) && number >= 0 && number <= 36) {
          return Math.trunc(number);
        }
      }
      const registryToken = contractForLookup ? this.lookupTokenByContract(contractForLookup) : null;
      const registryDecimals = Number(registryToken?.decimals);
      return Number.isFinite(registryDecimals) ? Math.trunc(registryDecimals) : null;
    },

    getAccountTraceAmount(tx = {}) {
      let amount = Number(tx.amount);
      const raw = tx.raw || {};
      const rawValueText = this.toText(
        raw.value
          ?? raw.quant
          ?? raw.amount_raw
          ?? raw.raw_amount
          ?? raw.quantity_raw
          ?? raw.value_raw
          ?? raw.token_value
          ?? raw.tokenValue
      );
      const hasRawInteger = /^\d+$/.test(rawValueText);
      const decimals = this.getTraceRowTokenDecimals(tx);
      if (hasRawInteger && Number.isFinite(decimals)) {
        const rawNumber = Number(rawValueText);
        if (Number.isFinite(rawNumber) && rawNumber > 0) {
          const divisor = 10 ** decimals;
          const converted = rawNumber / divisor;
          // Prefer atomic token values when available. Provider formatted values are often
          // rounded or pre-normalized differently, which can drift FIFO left-bounds by cents.
          if (Number.isFinite(converted) && converted > 0) {
            return Math.max(0, converted);
          }
        }
      }

      const formattedCandidates = [raw.amount_formatted, raw.value_formatted, raw.formatted_amount, raw.amountFormatted, raw.valueFormatted];
      for (const candidate of formattedCandidates) {
        const parsed = this.parseNumberLike(candidate);
        if (Number.isFinite(parsed)) {
          return Math.max(0, parsed);
        }
      }

      if (Number.isFinite(amount)) {
        return Math.max(0, amount);
      }
      return 0;
    },

    getAccountTraceFeeAmount(tx = {}, context = {}) {
      if (!context?.isNative) {
        return 0;
      }
      const raw = tx.raw || {};
      const directCandidates = [raw.fee_amount, raw.feeAmount, raw.fee, raw.gas_fee, raw.gasFee, raw.network_fee, raw.networkFee];
      for (const candidate of directCandidates) {
        const parsed = this.parseNumberLike(candidate);
        if (!Number.isFinite(parsed) || parsed <= 0) continue;
        if (this.toText(context.networkFamily).toLowerCase() === "tron" || context.nativeSymbol === "TRX") {
          return parsed > 1000 ? parsed / 1000000 : parsed;
        }
        return parsed > 1000000 ? parsed / 1e18 : parsed;
      }

      const gasUsed = this.parseNumberLike(raw.gasUsed ?? raw.gas_used ?? raw.receipt_gas_used);
      const gasPrice = this.parseNumberLike(raw.gasPrice ?? raw.gas_price ?? raw.effectiveGasPrice ?? raw.effective_gas_price);
      if (Number.isFinite(gasUsed) && Number.isFinite(gasPrice) && gasUsed > 0 && gasPrice > 0) {
        return (gasUsed * gasPrice) / 1e18;
      }
      return 0;
    },

    accountTransactionMatchesAsset(tx = {}, context = {}) {
      if (!tx.hash || !tx.date || tx.failedOrReverted || tx.analysisEligible === false || tx.nonBalanceAffecting) {
        return false;
      }
      const amount = this.getAccountTraceAmount(tx);
      const feeAmount = this.getAccountTraceFeeAmount(tx, context);
      if (amount <= 0 && feeAmount <= 0) {
        return false;
      }

      if (context.isNative) {
        const contract = normalizeTokenContractText(tx.tokenContractAddress || tx.contractAddress);
        if (contract) {
          return false;
        }
        const actual = this.toText(tx.token || tx.raw?.native_symbol || tx.raw?.token_symbol).toUpperCase().replace(/\s+/g, "");
        const expected = this.toText(context.token || context.nativeSymbol).toUpperCase().replace(/\s+/g, "");
        return !actual || !expected || actual === expected || (expected === "MATIC" && actual === "POL") || (expected === "POL" && actual === "MATIC");
      }

      const txContract = normalizeTokenContractText(tx.tokenContractAddress || tx.contractAddress);
      if (context.expectedContract) {
        return txContract === context.expectedContract;
      }
      if (context.requiresTokenContract) {
        return false;
      }
      return this.providerTransactionMatchesFollowedToken(tx, context.token);
    },

    accountTransactionEligibleForFifo(tx = {}) {
      return Boolean(
        tx &&
          tx.hash &&
          !tx.failedOrReverted &&
          tx.analysisEligible !== false &&
          !tx.nonBalanceAffecting
      );
    },

    normalizeAccountTransactionsForFifo(transactions = [], context = {}, diagnostics = null) {
      const normalized = (Array.isArray(transactions) ? transactions : [])
        .map((tx) => this.normalizeProviderTransaction(tx));

      if (diagnostics && typeof diagnostics === "object") {
        diagnostics.providerRowsReviewed = normalized.length;
        diagnostics.failedOrRevertedRowsExcluded = normalized.filter((tx) => tx.failedOrReverted).length;
        diagnostics.nonBalanceRowsExcluded = normalized.filter((tx) => !tx.failedOrReverted && tx.nonBalanceAffecting).length;
        diagnostics.ineligibleRowsExcluded = normalized.filter((tx) => !tx.failedOrReverted && !tx.nonBalanceAffecting && tx.analysisEligible === false).length;
      }

      const rows = normalized
        .filter((tx) => this.accountTransactionEligibleForFifo(tx))
        .filter((tx) => this.accountTransactionMatchesAsset(tx, context))
        .map((tx) => ({
          ...tx,
          amount: this.getAccountTraceAmount(tx),
          feeAmount: this.getAccountTraceFeeAmount(tx, context),
          token: context.token || tx.token,
          tokenContractAddress: tx.tokenContractAddress || tx.contractAddress || context.expectedContract || "",
        }));

      rows.sort((left, right) => this.compareAccountTransactions(left, right));
      return rows;
    },

    compareAccountTransactions(left = {}, right = {}) {
      const leftTime = left.date ? left.date.getTime() : 0;
      const rightTime = right.date ? right.date.getTime() : 0;
      if (leftTime !== rightTime) return leftTime - rightTime;
      const leftBlock = Number(left.raw?.blockNumber ?? left.raw?.block_number ?? left.blockNumber ?? 0);
      const rightBlock = Number(right.raw?.blockNumber ?? right.raw?.block_number ?? right.blockNumber ?? 0);
      if (Number.isFinite(leftBlock) && Number.isFinite(rightBlock) && leftBlock !== rightBlock) return leftBlock - rightBlock;
      const leftTxIndex = Number(left.raw?.transactionIndex ?? left.raw?.transaction_index ?? left.raw?.txIndex ?? left.raw?.tx_index ?? left.transactionIndex ?? 0);
      const rightTxIndex = Number(right.raw?.transactionIndex ?? right.raw?.transaction_index ?? right.raw?.txIndex ?? right.raw?.tx_index ?? right.transactionIndex ?? 0);
      if (Number.isFinite(leftTxIndex) && Number.isFinite(rightTxIndex) && leftTxIndex !== rightTxIndex) return leftTxIndex - rightTxIndex;
      const leftLogIndex = Number(left.raw?.logIndex ?? left.raw?.log_index ?? left.raw?.eventIndex ?? left.raw?.event_index ?? left.raw?.event_index_in_tx ?? left.logIndex ?? 0);
      const rightLogIndex = Number(right.raw?.logIndex ?? right.raw?.log_index ?? right.raw?.eventIndex ?? right.raw?.event_index ?? right.raw?.event_index_in_tx ?? right.logIndex ?? 0);
      if (Number.isFinite(leftLogIndex) && Number.isFinite(rightLogIndex) && leftLogIndex !== rightLogIndex) return leftLogIndex - rightLogIndex;
      return String(left.uniqueKey || left.hash).localeCompare(String(right.uniqueKey || right.hash));
    },

    isAccountTransactionBeforeEntry(tx = {}, entryDate = null, entryHash = "") {
      if (!entryDate || !tx.date) return true;
      const diff = tx.date.getTime() - entryDate.getTime();
      if (diff < 0) return true;
      if (diff > 0) return false;
      const txHash = this.normalizeHashKey(tx.hash);
      const interestHash = this.normalizeHashKey(entryHash);
      return Boolean(interestHash && txHash && txHash !== interestHash && this.traceDirection === "reverse");
    },

    isAccountTransactionAfterEntry(tx = {}, entryDate = null, entryHash = "") {
      if (!entryDate || !tx.date) return true;
      const diff = tx.date.getTime() - entryDate.getTime();
      if (diff > 0) return true;
      if (diff < 0) return false;
      const txHash = this.normalizeHashKey(tx.hash);
      const interestHash = this.normalizeHashKey(entryHash);
      return Boolean(txHash && interestHash && txHash !== interestHash);
    },

    getProviderAccountBalanceBeforeEntry(transactions = [], selectedAddress = "", entry = null, context = {}) {
      const result = this.getAccountPriorBalanceBeforeInterestEvent({
        transactions,
        targetEntry: entry,
        addressOfInterest: selectedAddress,
        tokenSymbol: context?.token || '',
        tokenContract: context?.expectedContract || '',
        chainId: context?.chainId || '',
        networkFamily: context?.networkFamily || '',
        isNative: context?.isNative,
        direction: this.traceDirection || 'forward',
        historyAddress: selectedAddress,
        context
      });
      return Number.isFinite(Number(result?.balanceBefore)) ? Number(result.balanceBefore) : null;
    },

    getAccountProviderBalanceBeforeCandidate(tx = {}, selectedAddress = '', options = {}) {
      const parseFirst = (candidates = []) => {
        for (const candidate of candidates) {
          const parsed = this.parseNumberLike(candidate);
          if (Number.isFinite(parsed) && parsed >= 0) return parsed;
        }
        return null;
      };
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const fromMatches = selectedKey && this.normalizeAddressKey(tx?.fromAddress) === selectedKey;
      const toMatches = selectedKey && this.normalizeAddressKey(tx?.toAddress) === selectedKey;
      const raw = tx?.raw || {};
      if (toMatches) {
        const toBalance = parseFirst([
          tx?.toBalanceBefore,
          tx?.to_balance_before,
          raw.toBalanceBefore,
          raw.to_balance_before,
          raw.recipientBalanceBefore,
          raw.recipient_balance_before,
        ]);
        if (Number.isFinite(toBalance)) return toBalance;
      }
      if (fromMatches) {
        const fromBalance = parseFirst([
          tx?.fromBalanceBefore,
          tx?.from_balance_before,
          raw.fromBalanceBefore,
          raw.from_balance_before,
          raw.senderBalanceBefore,
          raw.sender_balance_before,
        ]);
        if (Number.isFinite(fromBalance)) return fromBalance;
      }
      if (options.allowGenericProviderBalance === false) return null;
      return parseFirst([
        tx?.assetBalanceBefore,
        tx?.balanceBeforeTransaction,
        tx?.balance_before_transaction,
        tx?.priorBalanceBeforeTransaction,
        tx?.prior_balance_before_transaction,
        tx?.balanceBefore,
        tx?.balance_before,
        raw.assetBalanceBefore,
        raw.asset_balance_before,
        raw.balanceBeforeTransaction,
        raw.balance_before_transaction,
        raw.priorBalanceBeforeTransaction,
        raw.prior_balance_before_transaction,
        raw.balanceBefore,
        raw.balance_before,
      ]);
    },

    getAccountProviderBalanceAfterCandidate(tx = {}, selectedAddress = '', options = {}) {
      const parseFirst = (candidates = []) => {
        for (const candidate of candidates) {
          const parsed = this.parseNumberLike(candidate);
          if (Number.isFinite(parsed) && parsed >= 0) return parsed;
        }
        return null;
      };
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const fromMatches = selectedKey && this.normalizeAddressKey(tx?.fromAddress) === selectedKey;
      const toMatches = selectedKey && this.normalizeAddressKey(tx?.toAddress) === selectedKey;
      const raw = tx?.raw || {};
      if (toMatches) {
        const toBalance = parseFirst([
          tx?.toBalanceAfter,
          tx?.to_balance_after,
          raw.toBalanceAfter,
          raw.to_balance_after,
          raw.recipientBalanceAfter,
          raw.recipient_balance_after,
        ]);
        if (Number.isFinite(toBalance)) return toBalance;
      }
      if (fromMatches) {
        const fromBalance = parseFirst([
          tx?.fromBalanceAfter,
          tx?.from_balance_after,
          raw.fromBalanceAfter,
          raw.from_balance_after,
          raw.senderBalanceAfter,
          raw.sender_balance_after,
        ]);
        if (Number.isFinite(fromBalance)) return fromBalance;
      }
      if (options.allowGenericProviderBalance === false) return null;
      return parseFirst([
        tx?.assetBalanceAfter,
        tx?.balanceAfterTransaction,
        tx?.balance_after_transaction,
        tx?.postTransactionBalance,
        tx?.post_transaction_balance,
        tx?.balanceAfter,
        tx?.balance_after,
        raw.assetBalanceAfter,
        raw.asset_balance_after,
        raw.balanceAfterTransaction,
        raw.balance_after_transaction,
        raw.postTransactionBalance,
        raw.post_transaction_balance,
        raw.balanceAfter,
        raw.balance_after,
      ]);
    },

    getAccountEventOrderParts(tx = {}) {
      const raw = tx?.raw || {};
      const numberOrNull = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };
      return {
        timestamp: tx?.date ? tx.date.getTime() : null,
        blockNumber: numberOrNull(raw.blockNumber ?? raw.block_number ?? raw.blockHeight ?? raw.block_height ?? tx.blockNumber),
        transactionIndex: numberOrNull(raw.transactionIndex ?? raw.transaction_index ?? raw.txIndex ?? raw.tx_index ?? tx.transactionIndex),
        logIndex: numberOrNull(raw.logIndex ?? raw.log_index ?? raw.eventIndex ?? raw.event_index ?? raw.event_index_in_tx ?? raw.traceId ?? raw.trace_id ?? tx.logIndex),
        key: this.toText(tx?.uniqueKey || this.getProviderTransactionUniqueKey(tx?.raw || tx) || tx?.hash)
      };
    },

    summarizeAccountPriorBalanceEvent(tx = {}, selectedAddress = '', context = {}, role = '') {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const fromMatches = this.normalizeAddressKey(tx?.fromAddress) === selectedKey;
      const toMatches = this.normalizeAddressKey(tx?.toAddress) === selectedKey;
      const deltaInfo = this.getAccountSelectedAddressBalanceDelta(tx, selectedAddress, context);
      const order = this.getAccountEventOrderParts(tx);
      return {
        role,
        hash: this.toText(tx?.hash),
        uniqueKey: this.toText(tx?.uniqueKey || this.getProviderTransactionUniqueKey(tx?.raw || tx)),
        fromAddress: this.toText(tx?.fromAddress),
        toAddress: this.toText(tx?.toAddress),
        fromMatches,
        toMatches,
        amount: Number.isFinite(Number(tx?.amount)) ? Number(tx.amount) : null,
        feeAmount: Number.isFinite(Number(tx?.feeAmount)) ? Number(tx.feeAmount) : 0,
        delta: deltaInfo.delta,
        deltaRole: deltaInfo.role,
        token: this.toText(context?.token || tx?.token),
        contract: this.toText(context?.expectedContract || tx?.tokenContractAddress || tx?.contractAddress),
        order,
      };
    },

    getAccountSelectedAddressBalanceDelta(tx = {}, selectedAddress = '', context = {}) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const fromMatches = this.normalizeAddressKey(tx?.fromAddress) === selectedKey;
      const toMatches = this.normalizeAddressKey(tx?.toAddress) === selectedKey;
      const amount = Math.max(0, Number(tx?.amount) || 0);
      const feeAmount = Math.max(0, Number(tx?.feeAmount) || 0);
      if (!selectedKey || (!fromMatches && !toMatches)) {
        return { delta: 0, role: 'other', amount, feeAmount };
      }
      if (fromMatches && toMatches) {
        return { delta: context?.isNative ? -feeAmount : 0, role: 'self', amount, feeAmount };
      }
      if (toMatches) {
        return { delta: amount, role: 'incoming', amount, feeAmount };
      }
      return { delta: -amount - (context?.isNative ? feeAmount : 0), role: 'outgoing', amount, feeAmount };
    },

    inferAccountBalanceBeforeFromProviderAfter(tx = {}, selectedAddress = '', context = {}, options = {}) {
      const after = this.getAccountProviderBalanceAfterCandidate(tx, selectedAddress, options);
      if (!Number.isFinite(after) || after < 0) return null;
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const fromMatches = this.normalizeAddressKey(tx?.fromAddress) === selectedKey;
      const toMatches = this.normalizeAddressKey(tx?.toAddress) === selectedKey;
      const amount = Math.max(0, Number(tx?.amount) || 0);
      const feeAmount = Math.max(0, Number(tx?.feeAmount) || 0);
      let inferred = null;
      if (fromMatches && toMatches) {
        inferred = context?.isNative ? after + feeAmount : after;
      } else if (toMatches) {
        inferred = after - amount;
      } else if (fromMatches) {
        inferred = after + amount + (context?.isNative ? feeAmount : 0);
      }
      if (!Number.isFinite(inferred)) return null;
      return Math.max(0, inferred);
    },

    findAccountPriorBalanceTargetEvent(transactions = [], selectedAddress = '', entry = null, context = {}, diagnostics = null) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const interestHash = this.normalizeHashKey(entry?.txHash || entry?.hash || entry?.transactionHash || '');
      const entryDate = this.getEntryDate(entry);
      const target = this.getAccountTargetRowContext(entry, selectedAddress);
      const possibleAmount = target.rowAmount !== null
        ? target.rowAmount
        : (Number.isFinite(Number(context?.maxAmount))
            ? Number(context.maxAmount)
            : Math.max(0, Number(context?.fundsIn?.amount) || 0) + Math.max(0, Number(context?.leftBoundsAmount) || 0));
      const preferIncoming = this.traceDirection !== 'reverse';
      const candidates = (Array.isArray(transactions) ? transactions : [])
        .filter((tx) => {
          if (!tx || !tx.hash) return false;
          if (interestHash && this.normalizeHashKey(tx.hash) !== interestHash) return false;
          const fromMatches = this.normalizeAddressKey(tx.fromAddress) === selectedKey;
          const toMatches = this.normalizeAddressKey(tx.toAddress) === selectedKey;
          return fromMatches || toMatches;
        })
        .map((tx) => {
          const fromKey = this.normalizeAddressKey(tx.fromAddress);
          const toKey = this.normalizeAddressKey(tx.toAddress);
          const fromMatches = fromKey === selectedKey;
          const toMatches = toKey === selectedKey;
          const hashMatches = Boolean(interestHash && this.normalizeHashKey(tx.hash) === interestHash);
          const amountMatches = Number.isFinite(possibleAmount) && possibleAmount > 0
            ? this.accountAmountsNearlyEqual(tx.amount, possibleAmount, 0.0005, 0.00000001)
            : false;
          const dateMatches = Boolean(entryDate && tx.date && Math.abs(tx.date.getTime() - entryDate.getTime()) <= 1000);
          let score = 0;
          if (hashMatches) score += 1000;
          if (preferIncoming && toMatches) score += 300;
          if (!preferIncoming && fromMatches) score += 300;
          if (toMatches || fromMatches) score += 100;
          if (target.toKey && toKey === target.toKey) score += 60;
          if (target.fromKey && fromKey === target.fromKey) score += 40;
          if (amountMatches) score += 80;
          if (dateMatches) score += 20;
          return { tx, score, hashMatches, amountMatches, dateMatches, toMatches, fromMatches };
        })
        .filter(candidate => candidate.score > 0);

      candidates.sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        return this.compareAccountTransactions(left.tx, right.tx);
      });
      const best = candidates[0] || null;
      if (!best) {
        diagnostics?.warnings?.push?.('Tool 88 could not find the exact account transfer event of interest in the fetched provider rows; prior balance was reconstructed with strict hash/date exclusion and may need review.');
        return null;
      }
      if (interestHash && !best.hashMatches) {
        diagnostics?.warnings?.push?.('Prior-balance target event was selected without an exact transaction-hash match; review before relying on this FIFO result.');
      }
      if (Number.isFinite(possibleAmount) && possibleAmount > 0 && !best.amountMatches) {
        diagnostics?.warnings?.push?.('Prior-balance target event hash/address matched, but the provider amount did not closely match the spreadsheet row amount.');
      }
      return best.tx;
    },

    accountEventHasComparableSameHashOrder(left = {}, right = {}) {
      if (this.normalizeHashKey(left?.hash) !== this.normalizeHashKey(right?.hash)) return true;
      if (this.isSameAccountProviderRow(left, right)) return true;
      const leftOrder = this.getAccountEventOrderParts(left);
      const rightOrder = this.getAccountEventOrderParts(right);
      return Number.isFinite(leftOrder.logIndex) && Number.isFinite(rightOrder.logIndex) && leftOrder.logIndex !== rightOrder.logIndex;
    },

    accountEventsHaveReliableRelativeOrder(left = {}, right = {}, diagnostics = null) {
      const leftOrder = this.getAccountEventOrderParts(left);
      const rightOrder = this.getAccountEventOrderParts(right);
      const leftHash = this.normalizeHashKey(left?.hash);
      const rightHash = this.normalizeHashKey(right?.hash);
      if (leftOrder.timestamp !== null && rightOrder.timestamp !== null && leftOrder.timestamp !== rightOrder.timestamp) return true;
      if (leftOrder.blockNumber !== null && rightOrder.blockNumber !== null && leftOrder.blockNumber !== rightOrder.blockNumber) return true;
      if (leftOrder.transactionIndex !== null && rightOrder.transactionIndex !== null && leftOrder.transactionIndex !== rightOrder.transactionIndex) return true;
      if (leftHash && rightHash && leftHash === rightHash) {
        return this.accountEventHasComparableSameHashOrder(left, right);
      }
      if (leftOrder.timestamp !== null && rightOrder.timestamp !== null && leftOrder.timestamp === rightOrder.timestamp) {
        diagnostics?.warnings?.push?.(`Tool 88 excluded a same-timestamp account event near ${this.shortenMiddle(left?.hash || '', 10, 8)} because provider ordering was not specific enough to prove it occurred before the target event.`);
        return false;
      }
      return true;
    },

    isAccountTransactionBeforeInterestEvent(tx = {}, targetEvent = {}, diagnostics = null) {
      if (!tx || !targetEvent) return false;
      if (this.isSameAccountProviderRow(tx, targetEvent)) return false;
      if (!this.accountEventsHaveReliableRelativeOrder(tx, targetEvent, diagnostics)) {
        return false;
      }
      const sameHash = this.normalizeHashKey(tx.hash) && this.normalizeHashKey(tx.hash) === this.normalizeHashKey(targetEvent.hash);
      if (sameHash && !this.accountEventHasComparableSameHashOrder(tx, targetEvent)) {
        diagnostics?.warnings?.push?.(`Tool 88 excluded same-transaction account event ${this.shortenMiddle(tx.hash, 10, 8)} from prior balance because provider log/event ordering was ambiguous.`);
        return false;
      }
      return this.compareAccountTransactions(tx, targetEvent) < 0;
    },

    isAccountTransactionBeforeEntryStrict(tx = {}, entryDate = null, entryHash = '', diagnostics = null) {
      if (!tx?.date || !entryDate) {
        diagnostics?.warnings?.push?.(`Tool 88 excluded account event ${this.shortenMiddle(tx?.hash || '', 10, 8)} from prior balance because event or target date was missing.`);
        return false;
      }
      const diff = tx.date.getTime() - entryDate.getTime();
      if (diff < 0) return true;
      if (diff > 0) return false;
      const txHash = this.normalizeHashKey(tx.hash);
      const interestHash = this.normalizeHashKey(entryHash);
      if (interestHash && txHash === interestHash) return false;
      diagnostics?.warnings?.push?.(`Tool 88 excluded same-timestamp account event ${this.shortenMiddle(tx.hash, 10, 8)} because no target event/log ordering was available.`);
      return false;
    },

    getAccountPriorBalanceBeforeInterestEvent(options = {}, diagnostics = null) {
      const context = options.context || options || {};
      const transactions = Array.isArray(options.transactions) ? options.transactions : [];
      const addressOfInterest = this.toText(options.addressOfInterest || options.selectedAddress || '').trim();
      const selectedKey = this.normalizeAddressKey(addressOfInterest);
      const targetEntry = options.targetEntry || options.entry || null;
      const targetHash = this.normalizeHashKey(targetEntry?.txHash || targetEntry?.hash || targetEntry?.transactionHash || options.targetHash || '');
      const debug = {
        rowPathLabel: this.toText(targetEntry?.pathLabel || targetEntry?.label || ''),
        rowHash: targetHash,
        addressOfInterest,
        addressSource: options.addressSource || (this.traceDirection === 'reverse' ? 'context/from-side' : 'context/to-address'),
        tokenSymbol: this.toText(options.tokenSymbol || context.token || ''),
        tokenContract: this.toText(options.tokenContract || context.expectedContract || ''),
        isNative: !!(options.isNative ?? context.isNative),
        targetEventKey: '',
        targetOrdering: null,
        providerBalanceBeforeCandidates: [],
        providerBalanceAfterCandidates: [],
        providerBalanceAfterReversed: false,
        includedEvents: [],
        excludedEvents: [],
        includedFeeAmount: 0,
        includedFeeEventCount: 0,
        targetEventFeeAmount: 0,
        finalPriorBalance: 0,
        source: 'unknown',
        warnings: []
      };
      const pushWarning = (message) => {
        const clean = this.toText(message);
        if (!clean) return;
        debug.warnings.push(clean);
        diagnostics?.warnings?.push?.(clean);
      };
      if (!selectedKey) {
        pushWarning('Tool 88 could not compute account prior balance because the address of interest was blank.');
        return { balanceBefore: 0, source: 'unknown', warnings: debug.warnings, debug };
      }

      const ordered = transactions
        .filter(tx => tx && tx.hash && tx.date && tx.analysisEligible !== false && !tx.failedOrReverted && !tx.nonBalanceAffecting)
        .slice()
        .sort((left, right) => this.compareAccountTransactions(left, right));
      const targetEvent = options.targetEvent || this.findAccountPriorBalanceTargetEvent(ordered, addressOfInterest, targetEntry, context, { warnings: debug.warnings });
      if (targetEvent) {
        debug.targetEventKey = this.toText(targetEvent.uniqueKey || this.getProviderTransactionUniqueKey(targetEvent.raw || targetEvent));
        debug.targetOrdering = this.getAccountEventOrderParts(targetEvent);
        const targetEventSummary = this.summarizeAccountPriorBalanceEvent(targetEvent, addressOfInterest, context, 'target_event_excluded');
        debug.excludedEvents.push(targetEventSummary);
        if (context?.isNative && targetEventSummary.fromMatches && Number(targetEventSummary.feeAmount) > 0) {
          debug.targetEventFeeAmount = Math.max(0, Number(targetEventSummary.feeAmount) || 0);
        }
        const providerBefore = this.getAccountProviderBalanceBeforeCandidate(targetEvent, addressOfInterest, options);
        if (Number.isFinite(providerBefore) && providerBefore >= 0) {
          debug.providerBalanceBeforeCandidates.push(providerBefore);
          debug.finalPriorBalance = Math.max(0, providerBefore);
          debug.source = 'provider_balance_before';
          return {
            balanceBefore: debug.finalPriorBalance,
            token: debug.tokenSymbol,
            addressOfInterest,
            targetHash,
            targetEventKey: debug.targetEventKey,
            source: debug.source,
            includedEvents: debug.includedEvents,
            excludedEvents: debug.excludedEvents,
            warnings: debug.warnings,
            debug
          };
        }
        const providerAfter = this.getAccountProviderBalanceAfterCandidate(targetEvent, addressOfInterest, options);
        if (Number.isFinite(providerAfter) && providerAfter >= 0) {
          debug.providerBalanceAfterCandidates.push(providerAfter);
          const reversed = this.inferAccountBalanceBeforeFromProviderAfter(targetEvent, addressOfInterest, context, options);
          if (Number.isFinite(reversed) && reversed >= 0) {
            debug.providerBalanceAfterReversed = true;
            debug.finalPriorBalance = Math.max(0, reversed);
            debug.source = 'provider_balance_after_reversed';
            return {
              balanceBefore: debug.finalPriorBalance,
              token: debug.tokenSymbol,
              addressOfInterest,
              targetHash,
              targetEventKey: debug.targetEventKey,
              source: debug.source,
              includedEvents: debug.includedEvents,
              excludedEvents: debug.excludedEvents,
              warnings: debug.warnings,
              debug
            };
          }
          pushWarning('Tool 88 saw provider balance-after data but could not safely reverse it for the target event/address role.');
        }
      } else {
        pushWarning('Tool 88 did not find a provider event that exactly matched the transaction/event of interest; strict date/hash exclusion was used as a fallback.');
      }

      const historyAddressKey = this.normalizeAddressKey(options.historyAddress || addressOfInterest);
      const historyScopedToInterest = !historyAddressKey || historyAddressKey === selectedKey;
      if (!historyScopedToInterest && options.allowCrossAddressReconstruction !== true) {
        debug.source = 'unknown';
        debug.finalPriorBalance = null;
        pushWarning(`Tool 88 did not reconstruct prior balance for ${this.shortenMiddle(addressOfInterest, 8, 6)} from provider rows scoped to ${this.shortenMiddle(options.historyAddress || '', 8, 6)}. A side-specific provider balance-before field is required for this receiver row.`);
        return {
          balanceBefore: undefined,
          token: debug.tokenSymbol,
          addressOfInterest,
          targetHash,
          targetEventKey: debug.targetEventKey,
          source: debug.source,
          includedEvents: debug.includedEvents,
          excludedEvents: debug.excludedEvents,
          warnings: debug.warnings,
          debug
        };
      }

      const entryDate = this.getEntryDate(targetEntry);
      let balance = 0;
      ordered.forEach((tx) => {
        const before = targetEvent
          ? this.isAccountTransactionBeforeInterestEvent(tx, targetEvent, { warnings: debug.warnings })
          : this.isAccountTransactionBeforeEntryStrict(tx, entryDate, targetHash, { warnings: debug.warnings });
        if (!before) {
          if (!targetEvent || !this.isSameAccountProviderRow(tx, targetEvent)) {
            debug.excludedEvents.push(this.summarizeAccountPriorBalanceEvent(tx, addressOfInterest, context, 'not_before_target'));
          }
          return;
        }
        const deltaInfo = this.getAccountSelectedAddressBalanceDelta(tx, addressOfInterest, context);
        if (Math.abs(deltaInfo.delta) <= 1e-12) {
          debug.excludedEvents.push(this.summarizeAccountPriorBalanceEvent(tx, addressOfInterest, context, 'zero_delta_or_other_address'));
          return;
        }
        balance += deltaInfo.delta;
        const summary = this.summarizeAccountPriorBalanceEvent(tx, addressOfInterest, context, 'included_prior_event');
        summary.runningBalance = balance;
        if (context?.isNative && summary.fromMatches && Number(summary.feeAmount) > 0) {
          debug.includedFeeAmount += Math.max(0, Number(summary.feeAmount) || 0);
          debug.includedFeeEventCount += 1;
        }
        debug.includedEvents.push(summary);
        if (balance < -1e-9) {
          pushWarning(`Tool 88 reconstructed a negative account balance near ${this.shortenMiddle(tx.hash, 10, 8)}; clamped to zero after recording the warning.`);
          balance = 0;
        } else if (balance < 0) {
          balance = 0;
        }
      });

      debug.finalPriorBalance = Math.max(0, balance);
      debug.source = targetEvent || entryDate ? 'reconstructed_history' : 'unknown';
      if (debug.source === 'unknown') {
        pushWarning('Tool 88 could not prove prior-balance ordering from provider rows; Column N should be reviewed.');
      }
      return {
        balanceBefore: debug.finalPriorBalance,
        token: debug.tokenSymbol,
        addressOfInterest,
        targetHash,
        targetEventKey: debug.targetEventKey,
        source: debug.source,
        includedEvents: debug.includedEvents,
        excludedEvents: debug.excludedEvents,
        warnings: debug.warnings,
        debug
      };
    },

    calculateAccountPriorBalance(transactions = [], selectedAddress = "", entry = null, context = {}, diagnostics = null) {
      const result = this.getAccountPriorBalanceBeforeInterestEvent({
        transactions,
        targetEntry: entry,
        addressOfInterest: selectedAddress,
        tokenSymbol: context?.token || '',
        tokenContract: context?.expectedContract || '',
        chainId: context?.chainId || '',
        networkFamily: context?.networkFamily || '',
        isNative: context?.isNative,
        direction: this.traceDirection || 'forward',
        historyAddress: selectedAddress,
        context
      }, diagnostics);
      if (diagnostics) {
        diagnostics.accountPriorBalanceDebug = result?.debug || null;
        diagnostics.priorBalanceSource = result?.source || 'unknown';
        diagnostics.actualPriorBalanceSource = result?.source || 'unknown';
        diagnostics.priorBalanceFeeAmount = Math.max(0, Number(result?.debug?.includedFeeAmount) || 0);
        diagnostics.priorBalanceFeeEventCount = Math.max(0, Number(result?.debug?.includedFeeEventCount) || 0);
        diagnostics.targetEventFeeAmount = Math.max(0, Number(result?.debug?.targetEventFeeAmount) || 0);
      }
      if (Array.isArray(result?.warnings) && result.warnings.length && diagnostics?.warnings) {
        const existingWarnings = new Set(diagnostics.warnings.map(warning => this.toText(warning)));
        result.warnings.forEach((warning) => {
          const clean = this.toText(warning);
          if (clean && !existingWarnings.has(clean)) {
            diagnostics.warnings.push(clean);
            existingWarnings.add(clean);
          }
        });
      }
      return Math.max(0, Number(result?.balanceBefore) || 0);
    },

    calculateAccountBalanceAfterTransaction(transactions = [], selectedAddress = "", targetTx = null, context = {}, diagnostics = null) {
      if (!targetTx) return null;
      const directBalanceAfter = this.getAccountProviderBalanceAfterCandidate(targetTx, selectedAddress);
      if (Number.isFinite(directBalanceAfter) && directBalanceAfter >= 0) return directBalanceAfter;
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      if (!selectedKey) return null;
      const targetKey = this.toText(targetTx.uniqueKey || this.getProviderTransactionUniqueKey(targetTx.raw || targetTx));
      const ordered = (Array.isArray(transactions) ? transactions.slice() : [])
        .filter(tx => tx && tx.date)
        .sort((left, right) => this.compareAccountTransactions(left, right));
      let balance = 0;
      for (const tx of ordered) {
        const fromMatches = this.normalizeAddressKey(tx.fromAddress) === selectedKey;
        const toMatches = this.normalizeAddressKey(tx.toAddress) === selectedKey;
        if (fromMatches && toMatches) {
          if (context.isNative && tx.feeAmount > 0) balance -= tx.feeAmount;
        } else if (toMatches) {
          balance += Math.max(0, Number(tx.amount) || 0);
        } else if (fromMatches) {
          balance -= Math.max(0, Number(tx.amount) || 0);
          if (context.isNative && tx.feeAmount > 0) balance -= tx.feeAmount;
        }
        if (balance < -1e-9) {
          diagnostics?.warnings?.push?.(`Balance after ${this.shortenMiddle(tx.hash, 10, 8)} briefly went negative and was clamped to zero for the Case Buddy row export.`);
          balance = 0;
        }
        const txKey = this.toText(tx.uniqueKey || this.getProviderTransactionUniqueKey(tx.raw || tx));
        if ((targetKey && txKey && targetKey === txKey) || this.isSameAccountProviderRow(tx, targetTx)) {
          return Math.max(0, balance);
        }
      }
      return null;
    },

    solveAccountFifoForEntry(entry, selectedAddress, transactions = []) {
      const context = this.buildAccountFifoContext(entry, selectedAddress);
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const entryDate = this.getEntryDate(entry);
      const diagnostics = {
        solver: this.traceDirection === "reverse"
          ? (this.hopLogicType === "LIFO"
              ? "ACCOUNT_REVERSE_LIFO_SOURCE_STACK"
              : (this.hopLogicType === "NEXT_CHRONOLOGICAL" ? "ACCOUNT_REVERSE_NEXT_SOURCE_FIFO" : "ACCOUNT_REVERSE_FIFO"))
          : (this.hopLogicType === "LIFO"
              ? "ACCOUNT_FORWARD_LIFO_POST_TXN_DEPOSIT_QUEUE"
              : (this.hopLogicType === "NEXT_CHRONOLOGICAL" ? "ACCOUNT_NEXT_CHRONOLOGICAL_AS_ZERO_BALANCE_FIFO" : "ACCOUNT_FORWARD_FIFO")),
        asset: context.token,
        tokenContract: context.expectedContract,
        isNative: context.isNative,
        priorBalanceAmount: 0,
        actualPriorBalanceAmount: 0,
        inputLeftBoundsAmount: context.leftBoundsAmount,
        fundsFollowedAmount: context.fundsIn.amount,
        maxAmount: context.maxAmount,
        feeConsumedAmount: 0,
        nativeFeeResidualSuppressedAmount: 0,
        totalExitFollowedAmount: 0,
        dormantAmount: 0,
        requiresTokenContract: Boolean(context.requiresTokenContract),
        tokenRegistryStatus: context.tokenRegistryStatus || "",
        tokenRegistrySummary: context.tokenRegistrySummary || "",
        preventPathEnding: false,
        priorBalanceSource: 'unknown',
        accountPriorBalanceDebug: null,
        warnings: [],
      };
      const normalizedTransactions = this.normalizeAccountTransactionsForFifo(transactions, context, diagnostics);

      if (diagnostics.failedOrRevertedRowsExcluded > 0) {
        diagnostics.warnings.push(`${this.formatInteger(diagnostics.failedOrRevertedRowsExcluded)} failed/reverted provider row${diagnostics.failedOrRevertedRowsExcluded === 1 ? " was" : "s were"} excluded before FIFO balance and exit selection.`);
      }

      if (context.requiresTokenContract && !context.expectedContract) {
        diagnostics.preventPathEnding = true;
        diagnostics.dormantAmount = Math.max(0, Number(context.fundsIn.amount) || 0);
        diagnostics.warnings.push(`Tool 88 could not resolve a trusted contract address for ${context.token} on ${context.chainLabel || context.chainName || context.networkFamily || "this chain"}. Add the token to Tool 88 Token Manager or provide a trusted contract before running ERC20/TRC20 FIFO.`);
        return this.buildAccountFifoSolution(entry, [], [], context, diagnostics, this.traceDirection === "reverse" ? "reverse" : "forward");
      }

      if (!normalizedTransactions.length) {
        diagnostics.warnings.push("No provider rows matched the followed asset/contract after filtering failed, non-balance, and wrong-token rows.");
      }

      if (this.traceDirection === "reverse") {
        if (this.hopLogicType === "LIFO") {
          return this.solveAccountReverseLifoForEntry(entry, selectedAddress, normalizedTransactions, context, diagnostics);
        }
        return this.solveAccountReverseFifoForEntry(entry, selectedAddress, normalizedTransactions, context, diagnostics);
      }
      if (this.hopLogicType === "LIFO") {
        return this.solveAccountForwardPtdqForEntry(entry, selectedAddress, normalizedTransactions, context, diagnostics, entryDate, selectedKey);
      }
      return this.solveAccountForwardFifoForEntry(entry, selectedAddress, normalizedTransactions, context, diagnostics, entryDate, selectedKey);
    },

    solveAccountForwardPtdqForEntry(entry, selectedAddress, normalizedTransactions, context, diagnostics, entryDate, selectedKey) {
      const selectedAddressKey = selectedKey || this.normalizeAddressKey(selectedAddress);
      const depositAmount = Math.max(0, Number(context.maxAmount) || Number(context.fundsIn.amount) || 0);
      let depositPrefixRemaining = Math.max(0, Number(context.leftBoundsAmount) || 0);
      if (depositAmount > 0 && depositPrefixRemaining > depositAmount) {
        diagnostics.warnings.push(`LIFO/PTDQ clamped input left bounds from ${this.formatFunds(depositPrefixRemaining, context.token)} to the selected deposit amount ${this.formatFunds(depositAmount, context.token)}.`);
        depositPrefixRemaining = depositAmount;
      }

      let followRemaining = Math.max(0, Number(context.fundsIn.amount) || 0);
      if (depositAmount > 0) {
        const availableAfterPrefix = Math.max(0, depositAmount - depositPrefixRemaining);
        if (followRemaining > availableAfterPrefix) {
          diagnostics.warnings.push(`LIFO/PTDQ clamped funds followed from ${this.formatFunds(followRemaining, context.token)} to the selected deposit's available right-side amount ${this.formatFunds(availableAfterPrefix, context.token)}.`);
          followRemaining = availableAfterPrefix;
        }
      }

      diagnostics.ptdqDepositAmount = depositAmount;
      diagnostics.ptdqInitialLeftBoundsAmount = depositPrefixRemaining;
      diagnostics.ptdqInitialFollowAmount = followRemaining;
      diagnostics.fundsFollowedAmount = followRemaining;
      diagnostics.inputLeftBoundsAmount = depositPrefixRemaining;

      if (followRemaining <= 1e-12) {
        diagnostics.warnings.push("LIFO/PTDQ had no positive followed amount after applying the selected deposit's left bounds.");
        diagnostics.dormantAmount = 0;
        return this.buildAccountFifoSolution(entry, [], [], context, diagnostics, "forward");
      }

      const rowsAfterInterest = normalizedTransactions.filter((tx) => this.isAccountTransactionAfterEntry(tx, entryDate, entry?.txHash));
      const events = [];
      rowsAfterInterest.forEach((tx) => {
        const fromMatches = this.normalizeAddressKey(tx.fromAddress) === selectedAddressKey;
        const toMatches = this.normalizeAddressKey(tx.toAddress) === selectedAddressKey;
        const amount = Math.max(0, Number(tx.amount) || 0);
        if (fromMatches && toMatches) {
          if (context.isNative && tx.feeAmount > 0) {
            diagnostics.warnings.push(`LIFO/PTDQ ignored self-transfer value for ${this.shortenMiddle(tx.hash, 10, 8)}; review manually if its native fee should affect the followed range.`);
          }
          return;
        }
        if (toMatches && amount > 0) {
          events.push({ kind: "deposit", tx, amount });
        } else if (fromMatches && amount > 0) {
          events.push({ kind: "exit", tx, amount });
        }
      });

      events.sort((left, right) => {
        const c = this.compareAccountTransactions(left.tx, right.tx);
        if (c !== 0) return c;
        if (left.kind === right.kind) return 0;
        return left.kind === "deposit" ? -1 : 1;
      });

      let laterQueue = 0;
      let feeConsumedAmount = 0;
      const exits = [];
      const relevantTransactionsByKey = new Map();

      const rememberTx = (tx) => {
        const key = this.getProviderTransactionUniqueKey(tx.raw || tx) || `${tx.hash}|${tx.fromAddress}|${tx.toAddress}|${tx.amount}`;
        if (!relevantTransactionsByKey.has(key)) relevantTransactionsByKey.set(key, tx);
      };

      const consumePtdqCapacity = (capacity, tx, mode = "exit") => {
        let amountRemaining = Math.max(0, Number(capacity) || 0);
        if (amountRemaining <= 1e-12 || followRemaining <= 1e-12) return;

        const laterConsumed = Math.min(amountRemaining, laterQueue);
        laterQueue -= laterConsumed;
        amountRemaining -= laterConsumed;

        if (amountRemaining <= 1e-12) return;

        const prefixSkip = Math.min(amountRemaining, depositPrefixRemaining);
        depositPrefixRemaining -= prefixSkip;
        amountRemaining -= prefixSkip;

        if (amountRemaining <= 1e-12) return;

        const take = Math.min(amountRemaining, followRemaining);
        if (take <= 1e-12) return;

        followRemaining -= take;
        if (mode === "fee") {
          feeConsumedAmount += take;
          return;
        }

        const leftBoundAmount = laterConsumed + prefixSkip;
        exits.push({
          ...tx,
          followedAmount: take,
          leftBoundAmount,
          rightBoundAmount: leftBoundAmount + take,
          balanceAfterTransaction: this.calculateAccountBalanceAfterTransaction(normalizedTransactions, selectedAddress, tx, context, diagnostics),
          amount: tx.amount,
          token: context.token,
          chainId: tx.chainId || context.chainId,
          chainName: tx.chainName || context.chainName,
          transactionKind: `${context.isNative ? "native" : "token"}_lifo_ptdq_exit`,
          continuesAsLabel: tx.hash,
          ptdqLaterConsumedAmount: laterConsumed,
          ptdqPrefixConsumedAmount: prefixSkip,
        });
      };

      events.forEach((event) => {
        if (followRemaining <= 1e-12) return;
        rememberTx(event.tx);
        if (event.kind === "deposit") {
          laterQueue += Math.max(0, Number(event.amount) || 0);
          diagnostics.ptdqLaterDepositCount = (diagnostics.ptdqLaterDepositCount || 0) + 1;
          diagnostics.ptdqLaterDepositAmount = (diagnostics.ptdqLaterDepositAmount || 0) + Math.max(0, Number(event.amount) || 0);
          return;
        }

        if (context.isNative && event.tx.feeAmount > 0) {
          consumePtdqCapacity(event.tx.feeAmount, event.tx, "fee");
        }
        consumePtdqCapacity(event.amount, event.tx, "exit");
      });

      diagnostics.ptdqEventsReviewed = events.length;
      diagnostics.ptdqExitEventsReviewed = events.filter((event) => event.kind === "exit").length;
      diagnostics.ptdqEndingLaterQueueAmount = laterQueue;
      diagnostics.ptdqRemainingPrefixAmount = depositPrefixRemaining;
      diagnostics.feeConsumedAmount = feeConsumedAmount;
      diagnostics.totalExitFollowedAmount = exits.reduce((sum, exit) => sum + (Number(exit.followedAmount) || 0), 0);
      diagnostics.dormantAmount = Math.max(0, followRemaining);

      if (exits.length) {
        diagnostics.warnings.push(`LIFO/PTDQ found ${exits.length} outgoing exit transfer${exits.length === 1 ? "" : "s"} after newer deposits were consumed first.`);
      }
      if (diagnostics.dormantAmount > 1e-12) {
        diagnostics.warnings.push(`LIFO/PTDQ left ${this.formatFunds(diagnostics.dormantAmount, context.token)} dormant at the selected address.`);
      }
      if (feeConsumedAmount > 1e-12) {
        diagnostics.warnings.push(`LIFO/PTDQ consumed ${this.formatFunds(feeConsumedAmount, context.token)} of followed native funds as network fees.`);
      }

      return this.buildAccountFifoSolution(entry, exits, [...relevantTransactionsByKey.values()], context, diagnostics, "forward");
    },

    getFeeConsumedLabelForSolution(solution = {}) {
      const account = solution.accountAnalysis || {};
      const kindText = `${this.toText(solution.analysisKind)} ${this.toText(solution.solverKind)} ${this.toText(account.solver)}`.toLowerCase();
      if (account.isNative || /account_/.test(kindText)) {
        const token = this.toText(account.asset || solution.fundsIn?.token).toUpperCase();
        if (token === "TRX") return "TRON network fees";
        if (token) return `${token} network/gas fees`;
        return "network/gas fees";
      }
      if (/btc|bitcoin/.test(kindText)) return "BTC miner fees";
      return "network/gas fees";
    },

    trySolveNativeDirectSpendForEntry(entry, selectedAddress, normalizedTransactions, context, diagnostics, entryDate, selectedKey, actualPriorBalance = 0) {
      if (this.caseBuddyNativeDirectSpendGuardForcedOff === true || this.enableNativeDirectSpendMatchGuard !== true) return null;
      if (!context?.isNative || this.traceDirection === "reverse" || this.hopLogicType !== "FIFO") return null;
      const selectedAddressKey = selectedKey || this.normalizeAddressKey(selectedAddress);
      const outgoingRows = (normalizedTransactions || []).filter((tx) => {
        const fromMatches = this.normalizeAddressKey(tx.fromAddress) === selectedAddressKey;
        const toMatches = this.normalizeAddressKey(tx.toAddress) === selectedAddressKey;
        return fromMatches && !toMatches && this.isAccountTransactionAfterEntry(tx, entryDate, entry?.txHash) && Number(tx.amount) > 0;
      });
      if (!outgoingRows.length) return null;

      const firstExit = outgoingRows[0];
      const transferAmount = Math.max(0, Number(firstExit.amount) || 0);
      const feeAmount = Math.max(0, Number(firstExit.feeAmount) || 0);
      if (transferAmount <= 1e-12 || feeAmount <= 1e-12) return null;

      const followedAmountIn = Math.max(0, Number(context.fundsIn?.amount) || 0);
      const leftBoundsIn = Math.max(0, Number(context.leftBoundsAmount) || 0);
      const selectedRowAmount = Math.max(0, Number(context.maxAmount) || 0);
      const expectedSpend = selectedRowAmount > 0
        ? selectedRowAmount
        : Math.max(0, followedAmountIn + leftBoundsIn);
      if (expectedSpend <= 1e-12 || followedAmountIn <= 1e-12) return null;

      const totalSpend = transferAmount + feeAmount;
      const absoluteTolerance = Math.max(0.000002, feeAmount * 2.25, expectedSpend * 0.0035);
      if (Math.abs(totalSpend - expectedSpend) > absoluteTolerance) return null;

      let remainingLeft = leftBoundsIn;
      let remainingFollowed = followedAmountIn;
      let outputLeftBound = Math.min(remainingLeft, transferAmount);
      let outputFollowed = 0;

      if (remainingLeft >= transferAmount) {
        remainingLeft -= transferAmount;
      } else {
        const outputEligible = Math.max(0, transferAmount - remainingLeft);
        remainingLeft = 0;
        outputFollowed = Math.min(remainingFollowed, outputEligible);
        remainingFollowed -= outputFollowed;
      }

      let feeRemaining = feeAmount;
      const feeLeftTake = Math.min(remainingLeft, feeRemaining);
      remainingLeft -= feeLeftTake;
      feeRemaining -= feeLeftTake;
      const feeFollowedTake = Math.min(remainingFollowed, feeRemaining);
      remainingFollowed -= feeFollowedTake;
      feeRemaining -= feeFollowedTake;
      diagnostics.feeConsumedAmount += feeFollowedTake;

      const tinyResidualTolerance = Math.max(0.00000002, expectedSpend * 0.00001, feeAmount * 0.05);
      if (remainingFollowed > 0 && remainingFollowed <= tinyResidualTolerance) {
        diagnostics.feeConsumedAmount += remainingFollowed;
        diagnostics.warnings.push(`Native direct-spend guard absorbed a tiny residual ${this.formatFunds(remainingFollowed, context.token)} as gas/rounding rather than creating a separate branch.`);
        remainingFollowed = 0;
      }

      const exits = [];
      if (outputFollowed > 1e-12) {
        const receiverPriorBalanceResult = this.getAccountPriorBalanceBeforeInterestEvent({
          transactions: normalizedTransactions,
          targetEntry: entry,
          targetEvent: firstExit,
          addressOfInterest: firstExit.toAddress,
          tokenSymbol: context.token,
          tokenContract: context.expectedContract,
          chainId: context.chainId,
          networkFamily: context.networkFamily,
          isNative: context.isNative,
          direction: 'forward',
          historyAddress: selectedAddress,
          allowGenericProviderBalance: false
        });
        const receiverPriorBalance = Number(receiverPriorBalanceResult?.balanceBefore);
        exits.push({
          ...firstExit,
          followedAmount: outputFollowed,
          leftBoundAmount: outputLeftBound,
          rightBoundAmount: outputLeftBound + outputFollowed,
          priorBalanceBeforeTransaction: Number.isFinite(receiverPriorBalance) ? Math.max(0, receiverPriorBalance) : undefined,
          recipientPriorBalanceBeforeTransaction: Number.isFinite(receiverPriorBalance) ? Math.max(0, receiverPriorBalance) : undefined,
          priorBalanceBeforeTransactionForRow: Number.isFinite(receiverPriorBalance) ? Math.max(0, receiverPriorBalance) : undefined,
          receiverPriorBalanceSource: receiverPriorBalanceResult?.source || 'unknown',
          receiverPriorBalanceDebug: receiverPriorBalanceResult?.debug || null,
          sourceAddressPriorBalanceBeforeTransaction: Math.max(0, Number(actualPriorBalance) || 0),
          balanceAfterTransaction: this.calculateAccountBalanceAfterTransaction(normalizedTransactions, selectedAddress, firstExit, context, diagnostics),
          amount: transferAmount,
          token: context.token,
          chainId: firstExit.chainId || context.chainId,
          chainName: firstExit.chainName || context.chainName,
          transactionKind: "native_fifo_direct_spend_exit",
          continuesAsLabel: firstExit.hash,
        });
      }

      diagnostics.priorBalanceAmount = 0;
      diagnostics.nativeDirectSpendApplied = true;
      diagnostics.nativeDirectSpendTransaction = firstExit.hash;
      diagnostics.nativeDirectSpendTransferAmount = transferAmount;
      diagnostics.nativeDirectSpendFeeAmount = feeAmount;
      diagnostics.nativeDirectSpendExpectedAmount = expectedSpend;
      diagnostics.nativeDirectSpendActualPriorBalanceAmount = actualPriorBalance;
      diagnostics.totalExitFollowedAmount = exits.reduce((sum, exit) => sum + (Number(exit.followedAmount) || 0), 0);
      diagnostics.dormantAmount = Math.max(0, remainingFollowed);
      diagnostics.warnings.push(`Native gas-aware direct-spend guard used ${this.shortenMiddle(firstExit.hash, 10, 8)} because transfer value plus gas (${this.formatFunds(totalSpend, context.token)}) closely matches the selected deposit amount (${this.formatFunds(expectedSpend, context.token)}). This avoids creating small later branches caused only by gas/prior-balance ambiguity.`);
      if (actualPriorBalance > 1e-12) {
        diagnostics.warnings.push(`Native direct-spend guard disregarded the reconstructed prior balance (${this.formatFunds(actualPriorBalance, context.token)}) for this near-full spend, because the first outgoing transfer plus gas accounted for the selected incoming amount.`);
      }
      if (diagnostics.feeConsumedAmount > 1e-12) {
        diagnostics.warnings.push(`${this.formatFunds(diagnostics.feeConsumedAmount, context.token)} of followed native funds appears consumed by transaction fees.`);
      }
      return this.buildAccountFifoSolution(entry, exits, [firstExit], context, diagnostics, "forward");
    },

    solveAccountForwardFifoForEntry(entry, selectedAddress, normalizedTransactions, context, diagnostics, entryDate, selectedKey) {
      const actualPriorBalance = this.calculateAccountPriorBalance(normalizedTransactions, selectedAddress, entry, context, diagnostics);
      let priorBalance = this.hopLogicType === "NEXT_CHRONOLOGICAL" ? 0 : actualPriorBalance;
      diagnostics.actualPriorBalanceAmount = actualPriorBalance;
      diagnostics.priorBalanceAmount = priorBalance;
      if (Number.isFinite(Number(context.priorBalanceFromInput)) && Number(context.priorBalanceFromInput) >= 0) {
        diagnostics.priorBalanceFromInput = Number(context.priorBalanceFromInput);
        const difference = Math.abs(diagnostics.priorBalanceFromInput - actualPriorBalance);
        const tolerance = Math.max(0.000001, Math.max(diagnostics.priorBalanceFromInput, actualPriorBalance, 1) * 0.000001);
        if (difference > tolerance) {
          diagnostics.warnings.push(`Column N prior balance is ${this.formatFunds(diagnostics.priorBalanceFromInput, context.token)}, while Tool 88 reconstructed ${this.formatFunds(actualPriorBalance, context.token)} from fetched provider rows. Tool 88 used the reconstructed value for FIFO and surfaced this for review.`);
        }
      }
      if (this.hopLogicType === "NEXT_CHRONOLOGICAL" && actualPriorBalance > 0) {
        diagnostics.warnings.push(`Next chronological mode used FIFO with prior balance forced to zero. Actual prior balance estimate was ${this.formatFunds(actualPriorBalance, context.token)}.`);
      }

      let remainingLeftBounds = Math.max(0, Number(context.leftBoundsAmount) || 0);
      let remainingFollowed = Math.max(0, Number(context.fundsIn.amount) || 0);
      let exits = [];
      const relevantTransactions = normalizedTransactions.filter((tx) => {
        const fromMatches = this.normalizeAddressKey(tx.fromAddress) === selectedKey;
        const toMatches = this.normalizeAddressKey(tx.toAddress) === selectedKey;
        return fromMatches && !toMatches && this.isAccountTransactionAfterEntry(tx, entryDate, entry?.txHash);
      });

      const directNativeSpendSolution = this.trySolveNativeDirectSpendForEntry(
        entry,
        selectedAddress,
        normalizedTransactions,
        context,
        diagnostics,
        entryDate,
        selectedKey,
        actualPriorBalance
      );
      if (directNativeSpendSolution) {
        return directNativeSpendSolution;
      }

      for (const tx of relevantTransactions) {
        if (remainingFollowed <= 1e-12) break;

        const priorBalanceBeforeTransaction = Math.max(0, Number(priorBalance) || 0);
        const balanceAfterTransaction = this.calculateAccountBalanceAfterTransaction(normalizedTransactions, selectedAddress, tx, context, diagnostics);

        if (context.isNative && tx.feeAmount > 0) {
          let feeRemaining = tx.feeAmount;
          const priorFeeTake = Math.min(priorBalance, feeRemaining);
          priorBalance -= priorFeeTake;
          feeRemaining -= priorFeeTake;
          const leftFeeTake = Math.min(remainingLeftBounds, feeRemaining);
          remainingLeftBounds -= leftFeeTake;
          feeRemaining -= leftFeeTake;
          const followedFeeTake = Math.min(remainingFollowed, feeRemaining);
          remainingFollowed -= followedFeeTake;
          feeRemaining -= followedFeeTake;
          diagnostics.feeConsumedAmount += followedFeeTake;
        }

        let outgoingRemaining = Math.max(0, Number(tx.amount) || 0);
        let leftBoundsForThisExit = 0;
        const priorTake = Math.min(priorBalance, outgoingRemaining);
        priorBalance -= priorTake;
        outgoingRemaining -= priorTake;
        leftBoundsForThisExit += priorTake;

        const leftTake = Math.min(remainingLeftBounds, outgoingRemaining);
        remainingLeftBounds -= leftTake;
        outgoingRemaining -= leftTake;
        leftBoundsForThisExit += leftTake;

        if (outgoingRemaining <= 1e-12) continue;
        const followedAmount = Math.min(remainingFollowed, outgoingRemaining);
        if (followedAmount <= 1e-12) continue;

        remainingFollowed -= followedAmount;
        const rightBounds = leftBoundsForThisExit + followedAmount;
        const receiverPriorBalanceResult = this.getAccountPriorBalanceBeforeInterestEvent({
          transactions: normalizedTransactions,
          targetEntry: entry,
          targetEvent: tx,
          addressOfInterest: tx.toAddress,
          tokenSymbol: context.token,
          tokenContract: context.expectedContract,
          chainId: context.chainId,
          networkFamily: context.networkFamily,
          isNative: context.isNative,
          direction: 'forward',
          historyAddress: selectedAddress,
          allowGenericProviderBalance: false
        });
        const receiverPriorBalance = Number(receiverPriorBalanceResult?.balanceBefore);
        if (receiverPriorBalanceResult?.warnings?.length) {
          diagnostics.receiverPriorBalanceWarnings = [
            ...(diagnostics.receiverPriorBalanceWarnings || []),
            ...receiverPriorBalanceResult.warnings.map(warning => `${this.shortenMiddle(tx.toAddress, 8, 6)}: ${warning}`)
          ];
        }
        exits.push({
          ...tx,
          followedAmount,
          leftBoundAmount: leftBoundsForThisExit,
          rightBoundAmount: rightBounds,
          priorBalanceBeforeTransaction: Number.isFinite(receiverPriorBalance) ? Math.max(0, receiverPriorBalance) : undefined,
          recipientPriorBalanceBeforeTransaction: Number.isFinite(receiverPriorBalance) ? Math.max(0, receiverPriorBalance) : undefined,
          priorBalanceBeforeTransactionForRow: Number.isFinite(receiverPriorBalance) ? Math.max(0, receiverPriorBalance) : undefined,
          receiverPriorBalanceSource: receiverPriorBalanceResult?.source || 'unknown',
          receiverPriorBalanceDebug: receiverPriorBalanceResult?.debug || null,
          sourceAddressPriorBalanceBeforeTransaction: priorBalanceBeforeTransaction,
          balanceAfterTransaction,
          amount: tx.amount,
          token: context.token,
          chainId: tx.chainId || context.chainId,
          chainName: tx.chainName || context.chainName,
          transactionKind: `${context.isNative ? "native" : "token"}_fifo_exit`,
          continuesAsLabel: tx.hash,
        });
      }

      exits = this.pruneNativeResidualAccountExits(exits, context, diagnostics);
      diagnostics.totalExitFollowedAmount = exits.reduce((sum, exit) => sum + (Number(exit.followedAmount) || 0), 0);
      diagnostics.dormantAmount = Math.max(0, remainingFollowed);
      if (exits.length && diagnostics.dormantAmount > 1e-12) {
        diagnostics.warnings.push(`Account FIFO found exits and a dormant remainder of ${this.formatFunds(diagnostics.dormantAmount, context.token)}.`);
      }
      if (diagnostics.feeConsumedAmount > 1e-12) {
        diagnostics.warnings.push(`${this.formatFunds(diagnostics.feeConsumedAmount, context.token)} of followed native funds appears consumed by transaction fees.`);
      }

      return this.buildAccountFifoSolution(entry, exits, relevantTransactions, context, diagnostics, "forward");
    },

    getNativeResidualBranchCutoff(context = {}, diagnostics = {}) {
      const token = this.toText(context.token || context.nativeSymbol || diagnostics.asset || "").toUpperCase();
      const followed = Math.max(0, Number(context.fundsIn?.amount ?? diagnostics.fundsFollowedAmount) || 0);
      let absolute = 0.001;
      if (["BTC", "WBTC", "BTC.B", "BTCB"].includes(token)) absolute = 0.00000001;
      else if (["ETH", "WETH"].includes(token)) absolute = 0.001;
      else if (["BNB", "WBNB", "MATIC", "WMATIC", "POL", "AVAX", "WAVAX"].includes(token)) absolute = 0.01;
      else if (["TRX"].includes(token)) absolute = 1;
      const relative = followed > 0 ? followed * 0.001 : 0;
      return Math.max(absolute, relative);
    },

    pruneNativeResidualAccountExits(exits = [], context = {}, diagnostics = {}) {
      if (!context?.isNative || !Array.isArray(exits) || exits.length <= 1) {
        return exits;
      }

      const followedTotal = Math.max(0, Number(context.fundsIn?.amount ?? diagnostics.fundsFollowedAmount) || 0);
      const primaryFollowed = Math.max(0, Number(exits[0]?.followedAmount) || 0);
      const hasFeeOrBalanceCaution =
        (Number(diagnostics.feeConsumedAmount) || 0) > 1e-12 ||
        (diagnostics.warnings || []).some((warning) => /fee|gas|prior balance|reconstructed/i.test(this.toText(warning)));
      if (!hasFeeOrBalanceCaution || followedTotal <= 0 || primaryFollowed < followedTotal * 0.5) {
        return exits;
      }

      const cutoff = this.getNativeResidualBranchCutoff(context, diagnostics);
      const kept = [];
      const pruned = [];

      exits.forEach((exit, index) => {
        const amount = Math.max(0, Number(exit?.followedAmount) || 0);
        const isTinyTrailingResidual = index > 0 && amount > 0 && amount <= cutoff;
        if (isTinyTrailingResidual) {
          pruned.push(exit);
        } else {
          kept.push(exit);
        }
      });

      if (!pruned.length || !kept.length) {
        return exits;
      }

      const prunedAmount = pruned.reduce((sum, exit) => sum + (Number(exit.followedAmount) || 0), 0);
      diagnostics.nativeResidualPrunedAmount = (Number(diagnostics.nativeResidualPrunedAmount) || 0) + prunedAmount;
      diagnostics.feeConsumedAmount = (Number(diagnostics.feeConsumedAmount) || 0) + prunedAmount;
      diagnostics.nativeResidualPrunedExitCount = (Number(diagnostics.nativeResidualPrunedExitCount) || 0) + pruned.length;
      diagnostics.warnings.push(`Suppressed ${pruned.length} tiny native residual branch${pruned.length === 1 ? "" : "es"} totaling ${this.formatFunds(prunedAmount, context.token)}. Tool 88 treated this as a gas/dust tail instead of creating a separate hop.`);
      return kept;
    },

    accountAmountsNearlyEqual(left, right, relativeTolerance = 0.000001, absoluteTolerance = 1e-12) {
      const a = Number(left);
      const b = Number(right);
      if (!Number.isFinite(a) || !Number.isFinite(b)) {
        return false;
      }
      const diff = Math.abs(a - b);
      const scale = Math.max(Math.abs(a), Math.abs(b), 1);
      return diff <= Math.max(absoluteTolerance, scale * relativeTolerance);
    },

    getAccountTargetRowContext(entry, selectedAddress) {
      const row = this.getSelectedTargetRow(entry, selectedAddress) || [];
      const fromAddress = this.toText(row?.[COL.FROM_ADDRESS]);
      const toAddress = this.toText(row?.[COL.TO_ADDRESS]);
      const rowAmount = this.parseNumberLike(row?.[COL.AMOUNT]);
      const token = this.toText(row?.[COL.TOKEN]).toUpperCase();
      return {
        row,
        fromAddress,
        fromKey: this.normalizeAddressKey(fromAddress),
        toAddress,
        toKey: this.normalizeAddressKey(toAddress),
        rowAmount: Number.isFinite(rowAmount) ? Math.max(0, rowAmount) : null,
        token,
      };
    },

    isSameAccountProviderRow(left = {}, right = {}) {
      if (!left || !right) return false;
      const leftKey = this.toText(left.uniqueKey || this.getProviderTransactionUniqueKey(left.raw || left));
      const rightKey = this.toText(right.uniqueKey || this.getProviderTransactionUniqueKey(right.raw || right));
      if (leftKey && rightKey && leftKey === rightKey) return true;
      return Boolean(
        this.normalizeHashKey(left.hash) &&
        this.normalizeHashKey(left.hash) === this.normalizeHashKey(right.hash) &&
        this.normalizeAddressKey(left.fromAddress) === this.normalizeAddressKey(right.fromAddress) &&
        this.normalizeAddressKey(left.toAddress) === this.normalizeAddressKey(right.toAddress) &&
        this.accountAmountsNearlyEqual(left.amount, right.amount)
      );
    },

    getAccountReverseSimulationRole(tx = {}, selectedKey = "") {
      const fromMatches = this.normalizeAddressKey(tx.fromAddress) === selectedKey;
      const toMatches = this.normalizeAddressKey(tx.toAddress) === selectedKey;
      if (toMatches && !fromMatches) return "deposit";
      if (fromMatches && !toMatches) return "outgoing";
      if (fromMatches && toMatches) return "self";
      return "other";
    },

    compareAccountTransactionsForReverseSimulation(left = {}, right = {}, selectedKey = "") {
      const leftTime = left.date ? left.date.getTime() : 0;
      const rightTime = right.date ? right.date.getTime() : 0;
      if (leftTime !== rightTime) return leftTime - rightTime;

      const leftBlock = Number(left.raw?.blockNumber ?? left.raw?.block_number ?? left.blockNumber ?? 0);
      const rightBlock = Number(right.raw?.blockNumber ?? right.raw?.block_number ?? right.blockNumber ?? 0);
      if (Number.isFinite(leftBlock) && Number.isFinite(rightBlock) && leftBlock !== rightBlock) return leftBlock - rightBlock;

      const leftTxIndex = Number(left.raw?.transactionIndex ?? left.raw?.transaction_index ?? left.raw?.txIndex ?? left.raw?.tx_index ?? left.transactionIndex ?? 0);
      const rightTxIndex = Number(right.raw?.transactionIndex ?? right.raw?.transaction_index ?? right.raw?.txIndex ?? right.raw?.tx_index ?? right.transactionIndex ?? 0);
      if (Number.isFinite(leftTxIndex) && Number.isFinite(rightTxIndex) && leftTxIndex !== rightTxIndex) return leftTxIndex - rightTxIndex;

      const leftLogIndex = Number(left.raw?.logIndex ?? left.raw?.log_index ?? left.raw?.eventIndex ?? left.raw?.event_index ?? left.raw?.event_index_in_tx ?? left.logIndex ?? 0);
      const rightLogIndex = Number(right.raw?.logIndex ?? right.raw?.log_index ?? right.raw?.eventIndex ?? right.raw?.event_index ?? right.raw?.event_index_in_tx ?? right.logIndex ?? 0);
      if (Number.isFinite(leftLogIndex) && Number.isFinite(rightLogIndex) && leftLogIndex !== rightLogIndex) return leftLogIndex - rightLogIndex;

      const roleRank = { deposit: 0, self: 1, outgoing: 2, other: 3 };
      const leftRank = roleRank[this.getAccountReverseSimulationRole(left, selectedKey)] ?? 3;
      const rightRank = roleRank[this.getAccountReverseSimulationRole(right, selectedKey)] ?? 3;
      if (leftRank !== rightRank) return leftRank - rightRank;

      return String(left.uniqueKey || left.hash || "").localeCompare(String(right.uniqueKey || right.hash || ""));
    },

    findAccountReverseExitOfInterest(entry, selectedAddress, normalizedTransactions = [], context = {}, diagnostics = null) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const interestHash = this.getAccountInterestEntryHash(entry);
      const entryDate = this.getEntryDate(entry);
      const target = this.getAccountTargetRowContext(entry, selectedAddress);
      const fundsAmount = Number(context?.fundsIn?.amount || 0);
      const possibleExitAmount = target.rowAmount !== null
        ? target.rowAmount
        : (Number.isFinite(context.maxAmount) ? context.maxAmount : fundsAmount + Number(context.leftBoundsAmount || 0));

      const outgoingRows = (Array.isArray(normalizedTransactions) ? normalizedTransactions : []).filter((tx) => {
        if (!tx || !tx.hash) return false;
        const fromMatches = this.normalizeAddressKey(tx.fromAddress) === selectedKey;
        const toMatches = this.normalizeAddressKey(tx.toAddress) === selectedKey;
        return fromMatches && !toMatches && Number(tx.amount) > 0;
      });

      const candidates = outgoingRows.map((tx) => {
        let score = 0;
        const txHash = this.normalizeHashKey(tx.hash);
        const txToKey = this.normalizeAddressKey(tx.toAddress);
        const hashMatches = Boolean(interestHash && txHash === interestHash);
        const toMatches = Boolean(target.toKey && txToKey === target.toKey);
        const amountMatches = Number.isFinite(possibleExitAmount) && possibleExitAmount > 0
          ? this.accountAmountsNearlyEqual(tx.amount, possibleExitAmount, 0.0005, 0.00000001)
          : false;
        const dateMatches = Boolean(entryDate && tx.date && Math.abs(tx.date.getTime() - entryDate.getTime()) <= 1000);

        if (hashMatches) score += 1000;
        if (toMatches) score += 200;
        if (amountMatches) score += 100;
        if (dateMatches) score += 20;

        return { tx, score, hashMatches, toMatches, amountMatches, dateMatches };
      }).filter((candidate) => candidate.score > 0);

      candidates.sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        return this.compareAccountTransactions(left.tx, right.tx);
      });

      const best = candidates[0] || null;
      if (!best) {
        diagnostics?.warnings?.push?.("Reverse FIFO could not locate the selected outgoing transfer in the fetched provider history. Tool 88 left the output unchanged rather than guessing a source.");
        return null;
      }

      if (interestHash && !best.hashMatches) {
        diagnostics?.warnings?.push?.("Reverse FIFO selected an outgoing provider row by address/date/amount because the exact input transaction hash was not found in the filtered asset history.");
      }
      if (target.toKey && !best.toMatches) {
        diagnostics?.warnings?.push?.("Reverse FIFO could not match the selected row's To Address exactly in provider history; review the selected source rows before relying on them.");
      }
      if (Number.isFinite(possibleExitAmount) && possibleExitAmount > 0 && !best.amountMatches) {
        diagnostics?.warnings?.push?.("Reverse FIFO found the selected transaction hash but the provider amount did not closely match the selected spreadsheet row amount.");
      }

      return best;
    },

    isAccountTransactionBeforeSpecificRow(tx = {}, targetTx = {}, selectedKey = "") {
      if (!tx || !targetTx) return false;
      if (this.isSameAccountProviderRow(tx, targetTx)) return false;
      const txHash = this.normalizeHashKey(tx.hash);
      const targetHash = this.normalizeHashKey(targetTx.hash);
      if (txHash && targetHash && txHash === targetHash) return false;
      const comparison = this.compareAccountTransactionsForReverseSimulation(tx, targetTx, selectedKey);
      return comparison < 0;
    },

    consumeAccountLots(lots = [], amount = 0, recordSlices = null) {
      let remaining = Math.max(0, Number(amount) || 0);
      let consumed = 0;

      while (remaining > 1e-12 && lots.length) {
        const lot = lots[0];
        const available = Math.max(0, Number(lot.remainingAmount) || 0);
        if (available <= 1e-12) {
          lots.shift();
          continue;
        }

        const take = Math.min(available, remaining);
        const sourceLeft = Math.max(0, Number(lot.sourceOffset) || 0);
        if (recordSlices && take > 1e-12) {
          recordSlices.push({
            tx: lot.tx,
            usedAmount: take,
            leftBoundAmount: sourceLeft,
            rightBoundAmount: sourceLeft + take,
            depositTotalAmount: lot.totalAmount,
          });
        }

        lot.remainingAmount = available - take;
        lot.sourceOffset = sourceLeft + take;
        remaining -= take;
        consumed += take;

        if (lot.remainingAmount <= 1e-12) {
          lots.shift();
        }
      }

      return { consumed, unresolved: remaining, remaining };
    },

    consumeAccountLotsLifo(lots = [], amount = 0, recordSlices = null) {
      let remaining = Math.max(0, Number(amount) || 0);
      let consumed = 0;

      while (remaining > 1e-12 && lots.length) {
        const lot = lots[lots.length - 1];
        const available = Math.max(0, Number(lot.remainingAmount) || 0);
        if (available <= 1e-12) {
          lots.pop();
          continue;
        }

        const take = Math.min(available, remaining);
        const sourceLeft = Math.max(0, Number(lot.sourceOffset) || 0);
        if (recordSlices && take > 1e-12) {
          recordSlices.push({
            tx: lot.tx,
            usedAmount: take,
            leftBoundAmount: sourceLeft,
            rightBoundAmount: sourceLeft + take,
            depositTotalAmount: lot.totalAmount,
          });
        }

        lot.remainingAmount = available - take;
        lot.sourceOffset = sourceLeft + take;
        remaining -= take;
        consumed += take;

        if (lot.remainingAmount <= 1e-12) {
          lots.pop();
        }
      }

      return { consumed, unresolved: remaining, remaining };
    },

    solveAccountReverseLifoForEntry(entry, selectedAddress, normalizedTransactions, context, diagnostics) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const targetInfo = this.findAccountReverseExitOfInterest(entry, selectedAddress, normalizedTransactions, context, diagnostics);
      const interestTx = targetInfo?.tx || null;

      diagnostics.analysisDirection = "reverse";
      diagnostics.reverseTargetHash = interestTx?.hash || entry?.txHash || entry?.hash || entry?.transactionHash || "";
      diagnostics.reverseTargetFound = Boolean(interestTx);
      diagnostics.reverseTargetAmount = interestTx ? Math.max(0, Number(interestTx.amount) || 0) : 0;
      diagnostics.reverseTargetLeftOffset = Math.max(0, Number(context.leftBoundsAmount) || 0);
      diagnostics.reverseTargetFeeAmount = interestTx && context.isNative ? Math.max(0, Number(interestTx.feeAmount) || 0) : 0;
      diagnostics.reverseRowsBeforeTarget = 0;
      diagnostics.reverseSourceCount = 0;
      diagnostics.reverseSourceMatchedBy = targetInfo
        ? [targetInfo.hashMatches ? "hash" : "", targetInfo.toMatches ? "to-address" : "", targetInfo.amountMatches ? "amount" : "", targetInfo.dateMatches ? "date" : ""].filter(Boolean).join(", ")
        : "";

      if (!interestTx) {
        diagnostics.dormantAmount = Math.max(0, Number(context.fundsIn.amount) || 0);
        return this.buildAccountFifoSolution(entry, [], [], context, diagnostics, "reverse");
      }

      if (this.normalizeAddressKey(interestTx.fromAddress) !== selectedKey) {
        diagnostics.warnings.push("Reverse LIFO/PTDQ expects the selected address to be the outgoing/source side of the transaction being reversed.");
        diagnostics.dormantAmount = Math.max(0, Number(context.fundsIn.amount) || 0);
        return this.buildAccountFifoSolution(entry, [], [], context, diagnostics, "reverse");
      }

      if (interestTx.failedOrReverted || interestTx.analysisEligible === false || interestTx.nonBalanceAffecting) {
        diagnostics.warnings.push("Reverse LIFO/PTDQ target transaction appears failed, reverted, or non-balance-affecting; Tool 88 will not create reverse source rows from it.");
        diagnostics.dormantAmount = Math.max(0, Number(context.fundsIn.amount) || 0);
        return this.buildAccountFifoSolution(entry, [], [], context, diagnostics, "reverse");
      }

      const rowsBeforeTarget = normalizedTransactions
        .filter((tx) => this.isAccountTransactionBeforeSpecificRow(tx, interestTx, selectedKey))
        .sort((left, right) => this.compareAccountTransactionsForReverseSimulation(left, right, selectedKey));
      diagnostics.reverseRowsBeforeTarget = rowsBeforeTarget.length;

      const lots = [];
      rowsBeforeTarget.forEach((tx) => {
        const fromMatches = this.normalizeAddressKey(tx.fromAddress) === selectedKey;
        const toMatches = this.normalizeAddressKey(tx.toAddress) === selectedKey;

        if (fromMatches && toMatches) {
          if (context.isNative && tx.feeAmount > 0) {
            this.consumeAccountLotsLifo(lots, tx.feeAmount, null);
          }
          return;
        }

        if (toMatches && tx.amount > 0) {
          const amount = Math.max(0, Number(tx.amount) || 0);
          lots.push({
            tx,
            totalAmount: amount,
            remainingAmount: amount,
            sourceOffset: 0,
          });
          return;
        }

        if (fromMatches) {
          const spend = Math.max(0, Number(tx.amount) || 0) + (context.isNative ? Math.max(0, Number(tx.feeAmount) || 0) : 0);
          const spendResult = this.consumeAccountLotsLifo(lots, spend, null);
          if (spendResult.unresolved > 1e-9) {
            diagnostics.warnings.push(`Reverse LIFO/PTDQ prior-state spend exceeded reconstructed deposits by ${this.formatFunds(spendResult.unresolved, context.token)} near ${this.shortenMiddle(tx.hash, 10, 8)}.`);
          }
        }
      });

      diagnostics.priorBalanceAmount = lots.reduce((sum, lot) => sum + Math.max(0, Number(lot.remainingAmount) || 0), 0);
      diagnostics.actualPriorBalanceAmount = diagnostics.priorBalanceAmount;
      diagnostics.reverseDepositLotsAvailable = lots.length;

      const targetAmount = Math.max(0, Number(interestTx.amount) || 0);
      let exitLeftOffset = Math.max(0, Number(context.leftBoundsAmount) || 0);
      if (targetAmount > 0 && exitLeftOffset > targetAmount) {
        diagnostics.warnings.push(`Reverse LIFO/PTDQ clamped input left bounds from ${this.formatFunds(exitLeftOffset, context.token)} to the selected transfer amount ${this.formatFunds(targetAmount, context.token)}.`);
        exitLeftOffset = targetAmount;
      }

      let followAmount = Math.max(0, Number(context.fundsIn.amount) || 0);
      if (targetAmount > 0) {
        const availableRightSide = Math.max(0, targetAmount - exitLeftOffset);
        if (followAmount > availableRightSide) {
          diagnostics.warnings.push(`Reverse LIFO/PTDQ clamped funds followed from ${this.formatFunds(followAmount, context.token)} to the selected transfer's available right-side amount ${this.formatFunds(availableRightSide, context.token)}.`);
          followAmount = availableRightSide;
        }
      }

      diagnostics.reverseTargetAmount = targetAmount;
      diagnostics.reverseTargetLeftOffset = exitLeftOffset;
      diagnostics.reverseTargetFollowAmount = followAmount;

      if (followAmount <= 1e-12) {
        diagnostics.warnings.push("Reverse LIFO/PTDQ had no positive followed amount after applying the selected transfer's left bounds.");
        return this.buildAccountFifoSolution(entry, [], rowsBeforeTarget.concat([interestTx]), context, diagnostics, "reverse");
      }

      if (diagnostics.reverseTargetFeeAmount > 1e-12) {
        const feeResult = this.consumeAccountLotsLifo(lots, diagnostics.reverseTargetFeeAmount, null);
        diagnostics.reverseTargetFeeConsumedFromSources = feeResult.consumed;
        if (feeResult.unresolved > 1e-12) {
          diagnostics.warnings.push(`Reverse LIFO/PTDQ could not fully cover the selected native transaction fee from newer source lots; unresolved fee amount: ${this.formatFunds(feeResult.unresolved, context.token)}.`);
        } else {
          diagnostics.warnings.push(`Reverse LIFO/PTDQ accounted for ${this.formatFunds(diagnostics.reverseTargetFeeAmount, context.token)} of native transaction fee before mapping the selected transfer amount to source deposits.`);
        }
      }

      const skippedLeft = this.consumeAccountLotsLifo(lots, exitLeftOffset, null);
      diagnostics.reverseLeftBoundsSkippedAmount = skippedLeft.consumed;
      if (skippedLeft.unresolved > 1e-12) {
        diagnostics.warnings.push(`Reverse LIFO/PTDQ could not skip all selected-transfer left bounds; unresolved left-bound amount: ${this.formatFunds(skippedLeft.unresolved, context.token)}.`);
      }

      const slices = [];
      const followedResult = this.consumeAccountLotsLifo(lots, followAmount, slices);
      const sources = slices.map((slice) => {
        const sourceTx = slice.tx || {};
        return {
          ...sourceTx,
          followedAmount: slice.usedAmount,
          leftBoundAmount: slice.leftBoundAmount,
          rightBoundAmount: slice.rightBoundAmount,
          amount: sourceTx.amount,
          token: context.token,
          chainId: sourceTx.chainId || context.chainId,
          chainName: sourceTx.chainName || context.chainName,
          transactionKind: `${context.isNative ? "native" : "token"}_reverse_lifo_source`,
          continuesAsLabel: sourceTx.hash,
          reverseTargetHash: interestTx.hash,
          reverseTargetAmount: targetAmount,
          reverseTargetLeftOffset: exitLeftOffset,
        };
      });

      diagnostics.reverseSourceCount = sources.length;
      diagnostics.totalExitFollowedAmount = sources.reduce((sum, source) => sum + (Number(source.followedAmount) || 0), 0);
      diagnostics.dormantAmount = Math.max(0, followedResult.unresolved);
      diagnostics.reverseResolvedAmount = diagnostics.totalExitFollowedAmount;
      diagnostics.reverseUnresolvedAmount = diagnostics.dormantAmount;

      if (diagnostics.dormantAmount > 1e-12) {
        diagnostics.warnings.push(`Reverse LIFO/PTDQ could not tie ${this.formatFunds(diagnostics.dormantAmount, context.token)} to concrete newer-priority source rows.`);
      }
      if (sources.length) {
        diagnostics.warnings.push(`Reverse LIFO/PTDQ found ${sources.length} prior source transfer${sources.length === 1 ? "" : "s"} for the selected outgoing range, consuming newer source lots first.`);
      } else if (rowsBeforeTarget.length > 0) {
        diagnostics.warnings.push("Reverse LIFO/PTDQ saw prior rows for this asset but none remained in the LIFO source stack for the selected outgoing slice.");
      }

      return this.buildAccountFifoSolution(entry, sources, rowsBeforeTarget.concat([interestTx]), context, diagnostics, "reverse");
    },

    solveAccountReverseFifoForEntry(entry, selectedAddress, normalizedTransactions, context, diagnostics) {
      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const targetInfo = this.findAccountReverseExitOfInterest(entry, selectedAddress, normalizedTransactions, context, diagnostics);
      const interestTx = targetInfo?.tx || null;

      if (this.hopLogicType === "NEXT_CHRONOLOGICAL") {
        diagnostics.warnings.push("Reverse + Next Chronological is routed through the account reverse source-range solver so Tool 88 keeps token-contract filtering, native-fee handling, and same-transaction ordering instead of using the older generic nearest-prior fallback.");
      }

      diagnostics.analysisDirection = "reverse";
      diagnostics.reverseTargetHash = interestTx?.hash || entry?.txHash || entry?.hash || entry?.transactionHash || "";
      diagnostics.reverseTargetFound = Boolean(interestTx);
      diagnostics.reverseTargetAmount = interestTx ? Math.max(0, Number(interestTx.amount) || 0) : 0;
      diagnostics.reverseTargetLeftOffset = Math.max(0, Number(context.leftBoundsAmount) || 0);
      diagnostics.reverseTargetFeeAmount = interestTx && context.isNative ? Math.max(0, Number(interestTx.feeAmount) || 0) : 0;
      diagnostics.reverseRowsBeforeTarget = 0;
      diagnostics.reverseSourceCount = 0;
      diagnostics.reverseSourceMatchedBy = targetInfo
        ? [targetInfo.hashMatches ? "hash" : "", targetInfo.toMatches ? "to-address" : "", targetInfo.amountMatches ? "amount" : "", targetInfo.dateMatches ? "date" : ""].filter(Boolean).join(", ")
        : "";

      if (!interestTx) {
        diagnostics.dormantAmount = Math.max(0, Number(context.fundsIn.amount) || 0);
        return this.buildAccountFifoSolution(entry, [], [], context, diagnostics, "reverse");
      }

      if (this.normalizeAddressKey(interestTx.fromAddress) !== selectedKey) {
        diagnostics.warnings.push("Reverse FIFO expects the selected address to be the outgoing/source side of the transaction being reversed.");
        diagnostics.dormantAmount = Math.max(0, Number(context.fundsIn.amount) || 0);
        return this.buildAccountFifoSolution(entry, [], [], context, diagnostics, "reverse");
      }

      if (interestTx.failedOrReverted || interestTx.analysisEligible === false || interestTx.nonBalanceAffecting) {
        diagnostics.warnings.push("Reverse FIFO target transaction appears failed, reverted, or non-balance-affecting; Tool 88 will not create reverse source rows from it.");
        diagnostics.dormantAmount = Math.max(0, Number(context.fundsIn.amount) || 0);
        return this.buildAccountFifoSolution(entry, [], [], context, diagnostics, "reverse");
      }

      const rowsBeforeTarget = normalizedTransactions
        .filter((tx) => this.isAccountTransactionBeforeSpecificRow(tx, interestTx, selectedKey))
        .sort((left, right) => this.compareAccountTransactionsForReverseSimulation(left, right, selectedKey));
      diagnostics.reverseRowsBeforeTarget = rowsBeforeTarget.length;

      const lots = [];
      rowsBeforeTarget.forEach((tx) => {
        const fromMatches = this.normalizeAddressKey(tx.fromAddress) === selectedKey;
        const toMatches = this.normalizeAddressKey(tx.toAddress) === selectedKey;

        if (fromMatches && toMatches) {
          if (context.isNative && tx.feeAmount > 0) {
            this.consumeAccountLots(lots, tx.feeAmount, null);
          }
          return;
        }

        if (toMatches && tx.amount > 0) {
          const amount = Math.max(0, Number(tx.amount) || 0);
          lots.push({
            tx,
            totalAmount: amount,
            remainingAmount: amount,
            sourceOffset: 0,
          });
          return;
        }

        if (fromMatches) {
          const spend = Math.max(0, Number(tx.amount) || 0) + (context.isNative ? Math.max(0, Number(tx.feeAmount) || 0) : 0);
          const spendResult = this.consumeAccountLots(lots, spend, null);
          if (spendResult.unresolved > 1e-9) {
            diagnostics.warnings.push(`Reverse FIFO prior-state spend exceeded reconstructed deposits by ${this.formatFunds(spendResult.unresolved, context.token)} near ${this.shortenMiddle(tx.hash, 10, 8)}.`);
          }
        }
      });

      diagnostics.priorBalanceAmount = lots.reduce((sum, lot) => sum + Math.max(0, Number(lot.remainingAmount) || 0), 0);
      diagnostics.actualPriorBalanceAmount = diagnostics.priorBalanceAmount;
      diagnostics.reverseDepositLotsAvailable = lots.length;

      const targetAmount = Math.max(0, Number(interestTx.amount) || 0);
      let exitLeftOffset = Math.max(0, Number(context.leftBoundsAmount) || 0);
      if (targetAmount > 0 && exitLeftOffset > targetAmount) {
        diagnostics.warnings.push(`Reverse FIFO clamped input left bounds from ${this.formatFunds(exitLeftOffset, context.token)} to the selected transfer amount ${this.formatFunds(targetAmount, context.token)}.`);
        exitLeftOffset = targetAmount;
      }

      let followAmount = Math.max(0, Number(context.fundsIn.amount) || 0);
      if (targetAmount > 0) {
        const availableRightSide = Math.max(0, targetAmount - exitLeftOffset);
        if (followAmount > availableRightSide) {
          diagnostics.warnings.push(`Reverse FIFO clamped funds followed from ${this.formatFunds(followAmount, context.token)} to the selected transfer's available right-side amount ${this.formatFunds(availableRightSide, context.token)}.`);
          followAmount = availableRightSide;
        }
      }

      diagnostics.reverseTargetAmount = targetAmount;
      diagnostics.reverseTargetLeftOffset = exitLeftOffset;
      diagnostics.reverseTargetFollowAmount = followAmount;

      if (followAmount <= 1e-12) {
        diagnostics.warnings.push("Reverse FIFO had no positive followed amount after applying the selected transfer's left bounds.");
        return this.buildAccountFifoSolution(entry, [], rowsBeforeTarget.concat([interestTx]), context, diagnostics, "reverse");
      }

      if (diagnostics.reverseTargetFeeAmount > 1e-12) {
        const feeResult = this.consumeAccountLots(lots, diagnostics.reverseTargetFeeAmount, null);
        diagnostics.reverseTargetFeeConsumedFromSources = feeResult.consumed;
        if (feeResult.unresolved > 1e-12) {
          diagnostics.warnings.push(`Reverse FIFO could not fully cover the selected native transaction fee from prior lots; unresolved fee amount: ${this.formatFunds(feeResult.unresolved, context.token)}.`);
        } else {
          diagnostics.warnings.push(`Reverse FIFO accounted for ${this.formatFunds(diagnostics.reverseTargetFeeAmount, context.token)} of native transaction fee before mapping the selected transfer amount to prior deposits.`);
        }
      }

      const skippedLeft = this.consumeAccountLots(lots, exitLeftOffset, null);
      diagnostics.reverseLeftBoundsSkippedAmount = skippedLeft.consumed;
      if (skippedLeft.unresolved > 1e-12) {
        diagnostics.warnings.push(`Reverse FIFO could not skip all selected-transfer left bounds; unresolved left-bound amount: ${this.formatFunds(skippedLeft.unresolved, context.token)}.`);
      }

      const slices = [];
      const followedResult = this.consumeAccountLots(lots, followAmount, slices);
      const sources = slices.map((slice) => {
        const sourceTx = slice.tx || {};
        return {
          ...sourceTx,
          followedAmount: slice.usedAmount,
          leftBoundAmount: slice.leftBoundAmount,
          rightBoundAmount: slice.rightBoundAmount,
          amount: sourceTx.amount,
          token: context.token,
          chainId: sourceTx.chainId || context.chainId,
          chainName: sourceTx.chainName || context.chainName,
          transactionKind: `${context.isNative ? "native" : "token"}_reverse_fifo_source`,
          continuesAsLabel: sourceTx.hash,
          reverseTargetHash: interestTx.hash,
          reverseTargetAmount: targetAmount,
          reverseTargetLeftOffset: exitLeftOffset,
        };
      });

      diagnostics.reverseSourceCount = sources.length;
      diagnostics.totalExitFollowedAmount = sources.reduce((sum, source) => sum + (Number(source.followedAmount) || 0), 0);
      diagnostics.dormantAmount = Math.max(0, followedResult.unresolved);
      diagnostics.reverseResolvedAmount = diagnostics.totalExitFollowedAmount;
      diagnostics.reverseUnresolvedAmount = diagnostics.dormantAmount;

      if (diagnostics.dormantAmount > 1e-12) {
        diagnostics.warnings.push(`Reverse FIFO could not tie ${this.formatFunds(diagnostics.dormantAmount, context.token)} to concrete prior incoming source rows.`);
      }
      if (sources.length) {
        diagnostics.warnings.push(`Reverse FIFO found ${sources.length} prior source transfer${sources.length === 1 ? "" : "s"} for the selected outgoing range.`);
      } else if (rowsBeforeTarget.length > 0) {
        diagnostics.warnings.push("Reverse FIFO saw prior rows for this asset but none remained in the FIFO lot queue for the selected outgoing slice.");
      }

      return this.buildAccountFifoSolution(entry, sources, rowsBeforeTarget.concat([interestTx]), context, diagnostics, "reverse");
    },

    buildAccountFifoSolution(entry, exitTransactions, relevantTransactions, context, diagnostics, analysisDirection) {
      const positiveExitTransactions = (Array.isArray(exitTransactions) ? exitTransactions : []).filter((exit) => {
        const followedAmount = Number(exit?.followedAmount);
        return !Number.isFinite(followedAmount) || followedAmount > 1e-12;
      });
      const discardedZeroCount = Math.max(0, (Array.isArray(exitTransactions) ? exitTransactions.length : 0) - positiveExitTransactions.length);
      if (discardedZeroCount > 0) {
        if (!Array.isArray(diagnostics.warnings)) diagnostics.warnings = [];
        diagnostics.warnings.push(`Tool 88 discarded ${discardedZeroCount} zero-allocation source slice${discardedZeroCount === 1 ? "" : "s"} instead of exporting a meaningless path row.`);
      }
      const exitRows = positiveExitTransactions.map((exit, index) => {
        const row = this.buildExitRow({
          entry,
          exit,
          followedAmount: exit.followedAmount,
          leftBoundAmount: exit.leftBoundAmount,
          token: context.token,
        });
        const previousExit = positiveExitTransactions[index - 1];
        if (previousExit && previousExit.hash === exit.hash) {
          row[COL.TX_HASH] = "";
          row[COL.FROM_HASH] = entry?.txHash || "";
        }
        return row;
      });

      const accountWarningLabel = this.hopLogicType === "LIFO" ? "Account LIFO/PTDQ" : "Account FIFO";
      this.pushUniqueWarnings((diagnostics.warnings || []).map((warning) => `${accountWarningLabel}: ${warning}`));
      const dormantText = diagnostics.dormantAmount > 1e-12 ? this.formatFunds(diagnostics.dormantAmount, context.token) : "";
      const feeText = diagnostics.feeConsumedAmount > 1e-12 ? this.formatFunds(diagnostics.feeConsumedAmount, context.token) : "";
      const noRowsReason = diagnostics.preventPathEnding
        ? "Tool 88 could not safely run token FIFO because the followed ERC20/TRC20 contract address was not resolved from the effective Token Manager."
        : analysisDirection === "reverse"
          ? "Reverse FIFO could not resolve prior source transfer(s) for the selected outgoing range."
          : "No qualifying outgoing transfer was found after Tool 88 FIFO allocation.";
      return {
        status: exitRows.length
          ? (dormantText ? (analysisDirection === "reverse" ? "reverse_source_with_unresolved" : "mixed_exit_with_dormant") : (analysisDirection === "reverse" ? "reverse_source" : "exit"))
          : (analysisDirection === "reverse" ? "reverse_source_unresolved" : "dormant"),
        analysisKind: this.hopLogicType === "LIFO" ? "account_lifo_ptdq" : "account_fifo",
        solverKind: diagnostics.solver,
        analysisDirection,
        exitTransactions: positiveExitTransactions,
        exitRows,
        dormantAmountText: dormantText,
        dormantReason: dormantText
          ? (analysisDirection === "reverse" ? "Reverse FIFO could not tie all followed funds to prior source transfers." : "Some followed funds remain at the selected address after account FIFO allocation.")
          : "",
        feeConsumedAmountText: feeText,
        pathEndingReason: exitRows.length ? "" : noRowsReason,
        preventPathEnding: Boolean(diagnostics.preventPathEnding) || (analysisDirection === "reverse" && !exitRows.length),
        fundsIn: context.fundsIn,
        followedAmount: diagnostics.totalExitFollowedAmount,
        leftBoundAmount: positiveExitTransactions.length === 1 ? positiveExitTransactions[0].leftBoundAmount : null,
        relevantTransactions,
        accountAnalysis: diagnostics,
      };
    },

    solveHopForEntry(entry, selectedAddress, transactions) {
      if (this.shouldUseBtcFifoSolver(entry, selectedAddress, transactions)) {
        if (this.hopLogicType === "LIFO") {
          return this.traceDirection === "reverse"
            ? this.solveBtcReverseLifoForEntry(entry, selectedAddress, transactions)
            : this.solveBtcPtdqForEntry(entry, selectedAddress, transactions);
        }
        return this.traceDirection === "reverse"
          ? this.solveBtcReverseFifoForEntry(entry, selectedAddress, transactions)
          : this.solveBtcFifoForEntry(entry, selectedAddress, transactions);
      }

      if (this.shouldUseAccountFifoSolver(entry, selectedAddress, transactions)) {
        return this.solveAccountFifoForEntry(entry, selectedAddress, transactions);
      }

      const selectedKey = this.normalizeAddressKey(selectedAddress);
      const entryDate = this.getEntryDate(entry);
      const fundsIn = this.deriveFundsIn(entry, selectedAddress);
      const normalizedTransactions = (transactions || [])
        .map((tx) => this.normalizeProviderTransaction(tx))
        .filter((tx) => {
          const amount = Number(tx.amount);
          return tx.hash &&
            this.isTool88LikelyTransactionHash(tx.hash) &&
            tx.date &&
            !tx.failedOrReverted &&
            tx.analysisEligible !== false &&
            !tx.nonBalanceAffecting &&
            (!Number.isFinite(amount) || amount > 0) &&
            this.providerTransactionMatchesFollowedToken(tx, fundsIn.token);
        });

      let relevantTransactions = [];

      if (this.traceDirection === "reverse") {
        relevantTransactions = normalizedTransactions.filter((tx) => {
          const toMatches = this.normalizeAddressKey(tx.toAddress) === selectedKey;
          const beforeEntry = entryDate ? tx.date.getTime() < entryDate.getTime() : true;
          return toMatches && beforeEntry;
        });
        relevantTransactions.sort((left, right) => right.date.getTime() - left.date.getTime());
      } else {
        relevantTransactions = normalizedTransactions.filter((tx) => {
          const fromMatches = this.normalizeAddressKey(tx.fromAddress) === selectedKey;
          const afterEntry = entryDate ? tx.date.getTime() > entryDate.getTime() : true;
          return fromMatches && afterEntry;
        });
        relevantTransactions.sort((left, right) => left.date.getTime() - right.date.getTime());
      }

      if (!relevantTransactions.length) {
        const dormantAmountText = this.formatFunds(fundsIn.amount, fundsIn.token);
        return {
          status: "dormant",
          exitTransactions: [],
          exitRows: [],
          dormantAmountText,
          fundsIn,
          relevantTransactions,
        };
      }

      const selectedExit = relevantTransactions[0];
      const exitAmount = Number.isFinite(selectedExit.amount) ? selectedExit.amount : fundsIn.amount;
      const followedAmount = Number.isFinite(fundsIn.amount) && Number.isFinite(exitAmount)
        ? Math.min(fundsIn.amount, exitAmount)
        : exitAmount;
      const leftBoundAmount = Number.isFinite(fundsIn.amount) && Number.isFinite(followedAmount)
        ? Math.max(0, fundsIn.amount - followedAmount)
        : null;
      const token = selectedExit.token || fundsIn.token;
      const exitRows = [
        this.buildExitRow({
          entry,
          exit: selectedExit,
          followedAmount,
          leftBoundAmount,
          token,
        }),
      ];

      return {
        status: leftBoundAmount > 0 ? "partial_exit" : "exit",
        exitTransactions: [selectedExit],
        exitRows,
        dormantAmountText: leftBoundAmount > 0 ? this.formatFunds(leftBoundAmount, token) : "",
        fundsIn,
        followedAmount,
        leftBoundAmount,
        relevantTransactions,
      };
    },

    buildExitRow({ entry, exit, followedAmount, leftBoundAmount, token }) {
      const row = this.makeBlankRow();
      const amountText = Number.isFinite(exit.amount) ? this.formatAmount(exit.amount) : "";
      const leftText = Number.isFinite(leftBoundAmount) ? this.formatAmount(leftBoundAmount) : "";
      const tokenText = token || exit.token || "";
      const dateText = exit.date ? this.formatUtcDate(exit.date) : exit.dateText;
      const hopLogicLabel = this.hopLogicType;

      row[COL.PATH_LABEL] = "";
      row[COL.TX_HASH] = exit.hash;
      row[COL.FROM_ADDRESS] = exit.fromAddress;
      row[COL.TO_ADDRESS] = exit.toAddress;
      row[COL.DATE] = dateText;
      row[COL.AMOUNT] = amountText;
      row[COL.TOKEN] = tokenText;
      const caseBuddyBalanceSatoshis = Number(
        exit.priorBalanceBeforeTransactionSatoshis
          ?? exit.priorBalanceSatoshis
          ?? exit.balanceBeforeTransactionSatoshis
      );
      const caseBuddyBalanceNumber = Number(
        exit.recipientPriorBalanceBeforeTransaction
          ?? exit.toAddressPriorBalanceBeforeTransaction
          ?? exit.addressOfInterestPriorBalanceBeforeTransaction
          ?? exit.rowPriorBalanceBeforeTransaction
          ?? exit.priorBalanceBeforeTransactionForRow
          ?? exit.priorBalanceBeforeTransaction
      );
      if (String(tokenText || '').toUpperCase() === 'BTC' && Number.isFinite(caseBuddyBalanceSatoshis) && caseBuddyBalanceSatoshis >= 0) {
        row[COL.PRIOR_BALANCE] = this.formatBtcSatsCompact(caseBuddyBalanceSatoshis).replace(/ BTC$/, '');
      } else if (Number.isFinite(caseBuddyBalanceNumber) && caseBuddyBalanceNumber >= 0) {
        const balanceDecimals = String(tokenText || '').toUpperCase() === 'BTC' ? 8 : 4;
        row[COL.PRIOR_BALANCE] = caseBuddyBalanceNumber.toFixed(balanceDecimals);
      }
      const btcOutputNote = exit.outputIndex !== undefined && exit.outputIndex !== null
        ? ` BTC output index ${exit.outputIndex}.`
        : "";
      const btcRangeNote = Number.isFinite(exit.leftBoundsSatoshis) && Number.isFinite(exit.rightBoundsSatoshis)
        ? ` Followed BTC range: sats ${exit.leftBoundsSatoshis} to ${exit.rightBoundsSatoshis} of this output.`
        : "";
      const isReverseSource = this.traceDirection === "reverse" || this.toText(exit.transactionKind).includes("reverse_fifo_source");
      const isBtcLikeExit = this.toText(exit.transactionKind).toLowerCase().includes("btc") || exit.outputIndex !== undefined;
      const roleText = isReverseSource
        ? (isBtcLikeExit ? "selected this prior BTC source output" : "selected this prior source transfer")
        : "selected this transaction as the next hop";
      this.setTool88NoteOnRow(row, `Tool 88: ${this.directionLabel} ${hopLogicLabel} ${roleText}.${btcOutputNote}${btcRangeNote}`);
      const sourceBalanceBeforeExit = Number(exit.sourceAddressPriorBalanceBeforeTransaction);
      if (Number.isFinite(sourceBalanceBeforeExit) && sourceBalanceBeforeExit >= 0) {
        this.appendTool88NoteToRow(row, `Tool 88 FIFO diagnostics: source-address balance before this exit was ${this.formatFunds(sourceBalanceBeforeExit, tokenText)}.`);
      }
      if (Number.isFinite(caseBuddyBalanceNumber) && caseBuddyBalanceNumber >= 0) {
        this.appendTool88NoteToRow(row, `Tool 88 FIFO diagnostics: receiver prior balance before this transaction was ${this.formatFunds(caseBuddyBalanceNumber, tokenText)}.`);
      }
      row[COL.FUNDS_FOLLOWED] = this.formatFunds(followedAmount, tokenText);
      row[COL.FROM_HASH] = entry?.txHash || "";
      row[COL.CONTINUES_AS] = "Path ends.";
      // Column R's first line should stay as the numeric left-bound coordinate.
      // Case Buddy rebuilds the richer explanatory Column R details after the row
      // is accepted. Keeping Tool 88 output numeric here prevents token/note text
      // from becoming part of the canonical left-bound value.
      row[COL.LEFT_BOUNDS_DISPLAY] = this.formatCaseBuddyLeftBoundsAmount(leftBoundAmount || 0, tokenText);
      row[COL.LEFT_BOUNDS_NUMERIC] = leftText;
      row[COL.CHAIN_ID] = exit.chainId || entry?.chainId || "";
      row[COL.CHAIN_NAME] = exit.chainName || entry?.chainName || "";
      row[COL.HOP_LOGIC] = hopLogicLabel;

      return row;
    },

    markEntryContinuesAs(baseRows, entry, exits) {
      const outputRows = baseRows.map(row => this.cloneRowPreservingValues(row));
      const bounds = this.findEntryBoundsInRows(outputRows, entry);
      const entryStartIndex = bounds?.startRowIndex ?? Math.max(0, Number(entry?.startRowIndex || 0));
      const entryEndIndex = bounds?.endRowIndex ?? entryStartIndex;
      const selectedKey = this.normalizeAddressKey(this.selectedTargetAddress);
      let targetRowIndex = entryStartIndex;
      if (selectedKey) {
        for (let index = entryStartIndex; index <= entryEndIndex; index += 1) {
          if (this.normalizeAddressKey(outputRows[index]?.[this.getTargetColumnIndex()]) === selectedKey) {
            targetRowIndex = index;
            break;
          }
        }
      }
      const entryRow = outputRows[targetRowIndex] || this.makeBlankRow();
      const hashes = this.getCaseBuddyExitChildHashes(exits, entry?.txHash || entryRow?.[COL.TX_HASH]);
      entryRow[COL.CONTINUES_AS] = hashes.length
        ? `Continues as ${hashes.join(', ')}`
        : 'Path ends.';
      if (hashes.length) {
        this.removeRawDataPathEndsSentence(entryRow);
        this.removeTool88ManualPathEndingNotes(entryRow);
        this.removeReverseDormantNotesForTraceThrough(entryRow);
      }
      if (this.isSelectedEntrySolvedSwapContext(this.selectedTargetAddress, entry)) {
        this.removeRawDataPathEndsSentence(entryRow);
        this.removeTool88ManualPathEndingNotes(entryRow);
      }
      const traceThroughEntityMatch = this.getAutomaticTraceThroughEntityOverrideMatch(this.selectedTargetAddress);
      if (traceThroughEntityMatch) {
        this.appendRawDataTraceThroughEntityAttributionNote(entryRow, traceThroughEntityMatch, this.selectedTargetAddress);
      }
      const traceThroughHandleLabel = this.getTraceThroughFriendlyHandleAttributionLabel(this.getAutomaticDecisionAttributionLabels());
      if (traceThroughHandleLabel) {
        this.removeRawDataPathEndsSentence(entryRow);
        this.appendRawDataTraceThroughHandleAttributionNote(entryRow, traceThroughHandleLabel);
      }
      if (hashes.length) {
        this.appendTool88NoteToRow(
          entryRow,
          `Tool 88: ${this.directionLabel} ${this.hopLogicType} traced through ${this.selectedTargetAddress}.`
        );
        this.appendFlowCompletionMarkerToRow(entryRow, 'traced-through', `target=${this.selectedTargetAddress || 'unknown'}`);
      } else {
        this.appendTool88NoteToRow(
          entryRow,
          `Tool 88: Trace-through was attempted for ${this.selectedTargetAddress || 'the selected address'}, but no exportable child transaction hash was retained. This ending remains unresolved.`
        );
      }
      entryRow[COL.HOP_LOGIC] = this.hopLogicType;
      outputRows[targetRowIndex] = entryRow;
      return outputRows;
    },


    isBtcPathEndingLeftBoundsContext(row = [], entry = null, selectedAddress = "", fundsIn = {}, token = "") {
      const normalizedToken = this.toText(token || fundsIn?.token || row?.[COL.TOKEN]).trim().toUpperCase();
      if (normalizedToken === "BTC") return true;
      if (this.isBtcAddress(selectedAddress) || this.isBtcAddress(row?.[COL.FROM_ADDRESS]) || this.isBtcAddress(row?.[COL.TO_ADDRESS])) return true;
      return this.isBtcHopContext(entry, selectedAddress, []);
    },

    extractBtcLeftBoundsSatoshisFromTool88Notes(row = []) {
      const noteText = this.toText(row?.[COL.TOOL88_NOTES]);
      const match = noteText.match(/Followed\s+BTC\s+range:\s*sats\s*([0-9][0-9,]*)\s+to\s+([0-9][0-9,]*)\s+of\s+this\s+output/i);
      if (!match) return null;
      const parsed = Number(String(match[1] || "").replace(/,/g, ""));
      return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : null;
    },

    preserveBtcPathEndingLeftBounds(row = [], entry = null, selectedAddress = "") {
      const noteLeftSatoshis = this.extractBtcLeftBoundsSatoshisFromTool88Notes(row);
      if (noteLeftSatoshis !== null) {
        const leftText = this.formatBtcSatsCompact(noteLeftSatoshis).replace(/ BTC$/, "");
        row[COL.LEFT_BOUNDS_DISPLAY] = leftText;
        row[COL.LEFT_BOUNDS_NUMERIC] = leftText;
        return row;
      }

      const existingNumeric = this.parseNumberLike(row?.[COL.LEFT_BOUNDS_NUMERIC]);
      const existingDisplay = this.toText(row?.[COL.LEFT_BOUNDS_DISPLAY]);
      const existingDisplayNumeric = this.parseNumberLike(existingDisplay);
      const existingAmount = Number.isFinite(existingNumeric)
        ? Math.max(0, existingNumeric)
        : (Number.isFinite(existingDisplayNumeric) ? Math.max(0, existingDisplayNumeric) : null);
      const existingLooksLikeDormantWrite = /automatic\s+decision|dormant\/(?:left-bound|funds-followed)|path\s+ends\s+at/i.test(existingDisplay);
      if (existingAmount !== null && !existingLooksLikeDormantWrite) {
        const leftText = this.formatAmount(existingAmount);
        if (!existingDisplay) {
          row[COL.LEFT_BOUNDS_DISPLAY] = leftText;
        }
        row[COL.LEFT_BOUNDS_NUMERIC] = leftText;
        return row;
      }

      const selectedRow = existingLooksLikeDormantWrite ? null : this.getSelectedTargetRow(entry, selectedAddress);
      const selectedLeft = selectedRow ? this.getPlainRowLeftBoundsAmount(selectedRow) : 0;
      const leftText = this.formatAmount(Number.isFinite(selectedLeft) ? selectedLeft : 0);
      row[COL.LEFT_BOUNDS_DISPLAY] = leftText;
      row[COL.LEFT_BOUNDS_NUMERIC] = leftText;
      return row;
    },

    finalizeEntryAsPathEnding({ baseRows, entry, selectedAddress, reason, dormantAmountText, suppressDormantFallback = false, suppressRawPathEndingAttributionNote = false, dormantOutcome = false }) {
      const outputRows = baseRows.map((row) => this.cloneRowPreservingValues(row));
      const bounds = this.findEntryBoundsInRows(outputRows, entry);
      const entryStartIndex = bounds?.startRowIndex ?? Math.max(0, Number(entry?.startRowIndex || 0));
      const entryRow = outputRows[entryStartIndex] || this.makeBlankRow();
      const fundsIn = this.deriveFundsIn(entry, selectedAddress);
      const fallbackDormantText = !suppressDormantFallback && Number.isFinite(fundsIn.amount)
        ? this.normalizeTool88DormantAmountText(fundsIn.amount, fundsIn.token)
        : "";
      const finalDormantText = this.normalizeTool88DormantAmountText(dormantAmountText || fallbackDormantText, fundsIn.token || entryRow[COL.TOKEN] || "");
      const cleanReason = this.toText(reason) || "Tool 88 path ending recorded.";
      const token = this.parseTokenLike(finalDormantText) || fundsIn.token;
      const isBtcPathEnding = this.isBtcPathEndingLeftBoundsContext(entryRow, entry, selectedAddress, fundsIn, token);
      const existingLeftBoundsDisplay = this.toText(entryRow[COL.LEFT_BOUNDS_DISPLAY]).trim();
      const existingLeftBoundsNumeric = this.toText(entryRow[COL.LEFT_BOUNDS_NUMERIC]).trim();
      const amountLabel = isBtcPathEnding ? "Followed BTC amount at path ending" : "Funds followed at ending";
      const dormantNote = finalDormantText ? this.buildTool88DormantFundsNote(finalDormantText, true) : "";
      const note = `Tool 88: Path ends at ${selectedAddress}. ${cleanReason}${finalDormantText ? ` ${amountLabel}: ${finalDormantText}.` : ""}`;

      entryRow[COL.CONTINUES_AS] = "Path ends.";
      const rawPathEndingAttributionLabel = suppressRawPathEndingAttributionNote
        ? ""
        : this.getRawDataPathEndingAttributionLabel(entry, selectedAddress, entryRow);
      if (rawPathEndingAttributionLabel) {
        this.appendRawDataPathEndingAttributionNote(entryRow, rawPathEndingAttributionLabel, selectedAddress);
      }
      if (dormantOutcome && dormantNote) {
        entryRow[COL.SYSTEM_NOTES] = this.appendSentenceNote(entryRow[COL.SYSTEM_NOTES], dormantNote);
        this.appendTool88NoteToRow(entryRow, dormantNote);
      } else if (this.isPotentialServicePathEndingReason(cleanReason)) {
        this.appendRawDataPotentialServicePathEndingNote(entryRow);
      }
      this.appendTool88NoteToRow(entryRow, note);
      this.appendFlowCompletionMarkerToRow(entryRow, dormantOutcome ? "dormant-ending" : "manual-path-ending", cleanReason.slice(0, 180));
      entryRow[COL.HOP_LOGIC] = this.hopLogicType;

      if (isBtcPathEnding) {
        this.preserveBtcPathEndingLeftBounds(entryRow, entry, selectedAddress);
      } else {
        const parsedExistingLeft = this.parseNumberLike(existingLeftBoundsNumeric || existingLeftBoundsDisplay);
        const derivedLeftBounds = Number.isFinite(parsedExistingLeft)
          ? Math.max(0, parsedExistingLeft)
          : Math.max(0, Number(this.getEntryLeftBoundsAmount(entry, selectedAddress)) || 0);
        const leftBoundsText = this.formatAmount(derivedLeftBounds);
        entryRow[COL.LEFT_BOUNDS_DISPLAY] = leftBoundsText;
        entryRow[COL.LEFT_BOUNDS_NUMERIC] = leftBoundsText;
      }

      outputRows[entryStartIndex] = entryRow;
      return {
        rows: outputRows,
        note,
        dormantNote,
        dormantAmountText: finalDormantText,
        reason: cleanReason,
        rawPathEndingAttributionLabel,
      };
    },

    rowMatchesEntryStart(row = [], entry = null) {
      if (!entry || !row) return false;
      const entryHash = this.toText(entry.txHash).trim();
      if (!entryHash || this.toText(row?.[COL.TX_HASH]).trim() !== entryHash) return false;

      const entryPathLabel = this.toText(entry.pathLabel).trim();
      const rowPathLabel = this.toText(row?.[COL.PATH_LABEL]).trim();
      if (entryPathLabel && rowPathLabel && entryPathLabel !== rowPathLabel) return false;

      const firstEntryRow = entry.rows?.[0]?.row || [];
      const strictAddressColumns = [COL.FROM_ADDRESS, COL.TO_ADDRESS];
      for (const columnIndex of strictAddressColumns) {
        const expected = this.normalizeAddressKey(firstEntryRow?.[columnIndex]);
        const actual = this.normalizeAddressKey(row?.[columnIndex]);
        if (expected && actual && expected !== actual) return false;
      }
      const expectedParent = this.normalizeHashKey(firstEntryRow?.[COL.FROM_HASH]);
      const actualParent = this.normalizeHashKey(row?.[COL.FROM_HASH]);
      if (expectedParent && actualParent && expectedParent !== actualParent) return false;
      const expectedToken = this.toText(firstEntryRow?.[COL.TOKEN]).trim().toUpperCase();
      const actualToken = this.toText(row?.[COL.TOKEN]).trim().toUpperCase();
      if (expectedToken && actualToken && expectedToken !== actualToken) return false;
      const expectedOutputIndex = this.getCaseBuddyOutputIndex(firstEntryRow);
      const actualOutputIndex = this.getCaseBuddyOutputIndex(row);
      if (Number.isInteger(expectedOutputIndex) && Number.isInteger(actualOutputIndex) && expectedOutputIndex !== actualOutputIndex) return false;
      const expectedFunds = this.parseNumberLike(firstEntryRow?.[COL.FUNDS_FOLLOWED]);
      const actualFunds = this.parseNumberLike(row?.[COL.FUNDS_FOLLOWED]);
      if (Number.isFinite(expectedFunds) && Number.isFinite(actualFunds)) {
        const tolerance = Math.max(1e-12, Math.abs(expectedFunds) * 1e-10);
        if (Math.abs(expectedFunds - actualFunds) > tolerance) return false;
      }

      const comparedColumns = [COL.FROM_ADDRESS, COL.TO_ADDRESS, COL.DATE, COL.AMOUNT, COL.TOKEN, COL.FUNDS_FOLLOWED, COL.FROM_HASH];
      let comparable = 0;
      let matches = 0;
      comparedColumns.forEach((columnIndex) => {
        const expected = this.toText(firstEntryRow?.[columnIndex]).trim();
        const actual = this.toText(row?.[columnIndex]).trim();
        if (!expected) return;
        comparable += 1;
        if (columnIndex === COL.AMOUNT) {
          const token = this.toText(firstEntryRow?.[COL.TOKEN] || row?.[COL.TOKEN]);
          const expectedAmount = this.formatCaseBuddyTransactionAmountNoRound(expected, token);
          const actualAmount = this.formatCaseBuddyTransactionAmountNoRound(actual, token);
          if (expectedAmount === actualAmount) matches += 1;
          return;
        }
        if (expected === actual) matches += 1;
      });

      if (!comparable) return true;
      return matches >= Math.min(2, comparable);
    },

    findEntryBoundsInRows(rows = [], entry = null) {
      if (!entry || !Array.isArray(rows) || !rows.length) return null;

      const localHeaderDetected = Boolean(
        this.isLikelyHeaderRow?.(rows[0]) === true ||
        this.isCaseBuddySnapshotHeaderRow?.(rows[0]) === true
      );
      const sourceHeaderDetected = this.headerDetected === true;
      const preferredStartIndex = Number(entry.startRowIndex);
      const adjustedPreferredStartIndex = Number.isInteger(preferredStartIndex)
        ? preferredStartIndex - (sourceHeaderDetected ? 1 : 0) + (localHeaderDetected ? 1 : 0)
        : preferredStartIndex;
      const preferredCandidates = Array.from(new Set([
        preferredStartIndex,
        adjustedPreferredStartIndex,
      ].filter(index => Number.isInteger(index) && index >= 0 && index < rows.length)));

      for (const candidateIndex of preferredCandidates) {
        if (!this.rowMatchesEntryStart(rows[candidateIndex], entry)) continue;
        let endRowIndex = candidateIndex;
        for (let index = candidateIndex + 1; index < rows.length; index += 1) {
          if (this.toText(rows[index]?.[COL.TX_HASH]).trim()) break;
          endRowIndex = index;
        }
        return {
          startRowIndex: candidateIndex,
          endRowIndex,
          matchedBy: candidateIndex === preferredStartIndex ? 'preferred-index' : 'header-adjusted-index',
        };
      }

      const dataStartIndex = localHeaderDetected ? 1 : 0;
      const collected = this.collectEntries(this.trimTrailingBlankRows(rows), dataStartIndex);
      const entryHash = this.toText(entry.txHash).trim();
      const entryPathLabel = this.toText(entry.pathLabel).trim();
      const candidates = collected.entries.filter((candidate) => {
        if (this.toText(candidate.txHash).trim() !== entryHash) return false;
        const candidatePathLabel = this.toText(candidate.pathLabel).trim();
        if (entryPathLabel && candidatePathLabel && candidatePathLabel !== entryPathLabel) return false;
        return this.rowMatchesEntryStart(candidate.rows?.[0]?.row || [], entry);
      });

      if (!candidates.length) return null;
      const sortIndex = Number.isInteger(adjustedPreferredStartIndex) ? adjustedPreferredStartIndex : 0;
      candidates.sort((left, right) => Math.abs(left.startRowIndex - sortIndex) - Math.abs(right.startRowIndex - sortIndex));
      const best = candidates[0];
      return { startRowIndex: best.startRowIndex, endRowIndex: best.endRowIndex, matchedBy: 'entry-signature' };
    },

    replaceEntryWithRows(baseRows, entry, rowsToInsert) {
      const outputRows = baseRows.map((row) => this.cloneRowPreservingValues(row));
      const bounds = this.findEntryBoundsInRows(outputRows, entry);
      const start = Math.max(0, Number(bounds?.startRowIndex ?? entry?.startRowIndex ?? 0));
      const end = Math.max(start, Number(bounds?.endRowIndex ?? entry?.endRowIndex ?? start));
      const deleteCount = Math.max(1, end - start + 1);
      outputRows.splice(start, deleteCount, ...rowsToInsert.map((row) => this.cloneRowPreservingValues(row)));
      return outputRows;
    },

    insertRowsAfterEntry(baseRows, entry, rowsToInsert) {
      const outputRows = baseRows.map((row) => this.cloneRowPreservingValues(row));
      const bounds = this.findEntryBoundsInRows(outputRows, entry);
      const fallbackEnd = Math.max(0, Number(entry?.endRowIndex ?? entry?.startRowIndex ?? 0));
      const insertAt = Math.min((bounds?.endRowIndex ?? fallbackEnd) + 1, outputRows.length);
      outputRows.splice(insertAt, 0, ...rowsToInsert.map((row) => this.cloneRowPreservingValues(row)));
      return outputRows;
    },

    getOutputRowMatchKey(row = []) {
      return this.getCaseBuddyTraceBranchKey(row, row?.[COL.FROM_HASH] || '');
    },

    getOutputRowDisplayKey(row = []) {
      const hash = this.toText(row?.[COL.TX_HASH]);
      if (hash) return this.shortenMiddle(hash, 12, 8);
      const toAddress = this.toText(row?.[COL.TO_ADDRESS]);
      if (toAddress) return this.shortenMiddle(toAddress, 12, 8);
      return "unnamed output row";
    },

    verifyTraceOutputMutation(outputRows = [], expectedRows = [], beforeRows = null) {
      const expected = [];
      const seenExpectedKeys = new Set();
      (Array.isArray(expectedRows) ? expectedRows : [])
        .filter(row => row && this.rowHasAnyData(row))
        .forEach((row) => {
          const key = this.getOutputRowMatchKey(row);
          const hash = this.normalizeHashKey(row?.[COL.TX_HASH]);
          if (!key && !hash) return;
          if (key && seenExpectedKeys.has(key)) return;
          if (key) seenExpectedKeys.add(key);
          expected.push({
            key,
            label: this.getOutputRowDisplayKey(row),
            hash,
          });
        });

      if (!expected.length) {
        return {
          ok: true,
          expected: [],
          missing: [],
          changed: true,
          rowCountDelta: 0,
          newlyPresent: true,
          alreadyPresent: false,
          duplicateExpectedHashes: [],
        };
      }

      const afterRows = this.trimTrailingBlankRows(Array.isArray(outputRows) ? outputRows : []);
      const beforeRowsSafe = beforeRows === null
        ? null
        : this.trimTrailingBlankRows(Array.isArray(beforeRows) ? beforeRows : []);
      const dataRowsForVerification = (rows = []) => (Array.isArray(rows) ? rows : [])
        .filter(row => row && this.rowHasAnyData(row))
        .filter(row => !(typeof this.isLikelyHeaderRow === 'function' && this.isLikelyHeaderRow(row)));
      const afterDataRows = dataRowsForVerification(afterRows);
      const beforeDataRows = beforeRowsSafe === null ? [] : dataRowsForVerification(beforeRowsSafe);
      const countValues = (values = []) => {
        const counts = new Map();
        values.filter(Boolean).forEach(value => counts.set(value, (counts.get(value) || 0) + 1));
        return counts;
      };
      const keyCountsForRows = (rows = []) => countValues(
        rows.map(row => this.getOutputRowMatchKey(row)).filter(Boolean)
      );
      const hashCountsForRows = (rows = []) => countValues(
        rows.map(row => this.normalizeHashKey(row?.[COL.TX_HASH])).filter(Boolean)
      );
      const afterKeyCounts = keyCountsForRows(afterDataRows);
      const beforeKeyCounts = keyCountsForRows(beforeDataRows);
      const afterHashCounts = hashCountsForRows(afterDataRows);
      const beforeHashCounts = hashCountsForRows(beforeDataRows);
      const expectedHashCounts = countValues(expected.map(item => item.hash).filter(Boolean));

      const missing = [];
      expected.forEach((item) => {
        if (item.key) {
          if ((afterKeyCounts.get(item.key) || 0) < 1) {
            missing.push(`${item.key} (0/1)`);
          }
          return;
        }
        if (item.hash && (afterHashCounts.get(item.hash) || 0) < 1) {
          missing.push(`${item.label} (0/1)`);
        }
      });

      const newlyPresent = beforeRowsSafe === null || expected.some((item) => {
        if (item.key) {
          return (afterKeyCounts.get(item.key) || 0) > (beforeKeyCounts.get(item.key) || 0);
        }
        return item.hash && (afterHashCounts.get(item.hash) || 0) > (beforeHashCounts.get(item.hash) || 0);
      });
      const changed = beforeRowsSafe === null ? true : !this.rowsMateriallySame(beforeRowsSafe, afterRows);
      const beforeDataCount = beforeRowsSafe === null ? 0 : beforeDataRows.length;
      const afterDataCount = afterDataRows.length;
      const rowCountDelta = beforeRowsSafe === null ? 0 : afterDataCount - beforeDataCount;
      const alreadyPresent = missing.length === 0 && !newlyPresent;

      // Trace application is idempotent. Presence of every stable on-chain branch
      // is success even when a retry makes no new row because the row was already
      // committed during a prior segment. Requiring row-count growth caused Tool 88
      // to append the same child indefinitely.
      return {
        ok: missing.length === 0,
        expected: expected.map(item => item.label),
        missing,
        changed,
        rowCountDelta,
        newlyPresent,
        alreadyPresent,
        duplicateExpectedHashes: Array.from(expectedHashCounts.entries())
          .filter(([, count]) => count > 1)
          .map(([hash, count]) => ({ hash, count })),
      };
    },

    repairTraceOutputInsertion(outputRows = [], entry = null, expectedRows = [], beforeRows = null) {
      const merge = this.mergeCaseBuddyTraceRowsIdempotently(outputRows, entry, expectedRows);
      let repairedRows = this.repairCaseBuddyTracedContinuationRows(merge.rows);
      let verification = this.verifyTraceOutputMutation(repairedRows, expectedRows, beforeRows);
      let forceRepairApplied = false;
      let forcedMerge = null;

      if (entry) {
        const selectedParentHash = this.toText(entry?.txHash || entry?.row?.[COL.TX_HASH]).trim();
        const selectedParentKey = this.normalizeHashKey(selectedParentHash);
        const selectedTargetKey = this.normalizeAddressKey(this.selectedTargetAddress);
        let forcedParentChanged = false;
        const forcedExpectedRows = this.getCaseBuddyExportableTraceRows(expectedRows)
          .map((row) => {
            const next = this.cloneRowPreservingValues(row);
            const rowHash = this.normalizeHashKey(next?.[COL.TX_HASH]);
            const currentParentKey = this.normalizeHashKey(next?.[COL.FROM_HASH]);
            const childFromKey = this.normalizeAddressKey(next?.[COL.FROM_ADDRESS]);
            if (
              selectedParentHash
              && rowHash
              && rowHash !== selectedParentKey
              && currentParentKey !== selectedParentKey
              && (!selectedTargetKey || !childFromKey || childFromKey === selectedTargetKey)
            ) {
              next[COL.FROM_HASH] = selectedParentHash;
              forcedParentChanged = true;
            }
            if (selectedTargetKey && !this.normalizeAddressKey(next?.[COL.FROM_ADDRESS])) {
              next[COL.FROM_ADDRESS] = this.selectedTargetAddress;
            }
            return next;
          });

        // Even when the raw expected row is already present under a sibling parent,
        // the selected branch still needs its own exportable child row. This happens
        // when two unresolved siblings share the same target address and Tool 88's
        // preview reuses the already-present child row from the sibling. Force a
        // selected-parent branch instance so Manual Solve and Lite both graft the
        // preview answer onto the node the investigator actually selected.
        if (forcedExpectedRows.length && (!verification.ok || forcedParentChanged)) {
          let forcedRows = this.markEntryContinuesAs(repairedRows, entry, forcedExpectedRows);
          forcedMerge = this.mergeCaseBuddyTraceRowsIdempotently(forcedRows, entry, forcedExpectedRows);
          forcedRows = this.repairCaseBuddyTracedContinuationRows(forcedMerge.rows);
          const forcedVerification = this.verifyTraceOutputMutation(forcedRows, forcedExpectedRows, beforeRows);
          const forcedContinuation = this.caseBuddyTraceContinuationState(forcedRows, forcedExpectedRows);
          if (forcedVerification.ok && forcedContinuation.parentContinuationVerified) {
            repairedRows = forcedRows;
            verification = {
              ...forcedVerification,
              parentContinuationVerified: true,
              forcedSelectedParentRepair: true,
              expectedChildHashes: forcedContinuation.expectedChildHashes,
              referencedChildHashes: forcedContinuation.referencedChildHashes,
            };
            forceRepairApplied = true;
          }
        }
      }

      return {
        rows: repairedRows,
        verification: {
          ...verification,
          insertedCount: Number(merge.insertedCount || 0) + Number(forcedMerge?.insertedCount || 0),
          alreadyPresentCount: Number(merge.alreadyPresentCount || 0) + Number(forcedMerge?.alreadyPresentCount || 0),
          removedDuplicateCount: Number(merge.removedDuplicateCount || 0) + Number(forcedMerge?.removedDuplicateCount || 0),
          stableBranchIdentity: true,
          forcedSelectedParentRepair: forceRepairApplied,
        },
        repaired: Boolean(merge.insertedCount || merge.removedDuplicateCount || forceRepairApplied),
      };
    },

    buildChecksReportLines() {
      const lines = [];
      this.preTraceChecks.forEach((check) => {
        lines.push(`- ${check.title}: ${check.statusLabel}. ${check.summary}`);
        if (check.detail) {
          lines.push(`  ${check.detail}`);
        }
      });
      return lines;
    },

    buildParsingReport({ rows, entries, selectedEntry, candidates, selectedAddress, stageMessage }) {
      const lines = [];
      lines.push("Tool 88 Hop Logic Solver Report");
      lines.push("----------------------------------------");
      lines.push("Pass 1: Input parsing and target selection");
      lines.push(`Rows loaded: ${rows.filter((row) => this.rowHasAnyData(row)).length}`);
      lines.push(`Header row detected: ${this.headerDetected ? "Yes" : "No"}`);
      lines.push(`Entries found: ${entries.length}`);
      lines.push(`Direction: ${this.directionLabel}`);
      lines.push(`Target column: ${this.targetColumnLabel}`);
      lines.push(`Hop logic type: ${this.hopLogicType}`);
      lines.push(`Tool 88 note output: ${this.tool88NoteColumnLabel}`);

      if (selectedEntry) {
        lines.push(`Selected entry: ${this.formatEntryOption(selectedEntry)}`);
      }

      lines.push(`Candidate addresses found: ${candidates.length}`);
      candidates.forEach((candidate) => {
        const amountText = candidate.amountCount
          ? ` / summed row amount ${this.formatAmount(candidate.amountTotal)} ${candidate.token || ""}`.trim()
          : "";
        lines.push(`- ${candidate.address} on rows ${candidate.rowNumbers.join(", ")}${amountText}`);
      });

      if (selectedAddress) {
        lines.push(`Selected address: ${selectedAddress}`);
      }

      lines.push("");
      lines.push(stageMessage || "Ready for transaction count, attribution, and signal checks.");
      return lines.join("\n");
    },

    buildCountReport(entry, selectedAddress) {
      const lines = [];
      lines.push(
        this.buildParsingReport({
          rows: this.inputSnapshotRows,
          entries: this.parsedEntries,
          selectedEntry: entry,
          candidates: this.targetCandidates,
          selectedAddress,
          stageMessage: "Target address selected. Transaction count, attribution, and transaction-signal checks complete.",
        })
      );
      lines.push("");
      lines.push("Pass 2: Transaction count lookup and pre-trace checks");
      lines.push(`Lookup source: ${this.countLookupResult.source}`);
      if (this.countLookupResult.provider || this.countLookupResult.providerLabel) {
        lines.push(`Provider: ${this.countLookupResult.providerLabel || this.countLookupResult.provider}`);
      }
      if (this.countLookupResult.route || this.countLookupResult.routeLabel) {
        lines.push(`Count route: ${this.countLookupResult.routeLabel || this.countLookupResult.route}`);
      }
      if (this.countLookupResult.countKind) lines.push(`Count type: ${this.formatCountKind(this.countLookupResult.countKind)}`);
      if (this.countLookupResult.chainLabel || this.countLookupResult.chainName) lines.push(`Network: ${this.countLookupResult.chainLabel || this.countLookupResult.chainName}`);
      if (this.countLookupResult.tokenSymbol) lines.push(`Token/context: ${this.countLookupResult.tokenSymbol}`);
      const tokenResolution = this.selectedTokenResolution;
      if (tokenResolution) {
        lines.push(`Token manager: ${tokenResolution.title}`);
        lines.push(`Token manager detail: ${tokenResolution.summary}`);
      }
      lines.push(`Address: ${this.countLookupResult.address}`);
      lines.push(`Count availability: ${this.countLookupResult.countAvailable ? "Available" : "Unavailable"}`);
      lines.push(`Additional service checks: ${this.enableAdditionalServiceChecks ? "On" : "Off"}`);
      lines.push(`Native amount/date match shortcut: ${(this.caseBuddyNativeDirectSpendGuardForcedOff === true || this.enableNativeDirectSpendMatchGuard !== true) ? "Off / strict FIFO" : "On"}`);
      lines.push(`Deposit hot-wallet probe: ${this.enableDepositEntityHopExtension ? "On" : "Off"}`);
      lines.push(`Session cache: ${this.enableSessionDataCache ? "On" : "Off"}`);
      if (this.countLookupResult.cacheHit) {
        lines.push(`Count cache: reused from this browser session${this.countLookupResult.cachedAt ? ` (${this.countLookupResult.cachedAt})` : ""}.`);
      }
      lines.push(`Transaction count: ${this.formatCountValue(this.countLookupResult)}`);
      if (this.countLookupResult.countUnavailableReason) {
        lines.push(`Count note: ${this.countLookupResult.countUnavailableReason}`);
      }
      lines.push(`Recommendation: ${this.countLookupResult.recommendation}`);
      if (this.attributionLookupResult) {
        lines.push("");
        lines.push("Attribution lookup:");
        lines.push(`Attribution source: ${this.attributionLookupResult.sourceLabel}`);
        lines.push(`Label: ${this.attributionLookupResult.displayLabel || "None"}`);
        if (this.attributionLookupResult.serverNote) lines.push(`Server note: ${this.attributionLookupResult.serverNote}`);
        if (this.attributionLookupResult.vendorLabel) lines.push(`Vendor label: ${this.attributionLookupResult.vendorLabel}`);
        lines.push(`Classification: ${this.formatAttributionClassification(this.attributionLookupResult.classification || this.attributionLookupResult.entityType)}`);
        if (this.attributionLookupResult.signalsText) lines.push(`Signals: ${this.attributionLookupResult.signalsText}`);
        lines.push(`Attribution recommendation: ${this.attributionLookupResult.recommendation}`);
      }
      if (this.transactionSignalResult) {
        lines.push("");
        lines.push("Transaction signal check:");
        lines.push(`Signal source: ${this.transactionSignalResult.providerLabel || this.transactionSignalResult.source}`);
        lines.push(`Classification: ${this.transactionSignalResult.classificationLabel}`);
        lines.push(`Simple-transfer confidence: ${this.transactionSignalResult.simpleTransferConfidenceLabel}`);
        if (this.transactionSignalResult.evidenceSummary) lines.push(`Evidence: ${this.transactionSignalResult.evidenceSummary}`);
        if (this.transactionSignalResult.eventNameText) lines.push(`Event/log names: ${this.transactionSignalResult.eventNameText}`);
        if (this.transactionSignalResult.memoText) lines.push(`Memo: ${this.transactionSignalResult.memoText}`);
        lines.push(`Signal recommendation: ${this.transactionSignalResult.recommendation}`);
      }
      lines.push("");
      lines.push("Checks:");
      lines.push(...this.buildChecksReportLines());

      const autoDecision = this.autoDecisionPreview;
      if (autoDecision) {
        lines.push("");
        lines.push("Automatic decision preview:");
        lines.push(`Recommended action: ${autoDecision.title}`);
        lines.push(`Confidence/basis: ${autoDecision.confidenceLabel}`);
        lines.push(`Summary: ${autoDecision.summary}`);
        autoDecision.reasons.forEach((reason) => lines.push(`- ${reason}`));
      }

      lines.push("");
      if (this.countLookupResult.canTrace) {
        lines.push("Next step: review the checks, then either apply the automatic recommendation, mark the path ending, or press Trace Through Selected Address.");
      } else {
        lines.push("Trace-through is disabled for this address because its transaction volume is too high. Mark the path ending or narrow the scope.");
      }

      return lines.join("\n");
    },

    buildManualPathEndingReport(entry, selectedAddress, result) {
      const lines = [];
      lines.push(this.buildCountReport(entry, selectedAddress));
      lines.push("");
      lines.push(result.autoDecision ? "Pass 3: Automatic path-ending decision" : "Pass 3: Manual path-ending decision");
      lines.push(`Selected address: ${selectedAddress}`);
      if (result.specialTreatment) {
        lines.push(`Special treatment: ${result.specialTreatment}`);
      }
      if (result.autoDecision) {
        lines.push("Automatic recommendation applied: Yes");
      }
      lines.push(`Reason: ${result.reason}`);
      if (result.dormantAmountText) {
        lines.push(`Funds-followed amount recorded at ending: ${result.dormantAmountText}`);
      }
      lines.push("");
      lines.push("The selected input entry was finalized in the output spreadsheet with Column Q set to Path ends.");
      lines.push("For BTC rows, Column R/S left bounds are preserved as coordinates instead of being overwritten with the funds-followed amount.");
      return lines.join("\n");
    },

    buildServiceStopReport(entry, selectedAddress, transactionPayload = {}, fetchedCount = 0, serviceAssessment = null) {
      const lines = [];
      const traceFetch = transactionPayload.trace_fetch || {};
      const assessment = serviceAssessment || this.getTraceServiceAssessment(transactionPayload);
      lines.push(this.buildCountReport(entry, selectedAddress));
      lines.push("");
      lines.push("Pass 3: Mid-fetch service-address check");
      lines.push("Tool 88 stopped before hop logic because the additional service-address check found hot-wallet/service-like behavior.");
      lines.push("");
      if (transactionPayload.job_id) lines.push(`Trace job id: ${transactionPayload.job_id}`);
      lines.push(`Trace status: ${traceFetch.status || transactionPayload.trace_status || transactionPayload.status || "unknown"}`);
      lines.push(`Provider: ${traceFetch.provider || transactionPayload.provider || "unknown"}`);
      lines.push(`Route: ${traceFetch.route_label || traceFetch.route || transactionPayload.route || "unknown"}`);
      lines.push(`Rows fetched before stop: ${this.formatInteger(fetchedCount)}`);
      if (this.traceFetchExpectedCount >= 0) {
        lines.push(`Prior transaction-count estimate: ${this.formatInteger(this.traceFetchExpectedCount)}`);
      }
      if (assessment) {
        lines.push(`Service-pattern summary: ${assessment.summary}`);
        if (Number.isFinite(Number(assessment.score))) {
          lines.push(`Service-pattern score: ${Number(assessment.score).toFixed(2)}`);
        }
        if (assessment.reasons?.length) {
          assessment.reasons.forEach((reason) => lines.push(`- ${reason}`));
        }
      }
      lines.push("");
      lines.push("Output result: the selected entry was finalized as Unknown / Path ends because Tool 88 treated this as a likely unattributed service or exchange hot wallet.");
      lines.push("To override: uncheck Additional service checks in Step 1 and run Trace Through again after manual review.");
      return lines.join("\n");
    },

    appendDepositEntityHopExtensionReportLines(lines, solution = {}) {
      const decision = solution.depositEntityHopExtension;
      if (!decision?.applies) return;
      lines.push("");
      lines.push("Deposit hot-wallet probe");
      lines.push(`Original deposit address: ${decision.originalAddress || ""}`);
      lines.push(`Original attribution: ${decision.originalLabel || "Unknown"}`);
      if (Array.isArray(decision.originalEntityKeys) && decision.originalEntityKeys.length) {
        lines.push(`Original entity key(s): ${decision.originalEntityKeys.join(" / ")}`);
      }
      (decision.nextLookups || []).forEach((lookup, index) => {
        lines.push(`Next-hop check ${index + 1}: ${lookup.label || this.shortenMiddle(lookup.address, 12, 8) || "unknown"}`);
        if (lookup.classification) lines.push(`- Classification: ${this.formatAttributionClassification(lookup.classification)}`);
        if (Array.isArray(lookup.entityKeys) && lookup.entityKeys.length) lines.push(`- Entity key(s): ${lookup.entityKeys.join(" / ")}`);
        lines.push(`- Same-entity match: ${lookup.matched ? "Yes" : "No"}${lookup.matchedKey ? ` (${lookup.matchedKey})` : ""}`);
      });
      if (decision.keepCandidate) {
        lines.push(`Outcome: kept the extra hop. ${decision.summary}`);
      } else if (decision.dropCandidate) {
        lines.push(`Outcome: dropped the extra hop. ${decision.summary}`);
        if (decision.pathEndingReason) lines.push(`Path-ending reason: ${decision.pathEndingReason}`);
        if (Array.isArray(solution.rejectedExitTransactions) && solution.rejectedExitTransactions.length) {
          lines.push("Rejected candidate transaction(s):");
          solution.rejectedExitTransactions.forEach((exit, index) => {
            lines.push(`- ${index + 1}: ${exit.hash || "unknown hash"} to ${exit.toAddress || "unknown address"}`);
          });
        }
      } else if (decision.summary) {
        lines.push(`Outcome: ${decision.summary}`);
      }
    },

    buildAccountPriorBalanceDebugSummary(debug = {}) {
      const source = this.toText(debug.source || 'unknown');
      const address = this.shortenMiddle(debug.addressOfInterest || '', 10, 8) || 'unknown address';
      const hash = this.shortenMiddle(debug.rowHash || debug.targetHash || '', 10, 8) || 'unknown hash';
      const token = this.toText(debug.tokenSymbol || debug.token || 'asset') || 'asset';
      const balance = Number(debug.finalPriorBalance ?? debug.balanceBefore);
      const balanceText = Number.isFinite(balance) ? this.formatFunds(balance, token) : 'unknown balance';
      const included = Array.isArray(debug.includedEvents) ? debug.includedEvents.length : 0;
      const excluded = Array.isArray(debug.excludedEvents) ? debug.excludedEvents.length : 0;
      const warningCount = Array.isArray(debug.warnings) ? debug.warnings.length : 0;
      return `${balanceText} before ${hash} at ${address}; source=${source}; included=${included}; excluded=${excluded}; warnings=${warningCount}`;
    },

    buildTraceReport(entry, selectedAddress, transactionPayload, solution) {
      const lines = [];
      lines.push(this.buildCountReport(entry, selectedAddress));
      lines.push("");
      lines.push("Pass 3: Hop logic analysis");
      lines.push(`Transaction source: ${transactionPayload.source || transactionPayload.provider || "server"}`);
      if (transactionPayload.job_id) {
        lines.push(`Trace job id: ${transactionPayload.job_id}`);
      }
      const providerRows = Array.isArray(transactionPayload.transactions) ? transactionPayload.transactions : [];
      const failedRows = providerRows
        .map((tx) => this.normalizeProviderTransaction(tx))
        .filter((tx) => tx.failedOrReverted).length;
      const traceFetch = transactionPayload.trace_fetch || {};
      if (transactionPayload.provider || traceFetch.provider) {
        lines.push(`Provider route: ${transactionPayload.provider || traceFetch.provider}${transactionPayload.route ? ` / ${transactionPayload.route}` : ""}`);
      }
      lines.push(`Provider transactions fetched: ${providerRows.length}`);
      if (this.traceFetchExpectedCount >= 0) {
        lines.push(`Prior transaction-count estimate: ${this.traceFetchExpectedCount}`);
      }
      if (this.traceInputTransactionFound) {
        lines.push("Input transaction hash was found in the fetched provider history.");
      } else if (providerRows.length) {
        lines.push("Input transaction hash was not found in the fetched provider history; review the inspector before relying on the exit suggestion.");
      }
      if (failedRows > 0) {
        lines.push(`Failed/reverted provider rows excluded from hop selection: ${failedRows}`);
      }
      if (solution.accountAnalysis) {
        const excludedNonBalance = Number(solution.accountAnalysis.nonBalanceRowsExcluded || 0);
        const excludedIneligible = Number(solution.accountAnalysis.ineligibleRowsExcluded || 0);
        if (excludedNonBalance > 0) lines.push(`Non-balance provider rows excluded from FIFO balance and hop selection: ${excludedNonBalance}`);
        if (excludedIneligible > 0) lines.push(`Provider rows marked analysis-ineligible excluded from FIFO balance and hop selection: ${excludedIneligible}`);
      }
      lines.push(`Relevant transactions after direction/date filtering: ${solution.relevantTransactions.length}`);
      lines.push(`Solver used: ${this.hopLogicType}`);
      this.appendDepositEntityHopExtensionReportLines(lines, solution);
      if (solution.accountAnalysis) {
        lines.push(`Account hop solver module: ${solution.accountAnalysis.solver}`);
        lines.push(`Account hop asset: ${solution.accountAnalysis.asset}${solution.accountAnalysis.tokenContract ? ` / ${solution.accountAnalysis.tokenContract}` : ""}`);
        lines.push(`Account hop asset type: ${solution.accountAnalysis.isNative ? "native/main currency" : "token transfer"}`);
        if (solution.accountAnalysis.requiresTokenContract) {
          lines.push(`Token contract requirement: ${solution.accountAnalysis.tokenContract ? "Resolved" : "Missing"}`);
          if (solution.accountAnalysis.tokenRegistryStatus) {
            lines.push(`Token manager status: ${solution.accountAnalysis.tokenRegistryStatus}`);
          }
        }
        if (solution.analysisDirection === "reverse") {
          lines.push(`Reverse target transaction found in fetched history: ${solution.accountAnalysis.reverseTargetFound ? "Yes" : "No"}`);
          if (solution.accountAnalysis.reverseTargetHash) {
            lines.push(`Reverse target transaction: ${solution.accountAnalysis.reverseTargetHash}`);
          }
          if (solution.accountAnalysis.reverseSourceMatchedBy) {
            lines.push(`Reverse target matched by: ${solution.accountAnalysis.reverseSourceMatchedBy}`);
          }
          lines.push(`Reverse target transfer amount assessed: ${this.formatFunds(solution.accountAnalysis.reverseTargetAmount || 0, solution.fundsIn.token)}`);
          lines.push(`Reverse target left offset: ${this.formatFunds(solution.accountAnalysis.reverseTargetLeftOffset || 0, solution.fundsIn.token)}`);
          lines.push(`Reverse target followed amount requested: ${this.formatFunds(solution.accountAnalysis.reverseTargetFollowAmount || 0, solution.fundsIn.token)}`);
          lines.push(`Reverse rows simulated before target transfer: ${solution.accountAnalysis.reverseRowsBeforeTarget || 0}`);
          lines.push(`Reverse source lots available before target transfer: ${solution.accountAnalysis.reverseDepositLotsAvailable || 0}`);
          if (solution.accountAnalysis.reverseTargetFeeAmount) {
            lines.push(`Native target-transaction fee consumed before selected amount: ${this.formatFunds(solution.accountAnalysis.reverseTargetFeeConsumedFromSources || 0, solution.fundsIn.token)} of ${this.formatFunds(solution.accountAnalysis.reverseTargetFeeAmount, solution.fundsIn.token)}`);
          }
        }
        lines.push(`Prior balance dismissed: ${this.formatFunds(solution.accountAnalysis.priorBalanceAmount, solution.fundsIn.token)}`);
        if (solution.accountAnalysis.actualPriorBalanceAmount !== solution.accountAnalysis.priorBalanceAmount) {
          lines.push(`Actual prior balance estimate before zero-balance override: ${this.formatFunds(solution.accountAnalysis.actualPriorBalanceAmount, solution.fundsIn.token)}`);
        }
        if (Number.isFinite(Number(solution.accountAnalysis.priorBalanceFromInput))) {
          lines.push(`Column N prior balance shown in input: ${this.formatFunds(Number(solution.accountAnalysis.priorBalanceFromInput), solution.fundsIn.token)}`);
        }
        if (solution.accountAnalysis.actualPriorBalanceSource) {
          lines.push(`Selected-row Column N prior-balance source: ${solution.accountAnalysis.actualPriorBalanceSource}.`);
        }
        const accountPriorBalanceDebugs = Array.isArray(solution.accountAnalysis.accountPriorBalanceDebug)
          ? solution.accountAnalysis.accountPriorBalanceDebug
          : (solution.accountAnalysis.accountPriorBalanceDebug ? [solution.accountAnalysis.accountPriorBalanceDebug] : []);
        accountPriorBalanceDebugs.slice(0, 6).forEach((debug) => {
          lines.push(`Account prior-balance debug: ${this.buildAccountPriorBalanceDebugSummary(debug)}`);
        });
        lines.push(`Input left bounds: ${this.formatFunds(solution.accountAnalysis.inputLeftBoundsAmount, solution.fundsIn.token)}`);
        lines.push(`Funds followed: ${this.formatFunds(solution.accountAnalysis.fundsFollowedAmount, solution.fundsIn.token)}`);
        if (solution.accountAnalysis.ptdqEventsReviewed !== undefined) {
          lines.push(`LIFO/PTDQ events reviewed after selected deposit: ${solution.accountAnalysis.ptdqEventsReviewed || 0}`);
          lines.push(`LIFO/PTDQ later deposits queued: ${this.formatFunds(solution.accountAnalysis.ptdqLaterDepositAmount || 0, solution.fundsIn.token)}`);
          lines.push(`LIFO/PTDQ later-queue remainder after analysis: ${this.formatFunds(solution.accountAnalysis.ptdqEndingLaterQueueAmount || 0, solution.fundsIn.token)}`);
          lines.push(`LIFO/PTDQ remaining deposit prefix after analysis: ${this.formatFunds(solution.accountAnalysis.ptdqRemainingPrefixAmount || 0, solution.fundsIn.token)}`);
        }
        if (solution.accountAnalysis.totalExitFollowedAmount) {
          lines.push(solution.analysisDirection === "reverse"
            ? `Amount tied to prior source transfer(s): ${this.formatFunds(solution.accountAnalysis.totalExitFollowedAmount, solution.fundsIn.token)}`
            : `Amount assigned to outgoing exit transfer(s): ${this.formatFunds(solution.accountAnalysis.totalExitFollowedAmount, solution.fundsIn.token)}`);
        }
        if (solution.accountAnalysis.dormantAmount) {
          lines.push(solution.analysisDirection === "reverse"
            ? `Unresolved reverse amount: ${this.formatFunds(solution.accountAnalysis.dormantAmount, solution.fundsIn.token)}`
            : `Dormant/remainder amount at selected address: ${this.formatFunds(solution.accountAnalysis.dormantAmount, solution.fundsIn.token)}`);
        }
        if (solution.accountAnalysis.feeConsumedAmount) {
          lines.push(`Followed amount consumed by network fees: ${this.formatFunds(solution.accountAnalysis.feeConsumedAmount, solution.fundsIn.token)}`);
        }
        if (solution.accountAnalysis.nativeFeeResidualSuppressedAmount) {
          lines.push(`Native gas-residual branch suppressed: ${this.formatFunds(solution.accountAnalysis.nativeFeeResidualSuppressedAmount, solution.fundsIn.token)}`);
        }
        if (solution.accountAnalysis.nativeResidualPrunedAmount) {
          lines.push(`Tiny native residual branch suppressed: ${this.formatFunds(solution.accountAnalysis.nativeResidualPrunedAmount, solution.fundsIn.token)}`);
        }
        (solution.accountAnalysis.warnings || []).forEach((warning) => {
          lines.push(`Account hop review note: ${warning}`);
        });
      } else if (solution.btcAnalysis) {
        lines.push(`BTC solver module: ${solution.btcAnalysis.solver}`);
        if (solution.btcAnalysis.analysisDirection) {
          lines.push(`BTC analysis direction: ${solution.btcAnalysis.analysisDirection}`);
        }
        lines.push(solution.btcAnalysis.analysisDirection === "reverse"
          ? `BTC reconstructed prior selected-address balance before target spend: ${this.formatBtcSatsCompact(solution.btcAnalysis.priorBalanceSatoshis)}`
          : `BTC prior balance dismissed: ${this.formatBtcSatsCompact(solution.btcAnalysis.priorBalanceSatoshis)}`);
        if (solution.btcAnalysis.analysisDirection !== "reverse" && solution.btcAnalysis.priorBalanceSource) {
          const sourceLabel = solution.btcAnalysis.priorBalanceSource === "column_n_input"
            ? "Column N input"
            : (solution.btcAnalysis.priorBalanceSource === "next_chronological_zero_balance" ? "Next chronological zero-balance rule" : "provider-history reconstruction");
          lines.push(`BTC prior balance source: ${sourceLabel}.`);
        }
        if (solution.btcAnalysis.analysisDirection !== "reverse" && Number.isFinite(Number(solution.btcAnalysis.rowPriorBalanceSatoshis))) {
          lines.push(`BTC Column N prior balance shown in input: ${this.formatBtcSatsCompact(solution.btcAnalysis.rowPriorBalanceSatoshis)}`);
        }
        if (solution.btcAnalysis.analysisDirection !== "reverse" && Number.isFinite(Number(solution.btcAnalysis.reconstructedPriorBalanceSatoshis))) {
          lines.push(`BTC reconstructed prior balance from provider rows: ${this.formatBtcSatsCompact(solution.btcAnalysis.reconstructedPriorBalanceSatoshis)}`);
        }
        lines.push(solution.btcAnalysis.analysisDirection === "reverse"
          ? `BTC target-output left bounds: ${this.formatBtcSatsCompact(solution.btcAnalysis.inputLeftBoundsSatoshis)}`
          : `BTC incoming left bounds: ${this.formatBtcSatsCompact(solution.btcAnalysis.inputLeftBoundsSatoshis)}`);
        lines.push(solution.btcAnalysis.analysisDirection === "reverse"
          ? `BTC target-output funds followed: ${this.formatBtcSatsCompact(solution.btcAnalysis.fundsFollowedSatoshis)}`
          : `BTC funds followed: ${this.formatBtcSatsCompact(solution.btcAnalysis.fundsFollowedSatoshis)}`);
        if (solution.btcAnalysis.inputRightTailSatoshis) {
          lines.push(`BTC unfollowed right-tail after followed range: ${this.formatBtcSatsCompact(solution.btcAnalysis.inputRightTailSatoshis)}`);
        }
        lines.push(`BTC initial dismissal range: ${this.formatBtcSatsCompact(solution.btcAnalysis.initialDismissalSatoshis)}`);
        if (solution.btcAnalysis.wayFinderSpatializedMode) {
          lines.push("BTC FIFO mode: WayFinder AdvancedFundsFollowed-style outgoing-exit scan.");
        }
        const ignoredForwardDepositCount = solution.btcAnalysis.ignoredIncomingDepositCount || solution.btcAnalysis.ignoredPostInterestDepositCount || 0;
        const ignoredForwardDepositSatoshis = solution.btcAnalysis.ignoredIncomingDepositSatoshis || solution.btcAnalysis.ignoredPostInterestDepositSatoshis || 0;
        if (ignoredForwardDepositCount) {
          lines.push(`BTC later incoming deposits ignored for forward FIFO ordering: ${ignoredForwardDepositCount} (${this.formatBtcSatsCompact(ignoredForwardDepositSatoshis || 0)})`);
        }
        if (solution.btcAnalysis.dismissedByLeftBoundsSatoshis) {
          lines.push(`BTC amount dismissed by incoming left bounds: ${this.formatBtcSatsCompact(solution.btcAnalysis.dismissedByLeftBoundsSatoshis)}`);
        }
        if (solution.btcAnalysis.ptdqEventsReviewed !== undefined) {
          lines.push(`BTC LIFO/PTDQ events reviewed after selected deposit: ${solution.btcAnalysis.ptdqEventsReviewed || 0}`);
          lines.push(`BTC LIFO/PTDQ later deposits queued: ${this.formatBtcSatsCompact(solution.btcAnalysis.ptdqLaterDepositSatoshis || 0)}`);
          lines.push(`BTC LIFO/PTDQ later-queue remainder after analysis: ${this.formatBtcSatsCompact(solution.btcAnalysis.ptdqEndingLaterQueueSatoshis || 0)}`);
          lines.push(`BTC LIFO/PTDQ remaining deposit prefix after analysis: ${this.formatBtcSatsCompact(solution.btcAnalysis.ptdqRemainingPrefixSatoshis || 0)}`);
        }
        if (solution.btcAnalysis.totalExitFollowedSatoshis) {
          lines.push(solution.btcAnalysis.analysisDirection === "reverse"
            ? `BTC followed amount tied to prior source outputs: ${this.formatBtcSatsCompact(solution.btcAnalysis.totalExitFollowedSatoshis)}`
            : `BTC followed amount assigned to exit outputs: ${this.formatBtcSatsCompact(solution.btcAnalysis.totalExitFollowedSatoshis)}`);
        }
        if (solution.btcAnalysis.analysisDirection === "reverse" && solution.btcAnalysis.reverseTargetSeenSatoshis) {
          lines.push(`BTC reverse target range seen in output allocation: ${this.formatBtcSatsCompact(solution.btcAnalysis.reverseTargetSeenSatoshis)}`);
        }
        if (solution.btcAnalysis.analysisDirection === "reverse" && solution.btcAnalysis.reverseTargetResolvedSatoshis) {
          lines.push(`BTC reverse target amount tied to prior source outputs: ${this.formatBtcSatsCompact(solution.btcAnalysis.reverseTargetResolvedSatoshis)}`);
        }
        if (solution.btcAnalysis.dormantSatoshis) {
          lines.push(`BTC followed amount still dormant/queued at selected address: ${this.formatBtcSatsCompact(solution.btcAnalysis.dormantSatoshis)}`);
        }
        if (solution.btcAnalysis.memoConsumedSatoshis) {
          lines.push(`BTC followed amount consumed by OP_RETURN/message outputs: ${this.formatBtcSatsCompact(solution.btcAnalysis.memoConsumedSatoshis)}`);
        }
        if (solution.btcAnalysis.complexTransactionCount) {
          lines.push(`BTC complex outgoing transactions reviewed with WayFinder-style BTC FIFO: ${solution.btcAnalysis.complexTransactionCount}`);
        }
        if (solution.btcAnalysis.dismissedByPriorInputsSatoshis) {
          lines.push(`BTC mini-FIFO dismissed by earlier transaction inputs: ${this.formatBtcSatsCompact(solution.btcAnalysis.dismissedByPriorInputsSatoshis)}`);
        }
        if (solution.btcAnalysis.feeConsumedSatoshis) {
          lines.push(`BTC fee amount consumed from followed funds: ${this.formatBtcSatsCompact(solution.btcAnalysis.feeConsumedSatoshis)}`);
        }
        if (solution.btcAnalysis.feeAmbiguousSatoshis) {
          lines.push(`BTC fee amount needing manual payer review: ${this.formatBtcSatsCompact(solution.btcAnalysis.feeAmbiguousSatoshis)}`);
        }
        if (solution.btcAnalysis.followedReturnedToSelfChangeSatoshis) {
          lines.push(`BTC followed funds returned to self/change outputs during allocation: ${this.formatBtcSatsCompact(solution.btcAnalysis.followedReturnedToSelfChangeSatoshis)}`);
        }
        if (solution.btcAnalysis.followedSelfChangeRequeuedSatoshis) {
          lines.push(`BTC followed self-change preserved ahead of unrelated later funds: ${this.formatBtcSatsCompact(solution.btcAnalysis.followedSelfChangeRequeuedSatoshis)}`);
        }
        [...(solution.btcAnalysis.structureWarnings || []), ...(solution.btcAnalysis.feeWarnings || [])].forEach((warning) => {
          lines.push(`BTC review note: ${warning}`);
        });
      } else {
        lines.push("Current fallback behavior: choose the next chronological outgoing transaction for Forward, or nearest prior incoming transaction for Reverse.");
      }
      lines.push("");

      if (!solution.exitRows.length) {
        if (solution.preventPathEnding) {
          lines.push("No reverse source / forward exit was resolved confidently enough to modify the output spreadsheet.");
          if (solution.dormantAmountText) {
            lines.push(`Unresolved amount: ${solution.dormantAmountText}`);
          }
          if (solution.pathEndingReason) {
            lines.push(`Reason: ${solution.pathEndingReason}`);
          }
          lines.push("");
          lines.push("The output spreadsheet was left unchanged for this pass; review the trace-history inspector and fetched transaction history.");
          return lines.join("\n");
        }
        if (solution.depositEntityHopExtension?.dropCandidate) {
          lines.push("A candidate hop was found by the deposit hot-wallet probe, but Tool 88 dropped it because same-entity attribution was not confirmed.");
          if (solution.pathEndingReason) {
            lines.push(`Reason: ${solution.pathEndingReason}`);
          }
        } else if (solution.feeConsumedAmountText && !solution.dormantAmountText) {
          lines.push("No output exit was found. The followed BTC appears to have been consumed by miner fees instead of becoming a destination output.");
          lines.push(`Fee-consumed amount: ${solution.feeConsumedAmountText}`);
        } else {
          lines.push("No exit was found. The funds are treated as dormant at the selected address for this run.");
          if (solution.dormantAmountText) {
            lines.push(`Dormant amount: ${solution.dormantAmountText}`);
          }
          if (solution.feeConsumedAmountText) {
            lines.push(`Fee-consumed amount also detected: ${solution.feeConsumedAmountText}`);
          }
          if (solution.pathEndingReason) {
            lines.push(`Reason: ${solution.pathEndingReason}`);
          }
        }
        lines.push("");
        lines.push("The selected input entry was finalized in the output spreadsheet with Column Q set to Path ends.");
        return lines.join("\n");
      }

      const resultNoun = solution.analysisDirection === "reverse" ? "source" : "exit";
      lines.push(`${solution.exitRows.length} ${resultNoun}${solution.exitRows.length === 1 ? "" : "s"} found.`);
      solution.exitTransactions.forEach((exit, index) => {
        lines.push("");
        lines.push(`${solution.analysisDirection === "reverse" ? "Source" : "Exit"} ${index + 1}`);
        lines.push(`- Transaction hash: ${exit.hash}`);
        if (exit.outputIndex !== undefined && exit.outputIndex !== null) {
          lines.push(`- BTC output index: ${exit.outputIndex}`);
          if (solution.analysisDirection === "reverse" && exit.reverseTargetOutputIndex !== undefined && exit.reverseTargetOutputIndex !== null) {
            lines.push(`- Funded target output index: ${exit.reverseTargetOutputIndex}`);
          }
        }
        lines.push(`- ${solution.analysisDirection === "reverse" ? "Source" : "Exit"} date UTC: ${exit.date ? this.formatUtcDate(exit.date) : exit.dateText}`);
        lines.push(`- From: ${exit.fromAddress}`);
        lines.push(`- To: ${exit.toAddress}`);
        lines.push(`- Output amount: ${this.formatFunds(exit.amount, exit.token || solution.fundsIn.token)}`);
        const followedForExit = Number.isFinite(Number(exit.followedAmount)) ? Number(exit.followedAmount) : solution.followedAmount;
        lines.push(`- Funds followed: ${this.formatFunds(followedForExit, exit.token || solution.fundsIn.token)}`);
        const leftForExit = Number.isFinite(Number(exit.leftBoundAmount)) ? Number(exit.leftBoundAmount) : solution.leftBoundAmount;
        if (Number.isFinite(leftForExit)) {
          lines.push(`- Left bounds for this output: ${this.formatFunds(leftForExit, exit.token || solution.fundsIn.token)}`);
        }
        const rightForExit = Number.isFinite(Number(exit.rightBoundAmount)) ? Number(exit.rightBoundAmount) : null;
        if (Number.isFinite(rightForExit)) {
          lines.push(`- Right bounds for this output: ${this.formatFunds(rightForExit, exit.token || solution.fundsIn.token)}`);
        }
      });

      if (solution.dormantAmountText) {
        lines.push("");
        lines.push(`Dormant/remainder amount also detected: ${solution.dormantAmountText}`);
        lines.push(solution.analysisDirection === "reverse" ? "Tool 88 inserted source rows and annotated the unresolved reverse remainder for review." : "Tool 88 inserted the exit rows and also annotated the original entry so the user can review the mixed exit+dormant result.");
      }

      lines.push("");
      if (solution.outputMutationVerified === false) {
        const missing = solution.outputMutationVerification?.missing?.length
          ? solution.outputMutationVerification.missing.join(", ")
          : "the expected inserted row(s)";
        lines.push(`Output insertion verification failed for ${missing}. Tool 88 did not mark this result safe for Simple View continuation.`);
      } else {
        lines.push(solution.analysisDirection === "reverse"
          ? "The selected prior source row(s) were inserted below the selected input entry in the output spreadsheet. Path label is intentionally left blank for now."
          : "The selected exit row(s) were inserted below the selected input entry in the output spreadsheet. Path label is intentionally left blank for now.");
      }
      return lines.join("\n");
    },

    resetRunStateAfterExample(message) {
      this.loadOutputRows([]);
      this.warnings = [];
      this.reportText = message;
      this.lastRunMeta = null;
      this.inputSnapshotRows = [];
      this.outputBaseRows = [];
      this.outputSnapshotRows = [];
      this.parsedEntries = [];
      this.selectedEntryKey = "";
      this.targetCandidates = [];
      this.selectedTargetAddress = "";
      this.countLookupResult = null;
      this.attributionLookupResult = null;
      this.transactionSignalResult = null;
      this.traceResult = null;
      this.dormantResult = null;
      this.traceTransactionPayload = null;
      this.pathEndingResult = null;
      this.backendJobMeta = null;
      this.countJobMeta = null;
      this.attributionJobMeta = null;
      this.signalJobMeta = null;
      this.resetSwapUi();
      this.resetBridgeUi();
      this.resetTraceFetchUi(null);
      this.flowCompletionScanResult = null;
      this.flowCompletionMessage = "";
      this.flowCompletionDepth = 0;
      this.lastFlowSelectionResult = null;
      this.flowAbsorbResult = null;
      this.isSampleDataLoaded = true;
    },

    clearAll() {
      this.warningThreshold = INTERNAL_WARNING_THRESHOLD;
      this.hardLimitThreshold = INTERNAL_HARD_LIMIT_THRESHOLD;
      this.isProcessing = false;
      this.statusMessage = "";
      this.warnings = [];
      this.reportText = "Paste a Case Buddy-style entry into the input spreadsheet, then run Process Entry + Checks to begin.";
      this.lastRunMeta = null;
      this.inputSnapshotRows = [];
      this.outputBaseRows = [];
      this.outputSnapshotRows = [];
      this.headerDetected = false;
      this.dataStartIndex = 0;
      this.parsedEntries = [];
      this.selectedEntryKey = "";
      this.targetCandidates = [];
      this.selectedTargetAddress = "";
      this.countLookupResult = null;
      this.attributionLookupResult = null;
      this.transactionSignalResult = null;
      this.traceResult = null;
      this.dormantResult = null;
      this.traceTransactionPayload = null;
      this.pathEndingResult = null;
      this.backendJobMeta = null;
      this.countJobMeta = null;
      this.attributionJobMeta = null;
      this.signalJobMeta = null;
      this.resetSwapUi();
      this.resetBridgeUi();
      this.resetTraceFetchUi(null);
      this.flowCompletionScanResult = null;
      this.flowCompletionMessage = "";
      this.flowCompletionDepth = 0;
      this.lastFlowSelectionResult = null;
      this.flowAbsorbResult = null;
      this.isSampleDataLoaded = false;
      this.authSessionCache = null;
      this.isRefreshingSelections = false;
      this.resetCaseBuddyIntegrationRunState();
      if (!this.directAutomationRunState.active) {
        this.directAutomationRunState = createTool88DirectAutomationRunState();
      }

      this.$refs.inputSpreadsheet?.loadData(this.makeBlankGrid());
      this.loadOutputRows([]);
    },

    loadSelectedExample() {
      const loaders = {
        basic: this.loadExample,
        "eth-native": this.loadEthNativeExample,
        "bsc-native": this.loadBscNativeExample,
        ambiguous: this.loadAmbiguousExample,
        "evm-event-log": this.loadEvmEventLogExample,
        "btc-memo": this.loadBtcMemoExample,
        "tron-trc20": this.loadTronTrc20Example,
        "tron-trx": this.loadTronTrxExample,
        "unsupported-sol": this.loadUnsupportedCountExample,
        dormant: this.loadDormantExample,
        warning: this.loadWarningThresholdExample,
        "hard-limit": this.loadHardLimitExample,
      };

      const loader = loaders[this.selectedExampleKey] || this.loadExample;
      loader.call(this);
    },

    loadExample() {
      const rows = [
        HEADER_ROW,
        [
          "1",
          "0xinputhash0001",
          "0x1111111111111111111111111111111111111111",
          "0x654091d38405b4fc5fc1d0db9fafd1b7e5e933ea",
          "2026-05-13 18:00:00",
          "100",
          "USDT",
          "",
          "",
          "Example Tool 88 input entry.",
          "",
          "",
          "100 USDT",
          "",
          "0xpriorhash0000",
          "",
          "Path ends.",
          "100 USDT",
          "100",
          "1",
          "Ethereum",
          "",
        ],
        [
          "",
          "",
          "0x3333333333333333333333333333333333333333",
          "0x654091d38405b4fc5fc1d0db9fafd1b7e5e933ea",
          "2026-05-13 18:00:00",
          "0",
          "USDT",
          "",
          "",
          "Continuation row for same entry; same To Address keeps the target unambiguous.",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "1",
          "Ethereum",
          "",
        ],
      ];

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.resetRunStateAfterExample("Example loaded. Press Process Entry + Checks to start the workflow.");
    },

    loadEthNativeExample() {
      const rows = [
        HEADER_ROW,
        [
          "1n",
          "0xethnativeinput0001",
          "0x1111111111111111111111111111111111111111",
          "0x2222222222222222222222222222222222222222",
          "2026-05-13 18:00:00",
          "2.5",
          "ETH",
          "",
          "",
          "ETH native example. Tool 88 should fetch normal ETH transactions plus balance-affecting internal transfers through the native trace-history route.",
          "",
          "",
          "2.5 ETH",
          "",
          "0xpriorhash0000",
          "",
          "Path ends.",
          "2.5 ETH",
          "2.5",
          "1",
          "Ethereum",
          "",
        ],
      ];

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.resetRunStateAfterExample("ETH native example loaded. Process it, then trace through to exercise the native + internal EVM history route.");
    },

    loadBscNativeExample() {
      const rows = [
        HEADER_ROW,
        [
          "1b",
          "0xbscnativeinput0001",
          "0x3333333333333333333333333333333333333333",
          "0x4444444444444444444444444444444444444444",
          "2026-05-13 18:00:00",
          "12.75",
          "BNB",
          "",
          "",
          "BSC native BNB example. Tool 88 should route native EVM history through Moralis with internal transactions included.",
          "",
          "",
          "12.75 BNB",
          "",
          "0xpriorhash0000",
          "",
          "Path ends.",
          "12.75 BNB",
          "12.75",
          "56",
          "BSC",
          "",
        ],
      ];

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.resetRunStateAfterExample("BSC native example loaded. Process it, then trace through to exercise the Moralis native + internal EVM route.");
    },

    loadAmbiguousExample() {
      const rows = [
        HEADER_ROW,
        [
          "2",
          "0xambiguoushash0001",
          "0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
          "0x2222222222222222222222222222222222222222",
          "2026-05-13 18:00:00",
          "75",
          "USDT",
          "",
          "",
          "Ambiguous output example. First output should trigger the high-volume caution placeholder.",
          "",
          "",
          "75 USDT",
          "",
          "0xpriorhash0000",
          "",
          "Path ends.",
          "75 USDT",
          "75",
          "1",
          "Ethereum",
          "",
        ],
        [
          "",
          "",
          "",
          "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
          "2026-05-13 18:00:00",
          "25",
          "USDT",
          "",
          "",
          "Second output row includes attribution and should invite review before tracing.",
          "Known Exchange Placeholder",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "1",
          "Ethereum",
          "",
        ],
      ];

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.resetRunStateAfterExample("Ambiguous example loaded. Press Process Entry + Checks, choose the target address, then rerun the checks.");
    },

    loadEvmEventLogExample() {
      const rows = [
        HEADER_ROW,
        [
          "4",
          "0xeventloghash0001",
          "0xcccccccccccccccccccccccccccccccccccccccc",
          "0x9999999999999999999999999999999999999999",
          "2026-05-13 18:00:00",
          "250",
          "USDC",
          "",
          "",
          "EVM event-log review example. Visible text includes 0xddf so Step 3 should compare the provider receipt against the simple-transfer pattern.",
          "",
          "",
          "250 USDC",
          "",
          "0xpriorhash0000",
          "",
          "Path ends.",
          "250 USDC",
          "250",
          "1",
          "Ethereum",
          "",
        ],
      ];

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.resetRunStateAfterExample("EVM event-log example loaded. Process it to see the 0xddf-style pre-trace check.");
    },

    loadBtcMemoExample() {
      const rows = [
        HEADER_ROW,
        [
          "5",
          "btcinputhash0001",
          "1BoatSLRHtKNngkdXEeobR76b53LETtpyT",
          "3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy",
          "2026-05-13 18:00:00",
          "0.45",
          "BTC",
          "",
          "",
          "BTC OP_RETURN / memo review example. Memo text is intentionally present for the Step 3 check.",
          "",
          "",
          "0.45 BTC",
          "",
          "btcpriorhash0000",
          "",
          "Path ends.",
          "0.45 BTC",
          "0.45",
          "btc",
          "Bitcoin",
          "",
        ],
      ];

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.resetRunStateAfterExample("BTC memo example loaded. Process it to see the OP_RETURN/memo pre-trace check.");
    },

    loadTronTrc20Example() {
      const rows = [
        HEADER_ROW,
        [
          "8",
          "troninputhash0001",
          "TQn9Y2khEsLJW1ChVWFMSMeRDow5KcbLSE",
          "TXLAQ63Xg1NAzckPwKHvzw7CSEmLMEqcdj",
          "2026-05-13 18:00:00",
          "500",
          "USDT",
          "",
          "",
          "TRON TRC20 example. Tool 88 should route the count lookup to the TRON rangeTotal path, then run transaction-signal review.",
          "",
          "",
          "500 USDT",
          "",
          "tronpriorhash0000",
          "",
          "Path ends.",
          "500 USDT",
          "500",
          "tron",
          "Tron",
          "",
        ],
      ];

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.resetRunStateAfterExample("TRON TRC20 example loaded. Process it to route the count lookup through the TRON count path and run signal review.");
    },

    loadTronTrxExample() {
      const rows = [
        HEADER_ROW,
        [
          "8a",
          "trontrxinputhash0001",
          "TQn9Y2khEsLJW1ChVWFMSMeRDow5KcbLSE",
          "TXLAQ63Xg1NAzckPwKHvzw7CSEmLMEqcdj",
          "2026-05-13 18:05:00",
          "1250",
          "TRX",
          "",
          "",
          "TRON native TRX example. Tool 88 should route the count lookup to Tronscan's native transaction-count path instead of the TRC20 rangeTotal path.",
          "",
          "",
          "1250 TRX",
          "",
          "trontrxpriorhash0000",
          "",
          "Path ends.",
          "1250 TRX",
          "1250",
          "tron",
          "Tron",
          "",
        ],
      ];

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.resetRunStateAfterExample("TRON native TRX example loaded. Process it to route the count lookup through the native TRX transaction-count path.");
    },

    loadUnsupportedCountExample() {
      const rows = [
        HEADER_ROW,
        [
          "9",
          "solinputhash0001",
          "So11111111111111111111111111111111111111112",
          "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
          "2026-05-13 18:00:00",
          "25",
          "SOL",
          "",
          "",
          "Unsupported count example. Count should return unavailable while the workflow remains open for checks and decisions.",
          "",
          "",
          "25 SOL",
          "",
          "solpriorhash0000",
          "",
          "Path ends.",
          "25 SOL",
          "25",
          "sol",
          "Solana",
          "",
        ],
      ];

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.resetRunStateAfterExample("Unsupported count example loaded. Process it to confirm Tool 88 can continue with count unavailable.");
    },

    loadWarningThresholdExample() {
      const rows = [
        HEADER_ROW,
        [
          "6",
          "0xwarninghash0001",
          "0xdddddddddddddddddddddddddddddddddddddddd",
          "0x2222222222222222222222222222222222222222",
          "2026-05-13 18:00:00",
          "88",
          "USDT",
          "",
          "",
          "High-count caution example. With the real backend, the actual provider count decides whether this lands in caution.",
          "",
          "",
          "88 USDT",
          "",
          "0xpriorhash0000",
          "",
          "Path ends.",
          "88 USDT",
          "88",
          "1",
          "Ethereum",
          "",
        ],
      ];

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.resetRunStateAfterExample("High-count caution example loaded. Process it to run the chain-aware count path.");
    },

    loadHardLimitExample() {
      const rows = [
        HEADER_ROW,
        [
          "7",
          "0xblockedhash0001",
          "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
          "0x0000000000000000000000000000000000000000",
          "2026-05-13 18:00:00",
          "12",
          "ETH",
          "",
          "",
          "Blocked-count example. With the real backend, the actual provider count decides whether trace-through is blocked.",
          "",
          "",
          "12 ETH",
          "",
          "0xpriorhash0000",
          "",
          "Path ends.",
          "12 ETH",
          "12",
          "1",
          "Ethereum",
          "",
        ],
      ];

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.resetRunStateAfterExample("Blocked-count example loaded. Process it to run the chain-aware count path and review the decision gate.");
    },

    loadDormantExample() {
      const rows = [
        HEADER_ROW,
        [
          "3",
          "0xdormanthash0001",
          "0xbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb",
          "0x4444444444444444444444444444444444444444",
          "2026-05-13 18:00:00",
          "42.5",
          "ETH",
          "",
          "",
          "Dormant example. Supported ERC20/TRC20 routes now use provider pagination; unsupported routes still fall back to placeholder trace data.",
          "",
          "",
          "42.5 ETH",
          "",
          "0xpriorhash0000",
          "",
          "Path ends.",
          "42.5 ETH",
          "42.5",
          "1",
          "Ethereum",
          "",
        ],
      ];

      this.$refs.inputSpreadsheet?.loadData(this.ensureMinimumRows(rows));
      this.resetRunStateAfterExample("Dormant example loaded. Process, then use Mark as Path Ending or Trace Through depending on the count/check result.");
    },
  },
};
</script>

<style scoped>
.simple-view-launcher {
  position: fixed;
  left: 18px;
  top: 154px;
  z-index: 2147482990;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(79, 70, 229, 0.24);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #23345d;
  box-shadow: 0 14px 34px rgba(22, 31, 59, 0.16);
  padding: 10px 14px;
  font-weight: 800;
  cursor: pointer;
}
.simple-view-launcher-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, #7c3aed, #06b6d4);
  box-shadow: 0 0 0 6px rgba(124, 58, 237, 0.12);
}
.direct-auto-stop-overlay {
  position: fixed;
  right: 18px;
  top: 18px;
  z-index: 2147483640;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: min(560px, calc(100vw - 36px));
  border: 1px solid rgba(248, 113, 113, 0.48);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 22px 56px rgba(127, 29, 29, 0.25);
  padding: 10px 12px;
}
.direct-auto-stop-button {
  flex: 0 0 auto;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #b91c1c, #ef4444);
  color: #ffffff;
  cursor: pointer;
  font-size: 13px;
  font-weight: 1000;
  letter-spacing: 0.02em;
  padding: 12px 18px;
  text-transform: uppercase;
  box-shadow: 0 12px 26px rgba(185, 28, 28, 0.28);
}
.direct-auto-stop-copy {
  display: grid;
  gap: 2px;
  min-width: 0;
  color: #7f1d1d;
  font-size: 12px;
  line-height: 1.35;
}
.direct-auto-stop-copy strong {
  color: #111827;
  font-size: 13px;
}
.direct-auto-stop-copy span {
  overflow-wrap: anywhere;
}
.simple-view-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(6px, 1vw, 12px);
  isolation: isolate;
  overflow: hidden;
}
.simple-view-overlay * { box-sizing: border-box; }
.simple-view-backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  background:
    radial-gradient(circle at 18% 18%, rgba(79, 70, 229, 0.24), transparent 34%),
    radial-gradient(circle at 82% 14%, rgba(20, 184, 166, 0.2), transparent 34%),
    radial-gradient(circle at 60% 88%, rgba(244, 114, 182, 0.18), transparent 32%),
    rgba(9, 14, 29, 0.58);
  backdrop-filter: blur(2px);
}
.simple-view-modal {
  position: relative;
  z-index: 1;
  width: min(1560px, calc(100vw - 16px));
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 16px);
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  border: 1px solid rgba(226, 232, 240, 0.86);
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(247, 251, 255, 0.96));
  box-shadow: 0 34px 90px rgba(4, 12, 33, 0.35);
}
.simple-view-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 0;
  padding: 0;
  border: 1px solid rgba(148, 163, 184, 0.5);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  color: #1f2937;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}
.simple-view-close span {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1em;
  height: 1em;
  line-height: 1;
}
.simple-view-layout {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 430px);
  gap: clamp(16px, 1.6vw, 24px);
  align-items: start;
  justify-content: center;
  width: 100%;
  max-width: 1360px;
  min-width: 0;
  margin: 0 auto;
  padding: 40px clamp(18px, 1.8vw, 28px) clamp(18px, 1.8vw, 28px);
  clear: both;
  overflow-x: clip;
}
.simple-view-layout > * { min-width: 0; }
.simple-view-left,
.simple-view-right {
  position: relative;
  min-width: 0;
  overflow-wrap: anywhere;
}
.simple-view-left {
  display: grid;
  align-content: start;
  justify-self: end;
  width: 100%;
  max-width: 720px;
  gap: 10px;
  overflow: hidden;
}
.simple-view-right {
  display: grid;
  align-content: start;
  width: 100%;
  max-width: 430px;
  gap: 12px;
}
.simple-view-right .simple-next-actions-card {
  position: sticky;
  top: 12px;
  z-index: 2;
}
.simple-compact-header {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  align-items: start;
  min-width: 0;
  max-width: 100%;
}
.simple-compact-title {
  min-width: 0;
  padding-right: 44px;
}
.simple-view-kicker { display: none; }
.simple-view-left h2 {
  max-width: 100%;
  margin: 0;
  font-size: clamp(20px, 1.6vw, 27px);
  line-height: 1.1;
  color: #111827;
  overflow-wrap: anywhere;
}
.simple-view-left p,
.simple-status-card p,
.simple-recommendation-card p,
.simple-result-card p {
  line-height: 1.55;
  color: #475569;
}
.simple-view-intro {
  max-width: 680px;
  margin: 0;
  font-size: 14px;
}
.simple-view-visual {
  position: relative;
  min-height: 138px;
  margin: 8px 0 6px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(239, 246, 255, 0.88), rgba(240, 253, 250, 0.86)), #ffffff;
  overflow: hidden;
}
.simple-flow-map-head {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px 0;
}
.simple-flow-map-head strong {
  display: block;
  color: #0f172a;
  font-size: 13px;
  line-height: 1.25;
}
.simple-flow-map-pills {
  display: flex;
  justify-content: flex-end;
  gap: 5px;
  flex-wrap: wrap;
  max-width: 52%;
}
.simple-flow-map-pills span {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.76);
  color: #475569;
  font-size: 10.5px;
  font-weight: 900;
  padding: 3px 7px;
  white-space: nowrap;
}
.simple-flow-map-svg {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 104px;
  margin-top: -2px;
}
.simple-flow-map-link {
  fill: none;
  stroke: url(#tool88SimpleFlowLine);
  stroke-width: 2.6;
  stroke-linecap: round;
  stroke-dasharray: 5 7;
  animation: simple-flow-dash 8s linear infinite;
}
@keyframes simple-flow-dash { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -72; } }
.simple-flow-map-node circle:not(.simple-flow-map-node-pulse) {
  fill: url(#tool88SimpleFlowNode);
  stroke: rgba(99, 102, 241, 0.52);
  stroke-width: 2;
  filter: drop-shadow(0 8px 11px rgba(15, 23, 42, 0.16));
}
.simple-flow-map-node text {
  fill: #334155;
  font-size: 9.5px;
  font-weight: 900;
  text-anchor: middle;
  pointer-events: none;
}
.simple-flow-map-node-active circle:not(.simple-flow-map-node-pulse) {
  fill: #eef2ff;
  stroke: #4f46e5;
}
.simple-flow-map-node-complete circle:not(.simple-flow-map-node-pulse) {
  fill: #dcfce7;
  stroke: #10b981;
}
.simple-flow-map-node-open circle:not(.simple-flow-map-node-pulse) {
  fill: #fff7ed;
  stroke: #f59e0b;
}
.simple-flow-map-node-branch circle:not(.simple-flow-map-node-pulse) {
  fill: #ecfeff;
  stroke: #06b6d4;
}
.simple-flow-map-node-pulse {
  fill: rgba(99, 102, 241, 0.16);
  stroke: none;
  transform-origin: center;
  animation: simple-flow-pulse 1.9s ease-in-out infinite;
}
@keyframes simple-flow-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 0.16; } }
.simple-flow-map-hidden {
  fill: #64748b;
  font-size: 11px;
  font-weight: 900;
}
.simple-flow-empty-map {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 92px;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
}
.simple-flow-empty-dot {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: linear-gradient(135deg, #6366f1, #14b8a6);
  box-shadow: 0 0 0 8px rgba(99, 102, 241, 0.09);
}
.simple-orbit {
  position: absolute;
  width: 180px;
  height: 180px;
  border-radius: 999px;
  filter: blur(2px);
  opacity: 0.45;
  animation: simple-drift 7s ease-in-out infinite alternate;
}
.simple-orbit-one { left: -40px; top: -70px; background: radial-gradient(circle, rgba(79, 70, 229, 0.38), transparent 65%); }
.simple-orbit-two { right: -42px; bottom: -82px; background: radial-gradient(circle, rgba(20, 184, 166, 0.36), transparent 65%); animation-delay: -2s; }
@keyframes simple-drift { from { transform: translate3d(0, 0, 0) scale(1); } to { transform: translate3d(18px, 14px, 0) scale(1.08); } }
.simple-path {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: center;
  gap: 6px;
  padding: 12px;
}
.simple-path::before {
  content: "";
  position: absolute;
  left: 32px;
  right: 32px;
  top: 50%;
  height: 3px;
  background: linear-gradient(90deg, rgba(79, 70, 229, 0.3), rgba(20, 184, 166, 0.5));
  transform: translateY(-50%);
}
.simple-node {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  min-width: 0;
  border: 1px solid rgba(148, 163, 184, 0.38);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
}
.simple-node-active { color: #3730a3; border-color: rgba(99, 102, 241, 0.52); box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.08), 0 12px 26px rgba(15, 23, 42, 0.08); }
.simple-node-done { color: #065f46; border-color: rgba(20, 184, 166, 0.48); background: rgba(240, 253, 250, 0.96); }
.simple-mode-bar {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  max-width: 100%;
  margin: 8px 0;
  padding: 8px 10px;
  border: 1px solid rgba(99, 102, 241, 0.22);
  border-radius: 999px;
  background: rgba(238, 242, 255, 0.72);
  color: #475569;
}
.simple-mode-label {
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}
.simple-mode-button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  max-width: 100%;
  flex-wrap: wrap;
  border: 1px dashed rgba(79, 70, 229, 0.52);
  border-radius: 999px;
  background: #ffffff;
  color: #3730a3;
  font-weight: 900;
  padding: 7px 12px;
  box-shadow: none;
  text-decoration: none;
}
.simple-mode-button:hover:not(:disabled) {
  border-color: #4f46e5;
  box-shadow: 0 8px 18px rgba(79, 70, 229, 0.12);
}
.simple-mode-divider { color: #94a3b8; }
.simple-mode-hint {
  margin-left: 4px;
  border-bottom: 1px dotted currentColor;
  color: #2563eb;
  font-size: 12px;
}
.simple-settings-card,
.simple-target-card,
.simple-status-card,
.simple-recommendation-card,
.simple-result-card,
.simple-next-actions-card {
  min-width: 0;
  border: 1px solid rgba(203, 213, 225, 0.75);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.82);
  padding: 14px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
}
.simple-settings-card {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}
.simple-input-block {
  display: grid;
  gap: 8px;
  margin-top: 8px;
  min-width: 0;
  font-weight: 850;
  color: #334155;
}
.simple-input-block textarea {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 144px;
  max-height: 30vh;
  resize: vertical;
  overflow-wrap: anywhere;
  border: 1px solid #cbd5e1;
  border-radius: 14px;
  padding: 10px;
  font: 12.5px/1.38 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  background: rgba(255, 255, 255, 0.92);
  color: #0f172a;
}
.simple-staged-card {
  display: grid;
  gap: 10px;
  margin-top: 10px;
  min-width: 0;
  border: 1px solid rgba(96, 165, 250, 0.35);
  border-radius: 16px;
  background: rgba(239, 246, 255, 0.78);
  padding: 12px;
}
.simple-staged-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.simple-staged-head strong {
  display: block;
  color: #0f172a;
  font-size: 13px;
}
.simple-staged-hint {
  max-width: 280px;
  color: #475569;
  font-size: 11.5px;
  font-weight: 800;
  text-align: right;
}
.simple-staged-table-wrap {
  max-height: 220px;
  overflow: auto;
  border: 1px solid rgba(148, 163, 184, 0.34);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.88);
}
.simple-staged-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.simple-staged-table th,
.simple-staged-table td {
  padding: 7px 8px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.92);
  text-align: left;
  vertical-align: middle;
}
.simple-staged-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #eff6ff;
  color: #334155;
  font-size: 10.5px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.simple-staged-table code {
  color: #1e3a8a;
  font-size: 11.5px;
  font-weight: 900;
}
.simple-staged-table input {
  width: 100%;
  min-width: 180px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 6px 8px;
  font: 12px/1.3 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  color: #0f172a;
  background: #ffffff;
}
.simple-target-card {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}
.simple-actions-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 100%;
  margin-top: 10px;
}
.simple-actions-row-top {
  justify-content: flex-start;
  align-items: center;
  margin-top: 0;
}
.simple-actions-row .primary-button,
.simple-actions-row .ghost-button,
.simple-actions-row-top .primary-button,
.simple-actions-row-top .ghost-button {
  flex: 0 1 auto;
  max-width: 100%;
  min-height: 34px;
  padding: 8px 12px;
  white-space: normal;
}
.simple-next-action-heading {
  display: grid;
  gap: 3px;
  margin-bottom: 10px;
}
.simple-next-action-heading-after {
  margin-top: 10px;
  margin-bottom: 6px;
}
.simple-next-action-heading strong {
  color: #0f172a;
  font-size: 15px;
  line-height: 1.25;
}
.simple-next-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 9px;
  margin-top: 9px;
}
.simple-next-actions-primary {
  margin-top: 10px;
  margin-bottom: 10px;
}
.simple-next-actions button {
  display: inline-flex;
  align-items: center;
  width: 100%;
  justify-content: center;
  white-space: normal;
  text-align: center;
}
.simple-main-action {
  min-height: 44px;
  font-size: 15px;
}
.simple-action-hint {
  margin: 8px 0 0;
  min-height: 42px;
  color: #475569;
  line-height: 1.45;
}
.simple-decision-locked {
  border: 1px solid rgba(16, 185, 129, 0.26);
  border-radius: 14px;
  background: rgba(236, 253, 245, 0.74);
  color: #065f46;
  padding: 9px 11px;
  font-size: 12.5px;
  font-weight: 750;
  line-height: 1.35;
}
.direct-auto-card {
  display: grid;
  gap: 9px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(203, 213, 225, 0.72);
}

.direct-auto-card-advanced {
  margin-top: 14px;
  padding: 14px;
  border: 1px solid rgba(248, 113, 113, 0.26);
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(255, 247, 237, 0.82), rgba(255, 255, 255, 0.92));
}
.direct-auto-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.direct-auto-head strong {
  display: block;
  color: #0f172a;
  font-size: 14px;
  line-height: 1.25;
}
.direct-auto-run-button {
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #dc2626, #f97316);
  color: #ffffff;
  box-shadow: 0 12px 22px rgba(220, 38, 38, 0.22);
  cursor: pointer;
  font-size: 12px;
  font-weight: 950;
  padding: 8px 12px;
  white-space: nowrap;
}
.direct-auto-run-button:disabled {
  cursor: not-allowed;
  opacity: 0.48;
  box-shadow: none;
}
.direct-auto-speed {
  display: grid;
  gap: 6px;
  color: #334155;
  font-size: 12px;
  font-weight: 850;
}
.direct-auto-speed span {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.direct-auto-speed strong { color: #dc2626; }
.direct-auto-speed input { width: 100%; }
.direct-auto-hint,
.direct-auto-status {
  margin: 0;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.42;
}
.direct-auto-status {
  border: 1px solid rgba(251, 146, 60, 0.36);
  border-radius: 12px;
  background: rgba(255, 247, 237, 0.82);
  color: #9a3412;
  padding: 8px 10px;
}
.simple-export-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(203, 213, 225, 0.72);
}
.simple-export-actions button {
  width: 100%;
  justify-content: center;
  white-space: normal;
  text-align: center;
}
.simple-advanced-button { grid-column: 1 / -1; }
.simple-card-heading {
  display: grid;
  gap: 4px;
}
.simple-card-heading strong {
  display: block;
  color: #111827;
  font-size: 18px;
  line-height: 1.25;
}
.simple-recommendation-card p { margin-bottom: 0; }
.simple-status-card h3 {
  margin: 5px 0 7px;
  color: #111827;
  font-size: 20px;
  line-height: 1.18;
}
.simple-status-idle { background: linear-gradient(135deg, rgba(255, 255, 255, 0.92), rgba(248, 250, 252, 0.92)); }
.simple-status-active,
.simple-recommendation-warn,
.simple-recommendation-review { border-color: rgba(245, 158, 11, 0.4); background: linear-gradient(135deg, rgba(255, 251, 235, 0.9), rgba(255, 255, 255, 0.88)); }
.simple-status-complete,
.simple-recommendation-ok,
.simple-recommendation-trace { border-color: rgba(16, 185, 129, 0.42); background: linear-gradient(135deg, rgba(236, 253, 245, 0.92), rgba(255, 255, 255, 0.9)); }
.simple-status-block,
.simple-recommendation-block { border-color: rgba(239, 68, 68, 0.42); background: linear-gradient(135deg, rgba(254, 242, 242, 0.94), rgba(255, 255, 255, 0.88)); }
.simple-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
}
.simple-summary-grid div {
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.86);
  padding: 8px 10px;
}
.simple-summary-grid span {
  display: block;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}
.simple-summary-grid strong {
  display: block;
  margin-top: 3px;
  color: #0f172a;
}
.simple-flow-message {
  border: 1px dashed rgba(99, 102, 241, 0.38);
  border-radius: 14px;
  background: rgba(238, 242, 255, 0.76);
  padding: 12px;
  color: #3730a3;
  font-weight: 750;
  overflow-wrap: anywhere;
}
@media (max-width: 880px) {
  .simple-view-overlay { padding: 8px; }
  .simple-view-modal {
    width: calc(100vw - 16px);
    max-width: calc(100vw - 16px);
    max-height: calc(100vh - 16px);
    border-radius: 20px;
  }
  .simple-view-layout {
    grid-template-columns: 1fr;
    gap: 16px;
    max-width: none;
    padding: 42px 18px 18px;
  }
  .simple-view-left {
    justify-self: stretch;
    max-width: none;
    overflow: visible;
  }
  .simple-view-right {
    max-width: none;
  }
  .simple-view-right .simple-next-actions-card {
    position: static;
  }
  .simple-settings-card,
  .simple-summary-grid { grid-template-columns: 1fr 1fr; }
  .simple-view-launcher {
    left: 10px;
    top: auto;
    bottom: 16px;
  }
  .simple-input-block textarea {
    max-height: none;
    min-height: 160px;
  }
}
@media (max-width: 640px) {
  .simple-view-modal { border-radius: 18px; }
  .simple-view-layout { padding: 42px 12px 12px; }
  .simple-compact-title { padding-right: 38px; }
  .simple-actions-row-top {
    align-items: stretch;
  }
  .simple-actions-row-top button {
    flex: 1 1 100%;
  }
  .simple-settings-card,
  .simple-summary-grid,
  .simple-export-actions { grid-template-columns: 1fr; }
  .simple-mode-bar {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
    border-radius: 18px;
  }
  .simple-mode-button {
    width: 100%;
    justify-content: center;
  }
  .simple-view-close {
    top: 10px;
    right: 10px;
  }
  .simple-view-visual { min-height: 128px; }
  .simple-flow-map-head {
    flex-direction: column;
    gap: 5px;
  }
  .simple-flow-map-pills {
    justify-content: flex-start;
    max-width: 100%;
  }
  .simple-flow-map-svg { height: 86px; }
  .simple-path {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 12px;
    gap: 6px;
  }
  .simple-path::before { display: none; }
  .simple-node {
    min-height: 34px;
    font-size: 10.5px;
  }
}

:global(html.tool88-simple-view-open), :global(body.tool88-simple-view-open) { overflow: hidden; }
.tool88-page.simple-view-active :deep(.ht_clone_top),
.tool88-page.simple-view-active :deep(.ht_clone_left),
.tool88-page.simple-view-active :deep(.ht_clone_top_left_corner),
.tool88-page.simple-view-active :deep(.ht_clone_bottom),
.tool88-page.simple-view-active :deep(.handsontable) {
  z-index: 1 !important;
}

.tool88-page {
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  padding: 18px 16px 36px;
  color: #172033;
}

.tool88-page--case-buddy-embedded {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: none;
  min-height: 680px;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  color: #172033;
  background:
    radial-gradient(circle at 10% 8%, rgba(59, 130, 246, 0.08), transparent 34%),
    linear-gradient(180deg, rgba(248, 251, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
}

.tool88-page--case-buddy-embedded:not(.simple-view-active) {
  overflow: auto;
}

.tool88-page--case-buddy-embedded .simple-view-launcher {
  position: sticky;
  top: 10px;
  left: auto;
  align-self: flex-end;
  z-index: 24;
  margin: 10px 12px -54px auto;
}

.tool88-page--case-buddy-embedded .simple-view-overlay {
  position: absolute;
  inset: 0;
  z-index: 22;
  padding: 8px;
  align-items: stretch;
  justify-content: stretch;
}

.tool88-page--case-buddy-embedded .simple-view-backdrop {
  background:
    radial-gradient(circle at 18% 18%, rgba(79, 70, 229, 0.12), transparent 34%),
    radial-gradient(circle at 82% 14%, rgba(20, 184, 166, 0.1), transparent 34%),
    rgba(248, 250, 252, 0.86);
  backdrop-filter: none;
}

.tool88-page--case-buddy-embedded .simple-view-modal {
  width: 100%;
  max-width: 100%;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  border-radius: 18px;
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.12);
}

.tool88-page--case-buddy-embedded .simple-view-layout {
  max-width: none;
  height: 100%;
  grid-template-columns: minmax(0, 1.22fr) minmax(300px, 0.78fr);
  align-items: stretch;
  gap: 12px;
  padding: 36px 12px 12px;
  overflow: hidden;
}

.tool88-page--case-buddy-embedded .simple-view-left,
.tool88-page--case-buddy-embedded .simple-view-right {
  max-width: none;
  min-height: 0;
  overflow: auto;
  padding-right: 2px;
}

.tool88-page--case-buddy-embedded .simple-view-left {
  justify-self: stretch;
}

.tool88-page--case-buddy-embedded .simple-view-visual {
  position: sticky;
  top: 0;
  z-index: 4;
  min-height: clamp(180px, 26vh, 260px);
  margin-top: 0;
}

.tool88-page--case-buddy-embedded .simple-flow-map-svg {
  height: clamp(132px, 20vh, 204px);
}

.tool88-page--case-buddy-embedded .simple-compact-title,
.tool88-page--case-buddy-embedded .simple-input-block {
  display: none;
}

.tool88-page--case-buddy-embedded .simple-compact-header {
  grid-template-columns: minmax(0, 1fr);
  gap: 6px;
}

.tool88-page--case-buddy-embedded .simple-actions-row-top {
  justify-content: flex-start;
  padding-right: 40px;
}

.tool88-page--case-buddy-embedded .simple-mode-bar {
  margin-top: 0;
}

.tool88-page--case-buddy-embedded .simple-next-actions-card,
.tool88-page--case-buddy-embedded .simple-status-card,
.tool88-page--case-buddy-embedded .simple-recommendation-card,
.tool88-page--case-buddy-embedded .simple-result-card,
.tool88-page--case-buddy-embedded .simple-flow-message {
  padding: 12px;
}
.tool88-page--case-buddy-embedded .direct-auto-card,
.tool88-page--case-buddy-embedded .direct-auto-stop-overlay {
  display: none !important;
}

.tool88-page--case-buddy-embedded .simple-view-close {
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  font-size: 20px;
}

.tool88-page--case-buddy-embedded .hero-card,
.tool88-page--case-buddy-embedded .stage-card,
.tool88-page--case-buddy-embedded .workflow-shell,
.tool88-page--case-buddy-embedded .status-area,
.tool88-page--case-buddy-embedded .spreadsheet-section,
.tool88-page--case-buddy-embedded .report-panel {
  margin-left: 0;
  margin-right: 0;
}


.hero-card,
.stage-card,
.workflow-shell,
.status-area,
.spreadsheet-section,
.report-panel {
  width: 100%;
  box-sizing: border-box;
}

.hero-card {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(260px, 0.55fr);
  gap: 18px;
  align-items: stretch;
  background: linear-gradient(135deg, #f4f7ff 0%, #eef8f6 100%);
  border: 1px solid #d8e2f3;
  border-radius: 18px;
  padding: 24px;
  box-shadow: 0 10px 28px rgba(27, 39, 67, 0.08);
}

.hero-copy h1 {
  margin: 4px 0 8px;
  font-size: 32px;
  line-height: 1.1;
}

.hero-copy p,
.stage-copy,
.section-heading p,
.workflow-step span,
.result-callout span,
.decision-copy span,
.decision-hint {
  line-height: 1.45;
}

.eyebrow,
.stage-kicker,
.mini-label {
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 11px;
  font-weight: 800;
  color: #52627a;
}

.hero-stats {
  display: grid;
  gap: 10px;
}

.hero-stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(108, 123, 151, 0.24);
  border-radius: 14px;
  padding: 14px;
}

.hero-stat span {
  font-size: 12px;
  color: #59677c;
}

.hero-stat strong {
  word-break: break-word;
}

.workflow-shell {
  margin: 16px 0;
}

.workflow-steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 10px;
}

.workflow-step {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  position: relative;
  min-height: 90px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid #dfe5ee;
  background: #fff;
}

.workflow-step-active {
  border-color: #7fa3ff;
  box-shadow: 0 6px 16px rgba(42, 92, 204, 0.1);
}

.workflow-step-complete {
  border-color: #8ccfb1;
  background: #f4fcf8;
}

.workflow-step-locked {
  opacity: 0.62;
  background: #f8f9fb;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: #172033;
  color: #fff;
  font-weight: 800;
}

.step-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
}

.step-body span {
  font-size: 13px;
  color: #5b6575;
}

.step-status {
  position: absolute;
  right: 12px;
  bottom: 10px;
  font-size: 11px;
  font-weight: 800;
  color: #52627a;
}

.stage-card,
.status-area,
.spreadsheet-section,
.report-panel {
  background: #fff;
  border: 1px solid #dde4ef;
  border-radius: 16px;
  padding: 18px;
  margin: 14px 0;
  box-shadow: 0 6px 18px rgba(27, 39, 67, 0.05);
}

.stage-card-primary {
  border-color: #cbd9f6;
}

.stage-header,
.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 12px;
  text-align: left;
}

.stage-header h2,
.section-heading h2 {
  margin: 2px 0 0;
}

.stage-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #f0f3f8;
  color: #52627a;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 10px;
  white-space: nowrap;
}

.stage-badge-ready {
  background: #e7f7ef;
  color: #176a42;
}

.stage-copy {
  margin: 0 0 14px;
  text-align: left;
  color: #4d5a6d;
}

.settings-grid {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) minmax(240px, 1fr);
  gap: 12px;
  margin: 12px 0;
  text-align: left;
}

.setting-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
}

.setting-field select,
.setting-field input {
  min-height: 38px;
  padding: 7px 9px;
  border: 1px solid #cbd4e1;
  border-radius: 9px;
  background: #fff;
}


.settings-note {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 4px 0 0;
  color: #5c697d;
  font-size: 13px;
  text-align: left;
}

.settings-note-actions {
  justify-content: space-between;
}

.settings-note-button-row {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.cache-note {
  padding: 7px 10px;
  border: 1px solid #dbe5f3;
  border-radius: 10px;
  background: #f8fbff;
}

.wide-field {
  flex: 1;
  min-width: 280px;
}

.action-toolbar,
.decision-panel,
.decision-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}


.action-toolbar,
.decision-actions {
  margin-top: 14px;
}

.action-toolbar {
  justify-content: flex-start;
  gap: 8px;
}


.sample-data-panel {
  position: relative;
}

.sample-data-panel summary {
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  border: 1px solid #cbd4e1;
  border-radius: 10px;
  background: #f8fafc;
  color: #172033;
  font-size: 12px;
  font-weight: 800;
  padding: 0 10px;
  cursor: pointer;
  list-style: none;
}

.sample-data-panel summary::-webkit-details-marker {
  display: none;
}

.sample-data-panel[open] summary {
  border-color: #7892bc;
}

.example-loader {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 8px;
  padding: 6px 8px;
  border: 1px solid #dbe3ef;
  border-radius: 12px;
  background: #fbfcff;
}

.example-loader label {
  font-size: 12px;
  font-weight: 800;
  color: #52627a;
}

.example-loader select {
  min-height: 32px;
  max-width: 230px;
  border: 1px solid #cbd4e1;
  border-radius: 8px;
  background: #fff;
  padding: 5px 8px;
}

button {
  min-height: 34px;
  border: 1px solid #cbd4e1;
  border-radius: 10px;
  background: #fff;
  color: #172033;
  font-weight: 800;
  padding: 7px 12px;
  cursor: pointer;
}

button:hover:not(:disabled) {
  border-color: #7892bc;
  box-shadow: 0 4px 10px rgba(27, 39, 67, 0.08);
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.primary-button {
  background: #1b4fd7;
  border-color: #1b4fd7;
  color: #fff;
}

.secondary-button {
  background: #26324a;
  border-color: #26324a;
  color: #fff;
}

.review-button {
  background: #f8fafc;
  border-color: #9fb0c8;
  color: #26324a;
}

.ghost-button {
  background: #f8fafc;
}

.small-button {
  min-height: 32px;
  padding: 5px 10px;
  font-size: 12px;
}

.text-button {
  min-height: 28px;
  padding: 3px 8px;
  font-size: 12px;
}

.decision-panel {
  justify-content: flex-start;
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  background: #fbfcff;
  text-align: left;
}

.decision-panel-attention {
  border-color: #f2c56b;
  background: #fffaf0;
}

.decision-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 360px;
}

.count-panel,
.progress-panel,
.meta-panel,
.warnings-box {
  border-radius: 14px;
  padding: 14px;
  text-align: left;
}

.count-panel {
  border: 1px solid #dbe3ef;
}

.count-panel-ok {
  background: #f4fcf8;
  border-color: #98d5b7;
}

.count-panel-warning {
  background: #fff9ec;
  border-color: #f2c56b;
}

.count-panel-block {
  background: #fff2f2;
  border-color: #ef9999;
}

.count-headline {
  display: grid;
  grid-template-columns: 1.4fr 0.55fr 1.15fr;
  gap: 12px;
  align-items: stretch;
  margin-bottom: 12px;
}

.count-headline > div {
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: rgba(255, 255, 255, 0.74);
  border-radius: 10px;
  padding: 10px;
  min-width: 0;
}

.count-headline strong {
  word-break: break-word;
}

.attribution-panel {
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  padding: 14px;
  margin-bottom: 12px;
  text-align: left;
  background: #fbfcff;
}

.attribution-panel-warn {
  border-color: #f2c56b;
  background: #fff9ec;
}

.attribution-panel-block {
  border-color: #ef9999;
  background: #fff2f2;
}

.attribution-headline {
  display: grid;
  grid-template-columns: 1.3fr 0.8fr 0.9fr;
  gap: 10px;
  margin-bottom: 10px;
}

.attribution-headline > div {
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: rgba(255, 255, 255, 0.74);
  border-radius: 10px;
  padding: 10px;
  min-width: 0;
}

.attribution-headline strong {
  word-break: break-word;
}

.attribution-panel p {
  margin: 8px 0;
  color: #3e4a5c;
}

.attribution-panel small {
  color: #5d697a;
}

.signal-panel {
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  padding: 14px;
  margin-bottom: 12px;
  text-align: left;
  background: #fbfcff;
}

.signal-panel-ok {
  border-color: #98d5b7;
  background: #f4fcf8;
}

.signal-panel-warn {
  border-color: #f2c56b;
  background: #fff9ec;
}

.signal-panel-block {
  border-color: #ef9999;
  background: #fff2f2;
}

.signal-panel-neutral {
  border-color: #dbe3ef;
  background: #fbfcff;
}

.signal-headline {
  display: grid;
  grid-template-columns: 1.1fr 0.8fr 1.1fr;
  gap: 10px;
  margin-bottom: 10px;
}

.signal-headline > div {
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: rgba(255, 255, 255, 0.74);
  border-radius: 10px;
  padding: 10px;
  min-width: 0;
}

.signal-headline strong {
  word-break: break-word;
}

.signal-panel p {
  margin: 8px 0;
  color: #3e4a5c;
}

.signal-panel small {
  color: #5d697a;
}

.checks-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.check-card {
  min-height: 132px;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  padding: 14px;
  text-align: left;
  background: #fbfcff;
}

.check-card-ok {
  border-color: #98d5b7;
  background: #f4fcf8;
}

.check-card-warn {
  border-color: #f2c56b;
  background: #fff9ec;
}

.check-card-block {
  border-color: #ef9999;
  background: #fff2f2;
}

.check-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.check-title-row span {
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.86);
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 800;
}

.check-card p {
  margin: 10px 0 8px;
  line-height: 1.4;
}

.check-card small {
  color: #5d697a;
  line-height: 1.35;
}

.auto-decision-panel {
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  padding: 14px;
  margin: 12px 0 4px;
  text-align: left;
  background: #fbfcff;
}

.auto-decision-panel-ok {
  border-color: #98d5b7;
  background: #f4fcf8;
}

.auto-decision-panel-warn {
  border-color: #f2c56b;
  background: #fff9ec;
}

.auto-decision-panel-block {
  border-color: #ef9999;
  background: #fff2f2;
}

.auto-decision-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.auto-decision-head > div {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.auto-decision-head > span {
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.86);
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
}

.auto-decision-panel p {
  margin: 10px 0 6px;
  color: #3e4a5c;
}

.auto-decision-panel small {
  color: #5d697a;
}

.auto-decision-button {
  background: #0f766e;
  border-color: #0f766e;
}

.decision-stage {
  border-color: #cbd9f6;
}

.decision-hint {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  color: #5c697d;
  text-align: left;
}

.result-callout {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  text-align: left;
}

.result-callout-ending {
  background: #f8fafc;
  border: 1px solid #dbe3ef;
}

.result-callout-trace {
  background: #f4fcf8;
  border: 1px solid #98d5b7;
}

.status-area {
  display: grid;
  gap: 12px;
}

.progress-panel {
  background: #f4f7ff;
  border: 1px solid #cbd9f6;
}

.warnings-box {
  background: #fff9ec;
  border: 1px solid #f2c56b;
}

.warnings-box ul {
  margin: 8px 0 0 18px;
  padding: 0;
}

.meta-panel {
  background: #fbfcff;
  border: 1px solid #dbe3ef;
}

.panel-title {
  font-weight: 800;
  margin-bottom: 8px;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px 16px;
  font-size: 13px;
}

.compact-meta-grid {
  margin-top: 4px;
}

.spreadsheet-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.section-heading {
  width: 100%;
}

.section-heading p {
  margin: 4px 0 0;
  color: #5c697d;
}

.report-pre {
  width: 100%;
  box-sizing: border-box;
  white-space: pre-wrap;
  background: #0f172a;
  color: #e8eefc;
  border: 1px solid #1f2a44;
  border-radius: 14px;
  padding: 16px;
  min-height: 180px;
  max-height: 520px;
  overflow: auto;
  line-height: 1.45;
}


.trace-fetch-panel {
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #d5e0f2;
  border-radius: 16px;
  background: #fbfdff;
}

.trace-fetch-header,
.trace-inspector-header,
.trace-inspector-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.trace-fetch-header strong,
.trace-inspector-header strong {
  display: block;
}

.trace-progress-track {
  height: 10px;
  margin: 12px 0 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #e8edf7;
}

.trace-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: currentColor;
  transition: width 0.2s ease;
}

.trace-fetch-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  color: #5c697d;
  font-size: 12px;
}

.trace-fetch-metrics span {
  padding: 3px 8px;
  border: 1px solid #dbe3ef;
  border-radius: 999px;
  background: #fff;
}

.trace-fetch-warning {
  margin-top: 8px;
  padding: 8px 10px;
  border: 1px solid #f3d3a1;
  border-radius: 10px;
  background: #fff8eb;
  color: #7c4a03;
  font-size: 12px;
}

.trace-inspector {
  margin-top: 16px;
  display: grid;
  gap: 12px;
}

.trace-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.trace-summary-grid > div {
  padding: 10px 12px;
  border: 1px solid #dbe3ef;
  border-radius: 12px;
  background: #fff;
}

.trace-summary-grid span {
  display: block;
  color: #667085;
  font-size: 12px;
}

.trace-summary-grid strong {
  font-size: 18px;
}

.trace-interest-note {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 13px;
}

.trace-interest-note-ok {
  background: #f4fcf8;
  border: 1px solid #98d5b7;
}

.trace-interest-note-warn {
  background: #fff9ec;
  border: 1px solid #f2c56b;
}

.trace-assessor-note {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border: 1px dashed #b8c7dc;
  border-radius: 12px;
  background: #f8fbff;
  color: #344054;
  font-size: 13px;
}

.trace-inspector-toolbar {
  justify-content: flex-start;
}

.trace-inspector-toolbar select {
  min-width: 180px;
  padding: 7px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
}

.copy-feedback {
  color: #166534;
  font-size: 12px;
  font-weight: 700;
}

.trace-table-wrap {
  max-height: 460px;
  overflow: auto;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  background: #fff;
}

.trace-inspector-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.trace-cell-note {
  max-width: 220px;
  white-space: normal;
  font-size: 0.78rem;
  color: #4b5563;
}

.trace-inspector-table th,
.trace-inspector-table td {
  padding: 8px 9px;
  border-bottom: 1px solid #edf2f7;
  text-align: left;
  white-space: nowrap;
}

.trace-inspector-table td.trace-cell-note {
  white-space: normal;
  line-height: 1.35;
}


.trace-inspector-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f8fafc;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.trace-row-exit {
  outline: 3px solid rgba(22, 101, 52, 0.55);
  outline-offset: -3px;
  background: #f0fdf4;
  box-shadow: inset 5px 0 0 rgba(22, 101, 52, 0.65);
}

.trace-row-balance-impact td {
  background-image: linear-gradient(90deg, rgba(15, 23, 42, 0.015), rgba(15, 23, 42, 0));
}

.trace-row-balance-negative {
  background: #fff7ed;
}

.fifo-role-pill {
  display: inline-flex;
  align-items: center;
  max-width: 150px;
  padding: 3px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #f8fafc;
  color: #334155;
  font-size: 11px;
  font-weight: 800;
  white-space: normal;
}

.fifo-role-selected {
  border-color: #16a34a;
  background: #dcfce7;
  color: #14532d;
}

.muted-cell {
  color: #94a3b8;
}

.trace-row-interest {
  outline: 2px solid currentColor;
  outline-offset: -2px;
  background: #f8fbff;
}

.trace-row-failed {
  opacity: 0.72;
}

.hash-copy-button {
  padding: 3px 7px;
  border: 1px solid #dbe3ef;
  border-radius: 999px;
  background: #fff;
  cursor: pointer;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
}

.direction-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 8px;
  border: 1px solid #dbe3ef;
  background: #f8fafc;
  font-weight: 700;
}

.direction-pill-incoming {
  background: #eff6ff;
}

.direction-pill-outgoing {
  background: #fff7ed;
}

.direction-pill-self {
  background: #f5f3ff;
}

.trace-show-more {
  justify-self: start;
}



.token-manager-panel {
  margin: 16px 0 12px;
  border: 1px solid #dbe3ef;
  border-radius: 16px;
  background: #f8fbff;
  overflow: hidden;
}

.token-manager-panel > summary,
.token-manager-panel summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
  cursor: pointer;
  list-style: none;
  font-weight: 800;
}

.token-manager-panel > summary::-webkit-details-marker {
  display: none;
}

.token-manager-panel > summary small,
.token-manager-panel summary small {
  color: #64748b;
  font-weight: 700;
}

.token-manager-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 0 16px 16px;
}

.token-manager-card {
  display: grid;
  gap: 10px;
  padding: 14px;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  background: #fff;
}

.token-manager-card h3 {
  margin: 0;
  font-size: 1rem;
}

.token-manager-card p {
  margin: 0;
  color: #64748b;
  font-size: 0.86rem;
  line-height: 1.4;
}

.token-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.token-manager-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.token-import-textarea {
  min-height: 110px;
  width: 100%;
  padding: 10px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  resize: vertical;
}

.token-manager-message {
  margin: 0 16px 16px;
  padding: 10px 12px;
  border: 1px solid #b8c7dc;
  border-radius: 12px;
  background: #fff;
  color: #344054;
  font-size: 0.9rem;
}

.token-registry-preview {
  display: grid;
  gap: 10px;
  padding: 0 16px 16px;
}

.token-preview-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: end;
}

.token-preview-header > div {
  display: grid;
  gap: 2px;
}

.token-preview-header span,
.token-preview-note,
.empty-token-registry {
  color: #64748b;
  font-size: 0.84rem;
}

.token-search-label {
  display: grid;
  gap: 4px;
  min-width: 260px;
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 700;
}

.token-search-label input {
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
}

.token-registry-table {
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  font-size: 0.82rem;
}

.token-registry-table th,
.token-registry-table td {
  padding: 8px 9px;
  border-bottom: 1px solid #edf2f7;
  text-align: left;
}

.token-registry-table th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.empty-token-registry {
  padding: 12px;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #fff;
}

.token-resolution-panel {
  display: grid;
  gap: 6px;
  margin: 12px 0;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid #dbe3ef;
  background: #fff;
}

.token-resolution-panel p {
  margin: 0;
  color: #475569;
  font-size: 0.9rem;
}

.token-resolution-panel.ok {
  border-color: #98d5b7;
  background: #f4fcf8;
}

.token-resolution-panel.warn {
  border-color: #f2c56b;
  background: #fff9ec;
}

.token-resolution-panel.neutral {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.trace-token-check-cell {
  max-width: 220px;
}

.trace-token-check-cell small {
  display: block;
  margin-top: 4px;
  color: #66768a;
  line-height: 1.3;
}

.token-check-pill {
  display: inline-block;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
}

.token-check-ok {
  background: #e8f7ed;
  color: #176136;
}

.token-check-warn {
  background: #fff0cf;
  color: #7a4a00;
}

.token-check-neutral {
  background: #eef2f7;
  color: #526070;
}

.trace-row-token-issue {
  background: #fffaf0;
}


@media (max-width: 1300px) {
  .tool88-page {
    max-width: 100%;
  }

  .settings-grid,
  .workflow-steps,
  .checks-grid,
  .count-headline,
  .token-manager-grid,
  .token-form-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 840px) {
  .hero-card,
  .settings-grid,
  .workflow-steps,
  .checks-grid,
  .count-headline,
  .meta-grid,
  .token-manager-grid,
  .token-form-grid {
    grid-template-columns: 1fr;
  }

  .stage-header,
  .section-heading {
    flex-direction: column;
    align-items: stretch;
  }
}


.result-callout-swap {
  border-color: #9cc8ff;
  background: #f1f7ff;
}

.swap-explorer-panel {
  display: grid;
  gap: 14px;
  margin-top: 18px;
  padding: 16px;
  border: 1px solid #cbd5e1;
  border-radius: 18px;
  background: #fbfdff;
}

.swap-explorer-header,
.swap-explorer-meta,
.swap-explorer-actions,
.swap-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.swap-explorer-header {
  justify-content: space-between;
}

.swap-explorer-meta span {
  border: 1px solid #dbe3ef;
  border-radius: 999px;
  padding: 4px 9px;
  background: #fff;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
}

.swap-explorer-summary,
.manual-swap-card,
.swap-candidate-card {
  padding: 12px 14px;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  background: #fff;
}

.swap-candidate-card,
.manual-swap-card {
  display: grid;
  gap: 8px;
}

.swap-candidate-card p,
.manual-swap-card p {
  margin: 0;
  color: #475569;
}

.swap-candidate-ok {
  border-color: #99d7b5;
  background: #f4fcf8;
}

.swap-candidate-warn {
  border-color: #f0c36d;
  background: #fff9ec;
}

.swap-tab-button {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 999px;
  padding: 7px 11px;
  font-weight: 800;
  cursor: pointer;
}

.swap-tab-button.active {
  background: #1f2937;
  color: #fff;
  border-color: #1f2937;
}

.swap-tab-button span {
  margin-left: 5px;
  opacity: 0.75;
}

.swap-table-wrap {
  overflow: auto;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  background: #fff;
}

.swap-explorer-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.swap-explorer-table th,
.swap-explorer-table td {
  padding: 8px 9px;
  border-bottom: 1px solid #edf2f7;
  text-align: left;
  vertical-align: top;
}

.swap-explorer-table th {
  background: #f8fafc;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 11px;
}

.swap-row-incoming {
  background: #f0f9ff;
}

.swap-row-outgoing {
  background: #fff7ed;
}

.swap-row-selected {
  outline: 2px solid #2563eb;
  outline-offset: -2px;
}

.swap-detail-cell {
  max-width: 260px;
  color: #475569;
}

.bridge-candidate-title,
.bridge-full-value,
.bridge-address-cell,
.bridge-resolved-cell {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.bridge-candidate-title {
  display: block;
  line-height: 1.35;
}

.bridge-full-value-row,
.bridge-address-stack {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.bridge-full-value {
  display: inline-block;
  max-width: 100%;
  padding: 3px 6px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid #e2e8f0;
  color: #334155;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 11px;
  white-space: normal;
}

.tiny-button {
  padding: 3px 7px;
  font-size: 11px;
  line-height: 1.2;
}

.bridge-copy-feedback {
  align-self: center;
}

.result-callout-bridge {
  border-color: #d7b9ff;
  background: #f7f1ff;
}

.bridge-explorer-panel {
  display: grid;
  gap: 14px;
  margin-top: 18px;
  padding: 16px;
  border: 1px solid #d8c7ff;
  border-radius: 18px;
  background: #fcfaff;
}

.bridge-explorer-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.bridge-row-high {
  background: #f5f0ff;
}

.bridge-row-review {
  background: #fffdf7;
}

.bridge-log-details summary {
  cursor: pointer;
  font-weight: 800;
  color: #334155;
}

.bridge-stage-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 10px;
}

.bridge-stage-card {
  display: grid;
  gap: 4px;
  padding: 11px 12px;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  background: #fff;
}

.bridge-stage-card strong {
  color: #111827;
}

.bridge-stage-card p {
  margin: 0;
  color: #475569;
  font-size: 12px;
  line-height: 1.35;
}

.bridge-stage-ok {
  border-color: #99d7b5;
  background: #f4fcf8;
}

.bridge-stage-warn {
  border-color: #f0c36d;
  background: #fff9ec;
}

.bridge-stage-error {
  border-color: #f3a6a6;
  background: #fff5f5;
}

.bridge-stage-neutral {
  border-color: #d8c7ff;
  background: #fcfaff;
}


.setting-field-checkbox {
  gap: 0.45rem;
}

.toggle-row {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  font-weight: 500;
  color: #334155;
  line-height: 1.35;
}

.toggle-row input {
  margin-top: 0.2rem;
}

.setting-subrow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}

.setting-subrow small {
  color: #64748b;
  line-height: 1.35;
}

.trace-service-assessment {
  margin-top: 0.85rem;
  padding: 0.85rem 1rem;
  border-radius: 0.85rem;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
}

.trace-service-assessment.warn {
  border-color: #f59e0b;
  background: #fffbeb;
}

.trace-service-assessment.ok {
  border-color: #86efac;
  background: #f0fdf4;
}

.trace-service-assessment.muted {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.trace-service-assessment p {
  margin: 0.35rem 0 0;
  color: #475569;
}

.trace-service-assessment ul {
  margin: 0.45rem 0 0;
  padding-left: 1.15rem;
  color: #475569;
}


.flow-completion-settings {
  margin: 10px 0 14px;
  border: 1px solid #d7e0ed;
  border-radius: 14px;
  background: #fbfdff;
  padding: 8px 12px;
}

.flow-completion-settings summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  font-weight: 900;
  color: #172033;
}

.flow-completion-settings summary small {
  color: #6d7d94;
  font-weight: 700;
}

.flow-settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.flow-setting-wide {
  grid-column: 1 / -1;
}

.flow-setting-wide textarea {
  width: 100%;
  min-height: 58px;
  resize: vertical;
  border: 1px solid #d4deec;
  border-radius: 10px;
  padding: 8px 10px;
  font: inherit;
}

.flow-setting-wide small {
  color: #6d7d94;
  font-size: 11px;
}

.flow-completion-card {
  border-color: #cddcf7;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.flow-completion-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.flow-depth-pill {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  border: 1px solid #d4deec;
  border-radius: 999px;
  padding: 0 10px;
  color: #52627a;
  background: #fff;
  font-size: 12px;
  font-weight: 900;
}

.flow-completion-message {
  margin-top: 10px;
  border-left: 4px solid #7892bc;
  background: #f5f8fc;
  color: #24324a;
  padding: 9px 11px;
  border-radius: 10px;
  font-weight: 750;
}

.flow-completion-summary {
  margin-top: 12px;
  border: 1px solid #d9e2ef;
  border-radius: 14px;
  background: #fff;
  padding: 12px;
}

.flow-completion-complete {
  border-color: #b9e4c8;
  background: #f6fff9;
}

.flow-completion-incomplete {
  border-color: #f0d9a6;
  background: #fffaf0;
}

.flow-summary-head {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 10px;
}

.flow-summary-head span {
  color: #607089;
  font-size: 13px;
}

.flow-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
}

.flow-summary-grid div {
  border: 1px solid #e2e8f2;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.82);
  padding: 8px 10px;
}

.flow-summary-grid span {
  display: block;
  color: #6d7d94;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.flow-summary-grid strong {
  font-size: 18px;
}

.flow-next-panel {
  margin-top: 10px;
  border: 1px solid #e2d4a2;
  border-radius: 12px;
  background: #fffef7;
  padding: 9px 10px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.flow-ending-list {
  margin-top: 10px;
}

.flow-ending-list summary {
  cursor: pointer;
  font-weight: 900;
}

.flow-ending-list table {
  width: 100%;
  margin-top: 8px;
  border-collapse: collapse;
  font-size: 12px;
}

.flow-ending-list th,
.flow-ending-list td {
  border-bottom: 1px solid #e4eaf3;
  padding: 7px;
  text-align: left;
  vertical-align: top;
}

.flow-ending-complete td {
  background: rgba(238, 255, 242, 0.7);
}

.flow-ending-incomplete td {
  background: rgba(255, 249, 235, 0.8);
}



.output-absorb-actions span {
  color: #64748b;
  font-size: 13px;
}

.result-callout-flow {
  border-color: #c7d2fe;
  background: #eef2ff;
  color: #3730a3;
}

.flow-entity-override-field textarea {
  min-height: 74px;
  resize: vertical;
}

.trace-through-entity-manager-panel code {
  font-size: 11px;
}

.trace-through-entity-form-grid {
  align-items: start;
}

.trace-through-entity-wide-field {
  grid-column: span 2;
}

.trace-through-entity-wide-field textarea {
  min-height: 74px;
  resize: vertical;
}

.trace-through-entity-registry-table td strong {
  display: block;
}

.trace-through-entity-registry-table td small {
  display: block;
  margin-top: 3px;
  color: #6b7280;
}

@media (max-width: 760px) {
  .trace-through-entity-wide-field {
    grid-column: span 1;
  }
}




/* Case Buddy embedded simple-view refinement: make the animated flow map visible without hunting through nested scroll frames. */
.tool88-page--case-buddy-embedded.simple-view-active {
  min-height: 0;
  overflow: hidden;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-overlay {
  padding: 6px;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-modal {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-layout {
  flex: 1 1 auto;
  min-height: 0;
  align-items: stretch;
  grid-template-columns: minmax(0, 1.22fr) minmax(300px, 0.72fr);
  gap: 10px;
  padding: 34px 10px 10px;
  overflow: hidden;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-left,
.tool88-page--case-buddy-embedded.simple-view-active .simple-view-right {
  max-width: none;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-compact-header {
  gap: 5px;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-left h2 {
  font-size: clamp(17px, 1.2vw, 22px);
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-intro {
  display: none;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-input-block {
  display: none;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-modal {
  min-height: 0;
  max-height: 100%;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-left {
  display: flex;
  flex-direction: column;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-visual {
  flex: 0 0 auto;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-visual {
  min-height: clamp(220px, 34vh, 390px);
  margin: 4px 0 8px;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-flow-map-svg {
  height: clamp(174px, 28vh, 324px);
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-flow-map-head {
  padding-top: 8px;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-settings-card,
.tool88-page--case-buddy-embedded.simple-view-active .simple-target-card,
.tool88-page--case-buddy-embedded.simple-view-active .simple-status-card,
.tool88-page--case-buddy-embedded.simple-view-active .simple-recommendation-card,
.tool88-page--case-buddy-embedded.simple-view-active .simple-result-card,
.tool88-page--case-buddy-embedded.simple-view-active .simple-next-actions-card {
  padding: 10px;
}

@media (max-width: 980px) {
  .tool88-page--case-buddy-embedded.simple-view-active .simple-view-layout {
    grid-template-columns: 1fr;
    overflow: auto;
  }

  .tool88-page--case-buddy-embedded.simple-view-active .simple-view-left,
  .tool88-page--case-buddy-embedded.simple-view-active .simple-view-right {
    overflow: visible;
  }
}

/* Fix 22: embedded Case Buddy simple mode keeps the animated flow visible and avoids nested scroll traps. */
.tool88-page--case-buddy-embedded.simple-view-active .simple-view-layout {
  grid-template-columns: minmax(0, 1.36fr) minmax(280px, 0.64fr);
  padding: 24px 8px 8px;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-left {
  overflow: hidden;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-right {
  overflow: auto;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-view-visual {
  min-height: clamp(240px, 37vh, 360px);
  margin: 2px 0 8px;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-flow-map-svg {
  height: clamp(190px, 30vh, 300px);
  max-height: 300px;
}

.tool88-page--case-buddy-embedded.simple-view-active .simple-status-card,
.tool88-page--case-buddy-embedded.simple-view-active .simple-recommendation-card,
.tool88-page--case-buddy-embedded.simple-view-active .simple-result-card,
.tool88-page--case-buddy-embedded.simple-view-active .simple-next-actions-card {
  max-height: clamp(8rem, 28vh, 16rem);
  overflow: auto;
}

</style>
